// Ultimate Card Game (UCG) - On-Field Unit Rendering
//
// Extracted from js/engine/renderer.js (was its second-largest method at
// ~1100 lines - per-unit-type bespoke rendering: bounce/hover/recoil
// motion, HP bars, status effects, and blitting the baked sprite bitmaps
// from js/engine/render/unitSprites.js). Mixed onto Renderer.prototype (see
// the Object.assign() call at the bottom of renderer.js) rather than kept
// as a free function, since it reads instance state (this.width/etc.)
// throughout - a straight cut, not a rewrite.
import { buildSpriteBitmap, buildGoldenStatueBitmap, buildAuraBitmap, dampedSpringRecoil, getThermalHeatStrip, isUHDEnabled, isPinkBeretV2Enabled } from './unitSprites.js';

// Static frozen walkframe arrays — eliminates per-frame heap allocations (Paradigma 1)
const MARINE_WALK_FRAMES = Object.freeze([
  'Marine_Legs_Walk1', 'Marine_Legs_Walk2', 'Marine_Legs_Walk3',
  'Marine_Legs_Walk4', 'Marine_Legs_Walk5', 'Marine_Legs_Walk6'
]);
const MARINE_GOLD_WALK_FRAMES = Object.freeze([
  'MarineGold_Legs_Walk1', 'MarineGold_Legs_Walk2', 'MarineGold_Legs_Walk3',
  'MarineGold_Legs_Walk4', 'MarineGold_Legs_Walk5', 'MarineGold_Legs_Walk6'
]);
const HYPER_MARINE_WALK_FRAMES = Object.freeze([
  'HyperMarine_Legs_Walk1', 'HyperMarine_Legs_Walk2', 'HyperMarine_Legs_Walk3',
  'HyperMarine_Legs_Walk4', 'HyperMarine_Legs_Walk5', 'HyperMarine_Legs_Walk6'
]);
export const HOTHAND_SPRINT_FRAMES = Object.freeze([
  'HotHand_Legs_Sprint1', 'HotHand_Legs_Sprint2', 'HotHand_Legs_Sprint3',
  'HotHand_Legs_Sprint4', 'HotHand_Legs_Sprint5', 'HotHand_Legs_Sprint6'
]);
export const HOTHAND_SPRINT_COMPOSITES = Object.freeze([
  'HotHand_Sprint1', 'HotHand_Sprint2', 'HotHand_Sprint3',
  'HotHand_Sprint4', 'HotHand_Sprint5', 'HotHand_Sprint6'
]);
export const HOTHAND_GOLD_SPRINT_FRAMES = Object.freeze([
  'HotHandGold_Legs_Sprint1', 'HotHandGold_Legs_Sprint2', 'HotHandGold_Legs_Sprint3',
  'HotHandGold_Legs_Sprint4', 'HotHandGold_Legs_Sprint5', 'HotHandGold_Legs_Sprint6'
]);
export const HOTHAND_GOLD_SPRINT_COMPOSITES = Object.freeze([
  'HotHandGold_Sprint1', 'HotHandGold_Sprint2', 'HotHandGold_Sprint3',
  'HotHandGold_Sprint4', 'HotHandGold_Sprint5', 'HotHandGold_Sprint6'
]);
export const HOTHAND_ROLL_FRAMES = Object.freeze([
  'HotHand_Legs_Roll1', 'HotHand_Legs_Roll2', 'HotHand_Legs_Roll3', 'HotHand_Legs_Roll4'
]);
export const HOTHAND_GOLD_ROLL_FRAMES = Object.freeze([
  'HotHandGold_Legs_Roll1', 'HotHandGold_Legs_Roll2', 'HotHandGold_Legs_Roll3', 'HotHandGold_Legs_Roll4'
]);

export const PINKBERET_WALK_FRAMES = Object.freeze([
  'PinkBeret_Legs_Walk1', 'PinkBeret_Legs_Walk2', 'PinkBeret_Legs_Walk3',
  'PinkBeret_Legs_Walk4', 'PinkBeret_Legs_Walk5', 'PinkBeret_Legs_Walk6'
]);
export const PINKBERET_GOLD_WALK_FRAMES = Object.freeze([
  'PinkBeretGold_Legs_Walk1', 'PinkBeretGold_Legs_Walk2', 'PinkBeretGold_Legs_Walk3',
  'PinkBeretGold_Legs_Walk4', 'PinkBeretGold_Legs_Walk5', 'PinkBeretGold_Legs_Walk6'
]);
export const PINKBERET_CLASSIC_WALK_FRAMES = Object.freeze([
  'PinkBeret_Classic_Legs_Walk1', 'PinkBeret_Classic_Legs_Walk2', 'PinkBeret_Classic_Legs_Walk3',
  'PinkBeret_Classic_Legs_Walk4', 'PinkBeret_Classic_Legs_Walk5', 'PinkBeret_Classic_Legs_Walk6'
]);
export const PINKBERET_GOLD_CLASSIC_WALK_FRAMES = Object.freeze([
  'PinkBeretGold_Classic_Legs_Walk1', 'PinkBeretGold_Classic_Legs_Walk2', 'PinkBeretGold_Classic_Legs_Walk3',
  'PinkBeretGold_Classic_Legs_Walk4', 'PinkBeretGold_Classic_Legs_Walk5', 'PinkBeretGold_Classic_Legs_Walk6'
]);
export const PINKBERET_V2_WALK_FRAMES = Object.freeze([
  'PinkBeret_V2_Legs_Walk1', 'PinkBeret_V2_Legs_Walk2', 'PinkBeret_V2_Legs_Walk3',
  'PinkBeret_V2_Legs_Walk4', 'PinkBeret_V2_Legs_Walk5', 'PinkBeret_V2_Legs_Walk6'
]);
export const PINKBERET_GOLD_V2_WALK_FRAMES = Object.freeze([
  'PinkBeretGold_V2_Legs_Walk1', 'PinkBeretGold_V2_Legs_Walk2', 'PinkBeretGold_V2_Legs_Walk3',
  'PinkBeretGold_V2_Legs_Walk4', 'PinkBeretGold_V2_Legs_Walk5', 'PinkBeretGold_V2_Legs_Walk6'
]);
export const PINKBERET_WALK8_FRAMES = Object.freeze([
  'PinkBeret_Walk_Armed1', 'PinkBeret_Walk_Armed2', 'PinkBeret_Walk_Armed3', 'PinkBeret_Walk_Armed4',
  'PinkBeret_Walk_Armed5', 'PinkBeret_Walk_Armed6', 'PinkBeret_Walk_Armed7', 'PinkBeret_Walk_Armed8'
]);
export const PINKBERET_GOLD_WALK8_FRAMES = Object.freeze([
  'PinkBeretGold_Walk_Armed1', 'PinkBeretGold_Walk_Armed2', 'PinkBeretGold_Walk_Armed3', 'PinkBeretGold_Walk_Armed4',
  'PinkBeretGold_Walk_Armed5', 'PinkBeretGold_Walk_Armed6', 'PinkBeretGold_Walk_Armed7', 'PinkBeretGold_Walk_Armed8'
]);
export const PINKBERET_WALK16_FRAMES = Object.freeze([
  'PinkBeret_Walk_Armed1', 'PinkBeret_Walk_Armed2', 'PinkBeret_Walk_Armed3', 'PinkBeret_Walk_Armed4',
  'PinkBeret_Walk_Armed5', 'PinkBeret_Walk_Armed6', 'PinkBeret_Walk_Armed7', 'PinkBeret_Walk_Armed8',
  'PinkBeret_Walk_Armed9', 'PinkBeret_Walk_Armed10', 'PinkBeret_Walk_Armed11', 'PinkBeret_Walk_Armed12',
  'PinkBeret_Walk_Armed13', 'PinkBeret_Walk_Armed14', 'PinkBeret_Walk_Armed15', 'PinkBeret_Walk_Armed16'
]);
export const PINKBERET_GOLD_WALK16_FRAMES = Object.freeze([
  'PinkBeretGold_Walk_Armed1', 'PinkBeretGold_Walk_Armed2', 'PinkBeretGold_Walk_Armed3', 'PinkBeretGold_Walk_Armed4',
  'PinkBeretGold_Walk_Armed5', 'PinkBeretGold_Walk_Armed6', 'PinkBeretGold_Walk_Armed7', 'PinkBeretGold_Walk_Armed8',
  'PinkBeretGold_Walk_Armed9', 'PinkBeretGold_Walk_Armed10', 'PinkBeretGold_Walk_Armed11', 'PinkBeretGold_Walk_Armed12',
  'PinkBeretGold_Walk_Armed13', 'PinkBeretGold_Walk_Armed14', 'PinkBeretGold_Walk_Armed15', 'PinkBeretGold_Walk_Armed16'
]);
export const PINKBERET_WALK32_FRAMES = Object.freeze(
  Array.from({ length: 32 }, (_, i) => `PinkBeret_Walk_Armed${i + 1}`)
);
export const PINKBERET_GOLD_WALK32_FRAMES = Object.freeze(
  Array.from({ length: 32 }, (_, i) => `PinkBeretGold_Walk_Armed${i + 1}`)
);
export const PINKBERET_UHD_WALK32_FRAMES = Object.freeze(
  Array.from({ length: 32 }, (_, i) => `PinkBeret_UHD_Walk_Armed${i + 1}`)
);
export const PINKBERET_GOLD_UHD_WALK32_FRAMES = Object.freeze(
  Array.from({ length: 32 }, (_, i) => `PinkBeretGold_UHD_Walk_Armed${i + 1}`)
);

const DEVIL_PATROL_WALK_FRAMES = Object.freeze([
  'DevilPatrol_Legs_Walk1', 'DevilPatrol_Legs_Walk2', 'DevilPatrol_Legs_Walk3',
  'DevilPatrol_Legs_Walk4', 'DevilPatrol_Legs_Walk5', 'DevilPatrol_Legs_Walk6'
]);
export const DEVIL_PATROL_WALK_COMPOSITES = Object.freeze([
  'DevilPatrol_Walk1', 'DevilPatrol_Walk2', 'DevilPatrol_Walk3',
  'DevilPatrol_Walk4', 'DevilPatrol_Walk5', 'DevilPatrol_Walk6'
]);
const SHADE_WALK_FRAMES = Object.freeze([
  'Shade_Legs_Walk1', 'Shade_Legs_Walk2', 'Shade_Legs_Walk3',
  'Shade_Legs_Walk4', 'Shade_Legs_Walk5', 'Shade_Legs_Walk6'
]);
const HYPER_SHADE_WALK_FRAMES = Object.freeze([
  'HyperShade_Legs_Walk1', 'HyperShade_Legs_Walk2', 'HyperShade_Legs_Walk3',
  'HyperShade_Legs_Walk4', 'HyperShade_Legs_Walk5', 'HyperShade_Legs_Walk6'
]);
const TESLA_WALKER_WALK_FRAMES = Object.freeze([
  'TeslaWalker_Legs_Walk1',
  'TeslaWalker_Legs_Walk2',
  'TeslaWalker_Legs_Walk3',
  'TeslaWalker_Legs_Walk4'
]);
export const TESLA_WALKER_COMP_WALK_FRAMES = Object.freeze([
  'TeslaWalker_Walk1',
  'TeslaWalker_Walk2',
  'TeslaWalker_Walk3',
  'TeslaWalker_Walk4'
]);
const HYPER_TESLA_WALKER_WALK_FRAMES = Object.freeze([
  'HyperTeslaWalker_Legs_Walk1',
  'HyperTeslaWalker_Legs_Walk2',
  'HyperTeslaWalker_Legs_Walk3',
  'HyperTeslaWalker_Legs_Walk4'
]);
const HYPER_TESLA_WALKER_COMP_WALK_FRAMES = Object.freeze([
  'HyperTeslaWalker_Walk1',
  'HyperTeslaWalker_Walk2',
  'HyperTeslaWalker_Walk3',
  'HyperTeslaWalker_Walk4'
]);
const APOSTATE_LOAF_WALK_FRAMES = Object.freeze([
  'ApostateLoaf_Walk1',
  'ApostateLoaf_Walk2',
  'ApostateLoaf_Walk3',
  'ApostateLoaf_Walk4'
]);
export const PRESERVER_LOCK_WALK_FRAMES = Object.freeze([
  'PreserverLock_Legs_Walk1',
  'PreserverLock_Legs_Walk2',
  'PreserverLock_Legs_Walk3',
  'PreserverLock_Legs_Walk4'
]);
export const PRESERVER_LOCK_WALK_COMPOSITES = Object.freeze([
  'PreserverLock_Walk1',
  'PreserverLock_Walk2',
  'PreserverLock_Walk3',
  'PreserverLock_Walk4'
]);
export const PRESERVER_LOCK_BODY_FRAMES = Object.freeze([
  'PreserverLock_Body_Walk1',
  'PreserverLock_Body_Walk2',
  'PreserverLock_Body_Walk3',
  'PreserverLock_Body_Walk4'
]);
export const BASTION_LOCK_WALK_FRAMES = Object.freeze([
  'BastionLock_Legs_Walk1',
  'BastionLock_Legs_Walk2',
  'BastionLock_Legs_Walk3',
  'BastionLock_Legs_Walk4'
]);
export const BASTION_LOCK_WALK_COMPOSITES = Object.freeze([
  'BastionLock_Walk1',
  'BastionLock_Walk2',
  'BastionLock_Walk3',
  'BastionLock_Walk4'
]);
export const PULP_WALK_FRAMES = Object.freeze([
  'Pulp_Legs_Walk1',
  'Pulp_Legs_Walk2',
  'Pulp_Legs_Walk3',
  'Pulp_Legs_Walk4',
  'Pulp_Legs_Walk5',
  'Pulp_Legs_Walk6'
]);
export const PULP_HOP_COMPOSITES = Object.freeze([
  'Pulp_Hop1',
  'Pulp_Hop2',
  'Pulp_Hop3',
  'Pulp_Hop4',
  'Pulp_Hop5',
  'Pulp_Hop6'
]);
export const STICKMAN_WALK_FRAMES = Object.freeze([
  'Stickman_Legs_Walk1',
  'Stickman_Legs_Walk2',
  'Stickman_Legs_Walk3',
  'Stickman_Legs_Walk4',
  'Stickman_Legs_Walk5',
  'Stickman_Legs_Walk6',
  'Stickman_Legs_Walk7',
  'Stickman_Legs_Walk8'
]);
export const STICKMAN_WALK_COMPOSITES = Object.freeze([
  'Stickman_Walk1',
  'Stickman_Walk2',
  'Stickman_Walk3',
  'Stickman_Walk4',
  'Stickman_Walk5',
  'Stickman_Walk6',
  'Stickman_Walk7',
  'Stickman_Walk8'
]);
export const PARASITE_VULTURE_FLAP_COMPOSITES = Object.freeze([
  'ParasiteVulture_Flap1',
  'ParasiteVulture_Flap2',
  'ParasiteVulture_Flap3'
]);
export const PARASITE_VULTURE_EMPTY_FLAP_COMPOSITES = Object.freeze([
  'ParasiteVulture_Empty_Flap1',
  'ParasiteVulture_Empty_Flap2',
  'ParasiteVulture_Empty_Flap3'
]);
export const SENTINAL_LOCK_ROLL_COMPOSITES = Object.freeze([
  'SentinalLock_Roll1',
  'SentinalLock_Roll2',
  'SentinalLock_Roll3',
  'SentinalLock_Roll4'
]);
export const A9_PIRANHA_SPIN_COMPOSITES = Object.freeze([
  'A9Piranha_Spin1',
  'A9Piranha_Spin2',
  'A9Piranha_Spin3',
  'A9Piranha_Spin4'
]);
export const ANTI_SHADE_WALK_COMPOSITES = Object.freeze([
  'AntiShade_Walk1',
  'AntiShade_Walk2',
  'AntiShade_Walk3',
  'AntiShade_Walk4',
  'AntiShade_Walk5',
  'AntiShade_Walk6'
]);
// Wheelie Bomber: High-octane motorcycle physics with launch wheelie upon
// spawn, periodic stunt wheelies, lateral weaving (Schlenkerer), dynamic
// banking into turns, apex balance wobble, landing fork compression & rebound.
const WHEELIE_BOMBER_CRUISE_PERIOD = 2.8; // seconds per stunt cycle
const WHEELIE_BOMBER_STUNT_DURATION = 0.85; // seconds each stunt wheelie lasts
const WHEELIE_BOMBER_LAUNCH_DURATION = 0.80; // seconds for immediate spawn launch-wheelie
const WHEELIE_BOMBER_BIKE_ROLL_FRAMES = Object.freeze([
  'WheelieBomber_Bike_Roll1',
  'WheelieBomber_Bike_Roll2',
  'WheelieBomber_Bike_Roll3'
]);
const WHEELIE_BOMBER_WHEELIE1_ROLL_FRAMES = Object.freeze([
  'WheelieBomber_Bike_Wheelie1_Roll1',
  'WheelieBomber_Bike_Wheelie1_Roll2',
  'WheelieBomber_Bike_Wheelie1_Roll3'
]);
const WHEELIE_BOMBER_WHEELIE2_ROLL_FRAMES = Object.freeze([
  'WheelieBomber_Bike_Wheelie2_Roll1',
  'WheelieBomber_Bike_Wheelie2_Roll2',
  'WheelieBomber_Bike_Wheelie2_Roll3'
]);

// The Salvage Company: Pre-frozen animation frames & phased 8.0s salvage cycle
const VULTURE_NODE_T = Object.freeze([0.28, 0.52, 0.74]);
const VULTURE_NODE_EXT = Object.freeze([1.7, 1.5, 1.3]);
const VULTURE_COXA_OFFSETS = Object.freeze([-6, -2, 2, 6]);
const VULTURE_BUBBLE_SEEDS = Object.freeze([0.22, 0.54, 0.81, 0.36, 0.68]);

let _cachedHotHandPunchGrad = null;
let _cachedHotHandIdleGrad = null;

let _cachedVultureRisingGlowGrad = null;
let _cachedVultureNormalGlowGrad = null;
let _cachedVultureRisingFluidGrad = null;
let _cachedVultureNormalFluidGrad = null;

const SALVAGE_ARM_FRAMES = Object.freeze([
  'TheSalvageCompany_Arm_Rest',
  'TheSalvageCompany_Arm_Raise',
  'TheSalvageCompany_Arm_SwingFwd',
  'TheSalvageCompany_Arm_Lower',
  'TheSalvageCompany_Arm_Reach',
  'TheSalvageCompany_Arm_Grab',
  'TheSalvageCompany_Arm_Lift',
  'TheSalvageCompany_Arm_SwingBack',
  'TheSalvageCompany_Arm_Hover',
  'TheSalvageCompany_Arm_Dump',
  'TheSalvageCompany_Arm_Fold'
]);
const SALVAGE_CHASSIS_ROLL_FRAMES = Object.freeze([
  'TheSalvageCompany_Chassis_Roll1',
  'TheSalvageCompany_Chassis_Roll2',
  'TheSalvageCompany_Chassis_Roll3',
  'TheSalvageCompany_Chassis_Roll4',
  'TheSalvageCompany_Chassis_Roll5',
  'TheSalvageCompany_Chassis_Roll6'
]);
const SALVAGE_ROLL_COMPOSITES = Object.freeze([
  'TheSalvageCompany_Roll1',
  'TheSalvageCompany_Roll2',
  'TheSalvageCompany_Roll3',
  'TheSalvageCompany_Roll4',
  'TheSalvageCompany_Roll5',
  'TheSalvageCompany_Roll6'
]);
const SALVAGE_CYCLE_DURATION = 8.0;

const ORCHESTRA_CHASSIS_ROLL_FRAMES = Object.freeze([
  'Orchestra_Chassis_Roll1',
  'Orchestra_Chassis_Roll2',
  'Orchestra_Chassis_Roll3',
  'Orchestra_Chassis_Roll4',
  'Orchestra_Chassis_Roll5',
  'Orchestra_Chassis_Roll6'
]);
const ORCHESTRA_ROLL_COMPOSITES = Object.freeze([
  'Orchestra_Roll1',
  'Orchestra_Roll2',
  'Orchestra_Roll3',
  'Orchestra_Roll4',
  'Orchestra_Roll5',
  'Orchestra_Roll6'
]);

const PANZER_CHASSIS_ROLL_FRAMES = Object.freeze([
  'Panzer_Chassis_Roll1',
  'Panzer_Chassis_Roll2',
  'Panzer_Chassis_Roll3'
]);
const PANZER_ROLL_COMPOSITES = Object.freeze([
  'Panzer_Roll1',
  'Panzer_Roll2',
  'Panzer_Roll3'
]);

const HYPER_PANZER_CHASSIS_ROLL_FRAMES = Object.freeze([
  'HyperPanzer_Chassis_Roll1',
  'HyperPanzer_Chassis_Roll2',
  'HyperPanzer_Chassis_Roll3'
]);
const HYPER_PANZER_ROLL_COMPOSITES = Object.freeze([
  'HyperPanzer_Roll1',
  'HyperPanzer_Roll2',
  'HyperPanzer_Roll3'
]);

const A9_GRIZZLY_CHASSIS_ROLL_FRAMES = Object.freeze([
  'A9Grizzly_Chassis_Roll1',
  'A9Grizzly_Chassis_Roll2',
  'A9Grizzly_Chassis_Roll3',
  'A9Grizzly_Chassis_Roll4',
  'A9Grizzly_Chassis_Roll5',
  'A9Grizzly_Chassis_Roll6',
  'A9Grizzly_Chassis_Roll7',
  'A9Grizzly_Chassis_Roll8'
]);
const A9_GRIZZLY_ROLL_COMPOSITES = Object.freeze([
  'A9Grizzly_Roll1',
  'A9Grizzly_Roll2',
  'A9Grizzly_Roll3',
  'A9Grizzly_Roll4',
  'A9Grizzly_Roll5',
  'A9Grizzly_Roll6',
  'A9Grizzly_Roll7',
  'A9Grizzly_Roll8'
]);

const A9_GRIZZLY_RADAR_ROT_FRAMES = Object.freeze([
  'A9Grizzly_Radar_Rot1',
  'A9Grizzly_Radar_Rot2',
  'A9Grizzly_Radar_Rot3',
  'A9Grizzly_Radar_Rot4',
  'A9Grizzly_Radar_Rot5',
  'A9Grizzly_Radar_Rot6'
]);

export const ANTI_SHADE_WALK_FRAMES = Object.freeze([
  'AntiShade_Base_Walk1',
  'AntiShade_Base_Walk2',
  'AntiShade_Base_Walk3',
  'AntiShade_Base_Walk4',
  'AntiShade_Base_Walk5',
  'AntiShade_Base_Walk6'
]);

const CLAIMING_PILLAR_EMBER_COLORS = Object.freeze(['#ffffff', '#fbbf24', '#ef4444']);

const STICKMAN_LANDING_DUST_PUFF_CONFIG = Object.freeze([
  // Rear Knee / Foot ground impact plume
  { oxFrac: -0.20, liftSpd: 20, expandRate: 16, rad: 5.5, sway: -1.0 },
  // Center Chassis / Pelvic crash plume
  { oxFrac: -0.04, liftSpd: 26, expandRate: 20, rad: 7.0, sway: 0.3 },
  // Forward Knee strike plume
  { oxFrac: 0.14, liftSpd: 22, expandRate: 18, rad: 6.0, sway: 0.8 },
  // Railgun Lance muzzle ground anchor plume
  { oxFrac: 0.36, liftSpd: 15, expandRate: 14, rad: 4.8, sway: 1.2 }
]);
const STICKMAN_LANDING_DUST_COLORS = Object.freeze(['#cbd5e1', '#94a3b8', '#b4a591', '#64748b']);
const STICKMAN_LANDING_DUST_ALPHAS = Object.freeze([1.0, 0.90, 0.80, 0.65]);

// Sul Anti Shade: Asynchronous organic tattered cloth & smoke vortex constants
// Non-harmonic frequencies, desynchronized angular offsets, and variable shred lengths
// ensure the tattered black cloth cyclone appears organic, diffuse, and turbulent without
// geometric corkscrew uniformity (Dementor / Wraith artwork parity).
const ANTI_SHADE_TATTER_BASE_ANGLES  = Object.freeze([0.15, 1.42, 2.78, 4.02, 5.25]);
const ANTI_SHADE_TATTER_SPEED_MULTS  = Object.freeze([1.05, 0.90, 1.16, 0.85, 1.11]);
const ANTI_SHADE_TATTER_R_TOP        = Object.freeze([6.5,  5.2,  7.2,  5.0,  6.2]);
const ANTI_SHADE_TATTER_R_MID        = Object.freeze([13.5, 11.2, 14.8, 10.5, 12.8]);
const ANTI_SHADE_TATTER_R_BOT        = Object.freeze([16.8, 14.0, 18.2, 13.0, 15.8]);
const ANTI_SHADE_TATTER_Y_MID        = Object.freeze([6.0,  5.2,  6.8,  4.8,  5.8]);
const ANTI_SHADE_TATTER_Y_BOT        = Object.freeze([11.5, 9.8,  12.8, 9.0,  11.0]);
const ANTI_SHADE_TATTER_W_TOP        = Object.freeze([6.8,  5.2,  7.5,  4.8,  6.2]);
const ANTI_SHADE_TATTER_W_MID        = Object.freeze([5.5,  4.2,  6.2,  3.8,  5.0]);
const ANTI_SHADE_TATTER_NOTCH_AMP    = Object.freeze([1.8,  1.2,  2.0,  1.2,  1.6]);
const ANTI_SHADE_TATTER_CURL_AMP     = Object.freeze([0.0,  0.0,  0.0,  0.0,  0.0]);
const ANTI_SHADE_TATTER_FX           = Object.freeze([2.0,  2.4,  1.8,  2.5,  2.1]);
const ANTI_SHADE_TATTER_FY           = Object.freeze([1.6,  1.9,  1.4,  2.0,  1.7]);
const ANTI_SHADE_TATTER_FLUTTER_AMP  = Object.freeze([2.2,  1.8,  2.5,  1.6,  2.0]);

export function drawAntiShadeTatterPoly(ctx, x0, y0, w0, x1, y1, w1, x2, y2, notch1, notch2, curlAmp) {
  ctx.beginPath();
  // Waist anchor left
  ctx.moveTo(x0 - w0, y0);

  // Left jagged outer edge with serrated tears
  ctx.lineTo(x0 - w0 * 0.75 - notch1 * 0.6, y0 + (y1 - y0) * 0.35);
  ctx.lineTo(x0 - w0 * 0.95 + notch1 * 0.4, y0 + (y1 - y0) * 0.65);
  ctx.lineTo(x1 - w1, y1);
  ctx.lineTo(x1 - w1 * 0.70 - notch2, y1 + (y2 - y1) * 0.35);
  ctx.lineTo(x1 - w1 * 0.95 + notch2 * 0.5, y1 + (y2 - y1) * 0.70);

  // Tip: either a curling smoke hook (like in the Dementor artwork) or sharp jagged fang
  if (curlAmp > 0.5) {
    const cAmp = curlAmp;
    ctx.lineTo(x2 - 1.0, y2 - 1.0);
    if (ctx.bezierCurveTo) {
      ctx.bezierCurveTo(x2 + cAmp * 2.2, y2 + 1.5, x2 + cAmp * 3.8, y2 - 4.5, x2 + cAmp * 2.2, y2 - 7.0);
      ctx.bezierCurveTo(x2 + cAmp * 0.8, y2 - 8.5, x2 - cAmp * 0.2, y2 - 4.0, x2 + cAmp * 0.8, y2 - 2.0);
    } else {
      ctx.lineTo(x2 + cAmp * 2.0, y2 - 5.0);
      ctx.lineTo(x2 + cAmp * 0.8, y2 - 2.0);
    }
    ctx.lineTo(x2, y2);
  } else {
    // Sharp jagged torn point with split fang
    ctx.lineTo(x2 - 1.5, y2 - 2.0);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x2 + 2.0, y2 - 3.5);
    ctx.lineTo(x2 + 1.2, y2 - 1.5);
  }

  // Right jagged returning edge
  ctx.lineTo(x1 + w1 * 0.75 + notch2 * 0.6, y1 + (y2 - y1) * 0.65);
  ctx.lineTo(x1 + w1, y1);
  ctx.lineTo(x1 + w1 * 0.80 - notch1 * 0.5, y0 + (y1 - y0) * 0.75);
  ctx.lineTo(x0 + w0 * 0.85 + notch1 * 0.7, y0 + (y1 - y0) * 0.40);
  ctx.lineTo(x0 + w0, y0);
  ctx.closePath();
}



// Sul Rejected Loaf: Asynchronous organic strand physics constants
// Non-harmonic frequencies and desynchronized phase offsets guarantee that
// the 5 mycelium strands float and undulate independently without lockstep uniformity.
const REJECTED_LOAF_STRAND_FREQ = Object.freeze([0.82, 1.12, 0.94, 1.22, 0.86]);
const REJECTED_LOAF_STRAND_PHASE = Object.freeze([0.0, 2.15, 4.30, 1.45, 3.75]);
const REJECTED_LOAF_STRAND_SPATIAL = Object.freeze([4.2, 5.4, 4.6, 5.8, 4.4]);
const REJECTED_LOAF_STRAND_LEN = Object.freeze([0.48, 0.62, 0.74, 0.58, 0.50]);
const REJECTED_LOAF_STRAND_AMP = Object.freeze([1.10, 0.88, 1.05, 0.90, 1.15]);
const REJECTED_LOAF_SEG_COLORS = Object.freeze(['#581c87', '#6b21a8', '#7e22ce', '#9333ea', '#a855f7', '#c084fc']);
const REJECTED_LOAF_SEG_WIDTHS = Object.freeze([2.6, 2.2, 1.9, 1.6, 1.3, 1.0]);
const _rejectedStrandPtsScratch = Array.from({ length: 5 }, () => Array.from({ length: 7 }, () => ({ x: 0, y: 0 })));

// Sul Defective Loaf: Asynchronous organic root physics constants
// Non-harmonic frequencies and desynchronized phase offsets for the 5 root strands
const DEFECTIVE_LOAF_ROOT_FREQ = Object.freeze([0.78, 1.05, 0.88, 1.18, 0.92]);
const DEFECTIVE_LOAF_ROOT_PHASE = Object.freeze([0.0, 1.95, 3.80, 5.20, 2.60]);
const DEFECTIVE_LOAF_ROOT_SPATIAL = Object.freeze([3.8, 4.9, 4.2, 5.2, 3.9]);
const DEFECTIVE_LOAF_ROOT_LEN = Object.freeze([0.40, 0.52, 0.60, 0.48, 0.36]);
const DEFECTIVE_LOAF_ROOT_AMP = Object.freeze([1.05, 0.92, 1.15, 0.85, 1.00]);
const DEFECTIVE_LOAF_ROOT_COLORS = Object.freeze(['#991b1b', '#b45309', '#365314', '#7f1d1d', '#4d7c0f']);

// Sul Delving Pillar: Matrix-Sentinel Biomechanical Living Tentacle Rig
// 9 strands structured across 3 depth planes (Background: 0..2, Mid: 3..5, Foreground: 6..8)
// Combining squid propulsion pulses, multi-harmonic S-curves, and autonomous searching-finger tip precession
const DELVING_TENTACLE_FREQ = Object.freeze([0.85, 1.18, 0.92, 1.28, 0.88, 1.12, 1.22, 0.96, 1.15]);
const DELVING_TENTACLE_PHASE = Object.freeze([0.0, 1.85, 3.70, 0.95, 4.80, 2.30, 5.40, 1.40, 3.10]);
const DELVING_TENTACLE_SPATIAL = Object.freeze([4.8, 5.6, 5.0, 6.2, 5.4, 5.8, 6.5, 5.2, 6.0]);
const DELVING_TENTACLE_LEN = Object.freeze([0.72, 0.92, 0.78, 1.05, 1.25, 1.00, 0.85, 1.08, 0.80]);
const DELVING_TENTACLE_AMP = Object.freeze([1.15, 0.92, 1.08, 1.30, 1.00, 1.20, 1.35, 1.10, 1.25]);
const DELVING_TENTACLE_CURL_FREQ = Object.freeze([1.9, 2.5, 1.7, 2.9, 2.1, 2.6, 3.2, 2.3, 2.8]);
const DELVING_TENTACLE_CURL_AMP = Object.freeze([3.2, 4.0, 3.5, 5.2, 4.5, 4.8, 6.0, 4.2, 5.5]);
const DELVING_TENTACLE_ANCHOR_X = Object.freeze([-0.28, -0.22, -0.16, -0.24, -0.18, -0.10, -0.20, -0.14, -0.06]);
const DELVING_TENTACLE_ANCHOR_Y = Object.freeze([0.10, 0.14, 0.12, 0.16, 0.20, 0.18, 0.22, 0.25, 0.23]);

const DELVING_TENTACLE_BG_COLORS = Object.freeze([
  '#2a0404', '#3f0808', '#450a0a', '#581c87', '#6b1212', '#7f1d1d', '#991b1b', '#7f1d1d', '#450a0a'
]);
const DELVING_TENTACLE_FG_COLORS = Object.freeze([
  '#450a0a', '#6b1212', '#7f1d1d', '#991b1b', '#b91c1c', '#dc2626', '#ef4444', '#dc2626', '#b91c1c'
]);
const DELVING_TENTACLE_SEG_WIDTHS = Object.freeze([3.6, 3.2, 2.8, 2.4, 2.0, 1.7, 1.4, 1.1, 0.9]);
const _delvingTentaclePtsScratch = Array.from({ length: 9 }, () => Array.from({ length: 10 }, () => ({ x: 0, y: 0 })));

// Sul Harvesting Pillar: 7 Spider Fingers Kinematics (7 Fingers per Arm, 14 Total)
// Fingers are 40% shorter, compact arachnid grasping talons
const HARVESTING_FINGER_SPREAD_ANGLES = Object.freeze([-0.65, -0.42, -0.20, 0.00, 0.22, 0.45, 0.70]);
const HARVESTING_FINGER_FREQ          = Object.freeze([0.72, 0.95, 0.81, 1.15, 0.88, 1.02, 0.65]);
const HARVESTING_FINGER_PHASE         = Object.freeze([0.0, 1.80, 3.60, 5.40, 2.20, 4.00, 5.80]);
const HARVESTING_FINGER_SPATIAL       = Object.freeze([3.5, 4.2, 3.8, 4.5, 4.0, 4.4, 3.6]);
// 40% shorter finger lengths
const HARVESTING_FINGER_LEN           = Object.freeze([0.51, 0.63, 0.75, 0.84, 0.72, 0.60, 0.48]);
const HARVESTING_FINGER_AMP           = Object.freeze([1.10, 0.90, 1.20, 1.00, 1.15, 0.95, 1.05]);
const HARVESTING_FINGER_WIDTHS        = Object.freeze([2.4, 2.0, 1.7, 1.4, 1.1, 0.85, 0.65, 0.50]);
const HARVESTING_FINGER_CURL_FREQ     = Object.freeze([2.6, 3.2, 2.8, 3.6, 3.0, 3.4, 2.5]);
const HARVESTING_FINGER_CURL_AMP      = Object.freeze([4.0, 5.2, 4.5, 6.2, 4.8, 5.5, 3.8]);

// Arm roots on compact green brain head
const HARVESTING_ARM1_ROOT_X = -0.16; // Upper rear of head
const HARVESTING_ARM1_ROOT_Y = -0.22;
const HARVESTING_ARM2_ROOT_X =  0.14; // Upper front of head
const HARVESTING_ARM2_ROOT_Y = -0.06;

// Oil-Slick Iridescent Scales Palette:
// Delicately frightening dark look — the rainbow hue order is kept as a banding
// structure, but every band is desaturated into near-black, ink-blue and umber-brown
// (an oil-slick sheen on dark chitin), never bright/saturated candy tones.
const HARVESTING_ARM_SCALE_COLORS = Object.freeze([
  '#0a0a11', // 0: Void-Black root
  '#141724', // 1: Ink Blue-Black
  '#202a3f', // 2: Midnight Cobalt
  '#263951', // 3: Deep Petrol Blue
  '#152330', // 4: Abyssal Teal-Black
  '#271e18', // 5: Dark Umber
  '#372a1e', // 6: Bronze Umber
  '#1f1215', // 7: Blackened Oxblood-Brown
  '#150c0e', // 8: Dried Blood Black
  '#0d0d0f'  // 9: Obsidian Wrist Chitin
]);
const HARVESTING_ARM_SCALE_HIGHLIGHTS = Object.freeze([
  '#303158', // 0: Muted indigo sheen
  '#3a3580', // 1: Sinister indigo edge
  '#3a62ba', // 2: Deep sapphire glint
  '#286a7d', // 3: Petrol teal sheen
  '#285766', // 4: Dark cyan-blue reflection
  '#513c2a', // 5: Burnished umber rim
  '#6d4d31', // 6: Dull bronze edge
  '#4d2629', // 7: Deep oxblood rim
  '#351a1b', // 8: Blood-black glint
  '#3f3f44'  // 9: Obsidian chitin highlight
]);

// Segment palette: Obsidian Chitin base -> sinister joint ridges -> aged bone talons (per artwork)
const HARVESTING_FINGER_SEG_COLORS = Object.freeze([
  '#09090b', '#18181b', '#27272a', '#3f3f46', '#52525b', '#71717a', '#a1a1aa', '#d4d4d8'
]);
const HARVESTING_FINGER_PLASMA_COLORS = Object.freeze([
  '#2e1065', '#4c1d95', '#581c87', '#7c3aed', '#9333ea', '#c084fc', '#e9d5ff', '#ffffff'
]);

const _harvestingArm1PtsScratch    = Array.from({ length: 11 }, () => ({ x: 0, y: 0 }));
const _harvestingArm2PtsScratch    = Array.from({ length: 11 }, () => ({ x: 0, y: 0 }));
const _harvestingFingerPtsScratch  = Array.from({ length: 7 }, () => Array.from({ length: 10 }, () => ({ x: 0, y: 0 })));
const _harvestingFinger2PtsScratch = Array.from({ length: 7 }, () => Array.from({ length: 10 }, () => ({ x: 0, y: 0 })));

const _processingArmPtsScratch       = Array.from({ length: 5 }, () => Array.from({ length: 8 }, () => ({ x: 0, y: 0 })));
const _processingFingertipScratch    = Array.from({ length: 5 }, () => ({ x: 0, y: 0 }));
const _processingFeelerPtsScratch    = Array.from({ length: 4 }, () => Array.from({ length: 8 }, () => ({ x: 0, y: 0 })));
const _monitorLockSpinePtsScratch = Array.from({ length: 6 }, () => Array.from({ length: 5 }, () => ({ x: 0, y: 0 })));
const _defectiveRootPtsScratch    = Array.from({ length: 5 }, () => Array.from({ length: 6 }, () => ({ x: 0, y: 0 })));

const STICKMAN_LANCE_SPARK_COLORS   = Object.freeze(['#ffffff', '#fef08a', '#facc15', '#f59e0b', '#ea580c']);
const STICKMAN_SHORT_CIRCUIT_COLORS = Object.freeze(['#ffffff', '#00e5ff', '#38bdf8', '#fbbf24', '#f59e0b', '#ea580c']);
const _stickmanJointListScratch     = Array.from({ length: 6 }, () => ({ x: 0, y: 0, weight: 1.0 }));
const GRIZZLY_BULB_OFFSETS          = Object.freeze([-2.2, 0, 2.2]);
const LOOTING_VULTURE_SAND_PALETTE  = Object.freeze(['#fef08a', '#fde047', '#facc15', '#ca8a04', '#ffffff']);
const STICKMAN_IMPACT_PUFF_SPECS    = Object.freeze([
  { oxFrac: -0.16, oy: -4, r: 6.5, exp: 12 },
  { oxFrac: 0.14,  oy: -3, r: 7.0, exp: 14 },
  { oxFrac: 0.36,  oy: -2, r: 5.5, exp: 10 },
  { oxFrac: 0.0,   oy: -6, r: 8.0, exp: 16 }
]);
const STICKMAN_IMPACT_PUFF_BASE_COLORS = Object.freeze([
  '#cbd5e1',
  '#94a3b8',
  '#64748b',
  '#b4a591'
]);

// ---------------------------------------------------------------------------
// Low-tier walk motion
// ---------------------------------------------------------------------------
// The 'low' quality branches replace each multi-part rig (legs / chassis /
// turret layers) with ONE pre-baked composite blit. That is the right saving,
// but the composite is a single static image - units glided across the lane
// with no sense of walking at all, and several of them (Stickman, Pulp,
// Orchestra, A9 Grizzly, Anti Shade) have bounceY forced to 0 as well.
// This helper keeps the one-draw-call budget and adds the motion back using
// the walkPhase/walkStride renderUnit() already computed: only the position
// and size of the single drawImage change (no save/restore, no transform, no
// allocation). Purely cosmetic - never read by the simulation (INVARIANTS §7.11).
const LOW_WALK_STEP = 1;   // legged infantry / walkers: footfall lift + squash & stretch
const LOW_WALK_ROLL = 2;   // wheeled / tracked vehicles: suspension rumble
const LOW_WALK_HOP = 3;    // Pulp's low-gravity moon-hop
const LOW_WALK_FLOAT = 4;  // Anti Shade's drifting glide

function drawLowTierWalkSprite(ctx, src, cx, cy, w, h, isWalking, walkStride, walkPhase, bounceY, style) {
  if (!isWalking) {
    ctx.drawImage(src, cx - w / 2, cy - h / 2, w, h);
    return;
  }
  let lift = 0;
  let sx = 1;
  let sy = 1;
  if (style === LOW_WALK_STEP) {
    const s = walkStride < 0 ? -walkStride : walkStride;
    // renderUnit's bounceY already hops units that are not forced to 0
    lift = bounceY === 0 ? s * 1.6 : 0;
    sx = 1 - 0.035 * s;
    sy = 1 + 0.035 * s;
  } else if (style === LOW_WALK_ROLL) {
    const r = Math.sin(walkPhase * 3);
    lift = r * 0.45;
    sy = 1 + 0.008 * r;
  } else if (style === LOW_WALK_HOP) {
    const hop = Math.abs(Math.sin(walkPhase * 0.42));
    lift = hop * 4.0;
    sx = 1 - 0.05 * hop;
    sy = 1 + 0.07 * hop;
  } else {
    lift = Math.sin(walkPhase * 0.7) * 0.9;
  }
  const dw = w * sx;
  const dh = h * sy;
  // Anchor the bottom edge so squash & stretch reads as the unit pushing off the ground.
  ctx.drawImage(src, cx - dw / 2, cy + h / 2 - dh - lift, dw, dh);
}

// ---------------------------------------------------------------------------
// Hacker rig helpers (zero-allocation per-frame path, INVARIANTS §7.1)
// ---------------------------------------------------------------------------
// renderUnit() used to allocate a closure (drawPart), a colour array and ~10
// concatenated sprite-key strings per Hacker per frame. The key strings are
// now two frozen tables, the closure a module-level function, and the two
// holographic data-stream labels baked once instead of ctx.font + fillText
// every frame while hacking.
function _makeHackerKeys(prefix) {
  return Object.freeze({
    Kranz_Base: prefix + 'Kranz_Base',
    Kranz_UpperArm: prefix + 'Kranz_UpperArm',
    Kranz_MidArm: prefix + 'Kranz_MidArm',
    Legs: prefix + 'Legs',
    Torso: prefix + 'Torso',
    Head: prefix + 'Head',
    Arms_Typing_0: prefix + 'Arms_Typing_0',
    Arms_Typing_1: prefix + 'Arms_Typing_1',
    Arms_Typing: prefix + 'Arms_Typing',
    Arms_Idle: prefix + 'Arms_Idle',
    Keyboard: prefix + 'Keyboard'
  });
}
const HACKER_KEYS = Object.freeze({ Hacker: _makeHackerKeys('Hacker_'), HackerGold: _makeHackerKeys('HackerGold_') });
const HACKER_CABLE_COLORS = Object.freeze(['#0f172a', '#00f0ff', '#facc15', '#ef4444']);

// Draws one articulated Hacker part rotated/scaled around its anatomical joint pivot.
function drawHackerPart(ctx, key, renderRace, team, isHitFlashing, size, px, py, angle, dx, dy, sx, sy) {
  const bmp = buildSpriteBitmap(key, renderRace, team);
  const src = isHitFlashing ? bmp?.flash : bmp?.normal;
  if (!src) return;
  ctx.save();
  ctx.translate(px + dx, py + dy);
  if (angle !== 0) ctx.rotate(angle);
  if (sx !== 1.0 || sy !== 1.0) ctx.scale(sx, sy);
  ctx.translate(-px, -py);
  ctx.drawImage(src, -size / 2, -size / 2, size, size);
  ctx.restore();
}

// Baked once at 3x so the blit stays crisp at the lane's zoom; each canvas holds one label.
const HACKER_STREAM_SCALE = 3;
const _hackerStreamLabels = new Map();
function getHackerStreamLabelCanvas(text, color) {
  const key = text + '|' + color;
  let c = _hackerStreamLabels.get(key);
  if (c) return c;
  if (typeof document === 'undefined') return null;
  c = document.createElement('canvas');
  c.width = 72 * HACKER_STREAM_SCALE;
  c.height = 12 * HACKER_STREAM_SCALE;
  const gctx = c.getContext('2d');
  if (!gctx) return null;
  gctx.scale(HACKER_STREAM_SCALE, HACKER_STREAM_SCALE);
  gctx.font = 'bold 7px monospace';
  gctx.textBaseline = 'alphabetic';
  gctx.fillStyle = color;
  gctx.fillText(text, 0, 9); // baseline 9px below the canvas top
  _hackerStreamLabels.set(key, c);
  return c;
}

// Pulsing indestructible forcefield bubble of a Transcendent Hacker: the
// gradient disc used to be a fresh gradient object every frame; now a one-off bake,
// identical on every quality tier (low keeps its flat fill, see the call site).
let _hackerBubbleCanvas = null;
function getHackerBubbleCanvas() {
  if (_hackerBubbleCanvas) return _hackerBubbleCanvas;
  if (typeof document === 'undefined') return null;
  const refR = 64;
  const c = document.createElement('canvas');
  c.width = refR * 2;
  c.height = refR * 2;
  const gctx = c.getContext('2d');
  if (!gctx) return null;
  // one-off bake (not a per-frame effect) - quality-independent by design
  const grad = gctx.createRadialGradient(refR, refR, refR * 0.3, refR, refR, refR);
  grad.addColorStop(0, 'rgba(0, 240, 255, 0.05)');
  grad.addColorStop(0.7, 'rgba(56, 189, 248, 0.22)');
  grad.addColorStop(1, 'rgba(250, 204, 21, 0.45)');
  gctx.fillStyle = grad;
  gctx.beginPath();
  gctx.arc(refR, refR, refR, 0, Math.PI * 2);
  gctx.fill();
  _hackerBubbleCanvas = c;
  return c;
}

// Pre-baked Sul Heartbeat Core Glow offscreen canvas LUT (quality-gated callsite)
let _sulHeartbeatGradCanvas = null;
function getSulHeartbeatGradCanvas() {
  if (_sulHeartbeatGradCanvas) return _sulHeartbeatGradCanvas;
  if (typeof document === 'undefined') return null;
  const refR = 64;
  const c = document.createElement('canvas');
  c.width = refR * 2;
  c.height = refR * 2;
  const gctx = c.getContext('2d');
  if (!gctx) return null;
  // Pre-bake gradient LUT for this.quality !== 'low' renderer callers
  const grad = gctx.createRadialGradient(refR, refR, 1, refR, refR, refR);
  grad.addColorStop(0, '#bef264');
  grad.addColorStop(0.45, 'rgba(217, 70, 239, 0.35)');
  grad.addColorStop(1, 'rgba(168, 85, 247, 0)');
  gctx.fillStyle = grad;
  gctx.beginPath();
  gctx.arc(refR, refR, refR, 0, Math.PI * 2);
  gctx.fill();
  _sulHeartbeatGradCanvas = c;
  return c;
}

// Zero-allocation cached unit numeric ID resolver
function getUnitNumericId(unit) {
  if (!unit) return 0;
  if (unit._numericId !== undefined) return unit._numericId;
  const raw = unit.id;
  let nid = 0;
  if (typeof raw === 'number') {
    nid = raw | 0;
  } else if (typeof raw === 'string') {
    nid = parseInt(raw.replace(/\D/g, ''), 10) || 0;
  }
  unit._numericId = nid;
  return nid;
}

// Bastion Lock zero-allocation caches (gated by caller this.quality !== 'low')
const _bastionScratchTarget = { x: 0, y: 0 };
const _bastionBackSheenGradCache = new Map();
const _bastionFrontSheenGradCache = new Map();

function getBastionBackSheenGrad(ctx, drawSize) {
  let g = _bastionBackSheenGradCache.get(drawSize);
  if (!g) {
    const bSheenX = -drawSize * (1.5 / 36);
    const bSheenY = -drawSize * (4.5 / 36);
    // Cached sheen gradient for this.quality !== 'low' caller
    g = ctx.createLinearGradient(bSheenX - drawSize * (3 / 36), bSheenY - drawSize * (6 / 36), bSheenX + drawSize * (2 / 36), bSheenY + drawSize * (5 / 36));
    g.addColorStop(0.0, 'rgba(209, 222, 208, 0.0)');
    g.addColorStop(0.5, 'rgba(255, 255, 255, 0.35)');
    g.addColorStop(1.0, 'rgba(209, 222, 208, 0.0)');
    _bastionBackSheenGradCache.set(drawSize, g);
  }
  return g;
}

function getBastionFrontSheenGrad(ctx, drawSize) {
  let g = _bastionFrontSheenGradCache.get(drawSize);
  if (!g) {
    const sheenX = drawSize * (9.2 / 36);
    const sheenY = -drawSize * (2.5 / 36);
    // Cached sheen gradient for this.quality !== 'low' caller
    g = ctx.createLinearGradient(sheenX - drawSize * (4 / 36), sheenY - drawSize * (8 / 36), sheenX + drawSize * (2 / 36), sheenY + drawSize * (6 / 36));
    g.addColorStop(0.0, 'rgba(209, 222, 208, 0.0)');
    g.addColorStop(0.35, 'rgba(225, 238, 224, 0.40)');
    g.addColorStop(0.55, 'rgba(255, 255, 255, 0.65)');
    g.addColorStop(0.75, 'rgba(209, 222, 208, 0.30)');
    g.addColorStop(1.0, 'rgba(209, 222, 208, 0.0)');
    _bastionFrontSheenGradCache.set(drawSize, g);
  }
  return g;
}

// Module-level helper: Articulated segmented biomechanical arm connecting body shoulder to shield socket
function drawBastionArm(ctx, x1, y1, x2, y2, isFront, drawSize) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const midX = (x1 + x2) * 0.5 + (isFront ? -dy * 0.12 : -dy * 0.22);
  const midY = (y1 + y2) * 0.5 + drawSize * (2.5 / 36);

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Outer dark chitin outline
  ctx.strokeStyle = '#080604';
  ctx.lineWidth = Math.max(2.5, drawSize * (3.8 / 36));
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(midX, midY);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Chitin plates (desaturated dark chitin)
  ctx.strokeStyle = isFront ? '#4f4036' : '#362c25';
  ctx.lineWidth = Math.max(1.8, drawSize * (2.4 / 36));
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(midX, midY);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Blue muscle sinew (fully saturated)
  ctx.strokeStyle = isFront ? '#3b82f6' : '#1e293b';
  ctx.lineWidth = Math.max(1.0, drawSize * (1.0 / 36));
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(midX, midY);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Joint nodes
  const rNode = drawSize * (1.8 / 36);
  ctx.fillStyle = '#080604';
  ctx.beginPath();
  ctx.arc(x1, y1, rNode, 0, Math.PI * 2);
  ctx.arc(midX, midY, rNode, 0, Math.PI * 2);
  ctx.arc(x2, y2, rNode, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = isFront ? '#6d5c50' : '#4f4036';
  ctx.beginPath();
  ctx.arc(x1, y1, rNode * 0.55, 0, Math.PI * 2);
  ctx.arc(midX, midY, rNode * 0.55, 0, Math.PI * 2);
  ctx.arc(x2, y2, rNode * 0.55, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function _sampleGrizzlyTerrain(px) {
  const swell = Math.sin(px * 0.010) * 0.35;
  const period = 340;
  const cell = Math.floor(px / period);
  const cellHash = (((cell * 9301 + 49297) % 233280) + 233280) % 233280;
  const offset = 70 + (cellHash % 200);
  const rockCenter = cell * period + offset;
  const dist = px - rockCenter;
  const rockRadius = 11;
  let rock = 0;
  if (Math.abs(dist) < rockRadius) {
    const norm = dist / rockRadius;
    rock = Math.cos(norm * (Math.PI / 2)) * 2.8;
  }
  return swell + rock;
}

const HARVESTING_PHALANX_COLORS_1_LOCKED = Object.freeze(['#4c1d95', '#7c3aed', '#c084fc']);
const HARVESTING_PHALANX_COLORS_1_IDLE = Object.freeze(['#18181b', '#3f3f46', '#a1a1aa']);
const HARVESTING_PHALANX_WIDTHS_1 = Object.freeze([1.8, 1.3, 0.85]);

const HARVESTING_PHALANX_COLORS_2_LOCKED = Object.freeze(['#581c87', '#9333ea', '#f0abfc']);
const HARVESTING_PHALANX_COLORS_2_IDLE = Object.freeze(['#18181b', '#52525b', '#d4d4d8']);
const HARVESTING_PHALANX_WIDTHS_2 = Object.freeze([2.4, 1.7, 1.1]);

const HARVESTING_EYE_SPECS = Object.freeze([
  { dxFrac: 0.22, dyFrac: -0.02, r: 2.6, coreR: 1.2 },
  { dxFrac: 0.16, dyFrac: -0.11, r: 2.0, coreR: 0.9 },
  { dxFrac: 0.18, dyFrac:  0.07, r: 2.1, coreR: 1.0 },
  { dxFrac: 0.08, dyFrac: -0.06, r: 1.7, coreR: 0.7 }
]);

export const PROCESSING_ARM_ANCHOR_OFFSETS = Object.freeze([
  { dx: -0.14, dy: 0.02  },
  { dx: 0.08,  dy: -0.06 },
  { dx: 0.12,  dy: 0.05  },
  { dx: 0.05,  dy: 0.16  },
  { dx: -0.10, dy: 0.22  }
]);

export const PROCESSING_FEELER_ROOTS = Object.freeze([
  { dx: -0.16, dy: 0.20 },
  { dx: -0.06, dy: 0.25 },
  { dx: 0.04,  dy: 0.26 },
  { dx: 0.12,  dy: 0.22 }
]);


const FIRE_MECH_TYPES = Object.freeze(new Set([
  'Panzer', 'HyperPanzer'
]));

const HERO_OR_BOSS_TYPES = Object.freeze(new Set());

// Reusable offscreen radial gradient canvases for unit rendering (callers gate on this.quality !== 'low')
function bakeUnitRadialGradientCanvas(refR, stops) {
  if (typeof document === 'undefined') return null;
  const size = refR * 2;
  const canvas = document.createElement('canvas');
  if (!canvas || !canvas.getContext) return null;
  canvas.width = size;
  canvas.height = size;
  const gctx = canvas.getContext('2d');
  if (!gctx) return null;
  // Quality-aware one-time bake for cached LUT canvases (callers gate on this.quality !== 'low')
  const grad = gctx.createRadialGradient(refR, refR, stops.innerR || 0, refR, refR, refR);
  for (const [offset, color] of stops.colors) grad.addColorStop(offset, color);
  gctx.fillStyle = grad;
  gctx.fillRect(0, 0, size, size);
  return canvas;
}

const _unitFloorLightLUTs = new Map();
function getUnitFloorLightCanvas(color0, color1) {
  const key = color0 + '|' + color1;
  let c = _unitFloorLightLUTs.get(key);
  if (c) return c;
  c = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, color0],
      [0.6, color1],
      [1.0, 'rgba(0, 0, 0, 0)']
    ]
  });
  _unitFloorLightLUTs.set(key, c);
  return c;
}

// Looting Vulture apex solar flare (docs/PERFORMANCE_AND_ONLINE_STABILITY_PLAN_V2_2026-09.md
// §4.1) - baked once at a fixed 128px reference size and blit-scaled to the
// animated flareR below via drawImage, instead of allocating a fresh native
// gradient object every frame for every Looting Vulture on the field.
let _vultureSolarFlareLUT = null;
function getVultureSolarFlareLUT() {
  if (!_vultureSolarFlareLUT) {
    _vultureSolarFlareLUT = bakeUnitRadialGradientCanvas(64, {
      innerR: 1,
      colors: [
        [0, 'rgba(255, 255, 255, 0.98)'],
        [0.3, 'rgba(254, 240, 138, 0.80)'],
        [0.65, 'rgba(234, 179, 8, 0.35)'],
        [1, 'rgba(217, 119, 6, 0)']
      ]
    });
  }
  return _vultureSolarFlareLUT;
}

// Sul Cocoon internal embryonic luminescence LUT (Paradigma 3: Zero-Allocation Gradient LUT)
let _sulCocoonGlowLUTRed = null;
let _sulCocoonGlowLUTGreen = null;
function getSulCocoonGlowLUT(isUrgent) {
  if (isUrgent) {
    if (!_sulCocoonGlowLUTRed && typeof document !== 'undefined') {
      _sulCocoonGlowLUTRed = bakeUnitRadialGradientCanvas(32, {
        innerR: 2,
        colors: [
          [0, 'rgba(244, 63, 94, 1.0)'],
          [0.5, 'rgba(168, 85, 247, 0.6)'],
          [1, 'rgba(0, 0, 0, 0)']
        ]
      });
    }
    return _sulCocoonGlowLUTRed;
  }
  if (!_sulCocoonGlowLUTGreen && typeof document !== 'undefined') {
    _sulCocoonGlowLUTGreen = bakeUnitRadialGradientCanvas(32, {
      innerR: 2,
      colors: [
        [0, 'rgba(132, 204, 22, 1.0)'],
        [0.5, 'rgba(168, 85, 247, 0.6)'],
        [1, 'rgba(0, 0, 0, 0)']
      ]
    });
  }
  return _sulCocoonGlowLUTGreen;
}

// Sul Cocoon pre-baked husk gradient (callers gate on this.quality !== 'low')
let _sulCocoonHuskGrad = null;
function getSulCocoonHuskGrad(ctx) {
  if (!_sulCocoonHuskGrad && ctx) {
    const hw = 13.5;
    const cocoonH = 38;
    const grad = ctx.createLinearGradient(-hw, -cocoonH, hw, 0);
    grad.addColorStop(0, '#3d2015');
    grad.addColorStop(0.25, '#4a281a');
    grad.addColorStop(0.55, '#2e1810');
    grad.addColorStop(0.85, '#20100a');
    grad.addColorStop(1, '#140a06');
    _sulCocoonHuskGrad = grad;
  }
  return _sulCocoonHuskGrad;
}

let _sulCocoonRootsCanvas = null;
function getSulCocoonRootsCanvas() {
  if (_sulCocoonRootsCanvas) return _sulCocoonRootsCanvas;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 38;
  canvas.height = 12;
  const rctx = canvas.getContext('2d');
  rctx.strokeStyle = '#2e1911';
  rctx.lineWidth = 1.6;
  rctx.lineCap = 'round';
  rctx.beginPath();
  const cx = 19, cy = 4;
  rctx.moveTo(cx - 7, cy - 3);
  rctx.quadraticCurveTo(cx - 13, cy - 1, cx - 17, cy + 2);
  rctx.moveTo(cx - 4, cy - 2);
  rctx.quadraticCurveTo(cx - 9, cy + 1, cx - 12, cy + 4);
  rctx.moveTo(cx + 7, cy - 3);
  rctx.quadraticCurveTo(cx + 13, cy - 1, cx + 17, cy + 2);
  rctx.moveTo(cx + 4, cy - 2);
  rctx.quadraticCurveTo(cx + 9, cy + 1, cx + 12, cy + 4);
  rctx.moveTo(cx - 2, cy - 1);
  rctx.lineTo(cx + 2, cy + 4);
  rctx.stroke();
  _sulCocoonRootsCanvas = canvas;
  return canvas;
}

let _sulCocoonWebbingCanvas = null;
function getSulCocoonWebbingCanvas() {
  if (_sulCocoonWebbingCanvas) return _sulCocoonWebbingCanvas;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 36;
  canvas.height = 46;
  const wctx = canvas.getContext('2d');
  const cx = 18;
  const cy = 42;
  const hw = 13.5;
  const cocoonH = 38;

  wctx.save();
  wctx.translate(cx, cy);
  wctx.lineWidth = 1.0;
  wctx.strokeStyle = 'rgba(28, 15, 10, 0.7)';
  for (let sIdx = 0; sIdx < 4; sIdx++) {
    const sY = -cocoonH * (0.22 + sIdx * 0.19);
    const sW = hw * Math.sin(((sY + cocoonH) / cocoonH) * Math.PI) * 0.95;
    wctx.beginPath();
    wctx.ellipse(0, sY + 0.8, Math.max(3, sW), 3.5, -0.15 + (sIdx * 0.08), 0, Math.PI);
    wctx.stroke();
  }
  wctx.strokeStyle = 'rgba(214, 199, 178, 0.65)';
  for (let sIdx = 0; sIdx < 4; sIdx++) {
    const sY = -cocoonH * (0.22 + sIdx * 0.19);
    const sW = hw * Math.sin(((sY + cocoonH) / cocoonH) * Math.PI) * 0.95;
    wctx.beginPath();
    wctx.ellipse(0, sY, Math.max(3, sW), 3.2, -0.15 + (sIdx * 0.08), 0, Math.PI);
    wctx.stroke();
  }
  wctx.strokeStyle = 'rgba(195, 175, 150, 0.45)';
  wctx.lineWidth = 0.8;
  wctx.beginPath();
  wctx.moveTo(-hw * 0.55, -cocoonH * 0.78);
  wctx.quadraticCurveTo(hw * 0.1, -cocoonH * 0.52, hw * 0.65, -cocoonH * 0.28);
  wctx.moveTo(hw * 0.5, -cocoonH * 0.82);
  wctx.quadraticCurveTo(-hw * 0.1, -cocoonH * 0.55, -hw * 0.6, -cocoonH * 0.22);
  wctx.moveTo(-hw * 0.75, -cocoonH * 0.42);
  wctx.quadraticCurveTo(0, -cocoonH * 0.35, hw * 0.75, -cocoonH * 0.45);
  wctx.stroke();

  wctx.strokeStyle = 'rgba(140, 110, 85, 0.6)';
  wctx.lineWidth = 0.8;
  wctx.beginPath();
  wctx.moveTo(-hw * 0.45, -cocoonH * 0.15);
  wctx.lineTo(-hw * 0.48, -cocoonH * 0.04);
  wctx.moveTo(hw * 0.4, -cocoonH * 0.18);
  wctx.lineTo(hw * 0.42, -cocoonH * 0.06);
  wctx.moveTo(hw * 0.1, -cocoonH * 0.1);
  wctx.lineTo(hw * 0.12, 0);
  wctx.stroke();
  wctx.restore();

  _sulCocoonWebbingCanvas = canvas;
  return canvas;
}

const APOSTATE_PARTICLE_COLORS = Object.freeze(['#c026d3', '#a855f7', '#00f0ff', '#e879f9', '#38bdf8']);

// Pre-baked offscreen rim-light gradient canvas (callers gate on this.quality !== 'low')
let _rimLightGradCanvas = null;
function getRimLightGradCanvas() {
  if (!_rimLightGradCanvas && typeof document !== 'undefined') {
    _rimLightGradCanvas = document.createElement('canvas');
    _rimLightGradCanvas.width = 128;
    _rimLightGradCanvas.height = 128;
    const rctx = _rimLightGradCanvas.getContext('2d');
    const rimGrad = rctx.createLinearGradient(128, 0, 128 * 0.45, 128 * 0.55);
    rimGrad.addColorStop(0, 'rgba(224, 242, 254, 0.15)');
    rimGrad.addColorStop(1, 'rgba(224, 242, 254, 0)');
    rctx.fillStyle = rimGrad;
    rctx.fillRect(0, 0, 128, 128);
  }
  return _rimLightGradCanvas;
}

// Pre-baked offscreen teleport beam gradient canvas (callers gate on this.quality !== 'low')
let _teleportBeamCanvas = null;
function getTeleportBeamCanvas() {
  if (!_teleportBeamCanvas && typeof document !== 'undefined') {
    _teleportBeamCanvas = document.createElement('canvas');
    _teleportBeamCanvas.width = 4;
    _teleportBeamCanvas.height = 64;
    const bctx = _teleportBeamCanvas.getContext('2d');
    const grad = bctx.createLinearGradient(0, 0, 0, 64);
    grad.addColorStop(0, 'rgba(0, 240, 255, 0)');
    grad.addColorStop(1, 'rgba(0, 240, 255, 0.55)');
    bctx.fillStyle = grad;
    bctx.fillRect(0, 0, 4, 64);
  }
  return _teleportBeamCanvas;
}

let _sulDefectiveThroatCanvas = null;
function getSulDefectiveThroatCanvas() {
  if (_sulDefectiveThroatCanvas) return _sulDefectiveThroatCanvas;
  _sulDefectiveThroatCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, 'rgba(232, 121, 249, 0.75)'],
      [0.5, 'rgba(192, 38, 211, 0.35)'],
      [1.0, 'rgba(112, 26, 117, 0)']
    ]
  });
  return _sulDefectiveThroatCanvas;
}

let _sulDefectiveCoreLockedCanvas = null;
let _sulDefectiveCoreNormalCanvas = null;
function getSulDefectiveCoreCanvas(locked) {
  if (locked) {
    if (_sulDefectiveCoreLockedCanvas) return _sulDefectiveCoreLockedCanvas;
    _sulDefectiveCoreLockedCanvas = bakeUnitRadialGradientCanvas(32, {
      colors: [
        [0, 'rgba(255, 255, 255, 0.95)'],
        [0.3, 'rgba(240, 171, 252, 0.85)'],
        [0.6, 'rgba(217, 70, 239, 0.45)'],
        [1.0, 'rgba(112, 26, 117, 0)']
      ]
    });
    return _sulDefectiveCoreLockedCanvas;
  }
  if (_sulDefectiveCoreNormalCanvas) return _sulDefectiveCoreNormalCanvas;
  _sulDefectiveCoreNormalCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, 'rgba(240, 171, 252, 0.85)'],
      [0.4, 'rgba(192, 38, 211, 0.40)'],
      [1.0, 'rgba(112, 26, 117, 0)']
    ]
  });
  return _sulDefectiveCoreNormalCanvas;
}

let _sulRejectedThroatCanvas = null;
function getSulRejectedThroatCanvas() {
  if (_sulRejectedThroatCanvas) return _sulRejectedThroatCanvas;
  _sulRejectedThroatCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, 'rgba(232, 121, 249, 0.70)'],
      [0.5, 'rgba(168, 85, 247, 0.35)'],
      [1.0, 'rgba(88, 28, 135, 0)']
    ]
  });
  return _sulRejectedThroatCanvas;
}

let _sulRejectedHaloNormalCanvas = null;
let _sulRejectedHaloHitCanvas = null;
function getSulRejectedHaloCanvas(hit) {
  if (hit) {
    if (_sulRejectedHaloHitCanvas) return _sulRejectedHaloHitCanvas;
    _sulRejectedHaloHitCanvas = bakeUnitRadialGradientCanvas(32, {
      colors: [
        [0, 'rgba(244, 114, 182, 0.55)'],
        [0.5, 'rgba(192, 132, 252, 0.35)'],
        [1.0, 'rgba(88, 28, 135, 0)']
      ]
    });
    return _sulRejectedHaloHitCanvas;
  }
  if (_sulRejectedHaloNormalCanvas) return _sulRejectedHaloNormalCanvas;
  _sulRejectedHaloNormalCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, 'rgba(216, 180, 254, 0.40)'],
      [0.5, 'rgba(126, 34, 206, 0.22)'],
      [1.0, 'rgba(59, 7, 100, 0)']
    ]
  });
  return _sulRejectedHaloNormalCanvas;
}

let _sulApostateGillsCanvas = null;
function getSulApostateGillsCanvas() {
  if (_sulApostateGillsCanvas) return _sulApostateGillsCanvas;
  _sulApostateGillsCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, 'rgba(0, 240, 255, 1.0)'],
      [0.35, 'rgba(6, 182, 212, 0.55)'],
      [0.7, 'rgba(8, 145, 178, 0.15)'],
      [1.0, 'rgba(0, 240, 255, 0)']
    ]
  });
  return _sulApostateGillsCanvas;
}

let _sulApostateOpticCanvas = null;
function getSulApostateOpticCanvas() {
  if (_sulApostateOpticCanvas) return _sulApostateOpticCanvas;
  _sulApostateOpticCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, 'rgba(255, 255, 255, 1.0)'],
      [0.25, 'rgba(0, 240, 255, 0.705)'],
      [0.65, 'rgba(2, 132, 199, 0.294)'],
      [1.0, 'rgba(2, 132, 199, 0)']
    ]
  });
  return _sulApostateOpticCanvas;
}

let _sentinalLockHeadGlowCanvas = null;
export function getSentinalLockHeadGlowCanvas() {
  if (_sentinalLockHeadGlowCanvas) return _sentinalLockHeadGlowCanvas;
  _sentinalLockHeadGlowCanvas = bakeUnitRadialGradientCanvas(24, {
    colors: [
      [0, 'rgba(237, 233, 254, 1.0)'],
      [0.3, 'rgba(139, 92, 246, 0.75)'],
      [0.65, 'rgba(109, 40, 217, 0.28)'],
      [1.0, 'rgba(109, 40, 217, 0)']
    ]
  });
  return _sentinalLockHeadGlowCanvas;
}

let _monitorLockEyeGlowCanvas = null;
function getMonitorLockEyeGlowCanvas() {
  if (_monitorLockEyeGlowCanvas) return _monitorLockEyeGlowCanvas;
  _monitorLockEyeGlowCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, 'rgba(255, 255, 255, 1.0)'],
      [0.25, 'rgba(0, 240, 255, 0.8)'],
      [0.6, 'rgba(8, 145, 178, 0.35)'],
      [1.0, 'rgba(8, 145, 178, 0)']
    ]
  });
  return _monitorLockEyeGlowCanvas;
}

// Frozen static pod offsets and render order for Enforcer Vulture (Zero per-frame allocation, 48x48 native)
export const VULTURE_POD_SPINE_OFFSETS = Object.freeze([
  // 4 symmetrical dual-pod columns following ascending raked dorsal wedge (aufstrebende Struktur)
  // Col 0 (rear, elevated crest):
  { gx: 12.0, gy: 10.0, isFar: true },  // Pod 0 (far rear)
  { gx: 12.0, gy: 15.0, isFar: false }, // Pod 1 (near rear)
  // Col 1 (mid-rear):
  { gx: 18.0, gy: 11.0, isFar: true },  // Pod 2 (far mid-rear)
  { gx: 18.0, gy: 16.0, isFar: false }, // Pod 3 (near mid-rear)
  // Col 2 (mid-front):
  { gx: 24.0, gy: 12.0, isFar: true },  // Pod 4 (far mid-front)
  { gx: 24.0, gy: 17.0, isFar: false }, // Pod 5 (near mid-front)
  // Col 3 (front, neck base):
  { gx: 30.0, gy: 13.0, isFar: true },  // Pod 6 (far front)
  { gx: 30.0, gy: 18.0, isFar: false }  // Pod 7 (near front)
]);
export const VULTURE_RENDER_POD_ORDER = Object.freeze([0, 2, 4, 6, 1, 3, 5, 7]);

let _vulturePodFarCanvas = null;
function getVulturePodFarCanvas() {
  if (_vulturePodFarCanvas) return _vulturePodFarCanvas;
  if (typeof document === 'undefined') return null;
  _vulturePodFarCanvas = bakeUnitRadialGradientCanvas(32, {
    innerR: 2,
    colors: [
      [0.0, '#ffffff'],
      [0.22, '#fda4af'],
      [0.55, '#be123c'],
      [0.82, '#881337'],
      [1.0, 'rgba(59, 7, 18, 0)']
    ]
  });
  return _vulturePodFarCanvas;
}

let _vulturePodNearCanvas = null;
function getVulturePodNearCanvas() {
  if (_vulturePodNearCanvas) return _vulturePodNearCanvas;
  if (typeof document === 'undefined') return null;
  _vulturePodNearCanvas = bakeUnitRadialGradientCanvas(32, {
    innerR: 2,
    colors: [
      [0.0, '#ffffff'],
      [0.22, '#ff6b8b'],
      [0.55, '#ff0033'],
      [0.82, '#991b1b'],
      [1.0, 'rgba(69, 10, 10, 0)']
    ]
  });
  return _vulturePodNearCanvas;
}

let _vulturePodFarEmbryoCanvas = null;
function getVulturePodFarEmbryoCanvas() {
  if (_vulturePodFarEmbryoCanvas) return _vulturePodFarEmbryoCanvas;
  if (typeof document === 'undefined') return null;
  _vulturePodFarEmbryoCanvas = bakeUnitRadialGradientCanvas(32, {
    innerR: 1,
    colors: [
      [0.0, '#fda4af'],
      [0.45, '#be123c'],
      [0.80, '#881337'],
      [1.0, 'rgba(59, 7, 18, 0)']
    ]
  });
  return _vulturePodFarEmbryoCanvas;
}

let _vulturePodNearEmbryoCanvas = null;
function getVulturePodNearEmbryoCanvas() {
  if (_vulturePodNearEmbryoCanvas) return _vulturePodNearEmbryoCanvas;
  if (typeof document === 'undefined') return null;
  _vulturePodNearEmbryoCanvas = bakeUnitRadialGradientCanvas(32, {
    innerR: 1,
    colors: [
      [0.0, '#ffffff'],
      [0.30, '#f87171'],
      [0.65, '#dc2626'],
      [1.0, 'rgba(59, 7, 18, 0)']
    ]
  });
  return _vulturePodNearEmbryoCanvas;
}

let _vultureSacCanvas = null;
function getVultureSacCanvas() {
  if (_vultureSacCanvas) return _vultureSacCanvas;
  if (typeof document === 'undefined') return null;
  _vultureSacCanvas = bakeUnitRadialGradientCanvas(64, {
    innerR: 2,
    colors: [
      [0.0, 'rgba(255, 30, 60, 0.42)'],
      [0.6, 'rgba(200, 15, 45, 0.28)'],
      [0.92, 'rgba(80, 5, 15, 0.45)'],
      [1.0, 'rgba(30, 2, 8, 0)']
    ]
  });
  return _vultureSacCanvas;
}

export const MONITOR_LOCK_CROWN_SPINES = Object.freeze([
  // 6 curved rib-spines arching backward & upward from occipital skull
  // rootX, rootY on 24x24 grid, length in grid units, restAngle (rad, facing right), flareBias
  Object.freeze({ rootX: 9.2, rootY: 1.2, len: 4.8, angle: -2.75, flareBias: 0.50 }),
  Object.freeze({ rootX: 8.0, rootY: 2.0, len: 5.8, angle: -2.90, flareBias: 0.32 }),
  Object.freeze({ rootX: 6.8, rootY: 2.8, len: 6.8, angle: -3.05, flareBias: 0.14 }),
  Object.freeze({ rootX: 5.8, rootY: 3.8, len: 6.2, angle:  3.05, flareBias: -0.14 }),
  Object.freeze({ rootX: 5.8, rootY: 4.6, len: 5.2, angle:  2.90, flareBias: -0.32 }),
  Object.freeze({ rootX: 6.8, rootY: 5.4, len: 4.6, angle:  2.75, flareBias: -0.50 })
]);

export function drawMonitorLockCrownSpines(ctx, mlSize, headOffsetY, isWestern, isHitFlashing, t, numId, isSnapping, strikeT, quality, customFlare = 0) {
  const scale = mlSize / 24.0;

  // 1. Rare irregular autonomous flare (dual prime beat frequencies)
  const tCrown = t + numId * 19.7;
  const beat1 = Math.sin(tCrown * 0.27);
  const beat2 = Math.sin(tCrown * 0.41);
  const rawBeat = beat1 * beat2;
  let idleFlare = 0;
  if (rawBeat > 0.48) {
    const flareNorm = Math.min(1.0, (rawBeat - 0.48) / 0.38);
    idleFlare = Math.sin(flareNorm * Math.PI);
  }

  // 2. Attack strike threat flare
  let attackFlare = 0;
  if (isSnapping) {
    if (strikeT < 0.26) attackFlare = strikeT / 0.26;
    else if (strikeT < 0.68) attackFlare = 1.0;
    else if (strikeT < 0.78) attackFlare = 0.4;
    else attackFlare = Math.max(0, 1.0 - (strikeT - 0.78) / 0.22);
  }

  const totalFlare = Math.max(idleFlare, attackFlare, customFlare);
  const flashCol = isHitFlashing;

  for (let i = 0; i < 6; i++) {
    const sp = MONITOR_LOCK_CROWN_SPINES[i];
    const idleSway = Math.sin(t * 2.5 + i * 1.15 + numId) * 0.05 + Math.sin(t * 4.8 - i * 0.8) * 0.025;
    const baseAngle = sp.angle + idleSway + sp.flareBias * totalFlare;
    const totalAngle = isWestern ? baseAngle : (Math.PI - baseAngle);
    const length = sp.len * scale * (1.0 + 0.15 * totalFlare);

    const rootCol = isWestern ? sp.rootX : (24.0 - sp.rootX);
    const rx = -mlSize / 2 + (rootCol / 24) * mlSize;
    const ry = headOffsetY + (sp.rootY / 24) * mlSize;

    const numSegs = 4;
    const pts = _monitorLockSpinePtsScratch[i];
    const archSign = isWestern ? 1 : -1;
    for (let s = 0; s <= numSegs; s++) {
      const u = s / numSegs;
      const arch = Math.sin(u * Math.PI) * (scale * 0.4) * archSign;
      const perpAngle = totalAngle + Math.PI / 2.0;
      const dist = length * u;
      pts[s].x = rx + Math.cos(totalAngle) * dist + Math.cos(perpAngle) * arch;
      pts[s].y = ry + Math.sin(totalAngle) * dist + Math.sin(perpAngle) * arch;
    }

    // Segment 1: Chrome / steel root socket
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    ctx.lineTo(pts[1].x, pts[1].y);
    ctx.strokeStyle = flashCol ? '#ffffff' : '#334155';
    ctx.lineWidth = Math.max(1.2, scale * 0.95);
    ctx.lineCap = 'round';
    ctx.stroke();

    // Segment 2: Platin-Silber stem
    ctx.beginPath();
    ctx.moveTo(pts[1].x, pts[1].y);
    ctx.lineTo(pts[2].x, pts[2].y);
    ctx.strokeStyle = flashCol ? '#ffffff' : '#f1f5f9';
    ctx.lineWidth = Math.max(1.1, scale * 0.85);
    ctx.stroke();

    // Segment 3: Cool silver & cyan sheen
    ctx.beginPath();
    ctx.moveTo(pts[2].x, pts[2].y);
    ctx.lineTo(pts[3].x, pts[3].y);
    ctx.strokeStyle = flashCol ? '#ffffff' : '#22d3ee';
    ctx.lineWidth = Math.max(1.0, scale * 0.75);
    ctx.stroke();

    // Segment 4: Radiant cyan tip
    ctx.beginPath();
    ctx.moveTo(pts[3].x, pts[3].y);
    ctx.lineTo(pts[4].x, pts[4].y);
    ctx.strokeStyle = flashCol ? '#ffffff' : '#00f0ff';
    ctx.lineWidth = Math.max(0.9, scale * 0.65);
    ctx.stroke();

    // Glow mote at tip
    if (quality !== 'low' && totalFlare > 0.3) {
      const tip = pts[4];
      ctx.fillStyle = 'rgba(0, 240, 255, 0.75)';
      ctx.beginPath();
      ctx.arc(tip.x, tip.y, Math.max(1.0, scale * 0.45 * (0.8 + 0.4 * totalFlare)), 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

let _sulDelvingPillarCoronaCanvas = null;
function getSulDelvingPillarCoronaCanvas() {
  if (_sulDelvingPillarCoronaCanvas) return _sulDelvingPillarCoronaCanvas;
  _sulDelvingPillarCoronaCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, 'rgba(250, 204, 21, 0.28)'],
      [0.5, 'rgba(234, 179, 8, 0.12)'],
      [1.0, 'rgba(202, 138, 4, 0)']
    ]
  });
  return _sulDelvingPillarCoronaCanvas;
}

let _sulDelvingPillarTipGlowCanvas = null;
function getSulDelvingPillarTipGlowCanvas() {
  if (_sulDelvingPillarTipGlowCanvas) return _sulDelvingPillarTipGlowCanvas;
  _sulDelvingPillarTipGlowCanvas = bakeUnitRadialGradientCanvas(24, {
    colors: [
      [0, 'rgba(255, 255, 255, 0.95)'],
      [0.22, 'rgba(254, 240, 138, 0.85)'],
      [0.50, 'rgba(250, 204, 21, 0.45)'],
      [0.80, 'rgba(202, 138, 4, 0.15)'],
      [1.0, 'rgba(202, 138, 4, 0)']
    ]
  });
  return _sulDelvingPillarTipGlowCanvas;
}

let _sulDelvingPillarOcularGlowCanvas = null;
function getSulDelvingPillarOcularGlowCanvas() {
  if (_sulDelvingPillarOcularGlowCanvas) return _sulDelvingPillarOcularGlowCanvas;
  _sulDelvingPillarOcularGlowCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0, 'rgba(255, 255, 255, 0.90)'],
      [0.28, 'rgba(250, 204, 21, 0.65)'],
      [0.65, 'rgba(234, 179, 8, 0.25)'],
      [1.0, 'rgba(161, 98, 7, 0)']
    ]
  });
  return _sulDelvingPillarOcularGlowCanvas;
}

let _sulClaimingPillarFloorGlowCanvas = null;
export function getSulClaimingPillarFloorGlowCanvas() {
  if (_sulClaimingPillarFloorGlowCanvas) return _sulClaimingPillarFloorGlowCanvas;
  if (typeof document === 'undefined') return null;
  _sulClaimingPillarFloorGlowCanvas = bakeUnitRadialGradientCanvas(64, {
    innerR: 8.5,
    colors: [
      [0, 'rgba(239, 68, 68, 1.0)'],
      [0.4, 'rgba(220, 38, 38, 0.62)'],
      [0.75, 'rgba(185, 28, 28, 0.27)'],
      [1.0, 'rgba(127, 29, 29, 0)']
    ]
  });
  return _sulClaimingPillarFloorGlowCanvas;
}

// Pre-baked offscreen DelvingPillar forward search-beam canvas: a canonical
// 64x64 triangular cone (apex at the left-middle, base spanning the full
// right edge) filled with the same linear gradient the per-frame code used
// to build raw. snoutOffX/snoutOffY/beamDist/beamSpread all vary per frame
// (they track the unit's live depth-scaled size and combat state), but the
// beam's shape and color stops are fully determined by `inCombat` (2
// variants), so the caller stretches this cached bitmap into place via
// ctx.drawImage(..., destX, destY, destW, destH) instead of allocating a
// fresh gradient object every frame.
const _sulDelvingPillarBeamCache = [null, null];
function getSulDelvingPillarBeamCanvas(inCombat) {
  const idx = inCombat ? 1 : 0;
  if (_sulDelvingPillarBeamCache[idx]) return _sulDelvingPillarBeamCache[idx];
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const bctx = canvas.getContext('2d');
  // callers gate on this.quality !== 'low' before invoking this builder
  const grad = bctx.createLinearGradient(0, 32, 64, 32);
  grad.addColorStop(0, inCombat ? 'rgba(254, 240, 138, 0.32)' : 'rgba(250, 204, 21, 0.18)');
  grad.addColorStop(0.4, inCombat ? 'rgba(250, 204, 21, 0.14)' : 'rgba(250, 204, 21, 0.07)');
  grad.addColorStop(1, 'rgba(234, 179, 8, 0.0)');
  bctx.fillStyle = grad;
  bctx.beginPath();
  bctx.moveTo(0, 32);
  bctx.lineTo(64, 0);
  bctx.lineTo(64, 64);
  bctx.closePath();
  bctx.fill();
  _sulDelvingPillarBeamCache[idx] = canvas;
  return canvas;
}

// Pre-baked offscreen A9 Grizzly searchlight cone canvas (Paradigma 3: Zero-Allocation Gradient LUT)
let _grizzlySearchlightConeCanvas = null;
function getGrizzlySearchlightConeCanvas() {
  if (_grizzlySearchlightConeCanvas) return _grizzlySearchlightConeCanvas;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  // 128x48 reference size: root emitter at x: 0, y: 18 (y: 15..21)
  canvas.width = 128;
  canvas.height = 48;
  const ctx = canvas.getContext('2d');

  // callers gate on this.quality !== 'low' before invoking this builder
  // A. Primary Atmospheric Volumetric Cone
  const beamGrad = ctx.createLinearGradient(0, 18, 128, 18);
  beamGrad.addColorStop(0.00, 'rgba(255, 255, 235, 0.42)');
  beamGrad.addColorStop(0.12, 'rgba(254, 240, 138, 0.28)');
  beamGrad.addColorStop(0.40, 'rgba(253, 224, 71, 0.14)');
  beamGrad.addColorStop(0.75, 'rgba(250, 204, 21, 0.05)');
  beamGrad.addColorStop(1.00, 'rgba(250, 204, 21, 0.00)');

  ctx.fillStyle = beamGrad;
  ctx.beginPath();
  ctx.moveTo(0, 15);
  ctx.quadraticCurveTo(64, 11, 128, 4);
  ctx.quadraticCurveTo(134, 22, 128, 40);
  ctx.quadraticCurveTo(64, 30, 0, 21);
  ctx.closePath();
  ctx.fill();

  // B. High-Intensity Piercing Core (callers gate on this.quality !== 'low' before invoking this builder)
  const coreLen = 87; // 128 * 0.68
  const coreGrad = ctx.createLinearGradient(0, 18, coreLen, 18);
  coreGrad.addColorStop(0.00, 'rgba(255, 255, 255, 0.55)');
  coreGrad.addColorStop(0.20, 'rgba(255, 255, 225, 0.32)');
  coreGrad.addColorStop(0.60, 'rgba(254, 240, 138, 0.10)');
  coreGrad.addColorStop(1.00, 'rgba(250, 204, 21, 0.00)');

  ctx.fillStyle = coreGrad;
  ctx.beginPath();
  ctx.moveTo(0, 16.5);
  ctx.lineTo(coreLen, 16);
  ctx.lineTo(coreLen, 23);
  ctx.lineTo(0, 19.5);
  ctx.closePath();
  ctx.fill();

  _grizzlySearchlightConeCanvas = canvas;
  return canvas;
}

let _grizzlyGroundPoolCanvas = null;
function getGrizzlyGroundPoolCanvas() {
  if (_grizzlyGroundPoolCanvas) return _grizzlyGroundPoolCanvas;
  if (typeof document === 'undefined') return null;
  // callers gate on this.quality !== 'low' before invoking this builder
  _grizzlyGroundPoolCanvas = bakeUnitRadialGradientCanvas(32, {
    colors: [
      [0.0, 'rgba(254, 240, 138, 0.18)'],
      [0.45, 'rgba(250, 204, 21, 0.08)'],
      [1.0, 'rgba(250, 204, 21, 0.00)']
    ]
  });
  return _grizzlyGroundPoolCanvas;
}

let _grizzlyHalogenBulbCanvas = null;
function getGrizzlyHalogenBulbCanvas() {
  if (_grizzlyHalogenBulbCanvas) return _grizzlyHalogenBulbCanvas;
  if (typeof document === 'undefined') return null;
  // callers gate on this.quality !== 'low' before invoking this builder
  _grizzlyHalogenBulbCanvas = bakeUnitRadialGradientCanvas(16, {
    innerR: 2,
    colors: [
      [0.0, 'rgba(255, 255, 255, 0.90)'],
      [0.4, 'rgba(254, 240, 138, 0.50)'],
      [1.0, 'rgba(250, 204, 21, 0.00)']
    ]
  });
  return _grizzlyHalogenBulbCanvas;
}

const _stickmanCollapseFlashLUTs = new Map();
function getStickmanCollapseFlashCanvas(haloCol) {
  let c = _stickmanCollapseFlashLUTs.get(haloCol);
  if (c) return c;
  // callers gate on this.quality !== 'low' before invoking this builder
  c = bakeUnitRadialGradientCanvas(32, {
    innerR: 2,
    colors: [
      [0, '#ffffff'],
      [0.35, haloCol],
      [0.70, 'rgba(56, 189, 248, 0.6)'],
      [1, 'rgba(56, 189, 248, 0)']
    ]
  });
  _stickmanCollapseFlashLUTs.set(haloCol, c);
  return c;
}

let _sulHarvestingCoreGlowCanvas = null;
function getSulHarvestingCoreGlowCanvas() {
  if (_sulHarvestingCoreGlowCanvas) return _sulHarvestingCoreGlowCanvas;
  _sulHarvestingCoreGlowCanvas = bakeUnitRadialGradientCanvas(28, {
    colors: [
      [0, 'rgba(74, 222, 128, 0.85)'],
      [0.45, 'rgba(22, 163, 74, 0.40)'],
      [1.0, 'rgba(0, 0, 0, 0)']
    ]
  });
  return _sulHarvestingCoreGlowCanvas;
}

let _sulHarvestingEyeGlowCanvas = null;
function getSulHarvestingEyeGlowCanvas() {
  if (_sulHarvestingEyeGlowCanvas) return _sulHarvestingEyeGlowCanvas;
  _sulHarvestingEyeGlowCanvas = bakeUnitRadialGradientCanvas(20, {
    colors: [
      [0, 'rgba(255, 255, 255, 0.95)'],
      [0.25, 'rgba(239, 68, 68, 0.85)'],
      [0.60, 'rgba(153, 27, 27, 0.35)'],
      [1.0, 'rgba(0, 0, 0, 0)']
    ]
  });
  return _sulHarvestingEyeGlowCanvas;
}

let _sulHarvestingTetherGlowCanvas = null;
function getSulHarvestingTetherGlowCanvas() {
  if (_sulHarvestingTetherGlowCanvas) return _sulHarvestingTetherGlowCanvas;
  _sulHarvestingTetherGlowCanvas = bakeUnitRadialGradientCanvas(24, {
    colors: [
      [0, 'rgba(192, 132, 252, 0.90)'],
      [0.45, 'rgba(126, 34, 206, 0.45)'],
      [1.0, 'rgba(0, 0, 0, 0)']
    ]
  });
  return _sulHarvestingTetherGlowCanvas;
}

let _sulProcessingPillarOcularGlowCanvas = null;
export function getSulProcessingPillarOcularGlowCanvas() {
  if (_sulProcessingPillarOcularGlowCanvas) return _sulProcessingPillarOcularGlowCanvas;
  _sulProcessingPillarOcularGlowCanvas = bakeUnitRadialGradientCanvas(36, {
    colors: [
      [0, 'rgba(255, 255, 255, 0.95)'],
      [0.25, 'rgba(224, 242, 254, 0.80)'],
      [0.55, 'rgba(56, 189, 248, 0.35)'],
      [0.85, 'rgba(192, 132, 252, 0.12)'],
      [1.0, 'rgba(192, 132, 252, 0)']
    ]
  });
  return _sulProcessingPillarOcularGlowCanvas;
}

let _sulProcessingPillarArmTipGlowCanvas = null;
export function getSulProcessingPillarArmTipGlowCanvas() {
  if (_sulProcessingPillarArmTipGlowCanvas) return _sulProcessingPillarArmTipGlowCanvas;
  _sulProcessingPillarArmTipGlowCanvas = bakeUnitRadialGradientCanvas(20, {
    colors: [
      [0, 'rgba(255, 255, 255, 0.95)'],
      [0.3, 'rgba(103, 232, 249, 0.85)'],
      [0.65, 'rgba(56, 189, 248, 0.35)'],
      [1.0, 'rgba(56, 189, 248, 0)']
    ]
  });
  return _sulProcessingPillarArmTipGlowCanvas;
}

// Pre-baked linear ray canvas for Sul Processing Pillar pore sweeps (Zero-Allocation Paradigm)
// callers gate on this.quality !== 'low' before using this builder
let _sulProcessingPillarRayCanvas = null;
export function getSulProcessingPillarRayCanvas() {
  if (_sulProcessingPillarRayCanvas) return _sulProcessingPillarRayCanvas;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  if (!canvas || !canvas.getContext) return null;
  canvas.width = 128;
  canvas.height = 8;
  const bctx = canvas.getContext('2d');
  if (!bctx) return null;
  // Quality-aware one-time bake for ray LUT (callers gate on this.quality !== 'low')
  const grad = bctx.createLinearGradient(0, 0, 128, 0);
  grad.addColorStop(0, 'rgba(255, 255, 255, 0.92)');
  grad.addColorStop(0.12, 'rgba(125, 211, 252, 0.65)');
  grad.addColorStop(0.45, 'rgba(56, 189, 248, 0.30)');
  grad.addColorStop(1, 'rgba(56, 189, 248, 0)');
  bctx.fillStyle = grad;
  bctx.fillRect(0, 0, 128, 8);
  _sulProcessingPillarRayCanvas = canvas;
  return _sulProcessingPillarRayCanvas;
}

// 𓉐 SUL PROCESSING PILLAR 36x36 HD RIG (V3 DUAL-LAYER SPECIFICATION)
export const PILLAR_CONFIG = Object.freeze({
  gridSize: 36,
  tentacleThick: 16.0,
  braidDensity: 4.75,
  uCurveSag: 1.25,
  headPivot: { x: 21.5, y: 14.0 },
  headSway: true,
  autoGlance: true,
  godRays: 13,
  groundAttack: { damage: 40, splash: 50, homingSpeed: 130, coreFlash: 40, shockwave: 48 },
  airAttack: { damage: 44, speed: 750, pierceOvershoot: 35, anamorphicFlare: 130, needleSplinters: 10 }
});

// All 19 ocular pores on the spherical lantern face (Rows 4 to 14, Cols 15 to 29)
export const PROCESSING_PORES_36 = Object.freeze([
  { dx: 4.5,  dy: -10, baseAngle: -0.07 },
  { dx: 6.5,  dy: -6,  baseAngle: -0.01 },
  { dx: 6.5,  dy: -4,  baseAngle: 0.02 },
  { dx: 4.5,  dy: -2,  baseAngle: 0.05 },
  { dx: 5.5,  dy: 0,   baseAngle: 0.09 },
  { dx: 5.5,  dy: -8,  baseAngle: -0.04 },
  { dx: 3.5,  dy: -4,  baseAngle: 0.01 },
  { dx: 1.5,  dy: -10, baseAngle: -0.09 },
  { dx: 2.5,  dy: -8,  baseAngle: -0.05 },
  { dx: 4.5,  dy: -6,  baseAngle: -0.02 },
  { dx: 1.5,  dy: -2,  baseAngle: 0.04 },
  { dx: 2.5,  dy: 0,   baseAngle: 0.08 },
  { dx: 0.5,  dy: -4,  baseAngle: 0.00 }
]);

export const PROCESSING_ARM_ANCHORS_36 = Object.freeze([
  { x: 17, y: 15 },
  { x: 19, y: 16 },
  { x: 21, y: 16 },
  { x: 23, y: 16 },
  { x: 26, y: 15 }
]);

/**
 * 𓉐 UNIFIED SUL PROCESSING PILLAR 36x36 HD DUAL-LAYER RIG
 * Shared canonical renderer across Battlefield, Arsenal (Showroom), and Supply Surge (Slot Machine).
 */
export function drawProcessingPillarRig(ctx, {
  x,
  y,
  size,
  dir = 1,
  team = 'western',
  isHitFlashing = false,
  isCharging = false,
  isAttack = false,
  isAir = false,
  chargeProgress = 0,
  blendFactor = (isCharging || isAttack) ? 1.0 : 0.0,
  time = 0,
  wobble = 0,
  quality = 'high',
  headSway = true,
  autoGlance = true,
  showGodRays = true,
  bulletColorHex = '#38bdf8'
} = {}) {
  const pixelScale = size / 36.0;

  // 1. Recoil & Kinematic Tilt
  let recoilX = 0;
  let recoilY = 0;
  let tiltAngle = 0;

  if (isAir) {
    if (isCharging) {
      tiltAngle = -0.32;
      recoilX = -5 * (pixelScale / 8.0);
      recoilY = 4 * (pixelScale / 8.0);
    } else if (isAttack) {
      const kick = Math.sin(Math.min(1.0, chargeProgress) * Math.PI) * 10.0 * (pixelScale / 8.0);
      tiltAngle = -0.38 + (kick * 0.02);
      recoilY = kick * 0.6;
      recoilX = -kick * 0.5;
    }
  } else {
    if (isCharging) {
      tiltAngle = 0.05;
      recoilX = 3 * (pixelScale / 8.0);
    } else if (isAttack) {
      const kick = Math.sin(Math.min(1.0, chargeProgress) * Math.PI) * 12.0 * (pixelScale / 8.0);
      recoilX = -kick;
      tiltAngle = -kick * 0.015;
    }
  }

  // Smooth mode transition for body tilt and recoil
  tiltAngle *= blendFactor;
  recoilX *= blendFactor;
  recoilY *= blendFactor;

  // 2. Head Kinematics around neck pivot (X: 21.5, Y: 14.0) on 36x36 matrix
  const pX = (21.5 - 18.0) * pixelScale; // +3.5 * pixelScale
  const pY = (14.0 - 18.0) * pixelScale; // -4.0 * pixelScale

  const breathSway = headSway ? (Math.sin(time * 1.1 + wobble) * 0.025) : 0;
  let organicGlance = 0;
  if (autoGlance && !isCharging && !isAttack && blendFactor < 0.1) {
    const glanceCycle = ((time + wobble) % 5.5);
    if (glanceCycle >= 1.6 && glanceCycle < 3.0) {
      const u = (glanceCycle - 1.6) / 1.4;
      organicGlance = Math.sin(u * Math.PI) * 0.075;
    } else if (glanceCycle >= 3.6 && glanceCycle < 4.8) {
      const u = (glanceCycle - 3.6) / 1.2;
      organicGlance = -Math.sin(u * Math.PI) * 0.065;
    }
  }

  let modeHeadTilt = 0;
  if (isAir) {
    modeHeadTilt = -0.38;
  } else if (isCharging || isAttack) {
    modeHeadTilt = 0.06;
  }
  modeHeadTilt *= blendFactor;
  const headAngle = breathSway + organicGlance + modeHeadTilt;

  // 3. Bitmaps
  const bodyBmpObj = buildSpriteBitmap('ProcessingPillar_Body', 'Sul', team) || buildSpriteBitmap('ProcessingPillar', 'Sul', team);
  const headBmpObj = buildSpriteBitmap('ProcessingPillar_Head', 'Sul', team) || buildSpriteBitmap('ProcessingPillar', 'Sul', team);
  const bodyBmp = isHitFlashing ? (bodyBmpObj?.flash || bodyBmpObj?.normal) : bodyBmpObj?.normal;
  const headBmp = isHitFlashing ? (headBmpObj?.flash || headBmpObj?.normal) : headBmpObj?.normal;

  ctx.save();
  ctx.translate(x + dir * recoilX, y + recoilY);
  ctx.scale(dir, 1);
  ctx.rotate(tiltAngle);

  // 4. Draw Body Layer (Carapace + Tentacle bundle + Stilts)
  if (bodyBmp) {
    ctx.drawImage(bodyBmp, -size / 2, -size / 2, size, size);
  }

  // 5. Draw Head Layer with Neck Pivot
  ctx.save();
  ctx.translate(pX, pY);
  ctx.rotate(headAngle);
  if (headBmp) {
    ctx.drawImage(headBmp, -pX - size / 2, -pY - size / 2, size, size);
  }

  // 6. Draw 13 God Rays from Pores (Sweep with the head!)
  if (showGodRays && quality !== 'low') {
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    const count = Math.min(PILLAR_CONFIG.godRays, PROCESSING_PORES_36.length);
    const rayBmp = getSulProcessingPillarRayCanvas();

    for (let i = 0; i < count; i++) {
      const p = PROCESSING_PORES_36[i];
      const px = p.dx * pixelScale;
      const py = p.dy * pixelScale;

      const pulse = Math.sin(time * 3.2 + i * 1.1 + wobble);
      const angle = p.baseAngle + Math.sin(time * 2.0 + i * 0.8) * 0.015;
      const rayLen = (155 + pulse * 22) * (pixelScale / 8.0);
      const rayThick = (2.6 + (i % 3) * 1.0) * (pixelScale / 8.0);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.fillRect(px - 1, py - 1, 2, 2);

      if (rayBmp) {
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(angle);
        ctx.drawImage(rayBmp, 0, -rayThick / 2, rayLen, rayThick);
        ctx.restore();
      }
    }
    ctx.restore();
  }
  ctx.restore(); // restores head context

  // 7. Draw 5 Thick Braided Muscle Tentacles (U-Curve in Idle, Convergence in Combat)
  const tipGlowCanvas = getSulProcessingPillarArmTipGlowCanvas();
  const gndFocusX = 26 * pixelScale;
  const gndFocusY = -6 * pixelScale;
  const airFocusX = 22 * pixelScale;
  const airFocusY = -32 * pixelScale;

  for (let a = 0; a < 5; a++) {
    const anc = PROCESSING_ARM_ANCHORS_36[a];
    const startX = (anc.x - 18) * pixelScale;
    const startY = (anc.y - 18) * pixelScale;

    // A. IDLE / WALK: U-SHAPE (uCurveSag = 1.25)
    const phase = time * 1.5 + a * 1.25 + wobble;
    const waveX = Math.cos(phase) * (4 * pixelScale);
    const waveY = Math.sin(phase) * (5 * pixelScale);
    const uSag = PILLAR_CONFIG.uCurveSag;

    let idleCtrlX, idleCtrlY, idleEndX, idleEndY;
    if (a === 0) {
      idleCtrlX = startX - (10 * pixelScale);
      idleCtrlY = startY + (16 * pixelScale * uSag);
      idleEndX   = startX - (18 * pixelScale) + waveX;
      idleEndY   = startY + (2 * pixelScale) + waveY;
    } else if (a === 1) {
      idleCtrlX = startX - (6 * pixelScale);
      idleCtrlY = startY + (20 * pixelScale * uSag);
      idleEndX   = startX - (11 * pixelScale) + waveX;
      idleEndY   = startY + (7 * pixelScale) + waveY;
    } else if (a === 2) {
      idleCtrlX = startX + (12 * pixelScale);
      idleCtrlY = startY + (23 * pixelScale * uSag);
      idleEndX   = startX + (25 * pixelScale) + waveX;
      idleEndY   = startY + (3 * pixelScale) + waveY;
    } else if (a === 3) {
      idleCtrlX = startX + (8 * pixelScale);
      idleCtrlY = startY + (18 * pixelScale * uSag);
      idleEndX   = startX + (18 * pixelScale) + waveX;
      idleEndY   = startY + (8 * pixelScale) + waveY;
    } else {
      idleCtrlX = startX + (14 * pixelScale);
      idleCtrlY = startY + (15 * pixelScale * uSag);
      idleEndX   = startX + (22 * pixelScale) + waveX;
      idleEndY   = startY - (1 * pixelScale) + waveY;
    }

    // B. COMBAT POSE (GROUND CONVERGENCE / AIR JAVELIN)
    let combatCtrlX, combatCtrlY, combatEndX, combatEndY;
    if (isAir) {
      if (isAttack) {
        // 🏹 Javelin Throw Release Gesture:
        // Tentakeln schnellen peitschenartig nach vorn-oben und schleudern den Lichtspeer
        const throwSnap = Math.sin(Math.min(1.0, chargeProgress) * Math.PI) * (22.0 * pixelScale);
        const fingerSpread = (a - 2) * (6.5 * pixelScale);
        combatEndX = airFocusX + throwSnap * 0.85 + fingerSpread;
        combatEndY = airFocusY - throwSnap * 1.25 + (a - 2) * (2.5 * pixelScale);
        combatCtrlX = (startX + combatEndX) * 0.5 + 8 * pixelScale;
        combatCtrlY = (startY + combatEndY) * 0.5 - (16 * pixelScale + throwSnap * 0.5);
      } else {
        // 🎹 Piano Keys / Musical Scale Cadence (Tonleiter)
        const scaleTempo = 7.2;
        const noteOffset = a * 1.15;
        const keyPhase = time * scaleTempo - noteOffset + wobble;
        const keyDip = Math.sin(keyPhase) * (5.5 * pixelScale);
        const keyTremor = Math.sin(time * 28 + a * 2.2 + wobble) * (0.8 * pixelScale);
        const fingerSpread = (a - 2) * (5.5 * pixelScale);

        combatEndX = airFocusX + fingerSpread * 0.85 + Math.cos(keyPhase * 0.5) * (1.5 * pixelScale);
        combatEndY = airFocusY + (a - 2) * (2.2 * pixelScale) + keyDip + keyTremor;
        combatCtrlX = startX + (8 + (a - 2) * 2.0) * pixelScale;
        combatCtrlY = startY - (18 + (a % 2 === 0 ? 3 : 0)) * pixelScale + keyDip * 0.4;
      }
    } else {
      if (isAttack) {
        const attackKick = Math.sin(Math.min(1.0, chargeProgress) * Math.PI);
        const thrust = attackKick * 15 * pixelScale;
        const flareY = (a - 2) * (8 + 8 * attackKick) * pixelScale;
        combatEndX = gndFocusX + thrust + (10 * pixelScale * attackKick);
        combatEndY = gndFocusY + flareY;
        combatCtrlX = (startX + combatEndX) * 0.5 + (8 + 8 * attackKick) * pixelScale;
        combatCtrlY = (startY + combatEndY) * 0.5 + (a < 2 ? -8 : 8) * pixelScale + (a - 2) * 3 * pixelScale * attackKick;
      } else {
        const jitter = Math.sin(time * 24 + a * 2 + wobble) * (1.6 * (pixelScale / 8.0));
        const arcY = (a - 2) * 8 * pixelScale;
        combatEndX = gndFocusX + jitter;
        combatEndY = gndFocusY + arcY + jitter;
        combatCtrlX = (startX + combatEndX) * 0.5 + 8 * pixelScale;
        combatCtrlY = (startY + combatEndY) * 0.5 + (a < 2 ? -8 : 8) * pixelScale;
      }
    }

    // C. ORGANIC INTERPOLATION BETWEEN IDLE AND COMBAT
    let endX, endY, ctrlX, ctrlY;
    if (blendFactor <= 0) {
      endX = idleEndX;
      endY = idleEndY;
      ctrlX = idleCtrlX;
      ctrlY = idleCtrlY;
    } else if (blendFactor >= 1) {
      endX = combatEndX;
      endY = combatEndY;
      ctrlX = combatCtrlX;
      ctrlY = combatCtrlY;
    } else {
      // Organic whip easing with slight sequential tentacle lag
      const armLag = (a * 0.05);
      const clampedLag = Math.max(0, Math.min(1, (blendFactor - armLag * (1 - blendFactor)) / (1 - armLag * 0.45)));
      const smoothT = clampedLag * clampedLag * (3 - 2 * clampedLag); // S-curve

      endX = idleEndX + (combatEndX - idleEndX) * smoothT;
      endY = idleEndY + (combatEndY - idleEndY) * smoothT;
      ctrlX = idleCtrlX + (combatCtrlX - idleCtrlX) * smoothT;
      ctrlY = idleCtrlY + (combatCtrlY - idleCtrlY) * smoothT;
    }

    // Draw Braided Muscle Arm
    const totalThick = PILLAR_CONFIG.tentacleThick * (pixelScale / 8.0);
    ctx.save();
    ctx.lineCap = 'round';

    // 0. Leucht-Aura während Hit-Flash (Tentakel leuchten gemeinsam mit dem Rumpf)
    if (isHitFlashing) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
      ctx.lineWidth = totalThick + 5.0;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.quadraticCurveTo(ctrlX, ctrlY, endX, endY);
      ctx.stroke();
      ctx.restore();
    }

    // 1. Schattensaum / Kontur
    ctx.strokeStyle = isHitFlashing ? '#05070b' : '#120b06';
    ctx.lineWidth = totalThick + 2.5;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.quadraticCurveTo(ctrlX, ctrlY, endX, endY);
    ctx.stroke();

    // 2. Muskelkörper (Im Hit-Flash gleißend weiß)
    ctx.strokeStyle = isHitFlashing ? '#ffffff' : '#3e2417';
    ctx.lineWidth = totalThick;
    ctx.stroke();

    // 3. Geflochtene Dreifach-Stränge (Verdrilltes Tau)
    if (quality !== 'low') {
      const steps = 18;
      const twistFreq = PILLAR_CONFIG.braidDensity;
      const strandColors = isHitFlashing
        ? ['#f1f5f9', '#ffffff', '#e2e8f0']
        : ['#5e3925', '#845337', '#a86e4b'];

      for (let s = 0; s < 3; s++) {
        ctx.strokeStyle = strandColors[s];
        ctx.lineWidth = totalThick * 0.38;
        ctx.beginPath();

        for (let i = 0; i <= steps; i++) {
          const u = i / steps;
          const px = (1 - u) * (1 - u) * startX + 2 * (1 - u) * u * ctrlX + u * u * endX;
          const py = (1 - u) * (1 - u) * startY + 2 * (1 - u) * u * ctrlY + u * u * endY;
          const tx = 2 * (1 - u) * (ctrlX - startX) + 2 * u * (endX - ctrlX);
          const ty = 2 * (1 - u) * (ctrlY - startY) + 2 * u * (endY - ctrlY);
          const len = Math.hypot(tx, ty) || 1;
          const nx = -ty / len;
          const ny = tx / len;

          const twist = Math.sin(u * Math.PI * 2 * twistFreq + (s * Math.PI * 2 / 3) + a);
          const widthAtU = totalThick * (1.0 - u * 0.52);
          const offset = twist * (widthAtU * 0.35);

          const ox = px + nx * offset;
          const oy = py + ny * offset;

          if (i === 0) ctx.moveTo(ox, oy);
          else ctx.lineTo(ox, oy);
        }
        ctx.stroke();
      }

      // Feine spiralförmige Schattenspalten
      ctx.strokeStyle = isHitFlashing ? '#cbd5e1' : '#1a0e08';
      ctx.lineWidth = 1.2;
      for (let i = 2; i < steps; i += 2) {
        const u = i / steps;
        const px = (1 - u) * (1 - u) * startX + 2 * (1 - u) * u * ctrlX + u * u * endX;
        const py = (1 - u) * (1 - u) * startY + 2 * (1 - u) * u * ctrlY + u * u * endY;
        const tx = 2 * (1 - u) * (ctrlX - startX) + 2 * u * (endX - ctrlX);
        const ty = 2 * (1 - u) * (ctrlY - startY) + 2 * u * (endY - ctrlY);
        const len = Math.hypot(tx, ty) || 1;
        const nx = -ty / len;
        const ny = tx / len;
        const widthAtU = totalThick * (1.0 - u * 0.52);

        ctx.beginPath();
        ctx.moveTo(px - nx * widthAtU * 0.45, py - ny * widthAtU * 0.45);
        ctx.lineTo(px + nx * widthAtU * 0.45, py + ny * widthAtU * 0.45);
        ctx.stroke();
      }
    } else {
      ctx.strokeStyle = isHitFlashing ? '#ffffff' : '#845337';
      ctx.lineWidth = totalThick * 0.45;
      ctx.stroke();
    }

    // Speichere Fingerspitzen-Position für den Licht-Speer
    _processingFingertipScratch[a].x = endX;
    _processingFingertipScratch[a].y = endY;

    // Leuchtknospe an der Spitze
    if (tipGlowCanvas) {
      const glowSize = (isHitFlashing || isCharging || isAttack) ? 32 * (pixelScale / 8.0) : 24 * (pixelScale / 8.0);
      ctx.drawImage(tipGlowCanvas, endX - glowSize / 2, endY - glowSize / 2, glowSize, glowSize);
    }
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(endX, endY, (isHitFlashing ? 3.2 : 2.2) * (pixelScale / 8.0), 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  // 8. Draw Light Spear (Licht-Speer) for Anti-Air Attacks
  if (isAir && (isCharging || isAttack)) {
    const spearCenterX = airFocusX + 2.0 * pixelScale;
    const spearCenterY = airFocusY - 4.0 * pixelScale;
    const spearAngle = -0.92; // ~ -53° pointing diagonally up towards the aerial target
    const dirX = Math.cos(spearAngle);
    const dirY = Math.sin(spearAngle);
    const normX = -dirY;
    const normY = dirX;

    if (isCharging) {
      ctx.save();
      ctx.globalCompositeOperation = 'screen';

      // 1. Crackling energy arcs connecting the 5 fingertips to the spear shaft
      ctx.strokeStyle = '#67e8f9';
      ctx.lineWidth = 1.3 * (pixelScale / 8.0);
      for (let i = 0; i < 5; i++) {
        const fx = _processingFingertipScratch[i].x;
        const fy = _processingFingertipScratch[i].y;
        ctx.globalAlpha = 0.65 + 0.35 * Math.sin(time * 24 + i * 1.5);
        ctx.beginPath();
        ctx.moveTo(fx, fy);
        const attachDist = (i - 2) * (5.5 * pixelScale);
        const targetX = spearCenterX + dirX * attachDist;
        const targetY = spearCenterY + dirY * attachDist;
        const jitter = Math.sin(time * 32 + i * 2.8 + wobble) * (2.2 * pixelScale);
        ctx.quadraticCurveTo(
          (fx + targetX) * 0.5 + normX * jitter,
          (fy + targetY) * 0.5 + normY * jitter,
          targetX, targetY
        );
        ctx.stroke();
      }

      // 2. Weißer, blitzartiger Streifen (Sul Bio-Blitz, keine Pfeilspitzen)
      const spearLen = (38 + 22 * Math.min(1.0, chargeProgress)) * pixelScale;
      const halfLen = spearLen * 0.5;
      const tipX = spearCenterX + dirX * halfLen;
      const tipY = spearCenterY + dirY * halfLen;
      const tailX = spearCenterX - dirX * halfLen;
      const tailY = spearCenterY - dirY * halfLen;

      // 5-Segment zackiger Blitz
      const b0x = tailX, b0y = tailY;
      const b1x = tailX + (tipX - tailX) * 0.22 + normX * Math.sin(time * 34 + wobble) * (3.2 * pixelScale);
      const b1y = tailY + (tipY - tailY) * 0.22 + normY * Math.sin(time * 34 + wobble) * (3.2 * pixelScale);
      const b2x = tailX + (tipX - tailX) * 0.48 + normX * Math.sin(time * 42 + 1.2 + wobble) * (-3.8 * pixelScale);
      const b2y = tailY + (tipY - tailY) * 0.48 + normY * Math.sin(time * 42 + 1.2 + wobble) * (-3.8 * pixelScale);
      const b3x = tailX + (tipX - tailX) * 0.76 + normX * Math.sin(time * 38 + 2.5 + wobble) * (3.4 * pixelScale);
      const b3y = tailY + (tipY - tailY) * 0.76 + normY * Math.sin(time * 38 + 2.5 + wobble) * (3.4 * pixelScale);
      const b4x = tipX, b4y = tipY;

      // Äußerer Cyan-Plasmaglimm
      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = 4.8 * (pixelScale / 8.0);
      ctx.globalAlpha = 0.50 * (0.3 + 0.7 * chargeProgress);
      ctx.beginPath();
      ctx.moveTo(b0x, b0y); ctx.lineTo(b1x, b1y); ctx.lineTo(b2x, b2y); ctx.lineTo(b3x, b3y); ctx.lineTo(b4x, b4y);
      ctx.stroke();

      // Scharfe Cyan-Kontur
      ctx.strokeStyle = '#67e8f9';
      ctx.lineWidth = 2.4 * (pixelScale / 8.0);
      ctx.globalAlpha = 0.85 * (0.4 + 0.6 * chargeProgress);
      ctx.beginPath();
      ctx.moveTo(b0x, b0y); ctx.lineTo(b1x, b1y); ctx.lineTo(b2x, b2y); ctx.lineTo(b3x, b3y); ctx.lineTo(b4x, b4y);
      ctx.stroke();

      // Gleißend weißer Kern-Blitz
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.8 * (pixelScale / 8.0);
      ctx.globalAlpha = 1.0;
      ctx.beginPath();
      ctx.moveTo(b0x, b0y); ctx.lineTo(b1x, b1y); ctx.lineTo(b2x, b2y); ctx.lineTo(b3x, b3y); ctx.lineTo(b4x, b4y);
      ctx.stroke();

      // Organische Entladungs-Mikrogabeln
      const f1Len = 4.5 * pixelScale;
      ctx.lineWidth = 1.0 * (pixelScale / 8.0);
      ctx.beginPath();
      ctx.moveTo(b2x, b2y);
      ctx.lineTo(b2x + normX * f1Len + dirX * (f1Len * 0.5), b2y + normY * f1Len + dirY * (f1Len * 0.5));
      ctx.moveTo(b3x, b3y);
      ctx.lineTo(b3x - normX * f1Len + dirX * (f1Len * 0.5), b3y - normY * f1Len + dirY * (f1Len * 0.5));
      ctx.stroke();

      ctx.restore();
    } else if (isAttack) {
      // 🚀 Der weiße Blitzstreifen schießt nach vorn-oben in den Himmel
      const throwDist = chargeProgress * 95 * pixelScale;
      const thrownCenterX = spearCenterX + dirX * throwDist;
      const thrownCenterY = spearCenterY + dirY * throwDist;
      const throwAlpha = Math.max(0.0, 1.0 - chargeProgress * 1.35);

      if (throwAlpha > 0.05) {
        ctx.save();
        ctx.globalCompositeOperation = 'screen';

        const spearLen = 54 * pixelScale;
        const halfLen = spearLen * 0.5;
        const tipX = thrownCenterX + dirX * halfLen;
        const tipY = thrownCenterY + dirY * halfLen;
        const tailX = thrownCenterX - dirX * halfLen;
        const tailY = thrownCenterY - dirY * halfLen;

        const b0x = tailX, b0y = tailY;
        const b1x = tailX + (tipX - tailX) * 0.25 + normX * Math.sin(time * 50) * (4.0 * pixelScale);
        const b1y = tailY + (tipY - tailY) * 0.25 + normY * Math.sin(time * 50) * (4.0 * pixelScale);
        const b2x = tailX + (tipX - tailX) * 0.55 + normX * Math.sin(time * 58) * (-4.5 * pixelScale);
        const b2y = tailY + (tipY - tailY) * 0.55 + normY * Math.sin(time * 58) * (-4.5 * pixelScale);
        const b3x = tailX + (tipX - tailX) * 0.80 + normX * Math.sin(time * 52) * (3.8 * pixelScale);
        const b3y = tailY + (tipY - tailY) * 0.80 + normY * Math.sin(time * 52) * (3.8 * pixelScale);
        const b4x = tipX, b4y = tipY;

        // Geschwindigkeits-Leuchtspur
        ctx.strokeStyle = '#67e8f9';
        ctx.lineWidth = 3.2 * (pixelScale / 8.0);
        ctx.globalAlpha = 0.70 * throwAlpha;
        ctx.beginPath();
        ctx.moveTo(spearCenterX, spearCenterY);
        ctx.lineTo(b0x, b0y);
        ctx.stroke();

        // Weißer Blitz-Strahl im Flug
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2.2 * (pixelScale / 8.0);
        ctx.globalAlpha = 1.0 * throwAlpha;
        ctx.beginPath();
        ctx.moveTo(b0x, b0y); ctx.lineTo(b1x, b1y); ctx.lineTo(b2x, b2y); ctx.lineTo(b3x, b3y); ctx.lineTo(b4x, b4y);
        ctx.stroke();

        // Stoßwellen-Ring beim Abwurf an den Fingerspitzen
        ctx.strokeStyle = '#67e8f9';
        ctx.lineWidth = 2.0;
        ctx.globalAlpha = throwAlpha * 0.8;
        ctx.beginPath();
        ctx.arc(spearCenterX, spearCenterY, 8 + chargeProgress * 20 * (pixelScale / 8.0), 0, Math.PI * 2);
        ctx.stroke();

        ctx.restore();
      }
    }
  }

  ctx.restore(); // restores main translate & scale
}

/**
 * 𓉐 SUL HARVESTING PILLAR LIVING ARCHETYPE RIG
 * Shared canonical renderer across Battlefield and Arsenal (Showroom).
 * Living procedural scaled arms (Arm 1 and Arm 2) with 14 articulated spider-fingers,
 * visceral pulsating Fleischkern core, and dynamic red compound eyes.
 */
export function drawHarvestingPillarRig(ctx, {
  renderX,
  renderY,
  drawSize,
  dir = 1,
  team = 'western',
  renderRace = 'Sul',
  t = 0,
  wobble = 0,
  isHitFlashing = false,
  isAttacking = false,
  attackFactor = 0,
  targetX = null,
  targetY = null,
  isLocked1 = false,
  isLocked2 = false,
  quality = 'high',
  spawnAlpha = 1.0,
  walkFactor = 0
} = {}) {
  const isWestern = (team === 'western');
  const faceDir = dir;
  const pillarSize = drawSize * 1.012;

  // 1. Incommensurable Multi-Harmonic Wave Oscillators
  const f1 = t * 0.63 + wobble;          // Arm main sway
  const f2 = t * 0.89 + wobble * 1.3;    // Arm secondary wave
  const f3 = t * 0.41 + wobble * 0.7;    // Visceral brain respiration
  const f4 = t * 1.37 + wobble;          // Base finger undulation
  const f5 = t * 3.11 + wobble * 1.5;    // Nervous claw tremor

  // Levitation / Flying Hover Dynamics
  const hoverWave = (Math.sin(t * 2.6 + wobble) * 3.8 + Math.sin(t * 1.2) * 1.5) * 0.88;
  const totalBob = hoverWave;

  // Respiration & visceral pumping
  const breathPulse = Math.sin(f3);
  const stretchY = 1.0 + breathPulse * 0.025;
  const squashX = 1.0 - breathPulse * 0.018;

  const forwardPitch = faceDir * (walkFactor * 0.06);
  const totalTilt = forwardPitch;

  const pillarX = renderX;
  const pillarY = renderY + totalBob - 4;

  let hasTarget = false;
  let hasTargetAngle = false;
  let targetAngle = 0;
  let grabX1 = 0;
  let grabY1 = 0;
  let grabX2 = 0;
  let grabY2 = 0;

  if (targetX !== null && targetY !== null) {
    hasTarget = true;
    const dx = targetX - pillarX;
    const dy = targetY - (pillarY - pillarSize * 0.15);
    targetAngle = Math.atan2(dy, dx);
    hasTargetAngle = true;
    grabX1 = targetX - 16;
    grabY1 = targetY - 14;
    grabX2 = targetX - 16;
    grabY2 = targetY + 14;
  } else {
    const defaultTargetX = pillarX + faceDir * (pillarSize * 1.8);
    const defaultTargetY = pillarY - 6;
    grabX1 = defaultTargetX;
    grabY1 = defaultTargetY - 6;
    grabX2 = defaultTargetX;
    grabY2 = defaultTargetY + 6;
  }

  const flightFactor = Math.max(0, 1.0 - attackFactor);

  // Stochastic Nervous Micro-Twitches (Periodic rapid muscle shivers)
  const twitchCycle = ((t * 0.45 + wobble * 1.9) % 1.0);
  const isTwitching = twitchCycle < 0.12;
  const twitchAmp = isTwitching ? Math.sin(twitchCycle * 50.0) * 4.2 : 0;

  // Proportional Dimensions
  const headSize = pillarSize * 0.74;
  const armLength = pillarSize * 0.97;
  const isLow = (quality === 'low');

  // Forward-projecting head offset
  const headForwardOffset = faceDir * (headSize * 0.16);
  const headX = pillarX + headForwardOffset;
  const headY = pillarY;

  // Arm Roots on Forward-Jutting Green Brain Head
  const cosT = Math.cos(totalTilt);
  const sinT = Math.sin(totalTilt);

  const localRoot1X = faceDir * HARVESTING_ARM1_ROOT_X * headSize * squashX;
  const localRoot1Y = HARVESTING_ARM1_ROOT_Y * headSize * stretchY;
  const root1X = headX + cosT * localRoot1X - sinT * localRoot1Y;
  const root1Y = headY + sinT * localRoot1X + cosT * localRoot1Y;

  const localRoot2X = faceDir * HARVESTING_ARM2_ROOT_X * headSize * squashX;
  const localRoot2Y = HARVESTING_ARM2_ROOT_Y * headSize * stretchY;
  const root2X = headX + cosT * localRoot2X - sinT * localRoot2Y;
  const root2Y = headY + sinT * localRoot2X + cosT * localRoot2Y;

  _harvestingArm1PtsScratch[0].x = root1X;
  _harvestingArm1PtsScratch[0].y = root1Y;
  _harvestingArm2PtsScratch[0].x = root2X;
  _harvestingArm2PtsScratch[0].y = root2Y;

  // Calculate 10 segments along Arm 1 (Background Arm)
  for (let i = 1; i <= 10; i++) {
    const u = i / 10;
    const uCurveX1 = (-1.6 * u + 2.0 * u * u) * armLength;
    const flightWaveX1 = faceDir * Math.cos(t * 2.5 - u * 2.0 + wobble) * (u * 3.5);
    const flightX = root1X + faceDir * uCurveX1 + flightWaveX1;

    const uCurveY1 = -(headSize * 0.72 * u) - Math.sin(u * Math.PI) * (headSize * 0.32);
    const elbowFlex1 = Math.sin(t * 2.8 + wobble) * (Math.sin(u * Math.PI) * 4.5);
    const forearmWave1 = Math.sin(t * 3.4 - u * 2.4 + wobble) * (u * 4.0);
    const flightY = root1Y + uCurveY1 + elbowFlex1 + forearmWave1;

    const dist1 = Math.hypot(grabX1 - root1X, grabY1 - root1Y);
    const archY1 = -Math.sin(u * Math.PI) * Math.min(26, dist1 * 0.18);
    const writheY1 = Math.sin(t * 6.5 - u * 3.5 + wobble) * (Math.sin(u * Math.PI) * (isLocked2 ? 4.5 : 3.0));
    const surgeX1 = Math.cos(t * 5.0 - u * 2.8 + wobble) * (Math.sin(u * Math.PI) * 3.0);
    const attackTwitch1 = isTwitching ? (twitchAmp * u * 0.8) : 0;
    const crushTremor1 = hasTarget ? Math.sin(t * 24.0 + u * 4.0) * (u * (isLocked2 ? 1.8 : 1.0)) : 0;

    const attackReachX = root1X + (grabX1 - root1X) * u + surgeX1;
    const attackArchY = root1Y + (grabY1 - root1Y) * u + archY1 + writheY1 + attackTwitch1 + crushTremor1;

    _harvestingArm1PtsScratch[i].x = flightX * flightFactor + attackReachX * attackFactor;
    _harvestingArm1PtsScratch[i].y = flightY * flightFactor + attackArchY * attackFactor;
  }

  // Calculate 10 segments along Arm 2 (Foreground Arm)
  for (let i = 1; i <= 10; i++) {
    const u = i / 10;
    const uCurveX2 = (-1.5 * u + 1.9 * u * u) * armLength;
    const flightWaveX2 = faceDir * Math.cos(t * 2.3 - u * 1.8 + wobble + 1.2) * (u * 3.5);
    const flight2X = root2X + faceDir * uCurveX2 + flightWaveX2;

    const uCurveY2 = -(headSize * 0.66 * u) - Math.sin(u * Math.PI * 0.95) * (headSize * 0.28);
    const elbowFlex2 = Math.sin(t * 2.6 + wobble + 1.5) * (Math.sin(u * Math.PI) * 4.2);
    const forearmWave2 = Math.sin(t * 3.1 - u * 2.2 + wobble + 1.8) * (u * 4.5);
    const flight2Y = root2Y + uCurveY2 + elbowFlex2 + forearmWave2;

    const dist2 = Math.hypot(grabX2 - root2X, grabY2 - root2Y);
    const archY2 = Math.sin(u * Math.PI) * Math.min(18, dist2 * 0.14);
    const writheY2 = Math.sin(t * 6.0 - u * 3.2 + wobble + 1.8) * (Math.sin(u * Math.PI) * (isLocked2 ? 4.5 : 3.0));
    const surgeX2 = Math.cos(t * 4.8 - u * 2.5 + wobble + 1.2) * (Math.sin(u * Math.PI) * 3.0);
    const attackTwitch2 = isTwitching ? (-twitchAmp * u * 0.8) : 0;
    const crushTremor2 = hasTarget ? Math.sin(t * 24.0 + u * 4.0 + 1.4) * (u * (isLocked2 ? 1.8 : 1.0)) : 0;

    const attack2ReachX = root2X + (grabX2 - root2X) * u + surgeX2;
    const attack2ArchY = root2Y + (grabY2 - root2Y) * u + archY2 + writheY2 + attackTwitch2 + crushTremor2;

    _harvestingArm2PtsScratch[i].x = flight2X * flightFactor + attack2ReachX * attackFactor;
    _harvestingArm2PtsScratch[i].y = flight2Y * flightFactor + attack2ArchY * attackFactor;
  }

  const wrist1 = _harvestingArm1PtsScratch[10];
  const wrist2 = _harvestingArm2PtsScratch[10];

  const arm1Dx = wrist1.x - _harvestingArm1PtsScratch[9].x;
  const arm1Dy = wrist1.y - _harvestingArm1PtsScratch[9].y;
  const tipAngle1 = Math.atan2(arm1Dy, arm1Dx);

  const arm2Dx = wrist2.x - _harvestingArm2PtsScratch[9].x;
  const arm2Dy = wrist2.y - _harvestingArm2PtsScratch[9].y;
  const tipAngle2 = Math.atan2(arm2Dy, arm2Dx);

  const isLocked = isLocked1 || isLocked2;
  let clench1 = 0;
  let open1 = 0;
  let clench2 = 0;
  let open2 = 0;

  if (isLocked) {
    const siphonPulse = Math.sin(t * (isLocked2 ? 8.0 : 5.5) + wobble);
    const lockedClench = 0.85 + 0.15 * Math.max(0, siphonPulse);
    clench1 = lockedClench;
    clench2 = lockedClench;
  } else {
    const graspSpeed = attackFactor > 0.1 ? 5.2 : 3.6;
    const graspWave = Math.sin(t * graspSpeed + wobble);
    clench1 = Math.max(0, graspWave);
    clench2 = clench1;
    open1 = Math.max(0, -graspWave);
    open2 = open1;
  }

  const norm1X = -Math.sin(tipAngle1);
  const norm1Y =  Math.cos(tipAngle1);

  for (let s = 0; s < 7; s++) {
    const spreadAngle = HARVESTING_FINGER_SPREAD_ANGLES[s];
    const fLen = headSize * HARVESTING_FINGER_LEN[s] * 0.88 * stretchY;
    const len1 = fLen * 0.38;
    const len2 = fLen * 0.34;
    const len3 = fLen * 0.28;

    const palmLateral1 = (s - 3) * 0.32;
    const palmForward1 = (1.0 - Math.pow((s - 3) / 3, 2)) * 0.35;
    const root1FingX = wrist1.x + norm1X * palmLateral1 + Math.cos(tipAngle1) * palmForward1;
    const root1FingY = wrist1.y + norm1Y * palmLateral1 + Math.sin(tipAngle1) * palmForward1;

    _harvestingFingerPtsScratch[s][0].x = root1FingX;
    _harvestingFingerPtsScratch[s][0].y = root1FingY;

    const sFreq1 = (1.8 + HARVESTING_FINGER_FREQ[s] * 2.2);
    const sPhase1 = HARVESTING_FINGER_PHASE[s];
    const digitT1 = t * sFreq1 + sPhase1 + wobble;

    const rawWave1 = Math.sin(digitT1);
    const horrorSnap1 = Math.sign(rawWave1) * Math.pow(Math.abs(rawWave1), 0.65);
    const eerieHesitation1 = Math.sin(digitT1 * 0.48);
    const digitTwitch1 = Math.sin(t * 26.0 + s * 8.3) * 0.09 * (Math.sin(digitT1 * 0.65) > 0.35 ? 1 : 0);

    const flightSpread1 = spreadAngle * (0.34 + 0.22 * Math.sin(digitT1 * 0.85 + 0.6));
    const attackSpread1 = spreadAngle * (0.32 * (1.0 - clench1 * 0.45) + open1 * 0.55);
    const baseAngle1 = tipAngle1 + (flightSpread1 * flightFactor + attackSpread1 * attackFactor);

    const knuckleHorror1 = faceDir * (horrorSnap1 * 0.48 + eerieHesitation1 * 0.22 + (s % 2 === 0 ? 0.24 : -0.18));
    const phi1_flight = baseAngle1 - faceDir * (0.10 + horrorSnap1 * 0.12);
    const phi2_flight = phi1_flight + knuckleHorror1;

    const midHorror1 = faceDir * (Math.sin(digitT1 * 1.35 + 1.4) * 0.52 + 0.36 + digitTwitch1);
    const phi3_flight = phi2_flight + midHorror1;

    const cupping1 = faceDir * ((3 - s) * 0.06) * clench1;
    const openRelief1 = -faceDir * (open1 * 0.35);
    const gripTremor1 = isLocked ? Math.sin(t * 26.0 + s * 1.5) * 0.04 : 0;
    const attackTwitchF1 = (isTwitching && (s === 1 || s === 5)) ? (twitchAmp * 0.04) : 0;

    const phi1_attack = baseAngle1 + openRelief1 + faceDir * (clench1 * 0.20) + gripTremor1 + attackTwitchF1;
    const knuckleGrip1 = faceDir * (clench1 * 0.70) + cupping1;
    const phi2_attack = phi1_attack + knuckleGrip1;
    const talonGrip1 = faceDir * (clench1 * 0.85);
    const phi3_attack = phi2_attack + talonGrip1;

    const phi1 = phi1_flight * flightFactor + phi1_attack * attackFactor;
    const phi2 = phi2_flight * flightFactor + phi2_attack * attackFactor;
    const phi3 = phi3_flight * flightFactor + phi3_attack * attackFactor;

    let curX = root1FingX;
    let curY = root1FingY;

    curX += Math.cos(phi1) * len1;
    curY += Math.sin(phi1) * len1;
    _harvestingFingerPtsScratch[s][1].x = curX;
    _harvestingFingerPtsScratch[s][1].y = curY;

    curX += Math.cos(phi2) * len2;
    curY += Math.sin(phi2) * len2;
    _harvestingFingerPtsScratch[s][2].x = curX;
    _harvestingFingerPtsScratch[s][2].y = curY;

    curX += Math.cos(phi3) * len3;
    curY += Math.sin(phi3) * len3;
    _harvestingFingerPtsScratch[s][3].x = curX;
    _harvestingFingerPtsScratch[s][3].y = curY;
  }

  const norm2X = -Math.sin(tipAngle2);
  const norm2Y =  Math.cos(tipAngle2);

  for (let s = 0; s < 7; s++) {
    const spreadAngle = HARVESTING_FINGER_SPREAD_ANGLES[s];
    const fLen = headSize * HARVESTING_FINGER_LEN[s] * 0.98 * stretchY;
    const len1 = fLen * 0.38;
    const len2 = fLen * 0.34;
    const len3 = fLen * 0.28;

    const palmLateral2 = (s - 3) * 0.35;
    const palmForward2 = (1.0 - Math.pow((s - 3) / 3, 2)) * 0.35;
    const root2FingX = wrist2.x + norm2X * palmLateral2 + Math.cos(tipAngle2) * palmForward2;
    const root2FingY = wrist2.y + norm2Y * palmLateral2 + Math.sin(tipAngle2) * palmForward2;

    _harvestingFinger2PtsScratch[s][0].x = root2FingX;
    _harvestingFinger2PtsScratch[s][0].y = root2FingY;

    const sFreq2 = (1.9 + HARVESTING_FINGER_FREQ[(s + 3) % 7] * 2.1);
    const sPhase2 = HARVESTING_FINGER_PHASE[(s + 4) % 7] + 1.7;
    const digitT2 = t * sFreq2 + sPhase2 + wobble;

    const rawWave2 = Math.sin(digitT2);
    const horrorSnap2 = Math.sign(rawWave2) * Math.pow(Math.abs(rawWave2), 0.65);
    const eerieHesitation2 = Math.sin(digitT2 * 0.48);
    const digitTwitch2 = Math.sin(t * 27.0 + s * 8.7 + 1.5) * 0.09 * (Math.sin(digitT2 * 0.65) > 0.35 ? 1 : 0);

    const flightSpread2 = spreadAngle * (0.36 + 0.22 * Math.sin(digitT2 * 0.85 + 1.1));
    const attackSpread2 = spreadAngle * (0.34 * (1.0 - clench2 * 0.45) + open2 * 0.55);
    const baseAngle2 = tipAngle2 + (flightSpread2 * flightFactor + attackSpread2 * attackFactor);

    const knuckleHorror2 = faceDir * (horrorSnap2 * 0.46 + eerieHesitation2 * 0.20 + (s % 2 === 1 ? 0.24 : -0.18));
    const phi1_flight2 = baseAngle2 - faceDir * (0.08 + horrorSnap2 * 0.12);
    const phi2_flight2 = phi1_flight2 + knuckleHorror2;

    const midHorror2 = faceDir * (Math.sin(digitT2 * 1.35 + 1.6) * 0.50 + 0.34 + digitTwitch2);
    const phi3_flight2 = phi2_flight2 + midHorror2;

    const cupping2 = faceDir * ((3 - s) * 0.065) * clench2;
    const openRelief2 = faceDir * (open2 * 0.35);
    const gripTremor2 = isLocked ? Math.sin(t * 26.0 + s * 1.5 + 1.2) * 0.04 : 0;
    const attackTwitchF2 = (isTwitching && (s === 1 || s === 5)) ? (-twitchAmp * 0.04) : 0;

    const phi1_attack2 = baseAngle2 + openRelief2 - faceDir * (clench2 * 0.20) + gripTremor2 + attackTwitchF2;
    const knuckleGrip2 = -faceDir * (clench2 * 0.72) - cupping2;
    const phi2_attack2 = phi1_attack2 + knuckleGrip2;
    const talonGrip2 = -faceDir * (clench2 * 0.88);
    const phi3_attack2 = phi2_attack2 + talonGrip2;

    const phi1 = phi1_flight2 * flightFactor + phi1_attack2 * attackFactor;
    const phi2 = phi2_flight2 * flightFactor + phi2_attack2 * attackFactor;
    const phi3 = phi3_flight2 * flightFactor + phi3_attack2 * attackFactor;

    let curX = root2FingX;
    let curY = root2FingY;

    curX += Math.cos(phi1) * len1;
    curY += Math.sin(phi1) * len1;
    _harvestingFinger2PtsScratch[s][1].x = curX;
    _harvestingFinger2PtsScratch[s][1].y = curY;

    curX += Math.cos(phi2) * len2;
    curY += Math.sin(phi2) * len2;
    _harvestingFinger2PtsScratch[s][2].x = curX;
    _harvestingFinger2PtsScratch[s][2].y = curY;

    curX += Math.cos(phi3) * len3;
    curY += Math.sin(phi3) * len3;
    _harvestingFinger2PtsScratch[s][3].x = curX;
    _harvestingFinger2PtsScratch[s][3].y = curY;
  }

  // LAYER A: Background Scaled Arm 1 & 7 Spider Fingers
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (let k = 0; k < 10; k++) {
    const p0 = _harvestingArm1PtsScratch[k];
    const p1 = _harvestingArm1PtsScratch[k + 1];
    ctx.strokeStyle = '#09090b';
    ctx.lineWidth = Math.max(2.8, (10 - k) * 0.72);
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.lineTo(p1.x, p1.y);
    ctx.stroke();
  }

  for (let k = 0; k < 10; k++) {
    const p0 = _harvestingArm1PtsScratch[k];
    const p1 = _harvestingArm1PtsScratch[k + 1];
    ctx.strokeStyle = HARVESTING_ARM_SCALE_COLORS[k];
    ctx.lineWidth = Math.max(2.1, (10 - k) * 0.57);
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.lineTo(p1.x, p1.y);
    ctx.stroke();

    ctx.strokeStyle = HARVESTING_ARM_SCALE_HIGHLIGHTS[k];
    ctx.lineWidth = Math.max(0.9, (10 - k) * 0.25);
    ctx.stroke();
  }

  const fArm1 = _harvestingArm1PtsScratch[8];
  const fArmW1 = _harvestingArm1PtsScratch[9];
  const tWave1 = Math.sin(t * 3.5 + wobble) * 3.5;
  ctx.strokeStyle = '#18181b';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(fArm1.x, fArm1.y);
  ctx.lineTo(fArm1.x - faceDir * 5, fArm1.y + 4);
  ctx.lineTo(fArm1.x - faceDir * 9, fArm1.y + 7 + tWave1);
  ctx.moveTo(fArmW1.x, fArmW1.y);
  ctx.lineTo(fArmW1.x - faceDir * 6, fArmW1.y - 3);
  ctx.lineTo(fArmW1.x - faceDir * 11, fArmW1.y - 5 - tWave1);
  ctx.stroke();

  ctx.fillStyle = '#18181b';
  ctx.beginPath();
  ctx.arc(wrist1.x, wrist1.y, 1.2, 0, Math.PI * 2);
  ctx.fill();

  const phalanxColors1 = isLocked2 ? HARVESTING_PHALANX_COLORS_1_LOCKED : HARVESTING_PHALANX_COLORS_1_IDLE;
  const phalanxWidths1 = HARVESTING_PHALANX_WIDTHS_1;

  for (let k = 1; k <= 3; k++) {
    const prevIdx = k - 1;
    ctx.strokeStyle = phalanxColors1[k - 1];
    ctx.lineWidth = phalanxWidths1[k - 1];
    ctx.beginPath();
    for (let s = 0; s < 7; s++) {
      ctx.moveTo(_harvestingFingerPtsScratch[s][prevIdx].x, _harvestingFingerPtsScratch[s][prevIdx].y);
      ctx.lineTo(_harvestingFingerPtsScratch[s][k].x, _harvestingFingerPtsScratch[s][k].y);
    }
    ctx.stroke();
  }

  if (!isLow) {
    ctx.fillStyle = isLocked2 ? '#9333ea' : '#27272a';
    ctx.beginPath();
    for (let s = 0; s < 7; s++) {
      const j1 = _harvestingFingerPtsScratch[s][1];
      ctx.moveTo(j1.x + 1.2, j1.y);
      ctx.arc(j1.x, j1.y, 1.2, 0, Math.PI * 2);
    }
    ctx.fill();

    ctx.fillStyle = isLocked2 ? '#c084fc' : '#52525b';
    ctx.beginPath();
    for (let s = 0; s < 7; s++) {
      const j2 = _harvestingFingerPtsScratch[s][2];
      ctx.moveTo(j2.x + 0.9, j2.y);
      ctx.arc(j2.x, j2.y, 0.9, 0, Math.PI * 2);
    }
    ctx.fill();
  }
  ctx.restore();

  // LAYER B: Body Spire Sprite (Head)
  let spriteKey = (attackFactor > 0.25 || isAttacking || isLocked1 || isLocked2) ? 'HarvestingPillar_Harvest' : (walkFactor > 0.25 ? 'HarvestingPillar_Stalk' : 'HarvestingPillar_Idle');
  const pillarBitmap = buildSpriteBitmap(spriteKey, renderRace, team);
  const activeImg = (isHitFlashing && pillarBitmap?.flash) ? pillarBitmap.flash : pillarBitmap?.normal;

  if (activeImg) {
    ctx.save();
    ctx.translate(headX, headY);
    ctx.rotate(faceDir * totalTilt);
    ctx.scale(squashX, stretchY);
    ctx.globalAlpha = spawnAlpha;
    ctx.drawImage(activeImg, -headSize / 2, -headSize / 2, headSize, headSize);
    ctx.restore();
  }

  // LAYER C: Bioluminescent Fleischkern Glow
  if (!isHitFlashing && !isLow) {
    const coreGlowTex = getSulHarvestingCoreGlowCanvas();
    const corePulse = 0.65 + 0.35 * Math.sin(t * (isLocked2 ? 5.5 : 2.2) + wobble);
    const coreRadius = headSize * (isLocked2 ? 0.38 : 0.30);
    ctx.save();
    ctx.globalAlpha = (isLocked2 ? 0.90 : (isLocked1 ? 0.75 : 0.50)) * corePulse * spawnAlpha;
    ctx.drawImage(coreGlowTex, headX + faceDir * (headSize * 0.02) - coreRadius, headY - headSize * 0.04 - coreRadius, coreRadius * 2, coreRadius * 2);
    ctx.restore();
  }

  // LAYER D: Dynamic Compound Ocular Matrix (Glowing red eyes)
  if (!isHitFlashing) {
    const inCombat = isLocked1 || isAttacking || attackFactor > 0.1;
    const eyeFlicker = 0.85 + 0.15 * Math.sin(t * 11.0 + wobble);
    const eyeRadius = headSize * (inCombat ? 0.22 : 0.18);

    let eyeLookX = 0;
    let eyeLookY = 0;
    if (hasTargetAngle) {
      eyeLookX = Math.cos(targetAngle) * (headSize * 0.05);
      eyeLookY = Math.sin(targetAngle) * (headSize * 0.05);
    } else {
      const saccadeStep = Math.floor((t * 0.4 + wobble) % 4);
      eyeLookX = (saccadeStep === 1 ? 1.5 : (saccadeStep === 3 ? -1.5 : 0));
    }

    const eyeBaseX = headX + faceDir * (headSize * 0.20) + eyeLookX;
    const eyeBaseY = headY - (headSize * 0.03) + eyeLookY;

    ctx.save();
    if (!isLow) {
      const eyeGlowTex = getSulHarvestingEyeGlowCanvas();
      ctx.globalAlpha = (inCombat ? 1.0 : 0.85) * eyeFlicker * spawnAlpha;
      ctx.drawImage(eyeGlowTex, eyeBaseX - eyeRadius, eyeBaseY - eyeRadius, eyeRadius * 2, eyeRadius * 2);
      const fR = eyeRadius * 0.65;
      ctx.drawImage(eyeGlowTex, eyeBaseX - faceDir * (headSize * 0.07) - fR, eyeBaseY - (headSize * 0.05) - fR, fR * 2, fR * 2);
    }

    for (let eIdx = 0; eIdx < HARVESTING_EYE_SPECS.length; eIdx++) {
      const ep = HARVESTING_EYE_SPECS[eIdx];
      const ex = headX + faceDir * (headSize * ep.dxFrac) + eyeLookX * 0.6;
      const ey = headY + (headSize * ep.dyFrac) + eyeLookY * 0.6;

      ctx.globalAlpha = (inCombat ? 1.0 : 0.85) * eyeFlicker * spawnAlpha;
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(ex, ey, ep.r * (inCombat ? 1.25 : 1.0), 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(ex, ey, ep.r * 0.70, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = (inCombat ? 1.0 : 0.95) * spawnAlpha;
      ctx.fillStyle = isLocked2 ? '#fef08a' : '#ffffff';
      ctx.beginPath();
      ctx.arc(ex + eyeLookX * 0.2, ey + eyeLookY * 0.2, ep.coreR * (inCombat ? 1.2 : 1.0), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // LAYER E: Foreground Scaled Arm 2 & 7 Spider Fingers
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (let k = 0; k < 10; k++) {
    const p0 = _harvestingArm2PtsScratch[k];
    const p1 = _harvestingArm2PtsScratch[k + 1];
    ctx.strokeStyle = '#09090b';
    ctx.lineWidth = Math.max(3.4, (10 - k) * 0.92);
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.lineTo(p1.x, p1.y);
    ctx.stroke();
  }

  for (let k = 0; k < 10; k++) {
    const p0 = _harvestingArm2PtsScratch[k];
    const p1 = _harvestingArm2PtsScratch[k + 1];
    ctx.strokeStyle = HARVESTING_ARM_SCALE_COLORS[k];
    ctx.lineWidth = Math.max(2.6, (10 - k) * 0.75);
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.lineTo(p1.x, p1.y);
    ctx.stroke();

    ctx.strokeStyle = HARVESTING_ARM_SCALE_HIGHLIGHTS[k];
    ctx.lineWidth = Math.max(1.05, (10 - k) * 0.31);
    ctx.stroke();
  }

  const fArm2 = _harvestingArm2PtsScratch[8];
  const fArmW2 = _harvestingArm2PtsScratch[9];
  const tWave2 = Math.sin(t * 3.8 + wobble + 1.2) * 3.8;
  ctx.strokeStyle = '#18181b';
  ctx.lineWidth = 1.1;
  ctx.beginPath();
  ctx.moveTo(fArm2.x, fArm2.y);
  ctx.lineTo(fArm2.x - faceDir * 6, fArm2.y + 5);
  ctx.lineTo(fArm2.x - faceDir * 10, fArm2.y + 8 + tWave2);
  ctx.moveTo(fArmW2.x, fArmW2.y);
  ctx.lineTo(fArmW2.x - faceDir * 7, fArmW2.y - 4);
  ctx.lineTo(fArmW2.x - faceDir * 12, fArmW2.y - 6 - tWave2);
  ctx.stroke();

  ctx.fillStyle = '#18181b';
  ctx.beginPath();
  ctx.arc(wrist2.x, wrist2.y, 1.3, 0, Math.PI * 2);
  ctx.fill();

  const phalanxColors2 = isLocked2 ? HARVESTING_PHALANX_COLORS_2_LOCKED : HARVESTING_PHALANX_COLORS_2_IDLE;
  const phalanxWidths2 = HARVESTING_PHALANX_WIDTHS_2;

  for (let k = 1; k <= 3; k++) {
    const prevIdx = k - 1;
    ctx.strokeStyle = phalanxColors2[k - 1];
    ctx.lineWidth = phalanxWidths2[k - 1];
    ctx.beginPath();
    for (let s = 0; s < 7; s++) {
      ctx.moveTo(_harvestingFinger2PtsScratch[s][prevIdx].x, _harvestingFinger2PtsScratch[s][prevIdx].y);
      ctx.lineTo(_harvestingFinger2PtsScratch[s][k].x, _harvestingFinger2PtsScratch[s][k].y);
    }
    ctx.stroke();
  }

  if (!isLow) {
    ctx.fillStyle = isLocked2 ? '#c084fc' : '#3f3f46';
    ctx.beginPath();
    for (let s = 0; s < 7; s++) {
      const j1 = _harvestingFinger2PtsScratch[s][1];
      ctx.moveTo(j1.x + 1.4, j1.y);
      ctx.arc(j1.x, j1.y, 1.4, 0, Math.PI * 2);
    }
    ctx.fill();

    ctx.fillStyle = isLocked2 ? '#f0abfc' : '#71717a';
    ctx.beginPath();
    for (let s = 0; s < 7; s++) {
      const j2 = _harvestingFinger2PtsScratch[s][2];
      ctx.moveTo(j2.x + 1.1, j2.y);
      ctx.arc(j2.x, j2.y, 1.1, 0, Math.PI * 2);
    }
    ctx.fill();
  }
  ctx.restore();

  // Rend sparks if attacking and close to target
  if (attackFactor > 0.65 && hasTarget) {
    ctx.save();
    ctx.fillStyle = isLocked2 ? '#e879f9' : '#c084fc';
    for (let p = 0; p < 4; p++) {
      const ang = (p * Math.PI) / 2 + t * 14;
      ctx.fillRect(wrist1.x + Math.cos(ang) * 8, wrist1.y + Math.sin(ang) * 8, 2, 2);
      ctx.fillRect(wrist2.x + Math.cos(ang + 0.5) * 8, wrist2.y + Math.sin(ang + 0.5) * 8, 2, 2);
    }
    ctx.restore();
  }

  return { wrist1, wrist2, clench1, clench2 };
}

let _preserverLockFiringCoronaCanvas = null;
let _preserverLockIdleCoronaCanvas = null;
const _preserverLockCoronaCustomLUTs = new Map();

function _hexToPreserverRgba(hex, alpha) {
  if (typeof hex !== 'string' || !hex.startsWith('#') || (hex.length !== 7 && hex.length !== 4)) {
    return hex;
  }
  let r, g, b;
  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  } else {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getPreserverLockCoronaCanvas(isFiring, customColorHex = null) {
  if (customColorHex) {
    if (typeof document === 'undefined') return null;
    const lutKey = (isFiring ? '1:' : '0:') + customColorHex;
    let cached = _preserverLockCoronaCustomLUTs.get(lutKey);
    if (!cached) {
      cached = bakeUnitRadialGradientCanvas(32, {
        colors: isFiring ? [
          [0, 'rgba(255, 255, 255, 1.0)'],
          [0.2, _hexToPreserverRgba(customColorHex, 0.9)],
          [0.45, _hexToPreserverRgba(customColorHex, 0.75)],
          [0.75, _hexToPreserverRgba(customColorHex, 0.3)],
          [1, _hexToPreserverRgba(customColorHex, 0)]
        ] : [
          [0, 'rgba(255, 255, 255, 0.95)'],
          [0.2, _hexToPreserverRgba(customColorHex, 0.8)],
          [0.45, _hexToPreserverRgba(customColorHex, 0.45)],
          [0.75, _hexToPreserverRgba(customColorHex, 0.15)],
          [1, _hexToPreserverRgba(customColorHex, 0)]
        ]
      });
      _preserverLockCoronaCustomLUTs.set(lutKey, cached);
    }
    return cached;
  }
  if (isFiring) {
    if (!_preserverLockFiringCoronaCanvas) {
      if (typeof document === 'undefined') return null;
      _preserverLockFiringCoronaCanvas = bakeUnitRadialGradientCanvas(32, {
        colors: [
          [0, 'rgba(255, 255, 255, 1.0)'],
          [0.2, 'rgba(240, 249, 255, 0.9)'],
          [0.45, 'rgba(125, 211, 252, 0.75)'],
          [0.75, 'rgba(56, 189, 248, 0.3)'],
          [1, 'rgba(56, 189, 248, 0)']
        ]
      });
    }
    return _preserverLockFiringCoronaCanvas;
  }
  if (!_preserverLockIdleCoronaCanvas) {
    if (typeof document === 'undefined') return null;
    _preserverLockIdleCoronaCanvas = bakeUnitRadialGradientCanvas(32, {
      colors: [
        [0, 'rgba(255, 255, 255, 0.95)'],
        [0.2, 'rgba(224, 242, 254, 0.8)'],
        [0.45, 'rgba(56, 189, 248, 0.45)'],
        [0.75, 'rgba(2, 132, 199, 0.15)'],
        [1, 'rgba(2, 132, 199, 0)']
      ]
    });
  }
  return _preserverLockIdleCoronaCanvas;
}

// 🎗️ Dynamic 8-Bit Cartridge Ammo Belt Simulation (Hyper Devil Patrol)
// Aerodynamic wind flutter during flight, natural gravity catenary hang when stationary
const _BELT_PX = new Float32Array(7);
const _BELT_PY = new Float32Array(7);
const _BELT_ANG = new Float32Array(7);

export function drawHyperDevilPatrolAmmoBelt(ctx, startX, startY, dir, drawSize, age, wobble, flightFactor, beltPhase, quality) {
  const linkLen = drawSize * 0.075;
  const count = 6;
  const isLow = quality === 'low';

  let currX = startX;
  let currY = startY;

  // 1. Precalculate kinematics for all links (Zero-Allocation)
  for (let k = 0; k < count; k++) {
    const hangSway = Math.sin(age * 3.4 + wobble + k * 0.4 + beltPhase) * (0.12 + k * 0.05) - dir * 0.06;
    const hangAngle = (Math.PI * 0.5) + hangSway;
    const windBase = (dir === 1 ? Math.PI : 0);
    const windWave = (dir === 1 ? 1 : -1) * Math.sin(age * 16.0 - k * 0.85 + beltPhase) * (0.34 + k * 0.12);
    const flightAngle = windBase + (dir === 1 ? 0.20 : -0.20) + windWave;
    const angle = hangAngle + (flightAngle - hangAngle) * flightFactor;

    currX += Math.cos(angle) * linkLen;
    currY += Math.sin(angle) * linkLen;

    _BELT_PX[k] = currX;
    _BELT_PY[k] = currY;
    _BELT_ANG[k] = angle;
  }

  // 2. Batched dark steel connecting link (1 stroke for entire chain)
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  for (let k = 0; k < count; k++) {
    ctx.lineTo(_BELT_PX[k], _BELT_PY[k]);
  }
  ctx.stroke();

  // 3. Brass ammunition cartridges
  if (isLow) {
    ctx.fillStyle = '#facc15';
    for (let k = 0; k < count; k++) {
      ctx.fillRect(_BELT_PX[k] - 1.5, _BELT_PY[k] - 1.5, 3.0, 3.0);
    }
  } else {
    for (let k = 0; k < count; k++) {
      const px = _BELT_PX[k];
      const py = _BELT_PY[k];
      const angle = _BELT_ANG[k];

      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(angle);

      // Golden-brass cartridge casing
      ctx.fillStyle = (k % 2 === 0) ? '#facc15' : '#eab308';
      ctx.fillRect(-1.4, -2.5, 2.8, 5.0);
      // Cartridge base rim
      ctx.fillStyle = '#b45309';
      ctx.fillRect(-1.4, -3.2, 2.8, 1.0);
      // High-explosive grenade / copper tip
      ctx.fillStyle = '#f97316';
      ctx.fillRect(-1.0, 2.5, 2.0, 1.8);
      // Metallic specular gleam
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-0.5, -1.5, 1.0, 3.0);

      ctx.restore();
    }
  }
}

export const unitMixin = {
  renderUnit(ctx, unit, battlefield) {
    ctx.save();
    const t = performance.now() * 0.001;
    const isWestern = unit.team === 'western';
    const isSimpleTier = (this.quality === 'low') || (this.isMobile && this.quality !== 'ultra' && this.quality !== 'high');
    // Default facing is the team's forward marching direction, but a unit
    // actively engaging a target (e.g. defending against a flanker near its
    // own base) turns to face that target instead - the sprite itself gets
    // mirrored around renderX further down, and every weapon/recoil/laser
    // offset below already keys off this same `dir`, so they all follow.
    const forwardDir = isWestern ? 1 : -1;
    const dir = unit.facingDir ? unit.facingDir : (unit.target ? (Math.sign(unit.target.x - unit.x) || forwardDir) : forwardDir);
    const teamBadgeColor = isWestern ? '#00f0ff' : '#ff003c';
    const isHitFlashing = unit.hitFlashTimer > 0;
    // Depth cue: units standing further down the lane (closer to the
    // "camera") render larger. Widened from the old tight ±4% range to a
    // stronger 0.75-1.20 spread so the field reads as if seen from a lower
    // camera angle - see chat request "mehr Flucht... als wäre die Kamera
    // weiter unten". Every visual size in this function derives from this
    // local r, so the whole sprite/aura/shadow/HP-bar stack scales
    // together - only unit.radius (used for collision elsewhere) is untouched.
    const laneTop = (battlefield && (battlefield.laneY !== undefined ? battlefield.laneY : battlefield.laneTop)) ?? unit.y;
    const laneH = (battlefield && battlefield.laneHeight) || 1;
    const depthT = laneH > 0 && Number.isFinite(unit.y - laneTop) ? Math.max(0, Math.min(1, (unit.y - laneTop) / laneH)) : 0.5;
    const depthScale = unit.isStructure ? 1 : (0.75 + 0.45 * depthT);
    const r = (unit.radius ?? unit.r ?? 16) * depthScale;
    const isStickmanEarly = (unit.name === 'Stickman' || unit.cardData?.unitType === 'Stickman' || unit.isStickman);
    const drawSize = isStickmanEarly ? Math.max(98, Math.round((unit.isGoldenStatue ? 30 * depthScale : r) * 3.3)) : (r * 2.2);

    // Changeling Disguise: once it has copied an enemy unit's identity,
    // every render decision below (sprite, silhouette, floor light, hero
    // glow) uses that borrowed identity instead of its own blob shape.
    const unitType = (unit.isDecoy && unit.disguiseUnitType) ? unit.disguiseUnitType : (unit.cardData?.unitType || unit.unitType || unit.name);
    const rawType = unit.cardData?.unitType || unit.unitType || unit.name;
    const renderRace = (unit.isDecoy && unit.disguiseRace) ? unit.disguiseRace : unit.race;

    // Movement & Attack Dynamics
    // Hacked / Disabled units smoothly pitch-down their walk animation speed to 0% over 1.0s
    const rawdt = unit._lastRenderTime !== undefined ? Math.max(0, Math.min(1.0, t - unit._lastRenderTime)) : 0;
    unit._lastRenderTime = t;
    if (unit._walkPitchFactor === undefined) {
      unit._walkPitchFactor = 1.0;
    }
    if (unit.isDisabled) {
      unit._walkPitchFactor = Math.max(0.0, unit._walkPitchFactor - rawdt * 1.0); // 1.0s linear pitch-down to 0%
    } else {
      unit._walkPitchFactor = Math.min(1.0, unit._walkPitchFactor + rawdt * 2.5); // crisp power-up when reactivated
    }
    const walkPitch = unit._walkPitchFactor;
    const isPhysicallyWalking = !unit.isStructure && !unit.isHeld && !unit.isDisabled && (unit.isHyperPanzer ? !unit.isStoppedAtBase : (unit.attackCooldown <= 0 || !unit.target || !battlefield?.isInRange?.(unit, unit.target)));
    const isWalking = !unit.isStructure && !unit.isHeld && (isPhysicallyWalking || (unit.isDisabled && walkPitch > 0.01));
    const baseWalkSpeedFactor = Math.max(0.8, ((unit.speed || unit.baseSpeed || 50)) / 45);
    const walkSpeedFactor = baseWalkSpeedFactor * walkPitch;
    if (unit._accumWalkPhase === undefined) {
      unit._accumWalkPhase = t * 9.0 * baseWalkSpeedFactor;
    } else if (isWalking) {
      unit._accumWalkPhase = (unit._accumWalkPhase + rawdt * 9.0 * walkSpeedFactor) % (Math.PI * 200);
    }
    const walkPhase = unit._accumWalkPhase;
    const walkStride = isWalking ? Math.sin(walkPhase) * walkPitch : 0;
    
    // Vertical bouncing / breathing / hovering
    const isOrchestra = (unit.name === 'Orchestra' || unitType === 'Orchestra');
    const isA9Grizzly = (unit.name === 'A9 Grizzly' || unitType === 'A9Grizzly');
    const isA9Pigeon = (unit.isA9Pigeon || unit.name === 'A9 Pigeon' || unitType === 'A9Pigeon');
    const isSulLoaf = (unitType === 'RejectedLoaf' || unitType === 'Rejected_Loaf' || unitType === 'Rejected Loaf' ||
                       unitType === 'DefectiveLoaf' || unitType === 'Defective_Loaf' || unitType === 'Defective Loaf' ||
                       unitType === 'ApostateLoaf' || unitType === 'Apostate_Loaf' || unitType === 'Apostate Loaf' ||
                       unitType === 'ForgottenLoaf' || unitType === 'Forgotten_Loaf' || unitType === 'Forgotten Loaf' || unit.isForgottenLoaf ||
                       unitType === 'HarvestingPillar' || unitType === 'Harvesting_Pillar' || unitType === 'Harvesting Pillar' ||
                       unitType === 'DelvingPillar' || unitType === 'Delving_Pillar' || unitType === 'Delving Pillar' || rawType === 'DelvingPillar' ||
                       unitType === 'ProcessingPillar' || unitType === 'Processing_Pillar' || unitType === 'Processing Pillar' ||
                       unitType === 'ClaimingPillar' || unitType === 'Claiming_Pillar' || unitType === 'Claiming Pillar' || unit.isClaimingPillar);
    const isAntiShade = (unit.isAntiShade || unitType === 'AntiShade' || unitType === 'Anti_Shade' || unitType === 'Anti Shade');
    const isPulpUnit = (unitType === 'Pulp');
    const isStickman = (unit.name === 'Stickman' || unitType === 'Stickman' || unit.isStickman);
    const isPinkBeret = (unitType === 'PinkBeret' || unit.name === 'Pink Beret' || unit.cardData?.name === 'Pink Beret');
    const isHacker = (unitType === 'Hacker' || unit.name === 'Hacker' || unit.cardData?.name === 'Hacker' || unit.isHacker);
    const isSalvageCrawler = (unit.name === 'The Salvage Company' || unitType === 'TheSalvageCompany' || rawType === 'TheSalvageCompany');
    const isBastionLock = (unit.name === 'Bastion Lock' || unitType === 'BastionLock' || unitType === 'Bastion_Lock' || unit.isBastionLock);
    const bounceY = unit.isAir 
      ? Math.sin(unit.wobbleOffset + t * 3.5) * 5 
      : (unit.isHeld 
          ? Math.sin(t * 32) * 1.5 
          : (isOrchestra || isA9Grizzly || isSulLoaf || isAntiShade || isPulpUnit || isStickman || isHacker || isBastionLock
              ? 0 // Heavy military vehicles, floating Sul bio-organisms, Anti Shade, Pulp, Stickman, floating Hacker & Bastion Lock glide smoothly without bipedal infantry bobbing
              : (isPinkBeret
                  ? (isWalking && unit.pinkBeretState === 'NORMAL'
                      ? Math.sin(walkPhase * 0.125) * 3.0 * walkPitch
                      : 0)
                  : (isSalvageCrawler
                      ? (isWalking ? Math.abs(walkStride) * 1.0 : Math.sin(t * 3 + unit.x * 0.01) * 0.3) // Reduced amplitude sine-hopping (1.0px vs 2.8px) for heavy crawler
                      : (isWalking ? Math.abs(walkStride) * 2.8 : Math.sin(t * 3 + unit.x * 0.01) * 0.8)))));

    // Attack recoil / melee lunging (A9 Grizzly absorbs recoil in its roof cupola turret rather than shifting the heavy 8-ton hull; Bastion Lock planted shield handles micro-recoil internally; A9 Pigeon handles high-frequency Gatling micro-recoil internally)
    const isAttacking = unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.16);
    const attackRecoil = (isAttacking && !isA9Grizzly && !isA9Pigeon && !isBastionLock) ? (unit.range <= 24 ? dir * 6 : -dir * 3.5) : 0;

    // Heavy hit flinch impulse (only triggered on heavy absolute hits / crits)
    let flinchX = 0;
    if (unit.hitFlinchTimer > 0) {
      const flinchProg = 1 - (unit.hitFlinchTimer / 0.09);
      flinchX = (unit.hitFlinchDir || -forwardDir) * (unit.hitFlinchDist || 3.0) * Math.sin(flinchProg * Math.PI);
    }

    const renderX = unit.x + attackRecoil + flinchX;
    const renderY = unit.isAir ? (unit.y - 18 + bounceY) : (unit.y - bounceY);

    // Enemy cloak, undetected: the unit itself must stay fully invisible - no
    // sprite, no HP bar, no name tag, nothing that gives away its shape or
    // type. Only a faint phase-shimmer distortion hints something is there.
    // The moment it fires, unit.js clears isCloaked for ~1.5s (decloakTimer)
    // and it renders normally again below.
    const localPlayerTeam = (battlefield && battlefield.playerTeam) || 'western';
    const isEnemyInvisible = unit.isCloaked && unit.team !== localPlayerTeam
      && !(battlefield && battlefield.isPositionDetected(localPlayerTeam, unit.x, unit.y));

    let isDuelPerceived = false;
    if (isAntiShade && isEnemyInvisible) {
      const allies = this._allyAntiShades;
      if (allies && allies.length > 0) {
        for (let i = 0; i < allies.length; i++) {
          const ally = allies[i];
          const dx = ally.x - unit.x;
          const dy = ally.y - unit.y;
          if (dx * dx + dy * dy <= 240 * 240) {
            isDuelPerceived = true;
            break;
          }
        }
      } else if (battlefield && Array.isArray(battlefield.units)) {
        for (let i = 0; i < battlefield.units.length; i++) {
          const ally = battlefield.units[i];
          if (ally.team === localPlayerTeam && !ally.isDead && (ally.isAntiShade || ally.cardData?.unitType === 'AntiShade' || ally.name === 'Anti Shade')) {
            const dx = ally.x - unit.x;
            const dy = ally.y - unit.y;
            if (dx * dx + dy * dy <= 240 * 240) {
              isDuelPerceived = true;
              break;
            }
          }
        }
      }
    }

    if (isEnemyInvisible) {
      if (isAntiShade) {
        if (!isDuelPerceived) {
          ctx.restore();
          return;
        }
        // Allied Anti Shade perceives the cloaked enemy Anti Shade:
        // Continues rendering as a faint spectral silhouette across the shadow veil.
        ctx.globalAlpha = 0.38;
      } else {
        const shimmerX = Math.sin(t * 5 + unit.wobbleOffset) * 3;
        const shimmerY = Math.cos(t * 4 + unit.wobbleOffset) * 2;
        ctx.globalAlpha = 0.10 + 0.06 * Math.sin(t * 6 + unit.wobbleOffset);
        ctx.globalCompositeOperation = 'lighter';
        ctx.strokeStyle = 'rgba(150, 220, 255, 0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(renderX + shimmerX, renderY + shimmerY, r * 0.9, r * 1.1, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(renderX - shimmerX * 0.6, renderY - shimmerY * 0.6, r * 0.6, r * 0.8, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
        return;
      }
    }

    // Selection Halo
    if (battlefield && battlefield.selectedUnit === unit && !isEnemyInvisible) {
      ctx.strokeStyle = '#ffff00';
      ctx.lineWidth = 2;
      ctx.beginPath();
      const selRadius = r * 1.35 + Math.sin(t * 8) * 2;
      ctx.ellipse(unit.x, unit.y + (unit.isAir ? 24 : 6), selRadius, selRadius * 0.5, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255, 255, 0, 0.15)';
      ctx.fill();
    }

    // 🌑 Ambient Cast Shadow - stable, grounded ellipse skewed towards bottom-left
    // When held in execution, shadow stays anchored to the ground (unit.groundY)
    const shadowAnchorY = (unit.isHeld && unit.groundY !== undefined) ? unit.groundY : unit.y;
    const shadowBaseY = shadowAnchorY + (unit.isAir ? 24 : 6);
    const isHeldElevation = (unit.isHeld && unit.groundY !== undefined) ? Math.max(0, unit.groundY - unit.y) : 0;
    const shadowScale = unit.isAir 
      ? 1.0 - (bounceY * 0.03) 
      : (unit.isHeld ? Math.max(0.55, 1.0 - (isHeldElevation * 0.012)) : (isSulLoaf || isAntiShade || isPulpUnit ? 1.0 : (1.0 + Math.abs(walkStride) * 0.08)));
    const shadowStretch = unit.isAir ? 2.1 : 1.75;
    const shadowShiftX = -10 - (unit.isAir ? 12 : 5);
    const shadowShiftY = 5 + (unit.isAir ? 9 : 3);
    const shadowRot = -0.38;

    if (!isAntiShade) {
      ctx.fillStyle = unit.isHeld ? 'rgba(0, 0, 0, 0.28)' : 'rgba(0, 0, 0, 0.42)';
      ctx.beginPath();
      ctx.ellipse(unit.x + shadowShiftX, shadowBaseY + shadowShiftY, r * shadowStretch * shadowScale, r * 0.46 * shadowScale, shadowRot, 0, Math.PI * 2);
      ctx.fill();
    }

    // 🩸 Parasitized Unit Crimson Bio-Aura Floor Glow
    if (unit.isParasitized && (!unit._overtakeAnimTime || unit._overtakeAnimTime <= 0)) {
      const bioPulse = 0.8 + 0.2 * Math.sin(t * 4.0 + unit.wobbleOffset);
      ctx.save();
      ctx.fillStyle = '#dc2626';
      ctx.globalAlpha = 0.28 * bioPulse;
      ctx.beginPath();
      ctx.ellipse(unit.x, shadowBaseY, r * 1.6, r * 0.7, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // 🌋 Claiming Pillar Carmine Volcanic Floor Glow
    if (this.quality !== 'low' && (unit.isClaimingPillar || unitType === 'ClaimingPillar' || unitType === 'Claiming_Pillar' || unitType === 'Claiming Pillar')) {
      const isGrowing = !!unit.isGrowing;
      const growthProg = isGrowing ? Math.min(1.0, Math.max(0.02, (unit.growthTimer || 0) / 3.0)) : 1.0;
      const pulse = 0.85 + 0.15 * Math.sin(t * (isGrowing ? 5.5 : 3.0) + (unit.wobbleOffset || 0));
      const glowMultiplier = isGrowing ? (1.15 + 0.25 * Math.sin(t * 8.0)) : 1.0;
      ctx.save();
      const glowAlpha = Math.min(0.7, 0.45 * pulse * glowMultiplier);
      const glowCanvas = getSulClaimingPillarFloorGlowCanvas();
      if (glowCanvas) {
        ctx.globalAlpha = glowAlpha;
        const gw = r * 3.0;
        const gh = r * 1.3;
        ctx.drawImage(glowCanvas, unit.x - gw * 0.5, shadowBaseY - gh * 0.5, gw, gh);
      } else if (this.quality !== 'low') {
        const pillarGlowGrad = ctx.createRadialGradient(unit.x, shadowBaseY, r * 0.20, unit.x, shadowBaseY, r * 1.5);
        pillarGlowGrad.addColorStop(0, `rgba(239, 68, 68, ${glowAlpha})`);
        pillarGlowGrad.addColorStop(0.4, `rgba(220, 38, 38, ${Math.min(0.5, 0.28 * pulse * glowMultiplier)})`);
        pillarGlowGrad.addColorStop(0.75, `rgba(185, 28, 28, ${Math.min(0.3, 0.12 * pulse)})`);
        pillarGlowGrad.addColorStop(1, 'rgba(127, 29, 29, 0)');
        ctx.fillStyle = pillarGlowGrad;
        ctx.beginPath();
        ctx.ellipse(unit.x, shadowBaseY, r * 1.5, r * 0.65, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // Rupture fissures on ground while erupting/growing
      if (isGrowing && (this.quality === 'high' || this.quality === 'ultra')) {
        ctx.strokeStyle = `rgba(254, 202, 202, ${0.55 * (1.0 - growthProg * 0.4)})`;
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(unit.x - r * 0.45, shadowBaseY + 1);
        ctx.lineTo(unit.x - r * 0.1, shadowBaseY - 1);
        ctx.lineTo(unit.x + r * 0.15, shadowBaseY + 2);
        ctx.lineTo(unit.x + r * 0.5, shadowBaseY - 1);
        ctx.stroke();
      }
      ctx.restore();
    }

    // 💡 Dynamic Ground Point Light (Simulated floor illumination pool cast under unit)
    const groundPointY = shadowBaseY;
    const lightRadius = r * (unit.isAir ? 2.4 : 1.6);
    let floorLight0 = null;
    let floorLight1 = null;
    // 🥚 INDESTRUCTIBLE SUL BIO-COCOON RENDERING (EXPERIMENTAL METAMORPHOSIS)
    if (unit.isCocoon) {
      const remTime = (unit.cocoonTimer !== undefined) ? unit.cocoonTimer : 30.0;
      const progress = Math.max(0, Math.min(1.0, remTime / 30.0));
      const hatchUrgency = 1.0 - progress;

      // 1. Lifecycle Dynamics & Base-Anchored Kinematics (Fixpunkt am Boden gewahrt)
      // The base of the cocoon is permanently anchored into the earth at (unit.x, groundY).
      // Thrashing, writhing and bulging emanate from the organism inside, bending
      // and swaying the chrysalis around its fixed root anchor.
      const groundY = unit.y + 14;
      let swayAngle = 0;
      let bulgeX = 1.0;
      let bulgeY = 1.0;
      let pulseRate = 2.2;

      if (remTime < 3.0) {
        // Critical Rupture (0-3s): Violent erratic thrashing & bulging from mature Sul entity
        pulseRate = 26.0;
        swayAngle = (Math.sin(t * 36.0 + unit.x) * 0.09) + (Math.cos(t * 52.0) * 0.05);
        bulgeX = 1.0 + Math.sin(t * 22.0) * 0.09;
        bulgeY = 1.0 - Math.sin(t * 22.0) * 0.06;
      } else if (remTime < 10.0) {
        // Metamorphic Frenzy (3-10s): Nervous spasms & shifting
        pulseRate = 12.0;
        swayAngle = Math.sin(t * 16.0 + unit.x) * 0.045;
        bulgeX = 1.0 + Math.sin(t * 14.0) * 0.045;
        bulgeY = 1.0 - Math.sin(t * 14.0) * 0.03;
      } else if (remTime < 20.0) {
        // Gestating (10-20s): Deep rhythmic breathing pulse
        pulseRate = 5.5;
        swayAngle = Math.sin(t * 6.0 + unit.x) * 0.015;
        bulgeX = 1.0 + Math.sin(t * 5.5) * 0.025;
        bulgeY = 1.0 - Math.sin(t * 5.5) * 0.015;
      } else {
        // Dormant (20-30s): Slow bio-pulse
        swayAngle = Math.sin(t * 2.5 + unit.x) * 0.008;
        bulgeX = 1.0 + Math.sin(t * 2.2) * 0.015;
        bulgeY = 1.0 - Math.sin(t * 2.2) * 0.01;
      }

      const cocoonH = 38;
      const cocoonW = 27;

      // 2. Stationary Root System & Ground Slime (Unverrückbare Verankerung am Boden)
      // Rendered in world space at (unit.x, groundY) so the roots remain 100% stationary.
      ctx.save();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.beginPath();
      ctx.ellipse(unit.x, groundY + 1, cocoonW * 0.75, 6.5, 0, 0, Math.PI * 2);
      ctx.fill();

      // Ambient Bioplasma Floor Aura (Sul Violet / Spore Green)
      if (this.quality !== 'low') {
        ctx.fillStyle = remTime < 5.0 ? 'rgba(244, 63, 94, 0.18)' : 'rgba(168, 85, 247, 0.15)';
        ctx.beginPath();
        ctx.ellipse(unit.x, groundY + 1, cocoonW * 0.95, 9, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // Root Pedestal: Gnarled bio-roots branching into the soil
      if (this.quality !== 'low') {
        const rootsCanvas = getSulCocoonRootsCanvas();
        if (rootsCanvas) {
          ctx.drawImage(rootsCanvas, unit.x - 19, groundY - 4);
        }
      }
      ctx.restore();

      // 3. Anchored Chrysalis Body (Transformiert um den fixierten Bodenpunkt)
      ctx.save();
      ctx.translate(unit.x, groundY);
      ctx.rotate(swayAngle);
      ctx.scale(bulgeX, bulgeY);

      const hw = cocoonW * 0.5;

      // Organic tapered chrysalis silhouette (Teardrop / Bio-Pod)
      ctx.beginPath();
      ctx.moveTo(0, -cocoonH);
      ctx.bezierCurveTo(-hw * 0.85, -cocoonH * 0.92, -hw * 1.05, -cocoonH * 0.58, -hw, -cocoonH * 0.38);
      ctx.bezierCurveTo(-hw * 0.95, -cocoonH * 0.18, -hw * 0.55, -2, -hw * 0.35, 0);
      ctx.lineTo(hw * 0.35, 0);
      ctx.bezierCurveTo(hw * 0.55, -2, hw * 0.95, -cocoonH * 0.18, hw, -cocoonH * 0.38);
      ctx.bezierCurveTo(hw * 1.05, -cocoonH * 0.58, hw * 0.85, -cocoonH * 0.92, 0, -cocoonH);
      ctx.closePath();

      // 3A. Organic Mottled Bio-Bark Husk
      if (this.quality !== 'low') {
        const gradHusk = getSulCocoonHuskGrad(ctx);
        ctx.fillStyle = gradHusk || '#2e1810';
      } else {
        ctx.fillStyle = '#2e1810';
      }
      ctx.fill();

      ctx.strokeStyle = '#4d2b1d';
      ctx.lineWidth = 1.4;
      ctx.stroke();

      // 3B. Internal Embryonic Luminescence Glowing Through Husk
      if (this.quality !== 'low') {
        const coreGlowAlpha = 0.18 + Math.sin(t * pulseRate) * 0.08 + (hatchUrgency * 0.28);
        const glowLUT = getSulCocoonGlowLUT(remTime < 5.0);
        if (glowLUT) {
          ctx.save();
          ctx.globalAlpha = Math.min(1.0, coreGlowAlpha * 1.5);
          const glowW = hw * 1.8;
          const glowH = hw * 1.8;
          ctx.drawImage(glowLUT, -glowW / 2, -cocoonH * 0.48 - glowH / 2, glowW, glowH);
          ctx.restore();
        }
      }

      // 3C. Woven Fibrous Webbing & Sinew Filaments (Pre-baked canvas cache)
      const webbingCanvas = getSulCocoonWebbingCanvas();
      if (webbingCanvas) {
        ctx.drawImage(webbingCanvas, -18, -42);
      }

      // 4. Plastische Fissuren & Aufbrechende Schalen-Risse (3D Depth & Rupture)
      if (remTime < 24.0) {
        ctx.save();
        let riftGap = 0.8;
        if (remTime < 3.0) {
          riftGap = 4.0 + (3.0 - remTime) * 0.8 + Math.sin(t * 30.0) * 0.5;
        } else if (remTime < 10.0) {
          riftGap = 1.8 + (10.0 - remTime) * 0.25;
        } else {
          riftGap = 0.8 + (24.0 - remTime) * 0.06;
        }

        const riftCoreColor = remTime < 5.0
          ? (Math.sin(t * 20.0) > 0 ? '#f43f5e' : '#ffffff')
          : (remTime < 12.0 ? '#bef264' : '#a855f7');

        // 4A. Deep Shadow Chasm (Innenliegende Riss-Tiefe)
        ctx.strokeStyle = '#050201';
        ctx.lineWidth = riftGap + 2.4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'miter';
        ctx.beginPath();
        ctx.moveTo(0, -cocoonH * 0.82);
        ctx.lineTo(2.5, -cocoonH * 0.65);
        ctx.lineTo(-3.0, -cocoonH * 0.48);
        ctx.lineTo(3.5, -cocoonH * 0.35);
        ctx.lineTo(-1.5, -cocoonH * 0.22);
        ctx.lineTo(1.0, -cocoonH * 0.08);
        ctx.stroke();

        if (remTime < 16.0) {
          ctx.beginPath();
          ctx.moveTo(2.5, -cocoonH * 0.65);
          ctx.lineTo(hw * 0.62, -cocoonH * 0.70);
          ctx.moveTo(-3.0, -cocoonH * 0.48);
          ctx.lineTo(-hw * 0.68, -cocoonH * 0.44);
          ctx.stroke();
        }
        if (remTime < 8.0) {
          ctx.beginPath();
          ctx.moveTo(3.5, -cocoonH * 0.35);
          ctx.lineTo(hw * 0.58, -cocoonH * 0.26);
          ctx.stroke();
        }

        // 4B. Bioluminescent Living Core in the Gap
        ctx.strokeStyle = riftCoreColor;
        ctx.lineWidth = Math.max(1.0, riftGap);
        ctx.beginPath();
        ctx.moveTo(0, -cocoonH * 0.82);
        ctx.lineTo(2.5, -cocoonH * 0.65);
        ctx.lineTo(-3.0, -cocoonH * 0.48);
        ctx.lineTo(3.5, -cocoonH * 0.35);
        ctx.lineTo(-1.5, -cocoonH * 0.22);
        ctx.lineTo(1.0, -cocoonH * 0.08);
        ctx.stroke();

        if (remTime < 16.0) {
          ctx.lineWidth = Math.max(0.8, riftGap * 0.6);
          ctx.beginPath();
          ctx.moveTo(2.5, -cocoonH * 0.65);
          ctx.lineTo(hw * 0.62, -cocoonH * 0.70);
          ctx.moveTo(-3.0, -cocoonH * 0.48);
          ctx.lineTo(-hw * 0.68, -cocoonH * 0.44);
          ctx.stroke();
        }
        if (remTime < 8.0) {
          ctx.beginPath();
          ctx.moveTo(3.5, -cocoonH * 0.35);
          ctx.lineTo(hw * 0.58, -cocoonH * 0.26);
          ctx.stroke();
        }

        // 4C. Plastische Bruch-Kante (Raised Chitin Bevel Lip)
        ctx.strokeStyle = remTime < 5.0 ? 'rgba(255, 255, 255, 0.95)' : 'rgba(224, 212, 195, 0.85)';
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(-1.2, -cocoonH * 0.82);
        ctx.lineTo(1.3, -cocoonH * 0.65);
        ctx.lineTo(-4.2, -cocoonH * 0.48);
        ctx.lineTo(2.3, -cocoonH * 0.35);
        ctx.lineTo(-2.7, -cocoonH * 0.22);
        ctx.lineTo(-0.2, -cocoonH * 0.08);
        ctx.stroke();

        // 4D. Stretching Mucus & Sinew Bridges Spanning Across the Rift
        if (remTime < 10.0) {
          ctx.strokeStyle = remTime < 4.0 ? 'rgba(254, 240, 138, 0.85)' : 'rgba(190, 242, 100, 0.75)';
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(0.5, -cocoonH * 0.58);
          ctx.lineTo(-1.8, -cocoonH * 0.56);
          ctx.moveTo(-1.0, -cocoonH * 0.42);
          ctx.lineTo(1.8, -cocoonH * 0.40);
          ctx.moveTo(2.0, -cocoonH * 0.29);
          ctx.lineTo(-0.8, -cocoonH * 0.27);
          ctx.stroke();
        }

        // 4E. Volumetric Internal Light Shafts (Lichtkegel aus den Rissen)
        if (remTime < 3.0 && this.quality !== 'low') {
          const rayIntensity = (3.0 - remTime) / 3.0;
          const beamPulse = 0.55 + Math.sin(t * 32.0) * 0.4;
          const beamAlpha = rayIntensity * beamPulse * 0.35;

          ctx.fillStyle = '#ffffff';
          ctx.globalAlpha = beamAlpha * 1.2;
          ctx.beginPath();
          ctx.moveTo(2.5, -cocoonH * 0.65);
          ctx.lineTo(hw * 1.8, -cocoonH * 0.88);
          ctx.lineTo(hw * 1.5, -cocoonH * 0.55);
          ctx.closePath();
          ctx.fill();

          ctx.fillStyle = '#fef08a';
          ctx.globalAlpha = beamAlpha;
          ctx.beginPath();
          ctx.moveTo(-3.0, -cocoonH * 0.48);
          ctx.lineTo(-hw * 1.9, -cocoonH * 0.62);
          ctx.lineTo(-hw * 1.7, -cocoonH * 0.32);
          ctx.closePath();
          ctx.fill();

          ctx.fillStyle = '#f43f5e';
          ctx.globalAlpha = beamAlpha * 1.1;
          ctx.beginPath();
          ctx.moveTo(3.5, -cocoonH * 0.35);
          ctx.lineTo(hw * 1.9, -cocoonH * 0.42);
          ctx.lineTo(hw * 1.6, -cocoonH * 0.15);
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      }

      ctx.restore(); // Restores chrysalis body base-anchored transform

      // 5. Circular Bio-Countdown Ring (Kreisförmiger Countdown über dem Kokon)
      const ringY = groundY - cocoonH - 10;
      const ringX = unit.x;
      const ringR = 8.5;

      ctx.save();
      ctx.beginPath();
      ctx.arc(ringX, ringY, ringR, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.72)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      const startAngle = -Math.PI / 2;
      const endAngle = startAngle + (Math.PI * 2 * progress);
      ctx.beginPath();
      ctx.arc(ringX, ringY, ringR, startAngle, endAngle, false);
      if (remTime < 5.0) {
        ctx.strokeStyle = Math.sin(t * 20.0) > 0 ? '#f43f5e' : '#ffffff';
      } else {
        ctx.strokeStyle = '#84cc16';
      }
      ctx.lineWidth = 2.0;
      ctx.stroke();

      ctx.fillStyle = remTime < 5.0 ? '#f43f5e' : '#84cc16';
      ctx.beginPath();
      ctx.arc(ringX, ringY, 1.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.restore(); // Restores ctx.save() at top of renderUnit
      return; // Skip normal living unit HP bars, auras, and weapons
    }

    // 🪨 STICKMAN INDESTRUCTIBLE KNEELING OBSTACLE & CINEMATIC COLLAPSE RENDERING
    if (unit.isGoldenStatue && isStickman && !unit.hasGoldPedestal) {
      const stickmanDrawSize = drawSize;
      const footGroundY = renderY + 24;
      const stickmanYOffset = 24 - stickmanDrawSize / 2;

      // 1. Heavy Scorched Ground Shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
      ctx.beginPath();
      ctx.ellipse(renderX, footGroundY, stickmanDrawSize * 0.44, stickmanDrawSize * 0.14, 0, 0, Math.PI * 2);
      ctx.fill();

      // 2. Theatrical Prolonged Collapse Animation or Settled Indestructible Wreck
      const animTime = unit._kneelingAnimTime !== undefined ? unit._kneelingAnimTime : 0;
      const animDuration = unit._kneelingAnimDuration || 3.8;
      const elapsed = Math.max(0, Math.min(animDuration, animDuration - animTime)); // 0.0s -> 3.8s
      const progress = elapsed / animDuration; // 0.0 -> 1.0

      const deathPose = unit.deathPose || ((getUnitNumericId(unit) % 3) + 1);
      const obsKey = (deathPose === 2) ? 'Stickman_Obstacle_2' : ((deathPose === 3) ? 'Stickman_Obstacle_3' : 'Stickman_Obstacle');
      const obsBmp = buildSpriteBitmap(obsKey, renderRace, unit.team);
      const obsSrc = obsBmp?.normal; // CRITICAL: NEVER use obsBmp?.flash! Zero white glow!

      // Atmospheric, creeping color fade from living articulated droid to cold dead obstacle wreck:
      // Starts as knees touch ground and settles, fading gradually over 1.25s (2.35s -> 3.60s)
      const swapStart = 2.35;
      const swapEnd = 3.60;
      const isCrossfade = (elapsed >= swapStart && elapsed <= swapEnd);
      const rawFade = isCrossfade ? ((elapsed - swapStart) / (swapEnd - swapStart)) : (elapsed < swapStart ? 0 : 1);
      const crossfadeT = rawFade * rawFade * (3 - 2 * rawFade);
      const drawArticulated = (elapsed < swapEnd);
      const drawObstacle = (elapsed >= swapStart);

      if (drawArticulated) {
        // ── BEATS 1 to 3: Articulated Walker Fatal Shock & Kinetic Crash (0.0s - 2.88s) ──
        let jitterX = 0;
        let jitterY = 0;
        let dropY = 0;
        let slumpPitch = 0;
        let cannonTilt = 0;
        let eyeAlpha = 1.0;
        let eyeColor = '#00e5ff';
        let useKneelingLegs = false;
        let baseSinkY = 0;

        if (elapsed < 0.65) {
          // ── BEAT 1: FATAL CORE OVERLOAD & HYDRAULIC BLOWOUT (0.0s - 0.65s) ──
          // Violent recoil stagger backwards, rapid electrical tremors, frantic alarm strobe
          const p1 = elapsed / 0.65;
          jitterX = (Math.sin(t * 70) * 3.5) * (1 - p1);
          jitterY = (Math.cos(t * 75) * 1.5) * (1 - p1);
          slumpPitch = -0.18 * Math.sin(p1 * Math.PI) * forwardDir;
          eyeAlpha = (Math.sin(t * 40) > 0.0) ? 1.0 : 0.2;
          eyeColor = (Math.sin(t * 20) > 0) ? '#f59e0b' : '#00e5ff';

          if (this.quality !== 'low') {
            ctx.save();
            // Ruptured black hydraulic fluid jets spraying from shoulder & neck joints
            ctx.fillStyle = '#0f172a';
            for (let sp = 0; sp < 3; sp++) {
              const ox = renderX - forwardDir * (stickmanDrawSize * 0.10) + Math.sin(t * 22 + sp) * 12;
              const oy = renderY + stickmanYOffset - (stickmanDrawSize * 0.20) + (sp * 4);
              ctx.fillRect(ox, oy, 2.5, 3.5);
            }
            ctx.restore();
          }
        } else if (elapsed < 1.35) {
          // ── BEAT 2: SERVO POWER LOSS & FORWARD SAG (0.65s - 1.35s) ──
          // Power cuts out: B1 neck droops limp, heavy railgun drops out of arm clamp and swings down
          const p2 = (elapsed - 0.65) / 0.70;
          jitterX = 0;
          jitterY = 0;
          const targetSlump = (deathPose === 2 ? 0.12 : (deathPose === 3 ? 0.32 : 0.22)) * forwardDir;
          const targetCannon = (deathPose === 2 ? 0.38 : (deathPose === 3 ? 0.72 : 0.58)) * forwardDir;
          const sagDrop = stickmanDrawSize * 0.02;

          slumpPitch = p2 * targetSlump;
          cannonTilt = p2 * (targetCannon * 0.65);
          dropY = p2 * sagDrop;
          eyeAlpha = 0.60 * (1 - p2 * 0.3);
          eyeColor = '#00e5ff';
        } else {
          // ── BEAT 3: KNEE BUCKLE & VISCERAL KINETIC GROUND IMPACT (1.35s - 2.88s) ──
          // Knees buckle under colossal shifted weight: accelerated gravitational plunge & violent bedrock touchdown
          const u = Math.min(1.0, (elapsed - 1.35) / 1.45); // 0.0 -> 1.0

          // High-pressure steam jets venting horizontally from collapsing knee valves
          if (this.quality !== 'low') {
            ctx.save();
            ctx.fillStyle = '#e2e8f0';
            ctx.globalAlpha = 0.45 * (1 - u);
            for (let vn = -1; vn <= 1; vn += 2) {
              const ventX = renderX + vn * (stickmanDrawSize * 0.14);
              const ventY = renderY + stickmanYOffset + (dropY || 0) + (stickmanDrawSize * 0.12);
              ctx.beginPath();
              ctx.arc(ventX, ventY, 4 + u * 10, 0, Math.PI * 2);
              ctx.fill();
            }
            ctx.restore();
          }

          const targetSlump = (deathPose === 2 ? 0.12 : (deathPose === 3 ? 0.32 : 0.22)) * forwardDir;
          const targetCannon = (deathPose === 2 ? 0.38 : (deathPose === 3 ? 0.72 : 0.58)) * forwardDir;
          const sagDrop = stickmanDrawSize * 0.02;

          if (elapsed < 2.05) {
            // PHASE 3A: Accelerated gravitational plunge (1.35s - 2.05s)
            // Seamless C0 continuity with Beat 2 sag: zero upward hop or head hiccup!
            const fallProg = (elapsed - 1.35) / 0.70; // 0.0 -> 1.0
            const plunge = Math.pow(fallProg, 2.4);
            dropY = sagDrop + plunge * (stickmanDrawSize * 0.16 - sagDrop);
            slumpPitch = targetSlump * (1.0 + 0.15 * plunge);
            eyeAlpha = Math.max(0, 0.42 * (1.0 - fallProg * 0.7));

            if (fallProg >= 0.48) {
              useKneelingLegs = true;
            }

            // Weapon lance tip hits ground first at fallProg >= 0.55 (~1.74s)
            if (fallProg < 0.55) {
              cannonTilt = targetCannon * (0.65 + (fallProg / 0.55) * 0.35);
            } else {
              cannonTilt = targetCannon;
            }

            // Ground dust & grinding friction sparks at muzzle impact point
            if (this.quality !== 'low' && fallProg >= 0.55 && fallProg <= 0.98) {
              const mHitT = (fallProg - 0.55) / 0.43;
              const mDecay = Math.exp(-mHitT * 4.5);
              const mX = renderX + forwardDir * (stickmanDrawSize * 0.36);
              ctx.save();
              ctx.fillStyle = '#94a3b8';
              ctx.globalAlpha = mDecay * 0.65;
              ctx.beginPath();
              ctx.arc(mX + forwardDir * (mHitT * 10), footGroundY - mHitT * 4, 3 + mHitT * 12, 0, Math.PI * 2);
              ctx.fill();
              // Grinding friction sparks scraping along dirt as gun tip plants
              const lanceSparkCols = STICKMAN_LANCE_SPARK_COLORS;
              for (let sp = 0; sp < 4; sp++) {
                const spAngle = -Math.PI * (0.12 + Math.random() * 0.55);
                const spDist = (3 + Math.random() * 9) * mDecay;
                const sx = mX + forwardDir * (Math.cos(spAngle) * spDist);
                const sy = footGroundY + Math.sin(spAngle) * spDist;
                ctx.fillStyle = lanceSparkCols[sp % lanceSparkCols.length];
                ctx.fillRect(sx, sy, 1.8, 1.8);
              }
              ctx.restore();
            }
          } else {
            // PHASE 3B: HARD BEDROCK TOUCHDOWN & FULL-BODY KINETIC SHUDDER (elapsed >= 2.05s)
            // Knees smash into ground: chassis compression overshoot, head/torso momentum whip & metal shudder
            useKneelingLegs = true;
            const kHitT = Math.min(1.0, (elapsed - 2.05) / 0.35);
            // Smoothly sink the entire base to match the obstacle resting baseline (stickmanDrawSize * 0.06)
            baseSinkY = Math.min(1.0, kHitT) * (stickmanDrawSize * 0.06);

            const impElapsed = elapsed - 2.05;
            const impT = Math.min(1.0, impElapsed / 0.50);
            const impDecay = Math.exp(-impT * 6.5);

            // 1. Chassis compression overshoot & elastic rebound (+7.5px slam into shock mounts)
            const compressionBounce = Math.sin(impT * Math.PI * 3.2) * impDecay * 7.5;
            dropY = (stickmanDrawSize * 0.16) + compressionBounce;

            // 2. Upper torso & cranial forward momentum whip (+16° forward snap upon knee halt)
            const torsoWhip = Math.sin(impT * Math.PI * 2.8) * impDecay * (0.28 * forwardDir);
            slumpPitch = targetSlump + torsoWhip;

            // 3. High-frequency structural shudder rippling through the metallic chassis
            jitterY = (Math.sin(impElapsed * 85) * 5.5 + Math.sin(impElapsed * 150) * 2.5) * impDecay;
            jitterX = (Math.cos(impElapsed * 70) * 4.2 * forwardDir) * impDecay;

            // 4. Heavy underslung cannon clatter & mount vibration
            const cannonJolt = Math.sin(impT * Math.PI * 3.8 + 0.3) * impDecay * (0.22 * forwardDir);
            cannonTilt = targetCannon + cannonJolt;

            eyeAlpha = Math.max(0, 0.15 * impDecay);

            // 5. Ground kinetic shockwave ring & lateral knee dust puff ejection
            if (this.quality !== 'low' && impT < 0.65) {
              ctx.save();
              // Turf elliptical shockwave ring expanding outwards
              const shockR = stickmanDrawSize * (0.12 + impT * 0.48);
              ctx.strokeStyle = '#e2e8f0';
              ctx.globalAlpha = (0.65 - impT) * 0.85;
              ctx.lineWidth = Math.max(1, (1.0 - impT) * 2.2);
              ctx.beginPath();
              ctx.ellipse(renderX, footGroundY, shockR, shockR * 0.38, 0, 0, Math.PI * 2);
              ctx.stroke();

              // Lateral knee dust puff ejection shooting outward from impact points
              ctx.fillStyle = '#94a3b8';
              ctx.globalAlpha = (0.65 - impT) * 0.80;
              // Rear knee dust blast
              const rearKneeX = renderX - forwardDir * (stickmanDrawSize * 0.22) - forwardDir * (impT * 12);
              ctx.beginPath();
              ctx.arc(rearKneeX, footGroundY - impT * 5, 4 + impT * 13, 0, Math.PI * 2);
              ctx.fill();
              // Front knee dust blast
              const frontKneeX = renderX + forwardDir * (stickmanDrawSize * 0.14) + forwardDir * (impT * 14);
              ctx.beginPath();
              ctx.arc(frontKneeX, footGroundY - impT * 5, 4 + impT * 14, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }

            // 6. VISCERAL WEAPON KINETIC SPARK ERUPTION (Ground impact where weapon slams into bedrock)
            if (this.quality !== 'low' && impT < 0.65) {
              const mX = renderX + forwardDir * (stickmanDrawSize * 0.36);
              const lanceSparkCols = STICKMAN_LANCE_SPARK_COLORS;
              const sparkDecay = Math.max(0, (0.65 - impT) / 0.65);
              ctx.save();

              // Intense incandescent flash at the gun tip ground contact point
              if (impT < 0.28) {
                const flashA = (0.28 - impT) / 0.28;
                ctx.fillStyle = '#ffffff';
                ctx.globalAlpha = flashA * 0.95;
                ctx.beginPath();
                ctx.arc(mX, footGroundY - 2, 4 + flashA * 7, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = '#fef08a';
                ctx.beginPath();
                ctx.arc(mX, footGroundY - 2, 2 + flashA * 4, 0, Math.PI * 2);
                ctx.fill();
              }

              // Explosive shower of grinding kinetic sparks & ricochets spraying upward & outward
              for (let sp = 0; sp < 8; sp++) {
                const spAngle = -Math.PI * (0.10 + (sp / 8) * 0.70);
                const spLen = (7 + Math.sin(impElapsed * 75 + sp * 2.1) * 16) * sparkDecay;
                const spX = mX + forwardDir * (Math.cos(spAngle) * spLen);
                const spY = footGroundY + Math.sin(spAngle) * spLen;
                ctx.strokeStyle = lanceSparkCols[sp % lanceSparkCols.length];
                ctx.lineWidth = 1.4 * sparkDecay;
                ctx.globalAlpha = sparkDecay;
                ctx.beginPath();
                ctx.moveTo(mX, footGroundY - 1);
                ctx.lineTo(spX, spY);
                ctx.stroke();

                // Bright incandescent ember fleck at ray tip
                ctx.fillStyle = (sp % 2 === 0) ? '#ffffff' : '#f59e0b';
                ctx.fillRect(spX - 1, spY - 1, 2, 2);
              }
              ctx.restore();
            }
          }
        }

        // Render multi-layer articulated droid
        const legsKey = useKneelingLegs ? 'Stickman_Legs_Kneeling' : 'Stickman_Legs';
        const legsBmp = buildSpriteBitmap(legsKey, renderRace, unit.team);
        const chassisBmp = buildSpriteBitmap('Stickman_Chassis', renderRace, unit.team);
        const torsoBmp = buildSpriteBitmap('Stickman_Torso', renderRace, unit.team);
        const cannonBmp = buildSpriteBitmap('Stickman_Cannon', renderRace, unit.team);

        ctx.save();
        if (isCrossfade) {
          // Keep base articulated layer solid so background never shines through during the creeping fade
          ctx.globalAlpha = ctx.globalAlpha * (crossfadeT > 0.88 ? (1.0 - (crossfadeT - 0.88) / 0.12) : 1.0);
        }
        ctx.translate(renderX + jitterX, renderY + stickmanYOffset + baseSinkY + jitterY);

        // Legs
        if (legsBmp?.normal) {
          ctx.drawImage(legsBmp.normal, -stickmanDrawSize / 2, -stickmanDrawSize / 2, stickmanDrawSize, stickmanDrawSize);
        }
        // Chassis (dropping downward)
        if (chassisBmp?.normal) {
          ctx.save();
          ctx.translate(0, dropY);
          ctx.rotate(slumpPitch * 0.4);
          ctx.drawImage(chassisBmp.normal, -stickmanDrawSize / 2, -stickmanDrawSize / 2, stickmanDrawSize, stickmanDrawSize);
          ctx.restore();
        }
        // Torso (slumping forward)
        if (torsoBmp?.normal) {
          ctx.save();
          ctx.translate(0, dropY);
          ctx.rotate(slumpPitch);
          ctx.drawImage(torsoBmp.normal, -stickmanDrawSize / 2, -stickmanDrawSize / 2, stickmanDrawSize, stickmanDrawSize);

          // Sensor optic dying flare (quality !== 'low')
          if (this.quality !== 'low' && eyeAlpha > 0.05) {
            const eyeX = forwardDir * stickmanDrawSize * (3.5 / 48);
            const eyeY = -stickmanDrawSize * (16.0 / 48) + dropY;
            ctx.save();
            ctx.globalAlpha = 0.70 * eyeAlpha * (isCrossfade ? (1.0 - crossfadeT) : 1.0);
            ctx.fillStyle = eyeColor;
            ctx.beginPath();
            ctx.arc(eyeX, eyeY, 2.5 * eyeAlpha, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }
          ctx.restore();
        }
        // Cannon (pivoting down in the foreground — not covered by shoulder)
        if (cannonBmp?.normal) {
          ctx.save();
          const cannonYDrop = stickmanDrawSize * (2.2 / 48);
          const cannonPivotX = forwardDir * (-stickmanDrawSize * (8 / 48));
          const cannonPivotY = -stickmanDrawSize * (3 / 48) + dropY + cannonYDrop;
          ctx.translate(cannonPivotX, cannonPivotY);
          ctx.rotate(cannonTilt);
          ctx.translate(-cannonPivotX, -cannonPivotY);
          ctx.drawImage(cannonBmp.normal, -stickmanDrawSize / 2, -stickmanDrawSize / 2 + dropY + cannonYDrop, stickmanDrawSize, stickmanDrawSize);
          ctx.restore();
        }

        // ── 1. JOINT SPARKS: Fatal electrical short-circuits crackling at the articulated joints (0.0s - 1.35s) ──
        if (this.quality !== 'low' && elapsed < 1.35) {
          const sparkFade = Math.max(0, 1.0 - (elapsed / 1.35));
          const sparkColors = STICKMAN_SHORT_CIRCUIT_COLORS;
          const j0 = _stickmanJointListScratch[0];
          j0.x = forwardDir * (-stickmanDrawSize * (3 / 48)); j0.y = -stickmanDrawSize * (12 / 48) + dropY; j0.weight = 1.0;
          const j1 = _stickmanJointListScratch[1];
          j1.x = forwardDir * (-stickmanDrawSize * (5 / 48)); j1.y = -stickmanDrawSize * (9 / 48) + dropY; j1.weight = 1.2;
          const j2 = _stickmanJointListScratch[2];
          j2.x = forwardDir * (-stickmanDrawSize * (4 / 48)); j2.y = dropY; j2.weight = 1.1;
          const j3 = _stickmanJointListScratch[3];
          j3.x = forwardDir * (stickmanDrawSize * (9 / 48)); j3.y = (stickmanDrawSize * (10 / 48)) + dropY * 0.5; j3.weight = 1.0;
          const j4 = _stickmanJointListScratch[4];
          j4.x = forwardDir * (-stickmanDrawSize * (8 / 48)); j4.y = (stickmanDrawSize * (10 / 48)) + dropY * 0.5; j4.weight = 0.8;
          const j5 = _stickmanJointListScratch[5];
          j5.x = forwardDir * (stickmanDrawSize * (1 / 48)); j5.y = stickmanDrawSize * (19 / 48); j5.weight = 0.7;
          const jointList = _stickmanJointListScratch;

          for (let jIdx = 0; jIdx < jointList.length; jIdx++) {
            const j = jointList[jIdx];
            const jFreq = Math.sin(t * (48 + jIdx * 13) + jIdx * 3.7);
            if (jFreq > (0.15 + (1 - sparkFade) * 0.45)) {
              ctx.save();
              // Joint core electrical glint
              ctx.fillStyle = (jFreq > 0.6) ? '#ffffff' : '#00e5ff';
              ctx.globalAlpha = Math.min(1.0, sparkFade * 1.3);
              ctx.beginPath();
              ctx.arc(j.x, j.y, 1.2 * j.weight, 0, Math.PI * 2);
              ctx.fill();

              // Micro short-circuit arc crackle
              const arcAngle = (jIdx * 1.618 + t * 32) % (Math.PI * 2);
              const arcLen = (2.5 + Math.abs(Math.sin(t * 75 + jIdx)) * 5.0) * j.weight * sparkFade;
              ctx.strokeStyle = sparkColors[Math.floor((t * 22 + jIdx) % sparkColors.length)];
              ctx.lineWidth = 1.0;
              ctx.beginPath();
              ctx.moveTo(j.x, j.y);
              const midX = j.x + Math.cos(arcAngle) * (arcLen * 0.5) + (Math.sin(t * 40 + jIdx) * 1.5);
              const midY = j.y + Math.sin(arcAngle) * (arcLen * 0.5) + (Math.cos(t * 40 + jIdx) * 1.5);
              const endX = j.x + Math.cos(arcAngle) * arcLen;
              const endY = j.y + Math.sin(arcAngle) * arcLen;
              ctx.lineTo(midX, midY);
              ctx.lineTo(endX, endY);
              ctx.stroke();

              // Incandescent spark fleck shooting outward
              const spkX = j.x + Math.cos(arcAngle) * (arcLen * 1.25);
              const spkY = j.y + Math.sin(arcAngle) * (arcLen * 1.25);
              ctx.fillStyle = (Math.sin(t * 30 + jIdx) > 0) ? '#fbbf24' : '#f97316';
              ctx.fillRect(spkX - 0.5, spkY - 0.5, 1.5, 1.5);
              ctx.restore();
            }
          }
        }

        ctx.restore();
      }

      if (drawObstacle) {
        // ── BEAT 4 & PERMANENT OBSTACLE: SMOLDERING WRECKAGE (2.70s - 3.8s+) ──
        // Fully settled on folded knees, railgun lance buried into dirt!
        let settleShake = 0;
        if (elapsed >= 2.80 && elapsed < 3.20) {
          const sT = (elapsed - 2.80) / 0.40;
          settleShake = Math.sin(sT * Math.PI * 3) * Math.exp(-sT * 5) * 1.0;
        }

        ctx.save();
        if (isCrossfade) {
          ctx.globalAlpha = ctx.globalAlpha * crossfadeT;
        }
        ctx.translate(renderX, renderY + stickmanYOffset + (stickmanDrawSize * 0.06) + settleShake);

        if (obsSrc) {
          ctx.drawImage(obsSrc, -stickmanDrawSize / 2, -stickmanDrawSize / 2, stickmanDrawSize, stickmanDrawSize);
        }
        ctx.restore();
      }

      // ── ⚡ KASKADIERENDE ENERGIEBLITZE: 3 zeitlich versetzte Mikro-Blitze (-60% Größe) an Rumpf, Knie und Schulter (2.68s - 2.92s) ──
      if (this.quality !== 'low' && elapsed >= 2.68 && elapsed <= 2.92) {
        ctx.save();
        const baseY = renderY + stickmanYOffset + (stickmanDrawSize * 0.06);

        for (let fi = 0; fi < 3; fi++) {
          const fStart = 2.68 + fi * 0.05; // 2.68s (Torso), 2.73s (Knie), 2.78s (Schulter)
          const fEnd = fStart + 0.14;       // 2.82s, 2.87s, 2.92s
          if (elapsed < fStart || elapsed > fEnd) continue;

          const fNorm = (elapsed - fStart) / 0.14;
          const fT = Math.sin(fNorm * Math.PI);
          if (fT <= 0.02) continue;

          let posX = 0;
          let posY = 0;
          let haloCol = '#00e5ff';
          let rScale = 1.0;

          if (fi === 0) {
            // Flash 1: Rumpf / Reaktor-Kern & Visier-Leitung (Scheitelpunkt bei ~2.75s)
            posX = renderX + forwardDir * stickmanDrawSize * (1.5 / 48);
            posY = baseY - stickmanDrawSize * (6.0 / 48);
            haloCol = '#00e5ff';
            rScale = 1.0;
          } else if (fi === 1) {
            // Flash 2: Kniegelenk-Aktuator / Vorderes Fahrwerk (Scheitelpunkt bei ~2.80s)
            posX = renderX + forwardDir * stickmanDrawSize * (7.0 / 48);
            posY = baseY + stickmanDrawSize * (8.5 / 48);
            haloCol = '#38bdf8';
            rScale = 0.85;
          } else {
            // Flash 3: Schulter-Gimbal / Waffen-Verschluss (Scheitelpunkt bei ~2.85s)
            posX = renderX - forwardDir * stickmanDrawSize * (6.0 / 48);
            posY = baseY - stickmanDrawSize * (2.0 / 48);
            haloCol = '#00e5ff';
            rScale = 0.90;
          }

          // Auf 40% skaliert (-60% kleiner) für präzise, punktuelle Kurzschluss-Pops
          const flashR = stickmanDrawSize * (0.032 + fT * 0.044) * rScale;

          // Radial energy bloom (quality !== 'low')
          if (this.quality !== 'low') {
            const flashCanvas = getStickmanCollapseFlashCanvas(haloCol);
            if (flashCanvas) {
              ctx.save();
              ctx.globalAlpha = fT * 0.92;
              ctx.drawImage(flashCanvas, posX - flashR, posY - flashR, flashR * 2, flashR * 2);
              ctx.restore();
            }
          }

          // Micro lens flare glints (-60% scale)
          const hRayW = stickmanDrawSize * 0.12 * fT * rScale;
          const vRayH = stickmanDrawSize * 0.076 * fT * rScale;
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.0 * fT;
          ctx.beginPath();
          ctx.moveTo(posX - hRayW, posY);
          ctx.lineTo(posX + hRayW, posY);
          ctx.moveTo(posX, posY - vRayH);
          ctx.lineTo(posX, posY + vRayH);
          ctx.stroke();

          ctx.strokeStyle = haloCol;
          ctx.lineWidth = 0.6 * fT;
          ctx.beginPath();
          const dSpan = hRayW * 0.65;
          ctx.moveTo(posX - dSpan, posY - dSpan * 0.5);
          ctx.lineTo(posX + dSpan, posY + dSpan * 0.5);
          ctx.moveTo(posX - dSpan, posY + dSpan * 0.5);
          ctx.lineTo(posX + dSpan, posY - dSpan * 0.5);
          ctx.stroke();
        }

        ctx.restore();
      }

      // ── 💨 KOMPAKTER AUFPRALL-PUFF: Dense knee-impact dust puff masking lower limbs & feet (2.65s - 3.25s) ──
      if (this.quality !== 'low' && elapsed >= 2.65 && elapsed <= 3.25) {
        const puffProg = (elapsed - 2.65) / 0.60;
        const puffEnv = (puffProg < 0.22) ? (puffProg / 0.22) : Math.pow(1.0 - (puffProg - 0.22) / 0.78, 1.5);
        const pAlpha = Math.max(0, Math.min(0.85, puffEnv * 0.80));

        if (pAlpha > 0.02) {
          ctx.save();
          const impactPuffs = STICKMAN_IMPACT_PUFF_SPECS;
          for (let pi = 0; pi < 4; pi++) {
            const spec = impactPuffs[pi];
            const pX = renderX + forwardDir * (stickmanDrawSize * spec.oxFrac) + Math.sin(pi * 2.1 + elapsed * 3) * 2;
            const pY = footGroundY + spec.oy - (puffProg * 8);
            const pR = spec.r + puffProg * spec.exp;
            const alphaMult = (pi === 0) ? 1.0 : (pi === 1 ? 0.90 : (pi === 2 ? 0.75 : 0.70));

            ctx.fillStyle = STICKMAN_IMPACT_PUFF_BASE_COLORS[pi];
            ctx.globalAlpha = pAlpha * alphaMult;
            ctx.beginPath();
            ctx.arc(pX, pY, pR, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }
      }

      // ── 2. RISING DUST CLOUD: Followed by soft billowing dust rising from knee & chassis impact (1.50s - 3.8s+) ──
      if (this.quality !== 'low' && elapsed >= 1.50) {
        const dustElapsed = elapsed - 1.50; // 0.0s -> 2.3s+
        if (dustElapsed < 2.40) {
          ctx.save();
          const dNorm = dustElapsed / 2.40; // 0.0 -> 1.0
          // Dust swells up as knees impact, then gently ascends and disperses
          const dEnvelope = (dNorm < 0.18) ? (dNorm / 0.18) : Math.pow(1.0 - (dNorm - 0.18) / 0.82, 1.6);
          const dAlpha = Math.max(0, dEnvelope * 0.38);

          for (let pIdx = 0; pIdx < STICKMAN_LANDING_DUST_PUFF_CONFIG.length; pIdx++) {
            const puff = STICKMAN_LANDING_DUST_PUFF_CONFIG[pIdx];
            const baseOx = forwardDir * (stickmanDrawSize * puff.oxFrac);
            // Upward draft rise from footGroundY
            const riseY = Math.pow(dNorm, 0.75) * puff.liftSpd;
            const curX = renderX + baseOx + (puff.sway * dNorm * 10) + Math.sin(dustElapsed * 3 + pIdx) * 2;
            const curY = footGroundY - riseY;
            const curR = puff.rad + (dNorm * puff.expandRate);

            ctx.fillStyle = STICKMAN_LANDING_DUST_COLORS[pIdx % STICKMAN_LANDING_DUST_COLORS.length];
            ctx.globalAlpha = dAlpha * STICKMAN_LANDING_DUST_ALPHAS[pIdx % STICKMAN_LANDING_DUST_ALPHAS.length];
            ctx.beginPath();
            ctx.ellipse(curX, curY, curR, curR * 0.65, 0, 0, Math.PI * 2);
            ctx.fill();

            // Secondary upper billow lobe
            if (dNorm > 0.10) {
              const subRise = riseY * 1.28;
              const subR = curR * 0.72;
              const sIdx = (pIdx + 1) % STICKMAN_LANDING_DUST_COLORS.length;
              ctx.fillStyle = STICKMAN_LANDING_DUST_COLORS[sIdx];
              ctx.globalAlpha = dAlpha * STICKMAN_LANDING_DUST_ALPHAS[sIdx];
              ctx.beginPath();
              ctx.ellipse(curX + puff.sway * 3, footGroundY - subRise, subR, subR * 0.60, 0, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          ctx.restore();
        }
      }

      // Lingering cooling smoke wisps from collapsed chassis vents (quality !== 'low')
      if (this.quality !== 'low') {
        const smokePhase = (t * 1.2 + (unit.x * 0.05)) % 1.0;
        ctx.save();
        ctx.globalAlpha = (1.0 - smokePhase) * 0.32;
        ctx.fillStyle = '#475569';
        ctx.beginPath();
        ctx.arc(renderX - dir * 6, renderY + stickmanYOffset + (stickmanDrawSize * 0.15) - smokePhase * 16, 3 + smokePhase * 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      ctx.restore(); // Restores ctx.save() at top of renderUnit
      return;
    }

    // 🏛️ INDESTRUCTIBLE GOLDEN MONUMENT RENDERING
    if (unit.isGoldenStatue) {
      const isLootingStatue = Boolean(
        unit.isLootingVulture ||
        unitType === 'LootingVulture' ||
        unitType === 'Looting_Vulture' ||
        unit.name === 'Looting Vulture'
      );
      // 1. Plinth Footing Alignment
      // Sprite is drawn centered at (unit.x, unit.y) with size drawSize (or drawSize * 1.6 for Looting Vulture's 4-segment dragon).
      // Unit feet touch the base at unit.y + drawSize * 0.5 - 2 (or unit.y + 24 for Stickman colossus).
      const feetY = isStickman ? (unit.y + 24) : (unit.y + drawSize * 0.5 - 2);
      const pedW = isLootingStatue ? Math.max(58, r * 3.2) : (isStickman ? Math.max(54, r * 2.4) : Math.max(28, r * 2.2));
      const pedH = Math.max(7, Math.round(r * 0.32));
      const pedY = feetY;
      const groundY = pedY + pedH;

      // Ground cast shadow under plinth base
      ctx.fillStyle = 'rgba(0, 0, 0, 0.48)';
      ctx.beginPath();
      ctx.ellipse(unit.x, groundY + 1, pedW * 0.58, Math.max(4, pedH * 0.42), 0, 0, Math.PI * 2);
      ctx.fill();

      // Dual-Layer Radiant Ambient Floor Aura
      ctx.save();
      ctx.fillStyle = 'rgba(250, 204, 21, 0.16)';
      ctx.beginPath();
      ctx.ellipse(unit.x, groundY, pedW * 0.72, Math.max(5, pedH * 0.55), 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(253, 224, 71, 0.08)';
      ctx.beginPath();
      ctx.ellipse(unit.x, groundY, pedW * 0.95, Math.max(7, pedH * 0.75), 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Stepped 3D Obsidian-Marmor Plinth with Inlaid 24k Gold Trim
      // Step 2 (Lower Base)
      const baseW = pedW * 1.08;
      const baseH = Math.max(3, Math.round(pedH * 0.45));
      const baseY = pedY + pedH - baseH;
      ctx.fillStyle = '#0f0e0d';
      ctx.fillRect(unit.x - baseW * 0.5, baseY, baseW, baseH);
      ctx.fillStyle = '#78350f';
      ctx.fillRect(unit.x - baseW * 0.5, baseY, baseW, 1);

      // Step 1 (Main Pedestal Shaft)
      ctx.fillStyle = '#1c1917';
      ctx.fillRect(unit.x - pedW * 0.5, pedY, pedW, pedH - baseH + 1);

      // Gold Inlay Filigree Borders
      ctx.fillStyle = '#b45309';
      ctx.fillRect(unit.x - pedW * 0.48, pedY + 1, pedW * 0.96, Math.max(1, (pedH - baseH) * 0.35));
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(unit.x - pedW * 0.46, pedY, pedW * 0.92, 1.5);
      // Top polished rim highlight
      ctx.fillStyle = '#fef08a';
      ctx.fillRect(unit.x - pedW * 0.42, pedY, pedW * 0.84, 1.0);

      // 2. Polished 24k Golden Statue Bitmap
      const statueBmp = buildGoldenStatueBitmap(unitType, renderRace, unit.team);
      const statueSrc = statueBmp?.normal || statueBmp;
      if (statueSrc) {
        if (isLootingStatue) {
          // Composite dragon bitmap has segSize = 0.625 * size (1 / 1.6), so drawing at
          // 1.6 * drawSize renders all 4 segments at 1:1 battlefield scale across the full 1.58x body length.
          const statueDrawSize = drawSize * 1.6;
          const statueCenterY = unit.y + drawSize * 0.08;
          ctx.drawImage(
            statueSrc,
            unit.x - statueDrawSize * 0.5,
            statueCenterY - statueDrawSize * 0.5,
            statueDrawSize,
            statueDrawSize
          );
        } else if (isStickman) {
          const stickmanYOffset = 24 - drawSize / 2;
          ctx.drawImage(statueSrc, unit.x - drawSize / 2, unit.y + stickmanYOffset - drawSize / 2, drawSize, drawSize);
        } else {
          ctx.drawImage(statueSrc, unit.x - drawSize / 2, unit.y - drawSize / 2, drawSize, drawSize);
        }
      }

      // 3. Multi-Point Specular Glints & Ascending Gold Dust Atmosphere
      if (this.quality !== 'low') {
        ctx.save();
        // 3a. Wandering Specular Star Glints across the statue surface
        const glintSpreadX = isLootingStatue ? (r * 1.15) : (isStickman ? (r * 0.95) : (r * 0.55));
        for (let g = 0; g < 3; g++) {
          const gCycle = (t * 2.2 + g * 1.95 + ((unit.x * 0.08) % 3.0)) % 3.0;
          if (gCycle < 0.75) {
            const gAlpha = Math.sin((gCycle / 0.75) * Math.PI);
            const gx = unit.x + Math.sin(g * 2.1 + unit.x * 0.03) * glintSpreadX;
            const gy = (unit.y - (isStickman ? r * 0.7 : r * 0.4)) + Math.cos(g * 1.7 + unit.y * 0.03) * (r * (isStickman ? 0.8 : 0.5));
            const gSize = 2.5 + Math.sin(g) * 1.0;

            ctx.globalAlpha = gAlpha * 0.9;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(gx - 0.75, gy - gSize, 1.5, gSize * 2);
            ctx.fillRect(gx - gSize, gy - 0.75, gSize * 2, 1.5);
            ctx.fillStyle = '#fef08a';
            ctx.fillRect(gx - 0.5, gy - 0.5, 1, 1);
          }
        }

        // 3b. Ascending Gold Dust Motes rising from the plinth
        for (let m = 0; m < 4; m++) {
          const mPhase = (t * 1.4 + m * 0.78 + (unit.x * 0.05)) % 1.0;
          const mx = unit.x + Math.sin(m * 2.3 + t * 2.0) * (pedW * 0.42);
          const my = pedY - mPhase * (drawSize * 0.85);
          const mAlpha = Math.sin(mPhase * Math.PI) * 0.75;
          ctx.globalAlpha = mAlpha;
          ctx.fillStyle = m % 2 === 0 ? '#fde047' : '#ffffff';
          ctx.fillRect(mx - 1, my - 1, 2, 2);
        }
        ctx.restore();
      }

      ctx.restore(); // restores ctx.save() at top of renderUnit
      return; // Skip normal living unit HP bars, auras, and weapons
    }

    if (renderRace === 'Sul') {
      floorLight0 = 'rgba(132, 204, 22, 0.18)'; floorLight1 = 'rgba(132, 204, 22, 0.05)';
    } else if (renderRace === 'Human') {
      if (FIRE_MECH_TYPES.has(unitType)) {
        floorLight0 = 'rgba(249, 115, 22, 0.18)'; floorLight1 = 'rgba(249, 115, 22, 0.05)';
      } else if (unit.team === 'western') {
        floorLight0 = 'rgba(56, 189, 248, 0.12)'; floorLight1 = 'rgba(56, 189, 248, 0.05)';
      } else {
        floorLight0 = 'rgba(239, 68, 68, 0.12)'; floorLight1 = 'rgba(239, 68, 68, 0.05)';
      }
    }

    // Gold Edition (js/engine/inventory.js special-card ownership, resolved
    // into unit.edition at spawn - see docs/INVARIANTS.md rule 1) reuses the
    // same golden floor-light the game already draws for heroes/bosses: zero
    // extra draw calls, just a different color for a gradient that would be
    // painted either way.
    const isGoldEdition = unit.edition === 'gold';
    if (isGoldEdition) {
      floorLight0 = 'rgba(250, 204, 21, 0.22)'; floorLight1 = 'rgba(250, 204, 21, 0.05)';
    }

    // Dynamic floor lighting (skipped on low; only for gold-edition on mobile medium)
    const shouldDrawFloorLight = this.quality !== 'low' && (!this.isMobile || this.quality === 'high' || this.quality === 'ultra' || isGoldEdition);
    if (floorLight0 && shouldDrawFloorLight) {
      const lightTex = getUnitFloorLightCanvas(floorLight0, floorLight1);
      if (lightTex) {
        ctx.drawImage(lightTex, renderX - lightRadius, groundPointY - lightRadius * 0.45, lightRadius * 2, lightRadius * 0.9);
      }
    }

    // Speed Trail / Stimpack Adrenaline Ghosts
    let hasSpeedBuff = false;
    if (unit.buffs && unit.buffs.length > 0) {
      for (let bi = 0; bi < unit.buffs.length; bi++) {
        if (unit.buffs[bi].speedMult > 1.0) {
          hasSpeedBuff = true;
          break;
        }
      }
    }
    if (hasSpeedBuff) {
      for (let s = 1; s <= 2; s++) {
        ctx.fillStyle = s === 1 ? 'rgba(249, 115, 22, 0.3)' : 'rgba(239, 68, 68, 0.15)';
        ctx.fillRect(renderX - dir * (s * 10), renderY - r * 0.8, r * 1.6, r * 1.6);
      }
    }

    // Sul Dissolving Acid Visual FX (Green/Purple sizzle on victims)
    if (unit.acidStacks > 0) {
      ctx.save();
      ctx.fillStyle = '#84cc16';
      if (this.quality === 'low') {
        const aPhase = (t * 4) % 1.0;
        ctx.globalAlpha = 0.75;
        ctx.fillRect(renderX - 2, renderY + r * 0.2 - aPhase * r, 2.5, 2.5);
        ctx.fillRect(renderX + 2, renderY + r * 0.4 - ((aPhase + 0.5) % 1.0) * r, 2, 2);
      } else {
        for (let a = 0; a < Math.min(6, unit.acidStacks * 2); a++) {
          const aPhase = (t * 4 + a * 1.3) % 1.0;
          const ax = renderX + Math.sin(a * 2.1 + t) * (r * 0.8);
          const ay = renderY + r * 0.4 - aPhase * (r * 1.4);
          ctx.globalAlpha = (1.0 - aPhase) * 0.8;
          ctx.fillRect(ax - 1.5, ay - 1.5, 3, 3);
        }
      }
      ctx.restore();
    }

    // 1. BACKGROUND AURAS & GLOWS (Rendered behind sprite)
    // Custom Unit Aura / Underglow & Pixel Flame Corona (Slot Machine Unlockable)
    const customAuraHex = (typeof unit.getCustomAuraColorHex === 'function')
      ? unit.getCustomAuraColorHex()
      : (unit.customAuraHex || null);

    if (customAuraHex && !unit.isParachuting) {
      if (this.quality === 'low') {
        // Fast 1-stroke underglow ring on low tier: preserves the player's unlocked aura color
        ctx.save();
        ctx.strokeStyle = customAuraHex;
        ctx.globalAlpha = 0.55;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.ellipse(renderX, renderY + r * 0.4, r * 0.85, r * 0.35, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      } else {
      // Resolve unit visual size and center Y offset for units with non-standard scale or anchor
      let unitVisualDrawSize = drawSize;
      let unitVisualCenterY = renderY;

      if (isStickman) {
        unitVisualDrawSize = drawSize;
        const stickmanYOffset = 24 - drawSize / 2;
        unitVisualCenterY = renderY + stickmanYOffset;
      } else if (unitType === 'A9Piranha') {
        unitVisualDrawSize = drawSize * 1.236;
      } else if (unitType === 'A9Pigeon') {
        unitVisualDrawSize = drawSize * 1.18;
      } else if (unitType === 'A9Grizzly') {
        unitVisualDrawSize = drawSize * 1.12;
      } else if (unitType === 'TheSalvageCompany') {
        unitVisualDrawSize = drawSize * 1.15;
      } else if (unitType === 'Orchestra') {
        unitVisualDrawSize = drawSize * 1.25;
      } else if (unitType === 'AntiShade' || unitType === 'Anti_Shade' || unitType === 'Anti Shade') {
        unitVisualDrawSize = drawSize * 1.10;
      } else if (unitType === 'PinkBeret' || unitType === 'Pink Beret' || isPinkBeret) {
        unitVisualDrawSize = drawSize * 1.12;
      } else if (isHacker) {
        unitVisualDrawSize = drawSize * 1.35;
      } else if (unitType === 'LootingVulture' || unitType === 'Looting_Vulture' || unitType === 'Looting Vulture') {
        unitVisualCenterY = renderY + drawSize * 0.08;
      } else if (unitType === 'Pulp') {
        if (isWalking) {
          const hopCycle = ((walkPhase * 0.42 % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const u = hopCycle / (Math.PI * 2);
          let pulpBounceY = 0;
          if (u < 0.78) {
            const uAir = u / 0.78;
            const hang = Math.pow(Math.sin(uAir * Math.PI), 0.72);
            pulpBounceY = -hang * (drawSize * (8.0 / 24.0));
          } else {
            const uLand = (u - 0.78) / 0.22;
            const squash = Math.sin(uLand * Math.PI);
            pulpBounceY = squash * (drawSize * (1.8 / 24.0));
          }
          unitVisualCenterY = renderY + pulpBounceY;
        }
      } else if (unitType === 'HotHand' || unit.name === 'Hot Hand' || unit.cardData?.name === 'Hot Hand') {
        if (unit._dodgeAnimTime > 0) {
          const dodgeStyle = unit._dodgeStyle || 'roll';
          if (dodgeStyle === 'slide') {
            unitVisualCenterY = renderY + 3.5;
          } else {
            const rollProgress = Math.max(0, Math.min(1, 1 - (unit._dodgeAnimTime / 0.45)));
            unitVisualCenterY = renderY - Math.sin(rollProgress * Math.PI) * 4.0;
          }
        }
      }

      // 1. High-Performance 3-Frame Pixel Flame Aura (Pre-baked offscreen canvas blit)
      const frameIndex = Math.floor((t * 10) % 3);
      const auraEntry = buildAuraBitmap(unitType, renderRace, unit.team, customAuraHex, frameIndex);
      if (auraEntry && auraEntry.canvas) {
        const cellScreenSize = unitVisualDrawSize / auraEntry.gridSize;
        const auraDrawW = (auraEntry.gridSize + auraEntry.padX * 2) * cellScreenSize;
        const auraDrawH = (auraEntry.gridSize + auraEntry.padTop + auraEntry.padBottom) * cellScreenSize;
        const auraDrawX = renderX - unitVisualDrawSize / 2 - auraEntry.padX * cellScreenSize;
        const auraDrawY = unitVisualCenterY - unitVisualDrawSize / 2 - auraEntry.padTop * cellScreenSize;

        ctx.drawImage(auraEntry.canvas, auraDrawX, auraDrawY, auraDrawW, auraDrawH);
      }

      // 2. Upward floating flame sparks (deterministic, zero garbage collection)
      ctx.save();
      ctx.fillStyle = customAuraHex;
      const seed = unit.id ? (unit.id.charCodeAt(0) || 1) : 1;
      for (let sp = 0; sp < 3; sp++) {
        const sparkPhase = (t * 2.2 + sp * 0.33 + seed * 0.1) % 1.0;
        const sparkY = unitVisualCenterY + r * 0.3 - sparkPhase * (r * 2.2);
        const sparkX = renderX + Math.sin(t * 7 + sp * 2.4 + seed) * (r * 0.65);
        const sparkAlpha = Math.sin(sparkPhase * Math.PI) * 0.85;
        ctx.globalAlpha = Math.max(0, sparkAlpha);
        ctx.fillRect(Math.floor(sparkX), Math.floor(sparkY), 2, 2);
      }
      ctx.restore();
      }
    }

    // Spawn materialization - a short faction-specific "arriving" effect that
    // plays for materializeDuration right after a unit enters the field,
    // instead of it just popping into existence. Purely procedural (no
    // particle-system dependency, renderUnit doesn't receive `particles`),
    // and radially symmetric so it doesn't need the facing-flip below.
    let spawnAlpha = 1;
    if (unit.materializeTimer > 0 && unit.materializeDuration > 0) {
      const p = 1 - (unit.materializeTimer / unit.materializeDuration); // 0 -> 1
      spawnAlpha = Math.max(0.15, p);
      ctx.save();
      if (renderRace === 'Sul') {
        // Sul organic ground rupture - biomass chunks bursting outward
        ctx.globalAlpha = 0.5 * (1 - p);
        ctx.fillStyle = '#84cc16';
        for (let i = 0; i < 5; i++) {
          const ang = (i / 5) * Math.PI * 2 + unit.wobbleOffset;
          const dist = r * (0.6 + p * 1.4);
          ctx.fillRect(renderX + Math.cos(ang) * dist - 2, renderY + Math.sin(ang) * dist * 0.5 - 2, 4, 4);
        }
      } else {
        // Human (and default) - descending teleporter beam
        const beamH = r * 3.2 * (1 - p);
        if (this.quality === 'low' || isSimpleTier) {
          ctx.globalAlpha = 0.4 * (1 - p);
          ctx.fillStyle = '#00f0ff';
          ctx.fillRect(renderX - r * 0.5, renderY - r - beamH, r, beamH);
        } else {
          const beamCanvas = getTeleportBeamCanvas();
          if (beamCanvas) {
            ctx.save();
            ctx.globalAlpha = 1 - p;
            ctx.drawImage(beamCanvas, renderX - r * 0.5, renderY - r - beamH, r, beamH);
            ctx.restore();
          } else {
            ctx.globalAlpha = 0.4 * (1 - p);
            ctx.fillStyle = '#00f0ff';
            ctx.fillRect(renderX - r * 0.5, renderY - r - beamH, r, beamH);
          }
        }
      }
      ctx.restore();
    }

    // Facing: buildSpriteBitmap() already bakes a per-TEAM horizontal mirror
    // into the cached bitmap itself (western unflipped, eastern pre-flipped),
    // so it natively faces `forwardDir` for either team. Only mirror it here
    // ON TOP of that when the live `dir` actually differs from that baked
    // default (i.e. a target pulled the unit to face the other way) -
    // mirroring whenever dir===-1 outright would double-flip every eastern
    // unit back to facing right.
    const needsSpriteFlip = dir !== forwardDir;
    ctx.save();
    if (needsSpriteFlip) {
      ctx.translate(renderX, 0);
      ctx.scale(-1, 1);
      ctx.translate(-renderX, 0);
    }

    // Heavy Hit Elastic Squash & Stretch
    if (unit.hitSquashTimer > 0) {
      const sqProg = 1 - (unit.hitSquashTimer / 0.08);
      const sqAmount = Math.sin(sqProg * Math.PI) * 0.16;
      ctx.translate(renderX, renderY);
      ctx.scale(1 + sqAmount, 1 - sqAmount);
      ctx.translate(-renderX, -renderY);
    }

    if (unit.isHeld) {
      // Violent helpless struggle & thrashing in the hydraulic jaws
      const thrashAngle = Math.sin(t * 30) * 0.22 - 0.15 * dir;
      ctx.translate(renderX, renderY);
      ctx.rotate(thrashAngle);
      ctx.translate(-renderX, -renderY);

      // High-voltage dielectric crackle arcs wrapping around the victim
      ctx.save();
      ctx.strokeStyle = '#00f0ff';
      ctx.lineWidth = 1.6;
      for (let a = 0; a < 3; a++) {
        const aPhase = t * 24 + a * 2.3;
        const ax = renderX + Math.sin(aPhase) * (r * 0.85);
        const ay = renderY + Math.cos(aPhase * 1.5) * (r * 0.85);
        ctx.beginPath();
        ctx.moveTo(renderX, renderY);
        ctx.lineTo(ax + (Math.sin(aPhase * 3) * 6), ay + (Math.cos(aPhase * 3) * 6));
        ctx.stroke();
      }
      // Pulsating crimson struggle outline
      const panicGlow = 0.5 + 0.5 * Math.sin(t * 20);
      ctx.globalAlpha = 0.4 + panicGlow * 0.5;
      ctx.strokeStyle = '#ff003c';
      ctx.lineWidth = 2.0;
      ctx.beginPath();
      ctx.arc(renderX, renderY, r * 1.05, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Cloak transparency, combined multiplicatively with the spawn fade-in
    // so a cloaked unit that just warped in fades from fully-transparent up
    // to its usual 42% instead of snapping straight to 42%.
    let bodyAlpha = unit.isCloaked ? 0.42 : 1;
    if (!isPinkBeret) bodyAlpha *= spawnAlpha;
    if (bodyAlpha < 1) ctx.globalAlpha = bodyAlpha;

    // 2. PRE-RENDERED 8-BIT SPRITE BITMAP
    // Baked once per (unitType, race, team) in buildSpriteBitmap() - silhouette
    // outline, directional shading and pixel-snapping all happen there, not
    // per frame.
    ctx.imageSmoothingEnabled = false;

    const isPanzer = (unitType === 'Panzer' || unitType === 'HyperPanzer');
    const isMarine = (unitType === 'Marine');
    const isHyperMarine = (unitType === 'HyperMarine');
    const isDevilPatrol = (unitType === 'DevilPatrol');
    const isHyperDevilPatrol = (unitType === 'HyperDevilPatrol');
    const isWheelieBomber = (unitType === 'WheelieBomber');
    const isShade = (unitType === 'Shade');
    const isHyperShade = (unitType === 'HyperShade');
    const isPulp = (unitType === 'Pulp');
    const isHotHand = (unitType === 'HotHand' || unit.name === 'Hot Hand' || unit.cardData?.name === 'Hot Hand');

    if (unit.isParachuting) {
      // 🪂 PARACHUTE PILOT DESCENT: Wind-sway and parachute canopy floating down to battlefield
      // Solid 100% opacity - never transparent!
      ctx.globalAlpha = 1.0;

      const chuteKey = (unit.cardData?.spriteRecolor === 'gold') ? 'MarineGold_Parachute' : (unitType + '_Parachute');
      const chuteBitmap = buildSpriteBitmap(chuteKey, renderRace, unit.team) || buildSpriteBitmap(unitType + '_Parachute', renderRace, unit.team);
      const chuteSrc = isHitFlashing ? chuteBitmap.flash : chuteBitmap.normal;
      const sway = Math.sin(t * 4.5 + unit.wobbleOffset) * 0.12;
      const altitudeY = unit.parachuteAltitude || 0;

      // Generous scale so the pilot matches full Marine stature and the canopy billows overhead!
      const chuteDrawWidth = drawSize * 1.6;
      const chuteDrawHeight = drawSize * 2.0;

      ctx.save();
      ctx.translate(renderX, renderY - altitudeY);
      ctx.rotate(sway);
      ctx.drawImage(
        chuteSrc,
        -chuteDrawWidth / 2,
        -chuteDrawHeight * 0.7,
        chuteDrawWidth,
        chuteDrawHeight
      );
      ctx.restore();
    } else if (isPanzer) {
      if (this.quality === 'low' || isSimpleTier) {
        let panzerKey = unitType;
        if (isWalking) {
          const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const rollIdx = Math.floor((cycle / (Math.PI * 2)) * 3) % 3;
          panzerKey = (unitType === 'HyperPanzer') ? HYPER_PANZER_ROLL_COMPOSITES[rollIdx] : PANZER_ROLL_COMPOSITES[rollIdx];
        }
        const panzerBmp = buildSpriteBitmap(panzerKey, renderRace, unit.team) || buildSpriteBitmap(unitType, renderRace, unit.team);
        const panzerSrc = isHitFlashing ? panzerBmp?.flash : panzerBmp?.normal;
        if (panzerSrc) {
          drawLowTierWalkSprite(ctx, panzerSrc, renderX, renderY, drawSize, drawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_ROLL);
        }
      } else {
      // 🚀 TWO-PART PANZER / TANK ANIMATION: Ober- und Unterteil
      const rollFrames = (unitType === 'HyperPanzer') ? HYPER_PANZER_CHASSIS_ROLL_FRAMES : PANZER_CHASSIS_ROLL_FRAMES;
      let chassisKey = (unitType === 'HyperPanzer') ? 'HyperPanzer_Chassis' : 'Panzer_Chassis';
      if (isWalking) {
        const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const rollIdx = Math.floor((cycle / (Math.PI * 2)) * 3) % 3;
        chassisKey = rollFrames[rollIdx];
      }
      const chassisBitmap = buildSpriteBitmap(chassisKey, renderRace, unit.team) || buildSpriteBitmap(unitType + '_Chassis', renderRace, unit.team);
      let turretKey = `${unitType}_Turret`;
      let turretAimAngle = 0;
      if (unitType === 'HyperPanzer') {
        const aimTarget = unit.groundTarget || unit.target;
        if (aimTarget && !aimTarget.isDead) {
          const forwardDir = (unit.team === 'western' ? 1 : -1);
          const dx = (aimTarget.x - unit.x) * forwardDir;
          const dy = aimTarget.y - unit.y;
          const deg = Math.atan2(dy, dx) * (180 / Math.PI);

          if (dx < 0 && Math.abs(dy) < 18) {
            turretKey = 'HyperPanzer_Turret';
            unit.turretAimState = 'straight';
          } else if (deg <= -55) {
            turretKey = 'HyperPanzer_Turret_Up';
            unit.turretAimState = 'up';
          } else if (deg <= -18) {
            turretKey = 'HyperPanzer_Turret_Left';
            unit.turretAimState = 'left';
          } else if (deg >= 55) {
            turretKey = 'HyperPanzer_Turret_Down';
            unit.turretAimState = 'down';
          } else if (deg >= 18) {
            turretKey = 'HyperPanzer_Turret_Right';
            unit.turretAimState = 'right';
          } else {
            turretKey = 'HyperPanzer_Turret';
            unit.turretAimState = 'straight';
          }
        } else {
          turretKey = 'HyperPanzer_Turret';
          unit.turretAimState = 'straight';
        }
        turretAimAngle = 0; // Pure discrete pixel art barrel elevation prevents double-tilting & kinking
      }
      const turretBitmap = buildSpriteBitmap(turretKey, renderRace, unit.team) || buildSpriteBitmap(unitType + '_Turret', renderRace, unit.team);

      const chassisSrc = isHitFlashing ? chassisBitmap.flash : chassisBitmap.normal;
      const turretSrc = isHitFlashing ? turretBitmap.flash : turretBitmap.normal;

      // 1. CHASSIS (Unterteil): Suspension bounce & caterpillar tread rumble
      const chassisBob = isWalking ? Math.sin(walkPhase) * 1.0 : 0;

      ctx.drawImage(
        chassisSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 + chassisBob,
        drawSize,
        drawSize
      );

      // 2. TURRET (Oberteil): Firing Recoil Slide (Rohrrücklauf) & Elevation Aiming with Damped Spring
      let turretRecoilX = 0;
      let turretRockAngle = 0;
      const cooldownVal = (unitType === 'HyperPanzer') ? (unit.cannonCooldown || 0) : unit.attackCooldown;
      const attackSpdVal = unit.attackSpeed || 2.5;
      if (cooldownVal > 0 && cooldownVal > (attackSpdVal - 0.28)) {
        const recoilFraction = (cooldownVal - (attackSpdVal - 0.28)) / 0.28; // 1.0 -> 0.0
        const spring = dampedSpringRecoil(recoilFraction);
        turretRecoilX = -spring * (unitType === 'HyperPanzer' ? 7.5 : 6.2); // Heavy kinetic backward slide along barrel axis
        turretRockAngle = -spring * (unitType === 'HyperPanzer' ? 0.11 : 0.09); // Turret tilts upward on turret ring
      }

      // Vertical target elevation tracking (HyperPanzer stays anchored to hull Drehkranz; cannon barrel handles elevation in sprite)
      let turretAimY = 0;
      const aimTarget = (unitType === 'HyperPanzer') ? (unit.groundTarget || unit.target) : unit.target;
      if (aimTarget && !aimTarget.isDead) {
        const dTargetY = aimTarget.y - unit.y;
        turretAimY = (unitType === 'HyperPanzer') ? 0 : Math.max(-4.5, Math.min(4.5, dTargetY * 0.035));
      }

      // Draw Turret with pivot rotation and recoil slide
      ctx.save();
      const pivotX = renderX - drawSize * 0.06;
      const pivotY = renderY - drawSize * 0.06 + chassisBob;
      ctx.translate(pivotX, pivotY);
      ctx.rotate(turretRockAngle + turretAimAngle);
      ctx.translate(-pivotX, -pivotY);

      ctx.drawImage(
        turretSrc,
        renderX - drawSize / 2 + turretRecoilX,
        renderY - drawSize / 2 + chassisBob + turretAimY,
        drawSize,
        drawSize
      );

      // 🔥 Thermal Barrel Glow (Kirschrot zu Glühend-Gold bei Schussabgabe)
      // Uses pre-baked 64x4 Gradient LUT Strip (Paradigma 3)
      if (unit.barrelHeat > 0.04 && this.quality !== 'low') {
        const heatProg = unit.barrelHeat;
        const bDir = unit.team === 'western' ? 1 : -1;
        const barrelTipX = renderX + bDir * (drawSize * 0.32) + turretRecoilX;
        const barrelTipY = renderY - drawSize * 0.04 + chassisBob + turretAimY;
        const barrelW = drawSize * 0.22;
        const barrelH = 4;
        const heatStrip = getThermalHeatStrip();

        if (heatStrip) {
          ctx.save();
          ctx.globalAlpha = heatProg;
          const drawX = barrelTipX - (bDir === 1 ? barrelW : 0);
          const drawY = barrelTipY - barrelH / 2;
          if (bDir === 1) {
            ctx.drawImage(heatStrip, drawX, drawY, barrelW, barrelH);
          } else {
            ctx.translate(drawX + barrelW, drawY);
            ctx.scale(-1, 1);
            ctx.drawImage(heatStrip, 0, 0, barrelW, barrelH);
          }
          ctx.restore();
        }

        // Radial Thermal Bloom Halo
        ctx.globalAlpha = heatProg * 0.4;
        ctx.fillStyle = '#f97316';
        ctx.beginPath();
        ctx.arc(barrelTipX, barrelTipY, 5 * heatProg, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      }
    } else if (isPulp) {
      // 🤡 SUL PULP ANIMATION: Low-gravity clown moon-hop, articulated head nod/tilt, animated legs & sparks
      if (this.quality === 'low' || isSimpleTier) {
        let pulpKey = 'Pulp';
        let hopY = 0;
        if (isWalking) {
          const hopCycle = ((walkPhase * 0.42) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
          const u = hopCycle / (Math.PI * 2);
          let legIdx = 0;
          if (u < 0.78) {
            const uAir = u / 0.78;
            hopY = -Math.pow(Math.sin(uAir * Math.PI), 0.72) * 8.0;
            if (uAir < 0.16) legIdx = 0;
            else if (uAir < 0.35) legIdx = 1;
            else if (uAir < 0.58) legIdx = 2;
            else legIdx = 3;
          } else {
            const uGround = (u - 0.78) / 0.22;
            legIdx = (uGround < 0.50) ? 4 : 5;
          }
          pulpKey = PULP_HOP_COMPOSITES[legIdx] || 'Pulp_Hop1';
        }
        const pulpBmp = buildSpriteBitmap(pulpKey, renderRace, unit.team);
        const pulpSrc = isHitFlashing ? pulpBmp?.flash : pulpBmp?.normal;
        if (pulpSrc) {
          ctx.drawImage(pulpSrc, renderX - drawSize / 2, renderY - drawSize / 2 + hopY, drawSize, drawSize);
        }
      } else {
      let legsKey = 'Pulp_Legs_Walk1';
      let hopY = 0;
      let hopTilt = 0;
      let legTilt = 0;
      let scaleX = 1.0;
      let scaleY = 1.0;
      let legScaleX = 1.0;
      let legScaleY = 1.0;
      let headPitch = 0;
      let headRoll = 0;
      let headBobY = 0;

      if (isWalking) {
        // Low-gravity moon-hop cycle: deliberate, airy stride (~1.4 hops/sec)
        const hopCycle = ((walkPhase * 0.42) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
        const u = hopCycle / (Math.PI * 2); // 0.0 to 1.0

        if (u < 0.78) {
          // ── PHASE A: AIRBORNE LUNAR FLOAT (0.00 <= u < 0.78) ──
          const uAir = u / 0.78;
          // Flattened sinusoidal hangtime curve for slow-motion lunar apex
          const hang = Math.pow(Math.sin(uAir * Math.PI), 0.72);
          const maxHopHeight = 8.0;
          hopY = -hang * maxHopHeight;

          // Leg phase selection & articulation across flight
          if (uAir < 0.16) {
            // Frame 0: Launch push-off
            legsKey = 'Pulp_Legs_Walk1';
            legScaleY = 1.15;
            legScaleX = 0.90;
            legTilt = -0.12 * dir;
            hopTilt = -0.10 * dir;
            headPitch = -0.32 * dir; // Exaggerated push-off inertia: chin thrown back high!
            headRoll = -0.10 * dir;
            headBobY = 1.0;
          } else if (uAir < 0.35) {
            // Frame 1: Ascent Stretch & Driving Foot
            legsKey = 'Pulp_Legs_Walk2';
            const ascProg = (uAir - 0.16) / 0.19;
            legScaleY = 1.12;
            legScaleX = 0.94;
            legTilt = -0.06 * dir;
            hopTilt = -0.05 * dir;
            headPitch = (-0.32 + ascProg * 0.44) * dir; // Smoothly swinging forward from -0.32 to +0.12!
            headRoll = (-0.10 + ascProg * 0.18) * dir;
            headBobY = 1.0 - ascProg * 1.6;
          } else if (uAir < 0.58) {
            // Frame 2: APEX THEATRICAL HIGH CLOWN KICK! 🤡
            legsKey = 'Pulp_Legs_Walk3';
            const apexProg = (uAir - 0.35) / 0.23;
            // The leg swings high forward!
            legTilt = 0.24 * dir * Math.sin(apexProg * Math.PI);
            legScaleY = 1.05;
            hopTilt = 0.08 * dir * Math.sin(apexProg * Math.PI);
            headPitch = (0.12 + Math.sin(apexProg * Math.PI) * 0.24) * dir; // Exaggerated forward rocking chin (+0.36 rad)!
            headRoll = Math.sin(apexProg * Math.PI * 2) * 0.20;    // Exaggerated comedic head tilt & bell jingle
            headBobY = -1.6 * Math.sin(apexProg * Math.PI);
          } else if (uAir < 0.80) {
            // Frame 3: Mid-air Bicycle Prance & Scissor
            legsKey = 'Pulp_Legs_Walk4';
            const scissorProg = (uAir - 0.58) / 0.22;
            legTilt = 0.12 * dir * (1 - scissorProg);
            hopTilt = 0.04 * dir;
            headPitch = (0.12 - scissorProg * 0.22) * dir; // Rocks back towards -0.10 rad
            headRoll = Math.cos(scissorProg * Math.PI) * 0.14;
            headBobY = -0.4 * (1 - scissorProg);
          } else {
            // Frame 4: Descent Reach
            legsKey = 'Pulp_Legs_Walk5';
            const descProg = (uAir - 0.80) / 0.20;
            legTilt = 0.04 * dir * (1 - descProg);
            legScaleY = 1.14; // Legs reach down for ground!
            legScaleX = 0.90;
            hopTilt = 0.08 * dir * descProg;
            headPitch = (-0.10 - descProg * 0.10) * dir; // Anticipatory tilt back before impact
            headRoll = -0.06 * dir * descProg;
            headBobY = 0.2 * descProg;
          }
        } else {
          // ── PHASE B: TOUCHDOWN & DEEP COMEDIC SQUASH (0.78 <= u < 1.00) ──
          const uLand = (u - 0.78) / 0.22;
          legsKey = 'Pulp_Legs_Walk6'; // Wide comedic knee splay & compressed tread!
          const squash = Math.sin(uLand * Math.PI);

          hopY = squash * 1.8;
          scaleY = 1.0 - squash * 0.22;
          scaleX = 1.0 + squash * 0.18;
          // Legs squash heavily into ground!
          legScaleY = 1.0 - squash * 0.32;
          legScaleX = 1.0 + squash * 0.28;
          legTilt = 0.05 * dir * (1 - uLand);
          hopTilt = 0.04 * dir * (1 - uLand);

          // Head impact dynamics: dramatic comedic nod & overshoot!
          headPitch = (0.42 * squash - 0.15 * (1 - squash)) * dir; // WHAM! Massive forward head nod (+0.42 rad ~ 24 deg!)
          headBobY = squash * 2.2;
          headRoll = Math.sin(uLand * Math.PI * 2) * 0.16;

          // Tread friction sparks & dust on landing touchdown
          if (this.quality !== 'low' && uLand >= 0.05 && uLand <= 0.40 && Math.random() < 0.55) {
            this.game?.particles?.pushParticle?.({
              x: renderX - dir * 8 + (Math.random() * 4 - 2),
              y: renderY + 8 + (Math.random() * 2),
              vx: -dir * (24 + Math.random() * 32),
              vy: -Math.random() * 16 - 4,
              radius: 0.8 + Math.random() * 0.8,
              color: ['#f59e0b', '#fbbf24', '#ef4444', '#cbd5e1'][Math.floor(Math.random() * 4)],
              life: 0.26,
              maxLife: 0.26,
              alpha: 0.95
            });
          }
        }
      } else {
        // Idle: Eerie clown weight shift from jester leg to caterpillar tread
        const weightShift = Math.sin(t * 2.4);
        legsKey = (weightShift > 0.35) ? 'Pulp_Legs_Walk1' : 'Pulp_Legs_Walk5';
        legTilt = weightShift * 0.05;
        hopY = Math.sin(t * 3.0) * 0.5;
        hopTilt = Math.sin(t * 1.5) * 0.03;
        headRoll = Math.sin(t * 2.0) * 0.08;
        headPitch = Math.cos(t * 1.4) * 0.05;
        headBobY = Math.sin(t * 2.8) * 0.4;
      }

      const shootRecoil = isAttacking ? dampedSpringRecoil(unit.attackProgress || 0) : 0;
      const mortarKickAngle = -shootRecoil * 0.28;
      const mortarRecoilX = -dir * shootRecoil * 3.5;

      const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
      const bodyBitmap = buildSpriteBitmap('Pulp_Body', renderRace, unit.team);
      const headBitmap = buildSpriteBitmap('Pulp_Head', renderRace, unit.team);
      const weaponBitmap = buildSpriteBitmap('Pulp_Weapon', renderRace, unit.team);

      const legsSrc = isHitFlashing ? legsBitmap?.flash : legsBitmap?.normal;
      const bodySrc = isHitFlashing ? bodyBitmap?.flash : bodyBitmap?.normal;
      const headSrc = isHitFlashing ? headBitmap?.flash : headBitmap?.normal;
      const weaponSrc = isHitFlashing ? weaponBitmap?.flash : weaponBitmap?.normal;

      // 1. WEAPON (Far/Rear Shoulder Mortar with recoil - positioned BEHIND head and torso!)
      if (weaponSrc) {
        ctx.save();
        const weaponPivotX = renderX - dir * drawSize * (2 / 24);
        const weaponPivotY = renderY - drawSize * (3 / 24) + hopY;
        ctx.translate(weaponPivotX, weaponPivotY);
        ctx.rotate(hopTilt * 0.4 + mortarKickAngle);
        ctx.scale(scaleX, scaleY);
        ctx.translate(-weaponPivotX, -weaponPivotY);
        ctx.drawImage(weaponSrc, renderX - drawSize / 2 + mortarRecoilX, renderY - drawSize / 2 + hopY, drawSize, drawSize);
        ctx.restore();
      }

      // 2. LEGS (Asymmetric sprint / high clown moon-kick with hip articulation)
      if (legsSrc) {
        ctx.save();
        const hipPivotY = renderY + hopY + drawSize * (3 / 24);
        ctx.translate(renderX, hipPivotY);
        ctx.rotate(legTilt);
        ctx.scale(scaleX * legScaleX, scaleY * legScaleY);
        ctx.drawImage(legsSrc, -drawSize / 2, -drawSize / 2 - drawSize * (3 / 24), drawSize, drawSize);
        ctx.restore();
      }

      // 3. TORSO / BODY & SHORT SUL TENTACLE TIPS WOBBLING IN HOPSA-LAUF RHYTHM
      // Short writhing tentacle tips (in Sul style: plum flesh, magenta body, glowing emerald/cyan tips)
      // hanging from beneath the torn hem/waistband and reacting dynamically to the lunar hop kinematics
      const pSize = drawSize / 24;
      const tentAnchors = [
        { dx: -7.5, dy: 2.2, len: 5.5 }, // Strand 0 (Upper dorsal, background)
        { dx: -6.2, dy: 3.1, len: 6.5 }, // Strand 1 (Mid dorsal, background)
        { dx: -5.0, dy: 3.6, len: 7.0 }, // Strand 2 (Lower waist, foreground)
        { dx: -3.6, dy: 4.1, len: 5.8 }  // Strand 3 (Ventral hem, foreground)
      ];

      const drawPulpTentacleStrand = (s) => {
        const anc = tentAnchors[s];
        const rootX = dir * anc.dx * pSize;
        const rootY = anc.dy * pSize;

        let sway = 0;
        if (isWalking) {
          const hopCycle = ((walkPhase * 0.42) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
          const u = hopCycle / (Math.PI * 2);
          if (u < 0.78) {
            const uAir = u / 0.78;
            if (uAir < 0.20) {
              // Push-off drag: tentacles trail downward-rear
              sway = -0.55 * dir * (1.0 - uAir / 0.20);
            } else if (uAir < 0.45) {
              // Ascent whip: swinging through neutral
              const p = (uAir - 0.20) / 0.25;
              sway = (-0.55 + p * 0.85) * dir;
            } else if (uAir < 0.70) {
              // Apex high clown kick: weightless float & flutter
              sway = (0.35 + Math.sin(t * 14.0 + s * 1.8) * 0.24) * dir;
            } else {
              // Descent drag: trailing upward against gravity
              const p = (uAir - 0.70) / 0.30;
              sway = (0.35 + p * 0.25) * dir;
            }
          } else {
            // Touchdown & squash: comedic lateral splay & bounce
            const uLand = (u - 0.78) / 0.22;
            const squash = Math.sin(uLand * Math.PI);
            sway = ((s % 2 === 0 ? 0.70 : -0.50) * squash + Math.sin(uLand * Math.PI * 2) * 0.35) * dir;
          }
        } else {
          // Idle alien breathing writhe
          sway = (Math.sin(t * 3.4 + s * 1.7) * 0.22 + Math.cos(t * 2.1 + s * 0.9) * 0.12) * dir;
        }

        const strandAngle = Math.PI * 0.5 + dir * (0.45 + s * 0.12) + sway;
        const midX = rootX + Math.cos(strandAngle) * (anc.len * 0.45);
        const midY = rootY + Math.sin(strandAngle) * (anc.len * 0.45);

        const tipAngle = strandAngle + sway * 0.45 + dir * 0.25;
        const tipX = midX + Math.cos(tipAngle) * (anc.len * 0.55);
        const tipY = midY + Math.sin(tipAngle) * (anc.len * 0.55);

        // Segment 1 (Root to Mid): Deep Plum
        ctx.strokeStyle = '#701a75';
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(rootX, rootY);
        ctx.lineTo(midX, midY);
        ctx.stroke();

        // Segment 2 (Mid to Tip): Vibrant Sul Magenta
        ctx.strokeStyle = '#be185d';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(midX, midY);
        ctx.lineTo(tipX, tipY);
        ctx.stroke();

        // Luminous bulb tip (emerald or cyan)
        const tipColor = (s === 1 || s === 3) ? '#10b981' : '#06b6d4';
        ctx.fillStyle = tipColor;
        ctx.beginPath();
        ctx.arc(tipX, tipY, 1.2, 0, Math.PI * 2);
        ctx.fill();

        // Glint
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(tipX - 0.4, tipY - 0.4, 0.8, 0.8);
      };

      ctx.save();
      ctx.translate(renderX, renderY + hopY);
      ctx.rotate(hopTilt);
      ctx.scale(scaleX, scaleY);

      // Layer 3A: Background tentacle tips (Strands 0 and 1) peeking from under coat hem
      drawPulpTentacleStrand(0);
      drawPulpTentacleStrand(1);

      // Layer 3B: Torso & Scepter
      if (bodySrc) {
        ctx.drawImage(bodySrc, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
      }

      // Layer 3C: Foreground tentacle tips (Strands 2 and 3) flopping over waistband
      drawPulpTentacleStrand(2);
      drawPulpTentacleStrand(3);

      ctx.restore();

      // 4. ARTICULATED CLOWN HEAD (Iconic White Mask, Big Red Nose, Teeth Grin & Exaggerated Inertial Rocking - IN FOREGROUND!)
      if (headSrc) {
        ctx.save();
        const neckPivotX = renderX - dir * drawSize * (1.5 / 24);
        const neckPivotY = renderY - drawSize * (4 / 24) + hopY + headBobY;
        ctx.translate(neckPivotX, neckPivotY);
        ctx.rotate(hopTilt + headPitch + headRoll);
        ctx.scale(scaleX, scaleY);
        // Draw centered on neck pivot (row 8 col 10.5 in side profile)
        ctx.drawImage(headSrc, -drawSize / 2 + dir * drawSize * (1.5 / 24), -drawSize / 2 + drawSize * (4 / 24), drawSize, drawSize);
        ctx.restore();
      }

      // 5. Bio-Pustule green glow on right shoulder & floaty lunar bio-mote
      if (this.quality !== 'low') {
        const glowPulse = 0.5 + 0.5 * Math.sin(t * 6);
        ctx.save();
        ctx.globalAlpha = 0.4 * glowPulse * spawnAlpha;
        ctx.fillStyle = '#10b981';
        ctx.beginPath();
        ctx.arc(renderX + dir * 3, renderY - 4 + hopY, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // Occasional floating bio-mote drifting in low gravity
        if (isWalking && Math.random() < 0.08) {
          this.game?.particles?.pushParticle?.({
            x: renderX + dir * (2 + Math.random() * 3),
            y: renderY - 5 + hopY,
            vx: (Math.random() - 0.5) * 8,
            vy: -12 - Math.random() * 15,
            radius: 0.7 + Math.random() * 0.6,
            color: '#10b981',
            life: 0.6,
            maxLife: 0.6,
            alpha: 0.75
          });
        }
      }
      }
    } else if (isStickman) {
      // 🦾 STICKMAN: Slender Colossus Bipedal Walker with Heavy Underslung Lance Cannon
      const stickmanDrawSize = drawSize;
      const stickmanYOffset = 24 - stickmanDrawSize / 2;

      if (this.quality === 'low' || isSimpleTier) {
        let stickKey = 'Stickman';
        if (isWalking) {
          const cycle = ((walkPhase * 0.55 % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 8);
          stickKey = STICKMAN_WALK_COMPOSITES[phaseIdx] || 'Stickman_Walk1';
        }
        const stickmanBmp = buildSpriteBitmap(stickKey, renderRace, unit.team);
        const stickmanSrc = isHitFlashing ? stickmanBmp?.flash : stickmanBmp?.normal;
        if (stickmanSrc) {
          drawLowTierWalkSprite(ctx, stickmanSrc, renderX, renderY + stickmanYOffset, stickmanDrawSize, stickmanDrawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_STEP);
        }
      } else {
      let legsKey = 'Stickman_Legs';
      let walkerBob = 0;
      let chassisPitch = 0;
      let torsoWalkPitch = 0;
      let torsoWalkX = 0;
      let torsoWalkY = 0;
      let torsoScaleX = 1.0;

      if (isWalking) {
        // 8-phase authentic digitigrade mech walkcycle with pronounced knee bending & dynamic weight compression
        const cycle = ((walkPhase * 0.55 % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 8);
        legsKey = STICKMAN_WALK_FRAMES[phaseIdx] || 'Stickman_Legs_Walk1';
        // Hydraulic chassis bob: deep compression on load frames (1, 5), high-step extension on passing frames (3, 7)
        walkerBob = (phaseIdx === 1 || phaseIdx === 5) ? 1.6 : ((phaseIdx === 3 || phaseIdx === 7) ? -1.0 : 0.0);
        chassisPitch = ((phaseIdx === 0 || phaseIdx === 1) ? 0.025 : ((phaseIdx === 4 || phaseIdx === 5) ? -0.025 : 0.0)) * forwardDir;

        // 🦾 ORGANISCHE OBERKÖRPER- & KOPFBEWEGUNG IN DER WALK-ANIMATION:
        // 1. Stride-Pitch: Weiches Vor-/Zurück-Nicken des Oberkörpers mit dem Schrittzyklus (~2.4 Grad)
        const torsoStridePitch = Math.sin(cycle) * 0.042 * forwardDir;
        // 2. Sekundäre Wirbelsäulen-Kompression: Harmonisches vertikales Einfedern auf jedem Tritt
        const torsoStrideY = Math.cos(cycle * 2) * 1.2;
        // 3. Horizontaler Schritt-Impuls: Subtiler Massentransfer nach vorn/hinten
        const torsoStrideX = Math.sin(cycle) * 0.9 * forwardDir;

        // 4. Lebendige Gefechtsfeld-Erkundung & Kopfdrehung ("vll auch mal ein wenig drehen"):
        // Mehrfrequente Schwingung erzeugt unregelmäßiges, neugieriges Schwenken & Umschauen des Sensorkopfs
        const scanTime = t * 1.65;
        const scanHarmonic = Math.sin(scanTime) * Math.sin(scanTime * 0.47);
        const torsoScanPitch = scanHarmonic * 0.032 * forwardDir; // Leichte Neigung des Schädels (+-1.8 Grad)
        const torsoTurnYaw = scanHarmonic * 0.12; // Kopf- & Rumpf-Drehung / Swivel
        torsoScaleX = 1.0 - Math.abs(torsoTurnYaw) * 0.35; // 2D-Perspektivstauchung beim Eindrehen (~4% Squeeze)

        torsoWalkPitch = torsoStridePitch + torsoScanPitch;
        torsoWalkX = torsoStrideX;
        torsoWalkY = torsoStrideY;
      } else {
        // Idle: Slow towering hydraulic respiration with subtle sensory head swivel
        walkerBob = Math.sin(t * 2.2) * 0.6;
        chassisPitch = Math.sin(t * 1.1) * 0.015 * forwardDir;

        const idleScan = Math.sin(t * 0.9) * Math.sin(t * 0.38);
        torsoWalkPitch = Math.sin(t * 1.1) * 0.02 * forwardDir + idleScan * 0.015 * forwardDir;
        torsoWalkY = Math.sin(t * 2.2) * 0.4;
        torsoScaleX = 1.0 - Math.abs(idleScan) * 0.035;
      }

      // 1. Aiming elevation & Recoil
      let aimPitch = 0;
      if (unit.target && !unit.target.isDead) {
        const isAirTarget = unit.target.isAir || unit.target.elementType === 'Air';
        if (isAirTarget) {
          // Steeper upward tilt for air defense (halved range 9)
          aimPitch = -0.52; // ~30 deg elevation toward sky
        } else {
          const dy = unit.target.y - unit.y;
          const dx = Math.abs(unit.target.x - unit.x);
          aimPitch = Math.max(-0.25, Math.min(0.2, Math.atan2(dy, Math.max(1, dx))));
        }
      }

      // Dual Shot Variations:
      // Shot 1 & 2: Robot deliberately braces beforehand (stems against recoil, knees flex down, torso hunches forward). Recoil is smoothly absorbed.
      // Shot 3: Clumsy unbraced shot! Fails to brace, resulting in violent backward kickback, deep spine arch & desperate balance recovery.
      const currentShotCount = unit.shotCount || 0;
      const nextShotIsClumsy = ((currentShotCount + 1) % 3 === 0);
      const lastShotWasClumsy = (currentShotCount % 3 === 0 && currentShotCount > 0);

      // Pre-fire Bracing Anticipation (0.38s before firing on shots 1 and 2)
      let preFireBrace = 0;
      const preFireDuration = 0.38;
      const isPreparingShot = (!isWalking && unit.target && !unit.target.isDead && unit.attackCooldown > 0 && unit.attackCooldown <= preFireDuration);
      if (isPreparingShot && !nextShotIsClumsy) {
        const bNorm = (preFireDuration - unit.attackCooldown) / preFireDuration; // 0.0 -> 1.0
        preFireBrace = Math.sin(bNorm * Math.PI * 0.5); // Smooth ease-out
      }

      // Firing Recoil Kinematics
      let cannonRecoilX = 0;
      let cannonRecoilPitch = 0;
      let torsoRecoilPitch = 0;
      let torsoRecoilX = 0;
      let torsoRecoilY = 0;
      let chassisRecoilX = 0;
      let chassisRecoilPitch = 0;
      let chassisRecoilY = 0;
      let shotMuzzleFlare = 0;

      const recoilDuration = lastShotWasClumsy ? 0.72 : 0.52;
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - recoilDuration)) {
        const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - recoilDuration)) / recoilDuration; // 1.0 -> 0.0
        const spring = dampedSpringRecoil(recoilFraction);

        if (lastShotWasClumsy) {
          // ── VARIANT B: Clumsy Unbraced Blowout (Every 3rd Shot) ──
          // Failed to brace! Violent kinetic shock snaps the long body backward (~14 deg), sliding back 12px with gyro wobble
          cannonRecoilX = -spring * 24.0 * forwardDir;
          cannonRecoilPitch = -spring * 0.15 * forwardDir;

          torsoRecoilPitch = -spring * 0.24 * forwardDir;
          torsoRecoilX = -spring * 12.0 * forwardDir;
          torsoRecoilY = spring * 3.5;

          chassisRecoilX = -spring * 8.0 * forwardDir;
          chassisRecoilPitch = -spring * 0.10 * forwardDir;
          chassisRecoilY = spring * 2.5;

          // Clumsy gyro balance compensation tremor during rebound
          if (spring < -0.12) {
            torsoRecoilPitch += Math.sin(t * 36) * 0.03 * forwardDir;
          }
        } else {
          // ── VARIANT A: Disciplined Braced Shot (Shots 1 & 2) ──
          // Robot braced into knees and leaned forward before the blast; recoil pushes body back towards neutral cleanly
          cannonRecoilX = -spring * 18.0 * forwardDir;
          cannonRecoilPitch = -spring * 0.09 * forwardDir;

          // Residual forward brace decays smoothly while spring absorbs backward push
          const braceFade = Math.max(0, 1.0 - (1.0 - recoilFraction) * 2.2);
          torsoRecoilPitch = -spring * 0.09 * forwardDir + (braceFade * 0.12 * forwardDir);
          torsoRecoilX = -spring * 5.0 * forwardDir + (braceFade * 3.0 * forwardDir);
          torsoRecoilY = spring * 2.0 + (braceFade * 2.0);

          chassisRecoilX = -spring * 4.0 * forwardDir + (braceFade * 2.0 * forwardDir);
          chassisRecoilPitch = -spring * 0.04 * forwardDir;
          chassisRecoilY = spring * 2.8 + (braceFade * 2.5);
        }

        // Initial muzzle blast flare (first ~0.12s of shot)
        if (recoilFraction > 0.80) {
          shotMuzzleFlare = (recoilFraction - 0.80) / 0.20; // 1.0 -> 0.0
        }
      } else if (preFireBrace > 0) {
        // Pre-fire stance: actively stemming against the upcoming blast!
        // Squats into digitigrade knees, arches torso forward over weapon, locks breech rail forward
        chassisRecoilY = preFireBrace * 3.5;
        chassisRecoilX = forwardDir * (preFireBrace * 2.5);
        chassisRecoilPitch = forwardDir * (preFireBrace * 0.05);

        torsoRecoilPitch = forwardDir * (preFireBrace * 0.15); // ~8.5 degrees forward lean
        torsoRecoilX = forwardDir * (preFireBrace * 4.5);
        torsoRecoilY = preFireBrace * 2.5;

        cannonRecoilX = forwardDir * (preFireBrace * 3.5);
        cannonRecoilPitch = forwardDir * (-preFireBrace * 0.04);
      }

      const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
      const chassisBitmap = buildSpriteBitmap('Stickman_Chassis', renderRace, unit.team);
      const torsoBitmap = buildSpriteBitmap('Stickman_Torso', renderRace, unit.team);
      const cannonBitmap = buildSpriteBitmap('Stickman_Cannon', renderRace, unit.team);

      const legsSrc = isHitFlashing ? legsBitmap?.flash : legsBitmap?.normal;
      const chassisSrc = isHitFlashing ? chassisBitmap?.flash : chassisBitmap?.normal;
      const torsoSrc = isHitFlashing ? torsoBitmap?.flash : torsoBitmap?.normal;
      const cannonSrc = isHitFlashing ? cannonBitmap?.flash : cannonBitmap?.normal;

      ctx.save();
      ctx.translate(renderX, renderY + stickmanYOffset);

      // Layer 1: Long Digitigrade Stilt Legs (braced firmly against the ground)
      if (legsSrc) {
        ctx.drawImage(legsSrc, -stickmanDrawSize / 2, -stickmanDrawSize / 2, stickmanDrawSize, stickmanDrawSize);
      }

      // Layer 2: Chassis & Pelvic Suspension (absorbs recoil torque with knee flex)
      if (chassisSrc) {
        ctx.save();
        ctx.translate(chassisRecoilX, walkerBob + chassisRecoilY);
        ctx.rotate(chassisPitch + chassisRecoilPitch);
        ctx.drawImage(chassisSrc, -stickmanDrawSize / 2, -stickmanDrawSize / 2, stickmanDrawSize, stickmanDrawSize);
        ctx.restore();
      }

      // Layer 3: Upper Torso, Shoulder Carapace & Sensor Head (compensates with long body whip & recovery + lively walk swivel)
      if (torsoSrc) {
        ctx.save();
        ctx.translate(torsoRecoilX + chassisRecoilX + torsoWalkX, walkerBob + torsoRecoilY + chassisRecoilY + torsoWalkY);
        ctx.rotate(chassisPitch + chassisRecoilPitch + torsoRecoilPitch + torsoWalkPitch);

        if (torsoScaleX !== 1.0) {
          // Perspective turn / swivel scale around upper spine axis (x: ~ -4px from sprite center)
          const spinePivotX = forwardDir * stickmanDrawSize * (-4 / 48);
          ctx.translate(spinePivotX, 0);
          ctx.scale(torsoScaleX, 1.0);
          ctx.translate(-spinePivotX, 0);
        }

        ctx.drawImage(torsoSrc, -stickmanDrawSize / 2, -stickmanDrawSize / 2, stickmanDrawSize, stickmanDrawSize);

        // Glowing High-Tech Visor Optic Flare (with firing overload flash)
        if (this.quality !== 'low' && !isHitFlashing) {
          const eyeX = forwardDir * stickmanDrawSize * (3.5 / 48);
          const eyeY = -stickmanDrawSize * (15.5 / 48);
          const pulse = (shotMuzzleFlare > 0.1) 
            ? (1.0 + shotMuzzleFlare * 0.8) 
            : ((preFireBrace > 0)
              ? (0.85 + preFireBrace * 0.4)
              : (0.6 + 0.4 * Math.sin(t * 5.0)));
          const flareAlpha = (shotMuzzleFlare > 0.1) ? 0.95 : (preFireBrace > 0 ? 0.85 : 0.65);
          const flareR = 2.5 * pulse;

          ctx.save();
          ctx.globalAlpha = flareAlpha;
          ctx.fillStyle = (shotMuzzleFlare > 0.3) ? '#ffffff' : ((preFireBrace > 0.5) ? '#e0f2fe' : '#00e5ff');
          ctx.beginPath();
          ctx.arc(eyeX, eyeY, flareR, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
        ctx.restore();
      }

      // Layer 4: Heavy Underslung Cannon (in the viewer's foreground — not covered by shoulder)
      if (cannonSrc) {
        ctx.save();
        // Underslung mount lowered by ~2.2 grid units (~4.5px) so the shoulder dome and team badge remain prominently visible
        const cannonYDrop = stickmanDrawSize * (2.2 / 48);
        // Breech mounting pivot at (-8/48, -3/48) relative to center
        const cannonPivotX = forwardDir * (-stickmanDrawSize * (8 / 48)) + chassisRecoilX;
        const cannonPivotY = -stickmanDrawSize * (3 / 48) + walkerBob + chassisRecoilY + cannonYDrop;
        ctx.translate(cannonPivotX, cannonPivotY);
        ctx.rotate((aimPitch + chassisPitch + cannonRecoilPitch) * forwardDir);
        ctx.translate(-cannonPivotX, -cannonPivotY);
        ctx.drawImage(cannonSrc, -stickmanDrawSize / 2 + cannonRecoilX + chassisRecoilX, -stickmanDrawSize / 2 + walkerBob + chassisRecoilY + cannonYDrop, stickmanDrawSize, stickmanDrawSize);

        // 💥 High-Visibility Railgun Muzzle Blast & Lateral Vent Jets
        if (this.quality !== 'low' && shotMuzzleFlare > 0.05) {
          ctx.save();
          // In 48x48 cannon grid, needle tip is at x: 46, y: 20 -> relative to center: dx = +22/48, dy = -4/48
          const mX = forwardDir * stickmanDrawSize * (22 / 48) + cannonRecoilX + chassisRecoilX;
          const mY = -stickmanDrawSize * (4 / 48) + walkerBob + chassisRecoilY + cannonYDrop;

          // Piercing core plasma diamond / cone
          const coneLen = stickmanDrawSize * 0.45 * shotMuzzleFlare;
          const coneH = stickmanDrawSize * 0.16 * shotMuzzleFlare;

          // Cyan plasma aura
          ctx.fillStyle = '#00e5ff';
          ctx.globalAlpha = 0.85 * shotMuzzleFlare;
          ctx.beginPath();
          ctx.moveTo(mX, mY);
          ctx.lineTo(mX + forwardDir * coneLen, mY - coneH * 0.5);
          ctx.lineTo(mX + forwardDir * (coneLen * 1.25), mY);
          ctx.lineTo(mX + forwardDir * coneLen, mY + coneH * 0.5);
          ctx.closePath();
          ctx.fill();

          // Incandescent white inner laser core
          ctx.fillStyle = '#ffffff';
          ctx.globalAlpha = 0.95 * shotMuzzleFlare;
          ctx.beginPath();
          ctx.moveTo(mX, mY);
          ctx.lineTo(mX + forwardDir * (coneLen * 0.65), mY - coneH * 0.22);
          ctx.lineTo(mX + forwardDir * (coneLen * 0.85), mY);
          ctx.lineTo(mX + forwardDir * (coneLen * 0.65), mY + coneH * 0.22);
          ctx.closePath();
          ctx.fill();

          // High-pressure lateral muzzle brake vents (top & bottom 45 deg gas discharge)
          ctx.strokeStyle = '#e0f2fe';
          ctx.globalAlpha = 0.90 * shotMuzzleFlare;
          ctx.lineWidth = 2.0 * shotMuzzleFlare;
          const ventLen = stickmanDrawSize * 0.18 * shotMuzzleFlare;
          ctx.beginPath();
          ctx.moveTo(mX, mY);
          ctx.lineTo(mX - forwardDir * (ventLen * 0.6), mY - ventLen);
          ctx.moveTo(mX, mY);
          ctx.lineTo(mX - forwardDir * (ventLen * 0.6), mY + ventLen);
          ctx.stroke();

          // Anamorphic horizontal glare streak
          ctx.strokeStyle = '#38bdf8';
          ctx.globalAlpha = 0.75 * shotMuzzleFlare;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(mX - forwardDir * 15 * shotMuzzleFlare, mY);
          ctx.lineTo(mX + forwardDir * 35 * shotMuzzleFlare, mY);
          ctx.stroke();

          ctx.restore();
        }

        ctx.restore();
      }

      ctx.restore();
      }
    } else if (isMarine) {
      // 🎖️ MULTI-PART MARINE WALKING & COMBAT ANIMATION: Beine, Torso & C-14 Gauss Rifle
      const isGold = unit.cardData?.spriteRecolor === 'gold';
      let legsKey = isGold ? 'MarineGold_Legs' : 'Marine_Legs';
      let legBob = 0;
      let torsoBob = 0;
      let torsoLean = 0;

      if (isWalking) {
        // 6-phase high-fidelity infantry walkcycle (Paradigma 1: Zero-Allocation)
        const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6);
        const walkFrames = isGold ? MARINE_GOLD_WALK_FRAMES : MARINE_WALK_FRAMES;
        legsKey = walkFrames[phaseIdx] || (isGold ? 'MarineGold_Legs_Walk1' : 'Marine_Legs_Walk1');
        legBob = (phaseIdx === 1 || phaseIdx === 4) ? 0.7 : 0;
        torsoBob = Math.sin(walkPhase * 2) * 0.6;
        torsoLean = (walkStride * 0.04);
      }

      // Kampagnen- / Spell-exklusive Reskins (z.B. "Black Marine" oder Fodders Gold-Marines)
      // setzen nur dieses kosmetische Feld - unitType bleibt 'Marine', damit Sound/KI/Kampf-
      // Sonderfälle anderswo im Code (unit.js, ai.js) unverändert greifen.
      const torsoKey = isGold ? 'MarineGold_Torso' : (unit.cardData?.spriteRecolor === 'black' ? 'MarineBlack_Torso' : 'Marine_Torso');
      const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
      const torsoBitmap = buildSpriteBitmap(torsoKey, renderRace, unit.team);
      const gunBitmap = buildSpriteBitmap('Marine_Gun', renderRace, unit.team);

      const legsSrc = isHitFlashing ? legsBitmap.flash : legsBitmap.normal;
      const torsoSrc = isHitFlashing ? torsoBitmap.flash : torsoBitmap.normal;
      const gunSrc = isHitFlashing ? gunBitmap.flash : gunBitmap.normal;

      // 1. LEGS (Unterteil)
      ctx.drawImage(
        legsSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 - legBob,
        drawSize,
        drawSize
      );

      // 2. TORSO (Mittelteil): Walking breathing bob & upper body forward lean
      ctx.save();
      const torsoPivotX = renderX;
      const torsoPivotY = renderY + drawSize * 0.1;
      ctx.translate(torsoPivotX, torsoPivotY);
      ctx.rotate(torsoLean);
      ctx.translate(-torsoPivotX, -torsoPivotY);

      ctx.drawImage(
        torsoSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 - legBob + torsoBob,
        drawSize,
        drawSize
      );
      ctx.restore();

      // 3. C-14 GAUSS RIFLE & ARMS: Automatic weapon recoil with Damped Spring & target height aiming
      let gunRecoilX = 0;
      let gunKickAngle = 0;
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.14)) {
        const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - 0.14)) / 0.14; // 1.0 -> 0.0
        const spring = dampedSpringRecoil(recoilFraction);
        gunRecoilX = -spring * 3.6; // Rapid automatic rifle recoil kickback
        gunKickAngle = -spring * 0.08; // Gun muzzle kicks upward
      }

      // Vertical aiming towards target
      let gunAimY = 0;
      if (unit.target && !unit.target.isDead) {
        const dTargetY = unit.target.y - unit.y;
        gunAimY = Math.max(-3.5, Math.min(3.5, dTargetY * 0.035));
      }

      ctx.save();
      const gunPivotX = renderX + drawSize * 0.05;
      const gunPivotY = renderY - legBob + torsoBob;
      ctx.translate(gunPivotX, gunPivotY);
      ctx.rotate(gunKickAngle);
      ctx.translate(-gunPivotX, -gunPivotY);

      ctx.drawImage(
        gunSrc,
        renderX - drawSize / 2 + gunRecoilX,
        renderY - drawSize / 2 - legBob + torsoBob + gunAimY,
        drawSize,
        drawSize
      );
      ctx.restore();
    } else if (isHyperMarine) {
      // 🔥 MULTI-PART HYPER MARINE WALKING & COMBAT ANIMATION: identical rig to
      // the Marine above (see checkDrilling() in js/engine/cardSystem.js for
      // how this unit comes to exist) - only the sprite keys differ, pointing
      // at the molten crimson/ember recolor instead of Marine's blue.
      let legsKey = 'HyperMarine_Legs';
      let legBob = 0;
      let torsoBob = 0;
      let torsoLean = 0;

      if (isWalking) {
        // 6-phase high-fidelity infantry walkcycle (Paradigma 1: Zero-Allocation)
        const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6);
        legsKey = HYPER_MARINE_WALK_FRAMES[phaseIdx] || 'HyperMarine_Legs_Walk1';
        legBob = (phaseIdx === 1 || phaseIdx === 4) ? 0.7 : 0;
        torsoBob = Math.sin(walkPhase * 2) * 0.6;
        torsoLean = (walkStride * 0.04);
      }

      const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
      const torsoBitmap = buildSpriteBitmap('HyperMarine_Torso', renderRace, unit.team);
      const gunBitmap = buildSpriteBitmap('HyperMarine_Gun', renderRace, unit.team);

      const legsSrc = isHitFlashing ? legsBitmap.flash : legsBitmap.normal;
      const torsoSrc = isHitFlashing ? torsoBitmap.flash : torsoBitmap.normal;
      const gunSrc = isHitFlashing ? gunBitmap.flash : gunBitmap.normal;

      // 1. LEGS (Unterteil)
      ctx.drawImage(
        legsSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 - legBob,
        drawSize,
        drawSize
      );

      // 2. TORSO (Mittelteil): Walking breathing bob & upper body forward lean
      ctx.save();
      const torsoPivotX = renderX;
      const torsoPivotY = renderY + drawSize * 0.1;
      ctx.translate(torsoPivotX, torsoPivotY);
      ctx.rotate(torsoLean);
      ctx.translate(-torsoPivotX, -torsoPivotY);

      ctx.drawImage(
        torsoSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 - legBob + torsoBob,
        drawSize,
        drawSize
      );
      ctx.restore();

      // 3. C-14 GAUSS RIFLE & ARMS: Automatic weapon recoil with Damped Spring & target height aiming
      let gunRecoilX = 0;
      let gunKickAngle = 0;
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.14)) {
        const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - 0.14)) / 0.14; // 1.0 -> 0.0
        const spring = dampedSpringRecoil(recoilFraction);
        gunRecoilX = -spring * 3.6; // Rapid automatic rifle recoil kickback
        gunKickAngle = -spring * 0.08; // Gun muzzle kicks upward
      }

      // Vertical aiming towards target
      let gunAimY = 0;
      if (unit.target && !unit.target.isDead) {
        const dTargetY = unit.target.y - unit.y;
        gunAimY = Math.max(-3.5, Math.min(3.5, dTargetY * 0.035));
      }

      ctx.save();
      const gunPivotX = renderX + drawSize * 0.05;
      const gunPivotY = renderY - legBob + torsoBob;
      ctx.translate(gunPivotX, gunPivotY);
      ctx.rotate(gunKickAngle);
      ctx.translate(-gunPivotX, -gunPivotY);

      ctx.drawImage(
        gunSrc,
        renderX - drawSize / 2 + gunRecoilX,
        renderY - drawSize / 2 - legBob + torsoBob + gunAimY,
        drawSize,
        drawSize
      );
      ctx.restore();
    } else if (isDevilPatrol) {
      if (this.quality === 'low' || isSimpleTier) {
        let devilKey = 'DevilPatrol';
        if (isWalking) {
          const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6);
          devilKey = DEVIL_PATROL_WALK_COMPOSITES[phaseIdx] || 'DevilPatrol_Walk1';
        }
        const devilBmp = buildSpriteBitmap(devilKey, renderRace, unit.team);
        const devilSrc = isHitFlashing ? devilBmp?.flash : devilBmp?.normal;
        if (devilSrc) {
          drawLowTierWalkSprite(ctx, devilSrc, renderX, renderY, drawSize, drawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_STEP);
        }
      } else {
      // 🔥 MULTI-PART DEVIL PATROL RIG: Heavy heat-shielded flamethrower infantry
      let legsKey = 'DevilPatrol_Legs';
      let legBob = 0;
      let torsoBob = 0;
      let torsoLean = 0;

      if (isWalking) {
        // 6-phase high-fidelity infantry walkcycle (Paradigma 1: Zero-Allocation)
        const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6);
        legsKey = DEVIL_PATROL_WALK_FRAMES[phaseIdx] || 'DevilPatrol_Legs_Walk1';
        legBob = (phaseIdx === 1 || phaseIdx === 4) ? 0.7 : 0;
        torsoBob = Math.sin(walkPhase * 2) * 0.6;
        torsoLean = (walkStride * 0.04);
      }

      const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
      const torsoBitmap = buildSpriteBitmap('DevilPatrol_Torso', renderRace, unit.team);
      const gunBitmap = buildSpriteBitmap('DevilPatrol_Gun', renderRace, unit.team);

      const legsSrc = isHitFlashing ? legsBitmap.flash : legsBitmap.normal;
      const torsoSrc = isHitFlashing ? torsoBitmap.flash : torsoBitmap.normal;
      const gunSrc = isHitFlashing ? gunBitmap.flash : gunBitmap.normal;

      // 1. LEGS (Unterteil)
      ctx.drawImage(
        legsSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 - legBob,
        drawSize,
        drawSize
      );

      // 2. TORSO (Mittelteil): Walking breathing bob & upper body forward lean
      ctx.save();
      const torsoPivotX = renderX;
      const torsoPivotY = renderY + drawSize * 0.1;
      ctx.translate(torsoPivotX, torsoPivotY);
      ctx.rotate(torsoLean);
      ctx.translate(-torsoPivotX, -torsoPivotY);

      ctx.drawImage(
        torsoSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 - legBob + torsoBob,
        drawSize,
        drawSize
      );
      ctx.restore();

      // 3. FLAMETHROWER & ARMS: Continuous flamethrower recoil shake & target height aiming
      let gunRecoilX = 0;
      let gunKickAngle = 0;
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.20)) {
        const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - 0.20)) / 0.20;
        const spring = dampedSpringRecoil(recoilFraction);
        gunRecoilX = -spring * 3.0; // Sustained flame stream pressure recoil
        gunKickAngle = -spring * 0.06;
      }

      let gunAimY = 0;
      if (unit.target && !unit.target.isDead) {
        const dTargetY = unit.target.y - unit.y;
        gunAimY = Math.max(-3.5, Math.min(3.5, dTargetY * 0.035));
      }

      ctx.save();
      const gunPivotX = renderX + drawSize * 0.05;
      const gunPivotY = renderY - legBob + torsoBob;
      ctx.translate(gunPivotX, gunPivotY);
      ctx.rotate(gunKickAngle);
      ctx.translate(-gunPivotX, -gunPivotY);

      ctx.drawImage(
        gunSrc,
        renderX - drawSize / 2 + gunRecoilX,
        renderY - drawSize / 2 - legBob + torsoBob + gunAimY,
        drawSize,
        drawSize
      );
      ctx.restore();
      }
    } else if (isHyperDevilPatrol) {
      if (this.quality === 'low' || isSimpleTier) {
        const isWestern = (unit.team === 'western');
        const dir = isWestern ? 1 : -1;
        const age = unit.age || 0;
        const wobble = unit.wobbleOffset || 0;
        const hoverBob = Math.sin(age * 4.0 + wobble) * 2.0;

        const hyperBmp = buildSpriteBitmap('HyperDevilPatrol', renderRace, unit.team);
        const hyperSrc = isHitFlashing ? hyperBmp?.flash : hyperBmp?.normal;
        if (hyperSrc) {
          ctx.drawImage(hyperSrc, renderX - drawSize / 2, renderY + hoverBob - drawSize / 2, drawSize, drawSize);
        }

        // 🎗️ Dynamic Cartridge Ammo Belts on Low Tier (pre-baked fast path: 1 stroke + simple 3x3 brass rects, 0 matrix transforms)
        const flightFactor = isWalking ? 1.0 : 0.0;
        const b1AnchorX = renderX - dir * (drawSize * 0.16);
        const b1AnchorY = renderY - drawSize * 0.04 + hoverBob;
        drawHyperDevilPatrolAmmoBelt(ctx, b1AnchorX, b1AnchorY, dir, drawSize, age, wobble, flightFactor, 0.0, this.quality);

        const b2AnchorX = renderX + dir * (drawSize * 0.02);
        const b2AnchorY = renderY + drawSize * 0.08 + hoverBob;
        drawHyperDevilPatrolAmmoBelt(ctx, b2AnchorX, b2AnchorY, dir, drawSize, age, wobble, flightFactor, 2.1, this.quality);
      } else {
      // 🚀 MULTI-PART HYPER DEVIL PATROL RIG: Airborne jetpack infantry with rotary grenade launcher
      const isWestern = (unit.team === 'western');
      const dir = isWestern ? 1 : -1;
      const age = unit.age || 0;
      const wobble = unit.wobbleOffset || 0;

      // 1. Aerodynamic Jetpack Flight Kinematics
      const hoverBob = Math.sin(age * 4.0 + wobble) * 2.0;
      const flightLean = (isWalking ? 0.18 : 0.04);
      const legTrailLean = (isWalking ? 0.24 : 0.08);

      const legsBitmap = buildSpriteBitmap('HyperDevilPatrol_Legs', renderRace, unit.team);
      const torsoBitmap = buildSpriteBitmap('HyperDevilPatrol_Torso', renderRace, unit.team);
      const gunBitmap = buildSpriteBitmap('HyperDevilPatrol_Gun', renderRace, unit.team);

      const legsSrc = isHitFlashing ? legsBitmap.flash : legsBitmap.normal;
      const torsoSrc = isHitFlashing ? torsoBitmap.flash : torsoBitmap.normal;
      const gunSrc = isHitFlashing ? gunBitmap.flash : gunBitmap.normal;

      // 2. Animated Jetpack Thruster Flame (-X rear exhaust)
      if (this.quality !== 'low') {
        const flamePulse = 4.0 + Math.sin(age * 18.0) * 2.5;
        const nozzleX = renderX - dir * (drawSize * 0.28);
        const nozzle1Y = renderY - drawSize * 0.12 + hoverBob;
        const nozzle2Y = renderY + drawSize * 0.06 + hoverBob;

        ctx.fillStyle = '#38bdf8';
        ctx.fillRect(nozzleX - dir * (flamePulse * 0.6), nozzle1Y - 1, -dir * (flamePulse * 0.6), 2);
        ctx.fillRect(nozzleX - dir * (flamePulse * 0.6), nozzle2Y - 1, -dir * (flamePulse * 0.6), 2);
        ctx.fillStyle = '#f97316';
        ctx.fillRect(nozzleX - dir * flamePulse, nozzle1Y - 1.5, -dir * (flamePulse * 0.4), 3);
        ctx.fillRect(nozzleX - dir * flamePulse, nozzle2Y - 1.5, -dir * (flamePulse * 0.4), 3);
      }

      // Smooth inertia flight factor: 1.0 = trailing in wind, 0.0 = hanging downwards under gravity
      if (unit._beltFlightFactor === undefined) unit._beltFlightFactor = isWalking ? 1.0 : 0.0;
      const targetFlight = isWalking ? 1.0 : 0.0;
      unit._beltFlightFactor += (targetFlight - unit._beltFlightFactor) * 0.14;
      const flightFactor = Math.max(0, Math.min(1, unit._beltFlightFactor));

      // 3. Ammo Belt 1 (Rear Jetpack Hopper): Trailing in wind during flight, hanging down when stationary
      const b1AnchorX = renderX - dir * (drawSize * 0.16);
      const b1AnchorY = renderY - drawSize * 0.04 + hoverBob;
      drawHyperDevilPatrolAmmoBelt(ctx, b1AnchorX, b1AnchorY, dir, drawSize, age, wobble, flightFactor, 0.0, this.quality);

      // 4. Trailing Flight Legs (Unterteil)
      ctx.save();
      const legPivotX = renderX;
      const legPivotY = renderY + drawSize * 0.15 + hoverBob;
      ctx.translate(legPivotX, legPivotY);
      ctx.rotate(legTrailLean);
      ctx.translate(-legPivotX, -legPivotY);
      ctx.drawImage(
        legsSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 + hoverBob,
        drawSize,
        drawSize
      );
      ctx.restore();

      // 5. Armored Bronze Torso & Jetpack (Mittelteil)
      ctx.save();
      const torsoPivotX = renderX;
      const torsoPivotY = renderY + drawSize * 0.1 + hoverBob;
      ctx.translate(torsoPivotX, torsoPivotY);
      ctx.rotate(flightLean);
      ctx.translate(-torsoPivotX, -torsoPivotY);
      ctx.drawImage(
        torsoSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 + hoverBob,
        drawSize,
        drawSize
      );
      ctx.restore();

      // 6. Ammo Belt 2 (Front Drum Feeder): Loops from chest harness to rotary drum
      const b2AnchorX = renderX + dir * (drawSize * 0.02);
      const b2AnchorY = renderY + drawSize * 0.08 + hoverBob;
      drawHyperDevilPatrolAmmoBelt(ctx, b2AnchorX, b2AnchorY, dir, drawSize, age, wobble, flightFactor, 2.1, this.quality);

      // 5. Heavy Rotary Grenade Launcher (Waffe & Rückstoß)
      let gunRecoilX = 0;
      let gunKickAngle = 0;
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.22)) {
        const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - 0.22)) / 0.22;
        const spring = dampedSpringRecoil(recoilFraction);
        gunRecoilX = -spring * 3.5;
        gunKickAngle = -spring * 0.12;
      }

      let gunAimY = 0;
      if (unit.target && !unit.target.isDead) {
        const targetDy = unit.target.y - unit.y;
        gunAimY = Math.max(-4, Math.min(6, targetDy * 0.05));
      }

      ctx.save();
      const gunPivotX = renderX + dir * (drawSize * 0.1);
      const gunPivotY = renderY + drawSize * 0.05 + hoverBob;
      ctx.translate(gunPivotX, gunPivotY);
      ctx.rotate(gunKickAngle);
      ctx.translate(-gunPivotX, -gunPivotY);
      ctx.drawImage(
        gunSrc,
        renderX - drawSize / 2 + gunRecoilX,
        renderY - drawSize / 2 + hoverBob + gunAimY,
        drawSize,
        drawSize
      );
      ctx.restore();
      }
    } else if (isHotHand) {
      if (this.quality === 'low' || isSimpleTier) {
        const isGold = unit.cardData?.spriteRecolor === 'gold' || unit.edition === 'gold';
        let hhKey = isGold ? 'HotHandGold' : 'HotHand';
        if (isWalking) {
          const sprintRate = 0.85;
          const strideCycle = (((walkPhase * sprintRate) % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((strideCycle / (Math.PI * 2)) * 6) % 6;
          hhKey = (isGold ? HOTHAND_GOLD_SPRINT_COMPOSITES : HOTHAND_SPRINT_COMPOSITES)[phaseIdx] || (isGold ? 'HotHandGold_Sprint1' : 'HotHand_Sprint1');
        }
        const hhBmp = buildSpriteBitmap(hhKey, renderRace, unit.team);
        const hhSrc = isHitFlashing ? hhBmp?.flash : hhBmp?.normal;
        if (hhSrc) {
          drawLowTierWalkSprite(ctx, hhSrc, renderX, renderY, drawSize, drawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_STEP);
        }
      } else {
        // 🔥 MULTI-PART HOT HAND RIG: Nimble cybernetic martial artist
        // Features:
        // - 6-phase sprint cycle with forward-lunging athletic posture and flowing hair
        // - Reactive dynamic dodges before first hit:
        //     * Acrobatic 360° dodge roll with low arc and trailing hair whip
        //     * Deep knee ground-slide with friction sparks
        // - Target-reactive dynamic melee punches (80% size/type tailored, 20% random):
        //     * Straight Heat Jab (Infantry / Tier S)
        //     * Rising Molten Uppercut (Tanks / Walkers / Tier M)
        //     * Overhead Seismic Slam (Structures / Colossi / Tier L)
        // - Cybernetic arm localized fusion heat bloom and trailing embers
        const isGold = unit.cardData?.spriteRecolor === 'gold' || unit.edition === 'gold';
        const prefix = isGold ? 'HotHandGold_' : 'HotHand_';

        const isDodging = (unit._dodgeAnimTime > 0) && !unit.hasDealtFirstHit;
        const dodgeStyle = unit._dodgeStyle || 'roll';
        const isPunching = (unit._punchAnimTime > 0) || (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.28));
        const punchStyle = unit._punchStyle || 'jab';

        let legsKey = prefix + 'Legs';
        let hairKey = prefix + 'Hair';
        let armKey = prefix + 'Arm_Guard';
        let rearArmKey = prefix + 'Arm_Rear';
        let torsoKey = prefix + 'Torso';

        let legBob = 0;
        let torsoBob = 0;
        let torsoLean = 0;
        let armAngle = 0;
        let rearArmAngle = 0;
        let hairAngle = 0;
        let hairOffsetY = 0;
        let rollAngle = 0;
        let rollYOffset = 0;
        let slideYOffset = 0;

        if (isDodging) {
          if (dodgeStyle === 'slide') {
            const slideProgress = Math.max(0, Math.min(1, 1 - (unit._dodgeAnimTime / 0.40)));
            legsKey = prefix + 'Legs_Slide';
            hairKey = prefix + 'Hair_Sprint1';
            hairAngle = -0.15 * dir;
            hairOffsetY = -1.0;
            armKey = prefix + 'Arm_Guard';
            rearArmAngle = 0.30;
            slideYOffset = 3.5;
            torsoLean = -0.18; // Lean back during power slide
            torsoBob = 1.0;

            // Ground friction spark trail
            if (this.quality !== 'low') {
              ctx.save();
              ctx.fillStyle = '#f59e0b';
              const sparkPhase = (slideProgress * 12) % 4;
              ctx.fillRect(renderX - dir * (drawSize * 0.3 + sparkPhase * 2), renderY + drawSize * 0.42, 2, 2);
              ctx.fillStyle = '#fbbf24';
              ctx.fillRect(renderX - dir * (drawSize * 0.2 + sparkPhase * 1.5), renderY + drawSize * 0.44, 2, 2);
              ctx.restore();
            }
          } else {
            // Dodge Roll: 360 degree tumble
            const rollProgress = Math.max(0, Math.min(1, 1 - (unit._dodgeAnimTime / 0.45)));
            const rollFrameIdx = Math.min(3, Math.floor(rollProgress * 4));
            legsKey = (isGold ? HOTHAND_GOLD_ROLL_FRAMES : HOTHAND_ROLL_FRAMES)[rollFrameIdx] || (prefix + 'Legs_Roll1');
            hairKey = prefix + 'Hair_Whip';
            hairAngle = -0.4 * Math.sin(rollProgress * Math.PI * 2);
            armKey = prefix + 'Arm_Guard';
            rearArmAngle = 0.2;
            rollAngle = rollProgress * Math.PI * 2 * dir;
            rollYOffset = -Math.sin(rollProgress * Math.PI) * 4.0;
          }
        } else if (isPunching) {
          // Dynamic punch choreography with intense kinetic follow-through & hair whipping
          const punchDuration = 0.28;
          const punchElapsed = (unit._punchAnimTime > 0) ? Math.max(0, punchDuration - unit._punchAnimTime) : 0.14;
          const pProgress = Math.max(0, Math.min(1, punchElapsed / punchDuration));

          if (punchStyle === 'uppercut') {
            armKey = prefix + 'Arm_Uppercut';
            torsoBob = -2.5;
            rearArmAngle = 0.40;
            if (pProgress < 0.45) {
              // Rising surge: wind pulls hair down & back
              torsoLean = -0.08;
              hairKey = prefix + 'Hair_Sprint2';
              hairAngle = 0.35 * dir;
              hairOffsetY = 1.8;
            } else {
              // Apex: hair flings UPWARD in an explosive fiery crown as fist reaches sky
              torsoLean = -0.15;
              const apex = Math.sin((pProgress - 0.45) / 0.55 * Math.PI);
              hairKey = prefix + 'Hair_Whip';
              hairAngle = -0.50 * apex * dir;
              hairOffsetY = -apex * 3.5;
            }
          } else if (punchStyle === 'slam') {
            armKey = prefix + 'Arm_Slam';
            rearArmAngle = -0.35;
            if (pProgress < 0.42) {
              // Wind-up leap: body leaps high, hair arches up in the air
              torsoLean = -0.18;
              torsoBob = -3.2;
              const windup = Math.sin(pProgress / 0.42 * Math.PI);
              hairKey = prefix + 'Hair_Whip';
              hairAngle = -0.55 * windup * dir;
              hairOffsetY = -windup * 3.8;
            } else {
              // Hammer crash: torso slams down, hair whips downward with the shockwave
              torsoLean = 0.26;
              torsoBob = 2.8;
              const slam = Math.sin((pProgress - 0.42) / 0.58 * Math.PI);
              hairKey = prefix + 'Hair_Sprint1';
              hairAngle = 0.48 * slam * dir;
              hairOffsetY = slam * 2.8;
            }
          } else {
            // 'jab': Straight snappy heat punch
            armKey = prefix + 'Arm_Jab';
            rearArmAngle = 0.25;
            if (pProgress < 0.35) {
              // Forward lunge: sudden acceleration draws hair backward
              torsoLean = 0.20;
              torsoBob = 0.5;
              hairKey = prefix + 'Hair_Sprint1';
              hairAngle = -0.40 * dir;
              hairOffsetY = -1.2;
            } else if (pProgress < 0.75) {
              // Impact shock: torso halts hard, hair whips forward over shoulder
              torsoLean = 0.16;
              torsoBob = 0;
              const whip = Math.sin((pProgress - 0.35) / 0.40 * Math.PI);
              hairKey = prefix + 'Hair_Whip';
              hairAngle = 0.35 * whip * dir;
              hairOffsetY = -whip * 2.5;
            } else {
              // Settle back to athletic guard stance
              torsoLean = 0.10;
              torsoBob = 0;
              hairKey = prefix + 'Hair';
              hairAngle = 0;
            }
          }
        } else if (isWalking) {
          // ── ATHLETIC SPRINTER CYCLE ───────────────────────────
          // Cadence: ~2.0 full stride cycles / sec (~4.0 powerful sprint steps / sec) matching Hot Hand's speed (75 px/s)
          const sprintRate = 0.85;
          const strideCycle = (((walkPhase * sprintRate) % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((strideCycle / (Math.PI * 2)) * 6) % 6;
          legsKey = (isGold ? HOTHAND_GOLD_SPRINT_FRAMES : HOTHAND_SPRINT_FRAMES)[phaseIdx] || (prefix + 'Legs_Sprint1');

          // Smooth harmonic bounce: compression on ground contact, lift in airborne flight
          const bounce = Math.sin(strideCycle * 2);
          legBob = -bounce * 1.4;
          torsoBob = bounce * 0.5;
          torsoLean = 0.22 + Math.cos(strideCycle * 2) * 0.03; // Athletic forward-lunging sprinter lean with dynamic surge

          // Active arm pumping in opposition to legs:
          // Front cyber-arm drives forward and up when armSwing > 0, pulls back when armSwing < 0
          // Rear arm pumps in exact counter-balance!
          const armSwing = Math.sin(strideCycle);
          armAngle = armSwing * 0.52;
          rearArmAngle = -armSwing * 0.52;

          // Hair streaming & rippling in slipstream:
          // Follow-through wave with aerodynamic lag behind torso bob + high-speed flutter
          const hairWave = Math.sin(strideCycle * 2 - 0.7);
          const windFlutter = Math.sin(t * 18.0) * 0.05;
          hairAngle = (hairWave * 0.22 + windFlutter) * -dir;
          hairOffsetY = hairWave * 1.4;
          hairKey = (phaseIdx % 2 === 0) ? (prefix + 'Hair_Sprint1') : (prefix + 'Hair_Sprint2');
        } else {
          // Idle breathing
          torsoBob = Math.sin(t * 3.2 + unit.wobbleOffset) * 0.4;
          hairAngle = Math.sin(t * 2.4 + unit.wobbleOffset) * 0.04 * -dir;
        }

        const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
        const torsoBitmap = buildSpriteBitmap(torsoKey, renderRace, unit.team);
        const hairBitmap = buildSpriteBitmap(hairKey, renderRace, unit.team);
        const armBitmap = buildSpriteBitmap(armKey, renderRace, unit.team);
        const rearArmBitmap = buildSpriteBitmap(rearArmKey, renderRace, unit.team);

        const legsSrc = isHitFlashing ? legsBitmap?.flash : legsBitmap?.normal;
        const torsoSrc = isHitFlashing ? torsoBitmap?.flash : torsoBitmap?.normal;
        const hairSrc = isHitFlashing ? hairBitmap?.flash : hairBitmap?.normal;
        const armSrc = isHitFlashing ? armBitmap?.flash : armBitmap?.normal;
        const rearArmSrc = isHitFlashing ? rearArmBitmap?.flash : rearArmBitmap?.normal;

        const posX = renderX;
        const posY = renderY + rollYOffset + slideYOffset;

        ctx.save();
        if (rollAngle !== 0) {
          ctx.translate(posX, posY);
          ctx.rotate(rollAngle);
          ctx.translate(-posX, -posY);
        }

        // 1. REAR ARM (Tactical runner/guard arm drawn behind torso)
        if (rearArmSrc) {
          ctx.save();
          const rearShoulderX = posX - dir * (drawSize * 0.06);
          const rearShoulderY = posY - drawSize * 0.16 - legBob + torsoBob;
          ctx.translate(rearShoulderX, rearShoulderY);
          ctx.rotate(rearArmAngle);
          ctx.translate(-rearShoulderX, -rearShoulderY);
          ctx.drawImage(
            rearArmSrc,
            posX - drawSize / 2,
            posY - drawSize / 2 - legBob + torsoBob,
            drawSize,
            drawSize
          );
          ctx.restore();
        }

        // 2. LEGS (Elongated athletic sprinter strides)
        if (legsSrc) {
          ctx.drawImage(
            legsSrc,
            posX - drawSize / 2,
            posY - drawSize / 2 - legBob,
            drawSize,
            drawSize
          );
        }

        // 3. TORSO & LEAN
        ctx.save();
        const torsoPivotX = posX;
        const torsoPivotY = posY + drawSize * 0.1;
        ctx.translate(torsoPivotX, torsoPivotY);
        ctx.rotate(torsoLean);
        ctx.translate(-torsoPivotX, -torsoPivotY);

        if (torsoSrc) {
          ctx.drawImage(
            torsoSrc,
            posX - drawSize / 2,
            posY - drawSize / 2 - legBob + torsoBob,
            drawSize,
            drawSize
          );
        }

        // 4. HAIR (Dynamic flowing & whipping ponytail, pinned to helmet hair-tie at col 8.5, row 4)
        if (hairSrc) {
          ctx.save();
          const hairPivotX = posX - dir * (drawSize * (3.5 / 24));
          const hairPivotY = (posY - legBob + torsoBob) - drawSize * (8.0 / 24);
          ctx.translate(hairPivotX, hairPivotY);
          ctx.rotate(hairAngle);
          ctx.translate(-hairPivotX, -hairPivotY);
          ctx.drawImage(
            hairSrc,
            posX - drawSize / 2,
            posY - drawSize / 2 - legBob + torsoBob,
            drawSize,
            drawSize
          );
          ctx.restore();
        }

        // 5. FOREGROUND CYBERNETIC ARM & PUNCH (With dynamic arm swing)
        if (armSrc) {
          ctx.save();
          const frontShoulderX = posX + dir * (drawSize * 0.04);
          const frontShoulderY = posY - drawSize * 0.16 - legBob + torsoBob;
          ctx.translate(frontShoulderX, frontShoulderY);
          ctx.rotate(armAngle);
          ctx.translate(-frontShoulderX, -frontShoulderY);
          ctx.drawImage(
            armSrc,
            posX - drawSize / 2,
            posY - drawSize / 2 - legBob + torsoBob,
            drawSize,
            drawSize
          );

          // 6. MOLTEN HEAT BLOOM ON FUSION FIST (Inside front arm transform so glow follows arm swing!)
          if (this.quality !== 'low') {
            const fistLocalX = (punchStyle === 'jab' && isPunching) ? 0.38 : ((punchStyle === 'uppercut' && isPunching) ? 0.28 : ((punchStyle === 'slam' && isPunching) ? 0.32 : 0.18));
            const fistLocalY = (punchStyle === 'uppercut' && isPunching) ? -0.25 : ((punchStyle === 'slam' && isPunching) ? 0.22 : 0.05);
            const fistWorldX = posX + dir * (drawSize * fistLocalX);
            const fistWorldY = posY + (drawSize * fistLocalY) - legBob + torsoBob;

            const heatPulse = 0.65 + 0.35 * Math.sin(t * 8);
            const glowRadius = isPunching ? (drawSize * 0.45) : (drawSize * 0.22 * heatPulse);
            if (isPunching) {
              if (!_cachedHotHandPunchGrad) {
                // Quality-gated via outer this.quality !== 'low'
                _cachedHotHandPunchGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
                _cachedHotHandPunchGrad.addColorStop(0, 'rgba(255, 235, 120, 0.85)');
                _cachedHotHandPunchGrad.addColorStop(0.35, 'rgba(249, 115, 22, 0.55)');
                _cachedHotHandPunchGrad.addColorStop(0.75, 'rgba(239, 68, 68, 0.25)');
                _cachedHotHandPunchGrad.addColorStop(1, 'rgba(239, 68, 68, 0)');
              }
            } else {
              if (!_cachedHotHandIdleGrad) {
                // Quality-gated via outer this.quality !== 'low'
                _cachedHotHandIdleGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
                _cachedHotHandIdleGrad.addColorStop(0, 'rgba(249, 115, 22, 0.6)');
                _cachedHotHandIdleGrad.addColorStop(0.5, 'rgba(234, 88, 12, 0.25)');
                _cachedHotHandIdleGrad.addColorStop(1, 'rgba(234, 88, 12, 0)');
              }
            }
            ctx.save();
            ctx.translate(fistWorldX, fistWorldY);
            ctx.scale(glowRadius, glowRadius);
            ctx.fillStyle = isPunching ? _cachedHotHandPunchGrad : _cachedHotHandIdleGrad;
            ctx.beginPath();
            ctx.arc(0, 0, 1, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }

          ctx.restore(); // front arm restore
        }

        ctx.restore(); // torso pivot restore
        ctx.restore(); // roll angle restore
      }
    } else if (isPinkBeret) {
      // 🎖️ PINK BERET: Supports V2 (36×36 Tier M Multi-Layer Rig), V2_AutoRig (36×36 Legacy Auto-Rig), UHD (48×48 Legacy Relaunch), and Classic (24×24 Legacy HD)
      const isClassic = !!(unit.forceHD || unit.variant === 'classic' || unit.skin === 'classic');
      const isAutoRig = !!(unit.variant === 'v2_autorig' || unit.skin === 'v2_autorig');
      const isV2 = !isClassic && !isAutoRig && !!(unit.variant === 'v2' || unit.skin === 'v2' || isPinkBeretV2Enabled());
      const isUHD = !isClassic && !isV2 && !isAutoRig;
      const pbDrawSize = Math.round(drawSize * ((isUHD || isV2 || isAutoRig) ? 1.20 : 1.12));
      ctx.globalAlpha = 1.0;
      if (this.quality === 'low' || isSimpleTier) {
        const isGold = unit.cardData?.spriteRecolor === 'gold' || unit.edition === 'gold';
        const prefix = isV2 ? (isGold ? 'PinkBeretGold_V2_' : 'PinkBeret_V2_') : (isGold ? 'PinkBeretGold_UHD_' : 'PinkBeret_UHD_');
        const state = unit.pinkBeretState || (unit.hasFiredInitialRocket ? 'NORMAL' : 'AIMING');
        let pbKey = isClassic ? 'PinkBeret_Classic' : (isAutoRig ? 'PinkBeret_V2_AutoRig' : (isV2 ? 'PinkBeret_V2' : 'PinkBeret_UHD'));

        if (state === 'AIMING' || state === 'WAITING_FOR_TARGET') {
          pbKey = prefix + 'Rocket';
        } else if (isWalking) {
          const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6) + 1;
          pbKey = prefix + 'Walk' + phaseIdx;
        }

        const pbBmp = buildSpriteBitmap(pbKey, renderRace, unit.team);
        const pbSrc = isHitFlashing ? pbBmp?.flash : pbBmp?.normal;
        if (pbSrc) {
          ctx.drawImage(pbSrc, renderX - pbDrawSize / 2, renderY - pbDrawSize / 2, pbDrawSize, pbDrawSize);
        }
      } else if (isUHD || isAutoRig) {
        // 🎖️ PINK BERET (48×48 UHD NATIVE RELAUNCH OR 36×36 V2_AUTORIG LEGACY)
        const isGold = unit.cardData?.spriteRecolor === 'gold' || unit.edition === 'gold';
        const prefix = isAutoRig
          ? 'PinkBeret_V2_AutoRig_'
          : (isGold ? 'PinkBeretGold_UHD_' : 'PinkBeret_UHD_');
        const state = unit.pinkBeretState || (unit.hasFiredInitialRocket ? 'NORMAL' : 'AIMING');

        if (state === 'NORMAL') {
          const isFiring = (unit.attackCooldown > 0) && (unit.attackCooldown > (unit.attackSpeed - 0.18));
          if (isFiring) {
            const recoilT = (unit.attackCooldown - (unit.attackSpeed - 0.18)) / 0.18;
            const kickX = -dir * 3.5 * recoilT;
            const kickY = -1.5 * recoilT;

            const gunBmp = buildSpriteBitmap(prefix + 'Gunfire', renderRace, unit.team);
            const gunSrc = isHitFlashing ? gunBmp?.flash : gunBmp?.normal;
            if (gunSrc) {
              ctx.drawImage(gunSrc, renderX + kickX - pbDrawSize / 2, renderY + kickY - pbDrawSize / 2, pbDrawSize, pbDrawSize);
            }

            // High-energy muzzle flash star & sharp directional sparks
            if (this.quality !== 'low') {
              const tipX = renderX + kickX + dir * pbDrawSize * 0.38;
              const tipY = renderY + kickY - pbDrawSize * 0.08;
              ctx.save();

              // Sharp 4-point star flash core
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.moveTo(tipX + dir * 6, tipY);
              ctx.lineTo(tipX + dir * 1.5, tipY - 2.5);
              ctx.lineTo(tipX - dir * 2, tipY);
              ctx.lineTo(tipX + dir * 1.5, tipY + 2.5);
              ctx.closePath();
              ctx.fill();

              // Radiant forward fire tongue
              ctx.fillStyle = '#fef08a';
              ctx.beginPath();
              ctx.moveTo(tipX + dir * 8, tipY);
              ctx.lineTo(tipX + dir * 2, tipY - 1.8);
              ctx.lineTo(tipX + dir * 2, tipY + 1.8);
              ctx.closePath();
              ctx.fill();

              // Crisp high-velocity spark needles radiating forward
              ctx.strokeStyle = '#fde047';
              ctx.lineWidth = 1.2;
              ctx.beginPath();
              ctx.moveTo(tipX, tipY);
              ctx.lineTo(tipX + dir * 11, tipY - 5);
              ctx.moveTo(tipX, tipY);
              ctx.lineTo(tipX + dir * 14, tipY);
              ctx.moveTo(tipX, tipY);
              ctx.lineTo(tipX + dir * 10, tipY + 5);
              ctx.stroke();

              // Signature pink micro-sparks
              ctx.strokeStyle = '#ff2a85';
              ctx.lineWidth = 1.0;
              ctx.beginPath();
              ctx.moveTo(tipX, tipY);
              ctx.lineTo(tipX + dir * 8, tipY - 8);
              ctx.moveTo(tipX, tipY);
              ctx.lineTo(tipX + dir * 9, tipY + 7);
              ctx.stroke();

              ctx.restore();
            }
          } else if (isWalking) {
            // 32-Frame armed catwalk cycle: arms swing at half the frequency of leg strides, head turns with varied duration (resolute march -> hin -> pause -> her)
            const cycle = ((walkPhase % (Math.PI * 8)) + (Math.PI * 8)) % (Math.PI * 8);
            const phaseIdx = Math.floor((cycle / (Math.PI * 8)) * 32);
            const frameNum = (phaseIdx % 32) + 1;
            const walkKey = prefix + 'Walk_Armed' + frameNum;

            const walkBmp = buildSpriteBitmap(walkKey, renderRace, unit.team);
            const walkSrc = isHitFlashing ? walkBmp?.flash : walkBmp?.normal;
            if (walkSrc) {
              ctx.drawImage(walkSrc, renderX - pbDrawSize / 2, renderY - pbDrawSize / 2, pbDrawSize, pbDrawSize);
            }
          } else {
            // Confident upright combat standing pose with handgun
            const standBmp = buildSpriteBitmap(prefix + 'Stand', renderRace, unit.team);
            const standSrc = isHitFlashing ? standBmp?.flash : standBmp?.normal;
            if (standSrc) {
              ctx.drawImage(standSrc, renderX - pbDrawSize / 2, renderY - pbDrawSize / 2, pbDrawSize, pbDrawSize);
            }
          }
        } else if (state === 'AIMING') {
          // 🎖️ RPG-7 3-Stage Aiming Sequence:
          // Stage 1: Shoulder Mount Heave (launcher heaved from waist carry up onto shoulder)
          // Stage 2: Dynamic Up/Down Tracking with active Torso & Leg breathing/aiming work
          // Stage 3: The Shot (sharp launch recoil kick, muzzle blast & backblast plume)
          const aimTotal = 2.0;
          const p = Math.max(0, Math.min(1, 1 - ((unit.pinkBeretAimTimer ?? aimTotal) / aimTotal)));

          if (p < 0.22) {
            // Stage 1 (0.0s - 0.44s): Shoulder Mount Heave Motion
            if (p < 0.12) {
              const heaveFrac = p / 0.12;
              const heaveY = -Math.sin(heaveFrac * (Math.PI / 2)) * 3.5;
              const heaveLean = -dir * Math.sin(heaveFrac * Math.PI) * 0.025;

              ctx.save();
              ctx.translate(renderX, renderY);
              const legsBmp = buildSpriteBitmap(prefix + 'Legs_Stand', renderRace, unit.team);
              const legsSrc = isHitFlashing ? legsBmp?.flash : legsBmp?.normal;
              if (legsSrc) ctx.drawImage(legsSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);

              ctx.translate(0, heaveY);
              ctx.rotate(heaveLean);
              const torsoBmp = buildSpriteBitmap(prefix + 'Torso', renderRace, unit.team);
              const torsoSrc = isHitFlashing ? torsoBmp?.flash : torsoBmp?.normal;
              if (torsoSrc) ctx.drawImage(torsoSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);

              const weaponBmp = buildSpriteBitmap(prefix + 'Launcher_Carry', renderRace, unit.team);
              const weaponSrc = isHitFlashing ? weaponBmp?.flash : weaponBmp?.normal;
              if (weaponSrc) ctx.drawImage(weaponSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
              ctx.restore();
            } else {
              // Settling onto shoulder at 45°
              const settleFrac = (p - 0.12) / 0.10;
              const settleDip = Math.sin(settleFrac * Math.PI) * 1.2;
              ctx.save();
              ctx.translate(renderX, renderY + settleDip);
              const aimBmp = buildSpriteBitmap(prefix + 'Launcher_Aim45', renderRace, unit.team);
              const aimSrc = isHitFlashing ? aimBmp?.flash : aimBmp?.normal;
              if (aimSrc) ctx.drawImage(aimSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
              ctx.restore();
            }
          } else if (p < 0.95) {
            // Stage 2 (0.44s - 1.90s): Dynamic Up/Down Aiming with Torso & Leg Work
            const trackP = (p - 0.22) / 0.73;
            const aimPitch = Math.sin(trackP * 3 * Math.PI) * 0.045 * dir;
            const aimBob = Math.sin(trackP * 6 * Math.PI) * 1.2;
            const aimShiftX = dir * (0.8 + Math.sin(trackP * 3 * Math.PI) * 1.0);

            ctx.save();
            ctx.translate(renderX + aimShiftX, renderY + aimBob);
            ctx.rotate(aimPitch);
            const aimBmp = buildSpriteBitmap(prefix + 'Launcher_Aim45', renderRace, unit.team);
            const aimSrc = isHitFlashing ? aimBmp?.flash : aimBmp?.normal;
            if (aimSrc) ctx.drawImage(aimSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
            ctx.restore();
          } else {
            // Stage 3 (1.90s - 2.0s): The Shot! Launch Recoil & Backblast
            const fireP = (p - 0.95) / 0.05;
            const kickX = -dir * 5.0 * (1.0 - fireP);
            const kickY = -1.5 * (1.0 - fireP);
            const kickPitch = -dir * 0.05 * (1.0 - fireP);

            ctx.save();
            ctx.translate(renderX + kickX, renderY + kickY);
            ctx.rotate(kickPitch);
            const aimBmp = buildSpriteBitmap(prefix + 'Launcher_Fire', renderRace, unit.team);
            const aimSrc = isHitFlashing ? aimBmp?.flash : aimBmp?.normal;
            if (aimSrc) ctx.drawImage(aimSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
            ctx.restore();
          }
        } else if (state === 'DISCARDING') {
          // Discard choreography (heave launcher off shoulder -> tumbling airborne arc -> impact dust)
          const p = Math.max(0, Math.min(1, 1 - (unit.pinkBeretTransitionTimer / 0.8)));

          // Soldier draws pistol at p >= 0.4
          const soldierKey = p >= 0.4 ? (prefix + 'Stand') : (prefix + 'Launcher_Aim45');
          const soldierBmp = buildSpriteBitmap(soldierKey, renderRace, unit.team);
          const soldierSrc = isHitFlashing ? soldierBmp?.flash : soldierBmp?.normal;
          if (soldierSrc) {
            ctx.drawImage(soldierSrc, renderX - pbDrawSize / 2, renderY - pbDrawSize / 2, pbDrawSize, pbDrawSize);
          }

          if (p >= 0.25) {
            // Discarded RPG-7 tumbling along ballistic arc
            const flyT = (p - 0.25) / 0.75;
            const flyX = renderX + dir * (pbDrawSize * (0.15 + flyT * 1.0));
            const flyY = (renderY - pbDrawSize * 0.35) - Math.sin(flyT * Math.PI) * (pbDrawSize * 0.45) + (flyT * flyT * pbDrawSize * 0.8);
            const flyRot = flyT * Math.PI * 3.5 * dir;

            ctx.save();
            ctx.translate(flyX, flyY);
            ctx.rotate(flyRot);
            if (p >= 0.85) {
              ctx.globalAlpha = Math.max(0, 1 - (p - 0.85) / 0.15);
            }
            const discardedBmp = buildSpriteBitmap(prefix + 'Launcher_Prop', renderRace, unit.team);
            const discardedSrc = isHitFlashing ? discardedBmp?.flash : discardedBmp?.normal;
            if (discardedSrc) {
              ctx.drawImage(discardedSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
            }
            ctx.restore();

            // Ground impact dust & spark puff at p >= 0.85
            if (p >= 0.85 && this.quality !== 'low') {
              const impactX = renderX + dir * (pbDrawSize * 1.1);
              const impactY = renderY + pbDrawSize * 0.38;
              ctx.save();
              ctx.fillStyle = 'rgba(251, 191, 36, 0.7)';
              ctx.beginPath();
              ctx.arc(impactX, impactY, 3, 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = 'rgba(203, 213, 225, 0.5)';
              ctx.beginPath();
              ctx.arc(impactX + dir * 4, impactY - 2, 4, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }
          }
        } else {
          // WAITING_FOR_TARGET: Michael Jackson Style Fashion Groove & Waiting Dance
          // Animated leg choreography across 8-beat groove with zero torso separation
          const dancePhase = t * 2.2;
          const beatFrac = dancePhase % 1.0;
          const measureBeat = Math.floor(dancePhase) % 8;

          // 8-Beat Dynamic Leg Choreography:
          // Beat 0: Center groove (solid base)
          // Beat 1: MJ Knee Pop & Full Foot Lift (heel high in the air, ball of foot flexed)
          // Beat 2: Attitude hold / peak foot lift
          // Beat 3: Downbeat stomp & solid ground plant!
          // Beat 4: Rebound / 3/4 Head turn to camera with shades & smirk
          // Beat 5: Left leg groove & weight shift
          // Beat 6: Swagger hold
          // Beat 7: Rebound back to center groove
          const danceLegSuffixes = [
            'Legs_Dance1',
            'Legs_Dance2',
            'Legs_Dance2',
            'Legs_Dance3',
            'Legs_Dance1',
            'Legs_Dance4',
            'Legs_Dance4',
            'Legs_Dance1'
          ];
          const legsKey = prefix + (danceLegSuffixes[measureBeat] || 'Legs_Dance1');

          // Rhythmic knee flex / bounce moving the whole unit harmoniously
          const kneeBounce = Math.sin(beatFrac * Math.PI) * 1.2;
          const shoulderTilt = Math.sin(dancePhase * Math.PI) * 0.040 * dir;
          const isHeadTurned = (measureBeat >= 4 && measureBeat <= 6);

          ctx.save();
          ctx.translate(renderX, renderY + kneeBounce * 0.4);

          // 1. Legs (rendered under torso with seamless high waistband)
          const legsBmp = buildSpriteBitmap(legsKey, renderRace, unit.team);
          const legsSrc = isHitFlashing ? legsBmp?.flash : legsBmp?.normal;
          if (legsSrc) {
            ctx.drawImage(legsSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
          }

          // 2. Torso & Shoulders (anchored at waist, rotating without vertical disconnection)
          ctx.save();
          // Pivot tilt from waist (y=25 in 48x48 space -> normalized 1.0px)
          const waistY = 1.0;
          ctx.translate(0, waistY);
          ctx.rotate(shoulderTilt);
          ctx.translate(0, -waistY);

          const torsoKey = isHeadTurned ? (prefix + 'Torso_Turn') : (prefix + 'Torso');
          const torsoBmp = buildSpriteBitmap(torsoKey, renderRace, unit.team);
          const torsoSrc = isHitFlashing ? torsoBmp?.flash : torsoBmp?.normal;
          if (torsoSrc) {
            ctx.drawImage(torsoSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
          }

          // 3. Rocket Launcher swaying with arms to the groove
          const weaponBmp = buildSpriteBitmap(prefix + 'Launcher_Carry', renderRace, unit.team);
          const weaponSrc = isHitFlashing ? weaponBmp?.flash : weaponBmp?.normal;
          if (weaponSrc) {
            ctx.drawImage(weaponSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
          }

          ctx.restore();
          ctx.restore();
        }
      } else {
        // 🎖️ PINK BERET ARTICULATED MULTI-LAYER RIG (V2 36×36 Tier M & Classic 24×24 HD)
        const isGold = unit.cardData?.spriteRecolor === 'gold' || unit.edition === 'gold';
        const prefix = isV2
          ? (isGold ? 'PinkBeretGold_V2_' : 'PinkBeret_V2_')
          : (isGold ? 'PinkBeretGold_Classic_' : 'PinkBeret_Classic_');
        const state = unit.pinkBeretState || (unit.hasFiredInitialRocket ? 'NORMAL' : 'AIMING');

        let legsKey = prefix + 'Legs_Stand';
        let torsoKey = prefix + 'Torso';
        let rearArmKey = null;
        let weaponKey = null;

        let torsoBob = 0;
        let torsoSway = 0;
        let torsoLean = 0;
        let rearArmAngle = 0;
        let frontArmAngle = 0;
        let recoilX = 0;
        let recoilY = 0;
        let recoilPitch = 0;

        // Rhythmic Dance Timing (~125 BPM, ~0.48s per beat)
        const dancePhase = t * 2.1;
        const beatFrac = dancePhase % 1.0;
        const measureBeat = Math.floor(dancePhase) % 8; // 8-beat phrasing (~3.8s)
        const isToeTapUp = (beatFrac >= 0.35 && beatFrac < 0.88);
        const tapDip = beatFrac < 0.35 ? Math.sin((beatFrac / 0.35) * Math.PI) * 0.7 : 0;

        if (state === 'WAITING_FOR_TARGET') {
          // 🎖️ Michael Jackson Fashion Waiting Dance: Shoulder Pop & Drop, Rhythmic Knee Bounce & Occasional 3/4 Head Turn
          weaponKey = prefix + 'Launcher_Carry';
          legsKey = isToeTapUp ? (prefix + 'Legs_Tap') : (prefix + 'Legs_Stand');

          const mjBeat = dancePhase % 1.0;
          const shoulderPop = mjBeat < 0.30 ? Math.sin((mjBeat / 0.30) * Math.PI) * 1.8 : 0;
          torsoBob = tapDip - shoulderPop;
          torsoSway = Math.sin(dancePhase * Math.PI) * 0.035 * dir;
          torsoLean = Math.sin(dancePhase * 0.5 * Math.PI) * 0.015;

          // Occasional Head Turn: every 8 beats, he turns towards camera on beats 4..6 (~1.4s)
          const isHeadTurned = (measureBeat >= 4 && measureBeat <= 6);
          if (isHeadTurned) {
            torsoKey = prefix + 'Torso_Turn';
          }
        } else if (state === 'AIMING') {
          // Phase 1: High Fashion Tactical Shouldering Sequence & Rock-Solid Locked Aim
          legsKey = prefix + 'Legs_Stand';

          // Progress through the 2.0s shouldering & aiming cycle
          const aimTotal = 2.0;
          const p = Math.max(0, Math.min(1, 1 - ((unit.pinkBeretAimTimer ?? aimTotal) / aimTotal)));

          if (p < 0.18) {
            // Stage 1 (0.0s - 0.36s): Launcher carried at waist level with two-handed support + initial foot tap
            weaponKey = prefix + 'Launcher_Carry';
            legsKey = isToeTapUp ? (prefix + 'Legs_Tap') : (prefix + 'Legs_Stand');
            const subP = p / 0.18;
            torsoBob = 0.6 * (1 - subP) + tapDip * 0.4;
            torsoLean = -0.02 * (1 - subP);
            torsoSway = Math.sin(t * 2.0) * 0.015 * dir;
          } else if (p < 0.38) {
            // Stage 2 (0.36s - 0.76s): Knee flexion & load compression preparing upward heave
            weaponKey = prefix + 'Launcher_Lift';
            legsKey = prefix + 'Legs_Crouch';
            const subP = (p - 0.18) / 0.20;
            torsoBob = -0.5 * Math.sin(subP * Math.PI);
            torsoLean = 0.02 * subP;
            torsoSway = Math.sin(t * 2.0) * 0.015 * dir;
          } else if (p < 0.62) {
            // Stage 3 (0.76s - 1.24s): Muscular upward leg drive & power heave through chest height
            weaponKey = prefix + 'Launcher_Heave';
            legsKey = prefix + 'Legs_Heave';
            const subP = (p - 0.38) / 0.24;
            torsoBob = -1.2 * Math.sin(subP * Math.PI);
            torsoLean = 0.05 * Math.sin(subP * Math.PI);
            torsoSway = 0.02 * (1 - subP) * dir;
          } else if (p < 0.80) {
            // Stage 4 (1.24s - 1.60s): Stepping into wide tactical combat tripod stance, cresting onto shoulder
            weaponKey = prefix + 'Launcher_Mount';
            legsKey = prefix + 'Legs_Aim';
            const subP = (p - 0.62) / 0.18;
            torsoBob = -0.6 * (1 - subP);
            torsoLean = 0.02 * (1 - subP);
            torsoSway = 0.015 * dir;
          } else if (isV2 && p >= 0.92) {
            // Stage 5B (1.84s - 2.0s): RPG-7 Ignition & Backblast Plume!
            weaponKey = prefix + 'Launcher_Fire';
            legsKey = prefix + 'Legs_Aim';
            const fireP = (p - 0.92) / 0.08;
            recoilX = -4.5 * (1.0 - fireP);
            recoilY = -1.5 * (1.0 - fireP);
            recoilPitch = -0.06 * (1.0 - fireP);
            torsoLean = -0.05 * (1.0 - fireP);
          } else {
            // Stage 5 (1.60s - 2.0s): Settled onto shoulder at 45°, rock-solid braced combat shooting stance
            weaponKey = prefix + 'Launcher_Aim45';
            legsKey = prefix + 'Legs_Aim';
            const settleT = (p - 0.80) / 0.20;
            const settleDip = Math.sin(settleT * Math.PI * 2) * Math.exp(-settleT * 3.5) * 0.7;
            torsoBob = settleDip + Math.sin(t * 2.8) * 0.5;
            torsoLean = 0;
            torsoSway = Math.sin(t * 1.5) * 0.02 * dir;
          }
        } else if (state === 'DISCARDING') {
          // Phase 2: Dedicated Launcher Discard Choreography (Heave -> Airborne Tumbling Flight -> Ground Impact & Pistol Draw)
          const p = Math.max(0, Math.min(1, 1 - (unit.pinkBeretTransitionTimer / 0.8)));
          legsKey = p < 0.25 ? (prefix + 'Legs_Aim') : (prefix + 'Legs_Stand');

          // Soldier dynamic posture during discard
          torsoBob = -Math.sin(p * Math.PI) * 1.5;
          torsoSway = -0.08 * dir * Math.sin(p * Math.PI);

          if (p < 0.25) {
            // Act 1: Soldier heaves launcher off shoulder with upward push
            weaponKey = prefix + 'Launcher_Aim45';
            const heaveFrac = p / 0.25;
            recoilX = dir * pbDrawSize * 0.12 * heaveFrac;
            recoilY = -pbDrawSize * 0.18 * heaveFrac;
            recoilPitch = 0.35 * dir * heaveFrac;
          } else {
            // Act 2 & 3: Launcher is flying/discarded in the air!
            const flyT = (p - 0.25) / 0.75;
            const flyX = renderX + dir * (pbDrawSize * (0.15 + flyT * 1.0));
            const flyY = (renderY - pbDrawSize * 0.35) - Math.sin(flyT * Math.PI) * (pbDrawSize * 0.45) + (flyT * flyT * pbDrawSize * 0.8);
            const flyRot = flyT * Math.PI * 3.5 * dir;

            // Render discarded rocket launcher tumbling along ballistic arc
            ctx.save();
            ctx.translate(flyX, flyY);
            ctx.rotate(flyRot);
            if (p >= 0.85) {
              // Fade out on ground impact
              ctx.globalAlpha = Math.max(0, 1 - (p - 0.85) / 0.15);
            }
            const discardPropKey = isV2 ? (prefix + 'Launcher_Prop') : (prefix + 'Launcher_Aim45');
            const discardedBmp = buildSpriteBitmap(discardPropKey, renderRace, unit.team);
            const discardedSrc = isHitFlashing ? discardedBmp?.flash : discardedBmp?.normal;
            if (discardedSrc) {
              ctx.drawImage(discardedSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
            }
            ctx.restore();

            // Ground impact dust & spark puff at p >= 0.85
            if (p >= 0.85 && this.quality !== 'low') {
              const impactX = renderX + dir * (pbDrawSize * 1.1);
              const impactY = renderY + pbDrawSize * 0.38;
              ctx.save();
              ctx.fillStyle = 'rgba(251, 191, 36, 0.7)';
              ctx.beginPath();
              ctx.arc(impactX, impactY, 3, 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = 'rgba(203, 213, 225, 0.5)';
              ctx.beginPath();
              ctx.arc(impactX + dir * 4, impactY - 2, 4, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }

            // Soldier draws pistol at p >= 0.4 with stylish twirl
            if (p >= 0.4) {
              weaponKey = prefix + 'Pistol';
              const drawTwirl = (1 - (p - 0.4) / 0.6) * Math.PI * 2 * dir;
              frontArmAngle = drawTwirl * 0.3;
            }
          }
        } else {
          // Phase 3: Normal Combat — High Fashion Catwalk Gait & Snappy Revolver Recoil
          rearArmKey = prefix + 'Arm_Rear';
          weaponKey = prefix + 'Pistol';

          if (isWalking) {
            const walkFrames = isV2
              ? (isGold ? PINKBERET_GOLD_V2_WALK_FRAMES : PINKBERET_V2_WALK_FRAMES)
              : (isGold ? PINKBERET_GOLD_WALK_FRAMES : PINKBERET_WALK_FRAMES);
            const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
            const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6);
            legsKey = walkFrames[phaseIdx % 6] || (prefix + 'Legs_Walk1');

            // High fashion Catwalk hip sway & slower half-frequency swinging arms (relaxed model poise)
            torsoBob = 0;
            torsoSway = Math.sin(cycle) * 0.08;
            rearArmAngle = -Math.sin(walkPhase * 0.5) * 0.24;
            frontArmAngle = Math.sin(walkPhase * 0.5) * 0.20;
          } else {
            // Confident upright military stance
            legsKey = prefix + 'Legs_Stand';
            torsoBob = 0;
            torsoSway = 0;
            rearArmAngle = 0;
            frontArmAngle = 0;
          }

          // Revolver Firing Recoil Kick & Muzzle Flash (Crisp, noticeable snap & muzzle flip!)
          const isFiring = (unit.attackCooldown > 0) && (unit.attackCooldown > (unit.attackSpeed - 0.18));
          if (isFiring) {
            const recoilT = (unit.attackCooldown - (unit.attackSpeed - 0.18)) / 0.18;
            recoilPitch = -0.45 * recoilT; // Muzzle snaps up ~25°
            recoilX = -5.0 * recoilT;     // Arm and gun kick back
            recoilY = -2.5 * recoilT;     // Hand rises with muzzle rise
            torsoLean = -0.12 * recoilT;  // Muscular torso absorbs kickback
          }
        }

        // 1. Draw Rear Arm (Behind Torso & Legs, swinging with catwalk poise around rear shoulder)
        if (rearArmKey) {
          ctx.save();
          ctx.translate(renderX, renderY + torsoBob);
          const rearPivotX = isV2 ? (-6 / 36) * pbDrawSize : (-7 / 24) * pbDrawSize;
          const rearPivotY = isV2 ? (-8 / 36) * pbDrawSize : (-4 / 24) * pbDrawSize;
          ctx.translate(rearPivotX, rearPivotY);
          ctx.rotate(rearArmAngle);
          ctx.translate(-rearPivotX, -rearPivotY);
          const rearBmp = buildSpriteBitmap(rearArmKey, renderRace, unit.team);
          const rearSrc = isHitFlashing ? rearBmp?.flash : rearBmp?.normal;
          if (rearSrc) {
            ctx.drawImage(rearSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
          }
          ctx.restore();
        }

        // 2. Draw Legs / Breeches & Boots (Solid 5px thickness in V2, seamless pelvis bridge)
        const legsBmp = buildSpriteBitmap(legsKey, renderRace, unit.team);
        const legsSrc = isHitFlashing ? legsBmp?.flash : legsBmp?.normal;
        if (legsSrc) {
          ctx.drawImage(legsSrc, renderX - pbDrawSize / 2, renderY - pbDrawSize / 2, pbDrawSize, pbDrawSize);
        }

        // 3. Draw Torso with Open Jacket & Muscular Chest (with hip sway & recoil lean)
        ctx.save();
        ctx.translate(renderX + recoilX * 0.3, renderY + torsoBob);
        ctx.rotate(torsoSway + torsoLean);
        const torsoBmp = buildSpriteBitmap(torsoKey, renderRace, unit.team);
        const torsoSrc = isHitFlashing ? torsoBmp?.flash : torsoBmp?.normal;
        if (torsoSrc) {
          ctx.drawImage(torsoSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
        }

        // 4. Draw Active Weapon / Viewer-Facing Front Arm (Sculpted Upper Arm, Elbow & Revolver)
        if (weaponKey) {
          ctx.save();
          const frontPivotX = isV2 ? (-4 / 36) * pbDrawSize : (-5 / 24) * pbDrawSize;
          const frontPivotY = isV2 ? (-8 / 36) * pbDrawSize : (-4 / 24) * pbDrawSize;
          ctx.translate(frontPivotX + recoilX * 0.7, frontPivotY + recoilY);
          ctx.rotate(frontArmAngle + recoilPitch);
          ctx.translate(-frontPivotX, -frontPivotY);

          const wepBmp = buildSpriteBitmap(weaponKey, renderRace, unit.team);
          const wepSrc = isHitFlashing ? wepBmp?.flash : wepBmp?.normal;
          if (wepSrc) {
            ctx.drawImage(wepSrc, -pbDrawSize / 2, -pbDrawSize / 2, pbDrawSize, pbDrawSize);
          }

          // Revolver Muzzle Flash Star & Spark Rays attached to exact elevated barrel tip
          const isFiringNow = (unit.attackCooldown > 0) && (unit.attackCooldown > (unit.attackSpeed - 0.18));
          if (isFiringNow && state === 'NORMAL' && this.quality !== 'low') {
            const tipX = isV2 ? (10 / 36) * pbDrawSize : (7 / 24) * pbDrawSize;
            const tipY = isV2 ? (-8 / 36) * pbDrawSize : (-2.5 / 24) * pbDrawSize;
            ctx.save();
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(tipX, tipY, 2.4, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#fde047';
            ctx.beginPath();
            ctx.arc(tipX + 2, tipY - 0.5, 4.0, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#ff2a85';
            ctx.beginPath();
            ctx.arc(tipX + 4, tipY - 1, 2.5, 0, Math.PI * 2);
            ctx.fill();

            // Spark rays radiating from muzzle
            ctx.strokeStyle = '#fef08a';
            ctx.lineWidth = 1.3;
            ctx.beginPath();
            ctx.moveTo(tipX, tipY);
            ctx.lineTo(tipX + 8, tipY - 4);
            ctx.moveTo(tipX, tipY);
            ctx.lineTo(tipX + 7, tipY + 4);
            ctx.moveTo(tipX, tipY);
            ctx.lineTo(tipX + 10, tipY);
            ctx.stroke();
            ctx.restore();
          }

          ctx.restore(); // Weapon restore
        }

        ctx.restore(); // Torso restore
      }
    } else if (isHacker) {
      const hackerDrawSize = Math.round(drawSize * 1.21);
      const isGold = unit.cardData?.spriteRecolor === 'gold' || unit.edition === 'gold';
      const hackerKeys = isGold ? HACKER_KEYS.HackerGold : HACKER_KEYS.Hacker;
      const age = unit.age || 0;
      const numId = getUnitNumericId(unit);

      // Unit Animation States
      const isTranscendent = !!unit.isTranscendent;
      const isHacking = !!unit.isHacking;
      const isDrifting = unit.hackerState === 'DRIFTING' || (unit.speed > 0.1 && !isHacking && !isTranscendent);
      const isIdle = !isHacking && !isTranscendent && !isDrifting;

      // Hit-flash recoil shudder
      const hitRecoil = isHitFlashing ? Math.sin(age * 36.0) * 1.5 : 0;

      // 1. Root Hover & Attitude
      let hoverBob = 0;
      let rootTilt = 0;
      let torsoLean = 0;
      let legsBob = 0;
      let legsTilt = 0;
      let headTilt = 0;
      let headNod = 0;
      let upperArmAngle = 0;
      let midArmAngle = 0;
      let kranzAngle = 0;
      let kranzScale = 1.0;

      if (isTranscendent) {
        hoverBob = -13.0 + Math.sin(age * 1.8) * 2.2;
        rootTilt = Math.sin(age * 1.2) * 0.02;
        torsoLean = -0.04; // proud divine upright posture
        legsBob = Math.sin(age * 1.8 - 0.3) * 0.5;
        legsTilt = 0;
        headTilt = -0.24 + Math.sin(age * 1.5) * 0.03; // gazing up into celestial cyber-realm
        headNod = 0;
        upperArmAngle = -0.48 + Math.sin(age * 2.0) * 0.06; // fully raised to heaven in enlightenment mudra
        midArmAngle = -0.32 + Math.cos(age * 2.0) * 0.05; // wide outstretched mudra
        kranzAngle = Math.sin(age * 1.2) * 0.08;
        kranzScale = 1.08 + Math.sin(age * 3.0) * 0.04;
      } else if (isHacking) {
        const hackJitter = Math.sin(age * 34.0) * 0.35;
        hoverBob = -4.5 + hackJitter + hitRecoil;
        rootTilt = 0.07 + Math.sin(age * 4.0) * 0.015;
        torsoLean = 0.12 + Math.sin(age * 6.0) * 0.02; // aggressive forward lean into holographic terminal
        legsBob = 0;
        legsTilt = 0.04; // anchored hovering in padmasana
        headTilt = 0.16 + Math.sin(age * 6.0) * 0.03; // head tilted down watching matrix code stream
        headNod = Math.sin(age * 14.0) * 0.015; // micro-nodding along with 14 Hz keystroke flurry
        upperArmAngle = 0.24 + Math.sin(age * 6.0) * 0.12; // reaching high-forward conducting cyber-stream
        midArmAngle = 0.18 + Math.cos(age * 8.0) * 0.08; // pointing forward guiding data harpoon
        kranzAngle = 0.05 + Math.sin(age * 8.0) * 0.03;
        kranzScale = 1.03 + Math.sin(age * 10.0) * 0.03;
      } else if (isDrifting) {
        hoverBob = -5.5 + Math.sin(age * 3.0 + (numId % 7)) * 2.2 + hitRecoil;
        rootTilt = 0.15 + Math.sin(age * 2.2) * 0.02; // aerodynamic banking pitch forward
        torsoLean = 0.10 + Math.sin(age * 2.5) * 0.02;
        legsBob = Math.sin(age * 3.0 - 0.5) * 1.5;
        legsTilt = -0.18; // lotus legs trailing gracefully backwards in slipstream!
        headTilt = -0.06 + Math.sin(age * 2.0) * 0.03; // gazing forward along flight trajectory
        headNod = 0;
        upperArmAngle = -0.22 + Math.sin(age * 3.0) * 0.04; // swept back aerodynamic fin
        midArmAngle = -0.30 + Math.cos(age * 3.0) * 0.04; // swept back aerodynamic stabilizer
        kranzAngle = -0.12 + Math.sin(age * 2.5) * 0.02;
        kranzScale = 0.98;
      } else {
        // IDLE: Meditative Padmasana Float & Harmonious Shiva Breath
        hoverBob = -5.0 + Math.sin(age * 2.2 + (numId % 7)) * 3.0 + hitRecoil;
        rootTilt = Math.sin(age * 1.5) * 0.025;
        torsoLean = Math.sin(age * 2.0) * 0.02;
        legsBob = Math.sin(age * 2.2 - 0.4) * 1.2;
        legsTilt = Math.cos(age * 1.6) * 0.03;
        headTilt = Math.sin(age * 1.2) * 0.05; // meditative scanning & awareness
        headNod = Math.sin(age * 2.2) * 0.02;
        upperArmAngle = Math.sin(age * 1.6) * 0.07; // gentle contemplative mudra
        midArmAngle = Math.cos(age * 1.3) * 0.05; // micro horizon scan
        kranzAngle = Math.sin(age * 1.4) * 0.03;
        kranzScale = 1.0 + Math.sin(age * 1.8) * 0.02;
      }

      if (this.quality === 'low' || isSimpleTier) {
        const hkBmp = buildSpriteBitmap(isGold ? 'HackerGold' : 'Hacker', renderRace, unit.team);
        const hkSrc = isHitFlashing ? hkBmp?.flash : hkBmp?.normal;
        if (hkSrc) {
          ctx.drawImage(hkSrc, renderX - hackerDrawSize / 2, renderY - hackerDrawSize / 2 + hoverBob, hackerDrawSize, hackerDrawSize);
        }
      } else {
        // 🌐 HUMAN HACKER RIG: Cyber-Mystic Shiva Deity (8 Articulated Limbs & Multi-Part Skeletal Assembly)
        // Anatomical joint pivots on 24x24 grid (grid center is 12, 12):
        const kranzPivotX = (-7 / 24) * hackerDrawSize;
        const kranzPivotY = (-2.5 / 24) * hackerDrawSize;
        const upperArmPivotX = (-4.5 / 24) * hackerDrawSize;
        const upperArmPivotY = (-4.5 / 24) * hackerDrawSize;
        const midArmPivotX = (-5 / 24) * hackerDrawSize;
        const midArmPivotY = (-2.5 / 24) * hackerDrawSize;
        const legsPivotX = (-5 / 24) * hackerDrawSize;
        const legsPivotY = (3 / 24) * hackerDrawSize;
        const torsoPivotX = (-5.5 / 24) * hackerDrawSize;
        const torsoPivotY = (-1 / 24) * hackerDrawSize;
        const headPivotX = (-4 / 24) * hackerDrawSize;
        const headPivotY = (-4.5 / 24) * hackerDrawSize;
        const frontArmPivotX = (-3.5 / 24) * hackerDrawSize;
        const frontArmPivotY = (-3.5 / 24) * hackerDrawSize;

        // 1. Trailing organic body cables (drawn behind body with smooth catenary sway)
        ctx.save();
        ctx.translate(renderX, renderY + hoverBob);
        for (let i = 0; i < 4; i++) {
          const sway = Math.sin(age * 2.6 + i * 1.3) * (3.5 + i * 1.2);
          const startX = -hackerDrawSize * 0.25;
          const startY = -hackerDrawSize * 0.05;
          const endX = -hackerDrawSize * (0.42 + i * 0.06);
          const endY = hackerDrawSize * (0.28 + i * 0.05) + sway;
          const ctrlX = -hackerDrawSize * 0.38 + sway * 0.5;
          const ctrlY = hackerDrawSize * 0.35;

          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.quadraticCurveTo(ctrlX, ctrlY, endX, endY);
          ctx.strokeStyle = HACKER_CABLE_COLORS[i];
          ctx.lineWidth = i === 0 ? 2.2 : 1.2;
          ctx.stroke();
        }
        ctx.restore();

        // 2. ROOT BODY CONTAINER & SCENE GRAPH
        ctx.save();
        ctx.translate(renderX, renderY + hoverBob);
        ctx.rotate(rootTilt);

        // A. SHIVA KRANZ BASE (the 4 background halo wreath arms: Arms 1, 3, 5, 6)
        drawHackerPart(ctx, hackerKeys.Kranz_Base, renderRace, unit.team, isHitFlashing, hackerDrawSize, kranzPivotX, kranzPivotY, kranzAngle, 0, 0, kranzScale, kranzScale);

        // B. ARTICULATED KRANZ ARM 2 (Upper Front Arm - high command mudra / cyber-stream conductor)
        drawHackerPart(ctx, hackerKeys.Kranz_UpperArm, renderRace, unit.team, isHitFlashing, hackerDrawSize, upperArmPivotX, upperArmPivotY, upperArmAngle, 0, 0, 1.0, 1.0);

        // C. ARTICULATED KRANZ ARM 4 (Mid Front Arm - pointer / data-harpoon guide)
        drawHackerPart(ctx, hackerKeys.Kranz_MidArm, renderRace, unit.team, isHitFlashing, hackerDrawSize, midArmPivotX, midArmPivotY, midArmAngle, 0, 0, 1.0, 1.0);

        // D. LOTUS LEGS (Padmasana folded thighs & cyber-feet hovering with inertia bob & tilt)
        drawHackerPart(ctx, hackerKeys.Legs, renderRace, unit.team, isHitFlashing, hackerDrawSize, legsPivotX, legsPivotY, legsTilt, 0, legsBob, 1.0, 1.0);

        // E. TORSO HIERARCHY (Cyber-Spine, Robe Chest, Team Badge, Waistband)
        ctx.save();
        ctx.translate(torsoPivotX, torsoPivotY);
        ctx.rotate(torsoLean);
        ctx.translate(-torsoPivotX, -torsoPivotY);

        const torsoBmp = buildSpriteBitmap(hackerKeys.Torso, renderRace, unit.team);
        const torsoSrc = isHitFlashing ? torsoBmp?.flash : torsoBmp?.normal;
        if (torsoSrc) {
          ctx.drawImage(torsoSrc, -hackerDrawSize / 2, -hackerDrawSize / 2, hackerDrawSize, hackerDrawSize);
        }

        // F. KOPF MIT HEADSET (Neck connection, golden ushnisha, cyber-visor, headset earpiece & boom mic)
        drawHackerPart(ctx, hackerKeys.Head, renderRace, unit.team, isHitFlashing, hackerDrawSize, headPivotX, headPivotY, headTilt + headNod, 0, 0, 1.0, 1.0);

        // G. THE 2 ANIMATED FRONT ARMS:
        // - In IDLE / DRIFTING: Floating Gyana Mudra hands with gentle harmonic breathing sway
        // - In HACKING: Rapid 14 Hz typing flurry across alternating frames on luminous holo-keyboard
        // - In TRANSCENDENT: Elevated enlightened dual mudra
        if (isHacking) {
          const typeFrame = Math.floor((age * 14) % 2);
          const armKey = typeFrame === 0 ? hackerKeys.Arms_Typing_0 : hackerKeys.Arms_Typing_1;
          const armBmp = buildSpriteBitmap(armKey, renderRace, unit.team) || buildSpriteBitmap(hackerKeys.Arms_Typing, renderRace, unit.team);
          const armSrc = isHitFlashing ? armBmp?.flash : armBmp?.normal;
          if (armSrc) {
            ctx.drawImage(armSrc, -hackerDrawSize / 2, -hackerDrawSize / 2, hackerDrawSize, hackerDrawSize);
          }

          // Luminous Holographic Floating Keyboard & Code Terminals
          const kbBmp = buildSpriteBitmap(hackerKeys.Keyboard, renderRace, unit.team);
          const kbSrc = kbBmp?.normal;
          if (kbSrc) {
            ctx.drawImage(kbSrc, -hackerDrawSize / 2, -hackerDrawSize / 2, hackerDrawSize, hackerDrawSize);
          }

          // Floating holographic code UI popups (DECRYPTING..., MATRIX GLYPHS)
          // (baked labels - see getHackerStreamLabelCanvas(); baseline offset 9px)
          const labelA = getHackerStreamLabelCanvas('<DATA_STREAM>', '#00f0ff');
          const labelB = getHackerStreamLabelCanvas('01011001', '#10b981');
          if (labelA && labelB) {
            const prevStreamAlpha = ctx.globalAlpha;
            ctx.globalAlpha = prevStreamAlpha * (0.75 + 0.25 * Math.sin(age * 8.0));
            ctx.drawImage(labelA, hackerDrawSize * 0.15, -hackerDrawSize * 0.32 - 9, 72, 12);
            ctx.drawImage(labelB, hackerDrawSize * 0.20, -hackerDrawSize * 0.20 - 9, 72, 12);
            ctx.globalAlpha = prevStreamAlpha;
          }
        } else if (isTranscendent) {
          const armBmp = buildSpriteBitmap(hackerKeys.Arms_Idle, renderRace, unit.team);
          const armSrc = isHitFlashing ? armBmp?.flash : armBmp?.normal;
          if (armSrc) {
            ctx.save();
            ctx.translate(frontArmPivotX, frontArmPivotY);
            ctx.rotate(-0.15);
            ctx.translate(-frontArmPivotX, -frontArmPivotY);
            ctx.drawImage(armSrc, -hackerDrawSize / 2, -hackerDrawSize / 2 - 2, hackerDrawSize, hackerDrawSize);
            ctx.restore();
          }
        } else {
          // IDLE / DRIFTING: Gyana Mudra hands resting in front of lap with gentle harmonic sway
          const idleArmBob = Math.sin(age * 3.0) * 0.8;
          const armBmp = buildSpriteBitmap(hackerKeys.Arms_Idle, renderRace, unit.team);
          const armSrc = isHitFlashing ? armBmp?.flash : armBmp?.normal;
          if (armSrc) {
            ctx.drawImage(armSrc, -hackerDrawSize / 2, -hackerDrawSize / 2 + idleArmBob, hackerDrawSize, hackerDrawSize);
          }
        }

        ctx.restore(); // Restore Torso transform

        ctx.restore(); // Restore Root transform
      }
    } else if (isWheelieBomber) {
      const isWestern = (unit.team === 'western');
      const dir = isWestern ? 1 : -1;
      const age = unit.age || 0;
      const wobble = unit.wobbleOffset || 0;
      const numId = getUnitNumericId(unit);

      let isWheelieActive = false;
      let wheelieStage = 0; // 0 = normal, 1 = pop/touchdown, 2 = 12 o'clock apex
      let popProg = 0;
      if (isWalking) {
        if (age < WHEELIE_BOMBER_LAUNCH_DURATION) {
          isWheelieActive = true;
          popProg = age / WHEELIE_BOMBER_LAUNCH_DURATION;
        } else {
          const cyclePos = (age + numId * 0.73) % WHEELIE_BOMBER_CRUISE_PERIOD;
          if (cyclePos < WHEELIE_BOMBER_STUNT_DURATION) {
            isWheelieActive = true;
            popProg = cyclePos / WHEELIE_BOMBER_STUNT_DURATION;
          }
        }
        if (isWheelieActive) {
          if (popProg < 0.28) {
            wheelieStage = (popProg / 0.28 < 0.5) ? 1 : 2;
          } else if (popProg < 0.68) {
            wheelieStage = 2;
          } else if (popProg < 0.86) {
            wheelieStage = ((popProg - 0.68) / 0.18 < 0.6) ? 1 : 0;
          }
        }
      }

      if (this.quality === 'low' || isSimpleTier) {
        let bomberKey = 'WheelieBomber';
        if (wheelieStage === 1) bomberKey = 'WheelieBomber_Wheelie1';
        else if (wheelieStage === 2) bomberKey = 'WheelieBomber_Wheelie2';
        const bomberBmp = buildSpriteBitmap(bomberKey, renderRace, unit.team) || buildSpriteBitmap('WheelieBomber', renderRace, unit.team);
        const bomberSrc = isHitFlashing ? bomberBmp?.flash : bomberBmp?.normal;
        if (bomberSrc) {
          drawLowTierWalkSprite(ctx, bomberSrc, renderX, renderY, drawSize, drawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_ROLL);
        }
      } else {
      // 🏍️ MULTI-PART WHEELIE BOMBER RIG: High-octane wasteland combat motorcycle & rider.
      // Features:
      // 1. Initial Power-Wheelie: Launches violently upon spawn/acceleration (0.0 - 0.8s).
      // 2. Periodic Stunt-Wheelies: Non-linear stunt cycle with power lift, apex balance wobble,
      //    landing compression, and damped rebound bounce.
      // 3. Cinematic Motorcycle "Schlenkerer": Harmonic lateral weaving (S-curves) & agile banking lean into turns.
      // 4. Throttle Backfire & Exhaust FX: Animated flame jets & sparks trailing from the tailpipe.

      let bikeKey = 'WheelieBomber_Bike';
      let dynamicPitch = 0;
      let riderLean = 0;
      let reboundBounce = 0;
      let flameIntensity = 0;

      // --- 1. NON-LINEAR WHEELIE STUNT KINEMATICS ---
      if (isWalking) {
        if (isWheelieActive) {
          if (popProg < 0.28) {
            // Phase A: Power Pop & Lift (0.00 -> 0.28) - rapid exponential rise + throttle backfire
            const sub = popProg / 0.28;
            const ease = Math.pow(sub, 1.6);
            bikeKey = sub < 0.5 ? 'WheelieBomber_Bike_Wheelie1' : 'WheelieBomber_Bike_Wheelie2';
            dynamicPitch = ease * 0.30;
            riderLean = -ease * 0.25;
            flameIntensity = 1.0;
          } else if (popProg < 0.68) {
            // Phase B: 12 o'Clock Apex & Balance Wobble (0.28 -> 0.68) - float & stunt balance
            bikeKey = 'WheelieBomber_Bike_Wheelie2';
            const balanceWobble = Math.sin(t * 16 + numId) * 0.035;
            dynamicPitch = 0.30 + balanceWobble;
            riderLean = -0.28 - balanceWobble * 0.8;
            flameIntensity = 0.85 + 0.15 * Math.sin(t * 24);
          } else if (popProg < 0.86) {
            // Phase C: Descent & Touchdown (0.68 -> 0.86) - front wheel coming down
            const sub = (popProg - 0.68) / 0.18;
            bikeKey = sub < 0.6 ? 'WheelieBomber_Bike_Wheelie1' : 'WheelieBomber_Bike';
            dynamicPitch = (1 - sub) * 0.30;
            riderLean = -(1 - sub) * 0.25;
            flameIntensity = 0.4 * (1 - sub);
          } else {
            // Phase D: Landing Compression & Damped Rebound (0.86 -> 1.00)
            const sub = (popProg - 0.86) / 0.14;
            bikeKey = 'WheelieBomber_Bike';
            reboundBounce = Math.sin(sub * Math.PI) * 1.8;
            dynamicPitch = -Math.sin(sub * Math.PI) * 0.08;
            riderLean = Math.sin(sub * Math.PI) * 0.06;
          }
        }

        const rollIdx = Math.floor((t * 14 + numId * 0.5) % 3);
        if (bikeKey === 'WheelieBomber_Bike_Wheelie2') {
          bikeKey = WHEELIE_BOMBER_WHEELIE2_ROLL_FRAMES[rollIdx] || 'WheelieBomber_Bike_Wheelie2_Roll1';
        } else if (bikeKey === 'WheelieBomber_Bike_Wheelie1') {
          bikeKey = WHEELIE_BOMBER_WHEELIE1_ROLL_FRAMES[rollIdx] || 'WheelieBomber_Bike_Wheelie1_Roll1';
        } else {
          bikeKey = WHEELIE_BOMBER_BIKE_ROLL_FRAMES[rollIdx] || 'WheelieBomber_Bike_Roll1';
        }
      }

      // --- 2. CINEASTISCHE SCHLENKERER & DYNAMISCHE SCHRÄGLAGE (BANKING) ---
      let swerveY = 0;
      let engineVibe = 0;
      let leanAngle = 0;

      if (isWalking) {
        // Lateral harmonic S-curves (weaving/schlenkerer)
        swerveY = Math.sin(t * 3.6 + wobble) * 2.2 + Math.sin(t * 1.8 + wobble * 1.3) * 1.4;
        // Engine / road mechanical micro-vibration
        engineVibe = Math.sin(t * 36) * 0.35;

        // Banking lean into the curve (derivative of lateral movement + target intercept)
        const swerveSlope = Math.cos(t * 3.6 + wobble) * 0.05 + Math.cos(t * 1.8 + wobble * 1.3) * 0.035;
        let interceptLean = 0;
        if (unit.target && !unit.target.isDead) {
          const dy = unit.target.y - unit.y;
          interceptLean = Math.max(-0.14, Math.min(0.14, dy * 0.0028));
        }
        leanAngle = (swerveSlope + interceptLean) * dir;
      }

      const drawX = renderX;
      const drawY = renderY + swerveY + engineVibe;

      const bikeBitmap = buildSpriteBitmap(bikeKey, renderRace, unit.team);
      const riderBitmap = buildSpriteBitmap('WheelieBomber_Rider', renderRace, unit.team);
      const bikeSrc = isHitFlashing ? bikeBitmap.flash : bikeBitmap.normal;
      const riderSrc = isHitFlashing ? riderBitmap.flash : riderBitmap.normal;

      ctx.save();

      // Pivot around rear wheel contact patch (x: ~-0.30*drawSize, y: ~0.35*drawSize)
      const rearContactX = drawX - dir * (drawSize * 0.30);
      const rearContactY = drawY + (drawSize * 0.35);
      ctx.translate(rearContactX, rearContactY);
      ctx.rotate(leanAngle - dir * dynamicPitch);
      ctx.translate(-rearContactX, -rearContactY);

      // 1. BIKE: High-detail motorcycle chassis with wheelie frame + landing rebound
      ctx.drawImage(
        bikeSrc,
        drawX - drawSize / 2,
        drawY - drawSize / 2 + reboundBounce,
        drawSize,
        drawSize
      );

      // 2. RIDER: Wasteland biker leaning into the wind and bracing as the bike pitches up
      ctx.save();
      const riderPivotX = drawX - dir * (drawSize * 0.22);
      const riderPivotY = drawY + (drawSize * 0.28) + reboundBounce;
      ctx.translate(riderPivotX, riderPivotY);
      ctx.rotate(-dir * (riderLean || -0.05));
      ctx.translate(-riderPivotX, -riderPivotY);

      ctx.drawImage(
        riderSrc,
        drawX - drawSize / 2,
        drawY - drawSize / 2 + reboundBounce,
        drawSize,
        drawSize
      );
      ctx.restore();

      // 3. EXHAUST FLAME JET: Animated fire tongues trailing from tailpipe during power bursts
      if (isWalking && flameIntensity > 0.1 && this.quality !== 'low') {
        const flameDirX = -dir;
        const tailX = drawX + flameDirX * (drawSize * 0.44);
        const tailY = drawY + (drawSize * 0.22) + (isWheelieActive ? drawSize * 0.08 : 0) + reboundBounce;
        const flamePulse = (0.75 + 0.25 * Math.sin(t * 30)) * flameIntensity;
        const flameLen = (4.5 + Math.sin(t * 60 + numId) * 2.5 + 2.0) * flamePulse;

        ctx.fillStyle = '#f97316';
        ctx.fillRect(tailX, tailY, flameDirX * flameLen, 2.2);
        ctx.fillStyle = '#fef08a';
        ctx.fillRect(tailX, tailY + 0.4, flameDirX * (flameLen * 0.6), 1.4);
      }

      ctx.restore();
      }
    } else if (isShade || isHyperShade) {
      // 🌑/🦅 MULTI-PART SHADE & HYPER SHADE RIG: Stealth Legs Walkcycle, Tattered Cloak & Suppressed Sniper Rifle
      const prefix = isHyperShade ? 'HyperShade' : 'Shade';
      const walkFrames = isHyperShade ? HYPER_SHADE_WALK_FRAMES : SHADE_WALK_FRAMES;
      let legsKey = `${prefix}_Legs`;
      let legBob = 0;
      let torsoBob = 0;
      let torsoLean = 0;

      // Gentle continuous ambient breathing & wind sway
      const windSway = Math.sin(t * 3.2 + unit.wobbleOffset) * 0.025;

      // Cinematic actors (docs/singleplayer implementation soft-foraging-
      // flamingo.md) never have a target and never actually move via
      // marchForward() (js/engine/unit.js's isCinematic guard) - the
      // isWalking heuristic above only checks target/attackCooldown, so a
      // cutscene-paused cinematic Shade would otherwise stay on the walk
      // cycle forever (walkPhase is driven by elapsed time `t`, not by
      // actual distance covered) and look like it's marching on the spot
      // while stood still between path waypoints. Comparing this frame's
      // position to last frame's catches that: a genuinely moving unit's
      // x/y changes on every render() call, a paused one's doesn't.
      const reallyMoving = !unit.isCinematic || unit.x !== unit._lastRenderX || unit.y !== unit._lastRenderY;
      unit._lastRenderX = unit.x;
      unit._lastRenderY = unit.y;

      if (isWalking && reallyMoving) {
        // 6-phase high-fidelity infantry walkcycle (Paradigma 1: Zero-Allocation)
        const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 6);
        legsKey = walkFrames[phaseIdx] || `${prefix}_Legs_Walk1`;
        legBob = (phaseIdx === 1 || phaseIdx === 4) ? 0.7 : 0;
        torsoBob = Math.sin(walkPhase * 2) * 0.6;
        torsoLean = (walkStride * 0.04) + windSway;
      } else {
        // Idle gentle breathing - explicit `_Stand` key (currently the same
        // grid as the bare `_Legs` default, see unitSprites.js) so a future
        // visual split between "default" and "standing at ease" stays a
        // one-file change.
        legsKey = `${prefix}_Legs_Stand`;
        torsoBob = Math.sin(t * 2.2 + unit.wobbleOffset) * 0.4;
        torsoLean = windSway;
      }

      const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
      const torsoBitmap = buildSpriteBitmap(`${prefix}_Torso`, renderRace, unit.team);
      const gunBitmap = buildSpriteBitmap(`${prefix}_Gun`, renderRace, unit.team);

      const legsSrc = isHitFlashing ? legsBitmap.flash : legsBitmap.normal;
      const torsoSrc = isHitFlashing ? torsoBitmap.flash : torsoBitmap.normal;
      const gunSrc = isHitFlashing ? gunBitmap.flash : gunBitmap.normal;

      // Sniper Recoil and Elevation Aiming
      let gunRecoilX = 0;
      let gunKickAngle = 0;
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.22)) {
        const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - 0.22)) / 0.22;
        const spring = dampedSpringRecoil(recoilFraction);
        gunRecoilX = -spring * 4.8;
        gunKickAngle = -spring * 0.10;
      }

      let gunAimY = 0;
      if (unit.target && !unit.target.isDead) {
        const dTargetY = unit.target.y - unit.y;
        gunAimY = Math.max(-3.5, Math.min(3.5, dTargetY * 0.035));
      }

      // 🎨 Conditional Offscreen Composite Buffer for Multi-Part Cloaking (Paradigma 2)
      // Only engaged when semi-transparent (isCloaked or spawning) to eliminate alpha-bleed.
      // In fully opaque state (bodyAlpha === 1), direct drawing is 100% pixel-identical and skips clearRect.
      const needsOffscreenBuffer = unit.isCloaked || (unit.materializeTimer && unit.materializeTimer > 0);

      if (!needsOffscreenBuffer) {
        // 1. LEGS
        ctx.drawImage(
          legsSrc,
          renderX - drawSize / 2,
          renderY - drawSize / 2 - legBob,
          drawSize,
          drawSize
        );

        // 2. TORSO
        ctx.save();
        const torsoPivotX = renderX;
        const torsoPivotY = renderY + drawSize * 0.1;
        ctx.translate(torsoPivotX, torsoPivotY);
        ctx.rotate(torsoLean);
        ctx.translate(-torsoPivotX, -torsoPivotY);
        ctx.drawImage(
          torsoSrc,
          renderX - drawSize / 2,
          renderY - drawSize / 2 - legBob + torsoBob,
          drawSize,
          drawSize
        );
        ctx.restore();

        // 3. GUN
        ctx.save();
        const gunPivotX = renderX + drawSize * 0.05;
        const gunPivotY = renderY - legBob + torsoBob;
        ctx.translate(gunPivotX, gunPivotY);
        ctx.rotate(gunKickAngle);
        ctx.translate(-gunPivotX, -gunPivotY);
        ctx.drawImage(
          gunSrc,
          renderX - drawSize / 2 + gunRecoilX,
          renderY - drawSize / 2 - legBob + torsoBob + gunAimY,
          drawSize,
          drawSize
        );
        ctx.restore();
      } else {
        if (!this._shadeCompCanvas) {
          this._shadeCompCanvas = document.createElement('canvas');
          this._shadeCompCanvas.width = 128;
          this._shadeCompCanvas.height = 128;
          this._shadeCompCtx = this._shadeCompCanvas.getContext('2d');
        }

        const sctx = this._shadeCompCtx;
        const sw = 128, sh = 128;
        const scx = 64, scy = 64;
        sctx.clearRect(0, 0, sw, sh);
        sctx.imageSmoothingEnabled = false;

        // 1. LEGS on offscreen buffer
        sctx.drawImage(
          legsSrc,
          scx - drawSize / 2,
          scy - drawSize / 2 - legBob,
          drawSize,
          drawSize
        );

        // 2. TORSO on offscreen buffer
        sctx.save();
        const torsoPivotX = scx;
        const torsoPivotY = scy + drawSize * 0.1;
        sctx.translate(torsoPivotX, torsoPivotY);
        sctx.rotate(torsoLean);
        sctx.translate(-torsoPivotX, -torsoPivotY);
        sctx.drawImage(
          torsoSrc,
          scx - drawSize / 2,
          scy - drawSize / 2 - legBob + torsoBob,
          drawSize,
          drawSize
        );
        sctx.restore();

        // 3. GUN on offscreen buffer
        sctx.save();
        const gunPivotX = scx + drawSize * 0.05;
        const gunPivotY = scy - legBob + torsoBob;
        sctx.translate(gunPivotX, gunPivotY);
        sctx.rotate(gunKickAngle);
        sctx.translate(-gunPivotX, -gunPivotY);
        sctx.drawImage(
          gunSrc,
          scx - drawSize / 2 + gunRecoilX,
          scy - drawSize / 2 - legBob + torsoBob + gunAimY,
          drawSize,
          drawSize
        );
        sctx.restore();

        // 4. Cloak shimmer ghost copy (faint cyan copy drifting off-phase)
        if (unit.isCloaked) {
          const shimmerX = Math.sin(t * 5 + unit.wobbleOffset) * 3;
          const shimmerY = Math.cos(t * 4 + unit.wobbleOffset) * 2;
          ctx.save();
          ctx.globalAlpha = (0.16 + 0.08 * Math.sin(t * 7)) * spawnAlpha;
          ctx.globalCompositeOperation = 'lighter';
          ctx.drawImage(
            this._shadeCompCanvas,
            renderX - scx + shimmerX,
            renderY - scy + shimmerY,
            sw,
            sh
          );
          ctx.restore();
        }

        // 5. Final Blit onto main Canvas
        ctx.drawImage(
          this._shadeCompCanvas,
          renderX - scx,
          renderY - scy,
          sw,
          sh
        );
      }
    } else if (unitType === 'TeslaWalker' || unitType === 'Tesla_Walker' || unitType === 'Tesla Walker') {
      if (this.quality === 'low' || isSimpleTier) {
        let teslaKey = 'TeslaWalker';
        let walkerBob = 0;
        if (isWalking) {
          const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4);
          teslaKey = TESLA_WALKER_COMP_WALK_FRAMES[phaseIdx] || 'TeslaWalker_Walk1';
          walkerBob = (phaseIdx === 0 || phaseIdx === 2) ? 0.6 : 0.0;
        }
        const teslaBmp = buildSpriteBitmap(teslaKey, renderRace, unit.team);
        const teslaSrc = isHitFlashing ? teslaBmp?.flash : teslaBmp?.normal;
        if (teslaSrc) {
          ctx.drawImage(teslaSrc, renderX - drawSize / 2, renderY - drawSize / 2 + walkerBob, drawSize, drawSize);
        }
      } else {
      // ⚡ MULTI-PART TESLA WALKER ANIMATION: Spider Legs, Chassis & Tesla Coil Emitter
      let legsKey = 'TeslaWalker_Legs';
      let spiderBob = 0;
      let chassisPitch = 0;

      if (isWalking) {
        // 4-phase alternating quadruped spider gait (Paradigma 1: Zero-Allocation)
        const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4);
        legsKey = TESLA_WALKER_WALK_FRAMES[phaseIdx] || 'TeslaWalker_Legs_Walk1';
        spiderBob = (phaseIdx === 0 || phaseIdx === 2) ? 0.6 : 0.0;
        chassisPitch = phaseIdx === 0 ? 0.02 : (phaseIdx === 2 ? -0.02 : 0.0);
      }

      const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
      const chassisBitmap = buildSpriteBitmap('TeslaWalker_Chassis', renderRace, unit.team);
      const teslaBitmap = buildSpriteBitmap('TeslaWalker_Tesla', renderRace, unit.team);

      const legsSrc = isHitFlashing ? legsBitmap.flash : legsBitmap.normal;
      const chassisSrc = isHitFlashing ? chassisBitmap.flash : chassisBitmap.normal;
      const teslaSrc = isHitFlashing ? teslaBitmap.flash : teslaBitmap.normal;

      // 1. SPIDER LEGS (Unterteil): Articulated quadruped spider locomotion
      ctx.drawImage(
        legsSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 + spiderBob,
        drawSize,
        drawSize
      );

      // 2. ROBOTIC CHASSIS (Mittelteil): Hull suspension & team armor
      ctx.save();
      const chassisPivotX = renderX;
      const chassisPivotY = renderY + spiderBob;
      ctx.translate(chassisPivotX, chassisPivotY);
      ctx.rotate(chassisPitch);
      ctx.translate(-chassisPivotX, -chassisPivotY);

      ctx.drawImage(
        chassisSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2 + spiderBob,
        drawSize,
        drawSize
      );
      ctx.restore();

      // 3. TESLA COIL EMITTER (Oberteil): High-voltage discharge recoil with Damped Spring & oscillating field
      let teslaRecoilX = 0;
      let teslaSurgeAngle = 0;
      let isDischarging = false;

      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.24)) {
        const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - 0.24)) / 0.24; // 1.0 -> 0.0
        const spring = dampedSpringRecoil(recoilFraction);
        teslaRecoilX = -spring * 4.8; // Bracing backward recoil slide
        teslaSurgeAngle = -spring * 0.07; // Coil tilts back under magnetic flux
        isDischarging = true;
      }

      // Vertical aiming towards target
      let teslaAimY = 0;
      if (unit.target && !unit.target.isDead) {
        const dTargetY = unit.target.y - unit.y;
        teslaAimY = Math.max(-3.0, Math.min(3.0, dTargetY * 0.03));
      }

      ctx.save();
      const teslaPivotX = renderX;
      const teslaPivotY = renderY - drawSize * 0.1 + spiderBob;
      ctx.translate(teslaPivotX, teslaPivotY);
      ctx.rotate(teslaSurgeAngle);
      ctx.translate(-teslaPivotX, -teslaPivotY);

      ctx.drawImage(
        teslaSrc,
        renderX - drawSize / 2 + teslaRecoilX,
        renderY - drawSize / 2 + spiderBob + teslaAimY,
        drawSize,
        drawSize
      );

      // Glowing High-Voltage Dielectric Spark Arcs between Coil Emitter Prongs
      if (isDischarging) {
        const sparkPhase = t * 45;
        const coilTopX = renderX + teslaRecoilX + (unit.team === 'western' ? 4 : -4);
        const coilTopY = renderY - drawSize * 0.32 + spiderBob + teslaAimY;

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(coilTopX - 6, coilTopY);
        ctx.lineTo(coilTopX + (Math.sin(sparkPhase) * 4), coilTopY - 3);
        ctx.lineTo(coilTopX + 6, coilTopY);
        ctx.stroke();

        ctx.strokeStyle = '#00f0ff';
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(coilTopX - 5, coilTopY);
        ctx.lineTo(coilTopX + (Math.cos(sparkPhase) * 5), coilTopY + 3);
        ctx.lineTo(coilTopX + 5, coilTopY);
        ctx.stroke();

        // High-energy corona halo
        ctx.fillStyle = 'rgba(0, 240, 255, 0.4)';
        ctx.beginPath();
        ctx.arc(coilTopX, coilTopY, 8, 0, Math.PI * 2);
        ctx.fill();
      } else if (unit.target && !unit.target.isDead && unit.attackCooldown <= 0.24 && unit.attackCooldown > 0) {
        // ⚡ Anticipatory Capacitor Arc Priming (last 0.24s before the shot when aiming at target)
        const primeNorm = 1.0 - (unit.attackCooldown / 0.24); // 0.0 -> 1.0 (crescendo)
        const sparkPhase = t * (30 + primeNorm * 60);
        const coilTopX = renderX + (unit.team === 'western' ? 4 : -4);
        const coilTopY = renderY - drawSize * 0.32 + spiderBob + teslaAimY;

        if (this.quality !== 'low') {
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 0.8 + primeNorm * 0.8;
          ctx.beginPath();
          ctx.moveTo(coilTopX - 5, coilTopY);
          ctx.lineTo(coilTopX + (Math.sin(sparkPhase) * (2 + primeNorm * 3)), coilTopY - 2);
          ctx.lineTo(coilTopX + 5, coilTopY);
          ctx.stroke();

          ctx.strokeStyle = '#00f0ff';
          ctx.lineWidth = 0.6 + primeNorm * 0.6;
          ctx.beginPath();
          ctx.moveTo(coilTopX - 4, coilTopY);
          ctx.lineTo(coilTopX + (Math.cos(sparkPhase) * (2 + primeNorm * 4)), coilTopY + 2);
          ctx.lineTo(coilTopX + 4, coilTopY);
          ctx.stroke();
        }

        ctx.globalAlpha = 0.2 + primeNorm * 0.35;
        ctx.fillStyle = '#00f0ff';
        ctx.beginPath();
        ctx.arc(coilTopX, coilTopY, 4.5 + primeNorm * 3.5, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Idle capacitor oscillation glow
        const idleHum = 0.5 + 0.5 * Math.sin(t * 12);
        const coilTopX = renderX + (unit.team === 'western' ? 4 : -4);
        const coilTopY = renderY - drawSize * 0.32 + spiderBob + teslaAimY;

        ctx.globalAlpha = idleHum * 0.25;
        ctx.fillStyle = '#00f0ff';
        ctx.beginPath();
        ctx.arc(coilTopX, coilTopY, 4.5, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      }
    } else if (unitType === 'HyperTeslaWalker' || unitType === 'Hyper_Tesla_Walker' || unitType === 'Hyper Tesla Walker') {
      if (this.quality === 'low' || isSimpleTier) {
        // Low Quality Tier: Single pre-baked composite blit with 4-phase alternating tetrapod walk cycle (1 draw call, zero rig kinematics)
        let compKey = 'HyperTeslaWalker';
        let walkerBob = 0;
        if (isWalking) {
          const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4);
          compKey = HYPER_TESLA_WALKER_COMP_WALK_FRAMES[phaseIdx] || 'HyperTeslaWalker_Walk1';
          walkerBob = (phaseIdx === 0 || phaseIdx === 2) ? 0.7 : 0.0;
        }
        const compBitmap = buildSpriteBitmap(compKey, renderRace, unit.team);
        const compSrc = isHitFlashing ? compBitmap.flash : compBitmap.normal;
        ctx.drawImage(compSrc, renderX - drawSize / 2, renderY - drawSize / 2 + walkerBob, drawSize, drawSize);
      } else {
        // 🦂 8-LEGGED MECHANICAL SCORPION WALKER: Alternating Tetrapod Gait, Copper Carapace, Stinger Tail & Scissor Claw
        let legsKey = 'HyperTeslaWalker_Legs_Stand';
      let walkerBob = 0;
      let chassisPitch = 0;
      let chassisRoll = 0;

      if (isWalking) {
        // 4-phase alternating tetrapod gait (Zero-Allocation)
        const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4);
        legsKey = HYPER_TESLA_WALKER_WALK_FRAMES[phaseIdx] || 'HyperTeslaWalker_Legs_Walk1';
        walkerBob = (phaseIdx === 0 || phaseIdx === 2) ? 0.7 : 0.0;
        chassisPitch = (phaseIdx === 0) ? 0.025 : ((phaseIdx === 2) ? -0.025 : 0.0);
        chassisRoll = (phaseIdx === 1) ? 0.02 : ((phaseIdx === 3) ? -0.02 : 0.0);
      } else {
        // Idle pneumatic breathing
        walkerBob = Math.sin(t * 1.8) * 0.4;
      }

      // Claw animation state
      let clawKey = 'HyperTeslaWalker_Claw';
      let clawOffsetX = 0;
      let clawOffsetY = 0;
      let clawAngle = 0;

      if (unit.executionTimer && unit.executionTimer > 0) {
        const totalDur = unit.executionDuration || 1.45;
        const progress = Math.min(1.0, Math.max(0, 1.0 - (unit.executionTimer / totalDur)));
        const fDir = unit.team === 'western' ? 1 : -1;

        if (progress < 0.14) {
          // Phase 1: Aggressive Hydraulic Pounce & Clamp (0.00s - 0.20s)
          const p = progress / 0.14;
          clawKey = 'HyperTeslaWalker_Claw_Grab';
          chassisPitch = fDir * 0.15 * p;
          walkerBob = 4.0 * p;
          clawOffsetX = fDir * (18 * p);
          clawOffsetY = -2 * p;
          clawAngle = fDir * (0.22 * p);
        } else if (progress < 0.38) {
          // Phase 2: Heavy Mechanical Hoist (0.20s - 0.55s)
          const liftNorm = (progress - 0.14) / 0.24;
          const easeLift = Math.sin(liftNorm * Math.PI * 0.5);
          clawKey = 'HyperTeslaWalker_Claw_Lift';
          chassisPitch = fDir * (-0.24 * easeLift); // Rearing up on hind legs!
          walkerBob = -9.0 * easeLift;
          clawOffsetX = fDir * 18;
          clawOffsetY = -2 - (28 * easeLift);
          clawAngle = fDir * (0.22 + easeLift * 0.28);
        } else if (progress < 0.72) {
          // Phase 3: The Zenith Hold (0.55s - 1.05s) - 0.50s sustained suspense!
          clawKey = 'HyperTeslaWalker_Claw_Lift';
          chassisPitch = fDir * -0.24;
          walkerBob = -9.0;
          clawOffsetX = fDir * 18;
          clawOffsetY = -30;
          clawAngle = fDir * 0.50;
        } else if (progress < 0.86) {
          // Phase 4: Hydraulic Guillotine Cleave (1.05s - 1.25s) (violent slam shut!)
          const snapNorm = (progress - 0.72) / 0.14;
          clawKey = 'HyperTeslaWalker_Claw';
          chassisPitch = fDir * (0.28 * (1 - snapNorm)); // Heavy slam recoil!
          walkerBob = 6.0 * (1 - snapNorm);
          clawOffsetX = fDir * 18;
          clawOffsetY = -30;
          clawAngle = fDir * -0.16;
        } else {
          // Phase 5: Hydraulic Decompression & Reset (1.25s - 1.45s)
          const rNorm = (progress - 0.86) / 0.14;
          clawKey = 'HyperTeslaWalker_Claw';
          clawOffsetX = fDir * 18 * (1 - rNorm);
          clawOffsetY = -30 * (1 - rNorm);
          clawAngle = fDir * -0.16 * (1 - rNorm);
          chassisPitch = fDir * (0.05 * Math.sin(rNorm * Math.PI));
          walkerBob = 0;
        }
      } else if (unit.isPincerStalking) {
        // Pincer Stalk: Claw is open, reaching hungrily forward
        clawKey = 'HyperTeslaWalker_Claw_Grab';
        clawOffsetX = (unit.team === 'western' ? 1 : -1) * 3;
        clawOffsetY = -2;
        clawAngle = (unit.team === 'western' ? 1 : -1) * 0.12;
      }

      const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
      const chassisBitmap = buildSpriteBitmap('HyperTeslaWalker_Chassis', renderRace, unit.team);
      const tailBitmap = buildSpriteBitmap('HyperTeslaWalker_Tail', renderRace, unit.team);
      const clawBitmap = buildSpriteBitmap(clawKey, renderRace, unit.team);

      const legsSrc = isHitFlashing ? legsBitmap.flash : legsBitmap.normal;
      const chassisSrc = isHitFlashing ? chassisBitmap.flash : chassisBitmap.normal;
      const tailSrc = isHitFlashing ? tailBitmap.flash : tailBitmap.normal;
      const clawSrc = isHitFlashing ? clawBitmap.flash : clawBitmap.normal;

      // Stinger Tail: enlarged scale, fixed root anchor on chassis & dynamic tip whip
      const tailDrawSize = drawSize * 1.15;
      let tailRecoilX = 0;
      let tailSurgeAngle = 0;
      let isDischarging = false;

      const dir = unit.team === 'western' ? 1 : -1;

      if (unit.executionTimer && unit.executionTimer > 0) {
        const totalDur = unit.executionDuration || 1.45;
        const progress = Math.min(1.0, Math.max(0, 1.0 - (unit.executionTimer / totalDur)));
        if (progress < 0.14) {
          tailSurgeAngle = dir * (progress / 0.14 * -0.12);
        } else if (progress < 0.38) {
          const liftNorm = (progress - 0.14) / 0.24;
          const easeLift = Math.sin(liftNorm * Math.PI * 0.5);
          tailSurgeAngle = dir * (0.44 * easeLift);
          isDischarging = true;
        } else if (progress < 0.72) {
          // Sustained overcharge during zenith hold
          tailSurgeAngle = dir * 0.46;
          isDischarging = true;
        } else if (progress < 0.86) {
          tailSurgeAngle = dir * 0.48;
          isDischarging = true;
        } else {
          const rNorm = (progress - 0.86) / 0.14;
          tailSurgeAngle = dir * (0.48 * (1 - rNorm));
        }
      } else if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.28)) {
        const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - 0.28)) / 0.28;
        const spring = dampedSpringRecoil(recoilFraction);
        tailRecoilX = -dir * spring * 6.0;
        tailSurgeAngle = -dir * spring * 0.14;
        isDischarging = true;
      } else if (isWalking) {
        // Dynamic whip angle pivoted at the fixed root on the chassis
        // Higher harmonic makes only the elevated stinger tip whip forward & back dynamically
        const walkWhip = Math.sin(walkPhase) * 0.11 + Math.sin(walkPhase * 2 - 0.4) * 0.045;
        tailSurgeAngle = dir * walkWhip;
      } else {
        // Subtle pneumatic breathing & gyro stabilization
        tailSurgeAngle = Math.sin(t * 2.2) * 0.035;
      }

      let tailAimAngle = 0;
      if (unit.target && !unit.target.isDead) {
        const dTargetY = unit.target.y - unit.y;
        tailAimAngle = Math.max(-0.08, Math.min(0.08, dTargetY * 0.0008));
      }
      const totalTailAngle = tailSurgeAngle + dir * tailAimAngle;

      // 🎨 Conditional Offscreen Composite Buffer for Multi-Part Cloaking
      const needsOffscreenBuffer = unit.isCloaked || (unit.materializeTimer && unit.materializeTimer > 0);

      let targetCtx = ctx;
      let cx = renderX;
      let cy = renderY;

      if (needsOffscreenBuffer) {
        if (!this._hyperTeslaCompCanvas) {
          this._hyperTeslaCompCanvas = document.createElement('canvas');
          this._hyperTeslaCompCanvas.width = 128;
          this._hyperTeslaCompCanvas.height = 128;
          this._hyperTeslaCompCtx = this._hyperTeslaCompCanvas.getContext('2d');
        }
        targetCtx = this._hyperTeslaCompCtx;
        targetCtx.clearRect(0, 0, 128, 128);
        targetCtx.imageSmoothingEnabled = false;
        cx = 64;
        cy = 64;
      }

      // 1. 8-LEGGED SCORPION UNDERCARRIAGE
      targetCtx.drawImage(
        legsSrc,
        cx - drawSize / 2,
        cy - drawSize / 2 + walkerBob,
        drawSize,
        drawSize
      );

      // 2. MAIN ARMORED CHASSIS (Copper Pipes, Cockpit Pod, Exposed Gears)
      targetCtx.save();
      const chassisPivotX = cx;
      const chassisPivotY = cy + walkerBob;
      targetCtx.translate(chassisPivotX, chassisPivotY);
      targetCtx.rotate(chassisPitch + chassisRoll);
      targetCtx.translate(-chassisPivotX, -chassisPivotY);

      targetCtx.drawImage(
        chassisSrc,
        cx - drawSize / 2,
        cy - drawSize / 2 + walkerBob,
        drawSize,
        drawSize
      );
      targetCtx.restore();

      // 3. ARCHED SCORPION STINGER TAIL (Enlarged, Fixed Root on Carapace, Whipping Tip)
      // Fixed Root Anchor where the tail emerges from the rear carapace (x: 3.5, y: 9.5 in 20x20)
      const tailRootX = cx + (dir === 1 ? -0.325 : 0.325) * tailDrawSize + tailRecoilX;
      const tailRootY = cy + walkerBob - 0.025 * tailDrawSize;

      targetCtx.save();
      targetCtx.translate(tailRootX, tailRootY);
      targetCtx.rotate(totalTailAngle);
      targetCtx.translate(-tailRootX, -tailRootY);

      targetCtx.drawImage(
        tailSrc,
        cx - tailDrawSize / 2 + tailRecoilX,
        cy - tailDrawSize / 2 + walkerBob,
        tailDrawSize,
        tailDrawSize
      );

      // Compute exact tip coordinates under rotation around the fixed root
      const unrotatedTipX = cx + (dir === 1 ? 0.45 : -0.45) * tailDrawSize + tailRecoilX;
      const unrotatedTipY = cy + walkerBob - 0.375 * tailDrawSize;
      const cosA = Math.cos(totalTailAngle);
      const sinA = Math.sin(totalTailAngle);
      const dxTip = unrotatedTipX - tailRootX;
      const dyTip = unrotatedTipY - tailRootY;
      const stingerTipX = tailRootX + dxTip * cosA - dyTip * sinA;
      const stingerTipY = tailRootY + dxTip * sinA + dyTip * cosA;

      // High-Voltage Dielectric Spark Arcs & Corona Glow at Stinger Tip
      if (isDischarging) {
        const sparkPhase = t * 48;

        if (this.quality !== 'low') {
          targetCtx.strokeStyle = '#ffffff';
          targetCtx.lineWidth = 2.0;
          targetCtx.beginPath();
          targetCtx.moveTo(stingerTipX - 8, stingerTipY);
          targetCtx.lineTo(stingerTipX + (Math.sin(sparkPhase) * 6), stingerTipY - 5);
          targetCtx.lineTo(stingerTipX + 8, stingerTipY);
          targetCtx.stroke();

          targetCtx.strokeStyle = '#00f0ff';
          targetCtx.lineWidth = 1.4;
          targetCtx.beginPath();
          targetCtx.moveTo(stingerTipX - 7, stingerTipY);
          targetCtx.lineTo(stingerTipX + (Math.cos(sparkPhase) * 7), stingerTipY + 5);
          targetCtx.lineTo(stingerTipX + 7, stingerTipY);
          targetCtx.stroke();
        }

        // High-energy cyan plasma corona
        targetCtx.fillStyle = 'rgba(0, 240, 255, 0.50)';
        targetCtx.beginPath();
        targetCtx.arc(stingerTipX, stingerTipY, 11, 0, Math.PI * 2);
        targetCtx.fill();
      } else if (unit.target && !unit.target.isDead && unit.attackCooldown <= 0.24 && unit.attackCooldown > 0) {
        // ⚡ Anticipatory Stinger Arc Priming (last 0.24s before the shot when aiming at target)
        const primeNorm = 1.0 - (unit.attackCooldown / 0.24); // 0.0 -> 1.0 (crescendo)
        const sparkPhase = t * (30 + primeNorm * 60);

        if (this.quality !== 'low') {
          targetCtx.strokeStyle = '#ffffff';
          targetCtx.lineWidth = 1.0 + primeNorm * 0.8;
          targetCtx.beginPath();
          targetCtx.moveTo(stingerTipX - 6, stingerTipY);
          targetCtx.lineTo(stingerTipX + (Math.sin(sparkPhase) * (3 + primeNorm * 4)), stingerTipY - 3);
          targetCtx.lineTo(stingerTipX + 6, stingerTipY);
          targetCtx.stroke();

          targetCtx.strokeStyle = '#00f0ff';
          targetCtx.lineWidth = 0.8 + primeNorm * 0.6;
          targetCtx.beginPath();
          targetCtx.moveTo(stingerTipX - 5, stingerTipY);
          targetCtx.lineTo(stingerTipX + (Math.cos(sparkPhase) * (3 + primeNorm * 4)), stingerTipY + 3);
          targetCtx.lineTo(stingerTipX + 5, stingerTipY);
          targetCtx.stroke();
        }

        targetCtx.globalAlpha = 0.2 + primeNorm * 0.4;
        targetCtx.fillStyle = '#00f0ff';
        targetCtx.beginPath();
        targetCtx.arc(stingerTipX, stingerTipY, 6 + primeNorm * 4, 0, Math.PI * 2);
        targetCtx.fill();
      } else {
        const idlePulse = 0.5 + 0.5 * Math.sin(t * 10);
        targetCtx.globalAlpha = idlePulse * 0.35;
        targetCtx.fillStyle = '#00f0ff';
        targetCtx.beginPath();
        targetCtx.arc(stingerTipX, stingerTipY, 6, 0, Math.PI * 2);
        targetCtx.fill();
      }
      targetCtx.restore();

      // 4. LEFT HYDRAULIC CLEAVER CLAW
      targetCtx.save();
      const clawPivotX = cx + (unit.team === 'western' ? -6 : 6);
      const clawPivotY = cy + walkerBob;
      targetCtx.translate(clawPivotX, clawPivotY);
      targetCtx.rotate(clawAngle);
      targetCtx.translate(-clawPivotX, -clawPivotY);

      targetCtx.drawImage(
        clawSrc,
        cx - drawSize / 2 + clawOffsetX,
        cy - drawSize / 2 + walkerBob + clawOffsetY,
        drawSize,
        drawSize
      );
      targetCtx.restore();

      if (needsOffscreenBuffer) {
        ctx.drawImage(
          this._hyperTeslaCompCanvas,
          renderX - 64,
          renderY - 64
        );
      }
    }
    } else if (unitType === 'DeployableCannon' || unitType === 'Deployable_Cannon' || unitType === 'Deployable Cannon') {
      if (this.quality === 'low' || isSimpleTier) {
        const cannonBmp = buildSpriteBitmap('DeployableCannon', renderRace, unit.team);
        const cannonSrc = isHitFlashing ? cannonBmp?.flash : cannonBmp?.normal;
        if (cannonSrc) {
          drawLowTierWalkSprite(ctx, cannonSrc, renderX, renderY, drawSize, drawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_STEP);
        }
      } else {
      // 🕷️ MULTI-PART DEPLOYABLE CANNON ANIMATION: Spider Base & Autocannon Turret
      const baseBitmap = buildSpriteBitmap('DeployableCannon_Base', renderRace, unit.team);
      const turretBitmap = buildSpriteBitmap('DeployableCannon_Turret', renderRace, unit.team);

      const baseSrc = isHitFlashing ? baseBitmap.flash : baseBitmap.normal;
      const turretSrc = isHitFlashing ? turretBitmap.flash : turretBitmap.normal;

      // 1. BASE (Articulated spider platform anchored to ground with blast shields)
      ctx.drawImage(
        baseSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2,
        drawSize,
        drawSize
      );

      // 2. TURRET (360 Autocannon with kinetic recoil slide and elevation tracking)
      let turretRecoilX = 0;
      let turretRockAngle = 0;
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.22)) {
        const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - 0.22)) / 0.22; // 1.0 -> 0.0
        const spring = dampedSpringRecoil(recoilFraction);
        turretRecoilX = -spring * 4.8; // Kinetic backward slide along barrel axis
        turretRockAngle = -spring * 0.06;
      }

      // Vertical aiming towards target
      let turretAimY = 0;
      let turretAimAngle = 0;
      if (unit.target && !unit.target.isDead) {
        const dTargetY = unit.target.y - unit.y;
        turretAimY = Math.max(-3.5, Math.min(3.5, dTargetY * 0.03));
      } else {
        // 🛰️ IDLE SCANNING: Subtle elevation breathing / sensor sweep when no target
        const idleCycle = Math.sin(t * 1.8 + (unit.id ? (unit.id * 1.618) : 0));
        turretAimY = idleCycle * 1.2;
        turretAimAngle = idleCycle * 0.035;
      }

      ctx.save();
      const pivotX = renderX - drawSize * 0.05;
      const pivotY = renderY - drawSize * 0.05;
      ctx.translate(pivotX, pivotY);
      ctx.rotate(turretRockAngle + turretAimAngle);
      ctx.translate(-pivotX, -pivotY);

      ctx.drawImage(
        turretSrc,
        renderX - drawSize / 2 + turretRecoilX,
        renderY - drawSize / 2 + turretAimY,
        drawSize,
        drawSize
      );

      // 🔥 Slender Autocannon Fluted Barrel Thermal Glow (Paradigma 3: LUT Strip)
      if (unit.barrelHeat > 0.04 && this.quality !== 'low') {
        const heatProg = unit.barrelHeat;
        const bDir = unit.team === 'western' ? 1 : -1;
        const barrelTipX = renderX + bDir * (drawSize * 0.44) + turretRecoilX;
        const barrelTipY = renderY - drawSize * 0.12 + turretAimY;
        const barrelW = drawSize * 0.28;
        const barrelH = 3.5;
        const heatStrip = getThermalHeatStrip();

        if (heatStrip) {
          ctx.save();
          ctx.globalAlpha = heatProg;
          const drawX = barrelTipX - (bDir === 1 ? barrelW : 0);
          const drawY = barrelTipY - barrelH / 2;
          if (bDir === 1) {
            ctx.drawImage(heatStrip, drawX, drawY, barrelW, barrelH);
          } else {
            ctx.translate(drawX + barrelW, drawY);
            ctx.scale(-1, 1);
            ctx.drawImage(heatStrip, 0, 0, barrelW, barrelH);
          }
          ctx.restore();
        }

        // Radial Thermal Bloom Halo
        ctx.globalAlpha = heatProg * 0.45;
        ctx.fillStyle = '#f97316';
        ctx.beginPath();
        ctx.arc(barrelTipX, barrelTipY, 5 * heatProg, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      }
    } else if (unitType === 'Rocketship') {
      // 🚀 ROCKETSHIP AEROSPACE FIGHTER ANIMATION: Dynamic Banking & Rocket Afterburner Flames
      const shipBitmap = buildSpriteBitmap('Rocketship', renderRace, unit.team);
      const shipSrc = isHitFlashing ? shipBitmap.flash : shipBitmap.normal;

      // Flight attitude: subtle banking tilt during movement and hover wobble
      const bankTilt = Math.sin(t * 3.5 + unit.wobbleOffset) * 0.04 + (unit.speed > 0 ? 0.06 : 0);

      ctx.save();
      ctx.translate(renderX, renderY);
      ctx.rotate(bankTilt);
      ctx.translate(-renderX, -renderY);

      ctx.drawImage(
        shipSrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2,
        drawSize,
        drawSize
      );

      // Animated Twin Afterburner Rocket Exhaust Flames (-X rear)
      const flamePulse = 0.7 + 0.3 * Math.sin(t * 28);
      const flameLen = (6 + Math.random() * 5) * flamePulse;
      const flameDirX = unit.team === 'western' ? -1 : 1;
      const rearX = renderX + flameDirX * (drawSize * 0.42);

      // Top Thruster Flame
      ctx.fillStyle = '#f97316';
      ctx.fillRect(rearX, renderY - drawSize * 0.12, flameDirX * flameLen, 2.5);
      ctx.fillStyle = '#fef08a';
      ctx.fillRect(rearX, renderY - drawSize * 0.10, flameDirX * (flameLen * 0.6), 1.5);

      // Bottom Thruster Flame
      ctx.fillStyle = '#f97316';
      ctx.fillRect(rearX, renderY + drawSize * 0.01, flameDirX * flameLen, 2.5);
      ctx.fillStyle = '#fef08a';
      ctx.fillRect(rearX, renderY + drawSize * 0.03, flameDirX * (flameLen * 0.6), 1.5);

      ctx.restore();
    } else if (unitType === 'EnforcerVulture' || unitType === 'Enforcer_Vulture' || unitType === 'Enforcer Vulture') {
      if (this.quality === 'low' || isSimpleTier) {
        const enfBmp = buildSpriteBitmap('EnforcerVulture', renderRace, unit.team);
        const enfSrc = isHitFlashing ? enfBmp?.flash : enfBmp?.normal;
        if (enfSrc) {
          const recoil = unit._vultureRecoil || 0;
          const recoilY = recoil * 1.2;
          const phase = t * 1.6 + (unit.wobbleOffset || 0);
          const hoverY = Math.sin(phase) * 4.2 + recoilY;
          const hoverSurgeX = Math.cos(phase) * 1.8 * forwardDir;
          ctx.drawImage(
            enfSrc,
            renderX + hoverSurgeX - drawSize / 2,
            renderY + hoverY - drawSize / 2,
            drawSize,
            drawSize
          );
        }
      } else {
      // 🦅 SUL ENFORCER VULTURE BIOMECHANICAL TITAN (Facing forwardDir)
      const bodyBitmap = buildSpriteBitmap('EnforcerVulture_Body', renderRace, unit.team);
      const headBitmap = buildSpriteBitmap('EnforcerVulture_Head', renderRace, unit.team);
      const bodySrc = isHitFlashing ? bodyBitmap.flash : bodyBitmap.normal;
      const headSrc = isHitFlashing ? headBitmap.flash : headBitmap.normal;

      // Recoil & levitation dynamics
      const recoil = unit._vultureRecoil || 0;
      const recoilY = recoil * 1.2; // Downward jolt on pod launch
      const recoilPitch = -recoil * 0.02 * forwardDir;

      // Heavy leviathan hover dynamics (undulating air wobble with compound harmonic breathing)
      const omega = 1.6;
      const phase = t * omega + unit.wobbleOffset;
      // Compound vertical heave: primary buoyant wave + secondary respiration harmonic
      const hoverY = Math.sin(phase) * 5.0 + Math.sin(phase * 2.0 - 0.5) * 1.6 + recoilY;
      // Elliptical flight surge: gentle forward reach during ascent, settling back during descent
      const hoverSurgeX = Math.cos(phase) * 2.2 * forwardDir;
      // Dynamic banking tilt that sways with the buoyant heave and forward velocity
      const bankTilt = (unit.speed > 0 ? 0.05 * forwardDir : 0) + Math.cos(phase) * 0.042 * forwardDir + recoilPitch;

      const drawOriginX = renderX + hoverSurgeX;
      const drawOriginY = renderY + hoverY;

      ctx.save();
      ctx.translate(drawOriginX, drawOriginY);
      ctx.rotate(bankTilt);
      ctx.translate(-drawOriginX, -drawOriginY);

      // 1. Biomechanical Body Chassis & Carapace
      ctx.drawImage(
        bodySrc,
        drawOriginX - drawSize / 2,
        drawOriginY - drawSize / 2,
        drawSize,
        drawSize
      );

      // SOARING RAKED DORSAL SPINE STRUCTURE (Aufstrebende Struktur: swept-back cantilever buttress ribs)
      // Connects the dorsal flank with soaring architectural trusses reaching up and back towards the rear crest
      for (let s = 0; s < 4; s++) {
        const strutColX = 12.0 + s * 6.0; // 12.0, 18.0, 24.0, 30.0
        const strutBaseY = 10.0 + s * 1.0;
        const rootX = drawOriginX + ((strutColX - 23.5) / 48) * drawSize * forwardDir;
        const rootY = drawOriginY + ((strutBaseY - 23.5) / 48) * drawSize;
        // Raked apex swept backward (-X) and upward (-Y)
        const apexX = rootX - (drawSize * (0.065 + (3 - s) * 0.012) * forwardDir);
        const apexY = rootY - (drawSize * (0.080 + (3 - s) * 0.015));

        // Dark shadow backing girder
        ctx.strokeStyle = '#261002';
        ctx.lineWidth = 2.4;
        ctx.beginPath();
        ctx.moveTo(rootX, rootY);
        ctx.lineTo(apexX, apexY);
        ctx.stroke();

        // Heavy antique bronze structural rib
        ctx.strokeStyle = s === 0 ? '#d97706' : (s === 1 ? '#b45309' : '#92400e');
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(rootX, rootY);
        ctx.lineTo(apexX, apexY);
        ctx.stroke();

        // Diagonal flying-buttress strut tying into the rear spine
        if (s > 0) {
          ctx.strokeStyle = '#78350f';
          ctx.lineWidth = 1.0;
          ctx.beginPath();
          ctx.moveTo(rootX, rootY - drawSize * 0.02);
          ctx.lineTo(apexX - drawSize * 0.03 * forwardDir, apexY + drawSize * 0.025);
          ctx.stroke();
        }

        // Ivory bone specular cap at the tip of the ascending spine
        ctx.fillStyle = s === 0 ? '#ffffff' : '#fde68a';
        ctx.fillRect(apexX - 0.9, apexY - 0.9, 1.8, 1.8);
      }

      // 2. 8 DISTINCT DORSAL BIO-ROCKET BALLS IN 4 SYMMETRICAL COLUMNS (Ascending raked wedge)
      // Deeply recessed inside the creature's armored flank & chassis (sunk below the dorsal carapace)
      // Row 0 (far row / abgewandt, gy=10..13): upper dorsal flank in background
      // Row 1 (near row / zugewandt, gy=15..18): parallel lower dorsal flank closer to viewer
      // Columns at gx = 12.0, 18.0, 24.0, 30.0 (perfect 6.0px spacing, centered at 21.0)

      // Horizontal mounting rails connecting the sockets in each row (antique bronze)
      // Far row mounting rail
      ctx.strokeStyle = '#78350f';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let j = 0; j < 4; j++) {
        const p = VULTURE_POD_SPINE_OFFSETS[j * 2]; // 0, 2, 4, 6
        const px = drawOriginX + ((p.gx - 23.5) / 48) * drawSize * forwardDir;
        const py = drawOriginY + ((p.gy - 23.5) / 48) * drawSize;
        if (j === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();

      // Near row mounting rail (burnished bronze)
      ctx.strokeStyle = '#b45309';
      ctx.lineWidth = 1.9;
      ctx.beginPath();
      for (let j = 0; j < 4; j++) {
        const p = VULTURE_POD_SPINE_OFFSETS[j * 2 + 1]; // 1, 3, 5, 7
        const px = drawOriginX + ((p.gx - 23.5) / 48) * drawSize * forwardDir;
        const py = drawOriginY + ((p.gy - 23.5) / 48) * drawSize;
        if (j === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();

      // Vertical connector struts linking each dual-pod column (dark bronze iron)
      ctx.strokeStyle = '#5c3d1e';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      for (let j = 0; j < 4; j++) {
        const pFar = VULTURE_POD_SPINE_OFFSETS[j * 2];
        const pNear = VULTURE_POD_SPINE_OFFSETS[j * 2 + 1];
        const px = drawOriginX + ((pFar.gx - 23.5) / 48) * drawSize * forwardDir;
        const py1 = drawOriginY + ((pFar.gy - 23.5) / 48) * drawSize;
        const py2 = drawOriginY + ((pNear.gy - 23.5) / 48) * drawSize;
        ctx.moveTo(px, py1);
        ctx.lineTo(px, py2);
      }
      ctx.stroke();

      const baseBallR = drawSize * 0.052; // Crisp, visible bio-rocket pods sitting snugly in socket apertures

      for (let orderIdx = 0; orderIdx < 8; orderIdx++) {
        const i = VULTURE_RENDER_POD_ORDER[orderIdx];
        const pPos = VULTURE_POD_SPINE_OFFSETS[i];
        const isFar = pPos.isFar;
        const podX = drawOriginX + ((pPos.gx - 23.5) / 48) * drawSize * forwardDir;
        const podY = drawOriginY + ((pPos.gy - 23.5) / 48) * drawSize;

        const pod = (unit.vulturePods && unit.vulturePods[i]) ? unit.vulturePods[i] : { reloadTimer: 0, reloadDuration: 5.0 };
        const isReady = pod.reloadTimer <= 0;

        // Depth perspective scale: far row is slightly foreshortened (~88%), near row is full size (~100%)
        const curBaseR = isFar ? baseBallR * 0.88 : baseBallR * 1.0;

        // RECESSED SOCKET WELL: Cylindrical cavity cut into the bronze bone carapace
        // Outer bronze/chitin socket collar framing the aperture
        ctx.fillStyle = isFar ? '#2e1805' : '#451a03';
        ctx.beginPath();
        ctx.arc(podX, podY, curBaseR + (isFar ? 1.2 : 1.6), 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = isFar ? '#78350f' : '#b45309';
        ctx.lineWidth = isFar ? 0.7 : 1.0;
        ctx.stroke();

        // Inner cylindrical cavity well (pitch dark bronze-shadow interior)
        ctx.fillStyle = '#140a02';
        ctx.beginPath();
        ctx.arc(podX, podY, curBaseR + 0.3, 0, Math.PI * 2);
        ctx.fill();

        if (isReady) {
          // NESTLED BIO-SPHERE: Deeply embedded in the body with traveling respiration pulse across the 4 columns
          const colIdx = Math.floor(i / 2); // 0, 1, 2, 3
          const ballPulse = 1.0 + 0.03 * Math.sin(phase * 2.0 - colIdx * 0.65);
          const curR = curBaseR * ballPulse;
          const seatedY = podY + 0.3;

          if (this.quality !== 'low') {
            // Subtle bioluminescent corona halo emanating from behind the socket (traveling respiration wave)
            const haloPulse = Math.sin(phase * 2.0 - colIdx * 0.65);
            const haloAlpha = isFar ? (0.16 + 0.06 * haloPulse) : (0.26 + 0.10 * haloPulse);
            ctx.fillStyle = isFar ? '#e11d48' : '#ff0032';
            ctx.globalAlpha = haloAlpha;
            ctx.beginPath();
            ctx.arc(podX, seatedY, curR * (isFar ? 1.35 : 1.5), 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1.0;
          }

          if (this.quality !== 'low') {
            const podCanvas = isFar ? getVulturePodFarCanvas() : getVulturePodNearCanvas();
            if (podCanvas) {
              ctx.drawImage(podCanvas, podX - curR, seatedY - curR, curR * 2, curR * 2);
            } else {
              ctx.fillStyle = isFar ? '#be123c' : '#ff0033';
              ctx.beginPath();
              ctx.arc(podX, seatedY, curR, 0, Math.PI * 2);
              ctx.fill();
            }
          } else {
            ctx.fillStyle = isFar ? '#be123c' : '#ff0033';
            ctx.beginPath();
            ctx.arc(podX, seatedY, curR, 0, Math.PI * 2);
            ctx.fill();
          }

          // Specular glint on upper-left quadrant
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.beginPath();
          ctx.arc(podX - curR * 0.28, seatedY - curR * 0.32, Math.max(0.7, curR * 0.26), 0, Math.PI * 2);
          ctx.fill();

          // Ambient cavity rim occlusion: socket carapace casts a subtle rim shadow over the upper-outer edge
          ctx.strokeStyle = isFar ? 'rgba(31, 17, 3, 0.75)' : 'rgba(46, 24, 5, 0.65)';
          ctx.lineWidth = 0.85;
          ctx.beginPath();
          ctx.arc(podX, podY, curBaseR + 0.05, 1.1 * Math.PI, 1.9 * Math.PI);
          ctx.stroke();
        } else {
          // RELOADING / GESTATING POD: Visibly regrowing embryo in deep socket well
          const reloadProg = Math.max(0, Math.min(1.0, 1.0 - (pod.reloadTimer / (pod.reloadDuration || 5.0))));

          // Incubating bio-embryo growing inside socket cavity
          const embryoR = curBaseR * (0.28 + 0.64 * reloadProg);
          const seatedY = podY + 0.3;

          if (this.quality !== 'low' && reloadProg > 0.4) {
            ctx.fillStyle = isFar ? '#be123c' : '#ef4444';
            ctx.globalAlpha = (isFar ? 0.18 : 0.25) * reloadProg;
            ctx.beginPath();
            ctx.arc(podX, seatedY, embryoR * (isFar ? 1.25 : 1.35), 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1.0;
          }

          if (this.quality !== 'low') {
            const embCanvas = isFar ? getVulturePodFarEmbryoCanvas() : getVulturePodNearEmbryoCanvas();
            if (embCanvas) {
              ctx.drawImage(embCanvas, podX - embryoR, seatedY - embryoR, embryoR * 2, embryoR * 2);
            } else {
              ctx.fillStyle = isFar ? '#be123c' : '#dc2626';
              ctx.beginPath();
              ctx.arc(podX, seatedY, embryoR, 0, Math.PI * 2);
              ctx.fill();
            }
          } else {
            ctx.fillStyle = isFar ? '#be123c' : '#dc2626';
            ctx.beginPath();
            ctx.arc(podX, seatedY, embryoR, 0, Math.PI * 2);
            ctx.fill();
          }

          if (reloadProg > 0.6) {
            ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
            ctx.beginPath();
            ctx.arc(podX - embryoR * 0.28, seatedY - embryoR * 0.32, Math.max(0.6, embryoR * 0.24), 0, Math.PI * 2);
            ctx.fill();
          }
        }

        // Muzzle blast flash right at the socket when firing
        if (pod.lastLaunchFlash > 0) {
          const flashAlpha = Math.min(1.0, pod.lastLaunchFlash / 0.25);
          ctx.fillStyle = '#ffffff';
          ctx.globalAlpha = flashAlpha * 0.9;
          ctx.beginPath();
          ctx.arc(podX, podY - 2.5, curBaseR * 2.2, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = isFar ? '#e11d48' : '#ff003c';
          ctx.globalAlpha = (isFar ? 0.5 : 0.6) * flashAlpha;
          ctx.beginPath();
          ctx.arc(podX, podY - 2.5, curBaseR * 3.2, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1.0;
        }
      }

      // 3. Aft Levitation Exhaust Shimmer (pulses rhythmically during upward lift thrust)
      const thrusterX = drawOriginX - (drawSize * 0.38 * forwardDir);
      const thrusterY = drawOriginY - (drawSize * 0.04);
      // Lift heave impulse: peaks during upward propulsion
      const thrusterHeave = Math.max(0, -Math.cos(phase));
      const flamePulse = (0.7 + 0.3 * Math.sin(t * 18 + unit.wobbleOffset)) * (1.0 + 0.45 * thrusterHeave);
      const flameWidth = (5.5 + 2.5 * thrusterHeave) * flamePulse;
      ctx.fillStyle = '#f43f5e';
      ctx.globalAlpha = (0.32 + 0.22 * thrusterHeave) * flamePulse;
      ctx.beginPath();
      ctx.ellipse(thrusterX, thrusterY, flameWidth, 2.5 + thrusterHeave * 0.8, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;

      // 4. VENTRAL INCUBATION DOME WITH EMBEDDED ELDER SUL PILOT
      // Suspended fluid dome with phase-lagging fluid inertia and visceral respiration
      const domeLagY = Math.sin(phase - 0.5) * 2.2;
      const sacCenterX = drawOriginX - (drawSize * 0.16 * forwardDir);
      const sacCenterY = drawOriginY + (drawSize * 0.14) + domeLagY;
      const sacR = drawSize * 0.20;
      const sacPulse = 0.65 + 0.35 * Math.sin(phase * 2.0);
      const sacSquashY = 1.0 - 0.05 * Math.sin(phase * 2.0);
      const sacSquashX = 1.0 + 0.05 * Math.sin(phase * 2.0);

      if (this.quality !== 'low') {
        // Translucent amniotic fluid volume (pre-baked offscreen gradient canvas)
        const sacCanvas = getVultureSacCanvas();
        if (sacCanvas) {
          ctx.save();
          ctx.translate(sacCenterX, sacCenterY);
          ctx.scale(sacSquashX, sacSquashY);
          ctx.globalAlpha = sacPulse;
          ctx.drawImage(sacCanvas, -sacR, -sacR, sacR * 2, sacR * 2);
          ctx.restore();
        } else {
          ctx.fillStyle = '#c80f2d';
          ctx.globalAlpha = 0.35 * sacPulse;
          ctx.beginPath();
          ctx.ellipse(sacCenterX, sacCenterY, sacR * sacSquashX, sacR * sacSquashY, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1.0;
        }

        // Hexagonal framework ribs across the amniotic dome surface in weathered bronze
        ctx.strokeStyle = 'rgba(180, 83, 9, 0.40)';
        ctx.lineWidth = 0.9;
        ctx.beginPath();
        ctx.arc(sacCenterX, sacCenterY, sacR * 0.92, 0.15 * Math.PI, 0.85 * Math.PI);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(sacCenterX - sacR * 0.7 * forwardDir, sacCenterY);
        ctx.lineTo(sacCenterX + sacR * 0.6 * forwardDir, sacCenterY + sacR * 0.4);
        ctx.moveTo(sacCenterX - sacR * 0.4 * forwardDir, sacCenterY - sacR * 0.5);
        ctx.lineTo(sacCenterX + sacR * 0.2 * forwardDir, sacCenterY + sacR * 0.6);
        ctx.stroke();
      }

      // EMBEDDED ELDER SUL PILOT (Die kreatur steuernde Einheit)
      // Animated drifting silhouette suspended inside the amniotic fluid
      const pilotDriftY = Math.sin(phase * 1.2 + 0.5) * 2.8;
      const pilotDriftX = Math.cos(phase * 0.9) * 1.8 * forwardDir;
      const pilotTilt = Math.sin(phase * 0.8) * 0.14 * forwardDir;
      const pilotX = sacCenterX + pilotDriftX;
      const pilotY = sacCenterY + pilotDriftY;

      ctx.save();
      ctx.translate(pilotX, pilotY);
      ctx.rotate(pilotTilt);

      // Dark organic silhouette of the curled elder pilot
      ctx.fillStyle = '#140307';
      // Head of the elder pilot
      ctx.beginPath();
      ctx.ellipse(2 * forwardDir, -4, 3.4, 2.8, 0.2 * forwardDir, 0, Math.PI * 2);
      ctx.fill();

      // Curled fetal torso & curved spinal column
      ctx.beginPath();
      ctx.ellipse(-1 * forwardDir, 1, 4.2, 5.0, -0.3 * forwardDir, 0, Math.PI * 2);
      ctx.fill();

      // Curled limbs / biomechanical neuro-plugs
      ctx.beginPath();
      ctx.ellipse(1 * forwardDir, 5, 2.8, 2.0, 0.4 * forwardDir, 0, Math.PI * 2);
      ctx.fill();

      // Neural umbilical conduits connecting the pilot's spine up into the chassis
      ctx.strokeStyle = '#2d0610';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(-3 * forwardDir, -1);
      ctx.quadraticCurveTo(-6 * forwardDir, -6, -4 * forwardDir, -10);
      ctx.stroke();

      if (this.quality !== 'low' && (!this.isMobile || this.quality === 'high' || this.quality === 'ultra')) {
        // Synaptic bio-electrical control pulse (mental control signals passing into the warmachine)
        const synapticPulse = 0.5 + 0.5 * Math.sin(t * 2.8);
        ctx.fillStyle = '#ff3c5a';
        ctx.globalAlpha = 0.45 * synapticPulse;
        ctx.beginPath();
        ctx.arc(2 * forwardDir, -4, 1.4, 0, Math.PI * 2); // Pilot cranial eye/third-eye synapse
        ctx.arc(-2 * forwardDir, 0, 1.2, 0, Math.PI * 2); // Spinal ganglia node
        ctx.fill();
        ctx.globalAlpha = 1.0;
      }
      ctx.restore();

      // 5. ARTICULATED HEAD WITH INDEPENDENT PREDATORY MOVEMENT
      // Organic neck flex, phase-shifted breathing bob, inquisitive horizontal reach, and predatory tilt
      const neckJointX = drawOriginX + ((30.5 - 23.5) / 48) * drawSize * forwardDir;
      const neckJointY = drawOriginY + ((21.5 - 23.5) / 48) * drawSize;

      // Phase-shifted breathing cadence (phase + 0.85 creates graceful counter-bob to chassis)
      const headPhase = phase + 0.85;
      const headBobY = Math.sin(headPhase) * 3.6;
      // Inquisitive forward neck drift & aggressive lunging reach when in combat
      const headReachX = (Math.cos(headPhase) * 2.4 + (unit.isEngaged ? 4.2 : 0)) * forwardDir;
      // Predatory hunting pitch: neck curves downward to fixate on prey/ground targets
      const headPitch = (Math.sin(headPhase - 0.3) * 0.08 + (unit.isEngaged ? 0.06 : 0)) * forwardDir;

      ctx.save();
      // Pivot around the neck joint with independent translation and pitch
      ctx.translate(neckJointX + headReachX, neckJointY + headBobY);
      ctx.rotate(headPitch);
      ctx.translate(-(neckJointX + headReachX), -(neckJointY + headBobY));

      // Draw Articulated Head Sprite (Elfenbein skull casing, mandibles, tusks)
      ctx.drawImage(
        headSrc,
        drawOriginX - drawSize / 2 + headReachX,
        drawOriginY - drawSize / 2 + headBobY,
        drawSize,
        drawSize
      );

      // ANIMATED MOUTH TENTACLES & VISCERAL CABLES (Attached to articulated head with progressive wave flow)
      const throatBaseX = drawOriginX + (drawSize * 0.23 * forwardDir) + headReachX;
      const throatBaseY = drawOriginY + (drawSize * 0.11) + headBobY;
      const isCombat = unit.isEngaged;
      const tentacleCombatReach = isCombat ? 4.0 * forwardDir : 0;

      // Traveling wave propagation down the tendrils (progressive phase lag from root to tip)
      const waveFreq = 3.4;
      const waveTime = t * waveFreq + unit.wobbleOffset;

      // Tentacle 1: Main front sensory tendril (reaches forward, curls with lively whipping motion)
      const t1MidWave = Math.sin(waveTime - 0.7) * 2.8;
      const t1TipWave = Math.sin(waveTime) * 4.6;
      const t1TipX = throatBaseX + (8.5 * forwardDir) + tentacleCombatReach;
      const t1TipY = throatBaseY + 13 + t1TipWave;
      const t1MidX = throatBaseX + (3.5 * forwardDir) + (isCombat ? 1.8 * forwardDir : 0);
      const t1MidY = throatBaseY + 6.5 + t1MidWave;

      ctx.strokeStyle = '#991b1b';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(throatBaseX, throatBaseY);
      ctx.quadraticCurveTo(t1MidX, t1MidY, t1TipX, t1TipY);
      ctx.stroke();

      // Tentacle 1 tip highlight (receptive sensory bead)
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(t1TipX, t1TipY, 1.2, 0, Math.PI * 2);
      ctx.fill();

      // Tentacle 2: Mid visceral feeder tendril (sinuous wave propagation)
      const t2MidWave = Math.sin(waveTime - 1.3) * 2.2;
      const t2TipWave = Math.sin(waveTime - 0.5) * 3.8;
      const t2TipX = throatBaseX + (2.5 * forwardDir) + (isCombat ? 2.2 * forwardDir : 0);
      const t2TipY = throatBaseY + 15 + t2TipWave;
      const t2MidX = throatBaseX - (0.8 * forwardDir);
      const t2MidY = throatBaseY + 7.5 + t2MidWave;

      ctx.strokeStyle = '#7f1d1d';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(throatBaseX - (2 * forwardDir), throatBaseY + 1);
      ctx.quadraticCurveTo(t2MidX, t2MidY, t2TipX, t2TipY);
      ctx.stroke();

      // Tentacle 3: Inner neural conduit cable (lagging biomechanical bronze sheath)
      const t3TipWave = Math.sin(waveTime - 1.8) * 2.8;
      const t3TipX = throatBaseX - (4.2 * forwardDir);
      const t3TipY = throatBaseY + 11 + t3TipWave;

      ctx.strokeStyle = '#451a03';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(throatBaseX - (3 * forwardDir), throatBaseY);
      ctx.quadraticCurveTo(throatBaseX - (3 * forwardDir), throatBaseY + 5.5, t3TipX, t3TipY);
      ctx.stroke();

      // OCULAR HEADLIGHT SENSORS (Attached to cranial dome)
      if (this.quality !== 'low') {
        const eyeBaseX = drawOriginX + (drawSize * 0.22 * forwardDir) + headReachX;
        const eyeBaseY = drawOriginY - (drawSize * 0.08) + headBobY;
        const eyePulse = 0.75 + 0.25 * Math.sin(t * 4.5);

        // Main lower optic
        ctx.fillStyle = '#ff0032';
        ctx.globalAlpha = 0.9 * eyePulse;
        ctx.beginPath();
        ctx.arc(eyeBaseX, eyeBaseY, 2.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(eyeBaseX - 0.7, eyeBaseY - 0.7, 1.4, 1.4);

        // Auxiliary upper optics (triangular cluster)
        ctx.fillStyle = '#ff0032';
        ctx.globalAlpha = 0.75 * eyePulse;
        ctx.beginPath();
        ctx.arc(eyeBaseX - 2.8 * forwardDir, eyeBaseY - 3.2, 1.6, 0, Math.PI * 2);
        ctx.arc(eyeBaseX + 1.2 * forwardDir, eyeBaseY - 3.4, 1.6, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(eyeBaseX - 2.8 * forwardDir - 0.5, eyeBaseY - 3.2 - 0.5, 1.0, 1.0);
        ctx.fillRect(eyeBaseX + 1.2 * forwardDir - 0.5, eyeBaseY - 3.4 - 0.5, 1.0, 1.0);
      }

      // ── COLOSSAL BACKWARD-CURVED SICKLE TUSKS / MANDIBLES (Die großen nach hinten gebogenen Zähne) ──
      // Dominant, menacing bone scythe fangs plunging down from cheek and sweeping aggressively backward
      const tuskFlex = Math.sin(phase * 1.5) * 1.4 + (isCombat ? Math.sin(t * 14) * 3.0 : 0);
      const jawBaseX = throatBaseX + (6.0 * forwardDir);
      const jawBaseY = throatBaseY - 1.0;

      // 1. Primary Outer Sickle Mandible (Massive recurved bone scythe blade)
      // Plunges forward-down then curves dramatically back towards the throat/sac
      const m1KneeX = jawBaseX + ((7.5 + (isCombat ? 3.5 : 0)) * forwardDir);
      const m1KneeY = jawBaseY + 12.0;
      const m1TipX = jawBaseX - ((10.0 + tuskFlex) * forwardDir);
      const m1TipY = jawBaseY + 18.0;

      // Layer 1: Dark bronze silhouette backing shadow (makes tusks pop against any background)
      ctx.strokeStyle = '#261002';
      ctx.lineWidth = 4.0;
      ctx.beginPath();
      ctx.moveTo(jawBaseX, jawBaseY);
      ctx.quadraticCurveTo(m1KneeX, m1KneeY, m1TipX, m1TipY);
      ctx.stroke();

      // Layer 2: Heavy antique bronze structural core
      ctx.strokeStyle = '#92400e';
      ctx.lineWidth = 2.8;
      ctx.beginPath();
      ctx.moveTo(jawBaseX, jawBaseY);
      ctx.quadraticCurveTo(m1KneeX, m1KneeY, m1TipX, m1TipY);
      ctx.stroke();

      // Layer 3: Burnished bronze bevel ridge
      ctx.strokeStyle = '#d97706';
      ctx.lineWidth = 1.9;
      ctx.beginPath();
      ctx.moveTo(jawBaseX + (0.4 * forwardDir), jawBaseY);
      ctx.quadraticCurveTo(m1KneeX + (0.6 * forwardDir), m1KneeY + 0.3, m1TipX, m1TipY);
      ctx.stroke();

      // Layer 4: Razor-sharp ivory outer blade edge
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(jawBaseX + (0.8 * forwardDir), jawBaseY);
      ctx.quadraticCurveTo(m1KneeX + (1.2 * forwardDir), m1KneeY + 0.6, m1TipX, m1TipY);
      ctx.stroke();

      // Layer 5: Brilliant needle-sharp recurved tip specular glint
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(m1TipX, m1TipY, 1.8, 0, Math.PI * 2);
      ctx.fill();

      // 2. Secondary Inner Recurved Mandible (Nested parallel inside)
      const m2KneeX = jawBaseX + ((4.5 + (isCombat ? 2.0 : 0)) * forwardDir);
      const m2KneeY = jawBaseY + 9.5;
      const m2TipX = jawBaseX - ((6.8 + tuskFlex * 0.7) * forwardDir);
      const m2TipY = jawBaseY + 14.5;

      // Dark bronze shadow backing
      ctx.strokeStyle = '#261002';
      ctx.lineWidth = 3.0;
      ctx.beginPath();
      ctx.moveTo(jawBaseX - (2.0 * forwardDir), jawBaseY + 1.0);
      ctx.quadraticCurveTo(m2KneeX, m2KneeY, m2TipX, m2TipY);
      ctx.stroke();

      // Inner bronze spine
      ctx.strokeStyle = '#b45309';
      ctx.lineWidth = 2.0;
      ctx.beginPath();
      ctx.moveTo(jawBaseX - (2.0 * forwardDir), jawBaseY + 1.0);
      ctx.quadraticCurveTo(m2KneeX, m2KneeY, m2TipX, m2TipY);
      ctx.stroke();

      // Ivory blade edge & tip
      ctx.strokeStyle = '#fde68a';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(jawBaseX - (1.5 * forwardDir), jawBaseY + 1.0);
      ctx.quadraticCurveTo(m2KneeX + (0.6 * forwardDir), m2KneeY, m2TipX, m2TipY);
      ctx.stroke();
      ctx.fillStyle = '#fde68a';
      ctx.beginPath();
      ctx.arc(m2TipX, m2TipY, 1.3, 0, Math.PI * 2);
      ctx.fill();

      // 3. Under-throat Barb / Serrated Tusk (Third recurved hook in antique bronze and ivory)
      const m3TipX = jawBaseX - (4.2 * forwardDir);
      const m3TipY = jawBaseY + 9.5;
      ctx.strokeStyle = '#261002';
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(jawBaseX - (3.5 * forwardDir), jawBaseY + 2.0);
      ctx.quadraticCurveTo(jawBaseX - (0.5 * forwardDir), jawBaseY + 6.5, m3TipX, m3TipY);
      ctx.stroke();
      ctx.strokeStyle = '#fffbeb';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(jawBaseX - (3.5 * forwardDir), jawBaseY + 2.0);
      ctx.quadraticCurveTo(jawBaseX - (0.5 * forwardDir), jawBaseY + 6.5, m3TipX, m3TipY);
      ctx.stroke();

      ctx.restore(); // restores articulated head transform

      ctx.restore(); // restores body bank/hover transform
      }
    } else if (unitType === 'LootingVulture' || unitType === 'Looting_Vulture' || unitType === 'Looting Vulture') {
      if (this.quality === 'low' || isSimpleTier) {
        // High-fidelity 4-segment undulating dragon on Low quality (0 canvas matrix transforms, 4 simple drawImage calls)
        const bodyBitmap = buildSpriteBitmap('LootingVulture_Body', renderRace, unit.team);
        const pyramidBitmap = buildSpriteBitmap('LootingVulture_Pyramid', renderRace, unit.team);
        const headBitmap = buildSpriteBitmap('LootingVulture_Head', renderRace, unit.team);
        const tailBitmap = buildSpriteBitmap('LootingVulture_Tail', renderRace, unit.team);

        const bodySrc = isHitFlashing ? bodyBitmap?.flash : bodyBitmap?.normal;
        const pyramidSrc = isHitFlashing ? pyramidBitmap?.flash : pyramidBitmap?.normal;
        const headSrc = isHitFlashing ? headBitmap?.flash : headBitmap?.normal;
        const tailSrc = isHitFlashing ? (tailBitmap?.flash || bodySrc) : (tailBitmap?.normal || bodySrc);

        const omega = 2.3;
        const wobble = unit.wobbleOffset || 0;
        const hoverY = Math.sin(t * 1.3 + wobble) * 4.0;

        // Serpentine travelling wave across the 4 segments (harmonic undulating flight without rigid stiffness)
        const waveScale = 0.55;
        const tailWaveY = (Math.sin(t * omega - 2.0 + wobble) * 11.5 + Math.sin((t * omega - 2.0 + wobble) * 2.0 - 0.6) * 3.2) * waveScale;
        const bodyWaveY = (Math.sin(t * omega - 0.4 + wobble) * 7.5 + Math.sin((t * omega - 0.4 + wobble) * 2.0 - 0.4) * 2.2) * waveScale;
        const pyramidWaveY = (Math.sin(t * omega + 0.25 + wobble) * 5.2 + Math.sin((t * omega + 0.25 + wobble) * 2.0 - 0.3) * 1.5) * waveScale;
        const headBobY = (Math.sin(t * omega + 0.8 + wobble) * 4.6 + Math.sin((t * omega + 0.8 + wobble) * 2.0 - 0.2) * 1.2) * waveScale;

        const tailShiftX = -drawSize * 0.30 * forwardDir;
        const pyramidShiftX = drawSize * 0.18 * forwardDir;
        const headShiftX = drawSize * 0.22 * forwardDir;
        const baseY = renderY + hoverY - drawSize / 2;

        if (tailSrc) ctx.drawImage(tailSrc, renderX - drawSize / 2 + tailShiftX, baseY + tailWaveY, drawSize, drawSize);
        if (bodySrc) ctx.drawImage(bodySrc, renderX - drawSize / 2, baseY + bodyWaveY, drawSize, drawSize);
        if (pyramidSrc) ctx.drawImage(pyramidSrc, renderX - drawSize / 2 + pyramidShiftX, baseY + pyramidWaveY + drawSize * 0.04, drawSize, drawSize);
        if (headSrc) ctx.drawImage(headSrc, renderX - drawSize / 2 + headShiftX, baseY + bodyWaveY + headBobY, drawSize, drawSize);
      } else {
      // 🐉 SUL LOOTING VULTURE CELESTIAL DRAGON TITAN (Facing forwardDir)
      // Inspired by Zelda: Tears of the Kingdom sky dragons (Naydra, Dinraal, Farosh, Light Dragon)
      const bodyBitmap = buildSpriteBitmap('LootingVulture_Body', renderRace, unit.team);
      const pyramidBitmap = buildSpriteBitmap('LootingVulture_Pyramid', renderRace, unit.team);
      const headBitmap = buildSpriteBitmap('LootingVulture_Head', renderRace, unit.team);
      const tailBitmap = buildSpriteBitmap('LootingVulture_Tail', renderRace, unit.team);

      const bodySrc = isHitFlashing ? bodyBitmap.flash : bodyBitmap.normal;
      const pyramidSrc = isHitFlashing ? pyramidBitmap.flash : pyramidBitmap.normal;
      const headSrc = isHitFlashing ? headBitmap.flash : headBitmap.normal;
      const tailSrc = isHitFlashing ? (tailBitmap?.flash || bodySrc) : (tailBitmap?.normal || bodySrc);

      // 1. Celestial Leviathan Levitation Dynamics (Low base frequency for immense scale)
      const hoverY = Math.sin(t * 1.3 + unit.wobbleOffset) * 4.5;
      const flightBank = (unit.speed > 0 ? 0.05 * forwardDir : 0) + Math.sin(t * 1.1 + unit.wobbleOffset) * 0.02 * forwardDir;

      ctx.save();
      ctx.translate(renderX, renderY + hoverY);
      ctx.rotate(flightBank);
      ctx.translate(-renderX, -(renderY + hoverY));

      // ── TRAVELLING TRANSVERSE SINE WAVE KINEMATICS (Majestic Undulating Celestial Dragon) ──
      const omega = 2.3;
      // Horizontal segment offsets: stretches the dragon's length horizontally while keeping square pixel fidelity
      const tailShiftX = -drawSize * 0.30 * forwardDir;
      const pyramidShiftX = drawSize * 0.18 * forwardDir;
      const headShiftX = drawSize * 0.22 * forwardDir;
      const pyramidScale = 0.78; // Sleeker, more proportional cockscomb pyramid

      // Dynamic Sweeping Wave Phases across the 115px dragon body (spanning ~2.8 radians S-curve):
      // Tail (-2.0 rad) -> Pelvis (-1.1 rad) -> Body (-0.4 rad) -> Pyramid (+0.25 rad) -> Head (+0.8 rad)
      const tailWavePhase = t * omega - 2.0 + unit.wobbleOffset;
      const tailSurgeX = Math.cos(tailWavePhase) * 4.5 * forwardDir;
      const tailWaveY = Math.sin(tailWavePhase) * 11.5 + Math.sin(tailWavePhase * 2.0 - 0.6) * 3.2;
      const tailPitch = (Math.cos(tailWavePhase) * 0.28 + Math.cos(tailWavePhase * 2.0) * 0.08) * forwardDir;

      const bodyWavePhase = t * omega - 0.4 + unit.wobbleOffset;
      const bodySurgeX = Math.cos(bodyWavePhase) * 2.8 * forwardDir;
      const bodyWaveY = Math.sin(bodyWavePhase) * 7.5 + Math.sin(bodyWavePhase * 2.0 - 0.4) * 2.2;
      const bodyPitch = (Math.cos(bodyWavePhase) * 0.16 + Math.cos(bodyWavePhase * 2.0) * 0.04) * forwardDir;

      const pyramidWavePhase = t * omega + 0.25 + unit.wobbleOffset;
      const pyramidSurgeX = Math.cos(pyramidWavePhase) * 1.8 * forwardDir;
      const pyramidWaveY = Math.sin(pyramidWavePhase) * 5.2 + Math.sin(pyramidWavePhase * 2.0 - 0.3) * 1.5;
      const pyramidPitch = (Math.cos(pyramidWavePhase) * 0.12 + Math.cos(pyramidWavePhase * 2.0) * 0.03) * forwardDir;

      const headLeadPhase = t * omega + 0.8 + unit.wobbleOffset;
      const headSurgeX = Math.cos(headLeadPhase) * 1.4 * forwardDir;
      const headBobY = Math.sin(headLeadPhase) * 4.6 + Math.sin(headLeadPhase * 2.0 - 0.2) * 1.2;
      let headPitch = (Math.cos(headLeadPhase) * 0.14 + Math.cos(headLeadPhase * 2.0) * 0.035) * forwardDir;

      // A. Trailing Caudal S-Curve Tail (Phase delayed: omega * t - 2.0)
      ctx.save();
      // Pivot around pelvic junction (col 10 in tail frame, shifted backward)
      const tailPivotX = renderX + ((10 - 11.5) / 24) * drawSize * forwardDir + tailShiftX + tailSurgeX;
      const tailPivotY = (renderY + hoverY) + ((10 - 11.5) / 24) * drawSize;
      ctx.translate(tailPivotX, tailPivotY + tailWaveY);
      ctx.rotate(tailPitch);
      ctx.translate(-tailPivotX, -(tailPivotY + tailWaveY));

      ctx.drawImage(
        tailSrc,
        renderX - drawSize / 2 + tailShiftX + tailSurgeX,
        (renderY + hoverY + tailWaveY) - drawSize / 2,
        drawSize,
        drawSize
      );

      // Ethereal Caudal Fin Ray Tendrils (Fluid whip trailing off tail fin tips in the slipstream)
      const finTipX = renderX + ((1 - 11.5) / 24) * drawSize * forwardDir + tailShiftX + tailSurgeX;
      const finTipY = (renderY + hoverY + tailWaveY) + ((8 - 11.5) / 24) * drawSize;
      const finWhip = Math.sin(t * 3.4 + unit.wobbleOffset) * 4.5;
      ctx.strokeStyle = 'rgba(253, 224, 71, 0.85)';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(finTipX, finTipY);
      ctx.bezierCurveTo(
        finTipX - 10 * forwardDir, finTipY + finWhip * 0.4,
        finTipX - 18 * forwardDir, finTipY - finWhip * 0.6,
        finTipX - 28 * forwardDir, finTipY + finWhip
      );
      ctx.stroke();

      ctx.restore();

      // B. Serpentine Body Chassis (Center Segment: Phase omega * t - 0.4)
      ctx.save();
      ctx.translate(renderX + bodySurgeX, renderY + hoverY + bodyWaveY);
      ctx.rotate(bodyPitch);
      ctx.translate(-(renderX + bodySurgeX), -(renderY + hoverY + bodyWaveY));

      // Living Muscular Pelvis & Tail Bridge (connects body's pelvic arch to trailing tail)
      ctx.strokeStyle = '#1c1917';
      ctx.lineWidth = drawSize * 0.13;
      ctx.beginPath();
      const bodyPelvisX = renderX + ((5.5 - 11.5) / 24) * drawSize * forwardDir + bodySurgeX;
      const bodyPelvisY = renderY + hoverY + bodyWaveY + ((11 - 11.5) / 24) * drawSize;
      ctx.moveTo(bodyPelvisX, bodyPelvisY);
      ctx.quadraticCurveTo(
        (bodyPelvisX + tailPivotX) / 2,
        Math.min(bodyPelvisY, tailPivotY + tailWaveY) - 2.5,
        tailPivotX,
        tailPivotY + tailWaveY
      );
      ctx.stroke();

      // Gold Sinew Thread on Pelvis Bridge
      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(bodyPelvisX, bodyPelvisY - 0.5);
      ctx.quadraticCurveTo(
        (bodyPelvisX + tailPivotX) / 2,
        Math.min(bodyPelvisY, tailPivotY + tailWaveY) - 3.5,
        tailPivotX,
        tailPivotY + tailWaveY - 0.5
      );
      ctx.stroke();

      // Golden Vertebrae Spines along Pelvic Bridge
      for (let v = 1; v <= 3; v++) {
        const u = v / 4;
        const vx = bodyPelvisX + (tailPivotX - bodyPelvisX) * u;
        const vy = bodyPelvisY + (tailPivotY + tailWaveY - bodyPelvisY) * u - 2.5;
        ctx.fillStyle = '#fde047';
        ctx.fillRect(vx - 0.9, vy - 0.9, 1.8, 1.8);
      }

      // 1. Serpentine Body Chassis (Chest, Belly Dip, Dorsal Vertebrae with Coarse Fur)
      ctx.drawImage(
        bodySrc,
        renderX - drawSize / 2 + bodySurgeX,
        (renderY + hoverY + bodyWaveY) - drawSize / 2,
        drawSize,
        drawSize
      );

      // 2. Coarse Fibrous Mane Tufts along Dorsal Crest (Wild hairy mane from 1_lootingvulture.png)
      for (let m = 0; m < 5; m++) {
        const mu = m / 4;
        const mgx = 6.5 + mu * 9.5; // cols 6.5..16
        const mBaseX = renderX + ((mgx - 11.5) / 24) * drawSize * forwardDir + bodySurgeX;
        const mRowY = 8.2 - Math.sin(mu * Math.PI) * 1.6;
        const mBaseY = renderY + hoverY + bodyWaveY + ((mRowY - 11.5) / 24) * drawSize;

        // Wind-swept coarse mane bristles fluttering with phase lag
        const mFlutter = Math.sin(t * 4.2 - m * 0.75 + unit.wobbleOffset) * 3.5;
        const mLen = 8 + (m % 2) * 4;
        const mTipX = mBaseX - (mLen * forwardDir);
        const mTipY = mBaseY - 6 + mFlutter;

        // Dark charcoal coarse underfur bristle
        ctx.strokeStyle = '#1c1917';
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        ctx.moveTo(mBaseX, mBaseY);
        ctx.quadraticCurveTo(mBaseX - (mLen * 0.45 * forwardDir), mBaseY - 5, mTipX, mTipY);
        ctx.stroke();

        // Shaggy amber/gold wiry hair strand
        ctx.strokeStyle = (m % 2 === 0 ? '#fde047' : '#ca8a04');
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(mBaseX + 0.5 * forwardDir, mBaseY);
        ctx.quadraticCurveTo(mBaseX - (mLen * 0.45 * forwardDir) + 0.5, mBaseY - 5.5, mTipX + 1.0 * forwardDir, mTipY - 0.5);
        ctx.stroke();
      }

      // 3. Shaggy Fibrous Fur Fringes hanging from Belly Dip (trailing in the slipstream)
      for (let b = 0; b < 4; b++) {
        const bu = b / 3;
        const bgx = 7.5 + bu * 6.5; // cols 7.5..14
        const bBaseX = renderX + ((bgx - 11.5) / 24) * drawSize * forwardDir + bodySurgeX;
        const bRowY = 14.5 + Math.sin(bu * Math.PI) * 1.5;
        const bBaseY = renderY + hoverY + bodyWaveY + ((bRowY - 11.5) / 24) * drawSize;

        const bSway = Math.sin(t * 3.6 - b * 0.9 + unit.wobbleOffset) * 3.2;
        const bLen = 9 + (b % 2) * 5;
        const bTipX = bBaseX - (bLen * 1.2 * forwardDir);
        const bTipY = bBaseY + 6 + bSway;

        ctx.strokeStyle = '#292524';
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(bBaseX, bBaseY);
        ctx.quadraticCurveTo(bBaseX - (bLen * 0.5 * forwardDir), bBaseY + 3, bTipX, bTipY);
        ctx.stroke();

        ctx.strokeStyle = '#eab308';
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        ctx.moveTo(bBaseX, bBaseY);
        ctx.quadraticCurveTo(bBaseX - (bLen * 0.5 * forwardDir), bBaseY + 3.5, bTipX, bTipY);
        ctx.stroke();
      }

      // 4. Iconic Golden Sand Cascade: glittering golden dust continuously raining from shaggy belly fur
      const dustPhase = (t * 1.8 + unit.wobbleOffset) % 1.0;
      for (let d = 0; d < 4; d++) {
        const dOffset = (dustPhase + d * 0.25) % 1.0;
        const dSpanX = renderX - ((4 + dOffset * 18) * forwardDir) + bodySurgeX;
        const dSpanY = (renderY + hoverY + bodyWaveY) + 14 + (dOffset * 22);
        const dAlpha = (1.0 - dOffset) * 0.85;
        ctx.fillStyle = '#fef08a';
        ctx.globalAlpha = dAlpha;
        ctx.fillRect(dSpanX - 0.7, dSpanY - 0.7, 1.4, 1.4);
      }
      ctx.globalAlpha = 1.0;

      ctx.restore(); // restores body transform

      // C. Living Muscular Ventral Throat Ridge (runs smoothly under pyramid from chest to head throat)
      const bodyChestX = renderX + ((16.5 - 11.5) / 24) * drawSize * forwardDir + bodySurgeX;
      const bodyChestY = renderY + hoverY + bodyWaveY + ((11.5 - 11.5) / 24) * drawSize;
      const headThroatX = renderX + ((14.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
      const headThroatY = (renderY + hoverY + bodyWaveY + headBobY) + ((12 - 11.5) / 24) * drawSize;

      ctx.strokeStyle = '#1c1917';
      ctx.lineWidth = drawSize * 0.08;
      ctx.beginPath();
      ctx.moveTo(bodyChestX, bodyChestY);
      ctx.quadraticCurveTo(
        (bodyChestX + headThroatX) / 2,
        Math.max(bodyChestY, headThroatY) + 1.2,
        headThroatX,
        headThroatY
      );
      ctx.stroke();

      // Gold Sinew Thread on Ventral Throat
      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(bodyChestX, bodyChestY + 0.8);
      ctx.quadraticCurveTo(
        (bodyChestX + headThroatX) / 2,
        Math.max(bodyChestY, headThroatY) + 2.0,
        headThroatX,
        headThroatY + 0.8
      );
      ctx.stroke();

      // D. Fused 3D Megalithic Pyramid (Cervical Nape Crest directly behind head - scaled sleeker)
      const pyramidPivotX = renderX + ((13.5 - 11.5) / 24) * drawSize * forwardDir + pyramidShiftX + pyramidSurgeX;
      const pyramidPivotY = (renderY + hoverY + pyramidWaveY) + ((9 - 11.5) / 24) * drawSize;

      ctx.save();
      ctx.translate(pyramidPivotX, pyramidPivotY);
      ctx.rotate(pyramidPitch);
      ctx.scale(pyramidScale, pyramidScale);
      ctx.translate(-pyramidPivotX, -pyramidPivotY);

      ctx.drawImage(
        pyramidSrc,
        renderX - drawSize / 2 + pyramidShiftX + pyramidSurgeX,
        (renderY + hoverY + pyramidWaveY) - drawSize / 2,
        drawSize,
        drawSize
      );
      ctx.restore();

      // E. Autonomous Articulated Dragon Skull (Forward-Reaching Head: Phase omega * t + 0.8)
      const neckJointX = renderX + ((14 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
      const neckJointY = (renderY + hoverY + bodyWaveY) + ((10 - 11.5) / 24) * drawSize;

      // Dynamic Predatory Gaze Tracking: angle head toward target when in range/combat
      if (unit.target && !unit.target.isDead) {
        const dx = (unit.target.x - unit.x) * forwardDir;
        const dy = unit.target.y - unit.y;
        if (dx > 0) {
          const aimAngle = Math.atan2(dy, dx);
          // Clamp to realistic draconic neck pitch range (-22 deg to +22 deg)
          const clampedAim = Math.max(-0.38, Math.min(0.38, aimAngle));
          headPitch += clampedAim * forwardDir;
        }
      }

      ctx.save();
      ctx.translate(neckJointX, neckJointY + headBobY);
      ctx.rotate(headPitch);
      ctx.translate(-neckJointX, -(neckJointY + headBobY));

      ctx.drawImage(
        headSrc,
        renderX - drawSize / 2 + headShiftX + headSurgeX,
        (renderY + hoverY + bodyWaveY + headBobY) - drawSize / 2,
        drawSize,
        drawSize
      );

      // Flowing Celestial Whisker Barbels (Trailing with dual-control cubic bezier ribbon physics)
      const chinX = renderX + ((18.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
      const chinY = (renderY + hoverY + bodyWaveY + headBobY) + ((13.5 - 11.5) / 24) * drawSize;
      const flightSpeedFactor = unit.speed > 0 ? 1.4 : 1.0;

      // Barbel 1: Upper Golden Whisker Filament (Cubic ribbon with tip vortex wave)
      const b1WaveA = Math.sin(t * 3.6 + unit.wobbleOffset) * 2.6;
      const b1WaveB = Math.cos(t * 7.2 + unit.wobbleOffset) * 1.6;
      const b1TipX = chinX - (18 * forwardDir * flightSpeedFactor);
      const b1TipY = chinY + 10 + b1WaveA + b1WaveB;
      const b1Ctrl1X = chinX - (5 * forwardDir);
      const b1Ctrl1Y = chinY + 11 + b1WaveA * 0.5;
      const b1Ctrl2X = chinX - (12 * forwardDir * flightSpeedFactor);
      const b1Ctrl2Y = chinY + 6 - b1WaveA * 0.4;

      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(chinX, chinY);
      ctx.bezierCurveTo(b1Ctrl1X, b1Ctrl1Y, b1Ctrl2X, b1Ctrl2Y, b1TipX, b1TipY);
      ctx.stroke();

      // Specular golden pearl at whisker tip
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(b1TipX, b1TipY, 1.0, 0, Math.PI * 2);
      ctx.fill();

      // Barbel 2: Lower Chin Beard Filament (Interwoven counter-flutter)
      const b2WaveA = Math.sin(t * 4.2 + 1.2 + unit.wobbleOffset) * 2.2;
      const b2TipX = chinX - (13 * forwardDir * flightSpeedFactor);
      const b2TipY = chinY + 14 + b2WaveA;
      const b2Ctrl1X = chinX - (3 * forwardDir);
      const b2Ctrl1Y = chinY + 8;
      const b2Ctrl2X = chinX - (8 * forwardDir);
      const b2Ctrl2Y = chinY + 12 + b2WaveA * 0.6;

      ctx.strokeStyle = '#eab308';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(chinX - (1 * forwardDir), chinY + 1);
      ctx.bezierCurveTo(b2Ctrl1X, b2Ctrl1Y, b2Ctrl2X, b2Ctrl2Y, b2TipX, b2TipY);
      ctx.stroke();

      // Upturned Ivory Rostral Horn Tip Specular Gleam
      const hornTipX = renderX + ((23.0 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
      const hornTipY = (renderY + hoverY + bodyWaveY + headBobY) + ((9.0 - 11.5) / 24) * drawSize;
      const hornGlint = 0.5 + 0.5 * Math.sin(t * 3.5 + 0.8);
      ctx.fillStyle = '#ffffff';
      ctx.globalAlpha = 0.4 + 0.5 * hornGlint;
      ctx.beginPath();
      ctx.arc(hornTipX, hornTipY, 0.8 * (0.8 + 0.4 * hornGlint), 0, Math.PI * 2);
      ctx.fill();

      // Glowing Amber Compound Eyes Glints (Cluster across rows 6..9)
      const eyeShimmer = 0.6 + 0.4 * Math.sin(t * 4.2 + 1.2);
      const eye1X = renderX + ((16.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
      const eye1Y = (renderY + hoverY + bodyWaveY + headBobY) + ((6.8 - 11.5) / 24) * drawSize;
      const eye2X = renderX + ((18.2 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
      const eye2Y = (renderY + hoverY + bodyWaveY + headBobY) + ((7.8 - 11.5) / 24) * drawSize;
      ctx.fillStyle = '#ffffff';
      ctx.globalAlpha = 0.75 * eyeShimmer;
      ctx.beginPath();
      ctx.arc(eye1X, eye1Y, 0.65, 0, Math.PI * 2);
      ctx.arc(eye2X, eye2Y, 0.65, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;

      // Artwork-accurate Golden Sand Cascade pouring downwards from parted jaws into slipstream
      const mawX = renderX + ((19.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
      const mawY = (renderY + hoverY + bodyWaveY + headBobY) + ((12.0 - 11.5) / 24) * drawSize;
      const sandPal = LOOTING_VULTURE_SAND_PALETTE;
      for (let sIdx = 0; sIdx < 5; sIdx++) {
        const sPhase = (t * 2.6 + sIdx * 0.2 + unit.wobbleOffset) % 1.0;
        const sAlpha = Math.sin(sPhase * Math.PI) * 0.85;
        const dropDist = sPhase * 16.0;
        const driftBack = (sPhase * sPhase * 10.0 + Math.sin(sPhase * 6.0 + sIdx) * 1.5) * forwardDir * flightSpeedFactor;
        const sx = mawX - driftBack;
        const sy = mawY + dropDist;
        const sRadius = sIdx === 4 ? 0.7 : (sIdx % 2 === 0 ? 1.0 : 0.8);
        ctx.fillStyle = sandPal[sIdx % sandPal.length];
        ctx.globalAlpha = sAlpha;
        ctx.beginPath();
        ctx.arc(sx, sy, sRadius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1.0;

      ctx.restore(); // restores articulated head transform

      // F. Radiant Solar Apex Star on 3D Pyramid Capstone (scaled to sleeker apex)
      const scaledApexDistY = ((0.5 - 9) / 24) * drawSize * pyramidScale;
      const cosP = Math.cos(pyramidPitch);
      const sinP = Math.sin(pyramidPitch);
      const apexPx = pyramidPivotX - scaledApexDistY * sinP;
      const apexPy = pyramidPivotY + scaledApexDistY * cosP;

      // Radiant solar flare / corona (quality-gated)
      if ((unit._lootingAttackFlash || 0) > 0) {
        unit._lootingAttackFlash = Math.max(0, unit._lootingAttackFlash - 0.025);
      }
      if (this.quality !== 'low') {
        const isAttackingFlare = (unit._lootingAttackFlash || 0) > 0;
        const flarePulse = (isAttackingFlare ? 1.5 : (0.75 + 0.25 * Math.sin(t * 4.5 + unit.wobbleOffset)));
        const flareR = drawSize * 0.11 * flarePulse * pyramidScale;

        const flareLUT = getVultureSolarFlareLUT();
        if (flareLUT) {
          ctx.drawImage(flareLUT, apexPx - flareR, apexPy - flareR, flareR * 2, flareR * 2);
        }

        // 4-Point Anamorphic Star Cross
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(apexPx - flareR * 1.1, apexPy);
        ctx.lineTo(apexPx + flareR * 1.1, apexPy);
        ctx.moveTo(apexPx, apexPy - flareR * 1.1);
        ctx.lineTo(apexPx, apexPy + flareR * 1.1);
        ctx.stroke();

        // Secondary diagonal glints
        ctx.strokeStyle = 'rgba(254, 240, 138, 0.6)';
        ctx.lineWidth = 0.8;
        const diagR = flareR * 0.6;
        ctx.beginPath();
        ctx.moveTo(apexPx - diagR, apexPy - diagR);
        ctx.lineTo(apexPx + diagR, apexPy + diagR);
        ctx.moveTo(apexPx - diagR, apexPy + diagR);
        ctx.lineTo(apexPx + diagR, apexPy - diagR);
        ctx.stroke();

        // Ambient Shimmering Gold Dust vortices shedding into the slipstream
        const shimmerPhase = (t * 2.5 + unit.wobbleOffset) % 1.0;
        for (let s = 0; s < 3; s++) {
          const sOffset = (shimmerPhase + s * 0.33) % 1.0;
          const vortexSpin = Math.sin(sOffset * Math.PI * 3.0) * 3.0;
          const sx = renderX - ((6 + sOffset * 22) * forwardDir) + pyramidShiftX + pyramidSurgeX;
          const sy = (renderY + hoverY + pyramidWaveY) + 6 + (sOffset * 16) + vortexSpin;
          const sAlpha = (1.0 - sOffset) * 0.70;
          ctx.fillStyle = '#fef08a';
          ctx.globalAlpha = sAlpha;
          ctx.fillRect(sx - 0.7, sy - 0.7, 1.4, 1.4);
        }
        ctx.globalAlpha = 1.0;
      }

      ctx.restore();
      }
    } else if (unitType === 'ParasiteVulture' || unitType === 'Parasite_Vulture' || unitType === 'Parasite Vulture') {
      const hasParasite = unit.hasParasite !== false;
      if (this.quality === 'low' || isSimpleTier) {
        const tFlight = t + (unit.wobbleOffset || 0);
        let hoverY = 0;
        let flapIdx = 1; // 0: upstroke, 1: neutral/glide, 2: downstroke
        if (hasParasite) {
          const tau = ((tFlight % 4.80) + 4.80) % 4.80;
          if (tau < 1.38) {
            const flapSin = Math.sin((tau / 0.46) * Math.PI * 2);
            hoverY = flapSin * 3.2;
            flapIdx = flapSin > 0.35 ? 2 : (flapSin < -0.35 ? 0 : 1);
          } else if (tau < 2.60) {
            const glideT = (tau - 1.38) / 1.22;
            hoverY = -2.0 - Math.sin(glideT * Math.PI) * 1.6;
            flapIdx = 1;
          } else if (tau < 3.52) {
            const flapSin = Math.sin(((tau - 2.60) / 0.46) * Math.PI * 2);
            hoverY = flapSin * 3.2;
            flapIdx = flapSin > 0.35 ? 2 : (flapSin < -0.35 ? 0 : 1);
          } else {
            const glideT = (tau - 3.52) / 1.28;
            hoverY = -2.0 - Math.sin(glideT * Math.PI) * 1.6;
            flapIdx = 1;
          }
        } else {
          const tau = ((tFlight % 3.60) + 3.60) % 3.60;
          if (tau < 1.26) {
            const flapSin = Math.sin((tau / 0.42) * Math.PI * 2);
            hoverY = flapSin * 2.8;
            flapIdx = flapSin > 0.35 ? 2 : (flapSin < -0.35 ? 0 : 1);
          } else if (tau < 2.05) {
            const glideT = (tau - 1.26) / 0.79;
            hoverY = -1.6 - Math.sin(glideT * Math.PI) * 1.2;
            flapIdx = 1;
          } else if (tau < 2.89) {
            const flapSin = Math.sin(((tau - 2.05) / 0.42) * Math.PI * 2);
            hoverY = flapSin * 2.8;
            flapIdx = flapSin > 0.35 ? 2 : (flapSin < -0.35 ? 0 : 1);
          } else {
            const glideT = (tau - 2.89) / 0.71;
            hoverY = -1.6 - Math.sin(glideT * Math.PI) * 1.2;
            flapIdx = 1;
          }
        }
        const compList = hasParasite ? PARASITE_VULTURE_FLAP_COMPOSITES : PARASITE_VULTURE_EMPTY_FLAP_COMPOSITES;
        const vultureKey = compList[flapIdx] || (hasParasite ? 'ParasiteVulture' : 'ParasiteVulture_Empty');
        const vultureBmp = buildSpriteBitmap(vultureKey, renderRace, unit.team);
        const vultureSrc = isHitFlashing ? vultureBmp?.flash : vultureBmp?.normal;
        if (vultureSrc) {
          ctx.drawImage(vultureSrc, renderX - drawSize / 2, renderY + hoverY - drawSize / 2, drawSize, drawSize);
        }
      } else {
      // 🦅 SUL PARASITE VULTURE (Compact Agile Raptor with Majestic Soaring Wingspan)
      const isInsane = !!unit.isInsane;
      const bodyKey = hasParasite ? 'ParasiteVulture_Body' : 'ParasiteVulture_Body_Empty';
      const wingKey = hasParasite ? 'ParasiteVulture_Wing' : 'ParasiteVulture_Wing_Injured';
      const headKey = hasParasite ? 'ParasiteVulture_Head' : 'ParasiteVulture_Head_Injured';

      const bodyBitmap = buildSpriteBitmap(bodyKey, renderRace, unit.team);
      const wingBitmap = buildSpriteBitmap(wingKey, renderRace, unit.team);
      const headBitmap = buildSpriteBitmap(headKey, renderRace, unit.team);
      const parasiteBitmap = hasParasite ? buildSpriteBitmap('ParasiteVulture_Parasite', renderRace, unit.team) : null;

      const bodySrc = isHitFlashing ? bodyBitmap.flash : bodyBitmap.normal;
      const wingSrc = isHitFlashing ? (wingBitmap?.flash || bodySrc) : (wingBitmap?.normal || bodySrc);
      const headSrc = isHitFlashing ? headBitmap.flash : headBitmap.normal;
      const parasiteSrc = hasParasite ? (isHitFlashing ? parasiteBitmap.flash : parasiteBitmap.normal) : null;

      // 🦅 AVIAN RAPTOR FLAP-GLIDING KINEMATICS (3 Flaps -> Glide -> 2 Flaps -> Glide)
      // Natural Undulating Flight Pattern for Soaring Eagles & Condors:
      // Zero rowing motion: stroke is 100% vertical along the Y-axis (scaleY), with a steady backward sweep.
      let hoverY = 0;
      let nearScaleY = 0.22;
      let farScaleY = 0.20;
      let nearWingAngle = -0.16 * forwardDir;
      let farWingAngle = -0.28 * forwardDir;

      const tFlight = t + unit.wobbleOffset;

      if (hasParasite) {
        // ══════════════════════════════════════════════════════════════
        // 1. INFESTED VULTURE: Sovereign Undulating Flight (3 Beats -> Glide -> 2 Beats -> Glide)
        // ══════════════════════════════════════════════════════════════
        const cycleDuration = 4.80; // Total super-cycle (seconds)
        const tau = ((tFlight % cycleDuration) + cycleDuration) % cycleDuration;
        const beatDur = 0.46; // Duration of one full eagle wingbeat

        if (tau < 1.38) {
          // ── Segment A: 3 Powerful Downward/Upward Wingbeats (0.00s to 1.38s) ──
          const beatPhase = (tau / beatDur) * Math.PI * 2;
          const flapSin = Math.sin(beatPhase);
          const flapCos = Math.cos(beatPhase);
          const strokeSign = flapSin >= 0 ? 1 : -1;

          nearScaleY = strokeSign * Math.max(0.08, Math.abs(flapSin) * 1.10);
          nearWingAngle = (-0.16 - flapCos * 0.04) * forwardDir;

          const farSin = Math.sin(beatPhase - 0.12);
          farScaleY = (farSin >= 0 ? 1 : -1) * Math.max(0.08, Math.abs(farSin) * 0.96);
          farWingAngle = (-0.28 - flapCos * 0.04) * forwardDir;

          hoverY = flapSin * 3.2; // Lifts on downstroke, sinks on upstroke
        } else if (tau < 2.60) {
          // ── Segment B: Majestic Outstretched Soaring Glide (1.38s to 2.60s = 1.22s) ──
          const glideT = (tau - 1.38) / 1.22;
          const blendIn = Math.min(1.0, (tau - 1.38) / 0.18);
          const targetNear = 0.24 + Math.sin(glideT * Math.PI) * 0.05;
          const targetFar = 0.21 + Math.sin(glideT * Math.PI) * 0.04;

          nearScaleY = 0.08 + blendIn * (targetNear - 0.08);
          farScaleY = 0.08 + blendIn * (targetFar - 0.08);
          nearWingAngle = -0.16 * forwardDir;
          farWingAngle = -0.28 * forwardDir;

          hoverY = -2.0 - Math.sin(glideT * Math.PI) * 1.6; // Gentle thermal draft lift
        } else if (tau < 3.52) {
          // ── Segment C: 2 Powerful Wingbeats (2.60s to 3.52s = 0.92s) ──
          const beatPhase = ((tau - 2.60) / beatDur) * Math.PI * 2;
          const flapSin = Math.sin(beatPhase);
          const flapCos = Math.cos(beatPhase);
          const strokeSign = flapSin >= 0 ? 1 : -1;

          nearScaleY = strokeSign * Math.max(0.08, Math.abs(flapSin) * 1.10);
          nearWingAngle = (-0.16 - flapCos * 0.04) * forwardDir;

          const farSin = Math.sin(beatPhase - 0.12);
          farScaleY = (farSin >= 0 ? 1 : -1) * Math.max(0.08, Math.abs(farSin) * 0.96);
          farWingAngle = (-0.28 - flapCos * 0.04) * forwardDir;

          hoverY = flapSin * 3.2;
        } else {
          // ── Segment D: Majestic Outstretched Soaring Glide (3.52s to 4.80s = 1.28s) ──
          const glideT = (tau - 3.52) / 1.28;
          const blendIn = Math.min(1.0, (tau - 3.52) / 0.18);
          const targetNear = 0.24 + Math.sin(glideT * Math.PI) * 0.05;
          const targetFar = 0.21 + Math.sin(glideT * Math.PI) * 0.04;

          nearScaleY = 0.08 + blendIn * (targetNear - 0.08);
          farScaleY = 0.08 + blendIn * (targetFar - 0.08);
          nearWingAngle = -0.16 * forwardDir;
          farWingAngle = -0.28 * forwardDir;

          hoverY = -2.0 - Math.sin(glideT * Math.PI) * 1.6;
        }
      } else {
        // ══════════════════════════════════════════════════════════════
        // 2. INJURED VULTURE: Wounded Raptor Flight (Pure Vertical Wingbeats, ZERO Rowing)
        // ══════════════════════════════════════════════════════════════
        // Labored, rhythmic vertical stroke: 3 strained beats -> brief wounded glide -> 2 beats -> glide
        const cycleDuration = 3.60;
        const tau = ((tFlight % cycleDuration) + cycleDuration) % cycleDuration;
        const beatDur = 0.42;

        if (tau < 1.26) {
          // 3 strained, heavy vertical wingbeats (0.00s to 1.26s)
          const beatPhase = (tau / beatDur) * Math.PI * 2;
          const flapSin = Math.sin(beatPhase);
          const flapCos = Math.cos(beatPhase);
          const strokeSign = flapSin >= 0 ? 1 : -1;

          nearScaleY = strokeSign * Math.max(0.08, Math.abs(flapSin) * 1.05);
          nearWingAngle = (-0.16 - flapCos * 0.03) * forwardDir;

          const farSin = Math.sin(beatPhase - 0.10);
          farScaleY = (farSin >= 0 ? 1 : -1) * Math.max(0.08, Math.abs(farSin) * 0.92);
          farWingAngle = (-0.28 - flapCos * 0.03) * forwardDir;

          hoverY = flapSin * 2.8;
        } else if (tau < 2.05) {
          // Brief wounded glide / thermal falter (1.26s to 2.05s = 0.79s)
          const glideT = (tau - 1.26) / 0.79;
          const blendIn = Math.min(1.0, (tau - 1.26) / 0.15);
          const targetNear = 0.22 + Math.sin(glideT * Math.PI) * 0.04;
          const targetFar = 0.19 + Math.sin(glideT * Math.PI) * 0.03;

          nearScaleY = 0.08 + blendIn * (targetNear - 0.08);
          farScaleY = 0.08 + blendIn * (targetFar - 0.08);
          nearWingAngle = -0.16 * forwardDir;
          farWingAngle = -0.28 * forwardDir;

          hoverY = -1.2 + Math.sin(glideT * Math.PI * 2.0) * 0.8;
        } else if (tau < 2.89) {
          // 2 strained vertical wingbeats (2.05s to 2.89s = 0.84s)
          const beatPhase = ((tau - 2.05) / beatDur) * Math.PI * 2;
          const flapSin = Math.sin(beatPhase);
          const flapCos = Math.cos(beatPhase);
          const strokeSign = flapSin >= 0 ? 1 : -1;

          nearScaleY = strokeSign * Math.max(0.08, Math.abs(flapSin) * 1.05);
          nearWingAngle = (-0.16 - flapCos * 0.03) * forwardDir;

          const farSin = Math.sin(beatPhase - 0.10);
          farScaleY = (farSin >= 0 ? 1 : -1) * Math.max(0.08, Math.abs(farSin) * 0.92);
          farWingAngle = (-0.28 - flapCos * 0.03) * forwardDir;

          hoverY = flapSin * 2.8;
        } else {
          // Brief wounded glide (2.89s to 3.60s = 0.71s)
          const glideT = (tau - 2.89) / 0.71;
          const blendIn = Math.min(1.0, (tau - 2.89) / 0.15);
          const targetNear = 0.22 + Math.sin(glideT * Math.PI) * 0.04;
          const targetFar = 0.19 + Math.sin(glideT * Math.PI) * 0.03;

          nearScaleY = 0.08 + blendIn * (targetNear - 0.08);
          farScaleY = 0.08 + blendIn * (targetFar - 0.08);
          nearWingAngle = -0.16 * forwardDir;
          farWingAngle = -0.28 * forwardDir;

          hoverY = -1.2 + Math.sin(glideT * Math.PI * 2.0) * 0.8;
        }
      }

      // Smooth flight banking into movement direction (zero body rocking!)
      const flightBank = unit.speed > 0 ? 0.04 * forwardDir : 0;
      const drawOriginX = renderX;
      const drawOriginY = renderY + hoverY;

      ctx.save();
      ctx.translate(drawOriginX, drawOriginY);
      ctx.rotate(flightBank);
      ctx.translate(-drawOriginX, -drawOriginY);

      // Dedicated Soaring Eagle Wingspan Dimensions (dwarfs the compact body)
      const wingW = drawSize * 1.85;
      const wingH = drawSize * 1.60;
      const wingAnchorCol = isWestern ? 12.0 : 11.0;
      const wingAnchorRow = 18.0;
      const wingAnchorX = (wingAnchorCol / 24) * wingW;
      const wingAnchorY = (wingAnchorRow / 24) * wingH;

      // Avian Dual Shoulder Girdle (Separate Far vs Near shoulder roots on body):
      // Far Shoulder: Dorsal spine/scapula anchor (deeper in 2.5D background)
      const farShoulderX = drawOriginX + ((8.0 - 11.5) / 24) * drawSize * forwardDir;
      const farShoulderY = drawOriginY + ((7.5 - 11.5) / 24) * drawSize;

      // Near Shoulder: Ventral pectoral/clavicle anchor (closer in 2.5D foreground)
      const nearShoulderX = drawOriginX + ((11.0 - 11.5) / 24) * drawSize * forwardDir;
      const nearShoulderY = drawOriginY + ((9.5 - 11.5) / 24) * drawSize;

      // 1. FAR WING (swung behind body, shaded for 2.5D depth, beating vertically)
      if (wingSrc) {
        ctx.save();
        ctx.translate(farShoulderX, farShoulderY);
        ctx.rotate(farWingAngle);
        ctx.scale(0.92, farScaleY);
        ctx.globalAlpha = 0.68; // 2.5D depth shadow behind body
        ctx.drawImage(
          wingSrc,
          -wingAnchorX,
          -wingAnchorY,
          wingW,
          wingH
        );
        ctx.restore();
      }

      // 2. PARASITE VULTURE BODY CHASSIS & CLAWS
      ctx.drawImage(
        bodySrc,
        drawOriginX - drawSize / 2,
        drawOriginY - drawSize / 2,
        drawSize,
        drawSize
      );

      // 3. DORSAL STATE: Attached Meatball Parasite vs Gaping Wounded Cavity
      if (hasParasite && parasiteSrc) {
        // Pulsating crimson meatball parasite nestled in spinal cradle
        const pPulse = 1.0 + Math.sin(t * 6.0 + unit.wobbleOffset) * 0.07;
        const paraAnchorX = drawOriginX + ((10.0 - 11.5) / 24) * drawSize * forwardDir;
        const paraAnchorY = drawOriginY + ((5.5 - 11.5) / 24) * drawSize;

        ctx.save();
        ctx.translate(paraAnchorX, paraAnchorY);
        ctx.scale(pPulse, pPulse);
        ctx.drawImage(
          parasiteSrc,
          -drawSize / 2,
          -drawSize / 2,
          drawSize,
          drawSize
        );
        ctx.restore();
      } else {
        // Wounded / Injured State: raw bleeding torn flesh crater with dripping gore & bio-smoke
        if (this.quality !== 'low') {
          const dripProgress = (t * 6.5 + unit.wobbleOffset) % 1.0;
          const woundX = drawOriginX + ((9.0 - 11.5) / 24) * drawSize * forwardDir;
          const woundY = drawOriginY + ((5.0 - 11.5) / 24) * drawSize;
          const dripCount = (this.isMobile && this.quality !== 'high') ? 2 : 4;
          for (let d = 0; d < dripCount; d++) {
            const p = (dripProgress + d * (1.0 / dripCount)) % 1.0;
            const bx = woundX - p * 16 * forwardDir;
            const by = woundY + p * 20 + p * p * 8;
            const bAlpha = (1.0 - p) * 0.95;
            ctx.fillStyle = d % 2 === 0 ? '#dc2626' : '#991b1b';
            ctx.globalAlpha = bAlpha;
            ctx.fillRect(bx - 1.5, by - 1.5, 3, 3);
          }
          // Dark bio-smoke / necrotic puff drifting from torn back
          const smokeP = (t * 3.5 + unit.wobbleOffset) % 1.0;
          const sx = woundX - smokeP * 14 * forwardDir;
          const sy = woundY - smokeP * 8;
          ctx.fillStyle = '#450a0a';
          ctx.globalAlpha = (1.0 - smokeP) * 0.45;
          ctx.beginPath();
          ctx.arc(sx, sy, 2 + smokeP * 3.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1.0;
        }
      }

      // 4. NEAR WING (flapping powerfully in front of body, beating vertically through full arc)
      if (wingSrc) {
        ctx.save();
        ctx.translate(nearShoulderX, nearShoulderY);
        ctx.rotate(nearWingAngle);
        ctx.scale(1.0, nearScaleY);
        ctx.drawImage(
          wingSrc,
          -wingAnchorX,
          -wingAnchorY,
          wingW,
          wingH
        );
        ctx.restore();
      }

      // 5. ARTICULATED HEAD & HOOKED BEAK
      const neckPivotX = drawOriginX + ((14.0 - 11.5) / 24) * drawSize * forwardDir;
      const neckPivotY = drawOriginY + ((8.0 - 11.5) / 24) * drawSize;

      const headTwitch = isInsane
        ? (Math.sin(t * 6.0 + unit.wobbleOffset) * 0.10)
        : (Math.cos(t * 2.5 + unit.wobbleOffset) * 0.04);

      ctx.save();
      ctx.translate(neckPivotX, neckPivotY);
      ctx.rotate(headTwitch * forwardDir);
      ctx.translate(-neckPivotX, -neckPivotY);

      ctx.drawImage(
        headSrc,
        drawOriginX - drawSize / 2,
        drawOriginY - drawSize / 2,
        drawSize,
        drawSize
      );
      ctx.restore();

      ctx.restore(); // restores body bank/hover
      }
    } else if (unitType === 'AntiShade' || unitType === 'Anti_Shade' || unitType === 'Anti Shade') {
      const isAllyCloaked = unit.isCloaked && unit.team === localPlayerTeam;
      if (this.quality === 'low' || isSimpleTier) {
        if (isAllyCloaked) {
          ctx.globalAlpha = 0.45;
        } else if (isEnemyInvisible && isDuelPerceived) {
          ctx.globalAlpha = 0.38;
        }
        const unitDrawSize = drawSize * 1.10;
        const swirlFrame = Math.floor(Math.abs(((walkPhase * 0.35 * (isWalking ? 1.0 : 0.65)) + (unit.wobbleOffset || 0)) % 6));
        const antiKey = ANTI_SHADE_WALK_COMPOSITES[swirlFrame] || 'AntiShade';
        const antiBmp = buildSpriteBitmap(antiKey, renderRace, unit.team);
        const antiSrc = isHitFlashing ? antiBmp?.flash : antiBmp?.normal;
        if (antiSrc) {
          drawLowTierWalkSprite(ctx, antiSrc, renderX, renderY, unitDrawSize, unitDrawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_FLOAT);
        }
        if (isAllyCloaked) {
          ctx.globalAlpha = 1.0;
          ctx.fillStyle = '#ff0033';
          const eyeX = renderX + (forwardDir > 0 ? 0 : -2) * 1.10;
          ctx.fillRect(eyeX, renderY - 8 * 1.10, 2 * 1.10, 1.5 * 1.10);
        }
      } else {
      // 🌑 SUL ANTI SHADE (Cloaked Melee Wraith with Floating Tendrils & Massive Void Blade)
      // Continuous ethereal shadow vortex cycle (graceful, flowing wave pace, scaled by walkPitch when disabled)
      const animSpeed = (isWalking ? 2.8 : 1.8) * (unit.isDisabled ? walkPitch : 1.0);
      const swirlFrame = Math.floor(((walkPhase * 0.35 * (isWalking ? 1.0 : 0.65)) + unit.wobbleOffset) % 6);
      const baseKey = ANTI_SHADE_WALK_FRAMES[swirlFrame];

      const baseBitmap = buildSpriteBitmap(baseKey, renderRace, unit.team);
      const torsoBitmap = buildSpriteBitmap('AntiShade_Torso', renderRace, unit.team);
      const bladeBitmap = buildSpriteBitmap('AntiShade_Blade', renderRace, unit.team);

      const baseSrc = isHitFlashing ? baseBitmap?.flash : baseBitmap?.normal;
      const torsoSrc = isHitFlashing ? torsoBitmap?.flash : torsoBitmap?.normal;
      const bladeSrc = isHitFlashing ? bladeBitmap?.flash : bladeBitmap?.normal;

      // Attack lunging & blade slash
      const isStriking = unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.20);

      // Sentinel Idle state when lingering at the bonus level indicator without nearby enemies
      const isSentinelIdle = !isStriking && !!(
        unit.isSentinelIdle ||
        (!unit.isEngaged && !unit.target && battlefield?.territoryDivider !== undefined && Math.abs(unit.x - battlefield.territoryDivider) <= 10)
      );

      // Periodic Blade Readiness / Menacing Flourish (Option 3: every 4.4s a 1.15s smooth gesture)
      const idleCycle = ((t + unit.wobbleOffset * 0.8) % 4.4 + 4.4) % 4.4;
      const flourishP = (isSentinelIdle && idleCycle < 1.15) ? (idleCycle / 1.15) : 0;
      const flourishEnv = flourishP > 0 ? Math.sin(flourishP * Math.PI) : 0;

      // Option 1: Weightless Sentinel Breathing & Upright Hover vs. Forward Sprint Glide
      const hoverY = isSentinelIdle
        ? (Math.sin(t * 1.5 + unit.wobbleOffset) * 4.2 + Math.cos(t * 0.75 + unit.wobbleOffset) * 1.0)
        : (Math.sin(t * 2.2 + unit.wobbleOffset) * 2.8);
      const tiltAngle = (isSentinelIdle
        ? (0.022 * Math.sin(t * 1.5 + unit.wobbleOffset - 0.4))
        : (isWalking
          ? (0.045 + 0.015 * Math.sin(t * 2.2 + unit.wobbleOffset))
          : (0.015 * Math.sin(t * 1.8 + unit.wobbleOffset)))) * forwardDir;
      const torsoBreathY = isSentinelIdle ? Math.sin(t * 1.5 + unit.wobbleOffset - 0.35) * 1.2 : 0;

      // Ally cloaking transparency & Shadow Duel silhouette
      const isAllyCloaked = unit.isCloaked && unit.team === localPlayerTeam;
      if (isAllyCloaked) {
        ctx.globalAlpha = 0.45;
      } else if (isEnemyInvisible && isDuelPerceived) {
        ctx.globalAlpha = 0.38;
      }

      ctx.save();
      ctx.translate(renderX, renderY + hoverY);
      ctx.rotate(tiltAngle);

      // Einheit 10% vergrößert, Stoffbahnen bleiben unskaliert
      const unitDrawSize = drawSize * 1.10;

      // 1. Lower Body: Expansive Mystical Dementor Shadow Vortex
      // A. Procedural Shredded Black Cloth & Dual-Layer Ghosting Smoke Vortex (quality !== 'low')
      if (this.quality !== 'low') {
        const waistX = 0;
        const waistY = unitDrawSize * 0.08;

        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // I. Faseriger Schattendunst-Kern (fließender, ausladender Tusch-Nebel wie im Artwork)
        const smokePulse1 = Math.sin(t * 1.8 + unit.wobbleOffset) * 1.2;
        const smokePulse2 = Math.cos(t * 1.5 + unit.wobbleOffset) * 1.4;

        const smkW = 11.5;
        ctx.fillStyle = 'rgba(15, 23, 42, 0.32)';
        ctx.beginPath();
        ctx.moveTo(waistX - smkW * 0.6, waistY + 1.5);
        ctx.lineTo(waistX - smkW * 1.15 - smokePulse1 * 0.6, waistY + 6.0);
        ctx.lineTo(waistX - smkW * 0.5, waistY + 10.5 + smokePulse2 * 0.5);
        ctx.lineTo(waistX - smkW * 0.7, waistY + 12.5);
        ctx.lineTo(waistX - smkW * 0.2, waistY + 9.5);
        ctx.lineTo(waistX, waistY + 11.8 + smokePulse1 * 0.5);
        ctx.lineTo(waistX + smkW * 0.3, waistY + 9.0);
        ctx.lineTo(waistX + smkW * 0.75 + smokePulse2 * 0.6, waistY + 12.2);
        ctx.lineTo(waistX + smkW * 0.55, waistY + 7.0);
        ctx.lineTo(waistX + smkW * 0.65, waistY + 1.5);
        ctx.closePath();
        ctx.fill();

        const isHighDetail = (this.quality === 'ultra' || this.quality === 'high');

        // II. 5 fetzenartige Stoff- & Rauchbahnen (Ausladender, geschmeidig fließender Schwebemantel)
        for (let k = 0; k < 5; k++) {
          const speedMult = ANTI_SHADE_TATTER_SPEED_MULTS[k];
          const baseAngle = ANTI_SHADE_TATTER_BASE_ANGLES[k];
          const rTop = ANTI_SHADE_TATTER_R_TOP[k];
          const rMid = ANTI_SHADE_TATTER_R_MID[k];
          const rBot = ANTI_SHADE_TATTER_R_BOT[k];
          const yMid = ANTI_SHADE_TATTER_Y_MID[k];
          const yBot = ANTI_SHADE_TATTER_Y_BOT[k];
          const wTop = ANTI_SHADE_TATTER_W_TOP[k];
          const wMid = ANTI_SHADE_TATTER_W_MID[k];
          const notchAmp = ANTI_SHADE_TATTER_NOTCH_AMP[k];
          const curlAmp = ANTI_SHADE_TATTER_CURL_AMP[k];
          const fx = ANTI_SHADE_TATTER_FX[k];
          const fy = ANTI_SHADE_TATTER_FY[k];
          const flutterAmp = ANTI_SHADE_TATTER_FLUTTER_AMP[k];

          // Taille (Ansatz am Körper - harmonische, ruhige Strudelrotation)
          const phi0 = t * (2.0 * speedMult) + unit.wobbleOffset + baseAngle;
          const x0 = waistX + Math.cos(phi0) * rTop;
          const y0 = waistY + Math.sin(phi0) * (rTop * 0.35);

          // Trichter-Mitte (weiche Stoffströmung mit natürlichem Nachlauf)
          const phi1 = phi0 + 0.90 + Math.sin(t * 1.5 + k * 1.3) * 0.18;
          const x1 = waistX + Math.cos(phi1) * rMid;
          const y1 = waistY + yMid + Math.sin(phi1) * (rMid * 0.35);

          // Saum-Ende mit sanftem, ausladendem Wellenschwung
          const phi2 = phi1 + 1.05 + Math.cos(t * 1.3 + k * 1.4) * 0.20;
          const clothFlutterX = Math.sin(t * fx + k * 1.8) * flutterAmp + Math.cos(t * 1.7 + k * 1.1) * (flutterAmp * 0.4);
          const clothFlutterY = Math.cos(t * fy + k * 1.5) * (flutterAmp * 0.6) + Math.sin(t * 1.5 + k * 0.9) * (flutterAmp * 0.3);
          const x2 = waistX + Math.cos(phi2) * rBot + clothFlutterX;
          const y2 = waistY + yBot + Math.sin(phi2) * (rBot * 0.35) + clothFlutterY;

          // Ruhige, organische Stoffrisse (kein hochfrequentes Zittern)
          const notch1 = Math.sin(t * 2.0 + k * 1.8) * notchAmp + (notchAmp * 0.7);
          const notch2 = Math.cos(t * 1.7 + k * 1.6) * notchAmp + (notchAmp * 0.7);

          const inFront = Math.sin(phi1) >= 0;

          // PASS 1: HINTERER RAUCHSCHLEIER (Transluzente Geister-Silhouette wie im Artwork, Ultra/High only)
          if (isHighDetail) {
            const phiLag = 0.38;
            const x0s = waistX + Math.cos(phi0 - phiLag) * (rTop * 1.25);
            const y0s = waistY + Math.sin(phi0 - phiLag) * (rTop * 1.25 * 0.35);
            const x1s = waistX + Math.cos(phi1 - phiLag) * (rMid * 1.30);
            const y1s = waistY + (yMid + 1.2) + Math.sin(phi1 - phiLag) * (rMid * 1.30 * 0.35);
            const x2s = waistX + Math.cos(phi2 - phiLag) * (rBot * 1.30) + clothFlutterX * 1.2;
            const y2s = waistY + (yBot + 1.5) + Math.sin(phi2 - phiLag) * (rBot * 1.30 * 0.35) + clothFlutterY * 1.2;

            drawAntiShadeTatterPoly(ctx, x0s, y0s, wTop * 1.35, x1s, y1s, wMid * 1.30, x2s, y2s, notch1 * 1.2, notch2 * 1.2, curlAmp);
            ctx.fillStyle = inFront ? 'rgba(30, 41, 59, 0.42)' : 'rgba(15, 23, 42, 0.26)';
            ctx.fill();
          }

          // PASS 2: VORDERER ZERFETZTER STOFFKÖRPER (Tiefschwarze gezackte Stofffetzen)
          drawAntiShadeTatterPoly(ctx, x0, y0, wTop, x1, y1, wMid, x2, y2, notch1, notch2, curlAmp);
          ctx.fillStyle = inFront ? '#020617' : '#050811';
          ctx.fill();

          // PASS 3: INNERE STOFFSCHATTIERUNG / VERTİKALE RIPPENFALTE (Ultra/High only)
          if (isHighDetail && inFront) {
            ctx.strokeStyle = 'rgba(30, 41, 59, 0.55)';
            ctx.lineWidth = 1.3;
            ctx.beginPath();
            ctx.moveTo(x0, y0);
            ctx.lineTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
          }
        }

        ctx.restore();
      }

      // B. Main HD Sprite Base (Animated Rotating Vortex)
      if (baseSrc) {
        ctx.drawImage(
          baseSrc,
          -unitDrawSize / 2,
          -unitDrawSize / 2,
          unitDrawSize,
          unitDrawSize
        );
      }

      // 2. Upper Body: Chitin Torso & Horned Crown (with subtle sentinel breathing lift)
      if (torsoSrc) {
        ctx.drawImage(
          torsoSrc,
          -unitDrawSize / 2,
          -unitDrawSize / 2 + torsoBreathY,
          unitDrawSize,
          unitDrawSize
        );
      }

      // 3. Massive Void Blade (Option 1: Continuous Blade Sway + Option 3: Periodic Blade Readiness & Edge Glint)
      ctx.save();
      if (isStriking) {
        // Dynamic slash arc: blade swings from top-back to front-down
        const swingT = Math.min(1.0, (unit.attackSpeed - unit.attackCooldown) / 0.20);
        const bladeAngle = (-0.8 + swingT * 1.8) * forwardDir;
        ctx.translate(forwardDir * 4 * 1.10, 2 * 1.10);
        ctx.rotate(bladeAngle);
      } else if (isSentinelIdle) {
        // Weightless sentinel blade rocking + periodic menacing guard raise
        const bladeSwayY = Math.sin(t * 1.5 + unit.wobbleOffset - 0.75) * 1.5;
        const bladeSwayAngle = -0.12 + Math.sin(t * 1.5 + unit.wobbleOffset - 0.85) * 0.085;
        const flourishX = forwardDir * flourishEnv * 3.0 * 1.10;
        const flourishY = -flourishEnv * 2.5 * 1.10;
        const flourishRot = flourishEnv * 0.45 + Math.sin(flourishP * Math.PI * 2) * 0.07 * flourishEnv;

        ctx.translate(flourishX, bladeSwayY + flourishY);
        ctx.rotate((bladeSwayAngle + flourishRot) * forwardDir);
      } else {
        // Resting diagonal blade pose behind back while marching
        ctx.rotate(-0.15 * forwardDir);
      }

      if (bladeSrc) {
        ctx.drawImage(
          bladeSrc,
          -unitDrawSize / 2,
          -unitDrawSize / 2,
          unitDrawSize,
          unitDrawSize
        );

        // Option 3: Subtle Crimson & White-Hot Edge Glint along the curved Void Blade during readiness flourish
        if (isSentinelIdle && flourishEnv > 0.12 && this.quality !== 'low') {
          const prevBladeAlpha = ctx.globalAlpha;
          const xStart = ((17.2 - 11.5) / 24) * unitDrawSize * forwardDir;
          const yStart = ((11.0 - 11.5) / 24) * unitDrawSize;
          const xCtrl = ((22.2 - 11.5) / 24) * unitDrawSize * forwardDir;
          const yCtrl = ((17.0 - 11.5) / 24) * unitDrawSize;
          const xEnd = ((15.0 - 11.5) / 24) * unitDrawSize * forwardDir;
          const yEnd = ((22.0 - 11.5) / 24) * unitDrawSize;

          ctx.strokeStyle = '#ff0033';
          ctx.lineWidth = 1.6;
          ctx.globalAlpha = prevBladeAlpha * flourishEnv * 0.78;
          ctx.beginPath();
          ctx.moveTo(xStart, yStart);
          ctx.quadraticCurveTo(xCtrl, yCtrl, xEnd, yEnd);
          ctx.stroke();

          const u = flourishP;
          const invU = 1.0 - u;
          const glintX = invU * invU * xStart + 2 * invU * u * xCtrl + u * u * xEnd;
          const glintY = invU * invU * yStart + 2 * invU * u * yCtrl + u * u * yEnd;
          ctx.fillStyle = '#ffffff';
          ctx.globalAlpha = prevBladeAlpha * flourishEnv * 0.95;
          ctx.fillRect(glintX - 1.1, glintY - 1.1, 2.2, 2.2);
          ctx.globalAlpha = prevBladeAlpha;
        }
      }
      ctx.restore();

      // 4. Crimson Slash Arc Visual Effect (quality !== 'low')
      if (isStriking && this.quality !== 'low') {
        ctx.save();
        ctx.strokeStyle = '#ff0033';
        ctx.lineWidth = 2.5;
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        const startAng = forwardDir > 0 ? -Math.PI * 0.4 : -Math.PI * 0.6;
        const endAng = forwardDir > 0 ? Math.PI * 0.35 : Math.PI * 0.65;
        ctx.arc(forwardDir * 10 * 1.10, 0, unitDrawSize * 0.55, startAng, endAng, forwardDir < 0);
        ctx.stroke();

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.0;
        ctx.globalAlpha = 0.95;
        ctx.stroke();
        ctx.restore();
      }

      // 5. Piercing Red Eyes Glow (drawn at full opacity when cloaked for ally, or gleaming during sentinel blade flourish!)
      if (isAllyCloaked || (isSentinelIdle && flourishEnv > 0.15)) {
        ctx.globalAlpha = isAllyCloaked ? 1.0 : flourishEnv * 0.85;
        ctx.fillStyle = '#ff0033';
        const eyeX = (forwardDir > 0 ? 0 : -2) * 1.10;
        ctx.fillRect(eyeX, -8 * 1.10 + torsoBreathY, 2 * 1.10, 1.5 * 1.10);
      }

      ctx.restore();
      }
    } else if (unitType === 'A9Piranha') {
      const piranhaDrawSize = drawSize * 1.236;
      if (this.quality === 'low' || isSimpleTier) {
        const spinIdx = Math.floor(Math.abs((t * 28 + (unit.wobbleOffset || 0)) % 4));
        const piranhaKey = A9_PIRANHA_SPIN_COMPOSITES[spinIdx] || 'A9Piranha';
        const piranhaBmp = buildSpriteBitmap(piranhaKey, renderRace, unit.team);
        const piranhaSrc = isHitFlashing ? piranhaBmp?.flash : piranhaBmp?.normal;
        if (piranhaSrc) {
          ctx.drawImage(piranhaSrc, renderX - piranhaDrawSize / 2, renderY - piranhaDrawSize / 2, piranhaDrawSize, piranhaDrawSize);
        }
      } else {
      // 🚁 A9 PIRANHA HEAVY ATTACK HELICOPTER (Side Profile Perspective - Scaled +23.6%)
      const hullBitmap = buildSpriteBitmap('A9Piranha_Hull', renderRace, unit.team);
      const hullSrc = isHitFlashing ? hullBitmap.flash : hullBitmap.normal;

      // Flight attitude: forward nose-down pitch during flight + hover wobble
      const pitchTilt = (unit.speed > 0 ? 0.08 * forwardDir : 0) + Math.sin(t * 4.0 + unit.wobbleOffset) * 0.03;

      ctx.save();
      ctx.translate(renderX, renderY);
      ctx.rotate(pitchTilt);
      ctx.translate(-renderX, -renderY);

      // 1. Helicopter Hull (Side Profile)
      ctx.drawImage(
        hullSrc,
        renderX - piranhaDrawSize / 2,
        renderY - piranhaDrawSize / 2,
        piranhaDrawSize,
        piranhaDrawSize
      );

      // 2. High-Speed Spinning Main Rotor
      if (this.quality === 'low' || isSimpleTier) {
        // Low Quality Tier: Pre-baked rotor sprite blit (1 draw call, 0 vector trigonometry)
        const rotorBitmap = buildSpriteBitmap('A9Piranha_Rotor', renderRace, unit.team);
        const rotorSrc = isHitFlashing ? rotorBitmap.flash : rotorBitmap.normal;
        ctx.drawImage(rotorSrc, renderX - piranhaDrawSize / 2, renderY - piranhaDrawSize / 2, piranhaDrawSize, piranhaDrawSize);
      } else {
        // High/Medium Quality Tier: Batched 5-Blade Vector Geometry (Invariante #6)
        const mastX = renderX + forwardDir * (piranhaDrawSize * 0.06);
        const mastY = renderY - (piranhaDrawSize * 0.32);
        const rotorRadiusX = piranhaDrawSize * 0.85;
        const rotorRadiusY = piranhaDrawSize * 0.14;
        const rotorAngle = t * 36 * forwardDir;

        ctx.save();
        ctx.translate(mastX, mastY);

        const bladeCount = 5;
        ctx.strokeStyle = 'rgba(30, 41, 59, 0.75)';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        for (let b = 0; b < bladeCount; b++) {
          const ang = rotorAngle + (b * (Math.PI * 2) / bladeCount);
          ctx.moveTo(0, 0);
          ctx.lineTo(Math.cos(ang) * rotorRadiusX, Math.sin(ang) * rotorRadiusY);
        }
        ctx.stroke();

        // Batched yellow tip warning markers (Zero-Allocation)
        ctx.fillStyle = '#facc15';
        for (let b = 0; b < bladeCount; b++) {
          const ang = rotorAngle + (b * (Math.PI * 2) / bladeCount);
          ctx.fillRect(Math.cos(ang) * rotorRadiusX - 1.5, Math.sin(ang) * rotorRadiusY - 1.5, 3, 3);
        }

        // Rotor Mast Hub Cap
        ctx.fillStyle = '#0f172a';
        ctx.beginPath();
        ctx.arc(0, 0, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // 3. Tail Rotor (Spinning vertically at the rear tail fin)
      const tailX = renderX - forwardDir * (piranhaDrawSize * 0.44);
      const tailY = renderY - (piranhaDrawSize * 0.08);
      const tailAngle = t * 45;
      const tailRadius = piranhaDrawSize * 0.18;

      ctx.save();
      ctx.translate(tailX, tailY);
      ctx.rotate(tailAngle);
      ctx.fillStyle = 'rgba(203, 213, 225, 0.85)';
      ctx.fillRect(-1.5, -tailRadius, 3, tailRadius * 2);
      // Yellow tip marker on tail rotor
      ctx.fillStyle = '#facc15';
      ctx.fillRect(-1.5, -tailRadius, 3, 2);
      ctx.fillRect(-1.5, tailRadius - 2, 3, 2);
      ctx.restore();

      ctx.restore();
      }
    } else if (unitType === 'A9Pigeon') {
      const pigeonDrawSize = drawSize * 1.18;
      const nozzleTilt = Math.max(0, Math.min(1, unit.nozzleTilt || 0));
      const hoverMicroTilt = Math.sin(t * 4.2 + (unit.wobbleOffset || 0)) * (unit.isHovering || unit.isFlyingBackward ? 0.024 : 0.014);
      const hullRot = ((unit.pigeonPitch || 0) * forwardDir) + hoverMicroTilt;
      const isFiringGatling = unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.12);
      const gatlingVibX = isFiringGatling ? (-forwardDir * Math.sin(t * 78) * 1.15) : 0;
      const bombRecoilY = (unit._pigeonBombRecoilY || 0);
      const jetX = renderX + gatlingVibX;
      const jetY = renderY + bombRecoilY;

      if (this.quality === 'low' || isSimpleTier) {
        const pigeonBmp = buildSpriteBitmap('A9Pigeon', renderRace, unit.team);
        const pigeonSrc = isHitFlashing ? pigeonBmp?.flash : pigeonBmp?.normal;
        if (pigeonSrc) {
          ctx.save();
          ctx.translate(jetX, jetY);
          ctx.rotate(hullRot);
          ctx.drawImage(pigeonSrc, -pigeonDrawSize / 2, -pigeonDrawSize / 2, pigeonDrawSize, pigeonDrawSize);
          ctx.restore();
        }

        // 🕊️ Wingman Companion Pigeon on Low tier (flapping in escort or perched during hover/drop, 0 matrix transforms, 1 draw call)
        const isHoverOrPerch = unit.isHovering || unit.pigeonState === 'BOMB_HOVER_DROP' || unit.pigeonState === 'HOVER_RANGE';
        const birdFrame = isHoverOrPerch
          ? 'A9Pigeon_Bird_Perched'
          : ((Math.floor(t * 8) % 2 === 0) ? 'A9Pigeon_Bird_Flap1' : 'A9Pigeon_Bird_BackFlap1');
        const birdBmp = buildSpriteBitmap(birdFrame, renderRace, unit.team);
        const birdSrc = isHitFlashing ? birdBmp?.flash : birdBmp?.normal;
        if (birdSrc) {
          const birdSize = pigeonDrawSize * 0.46;
          let birdX, birdY;
          if (isHoverOrPerch) {
            // Perched at the nose probe of the aircraft
            birdX = jetX + forwardDir * (pigeonDrawSize * (14.6 / 36));
            birdY = jetY + pigeonDrawSize * (-3.2 / 36) - birdSize * (3.8 / 24);
          } else {
            // Escort formation alongside the aircraft
            birdX = jetX - forwardDir * (pigeonDrawSize * 0.22);
            birdY = jetY - pigeonDrawSize * 0.42 + Math.sin(t * 5.8 + (unit.wobbleOffset || 0)) * 2.8;
          }
          ctx.drawImage(birdSrc, birdX - birdSize / 2, birdY - birdSize / 2, birdSize, birdSize);
        }
      } else {
        // 🕊️✈️ A9 PIGEON HEAVY VTOL FIGHTER JET (5-Part Rig: Fuselage + Gyro-Compensated Vector Engine + Spinning Gatling + Belly Bomb + Wingman Pigeon)
        const hullBitmap = buildSpriteBitmap('A9Pigeon_Hull', renderRace, unit.team);
        const engineBitmap = buildSpriteBitmap('A9Pigeon_Engine_Horiz', renderRace, unit.team);
        const gatlingKey = (isFiringGatling || unit.isEngaged) && (Math.floor(t * 28) % 2 === 1)
          ? 'A9Pigeon_Gatling_Spin2'
          : 'A9Pigeon_Gatling_Spin1';
        const gatlingBitmap = buildSpriteBitmap(gatlingKey, renderRace, unit.team) || buildSpriteBitmap('A9Pigeon_Gatling_Spin1', renderRace, unit.team);
        const hullSrc = isHitFlashing ? hullBitmap?.flash : hullBitmap?.normal;
        const engineSrc = isHitFlashing ? engineBitmap?.flash : engineBitmap?.normal;
        const gatlingSrc = isHitFlashing ? gatlingBitmap?.flash : gatlingBitmap?.normal;

        // 0. VTOL Ground Downwash Rings (when vector engine pivots downward during hover or reverse-thrust kiting)
        if (nozzleTilt >= 0.25) {
          const downwashIntensity = (nozzleTilt - 0.25) / 0.75;
          const washOffsetForward = unit.isFlyingBackward ? forwardDir * 14 : forwardDir * 2;
          const washCenterX = jetX - forwardDir * (pigeonDrawSize * 0.24) + washOffsetForward;
          const washCenterY = (unit.pigeonState === 'BOMB_HOVER_DROP') ? (unit.y + 36) : (unit.y + 6);
          ctx.save();
          ctx.lineWidth = 1.4;
          for (let ringIdx = 0; ringIdx < 2; ringIdx++) {
            const rPhase = (t * 3.4 + ringIdx * 0.5) % 1.0;
            const ringRx = (8 + rPhase * 20) * downwashIntensity;
            const ringRy = (2.6 + rPhase * 5.5) * downwashIntensity;
            ctx.strokeStyle = ringIdx === 0 ? 'rgba(56, 189, 248, 0.45)' : 'rgba(226, 232, 240, 0.35)';
            ctx.globalAlpha = (1.0 - rPhase) * downwashIntensity * 0.9;
            ctx.beginPath();
            ctx.ellipse(washCenterX, washCenterY, ringRx, ringRy, 0, 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.restore();
        }

        ctx.save();
        ctx.translate(jetX, jetY);
        ctx.rotate(hullRot);

        // 1. Separate Vector-Thrust Jet Engine Sprite (Actively counter-rotates against hull pitch so VTOL thrust points straight down!)
        const pivotLocalX = -forwardDir * (pigeonDrawSize * (10.5 / 36));
        const pivotLocalY = pigeonDrawSize * (-2.5 / 36);
        // Reverse flight: active counter-thrust braking angle (angles nozzle ~102° forward-downward to physically brake & push backward)
        const reverseThrustCant = unit.isFlyingBackward ? 0.22 : 0;
        const targetDownWorldRot = -forwardDir * (Math.PI * 0.5 - reverseThrustCant);

        // Active FBW Flight Control System (FCS) Micro-Trim Oscillations (rapid gyroscopic attitude stabilization in hover/kiting)
        const isHoverOrKite = unit.isHovering || unit.isFlyingBackward || nozzleTilt > 0.35;
        const fcsTrim = isHoverOrKite ? Math.sin(t * 46 + (unit.wobbleOffset || 0)) * 0.042 : 0;
        // Active Gatling Recoil Counter-Trim (only active during hover/kiting to stabilize nozzle against Gatling kick)
        const gatlingRecoilTrim = (isFiringGatling && nozzleTilt > 0.15) ? 0.08 : 0;
        const localNozzleRot = nozzleTilt * (targetDownWorldRot - hullRot) + fcsTrim + gatlingRecoilTrim;

        // ⚙️ Mechanical Telescoping Hydraulic Piston Linkage (Connects fuselage clevis to pivoting nozzle ring)
        const lugX = -forwardDir * (pigeonDrawSize * (10.0 / 36));
        const lugY = pigeonDrawSize * (-4.0 / 36);
        const earRestX = -forwardDir * (pigeonDrawSize * (11.5 / 36));
        const earRestY = pigeonDrawSize * (-6.0 / 36);
        const earRestDx = earRestX - pivotLocalX;
        const earRestDy = earRestY - pivotLocalY;
        const cosR = Math.cos(localNozzleRot);
        const sinR = Math.sin(localNozzleRot);
        const earRotX = pivotLocalX + earRestDx * cosR - earRestDy * sinR;
        const earRotY = pivotLocalY + earRestDx * sinR + earRestDy * cosR;

        const actDx = earRotX - lugX;
        const actDy = earRotY - lugY;
        const actDist = Math.hypot(actDx, actDy);
        const actAngle = Math.atan2(actDy, actDx);

        ctx.save();
        ctx.translate(lugX, lugY);
        ctx.rotate(actAngle);
        const cylLen = Math.max(2, actDist * 0.54);
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(0, -1.6, cylLen, 3.2);
        ctx.fillStyle = '#475569';
        ctx.fillRect(0, -1.0, cylLen, 1.4);
        ctx.fillStyle = '#f59e0b'; // Gold hydraulic pressure fitting
        ctx.fillRect(1.5, -1.8, 1.4, 3.6);
        const rodLen = Math.max(1, actDist - cylLen);
        ctx.fillStyle = '#cbd5e1'; // Chrome piston rod
        ctx.fillRect(cylLen, -0.9, rodLen, 1.8);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(cylLen, -0.4, rodLen, 0.8);
        ctx.fillStyle = '#94a3b8'; // Clevis pins
        ctx.beginPath(); ctx.arc(0, 0, 1.6, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(actDist, 0, 1.4, 0, Math.PI * 2); ctx.fill();
        ctx.restore();

        // 〰️ Flexible Braided Armored Hydraulic Hose Line (Flexes with inertia alongside telescoping piston)
        const hoseSag = (1.0 + nozzleTilt * 2.8) * Math.sin(t * 6.0 + (unit.wobbleOffset || 0)) * 0.6 + (nozzleTilt * 3.5);
        const midHoseX = (lugX + earRotX) * 0.5;
        const midHoseY = (lugY + earRotY) * 0.5 + (nozzleTilt > 0.1 ? hoseSag : -2.2);
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(lugX, lugY + 1.2);
        ctx.quadraticCurveTo(midHoseX, midHoseY, earRotX, earRotY + 1.0);
        ctx.stroke();
        ctx.strokeStyle = '#334155';
        ctx.lineWidth = 1.0;
        ctx.stroke();
        // Golden brass crimp collar couplings on hose ends
        ctx.fillStyle = '#f59e0b';
        ctx.fillRect(lugX - 0.8, lugY + 0.6, 1.6, 1.6);
        ctx.fillRect(earRotX - 0.8, earRotY + 0.4, 1.6, 1.6);

        // 💨 Active Attitude Reaction Control System (RCS) Micro-Puffs
        if (isHoverOrKite) {
          const rcsPulse1 = Math.sin(t * 26 + (unit.wobbleOffset || 0));
          const rcsPulse2 = Math.cos(t * 33 + (unit.wobbleOffset || 0));
          // 90° Slender Wingtip Roll Thruster (drooped winglet tip at x: 8..13, y: 26..28)
          if (rcsPulse1 > 0.42) {
            const tipX = -forwardDir * (pigeonDrawSize * (7.5 / 36));
            const tipY = pigeonDrawSize * (9.0 / 36);
            const rcsLen = 3.6 * (rcsPulse1 - 0.42) * 1.8;
            ctx.fillStyle = 'rgba(56, 189, 248, 0.8)';
            ctx.beginPath();
            ctx.moveTo(tipX, tipY);
            ctx.lineTo(tipX - forwardDir * 1.2, tipY + rcsLen);
            ctx.lineTo(tipX + forwardDir * 1.2, tipY + rcsLen);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(tipX - 0.6, tipY, 1.2, rcsLen * 0.5);
          }
          // Ventral Pitch Thruster (under chin at x=28, y=16)
          if (rcsPulse2 > 0.50) {
            const noseRcsX = forwardDir * (pigeonDrawSize * (10.5 / 36));
            const noseRcsY = pigeonDrawSize * (-1.5 / 36);
            const rcsLen = 2.8 * (rcsPulse2 - 0.50) * 2.2;
            ctx.fillStyle = 'rgba(56, 189, 248, 0.8)';
            ctx.beginPath();
            ctx.moveTo(noseRcsX, noseRcsY);
            ctx.lineTo(noseRcsX - 1.2, noseRcsY - rcsLen);
            ctx.lineTo(noseRcsX + 1.2, noseRcsY - rcsLen);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(noseRcsX - 0.5, noseRcsY - rcsLen * 0.6, 1.0, rcsLen * 0.6);
          }
        }

        ctx.save();
        ctx.translate(pivotLocalX, pivotLocalY);
        ctx.rotate(localNozzleRot);
        ctx.translate(-pivotLocalX, -pivotLocalY);

        // Dynamic Supersonic Afterburner Plume with 3 Mach Shock Diamonds (Stoßwellenrauten)
        // Precisely aligned to engine nozzle centerline (row 15.5 -> y: -2.5/36, exhaust rim x: 1.0 -> x: -17.0/36)
        const thrustPower = unit.pigeonState === 'BOMB_APPROACH' ? 1.45 : (unit.isFlyingBackward || unit.isHovering ? 1.20 : 1.0);
        const flamePulse = (0.82 + 0.18 * Math.sin(t * 44 + (unit.wobbleOffset || 0))) * thrustPower;
        const nozzleTipX = -forwardDir * (pigeonDrawSize * (17.0 / 36));
        const nozzleTipY = pigeonDrawSize * (-2.5 / 36);
        const plumeLen = pigeonDrawSize * 0.40 * flamePulse;
        const plumeDir = -forwardDir;
        const flameHalfH = pigeonDrawSize * (2.0 / 36);
        const coreHalfH = pigeonDrawSize * (1.2 / 36);

        // 1. Outer Atmospheric Expansion Envelope (Translucent Cyan Glow)
        ctx.fillStyle = 'rgba(14, 165, 233, 0.65)';
        ctx.beginPath();
        ctx.moveTo(nozzleTipX, nozzleTipY - flameHalfH);
        ctx.lineTo(nozzleTipX + plumeDir * plumeLen, nozzleTipY);
        ctx.lineTo(nozzleTipX, nozzleTipY + flameHalfH);
        ctx.closePath();
        ctx.fill();

        // 2. High-Temperature Core Jet (Vibrant Cyan Plasma)
        ctx.fillStyle = 'rgba(56, 189, 248, 0.92)';
        ctx.beginPath();
        ctx.moveTo(nozzleTipX, nozzleTipY - coreHalfH);
        ctx.lineTo(nozzleTipX + plumeDir * (plumeLen * 0.85), nozzleTipY);
        ctx.lineTo(nozzleTipX, nozzleTipY + coreHalfH);
        ctx.closePath();
        ctx.fill();

        // 3. Supersonic Mach Shock Diamonds (3 Distinct Diamond Nodes) - Batched GPU draw calls
        const diamondDistances = [0.24, 0.52, 0.78];
        const diamondSizes = [2.2, 1.7, 1.2];
        const pixelScale = pigeonDrawSize / 36;

        // Diamond outer shockwave contour - batched across all 3 nodes
        ctx.fillStyle = '#00f0ff';
        ctx.beginPath();
        for (let d = 0; d < 3; d++) {
          const dCenterDist = plumeLen * diamondDistances[d];
          const dCenterX = nozzleTipX + plumeDir * dCenterDist;
          const dSize = diamondSizes[d] * flamePulse * pixelScale;
          ctx.moveTo(dCenterX - plumeDir * dSize * 1.2, nozzleTipY);
          ctx.lineTo(dCenterX, nozzleTipY - dSize);
          ctx.lineTo(dCenterX + plumeDir * dSize * 1.2, nozzleTipY);
          ctx.lineTo(dCenterX, nozzleTipY + dSize);
          ctx.closePath();
        }
        ctx.fill();

        // White-hot shock diamond nucleus for node 0
        const d0Dist = plumeLen * diamondDistances[0];
        const d0CenterX = nozzleTipX + plumeDir * d0Dist;
        const d0Size = diamondSizes[0] * flamePulse * pixelScale;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(d0CenterX - plumeDir * (d0Size * 0.55), nozzleTipY);
        ctx.lineTo(d0CenterX, nozzleTipY - d0Size * 0.45);
        ctx.lineTo(d0CenterX + plumeDir * (d0Size * 0.55), nozzleTipY);
        ctx.lineTo(d0CenterX, nozzleTipY + d0Size * 0.45);
        ctx.closePath();
        ctx.fill();

        // Incandescent shock diamond nuclei for nodes 1 & 2 - batched in single fill
        ctx.fillStyle = '#fef08a';
        ctx.beginPath();
        for (let d = 1; d < 3; d++) {
          const dCenterDist = plumeLen * diamondDistances[d];
          const dCenterX = nozzleTipX + plumeDir * dCenterDist;
          const dSize = diamondSizes[d] * flamePulse * pixelScale;
          ctx.moveTo(dCenterX - plumeDir * (dSize * 0.55), nozzleTipY);
          ctx.lineTo(dCenterX, nozzleTipY - dSize * 0.45);
          ctx.lineTo(dCenterX + plumeDir * (dSize * 0.55), nozzleTipY);
          ctx.lineTo(dCenterX, nozzleTipY + dSize * 0.45);
          ctx.closePath();
        }
        ctx.fill();

        if (engineSrc) {
          ctx.drawImage(engineSrc, -pigeonDrawSize / 2, -pigeonDrawSize / 2, pigeonDrawSize, pigeonDrawSize);
        }
        ctx.restore();

        // 2. Belly Ordnance Pylon & Heavy Ground Bomb (Only rendered while unit.hasBomb !== false)
        if (unit.hasBomb !== false) {
          const bombBitmap = buildSpriteBitmap('A9Pigeon_Bomb', renderRace, unit.team);
          const bombSrc = isHitFlashing ? bombBitmap?.flash : bombBitmap?.normal;
          if (bombSrc) {
            // Smooth 3-frame pneumatic unlatch ease right before drop (0.38s down to 0.32s)
            let unlatchY = 0;
            if (unit.pigeonState === 'BOMB_HOVER_DROP' && (unit.bombHoverTimer || 0) <= 0.38) {
              const unlatchProg = Math.min(1.0, (0.38 - unit.bombHoverTimer) / 0.06);
              unlatchY = Math.pow(unlatchProg, 1.4) * 2.8;
            }
            ctx.drawImage(bombSrc, -pigeonDrawSize / 2, -pigeonDrawSize / 2 + unlatchY, pigeonDrawSize, pigeonDrawSize);
          }
        }

        // 3. Main Fighter Fuselage (Iridescent Pigeon-Neck Nose + Swept Wings + Cyan Canopy)
        if (hullSrc) {
          ctx.drawImage(hullSrc, -pigeonDrawSize / 2, -pigeonDrawSize / 2, pigeonDrawSize, pigeonDrawSize);
        }

        // 4. Chin-Mounted 6-Barrel Gatling Gun + Thermal Barrel Glow & Ejecting Brass Casings
        if (gatlingSrc) {
          const barrelKickX = isFiringGatling ? (-forwardDir * 1.2) : 0;
          ctx.drawImage(gatlingSrc, -pigeonDrawSize / 2 + barrelKickX, -pigeonDrawSize / 2, pigeonDrawSize, pigeonDrawSize);
        }

        // Ventral Brass Cartridge Ejection (Cascading spent casings tumbling from under-chin port when firing)
        // High-performance batched rasterization: zero per-casing ctx.save/restore thrashing, batched by fill style!
        if (isFiringGatling || (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.15))) {
          const portX = forwardDir * (pigeonDrawSize * (6.0 / 36));
          const portY = pigeonDrawSize * (5.5 / 36);

          // Casing 0
          const c0Phase = ((t * 24) % 1.0);
          const c0X = portX - forwardDir * (c0Phase * 8.5);
          const c0Y = portY + Math.pow(c0Phase, 1.6) * 16;
          const c0Rot = t * 38;
          const c0Cos = Math.cos(c0Rot), c0Sin = Math.sin(c0Rot);

          // Casing 1
          const c1Phase = ((t * 24 + 0.33) % 1.0);
          const c1X = portX - forwardDir * (c1Phase * 8.5);
          const c1Y = portY + Math.pow(c1Phase, 1.6) * 16;
          const c1Rot = t * 38 + 2.1;
          const c1Cos = Math.cos(c1Rot), c1Sin = Math.sin(c1Rot);

          // Casing 2
          const c2Phase = ((t * 24 + 0.66) % 1.0);
          const c2X = portX - forwardDir * (c2Phase * 8.5);
          const c2Y = portY + Math.pow(c2Phase, 1.6) * 16;
          const c2Rot = t * 38 + 4.2;
          const c2Cos = Math.cos(c2Rot), c2Sin = Math.sin(c2Rot);

          // Subpath generator for a rotated rectangle centered at (cx, cy) with orientation (cos, sin)
          const appendRotRect = (cx, cy, cos, sin, rx, ry, rw, rh) => {
            const x0 = rx, y0 = ry;
            const x1 = rx + rw, y1 = ry + rh;
            ctx.moveTo(cx + x0 * cos - y0 * sin, cy + x0 * sin + y0 * cos);
            ctx.lineTo(cx + x1 * cos - y0 * sin, cy + x1 * sin + y0 * cos);
            ctx.lineTo(cx + x1 * cos - y1 * sin, cy + x1 * sin + y1 * cos);
            ctx.lineTo(cx + x0 * cos - y1 * sin, cy + x0 * sin + y1 * cos);
            ctx.closePath();
          };

          // 1. Polished brass cartridge body (#facc15) - all 3 casings batched in 1 GPU draw call
          ctx.fillStyle = '#facc15';
          ctx.beginPath();
          appendRotRect(c0X, c0Y, c0Cos, c0Sin, -1.4, -0.6, 2.8, 1.2);
          appendRotRect(c1X, c1Y, c1Cos, c1Sin, -1.4, -0.6, 2.8, 1.2);
          appendRotRect(c2X, c2Y, c2Cos, c2Sin, -1.4, -0.6, 2.8, 1.2);
          ctx.fill();

          // 2. Cartridge rim (#b45309) - all 3 rims batched in 1 GPU draw call
          ctx.fillStyle = '#b45309';
          ctx.beginPath();
          appendRotRect(c0X, c0Y, c0Cos, c0Sin, -1.4, -0.6, 0.7, 1.2);
          appendRotRect(c1X, c1Y, c1Cos, c1Sin, -1.4, -0.6, 0.7, 1.2);
          appendRotRect(c2X, c2Y, c2Cos, c2Sin, -1.4, -0.6, 0.7, 1.2);
          ctx.fill();

          // 3. Metallic specular gleam (rgba(255, 255, 255, 0.75)) - all 3 gleams batched in 1 GPU draw call
          ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
          ctx.beginPath();
          appendRotRect(c0X, c0Y, c0Cos, c0Sin, -0.4, -0.3, 1.2, 0.6);
          appendRotRect(c1X, c1Y, c1Cos, c1Sin, -0.4, -0.3, 1.2, 0.6);
          appendRotRect(c2X, c2Y, c2Cos, c2Sin, -0.4, -0.3, 1.2, 0.6);
          ctx.fill();
        }

        if (unit.barrelHeat > 0.05) {
          const heatProg = Math.min(1, unit.barrelHeat);
          const heatStrip = getThermalHeatStrip();
          const barrelTipX = forwardDir * (pigeonDrawSize * (16.5 / 36));
          const barrelTipY = pigeonDrawSize * (4.0 / 36);
          const barrelW = pigeonDrawSize * (8.0 / 36);
          const barrelH = 3.2;
          if (heatStrip) {
            ctx.save();
            ctx.globalAlpha = heatProg * 0.85;
            const drawX = barrelTipX - (forwardDir === 1 ? barrelW : 0);
            const drawY = barrelTipY - barrelH / 2;
            if (forwardDir === 1) {
              ctx.drawImage(heatStrip, drawX, drawY, barrelW, barrelH);
            } else {
              ctx.translate(drawX + barrelW, drawY);
              ctx.scale(-1, 1);
              ctx.drawImage(heatStrip, 0, 0, barrelW, barrelH);
            }
            ctx.restore();
          }
        }

        ctx.restore();

        // 5. Companion Wingman Pigeon ("The Statue & The Pigeon" Multi-State System)
        // Escort formation in flight <-> Dynamic non-clipping landing flare <-> Perched & inspecting on nose probe during hover!
        const dtStep = (rawdt || 0.016);
        const birdSize = pigeonDrawSize * 0.46;

        // Escort formation target position (flies above and slightly behind near wing, or forward above canopy during reverse hover)
        const isDogfight = (unit.pigeonState === 'DOGFIGHT') || (unit.target && (unit.target.isAir || unit.target.elementType === 'Air') && (unit.isHovering || unit.pigeonState === 'HOVER_RANGE' || unit.pigeonState === 'DOGFIGHT'));
        const isFlyingBackward = !isDogfight && (unit.isFlyingBackward || unit.pigeonState === 'KITE_BACK');
        const isHoveringPerch = !isFlyingBackward && !isDogfight && (unit.isHovering || unit.pigeonState === 'BOMB_HOVER_DROP' || unit.pigeonState === 'HOVER_RANGE');

        // Dynamic Escort Formation & Periodic Speed Surge ("ab und zu auch mal kurz etwas schneller fliegen"):
        // Normal flight: ~75% cruising at standard wingman offset (-0.22 * pigeonDrawSize).
        // Periodic speed surge: every ~7.2s, the bird vigorously accelerates forward past the wing towards the cockpit!
        let forwardSurgeOffset = 0;
        let surgeLiftY = 0;
        let isSpeedSurging = false;
        const surgeCycle = ((t * 0.14 + (unit.wobbleOffset || 0)) % 1.0); // 7.14s full period
        if (!isFlyingBackward && !isHoveringPerch && !isDogfight) {
          if (surgeCycle > 0.68 && surgeCycle < 0.92) {
            isSpeedSurging = true;
            const surgePhase = (surgeCycle - 0.68) / (0.92 - 0.68);
            const surgeCurve = Math.sin(surgePhase * Math.PI);
            forwardSurgeOffset = forwardDir * (pigeonDrawSize * 0.38) * surgeCurve;
            surgeLiftY = -surgeCurve * 2.5;
          }
        }

        const baseEscortOffset = isFlyingBackward ? (pigeonDrawSize * 0.06) : (-forwardDir * (pigeonDrawSize * 0.22));
        const escortOffsetForward = baseEscortOffset + forwardSurgeOffset;
        const targetWingX = jetX + escortOffsetForward;
        const targetWingY = jetY - pigeonDrawSize * 0.42 + Math.sin(t * 5.8 + (unit.wobbleOffset || 0)) * 2.8 + surgeLiftY;
        if (unit._wingmanX === undefined || unit._wingmanY === undefined) {
          unit._wingmanX = targetWingX;
          unit._wingmanY = targetWingY;
        } else {
          const springRate = isSpeedSurging ? 10.5 : 7.5;
          const springFollow = Math.min(1, dtStep * springRate);
          unit._wingmanX += (targetWingX - unit._wingmanX) * springFollow;
          unit._wingmanY += (targetWingY - unit._wingmanY) * springFollow;
        }
        const wingLagDX = targetWingX - unit._wingmanX;
        const isManeuvering = Math.abs(wingLagDX) > 2.0 || unit.pigeonState === 'BOMB_APPROACH' || isFlyingBackward;

        // Perch Condition: unit hovers over ground target to drop bomb or hovers at range
        if (unit._perchProg === undefined) unit._perchProg = 0.0;
        if (isHoveringPerch) {
          // Graceful aerodynamic glide into landing flare and touchdown
          unit._perchProg = Math.min(1.0, unit._perchProg + dtStep * 2.6);
        } else {
          // Prompt push-off takeoff flap back into formation
          unit._perchProg = Math.max(0.0, unit._perchProg - dtStep * 3.4);
        }

        // Dogfight Perch Condition: unit engages enemy air unit in dogfight (Version C: Aggro Threat on Canopy)
        if (unit._dogfightProg === undefined) unit._dogfightProg = 0.0;
        if (isDogfight) {
          unit._dogfightProg = Math.min(1.0, unit._dogfightProg + dtStep * 2.8);
          unit._perchProg = Math.max(0.0, unit._perchProg - dtStep * 4.0);
        } else {
          unit._dogfightProg = Math.max(0.0, unit._dogfightProg - dtStep * 3.4);
        }

        // Strict Hierarchical Kinematic Transform for the Perch Point on the Aircraft Nose Probe:
        // Anchored in aircraft local space at (localNeedleX, localNeedleY), rotated by hullRot, translated by (jetX, jetY).
        const localNeedleX = forwardDir * (pigeonDrawSize * (14.6 / 36));
        const localNeedleY = pigeonDrawSize * (-3.2 / 36);
        const footOffsetLocal = birdSize * (3.8 / 24);
        const localPerchCenterX = localNeedleX;
        const localPerchCenterY = localNeedleY - footOffsetLocal;

        const cosHull = Math.cos(hullRot);
        const sinHull = Math.sin(hullRot);
        const perchWorldX = jetX + (localPerchCenterX * cosHull - localPerchCenterY * sinHull);
        const perchWorldY = jetY + (localPerchCenterX * sinHull + localPerchCenterY * cosHull);

        // Cockpit Canopy Perch Point for Dogfight Threat Display (x: +2.8/36, y: -7.0/36)
        const localCanopyX = forwardDir * (pigeonDrawSize * (2.8 / 36));
        const localCanopyY = pigeonDrawSize * (-7.0 / 36) - footOffsetLocal;
        const canopyWorldX = jetX + (localCanopyX * cosHull - localCanopyY * sinHull);
        const canopyWorldY = jetY + (localCanopyX * sinHull + localCanopyY * cosHull);

        // Smooth non-clipping transition trajectory:
        const uProg = unit._perchProg;
        const uDog = unit._dogfightProg;
        const blendU = uProg * uProg * (3.0 - 2.0 * uProg); // Hermite smoothstep
        const blendDog = uDog * uDog * (3.0 - 2.0 * uDog);
        const flareArcY = Math.sin(uProg * Math.PI) * (pigeonDrawSize * 0.20);
        const dogFlareArcY = Math.sin(uDog * Math.PI) * (pigeonDrawSize * 0.18);
        const backFlutterHopY = isFlyingBackward ? Math.sin(t * 38 + (unit.wobbleOffset || 0)) * 1.3 : 0;

        let currentBirdX, currentBirdY;
        if (uDog > 0.001) {
          currentBirdX = (1.0 - blendDog) * unit._wingmanX + blendDog * canopyWorldX;
          currentBirdY = (1.0 - blendDog) * unit._wingmanY + blendDog * canopyWorldY - dogFlareArcY;
        } else {
          currentBirdX = (1.0 - blendU) * unit._wingmanX + blendU * perchWorldX;
          currentBirdY = (1.0 - blendU) * unit._wingmanY + blendU * perchWorldY - flareArcY + backFlutterHopY;
        }

        // Dynamic Rotation & Biological Balancing:
        // In reverse hover/kiting: distinct aerodynamic back-pedal flare tilt resisting airflow
        const backPedalTilt = -forwardDir * (0.22 + Math.sin(t * 19.5 + (unit.wobbleOffset || 0)) * 0.04);
        const flightBank = isFlyingBackward
          ? (backPedalTilt + hullRot * 0.35)
          : (Math.max(-0.28, Math.min(0.28, wingLagDX * 0.04)) + hullRot * 0.45);

        // When perched on the swaying aircraft ("aber ganz starr sitzt das flugzeug auch nicht"):
        const balanceCounterLean = -hoverMicroTilt * 0.65;
        // Natural lifelike behavioral cycle when perched on the aircraft nose (4.0s full cycle):
        const perchCycleTime = ((t * 0.25 + (unit.wobbleOffset || 0)) % 1.0);
        const isPeckPhase = perchCycleTime >= 0.72 && perchCycleTime < 0.84;
        const isFlutterPhase = perchCycleTime >= 0.84;
        const isPeckPose = (unit.pigeonState === 'BOMB_HOVER_DROP' && unit.bombHoverTimer > 0.32)
          ? (((t * 1.8) % 1.0) > 0.45)
          : isPeckPhase;
        const peckNod = isPeckPose ? 0.05 * forwardDir : 0.0;
        const perchedBank = hullRot + balanceCounterLean + peckNod;
        const dogBank = hullRot + balanceCounterLean;

        const currentBirdRot = (uDog > 0.001)
          ? ((1.0 - blendDog) * flightBank + blendDog * dogBank)
          : ((1.0 - blendU) * flightBank + blendU * perchedBank);

        // Frame selection: Reverse hover flutter -> Escort flight -> Landing flare -> Perched behaviors!
        let birdFrameKey;
        if (uDog >= 0.85) {
          // ⚔️ Dogfight Multi-Phase Aggro-Display perched on cockpit canopy!
          // Extended behavioral routine with rich variance (~4.4s full cycle):
          // 1. Phase 1 (0.0s..1.2s): "Hals aufblasen" (Puffing up throat & chest) -> Threat_Puff
          // 2. Phase 2 (1.2s..2.4s): "Mit Kopf nicken" (Aggro Head-Bobbing, 3.5 Hz) -> Threat_Bob <-> Threat_Alert
          // 3. Phase 3 (2.4s..3.4s): "Flügel strecken" (Wing Stretch & Impressive Display) -> Threat_WingStretch
          // 4. Phase 4 (3.4s..4.4s): "Wachsamer Blick" (Alert focal glare & re-centering) -> Threat_Alert
          if (unit._pigeonRecoilTimer && unit._pigeonRecoilTimer > 0.18) {
            birdFrameKey = 'A9Pigeon_Bird_Flap1'; // Quick balance flap on shock
          } else {
            const dogCyclePeriod = 4.4;
            const dogPhase = ((t + (unit.wobbleOffset || 0) * 1.5) % dogCyclePeriod);
            if (dogPhase < 1.2) {
              // 1. Hals aufblasen: Kropf maximal geschwellt, breite Brust
              birdFrameKey = 'A9Pigeon_Bird_Threat_Puff';
            } else if (dogPhase < 2.4) {
              // 2. Mit Kopf nicken: energische Nickstöße in Richtung Gegner (~3.5 Hz)
              const bobCycle = Math.floor((dogPhase - 1.2) * 7) % 2;
              birdFrameKey = (bobCycle === 0) ? 'A9Pigeon_Bird_Threat_Bob' : 'A9Pigeon_Bird_Threat_Alert';
            } else if (dogPhase < 3.4) {
              // 3. Flügel strecken: Schwingen weit nach oben durchgestreckt
              birdFrameKey = 'A9Pigeon_Bird_Threat_WingStretch';
            } else {
              // 4. Wachsamer Blick: lauernd fokussiert auf den Gegner
              birdFrameKey = 'A9Pigeon_Bird_Threat_Alert';
            }
          }
        } else if (uDog > 0.25) {
          // Landing flare onto canopy or takeoff back into formation
          birdFrameKey = 'A9Pigeon_Bird_Flap1';
        } else if (uProg >= 0.85) {
          // Perched directly on the nose probe ("The Statue & The Pigeon")
          if (unit._pigeonBombRecoilY && unit._pigeonBombRecoilY < -2.0) {
            // Sudden bomb drop shockwave causes brief instinctive balance flutter
            birdFrameKey = 'A9Pigeon_Bird_Flap1';
          } else if (isFlutterPhase) {
            // Phasic wing ruffle / re-settling balance flutter (brief 0.6s burst every 4 seconds)
            const flutterCycle = Math.floor(t * 12) % 3;
            birdFrameKey = flutterCycle === 0
              ? 'A9Pigeon_Bird_Flap1'
              : (flutterCycle === 1 ? 'A9Pigeon_Bird_Flap2' : 'A9Pigeon_Bird_Flap3');
          } else if (isPeckPose) {
            // Curiously inspecting down at the ground target
            birdFrameKey = 'A9Pigeon_Bird_Peck';
          } else {
            // 🕊️ Calm, proud, alert upright perched pose with folded wings (dominant resting state!)
            birdFrameKey = 'A9Pigeon_Bird_Perched';
          }
        } else if (uProg > 0.25) {
          // Landing flare / takeoff flap: wings raised high in dihedral V-shape
          birdFrameKey = 'A9Pigeon_Bird_Flap1';
        } else if (isFlyingBackward) {
          // 🕊️💨 High-frequency aerodynamic reverse-hover flutter (thrusting air forward while moving backward)
          const backFlapRate = 19.5;
          const backCycle = Math.floor((t * backFlapRate + (unit.wobbleOffset || 0)) % 4);
          birdFrameKey = backCycle === 0
            ? 'A9Pigeon_Bird_BackFlap1'
            : (backCycle === 2 ? 'A9Pigeon_Bird_BackFlap3' : 'A9Pigeon_Bird_BackFlap2');
        } else {
          // Escort flight: aerodynamic glide or rhythmic flap
          // When speed-surging ahead: fast vigorous flapping on the dash, then smooth outstretched glide at the crest!
          const glidePeriod = ((t * 0.31 + (unit.wobbleOffset || 0)) % 1.0);
          if (isSpeedSurging) {
            const surgePhase = (surgeCycle - 0.68) / (0.92 - 0.68);
            if (surgePhase > 0.40 && surgePhase < 0.72) {
              // Crest of the surge: outstretched proud aerodynamic glide riding the bow wave!
              birdFrameKey = 'A9Pigeon_Bird_Flap2';
            } else {
              // Rapid vigorous thrust flaps powering forward!
              const fastFlapRate = 18.0;
              const fastCycle = Math.floor(t * fastFlapRate) % 4;
              birdFrameKey = fastCycle === 0 ? 'A9Pigeon_Bird_Flap1' : (fastCycle === 2 ? 'A9Pigeon_Bird_Flap3' : 'A9Pigeon_Bird_Flap2');
            }
          } else {
            const isGliding = !isManeuvering && (glidePeriod > 0.70);
            if (isGliding) {
              birdFrameKey = 'A9Pigeon_Bird_Flap2';
            } else {
              const flapRate = isManeuvering ? 16.0 : 10.5;
              const flapCycle = Math.floor((t * flapRate + (unit.wobbleOffset || 0)) % 4);
              birdFrameKey = flapCycle === 0 ? 'A9Pigeon_Bird_Flap1' : (flapCycle === 2 ? 'A9Pigeon_Bird_Flap3' : 'A9Pigeon_Bird_Flap2');
            }
          }
        }

        const birdBmp = buildSpriteBitmap(birdFrameKey, renderRace, unit.team);
        const birdSrc = isHitFlashing ? birdBmp?.flash : birdBmp?.normal;
        if (birdSrc) {
          ctx.save();
          ctx.translate(currentBirdX, currentBirdY);
          ctx.rotate(currentBirdRot);
          ctx.drawImage(birdSrc, -birdSize / 2, -birdSize / 2, birdSize, birdSize);
          ctx.restore();
        }
      }
    } else if (unitType === 'A9Grizzly') {
      const grizzlyDrawSize = drawSize * 1.12;
      if (this.quality === 'low' || isSimpleTier) {
        let grizzlyKey = 'A9Grizzly';
        if (isWalking) {
          const heavyWheelPhase = walkPhase * 0.58 + (unit.wobbleOffset || 0);
          const cycle = ((heavyWheelPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const rollIdx = Math.floor((cycle / (Math.PI * 2)) * 8) % 8;
          grizzlyKey = A9_GRIZZLY_ROLL_COMPOSITES[rollIdx] || 'A9Grizzly';
        }
        const grizzlyBmp = buildSpriteBitmap(grizzlyKey, renderRace, unit.team) || buildSpriteBitmap('A9Grizzly', renderRace, unit.team);
        const grizzlySrc = isHitFlashing ? grizzlyBmp?.flash : grizzlyBmp?.normal;
        if (grizzlySrc) {
          drawLowTierWalkSprite(ctx, grizzlySrc, renderX, renderY, grizzlyDrawSize, grizzlyDrawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_ROLL);
        }
      } else {
      // 🐻 A9 GRIZZLY ARMORED EXPLORATION VEHICLE (3-Part HD Rig: V-Tread Roller Chassis + Roof Radar + Cupola MG - Scaled +12%)
      let chassisKey = 'A9Grizzly_Chassis';
      if (isWalking) {
        const heavyWheelPhase = walkPhase * 0.58 + (unit.wobbleOffset || 0);
        const cycle = ((heavyWheelPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const rollIdx = Math.floor((cycle / (Math.PI * 2)) * 8) % 8;
        chassisKey = A9_GRIZZLY_CHASSIS_ROLL_FRAMES[rollIdx];
      }
      const radarPhase = ((t * 3.2 + (unit.wobbleOffset || 0)) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const radarRotIdx = Math.floor((radarPhase / (Math.PI * 2)) * 6) % 6;
      const radarKey = A9_GRIZZLY_RADAR_ROT_FRAMES[radarRotIdx];
      const chassisBitmap = buildSpriteBitmap(chassisKey, renderRace, unit.team) || buildSpriteBitmap('A9Grizzly_Chassis', renderRace, unit.team);
      const radarBitmap = buildSpriteBitmap(radarKey, renderRace, unit.team) || buildSpriteBitmap('A9Grizzly_Radar', renderRace, unit.team);
      const turretBitmap = buildSpriteBitmap('A9Grizzly_Turret', renderRace, unit.team);

      const chassisSrc = isHitFlashing ? chassisBitmap.flash : chassisBitmap.normal;
      const radarSrc = radarBitmap ? (isHitFlashing ? radarBitmap.flash : radarBitmap.normal) : null;
      const turretSrc = turretBitmap ? (isHitFlashing ? turretBitmap.flash : turretBitmap.normal) : null;

      // 1. CHASSIS & BRONZE V-TREAD ROLLER WHEELS: Heavy hydraulic suspension kinematics over rough terrain ("Stock und Stein")
      const wobble = unit.wobbleOffset || 0;
      let chassisBob = 0;
      let chassisPitch = 0;

      if (isWalking) {
        // Sparse terrain obstacle profile: mostly smooth and composed, with occasional isolated rocks/bumps
        const worldX = unit.x || renderX;
        const xF = worldX + 14 * forwardDir;
        const xR = worldX - 14 * forwardDir;
        const hF = _sampleGrizzlyTerrain(xF);
        const hR = _sampleGrizzlyTerrain(xR);

        // Heavy industrial hydraulic suspension absorbs ~70% of raw ground deflection
        const terrainBob = -(hF + hR) * 0.30;
        // Wheelbase delay: front wheel rolls over rock first, then rear wheel
        const terrainPitch = -(hF - hR) * 0.013 * forwardDir;
        // High-frequency heavy diesel motor & roller tread vibration
        const engineRumble = Math.sin(t * 18.0 + wobble) * 0.08 + Math.sin(t * 9.2 + wobble) * 0.09;

        chassisBob = terrainBob + engineRumble;
        chassisPitch = terrainPitch;
      } else {
        // Stationary idle: heavy 20-ton chassis idling with deep diesel vibration
        chassisBob = Math.sin(t * 14.0 + wobble) * 0.12;
        chassisPitch = 0;
      }

      ctx.save();
      ctx.translate(renderX, renderY + chassisBob);
      if (chassisPitch !== 0) ctx.rotate(chassisPitch);

      // Front Bullbar Triple Headlight Beam Cone (High/Medium quality)
      // Realistic volumetric halogen searchlight: dual-cone beam, atmospheric dust gradient, ground pool & lens flares
      if (this.quality !== 'low') {
        const prevAlpha = ctx.globalAlpha;
        const lampX = forwardDir * (grizzlyDrawSize * 0.42);
        const lampY = grizzlyDrawSize * 0.14;
        const beamLength = grizzlyDrawSize * 1.15;

        // A & B. Volumetric Cone & Piercing Core (Pre-baked offscreen canvas)
        const coneCanvas = getGrizzlySearchlightConeCanvas();
        if (coneCanvas) {
          const coneW = beamLength;
          const coneH = (coneW / 128) * 48;
          ctx.save();
          ctx.translate(lampX, lampY);
          if (forwardDir < 0) {
            ctx.scale(-1, 1);
          }
          ctx.drawImage(coneCanvas, 0, -18 * (coneH / 48), coneW, coneH);
          ctx.restore();
        }

        // C. Illuminated Ground Pool (Pre-baked offscreen canvas)
        const poolCanvas = getGrizzlyGroundPoolCanvas();
        if (poolCanvas) {
          const poolX = lampX + forwardDir * (beamLength * 0.55);
          const poolY = grizzlyDrawSize * 0.36;
          const poolRadius = beamLength * 0.38;
          ctx.drawImage(poolCanvas, poolX - poolRadius, poolY - poolRadius * 0.30, poolRadius * 2, poolRadius * 2 * 0.30);
        }

        // D. Triple Halogen Lens Flares (Pre-baked offscreen canvas)
        const bulbCanvas = getGrizzlyHalogenBulbCanvas();
        if (bulbCanvas) {
          for (let i = 0; i < 3; i++) {
            const ly = lampY + GRIZZLY_BULB_OFFSETS[i];
            ctx.drawImage(bulbCanvas, lampX - 3.2, ly - 3.2, 6.4, 6.4);
          }
        }

        ctx.globalAlpha = prevAlpha;
      }

      ctx.drawImage(
        chassisSrc,
        -grizzlyDrawSize / 2,
        -grizzlyDrawSize / 2,
        grizzlyDrawSize,
        grizzlyDrawSize
      );

      // 2. ROOF RADAR ARRAY: Full 360-degree continuous rotation (6-phase HD radar scanner)
      if (radarSrc) {
        ctx.drawImage(
          radarSrc,
          -grizzlyDrawSize / 2,
          -grizzlyDrawSize / 2,
          grizzlyDrawSize,
          grizzlyDrawSize
        );
      }

      // 3. FRONT ROOF CUPOLA GUNNER & .50 CAL HEAVY MG: Target elevation tracking, rapid-fire recoil & reload droop
      if (turretSrc) {
        let turretRecoilX = 0;
        let turretRockAngle = 0;
        const attackSpdVal = unit.attackSpeed || 0.1;
        if (!unit.isReloading && unit.attackCooldown > 0 && unit.attackCooldown > (attackSpdVal - 0.08)) {
          const recoilFraction = (unit.attackCooldown - (attackSpdVal - 0.08)) / 0.08;
          const spring = dampedSpringRecoil(recoilFraction);
          turretRecoilX = -spring * 2.0 * forwardDir;
          turretRockAngle = -spring * 0.045 * forwardDir;
        }

        let turretAimY = 0;
        if (unit.target && !unit.target.isDead && !unit.isReloading) {
          const dTargetY = unit.target.y - unit.y;
          turretAimY = Math.max(-2.5, Math.min(2.5, dTargetY * 0.03));
        }

        // During 2s reload pause, barrel tilts slightly downward for ammo belt replacement
        const reloadTilt = unit.isReloading ? (0.065 * forwardDir) : 0;
        const reloadDropY = unit.isReloading ? 0.8 : 0;
        const cupolaPivotX = forwardDir * (grizzlyDrawSize * 0.12);
        const cupolaPivotY = -grizzlyDrawSize * 0.20;

        ctx.save();
        ctx.translate(cupolaPivotX, cupolaPivotY);
        ctx.rotate(turretRockAngle + reloadTilt);
        ctx.translate(-cupolaPivotX, -cupolaPivotY);
        ctx.drawImage(
          turretSrc,
          -grizzlyDrawSize / 2 + turretRecoilX,
          -grizzlyDrawSize / 2 + turretAimY + reloadDropY,
          grizzlyDrawSize,
          grizzlyDrawSize
        );
        ctx.restore();
      }

      // Cooling barrel smoke wisp during 2s reload pause
      if (unit.isReloading && this.quality !== 'low') {
        const smokeProg = (t * 2.2 + unit.wobbleOffset) % 1.0;
        const prevAlpha = ctx.globalAlpha;
        ctx.globalAlpha = prevAlpha * (1.0 - smokeProg) * 0.55;
        ctx.fillStyle = '#cbd5e1';
        const barrelTipX = forwardDir * (grizzlyDrawSize * 0.44);
        const barrelTipY = -grizzlyDrawSize * 0.22 - smokeProg * 8;
        ctx.fillRect(barrelTipX - 1.5, barrelTipY - 1.5, 2.5, 2.5);
        ctx.globalAlpha = prevAlpha;
      }

      ctx.restore();
      }
    } else if (unitType === 'SentinalLock') {
      if (this.quality === 'low' || isSimpleTier) {
        const numId = getUnitNumericId(unit);
        const rollIdx = Math.floor(Math.abs((t * 2.4 + numId * 0.7) % 4));
        const sentinalKey = SENTINAL_LOCK_ROLL_COMPOSITES[rollIdx] || 'SentinalLock';
        const sentinalBmp = buildSpriteBitmap(sentinalKey, renderRace, unit.team);
        const sentinalSrc = isHitFlashing ? sentinalBmp?.flash : sentinalBmp?.normal;
        if (sentinalSrc) {
          ctx.drawImage(sentinalSrc, renderX - drawSize / 2, renderY - drawSize / 2, drawSize, drawSize);
        }
      } else {
      // 🦇 SENTINAL LOCK "SPITFIRE": Three-Winged Alien Bat, Barrel Roll
      // Rolls around its OWN FORWARD (flight) axis - not around the axis
      // pointing at the camera, which is what a plain ctx.rotate() of the
      // whole sprite would fake (that reads as a flat spin/yaw, and drags
      // the head/nose around with it). Viewed from this side-scroller
      // camera, a true barrel roll instead means: the fuselage never
      // visibly rotates at all (the nose keeps pointing forward, so it's
      // drawn completely static), while each wing appears to swing
      // edge-on-and-back through the screen's depth axis - its on-screen
      // height scales by cos(phase) (shrinking to a sliver at the edge-on
      // point, reappearing pointing the other way past it) and it swaps in
      // front of or behind the fuselage depending on which half of the
      // roll it's currently in (sin(phase) > 0 = swung toward the camera =
      // drawn on top). Three wings, phases 120° apart, so at any instant
      // some are near full height while others are collapsing - exactly
      // how a barrel-rolling multi-wing craft reads from the side. Purely
      // a client-side render choice - no server/snapshot state involved
      // (deterministic from unit.age/id).
      const bodyBitmap = buildSpriteBitmap('SentinalLock_Body', renderRace, unit.team);
      const bodySrc = isHitFlashing ? bodyBitmap.flash : bodyBitmap.normal;
      const wingBitmap = buildSpriteBitmap('SentinalLock_WingPanel', renderRace, unit.team);
      const wingSrc = isHitFlashing ? wingBitmap.flash : wingBitmap.normal;

      const numId = getUnitNumericId(unit);
      const rollBase = t * 0.6 + numId * 0.7;
      // Wing root: dorsal point just behind/above the head (grid row 11 of
      // 24, col 12 of 24 in SentinalLock_Body - see _buildSentinalLockBody).
      const attachX = renderX;
      const attachY = renderY - drawSize * 0.042;
      // SentinalLock_WingPanel's own root pixel sits at grid row 22, col 12
      // of its 24x24 canvas - this offset places that pixel at (0,0) after
      // the translate/scale below, so the panel grows/shrinks from the
      // attach point instead of from its bitmap's top-left corner.
      const wingOffsetX = -drawSize * 0.5;
      const wingOffsetY = -drawSize * 0.9167;

      const phase0 = rollBase;
      const phase1 = rollBase + (Math.PI * 2 / 3);
      const phase2 = rollBase + (Math.PI * 4 / 3);

      // Wings currently swung away from the camera go behind the fuselage...
      for (let w = 0; w < 3; w++) {
        const p = (w === 0) ? phase0 : (w === 1 ? phase1 : phase2);
        if (Math.sin(p) <= 0) {
          const s = Math.cos(p);
          if (Math.abs(s) >= 0.03) {
            ctx.save();
            ctx.translate(attachX, attachY);
            ctx.scale(1, s);
            ctx.drawImage(wingSrc, wingOffsetX, wingOffsetY, drawSize, drawSize);
            ctx.restore();
          }
        }
      }

      ctx.drawImage(
        bodySrc,
        renderX - drawSize / 2,
        renderY - drawSize / 2,
        drawSize,
        drawSize
      );

      // Leicht glimmender lila Schnauzen-Tip: the only part of the body
      // left un-recolored to reddish-brown (see _buildSentinalLockBody) -
      // a small pulsing additive glow on top of that bitmap sells the
      // "still lit" read instead of relying on the flat sprite color alone.
      if (this.quality !== 'low') {
        const tipGlowPulse = 0.65 + 0.35 * Math.sin(t * 3.4 + numId);
        const tipGlowR = drawSize * 0.11;
        const tipX = renderX + drawSize * 0.31;
        const tipY = renderY - drawSize * 0.06;
        const tipGlowTex = getSentinalLockHeadGlowCanvas();
        ctx.save();
        ctx.globalAlpha = 0.8 * tipGlowPulse;
        ctx.drawImage(tipGlowTex, tipX - tipGlowR, tipY - tipGlowR, tipGlowR * 2, tipGlowR * 2);
        ctx.restore();
      }

      // Vestigial tail tentacle stubs: 3 TINY appendages ("wirklich nur
      // Ansätze") at the tail/torso joint (grid row~18, col~3 of
      // SentinalLock_Body - see _buildSentinalLockBody section 8's tail),
      // each a single short stroke rather than a full curved filament
      // (unlike Apostate's longer chin cirri above) - just enough to read
      // as loose growths being dragged along by the unit's own motion.
      // Phase runs faster than (but stays weakly coupled to, via rollBase)
      // the barrel roll so the swing itself is actually visible frame to
      // frame - an earlier version's amplitude (0.025 * drawSize) came out
      // under 1px at typical unit scale, i.e. imperceptible.
      if (this.quality !== 'low') {
        ctx.save();
        ctx.lineCap = 'round';
        const tailAttachX = renderX - drawSize * 0.37;
        const tailAttachY = renderY + drawSize * 0.24;
        for (let c = 0; c < 3; c++) {
          const stubOffX = (c - 1) * (drawSize * 0.045);
          const stubPhase = t * 5.5 + rollBase * 0.4 + c * 2.1;
          const stubLen = drawSize * 0.05;
          const stubSwing = Math.sin(stubPhase) * (drawSize * 0.09);
          const b1X = tailAttachX + stubOffX + stubSwing * 0.25;
          const b1Y = tailAttachY;
          const b2X = b1X - stubSwing;
          const b2Y = b1Y + stubLen;

          ctx.strokeStyle = (c % 2 === 0) ? '#7c2d12' : '#9a3412';
          ctx.lineWidth = 1.1;
          ctx.beginPath();
          ctx.moveTo(b1X, b1Y);
          ctx.lineTo(b2X, b2Y);
          ctx.stroke();
        }
        ctx.restore();
      }

      // ...and wings currently swung toward the camera go on top of it.
      for (let w = 0; w < 3; w++) {
        const p = (w === 0) ? phase0 : (w === 1 ? phase1 : phase2);
        if (Math.sin(p) > 0) {
          const s = Math.cos(p);
          if (Math.abs(s) >= 0.03) {
            ctx.save();
            ctx.translate(attachX, attachY);
            ctx.scale(1, s);
            ctx.drawImage(wingSrc, wingOffsetX, wingOffsetY, drawSize, drawSize);
            ctx.restore();
          }
        }
      }
      }
    } else if (unitType === 'MonitorLock' || unitType === 'Monitor_Lock' || unitType === 'Monitor Lock') {
      // 𓉐 SUL MONITOR LOCK: Giant Serpentine Guardian Worm
      // Erupts from ground crater over 3.0s, twines in air, snaps at enemies with full-body kinematics
      const numId = getUnitNumericId(unit);
      const isGrowing = !!unit.isGrowing;
      const growthDuration = unit.growthDuration || 3.0;
      const growthProgress = Math.min(1.0, (unit.growthTimer || 0) / growthDuration);
      const isSnapping = (unit.snapStrikeTimer || 0) > 0;
      const strikeDuration = unit.snapStrikeDuration || 1.2;
      const strikeT = isSnapping ? Math.max(0, Math.min(1, 1.0 - (unit.snapStrikeTimer / strikeDuration))) : 0;

      // Scaled down 30% per user request (1.75 * 0.70 = 1.225)
      const mlSize = drawSize * 1.225;
      const groundAnchorY = renderY;

      // 1. Crater base (always grounded at the ground plane, row 23 = groundAnchorY)
      // Perfectly harmonized 24x24 scale with zero offset so worm base is 100% embedded inside
      const craterSize = mlSize;
      const craterDrawX = renderX - craterSize / 2;
      const craterDrawY = groundAnchorY - craterSize;
      const craterBmp = buildSpriteBitmap('MonitorLock_Crater', renderRace, unit.team);
      const craterSrc = isHitFlashing ? craterBmp.flash : craterBmp.normal;
      ctx.drawImage(
        craterSrc,
        craterDrawX,
        craterDrawY,
        craterSize,
        craterSize
      );

      // 4-Phase Eruption Choreography ("optisch gerade aus dem boden schießt")
      let growthScaleY = 0;
      let eruptionBendFactor = 1.0; // 0 = straight vertical, 1 = normal serpentine undulation
      let eruptionFlare = 0;
      let eruptionJawOpen = false;

      if (isGrowing) {
        if (growthProgress < 0.12) {
          // Phase 1: Subterranean Rumble (0.0s - 0.36s) - still underground, earth shakes
          growthScaleY = 0.0;
          eruptionBendFactor = 0.0;
        } else if (growthProgress < 0.32) {
          // Phase 2: Explosive Rocket Ejection (0.36s - 0.96s) - shoots straight up out of the crater!
          const pEject = (growthProgress - 0.12) / 0.20;
          const rocketEase = Math.sin(pEject * Math.PI * 0.5);
          const overshoot = 1.0 + 0.12 * Math.sin(pEject * Math.PI);
          growthScaleY = Math.max(0.04, rocketEase * overshoot);
          eruptionBendFactor = 0.0; // 100% straight vertical!
          eruptionFlare = Math.sin(pEject * Math.PI * 0.8); // Crown flaring during breach
          eruptionJawOpen = (pEject > 0.3 && pEject < 0.9); // Roar / threat display as it breaches
        } else if (growthProgress < 0.60) {
          // Phase 3: Majestic Towering Apex Pose (0.96s - 1.80s) - erect titan surveying the field
          const pApex = (growthProgress - 0.32) / 0.28;
          growthScaleY = 1.12 - pApex * 0.12; // settles from 1.12 to 1.0
          eruptionBendFactor = pApex * 0.15; // very subtle start of awareness, still erect
          eruptionFlare = Math.max(0, 0.8 * (1.0 - pApex));
          eruptionJawOpen = (pApex < 0.25);
        } else {
          // Phase 4: Serpentine S-Coil Transition (1.80s - 3.00s) - smooth easing into guardian coil
          const pSettle = (growthProgress - 0.60) / 0.40;
          const smoothSettle = 0.5 - 0.5 * Math.cos(pSettle * Math.PI);
          growthScaleY = 1.0;
          eruptionBendFactor = 0.15 + 0.85 * smoothSettle; // seamlessly blends into full serpentine undulation!
          eruptionFlare = 0;
          eruptionJawOpen = false;
        }
      }

      // High-impact Ground Eruption: Flying basalt boulders, earthen clods & shockwave rings (Punkt B)
      if (isGrowing && this.quality !== 'low' && growthProgress >= 0.10) {
        const breachT = (growthProgress - 0.10) / 0.90; // 0.0 to 1.0 from breach to ready

        // 1. Expanding ground shockwave rings
        if (breachT < 0.55) {
          const swLife = breachT / 0.55;
          const ringR = mlSize * (0.2 + 0.85 * swLife);
          const ringAlpha = (1.0 - swLife) * 0.85;
          ctx.save();
          // Inner cyan energy ring
          ctx.globalAlpha = ringAlpha;
          ctx.strokeStyle = '#00f0ff';
          ctx.lineWidth = Math.max(1.2, 2.5 * (1 - swLife));
          ctx.beginPath();
          ctx.ellipse(renderX, groundAnchorY, ringR, ringR * 0.38, 0, 0, Math.PI * 2);
          ctx.stroke();

          // Outer secondary compression ring
          if (swLife > 0.15) {
            const sw2 = (swLife - 0.15) / 0.85;
            ctx.globalAlpha = (1.0 - sw2) * 0.5;
            ctx.strokeStyle = '#38bdf8';
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.ellipse(renderX, groundAnchorY, ringR * 1.25, ringR * 0.45, 0, 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.restore();
        }

        // 2. Ballistic flying rock, basalt & earthen boulders (18 pieces on desktop high/ultra, 6 on mobile/medium)
        const DEBRIS_COUNT = (!this.isMobile || this.quality === 'high' || this.quality === 'ultra') ? 18 : 6;
        for (let ci = 0; ci < DEBRIS_COUNT; ci++) {
          const seed = numId * 17 + ci * 43;
          // Staggered launch times so fragments erupt continuously as the worm breaches
          const launchFrac = 0.02 + (ci % 6) * 0.035;
          const pieceAge = (breachT - launchFrac) / 0.65;
          if (pieceAge <= 0 || pieceAge >= 1.0) continue;

          // Ballistic trajectory: high upward velocity with gravity pull-down
          const ang = ((seed % 360) / 360) * Math.PI * 2;
          const speedX = ((seed % 47) / 47) * 0.75 + 0.35;
          const speedY = ((seed % 53) / 53) * 0.85 + 0.75;
          const distHoriz = Math.cos(ang) * mlSize * 0.95 * speedX * pieceAge;
          const upwardArc = speedY * mlSize * 2.1 * pieceAge;
          const gravityDrop = 2.4 * mlSize * (pieceAge * pieceAge);

          const chipX = renderX + distHoriz;
          const chipY = groundAnchorY - upwardArc + gravityDrop;

          // Size variation: big boulders (7-9px), medium rubble (4-6px), fine shrapnel (2-3px)
          const baseSz = (ci % 3 === 0) ? 0.09 : (ci % 3 === 1) ? 0.06 : 0.04;
          const chipSize = mlSize * baseSz * (1.0 - pieceAge * 0.3);
          const fadeAlpha = pieceAge < 0.75 ? 1.0 : (1.0 - (pieceAge - 0.75) / 0.25);

          ctx.save();
          ctx.translate(chipX, chipY);
          ctx.rotate(seed + pieceAge * (ci % 2 === 0 ? 11 : -14));

          // Color classification: Dark basalt rock, rich earthen soil, glowing Sul mineral
          const isCyanShard = (ci % 5 === 0);
          const isBasalt = (ci % 2 === 0);

          if (isCyanShard) {
            // Glowing cyan mineral fragment
            ctx.globalAlpha = fadeAlpha;
            ctx.fillStyle = '#00f0ff';
            ctx.fillRect(-chipSize / 2, -chipSize / 2, chipSize, chipSize);
            ctx.globalAlpha = fadeAlpha * 0.8;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(-chipSize / 4, -chipSize / 4, chipSize / 2, chipSize / 2);
          } else {
            // 3D shaded rock boulder (sunlit facet & shadow facet)
            const mainCol = isBasalt ? '#292524' : '#78350f';
            const shadowCol = isBasalt ? '#0f172a' : '#451a03';
            const lightCol = isBasalt ? '#57534e' : '#92400e';

            ctx.globalAlpha = fadeAlpha;
            ctx.fillStyle = mainCol;
            ctx.fillRect(-chipSize / 2, -chipSize / 2, chipSize, chipSize);
            // Highlight edge
            ctx.fillStyle = lightCol;
            ctx.fillRect(-chipSize / 2, -chipSize / 2, chipSize, Math.max(1, chipSize * 0.35));
            // Shadow edge
            ctx.fillStyle = shadowCol;
            ctx.fillRect(-chipSize / 2, chipSize * 0.15, chipSize, Math.max(1, chipSize * 0.35));
          }
          ctx.restore();
        }

        // 3. Billowing dust puffs at crater rim
        if (breachT < 0.50) {
          const dustT = breachT / 0.50;
          const dustPuffCount = (!this.isMobile || this.quality === 'high' || this.quality === 'ultra') ? 6 : 2;
          for (let di = 0; di < dustPuffCount; di++) {
            const dAngle = (di / dustPuffCount) * Math.PI * 2;
            const dDist = mlSize * (0.28 + 0.35 * dustT);
            const dx = renderX + Math.cos(dAngle) * dDist;
            const dy = groundAnchorY + Math.sin(dAngle) * dDist * 0.35 - (dustT * mlSize * 0.18);
            const dRadius = mlSize * (0.07 + 0.12 * dustT);
            const dAlpha = (1.0 - dustT) * 0.35;
            ctx.save();
            ctx.globalAlpha = dAlpha;
            ctx.fillStyle = (di % 2 === 0) ? '#78350f' : '#57534e';
            ctx.beginPath();
            ctx.arc(dx, dy, dRadius, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }
        }
      }

      // 2. DYNAMIC ELEVATION & BODY HEIGHT ("mal etwas hoch, mal runterfahren")
      // Anchored strictly at groundAnchorY (the crater hole) so the tail never slides out of the hole!
      const hasTarget = !!(unit.target && !unit.target.isDead);
      const isEngaged = !!(unit.isEngaged || hasTarget);

      // Natural breathing elevation surge (slow, hypnotic undulating vertical oscillation)
      const breathSurge = Math.sin(t * 1.4 + numId * 1.7) * 0.08;
      // Combat rearing stance: when engaged or tracking enemies, the worm rears up taller
      const combatRearing = isEngaged ? 0.12 : 0.0;
      // Hit flinch compression
      const hitCompression = isHitFlashing ? -0.10 : 0.0;

      const totalScaleY = isGrowing
        ? growthScaleY
        : Math.max(0.65, 1.0 + breathSurge + combatRearing + hitCompression);

      // Soil vibration / shake during burst or heavy damage
      const burstShake = (isGrowing && growthProgress < 0.25)
        ? Math.sin(t * 52 + numId) * mlSize * 0.05 * (1 - growthProgress / 0.25)
        : 0;

      // 3. KINEMATICS: HARVESTER-STYLE ARTICULATED SLOW STRETCHING ATTACK
      // Modelled after the articulated Harvester tentacle reach: slow, creeping uncoiling with dynamic spinal elongation
      const FIXED_JOINT_FRAC = 18 / 24;
      const LOW_JOINT_FRAC   = 14 / 24;
      const MID_JOINT_FRAC   = 10 / 24;
      const NECK_ROW_FRAC    = 6 / 24;

      // Full-body serpentine undulation wave ("der ganze körper soll sich bewegen")
      const undulateFreq = isEngaged ? 3.4 : 1.9;
      const wave = t * undulateFreq + numId * 1.3;
      const combatLean = isEngaged ? dir * 0.14 : 0;

      let sway1 = Math.sin(wave) * (isEngaged ? 0.07 : 0.04);
      let sway2 = Math.sin(wave - 0.8) * (isEngaged ? 0.11 : 0.06) + combatLean * 0.35;
      let sway3 = Math.sin(wave - 1.6) * (isEngaged ? 0.15 : 0.09) + combatLean * 0.55;
      let headLook = Math.sin(t * 1.1 + numId * 2.1) * 0.14 + (isEngaged ? combatLean * 0.4 : 0);

      // Hit reaction flinch ("Treffer an der Animation spüren")
      let flinchAngle = 0;
      let flinchX = 0;
      if (isHitFlashing) {
        const shudder = Math.sin(t * 70) * (mlSize * 0.035);
        flinchX = -dir * mlSize * 0.07 + shudder;
        flinchAngle = -dir * 0.20;
      }

      // Snapping attack strike calculation ("greift der wurm an, soll er mit dem kopf danach, den körper verbiegen um zum gegner zu gelangen")
      let targetAngle = dir * 0.85;
      let snapTarget = null;
      if (unit.snapTargetId && battlefield?.getUnitById) snapTarget = battlefield.getUnitById(unit.snapTargetId);
      else if (unit.target && !unit.target.isDead) snapTarget = unit.target;

      if (snapTarget && !snapTarget.isDead) {
        const dxT = snapTarget.x - renderX;
        const dyT = (snapTarget.y - 12) - (groundAnchorY - mlSize * 0.5);
        targetAngle = Math.atan2(dxT, -dyT);
      }
      const maxBend = 1.15; // ~66 degrees reaching predatory lunge
      targetAngle = Math.max(-maxBend, Math.min(maxBend, targetAngle));

      let lungeReach = 0;
      let stretchBonus = 0;
      let jawOpen = false;

      if (isSnapping) {
        if (strikeT < 0.26) {
          // Phase 1: Deep threat tension & coiling back (slow, menacing windup)
          const p = strikeT / 0.26;
          lungeReach = -0.16 * Math.sin(p * Math.PI * 0.5);
          stretchBonus = -0.06 * Math.sin(p * Math.PI);
          jawOpen = p > 0.45;
        } else if (strikeT < 0.68) {
          // Phase 2: Deliberate creeping extension & dynamic spinal stretch (slow, menacing reach towards target)
          const p = (strikeT - 0.26) / 0.42;
          const smoothP = 0.5 - 0.5 * Math.cos(p * Math.PI);
          lungeReach = smoothP;
          stretchBonus = 0.50 * Math.sin(smoothP * Math.PI * 0.5); // segments lengthen up to +50%!
          jawOpen = true; // mouth wide open revealing fangs & gullet
        } else if (strikeT < 0.78) {
          // Phase 3: Apex snap & crushing bite clamp on victim!
          const p = (strikeT - 0.68) / 0.10;
          lungeReach = 1.0 - p * 0.08;
          stretchBonus = 0.50 * (1.0 - p * 0.15);
          jawOpen = false; // VIOLENT SNAP SHUT!
        } else {
          // Phase 4: Heavy, fluid recoil back to guardian coil
          const p = (strikeT - 0.78) / 0.22;
          const recEase = Math.sin(p * Math.PI * 0.5);
          lungeReach = 0.92 * (1.0 - recEase);
          stretchBonus = 0.42 * (1.0 - recEase);
          jawOpen = false;
        }
      }

      if (isGrowing && eruptionJawOpen) jawOpen = true;

      // Peristaltic muscular ripples traveling through the spine during reach
      const ripple1 = isSnapping ? Math.sin(strikeT * Math.PI * 3.0) * 0.08 : 0;
      const ripple2 = isSnapping ? Math.sin(strikeT * Math.PI * 3.0 - 0.9) * 0.12 : 0;
      const ripple3 = isSnapping ? Math.sin(strikeT * Math.PI * 3.0 - 1.8) * 0.16 : 0;

      const snapBend = targetAngle * lungeReach;
      const bend1 = isGrowing
        ? (sway1 * eruptionBendFactor)
        : (sway1 + snapBend * 0.32 + ripple1 + flinchAngle * 0.2);
      const bend2 = isGrowing
        ? (sway2 * eruptionBendFactor)
        : (sway2 + snapBend * 0.44 + ripple2 + flinchAngle * 0.3);
      const bend3 = isGrowing
        ? (sway3 * eruptionBendFactor)
        : (sway3 + snapBend * 0.36 + ripple3 + flinchAngle * 0.4);
      const finalHeadAngle = isGrowing
        ? (headLook * eruptionBendFactor)
        : (isSnapping
            ? (targetAngle * 0.28 * lungeReach + (jawOpen ? 0.06 * dir : -0.10 * dir))
            : (headLook + flinchAngle * 0.2));

      // Dynamic spinal segment lengths (Harvester-style stretching reach)
      const fixedDstH = mlSize * (1 - FIXED_JOINT_FRAC);
      const lowDstH   = mlSize * (FIXED_JOINT_FRAC - LOW_JOINT_FRAC) * (1.0 + stretchBonus * 0.5);
      const midDstH   = mlSize * (LOW_JOINT_FRAC - MID_JOINT_FRAC)   * (1.0 + stretchBonus * 0.8);
      const neckDstH  = mlSize * (MID_JOINT_FRAC - NECK_ROW_FRAC)    * (1.0 + stretchBonus * 1.0);

      const headKey = jawOpen ? 'MonitorLock_Head_Bite_Face' : 'MonitorLock_Head_Face';
      const bodyBmp = buildSpriteBitmap('MonitorLock_Body', renderRace, unit.team);
      const bodySrc = isHitFlashing ? bodyBmp.flash : bodyBmp.normal;
      const headBmp = buildSpriteBitmap(headKey, renderRace, unit.team);
      const headSrc = isHitFlashing ? headBmp.flash : headBmp.normal;
      const bmSize = bodyBmp.size;

      ctx.save();
      // Clip rendering to above crater ground line during growth eruption
      if (isGrowing) {
        ctx.beginPath();
        ctx.rect(renderX - mlSize * 1.3, groundAnchorY - mlSize * 2.2, mlSize * 2.6, mlSize * 2.2 + mlSize * 0.4);
        ctx.clip();
      }

      // Outer transform: tail-anchored position + vertical elevation scale
      ctx.translate(renderX + burstShake + flinchX, groundAnchorY);
      ctx.scale(1, totalScaleY);

      // Segment 0: Fixed base (rows 18-23). Drawn anchored at (0, 0) with zero rotation -> 100% stable in hole!
      const fixedSrcY = FIXED_JOINT_FRAC * bmSize;
      ctx.drawImage(bodySrc, 0, fixedSrcY, bmSize, bmSize - fixedSrcY, -mlSize / 2, -fixedDstH, mlSize, fixedDstH);

      // Segment 1: Lower Trunk (rows 14-18), hinged at the top of the fixed base
      ctx.save();
      ctx.translate(0, -fixedDstH);
      ctx.rotate(bend1);
      const lowSrcY = LOW_JOINT_FRAC * bmSize;
      ctx.drawImage(bodySrc, 0, lowSrcY, bmSize, fixedSrcY - lowSrcY, -mlSize / 2, -lowDstH, mlSize, lowDstH);

      // Segment 2: Mid Body (rows 10-14), hinged at the top of lower trunk
      ctx.save();
      ctx.translate(0, -lowDstH);
      ctx.rotate(bend2);
      const midSrcY = MID_JOINT_FRAC * bmSize;
      ctx.drawImage(bodySrc, 0, midSrcY, bmSize, lowSrcY - midSrcY, -mlSize / 2, -midDstH, mlSize, midDstH);

      // Segment 3: Upper Torso / Neck (rows 6-10), hinged at the top of mid body
      ctx.save();
      ctx.translate(0, -midDstH);
      ctx.rotate(bend3);
      const neckSrcY = NECK_ROW_FRAC * bmSize;
      ctx.drawImage(bodySrc, 0, neckSrcY, bmSize, midSrcY - neckSrcY, -mlSize / 2, -neckDstH, mlSize, neckDstH);

      // Segment 4: Head & Armored Jaws, hinged at the top of the neck
      const headOffsetY = -mlSize * NECK_ROW_FRAC;
      ctx.save();
      ctx.translate(0, -neckDstH);
      ctx.rotate(finalHeadAngle);

      // Animated Platin-Chrom Crown Spines (organic micro-movement & pseudo-random rare flaring)
      drawMonitorLockCrownSpines(
        ctx,
        mlSize,
        headOffsetY,
        isWestern,
        isHitFlashing,
        t,
        numId,
        isSnapping,
        strikeT,
        this.quality,
        eruptionFlare
      );

      ctx.drawImage(headSrc, -mlSize / 2, headOffsetY, mlSize, mlSize);

      // Glowing Facet Eye ("Monitor") additive pulse & flaring during strike
      if (this.quality !== 'low' && !isGrowing) {
        const eyeGlowTex = getMonitorLockEyeGlowCanvas();
        if (eyeGlowTex) {
          const eyePulseSpeed = isEngaged ? 6.2 : 3.0;
          const eyePulse = 0.75 + 0.25 * Math.sin(t * eyePulseSpeed + numId);
          const eyeGlowR = mlSize * (isSnapping ? 0.24 : 0.16);
          const eyeX = (16 / 24 - 0.5) * mlSize * dir;
          const eyeY = headOffsetY + (4 / 24) * mlSize;
          ctx.save();
          ctx.globalAlpha = (isSnapping ? 1.0 : 0.85) * eyePulse;
          ctx.drawImage(eyeGlowTex, eyeX - eyeGlowR, eyeY - eyeGlowR, eyeGlowR * 2, eyeGlowR * 2);
          ctx.restore();
        }
      }

      ctx.restore(); // head
      ctx.restore(); // upper torso / neck
      ctx.restore(); // mid body
      ctx.restore(); // lower trunk
      ctx.restore(); // outer transform

      // 4. Foreground Crater Basalt Teeth & Uplifted Lip (overlaps lower trunk base -> ZERO GAP)
      const craterFrontBmp = buildSpriteBitmap('MonitorLock_Crater_Front', renderRace, unit.team);
      const craterFrontSrc = isHitFlashing ? craterFrontBmp.flash : craterFrontBmp.normal;
      ctx.drawImage(
        craterFrontSrc,
        craterDrawX,
        craterDrawY,
        craterSize,
        craterSize
      );
    } else if (unitType === 'TheSalvageCompany') {
      // 🚜 THE SALVAGE COMPANY: Super-Heavy Industrial Scrap Recovery Crawler
      // Scaled up imposing footprint & massive silhouette
      const salvageDrawSize = drawSize * 1.15;
      const fwd = unit.team === 'western' ? 1 : -1;
      const numId = getUnitNumericId(unit);

      if (this.quality === 'low' || isSimpleTier) {
        // Low Quality Tier: Single pre-baked composite blit (1 draw call, zero rig kinematics)
        let compKey = 'TheSalvageCompany';
        if (isWalking && unit.speed > 0) {
          const rollPhase = Math.floor((walkPhase * 0.275 + numId * 0.5) % 6);
          compKey = SALVAGE_ROLL_COMPOSITES[rollPhase] || 'TheSalvageCompany';
        }
        const compBitmap = buildSpriteBitmap(compKey, renderRace, unit.team) || buildSpriteBitmap('TheSalvageCompany', renderRace, unit.team);
        const compSrc = isHitFlashing ? compBitmap?.flash : compBitmap?.normal;
        if (compSrc) {
          drawLowTierWalkSprite(ctx, compSrc, renderX, renderY, salvageDrawSize, salvageDrawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_ROLL);
        }
      } else {
        // High / Medium Quality Tier: Multi-Part Kinematic Rig with rolling wheels & heavy suspension
        // Heavy, massive movement: Hoppeln vollständig entfernt (100% flach und satt am Boden)
        const heavyChug = 0;
        const heavyPitch = 0;

        // 1. Animated Rolling Heavy Industrial Road Wheels (langsamer, würdevoller 50-Tonnen-Schwerlast-Crawl)
        // 6-Phasen Stollen-Crawl bei halbiertem Tempo (ruhig, monumental, kein Sliden)
        const isRolling = ((unit.speed > 0 && !unit.isDisabled) || (unit.isDisabled && walkPitch > 0.01)) && !unit.isDead;
        const rollPhase = isRolling ? Math.floor((walkPhase * 0.275 + numId * 0.5) % 6) : 0;
        const chassisKey = SALVAGE_CHASSIS_ROLL_FRAMES[rollPhase] || 'TheSalvageCompany_Chassis';
        const chassisBitmap = buildSpriteBitmap(chassisKey, renderRace, unit.team);
        const chassisSrc = isHitFlashing ? chassisBitmap.flash : chassisBitmap.normal;

        ctx.save();
        ctx.translate(renderX, renderY + heavyChug);
        ctx.rotate(heavyPitch);
        ctx.translate(-renderX, -(renderY + heavyChug));

        ctx.drawImage(
          chassisSrc,
          renderX - salvageDrawSize / 2,
          renderY - salvageDrawSize / 2 + heavyChug,
          salvageDrawSize,
          salvageDrawSize
        );

        // 2. Phasenweise aktiver Greifarm: 11 fein abgestufte Kinematik-Phasen (flüssiger Arbeitszyklus)
        // Konstante Segmentlängen (Boom 13.0px, Stick 13.4px) – kein Teleskopieren/Strecken.
        // 0: Rest in Wiege (25%)
        // 1: Ausheben nach oben (7%)
        // 2: Hochschwenken über Kabine (8%)
        // 3: Absenken nach vorn (8%)
        // 4: Bodenkontakt / Klaue weit offen (8%)
        // 5: Zupacken am Boden um Schrott (12%)
        // 6: Kraftvolles Heben (8%)
        // 7: Rückschwenken im Zenit über Kabine (8%)
        // 8: Über Mulde positioniert (6%)
        // 9: Abladen in Mulde / Schrottfall (6%)
        // 10: Einfalten zurück in Wiege (4%)
        const cycleProgress = ((t + numId * 1.37) % SALVAGE_CYCLE_DURATION) / SALVAGE_CYCLE_DURATION;
        let armIndex = 0;
        if (cycleProgress < 0.25) armIndex = 0;
        else if (cycleProgress < 0.32) armIndex = 1;
        else if (cycleProgress < 0.40) armIndex = 2;
        else if (cycleProgress < 0.48) armIndex = 3;
        else if (cycleProgress < 0.56) armIndex = 4;
        else if (cycleProgress < 0.68) armIndex = 5;
        else if (cycleProgress < 0.76) armIndex = 6;
        else if (cycleProgress < 0.84) armIndex = 7;
        else if (cycleProgress < 0.90) armIndex = 8;
        else if (cycleProgress < 0.96) armIndex = 9;
        else armIndex = 10;

        const armFrameName = SALVAGE_ARM_FRAMES[armIndex];
        const armBitmap = buildSpriteBitmap(armFrameName, renderRace, unit.team);
        const armSrc = isHitFlashing ? armBitmap.flash : armBitmap.normal;

        ctx.drawImage(
          armSrc,
          renderX - salvageDrawSize / 2,
          renderY - salvageDrawSize / 2 + heavyChug,
          salvageDrawSize,
          salvageDrawSize
        );

        // 3. Quality-Gated Cosmetic Particles (Invariante #4 & #10: Diesel Exhaust Puffs)
        if (this.quality !== 'low') {
          const exhaustX = renderX - fwd * (salvageDrawSize * 0.02);
          const exhaustY = renderY - salvageDrawSize * 0.28 + heavyChug;
          const puffPulse = Math.sin(t * 8 + numId);
          if (puffPulse > 0.35) {
            ctx.fillStyle = 'rgba(87, 83, 78, 0.45)';
            ctx.beginPath();
            ctx.arc(exhaustX, exhaustY - (puffPulse * 4), 2.5 + puffPulse * 1.5, 0, Math.PI * 2);
            ctx.fill();
          }

          // Scrap clatter sparks while dumping
          if (armIndex === 9 && Math.sin(t * 24) > 0.4) {
            const hopperX = renderX - fwd * (salvageDrawSize * 0.22);
            const hopperY = renderY - salvageDrawSize * 0.05 + heavyChug;
            ctx.fillStyle = '#facc15';
            ctx.fillRect(hopperX + (Math.sin(t * 30) * 4), hopperY, 2, 2);
          }
        }
        ctx.restore();
      }
    } else if (unitType === 'Orchestra') {
      // 🚚 ORCHESTRA: Heavy Long-Range 8x8 Rocket Artillery Transporter (BM-30 Smerch Profile, 20x20)
      const orchDrawSize = drawSize * 1.25;
      const fwd = unit.team === 'western' ? 1 : -1;
      const numId = getUnitNumericId(unit);

      if (this.quality === 'low' || isSimpleTier) {
        const inRange = !!(unit.target && battlefield?.isInRange?.(unit, unit.target));
        const deployVal = unit.deployProgress !== undefined ? unit.deployProgress : (unit._deployProgress || 0);

        if (deployVal > 0.001 || inRange) {
          // Stationary & Deploying / Aiming: elevate rocket organ smoothly (2 draw calls, 1 simple rotate)
          const chassisBmp = buildSpriteBitmap('Orchestra_Chassis', renderRace, unit.team);
          const chassisSrc = isHitFlashing ? chassisBmp?.flash : chassisBmp?.normal;
          if (chassisSrc) {
            ctx.drawImage(chassisSrc, renderX - orchDrawSize / 2, renderY - orchDrawSize / 2, orchDrawSize, orchDrawSize);
          }

          const maxElevationAngle = 0.66;
          const currentElevation = deployVal * maxElevationAngle;
          const pivotX = renderX + fwd * (-0.325 * orchDrawSize);
          const pivotY = renderY + (0.075 * orchDrawSize);

          const launcherBmp = buildSpriteBitmap('Orchestra_Launcher', renderRace, unit.team);
          const launcherSrc = isHitFlashing ? launcherBmp?.flash : launcherBmp?.normal;
          if (launcherSrc) {
            ctx.save();
            ctx.translate(pivotX, pivotY);
            ctx.rotate(-currentElevation * fwd);
            ctx.translate(-pivotX, -pivotY);
            ctx.drawImage(launcherSrc, renderX - orchDrawSize / 2, renderY - orchDrawSize / 2, orchDrawSize, orchDrawSize);
            ctx.restore();
          }
        } else {
          // Rolling / marching flat on chassis (1 single composite blit, 0 matrix transforms)
          let orchKey = 'Orchestra';
          if (isWalking && unit.speed > 0) {
            const rollPhase = Math.floor((walkPhase * 0.68 + numId * 0.5) % 6);
            orchKey = ORCHESTRA_ROLL_COMPOSITES[rollPhase] || 'Orchestra';
          }
          const compBitmap = buildSpriteBitmap(orchKey, renderRace, unit.team) || buildSpriteBitmap('Orchestra', renderRace, unit.team);
          const compSrc = isHitFlashing ? compBitmap?.flash : compBitmap?.normal;
          if (compSrc) {
            drawLowTierWalkSprite(ctx, compSrc, renderX, renderY, orchDrawSize, orchDrawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_ROLL);
          }
        }
      } else {
        // High / Medium Quality Tier: Kinematic Rig with rolling 8-wheels, chassis suspension, and folding/elevating rocket organ
        const inRange = !!(unit.target && battlefield?.isInRange?.(unit, unit.target));
        // Deploy value directly from authoritative simulation (0.0 = flat, 1.0 = elevated)
        const deployVal = unit.deployProgress !== undefined ? unit.deployProgress : (unit._deployProgress || 0);

        // Vehicle only rolls when moving AND fully folded flat (deployVal <= 0.001) AND not stopped inRange
        const isRolling = isWalking && unit.speed > 0 && deployVal <= 0.001 && !inRange;
        const rollPhase = isRolling ? Math.floor((walkPhase * 0.68 + numId * 0.5) % 6) : 0;
        // 45-Ton Heavy 8x8 Armored Transporter:
        // Massive independent suspension absorbs terrain shock - chassis stays 100% rock-solid, dead-flat and grounded (zero hopping or wobbling).

        // 1. Animated Rolling 8-Wheel Chassis
        // User request: "die räder müssen sich langsamer drehen (aber schneller als beim salvage)"
        const chassisKey = ORCHESTRA_CHASSIS_ROLL_FRAMES[rollPhase] || 'Orchestra_Chassis';
        const chassisBitmap = buildSpriteBitmap(chassisKey, renderRace, unit.team);
        const chassisSrc = isHitFlashing ? chassisBitmap.flash : chassisBitmap.normal;

        ctx.drawImage(
          chassisSrc,
          renderX - orchDrawSize / 2,
          renderY - orchDrawSize / 2,
          orchDrawSize,
          orchDrawSize
        );

        // 2. Folding / Elevating Rocket Organ ("einklappen beim fahren, ausklappen zum schießen")
        // Maximum elevation angle: ~38 degrees (0.66 radians)
        const maxElevationAngle = 0.66;
        const currentElevation = deployVal * maxElevationAngle;

        // Weapon firing recoil slide along elevated tube angle
        let recoilDist = 0;
        if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.28)) {
          const recoilFraction = (unit.attackCooldown - (unit.attackSpeed - 0.28)) / 0.28;
          recoilDist = dampedSpringRecoil(recoilFraction) * 5.5;
        }

        // Pivot Point in 20x20 grid: rear turntable at x: 3.5, y: 11.5
        // Center of 20x20 is (10, 10) -> dx = (3.5 - 10)/20 = -0.325, dy = (11.5 - 10)/20 = +0.075
        const pivotX = renderX + fwd * (-0.325 * orchDrawSize);
        const pivotY = renderY + (0.075 * orchDrawSize);

        // Recoil vector sliding backward down the tube angle
        const recoilVecX = -Math.cos(currentElevation) * recoilDist * fwd;
        const recoilVecY = Math.sin(currentElevation) * recoilDist;

        const launcherBitmap = buildSpriteBitmap('Orchestra_Launcher', renderRace, unit.team);
        const launcherSrc = isHitFlashing ? launcherBitmap.flash : launcherBitmap.normal;

        ctx.save();
        ctx.translate(pivotX + recoilVecX, pivotY + recoilVecY);
        // Elevate upward: negative rotation in canvas space for +X facing
        ctx.rotate(-currentElevation * fwd);
        ctx.translate(-(pivotX + recoilVecX), -(pivotY + recoilVecY));

        ctx.drawImage(
          launcherSrc,
          renderX - orchDrawSize / 2 + recoilVecX,
          renderY - orchDrawSize / 2 + recoilVecY,
          orchDrawSize,
          orchDrawSize
        );
        ctx.restore();

        // 3. Quality-Gated Cosmetic Effects (Diesel exhaust puffs)
        if (this.quality !== 'low') {
          const exhaustX = renderX - fwd * (orchDrawSize * 0.42);
          const exhaustY = renderY - orchDrawSize * 0.10;
          const puffPulse = Math.sin(t * 7 + numId);
          if (puffPulse > 0.4 && isRolling) {
            ctx.fillStyle = 'rgba(71, 85, 105, 0.4)';
            ctx.beginPath();
            ctx.arc(exhaustX, exhaustY - (puffPulse * 3), 2.0 + puffPulse * 1.2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    } else if (unitType === 'DefectiveLoaf' || unitType === 'Defective_Loaf' || unitType === 'Defective Loaf') {
      // 𓏏 BESPOKE SUL DEFECTIVE LOAF LIVING BIO-RIG:
      // Otherworldly prehensile proboscis, living starburst plasma core,
      // 5-strand root skirt, cartilage flexion and bio-electric acid tether
      const isWestern = (unit.team === 'western');
      const forwardSign = isWestern ? 1 : -1;
      const wobble = unit.wobbleOffset || 0;

      // 1. Aerodynamic Flight State Interpolation
      if (unit._defectiveFlightFactor === undefined) unit._defectiveFlightFactor = isWalking ? 1.0 : 0.0;
      const targetFlight = isWalking ? 1.0 : 0.0;
      unit._defectiveFlightFactor += (targetFlight - unit._defectiveFlightFactor) * 0.12;
      const flightFactor = Math.max(0, Math.min(1, unit._defectiveFlightFactor));

      // 2. Wide-Wavelength Spatial Ether Undulation (Paradigm 1: Extended Wegstrecken-Sinus ~270px)
      const travelDist = forwardSign * unit.x;
      const flightTravelPhase = (travelDist * 0.015) + (t * 0.20) + wobble;
      const flightBob = Math.sin(flightTravelPhase) * 2.5;
      const idleBob = Math.sin(t * 0.65 + wobble) * 1.8 + Math.cos(t * 0.30 + wobble * 0.7) * 0.6;
      const hoverBob = (1 - flightFactor) * idleBob + flightFactor * flightBob;

      const bioDriftX = Math.cos(t * 0.50 + wobble) * 1.0 + Math.sin(t * 1.10 + wobble * 0.5) * 0.4;

      // Gentle living respiration: subtle crown squash & stretch (±2.4%)
      const breathPulse = Math.sin(t * 1.20 + wobble);
      const stretchY = 1.0 + breathPulse * 0.024;
      const squashX = 1.0 - breathPulse * 0.018;

      // Hit recoil from damage
      let recoilX = 0;
      let recoilTilt = 0;
      if (unit.hitFlashTimer > 0) {
        const recoilProg = unit.hitFlashTimer / 0.12; // 1 -> 0
        const kick = Math.sin(recoilProg * Math.PI) * 4.5;
        recoilX = -forwardSign * kick;
        recoilTilt = -forwardSign * (kick * 0.035);
      }

      // Dynamic wave slope banking: nose pitches up during ascent, down during descent
      const waveSlopeTilt = forwardSign * (Math.cos(flightTravelPhase) * 0.035 * flightFactor);
      const idleTilt = Math.sin(t * 0.65 + wobble) * 0.03 + Math.cos(t * 1.15) * 0.015;
      const flightTilt = forwardSign * (flightFactor * 0.11) + waveSlopeTilt;

      // Lock-on targeting tilt
      let tetherTarget = null;
      if (unit.tetherTargetId && battlefield) {
        tetherTarget = battlefield.getUnitOrBaseById?.(unit.tetherTargetId);
        if (tetherTarget && (tetherTarget.isDead || tetherTarget.isGoldenStatue)) {
          tetherTarget = null;
        }
      }
      const isLocked = !!(unit.tetherTargetId && tetherTarget);
      let lockOnTilt = 0;
      if (isLocked && tetherTarget) {
        const dy = (tetherTarget.y - 8) - renderY;
        const dx = Math.abs(tetherTarget.x - renderX) || 1;
        lockOnTilt = forwardSign * Math.atan2(dy, dx) * 0.35;
      }
      const attackTilt = (isAttacking && !isLocked) ? forwardSign * 0.12 : 0;
      const totalTilt = (1 - flightFactor) * idleTilt + flightTilt + lockOnTilt + attackTilt + recoilTilt;

      // Loaf center position
      const loafX = renderX + recoilX + bioDriftX * (1 - flightFactor * 0.7);
      const loafY = renderY + hoverBob - drawSize * 0.14;

      // Panel State Selection
      let spriteKey = 'DefectiveLoaf_Front';
      if (unit.hitFlashTimer > 0 && (unit.hp <= (unit.maxHp || 100) * 0.30)) {
        spriteKey = 'DefectiveLoaf_Rupture'; // Panel 4: Ruptured Shell when critically damaged
      } else if (isLocked || isAttacking) {
        spriteKey = 'DefectiveLoaf_Attack';  // Panel 3: Attack Starburst Lock-On
      } else if (flightFactor > 0.40) {
        spriteKey = 'DefectiveLoaf_Drift';   // Panel 2: 3/4 Flight Profile
      }
      const bitmap = buildSpriteBitmap(spriteKey, renderRace, unit.team);
      const sourceBitmap = isHitFlashing ? bitmap.flash : bitmap.normal;

      const cosT = Math.cos(totalTilt);
      const sinT = Math.sin(totalTilt);

      // --- LAYER 1: 5-Strand Organic Root Skirt (Drawn behind shell) (Paradigm 2) ---
      ctx.save();
      const isLow = (this.quality === 'low') || isSimpleTier;
      const strandStep = isLow ? 2 : 1;
      for (let s = 0; s < 5; s += strandStep) {
        const sFreq = DEFECTIVE_LOAF_ROOT_FREQ[s];
        const sPhase = DEFECTIVE_LOAF_ROOT_PHASE[s];
        const sSpatial = DEFECTIVE_LOAF_ROOT_SPATIAL[s];
        const sAmp = DEFECTIVE_LOAF_ROOT_AMP[s];
        const sColor = DEFECTIVE_LOAF_ROOT_COLORS[s];

        // Anchor distributed across the bottom edge of the shield
        const anchorLocalX = (s - 2) * (drawSize * 0.08 * squashX);
        const anchorLocalY = drawSize * 0.18 * stretchY;
        const anchorX = loafX + cosT * anchorLocalX - sinT * anchorLocalY;
        const anchorY = loafY + sinT * anchorLocalX + cosT * anchorLocalY;

        const strandLen = drawSize * DEFECTIVE_LOAF_ROOT_LEN[s] * stretchY;
        const pts = _defectiveRootPtsScratch[s];
        pts[0].x = anchorX;
        pts[0].y = anchorY;

        for (let k = 1; k <= 5; k++) {
          const u = k / 5;
          // Idle waving
          const idleWavePhase = t * (1.6 * sFreq) - u * sSpatial + sPhase + wobble;
          const idleWaveX = Math.sin(idleWavePhase) * (u * 3.8 * sAmp);
          const idleWaveY = Math.cos(t * (1.1 * sFreq) - u * 2.5 + sPhase) * (u * 1.6 * sAmp);

          // Flight drag
          const dragX = -forwardSign * (flightFactor * (Math.pow(u, 1.2) * (drawSize * 0.36)));
          const dragY = -flightFactor * (u * (drawSize * 0.09));
          const flightPhase = t * (2.8 * sFreq) - u * sSpatial + sPhase + wobble;
          const flightWaveY = Math.sin(flightPhase) * (flightFactor * u * 5.2 * sAmp);
          const flightWaveX = Math.cos(flightPhase) * (flightFactor * u * 2.8 * sAmp);

          pts[k].x = anchorX + idleWaveX * (1 - flightFactor) + dragX + flightWaveX;
          pts[k].y = anchorY + (u * strandLen) + idleWaveY * (1 - flightFactor) + dragY + flightWaveY;
        }

        ctx.strokeStyle = sColor;
        ctx.lineWidth = 1.8;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let k = 1; k <= 5; k++) {
          ctx.lineTo(pts[k].x, pts[k].y);
        }
        ctx.stroke();

        // Root nodule tip
        ctx.fillStyle = sColor;
        ctx.beginPath();
        ctx.arc(pts[5].x, pts[5].y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      // --- LAYER 2: Sprite Bitmap with Cartilage Flexion (Paradigm 3) ---
      const sW = sourceBitmap.width;
      const sH = sourceBitmap.height;
      const splitFrac = 10 / 24; // Rows 0-9: Cranium & Antlers; Rows 10-23: Breastplate & Glyph
      const splitH = drawSize * splitFrac;

      ctx.save();
      ctx.translate(loafX, loafY);
      ctx.rotate(totalTilt);
      ctx.scale(squashX, stretchY);

      // 2A. Gutturale Kehlkopf-Pulsation (Infraschall-Resonanz, Paradigm 4)
      const throatFreq = 5.2;
      const throatPulse = 0.5 + 0.5 * Math.sin(t * throatFreq + wobble);
      const throatW = drawSize * (0.15 + throatPulse * 0.05);
      const throatH = drawSize * (0.07 + throatPulse * 0.03);
      const throatY = drawSize * 0.14;
      if (this.quality !== 'low') {
        const throatTex = getSulDefectiveThroatCanvas();
        const tw = Math.max(1, throatW);
        const th = Math.max(1, throatH);
        ctx.drawImage(throatTex, -tw, throatY - th, tw * 2, th * 2);
      } else {
        ctx.fillStyle = 'rgba(192, 38, 211, 0.45)';
        ctx.beginPath();
        ctx.ellipse(0, throatY, Math.max(1, throatW), Math.max(1, throatH), 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // 2B. Upper Cranium & Antler Boughs (Rows 0-9)
      ctx.drawImage(
        sourceBitmap,
        0, 0, sW, sH * splitFrac,
        -drawSize / 2, -drawSize / 2, drawSize, splitH
      );

      // 2C. Lower Breastplate, Glyph & Shield (Rows 10-23) with delayed inertial flexion
      const shieldLagY = Math.sin(flightTravelPhase - 0.45) * (flightFactor * 0.9) - (breathPulse * 0.35);
      const shieldLocalTilt = Math.sin(t * 1.6 + wobble) * 0.02;
      ctx.save();
      ctx.translate(0, -drawSize / 2 + splitH + shieldLagY);
      ctx.rotate(shieldLocalTilt);
      ctx.drawImage(
        sourceBitmap,
        0, sH * splitFrac, sW, sH * (1 - splitFrac),
        -drawSize / 2, 0, drawSize, drawSize * (1 - splitFrac)
      );

      // Bioluminescent Chitin Suture Glow (Paradigm 6)
      if (this.quality !== 'low' && !isHitFlashing) {
        ctx.globalAlpha = 0.18 + 0.25 * Math.sin(t * 2.4 + wobble);
        ctx.strokeStyle = '#93c5fd';
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(-drawSize * 0.12, drawSize * 0.08);
        ctx.lineTo(-drawSize * 0.05, drawSize * 0.15);
        ctx.lineTo(0, drawSize * 0.12);
        ctx.lineTo(drawSize * 0.05, drawSize * 0.15);
        ctx.lineTo(drawSize * 0.12, drawSize * 0.08);
        ctx.stroke();
      }
      ctx.restore(); // end shieldLag translate

      // 2D. Living Starburst Plasma Core & Saccades (Paradigm 5)
      if (!isHitFlashing) {
        const coreNoise = Math.sin(t * 8.5 + wobble) * 0.2 + Math.sin(t * 15.0) * 0.1;
        const corePulse = Math.max(0.3, 0.6 + 0.35 * Math.sin(t * 4.2 + wobble) + coreNoise);

        // Wandering micro-saccades
        const saccadeStep = Math.floor((t + wobble * 0.5) / 1.6);
        let lookOffX = Math.sin(saccadeStep * 6.7) * (drawSize * 0.022);
        let lookOffY = Math.cos(saccadeStep * 4.3) * (drawSize * 0.014);

        if (tetherTarget && !tetherTarget.isDead) {
          const relX = (tetherTarget.x - unit.x) * forwardSign;
          lookOffX = Math.sign(relX) * (drawSize * 0.032);
        }

        const eyeX = (spriteKey === 'DefectiveLoaf_Drift' ? forwardSign * (drawSize * 0.06) : 0) + lookOffX;
        const eyeY = -drawSize * 0.13 + lookOffY;

        if (this.quality !== 'low') {
          const coreRadius = Math.max(2, (drawSize * (isLocked ? 0.32 : 0.20)) * corePulse);
          const coreTex = getSulDefectiveCoreCanvas(isLocked || isAttacking);
          ctx.drawImage(coreTex, eyeX - coreRadius, eyeY - coreRadius, coreRadius * 2, coreRadius * 2);

          // Exploding Starburst Corona Rays in Attack/Locked state (Panel 3)
          if (isLocked || isAttacking) {
            const rayLen = drawSize * (0.28 + 0.08 * Math.sin(t * 22));
            const rayAngle = (t * 1.5) % (Math.PI * 2);
            ctx.save();
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
            ctx.lineWidth = 1.2;
            for (let r = 0; r < 8; r++) {
              const ang = rayAngle + (r * Math.PI / 4);
              const rLen = (r % 2 === 0) ? rayLen : rayLen * 0.65;
              ctx.beginPath();
              ctx.moveTo(eyeX, eyeY);
              ctx.lineTo(eyeX + Math.cos(ang) * rLen, eyeY + Math.sin(ang) * rLen);
              ctx.stroke();
            }
            ctx.restore();
          }
        }
      }
      ctx.restore(); // end loafX translate

      // --- LAYER 3: Prehensile Proboscis (Kinematic Bio-Rig) ---
      // Originates at bottom-center of the shield
      const probBaseX = loafX + sinT * (drawSize * 0.02);
      const probBaseY = loafY + cosT * (drawSize * 0.17 * stretchY) + shieldLagY;

      let probTipX = probBaseX;
      let probTipY = probBaseY;

      ctx.save();
      let prevProbX = probBaseX;
      let prevProbY = probBaseY;

      for (let k = 1; k <= 6; k++) {
        const u = k / 6;
        let currX, currY;

        if (isLocked && tetherTarget && !tetherTarget.isDead) {
          // Reaching out towards tether target with peristaltic digestive pumping
          const targetDist = Math.hypot(tetherTarget.x - probBaseX, tetherTarget.y - probBaseY) || 1;
          const probReachLen = Math.min(drawSize * 0.65, targetDist * 0.45);
          const tdx = (tetherTarget.x - probBaseX) / targetDist;
          const tdy = (tetherTarget.y - probBaseY) / targetDist;
          const perpX = -tdy;
          const perpY = tdx;

          const peristalsis = Math.sin(k * 1.6 - t * 18) * (2.2 * Math.sin(u * Math.PI));
          currX = probBaseX + tdx * (u * probReachLen) + perpX * peristalsis;
          currY = probBaseY + tdy * (u * probReachLen) + perpY * peristalsis;
        } else if (flightFactor > 0.35) {
          // Uncoiling forward in direction of flight (S-Curve)
          const reachDist = forwardSign * (u * drawSize * 0.52 * flightFactor);
          const sWave = Math.sin(t * 3.2 - u * 3.4 + wobble) * (u * 4.6);
          currX = probBaseX + reachDist + Math.cos(t * 2.2 - u * 2.6) * (u * 2.8);
          currY = probBaseY + (u * drawSize * 0.22) + sWave;

          // Bioluminescent micro-spores drifting back from tip
          if (this.quality !== 'low' && k === 6) {
            const sporeProg = (t * 2.0 + wobble) % 1.0;
            const spAlpha = (1 - sporeProg) * 0.6;
            const spX = currX - forwardSign * (sporeProg * drawSize * 0.25);
            const spY = currY + Math.sin(t * 6 + sporeProg * 4) * 2;
            const prevAlpha = ctx.globalAlpha;
            ctx.globalAlpha = prevAlpha * spAlpha;
            ctx.fillStyle = '#f0abfc';
            ctx.fillRect(spX - 1, spY - 1, 2, 2);
            ctx.globalAlpha = prevAlpha;
          }
        } else {
          // Idle Coiled Spiral under shield
          const coilAng = u * (Math.PI * 1.35) + Math.sin(t * 1.5 + wobble) * 0.20;
          const coilR = (1 - u * 0.45) * (drawSize * 0.20);
          currX = probBaseX + Math.sin(coilAng) * coilR * forwardSign;
          currY = probBaseY + (u * drawSize * 0.26) + (1 - Math.cos(coilAng)) * (drawSize * 0.10) + breathPulse * 0.8;
        }

        // Muscular tapering trunk coloring
        let probColor = '#4338ca';
        if (k === 3) probColor = '#6366f1';
        else if (k === 4) probColor = '#818cf8';
        else if (k === 5) probColor = '#a855f7';
        else if (k === 6) probColor = '#f0abfc';

        ctx.strokeStyle = probColor;
        ctx.lineWidth = Math.max(1.6, (7 - k) * 0.65);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(prevProbX, prevProbY);
        ctx.lineTo(currX, currY);
        ctx.stroke();

        // Chitinous segment ring rib
        if (k < 6) {
          const segDx = currX - prevProbX;
          const segDy = currY - prevProbY;
          const segDist = Math.hypot(segDx, segDy) || 1;
          const ribPerpX = -segDy / segDist;
          const ribPerpY = segDx / segDist;
          const ribW = Math.max(1.8, (7 - k) * 0.55);

          ctx.strokeStyle = '#312e81';
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(currX - ribPerpX * ribW, currY - ribPerpY * ribW);
          ctx.lineTo(currX + ribPerpX * ribW, currY + ribPerpY * ribW);
          ctx.stroke();
        }

        prevProbX = currX;
        prevProbY = currY;

        if (k === 6) {
          probTipX = currX;
          probTipY = currY;
          // Tip nozzle glow
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(currX, currY, 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();

      // --- LAYER 4: Bio-Electric Tether & Corrosive Acid Impact Crystals ---
      if (isLocked && tetherTarget && !tetherTarget.isDead && !tetherTarget.isGoldenStatue) {
        const emitterX = probTipX;
        const emitterY = probTipY;
        const targetX = tetherTarget.x;
        const targetY = tetherTarget.y - 8;
        const dist = Math.hypot(targetX - emitterX, targetY - emitterY);
        const steps = Math.max(10, Math.floor(dist / 7));
        const dx = (targetX - emitterX) / steps;
        const dy = (targetY - emitterY) / steps;
        const perpX = -(targetY - emitterY) / (dist || 1);
        const perpY = (targetX - emitterX) / (dist || 1);

        ctx.save();
        // Construct single multi-stroke wave path
        ctx.beginPath();
        ctx.moveTo(emitterX, emitterY);
        for (let s = 1; s <= steps; s++) {
          const wave = Math.sin(s * 0.95 - t * 20) * 3.6 * Math.sin((s / steps) * Math.PI);
          ctx.lineTo(emitterX + dx * s + perpX * wave, emitterY + dy * s + perpY * wave);
        }

        // 1. Outer bio-muscular sheath
        ctx.strokeStyle = '#581c87';
        ctx.lineWidth = 3.8;
        ctx.stroke();

        // 2. Inner high-energy plasma conduit
        ctx.strokeStyle = '#e879f9';
        ctx.lineWidth = 2.0;
        ctx.stroke();

        // 3. Core laser filament
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.0;
        ctx.stroke();

        // Traveling energy nodes along conduit (quality-gated)
        if (this.quality !== 'low') {
          ctx.fillStyle = '#ffffff';
          for (let n = 2; n < steps; n += 3) {
            const wave = Math.sin(n * 0.95 - t * 20) * 3.6 * Math.sin((n / steps) * Math.PI);
            const nx = emitterX + dx * n + perpX * wave;
            const ny = emitterY + dy * n + perpY * wave;
            ctx.fillRect(nx - 1.5, ny - 1.5, 3, 3);
          }

          // Corrosive Acid Crystals erupting at victim impact point (Panel 3 concept)
          const crystalCount = 4;
          for (let c = 0; c < crystalCount; c++) {
            const cAng = (c * Math.PI * 2 / crystalCount) + (t * 3.0);
            const cLen = 4.0 + 2.0 * Math.sin(t * 12 + c);
            const cx = targetX + Math.cos(cAng) * cLen;
            const cy = targetY + Math.sin(cAng) * cLen;
            ctx.fillStyle = (c % 2 === 0) ? '#c026d3' : '#a3e635';
            ctx.beginPath();
            ctx.moveTo(cx, cy - 2);
            ctx.lineTo(cx + 2, cy);
            ctx.lineTo(cx, cy + 2);
            ctx.lineTo(cx - 2, cy);
            ctx.closePath();
            ctx.fill();
          }

          // Caustic bubbling acid mist
          const mistProg = (t * 2.5) % 1.0;
          const mx = targetX + Math.sin(t * 8) * 5;
          const my = targetY - mistProg * 12;
          ctx.globalAlpha = (1 - mistProg) * 0.65;
          ctx.fillStyle = '#a3e635';
          ctx.fillRect(mx - 1, my - 1, 2, 2);
        }
        ctx.restore();
      }

      // --- LAYER 5: Ruptured Shell Acid Spill on Floor (Panel 4) ---
      if (spriteKey === 'DefectiveLoaf_Rupture' && this.quality !== 'low') {
        ctx.save();
        ctx.fillStyle = 'rgba(132, 204, 22, 0.35)';
        ctx.beginPath();
        ctx.ellipse(loafX, renderY + 4, drawSize * 0.50, drawSize * 0.18, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    } else if (unitType === 'RejectedLoaf' || unitType === 'Rejected_Loaf' || unitType === 'Rejected Loaf') {
      // 𓏏 BESPOKE SUL REJECTED LOAF LIVING BIO-RIG:
      // Otherworldly medusa propulsion, living crown breathing & squash/stretch,
      // dynamic filament umbrelling, autonomic nerve twitches and floating bio-spores
      const isWestern = (unit.team === 'western');
      const forwardSign = isWestern ? 1 : -1;
      const wobble = unit.wobbleOffset || 0;

      // 1. Aerodynamic Flight State Interpolation
      if (unit._loafFlightFactor === undefined) unit._loafFlightFactor = isWalking ? 1.0 : 0.0;
      const targetFlight = isWalking ? 1.0 : 0.0;
      unit._loafFlightFactor += (targetFlight - unit._loafFlightFactor) * 0.12;
      const flightFactor = Math.max(0, Math.min(1, unit._loafFlightFactor));

      // 2. Wide-Wavelength Spatial Ether Undulation (Extended Wegstrecken-Sinus)
      // When traveling, one full vertical cycle ("ein mal hoch und runter") stretches over
      // a wide distance of ~270px (k = 0.015 rad/px, ~6-7 body lengths) for a majestic, long-distance wave.
      const travelDist = forwardSign * unit.x;
      const flightTravelPhase = (travelDist * 0.015) + (t * 0.20) + wobble;
      const flightBob = Math.sin(flightTravelPhase) * 2.4;
      const idleBob = Math.sin(t * 0.65 + wobble) * 1.8 + Math.cos(t * 0.32 + wobble * 0.7) * 0.6;
      const hoverBob = (1 - flightFactor) * idleBob + flightFactor * flightBob;

      const bioDriftX = Math.cos(t * 0.55 + wobble) * 1.0 + Math.sin(t * 1.15 + wobble * 0.5) * 0.4;

      // Gentle living respiration: very subtle crown squash & stretch (±2.5%)
      const breathPulse = Math.sin(t * 1.25 + wobble);
      const stretchY = 1.0 + breathPulse * 0.025;
      const squashX = 1.0 - breathPulse * 0.018;

      // Hit recoil from damage/reflection
      let recoilX = 0;
      let recoilTilt = 0;
      if (unit.hitFlashTimer > 0) {
        const recoilProg = unit.hitFlashTimer / 0.12; // 1 -> 0
        const kick = Math.sin(recoilProg * Math.PI) * 4.5;
        recoilX = -forwardSign * kick;
        recoilTilt = -forwardSign * (kick * 0.035);
      }

      // Dynamic wave slope banking: subtle nose pitch up during ascent, pitch down during descent
      const waveSlopeTilt = forwardSign * (Math.cos(flightTravelPhase) * 0.035 * flightFactor);
      const idleTilt = Math.sin(t * 0.65 + wobble) * 0.03 + Math.cos(t * 1.2) * 0.015;
      const flightTilt = forwardSign * (flightFactor * 0.10) + waveSlopeTilt;
      const totalTilt = (1 - flightFactor) * idleTilt + flightTilt + recoilTilt;

      // Skull center position: lifted above ground lane with lateral biological drift
      const skullX = renderX + recoilX + bioDriftX * (1 - flightFactor * 0.7);
      const skullY = renderY + hoverBob - drawSize * 0.15;

      // Autonomic biological nerve micro-twitch (occurs rarely every ~4.2s for ~120ms)
      const twitchCycle = (t * 0.24 + wobble) % 1.0;
      let twitchSpasm = 0;
      let twitchStrandIdx = -1;
      let twitchProg = 0;
      if (twitchCycle > 0.93 && twitchCycle < 0.98) {
        twitchProg = (twitchCycle - 0.93) / 0.05;
        twitchSpasm = Math.sin(twitchProg * Math.PI) * 2.2;
        twitchStrandIdx = Math.floor(Math.abs(wobble * 7)) % 5;
      }

      // 3. Coronal Thorny Halo Glow, Respiration & Nerve Discharge (Layer 5)
      if (this.quality !== 'low') {
        const crownFlare = 1.0 + breathPulse * 0.08;
        const haloPulse = (0.80 + 0.20 * Math.sin(t * 2.0 + wobble)) * crownFlare;
        const haloRadius = Math.max(2, 20 * haloPulse);
        const haloTex = getSulRejectedHaloCanvas(unit.hitFlashTimer > 0);
        ctx.save();
        ctx.drawImage(haloTex, skullX - haloRadius, skullY - haloRadius, haloRadius * 2, haloRadius * 2);

        // Circulating nerve energy wave around the crown thorns with radial flaring
        const waveAngle = (t * 2.2 + wobble) % (Math.PI * 2);
        const spDist = drawSize * 0.48 * crownFlare;
        for (let sp = 0; sp < 6; sp++) {
          const spAngle = waveAngle + (sp * Math.PI / 3);
          const spX = skullX + Math.cos(spAngle) * spDist;
          const spY = skullY + Math.sin(spAngle) * spDist * stretchY;
          const spPulse = 0.5 + 0.5 * Math.sin(t * 4.5 + sp * 1.5);
          ctx.globalAlpha = 0.25 * spPulse;
          ctx.fillStyle = '#e9d5ff';
          ctx.beginPath();
          ctx.arc(spX, spY, 2.0 * spPulse, 0, Math.PI * 2);
          ctx.fill();
        }

        // Telepathic nerve discharge spark across adjacent crown tips (upper dome arc)
        if (twitchSpasm > 0) {
          const sparkSlot = Math.floor(Math.abs(wobble * 13)) % 4;
          const ang1 = -Math.PI * 0.15 - (sparkSlot * Math.PI * 0.23);
          const ang2 = -Math.PI * 0.15 - (((sparkSlot + 1) % 4) * Math.PI * 0.23);
          const x1 = skullX + Math.cos(ang1) * spDist;
          const y1 = skullY + Math.sin(ang1) * spDist * stretchY;
          const x2 = skullX + Math.cos(ang2) * spDist;
          const y2 = skullY + Math.sin(ang2) * spDist * stretchY;
          ctx.globalAlpha = 0.6 + 0.4 * Math.sin(twitchProg * Math.PI);
          ctx.strokeStyle = '#f5d0fe';
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.quadraticCurveTo((x1 + x2) / 2 + Math.sin(t * 35) * 3, (y1 + y2) / 2 + Math.cos(t * 35) * 3, x2, y2);
          ctx.stroke();
        }
        ctx.restore();
      }

      // 4. Multi-Strand Fluid Mycelium Filaments: Independent Asynchronous Wave Physics
      // Dynamic umbrelling: gentle breathing expansion & contraction
      const umbrellaFactor = breathPulse * 0.25;

      const cosT = Math.cos(totalTilt);
      const sinT = Math.sin(totalTilt);
      const bulbLocalY = drawSize * 0.20 * stretchY;

      ctx.save();
      const isLow = (this.quality === 'low') || isSimpleTier;
      const strandStep = isLow ? 2 : 1;
      const segStep = isLow ? 2 : 1;
      for (let s = 0; s < 5; s += strandStep) {
        const sFreq = REJECTED_LOAF_STRAND_FREQ[s];
        const sPhase = REJECTED_LOAF_STRAND_PHASE[s];
        const sSpatial = REJECTED_LOAF_STRAND_SPATIAL[s];
        const sAmp = REJECTED_LOAF_STRAND_AMP[s];

        // Distribute strand anchors across the bulb with crown elasticity
        const bulbLocalX = (s - 2) * (drawSize * 0.075 * squashX);
        const anchorX = skullX + cosT * bulbLocalX - sinT * bulbLocalY;
        const anchorY = skullY + sinT * bulbLocalX + cosT * bulbLocalY;

        // Natural strand length (center is longest, distinct per strand)
        const strandLength = drawSize * REJECTED_LOAF_STRAND_LEN[s] * stretchY;

        _rejectedStrandPtsScratch[s][0].x = anchorX;
        _rejectedStrandPtsScratch[s][0].y = anchorY;

        for (let k = segStep; k <= 6; k += segStep) {
          const u = k / 6; // 0.166, 0.333, 0.5, 0.666, 0.833, 1.0

          // 1. Idle Serpentine Traveling Wave (Slower, independent frequency & phase per strand)
          const idleWavePhase = t * (1.8 * sFreq) - u * sSpatial + sPhase + wobble;
          const idleWaveX = Math.sin(idleWavePhase) * (u * 4.8 * sAmp) + Math.sin(t * 0.85 + sPhase) * (u * 1.8);
          const idleWaveY = Math.cos(t * (1.3 * sFreq) - u * 3.0 + sPhase) * (u * 2.0 * sAmp);

          // 2. Umbrelling lateral breathing spread
          const lateralSpreadDir = (s - 2);
          const umbrellaSpreadX = lateralSpreadDir * umbrellaFactor * (u * drawSize * 0.18);

          // 3. Flight Undulating Wave (Slower base wave ~2.8-3.2 rad/s instead of 6.5 rad/s,
          // modulated per strand with sFreq so strands move non-uniformly)
          const dragX = -forwardSign * (flightFactor * (Math.pow(u, 1.2) * (drawSize * 0.44)));
          const dragY = -flightFactor * (u * (drawSize * 0.12));
          const flightPhase = t * (3.0 * sFreq) - u * sSpatial + sPhase + wobble;
          const flightWaveY = Math.sin(flightPhase) * (flightFactor * u * 6.5 * sAmp) + Math.cos(t * (1.5 * sFreq) - u * 2.4 + sPhase) * (flightFactor * u * 2.4);
          const flightWaveX = Math.cos(flightPhase) * (flightFactor * u * 3.4 * sAmp);

          // 4. Grounded splay (when near ground, tips flare horizontally)
          const groundProximity = Math.max(0, Math.min(1, (anchorY + u * strandLength - renderY) / (drawSize * 0.3)));
          const groundSpread = (s - 2) * (1.0 - flightFactor) * groundProximity * 3.5;

          // 5. Autonomic nerve twitch impulse on specific strand
          const strandTwitch = (s === twitchStrandIdx) ? (twitchSpasm * u * (k >= 4 ? 1.5 : 0.8)) : 0;

          const currX = anchorX + idleWaveX * (1 - flightFactor) + umbrellaSpreadX * (1 - flightFactor * 0.8) + dragX + flightWaveX + groundSpread + strandTwitch;
          const currY = anchorY + (u * strandLength) + idleWaveY * (1 - flightFactor) + dragY + flightWaveY;

          _rejectedStrandPtsScratch[s][k].x = currX;
          _rejectedStrandPtsScratch[s][k].y = currY;
        }
      }

      // Batch stroke per segment-depth k across all active strands (Paradigma 6)
      ctx.lineCap = 'round';
      for (let k = segStep; k <= 6; k += segStep) {
        const prevIdx = k - segStep;
        ctx.strokeStyle = REJECTED_LOAF_SEG_COLORS[k - 1];
        ctx.lineWidth = REJECTED_LOAF_SEG_WIDTHS[k - 1];
        ctx.beginPath();
        for (let s = 0; s < 5; s += strandStep) {
          ctx.moveTo(_rejectedStrandPtsScratch[s][prevIdx].x, _rejectedStrandPtsScratch[s][prevIdx].y);
          ctx.lineTo(_rejectedStrandPtsScratch[s][k].x, _rejectedStrandPtsScratch[s][k].y);
        }
        ctx.stroke();
      }

      // Batch draw all luminous spore tip beads (k === 6)
      ctx.fillStyle = '#f5d0fe';
      ctx.beginPath();
      for (let s = 0; s < 5; s += strandStep) {
        const tip = _rejectedStrandPtsScratch[s][6];
        ctx.moveTo(tip.x + 1.4, tip.y);
        ctx.arc(tip.x, tip.y, 1.4, 0, Math.PI * 2);
      }
      ctx.fill();

      // Drifting bioluminescent micro-spores dissolving into the ether
      if (this.quality !== 'low') {
        for (let s = 1; s < 5; s += 2) {
          const tip = _rejectedStrandPtsScratch[s][6];
          const sporeTime = (t * 1.4 + s * 0.5) % 1.0;
          const sporeAlpha = (1 - sporeTime) * 0.55;
          const sporeX = tip.x + Math.sin(t * 3.5 + s) * 4;
          const sporeY = tip.y - sporeTime * 14;
          ctx.fillStyle = '#f5d0fe';
          ctx.globalAlpha = sporeAlpha;
          ctx.fillRect(sporeX - 0.75, sporeY - 0.75, 1.5, 1.5);
        }
        ctx.globalAlpha = 1.0;
      }
      ctx.restore();

      // 5. Living Skull Bio-Rig: Cartilage Flexion, Guttural Pulsation, Saccades & Suture Glow
      let spriteKey = 'RejectedLoaf_Front';
      if (unit.hitFlashTimer > 0) {
        spriteKey = 'RejectedLoaf_Shield'; // Panel 4: Crystalline Chitin Carapace
      } else if (flightFactor > 0.45) {
        spriteKey = 'RejectedLoaf_Drift';  // Panel 2: 3/4 Flight Profile Skull
      }
      const bitmap = buildSpriteBitmap(spriteKey, renderRace, unit.team);
      const sourceBitmap = isHitFlashing ? bitmap.flash : bitmap.normal;

      const sW = sourceBitmap.width;
      const sH = sourceBitmap.height;
      const splitFrac = 10 / 24; // Rows 0-9: Cranium & Crown; Rows 10-23: Cheekbones, Jaw, Chin & Root
      const splitH = drawSize * splitFrac;

      ctx.save();
      ctx.translate(skullX, skullY);
      ctx.rotate(totalTilt);
      ctx.scale(squashX, stretchY);

      // --- LAYER 2: Gutturale Kehlkopf-Pulsation (Infraschall-Resonanz) ---
      // Visualizing deep oscillating guttural frequencies (t * 5.2) at the sub-cranial throat sac
      const throatFreq = 5.2;
      const throatOsc = Math.sin(t * throatFreq + wobble);
      const throatPulse = 0.5 + 0.5 * throatOsc;
      const throatW = drawSize * (0.16 + throatPulse * 0.05);
      const throatH = drawSize * (0.08 + throatPulse * 0.03);
      const throatY = drawSize * 0.16;
      if (this.quality !== 'low') {
        const throatTex = getSulRejectedThroatCanvas();
        const tw = Math.max(1, throatW);
        const th = Math.max(1, throatH);
        ctx.drawImage(throatTex, -tw, throatY - th, tw * 2, th * 2);
      } else {
        ctx.fillStyle = 'rgba(168, 85, 247, 0.5)';
        ctx.beginPath();
        ctx.ellipse(0, throatY, Math.max(1, throatW), Math.max(1, throatH), 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // --- LAYER 1: Knorpel-Segmentierung & Phasenversatz ---
      // 1A. Upper Cranium & Thorny Crown (Rows 0-9)
      ctx.drawImage(
        sourceBitmap,
        0, 0, sW, sH * splitFrac,
        -drawSize / 2, -drawSize / 2, drawSize, splitH
      );

      // 1B. Lower Jaw, Cheekbones & Root (Rows 10-23) with delayed inertial flexion
      const jawLagY = Math.sin(flightTravelPhase - 0.45) * (flightFactor * 0.9) - (breathPulse * 0.4);
      const jawLocalTilt = Math.sin(t * 1.8 + wobble) * 0.025;
      ctx.save();
      ctx.translate(0, -drawSize / 2 + splitH + jawLagY);
      ctx.rotate(jawLocalTilt);
      ctx.drawImage(
        sourceBitmap,
        0, sH * splitFrac, sW, sH * (1 - splitFrac),
        -drawSize / 2, 0, drawSize, drawSize * (1 - splitFrac)
      );
      ctx.restore();

      // --- LAYER 4: Biolumineszierende Schädelnähte / Suturen (Pulsing Cranial Fissures) ---
      if (this.quality !== 'low' && !isHitFlashing) {
        const sutureBreath = 0.5 + 0.5 * Math.sin(t * 2.2 + wobble);
        const sutureAlpha = 0.15 + 0.32 * sutureBreath;
        ctx.save();
        ctx.globalAlpha = sutureAlpha;
        ctx.strokeStyle = '#e9d5ff';
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        const seamTopY = -drawSize * 0.16;
        const seamMidY = -drawSize * 0.08;
        const seamWiggleX = drawSize * 0.015 * Math.sin(t * 1.5);
        ctx.moveTo(0, seamTopY);
        ctx.lineTo(seamWiggleX, seamMidY);
        ctx.lineTo(-drawSize * 0.05, seamMidY + drawSize * 0.02);
        ctx.moveTo(seamWiggleX, seamMidY);
        ctx.lineTo(drawSize * 0.05, seamMidY + drawSize * 0.02);
        ctx.stroke();
        ctx.restore();
      }

      // --- LAYER 3: Lebendige Augenhöhlen: Tastender Blick (Sakkaden), Fokus & Miasma ---
      if (!isHitFlashing) {
        const flameNoise = Math.sin(t * 9.2 + wobble) * 0.25 + Math.sin(t * 16.5) * 0.15;
        const eyePulse = Math.max(0.25, 0.5 + 0.3 * Math.sin(t * 3.6 + wobble) + flameNoise);

        // Intelligent wandering micro-saccades (focus shifts discretely every ~1.8s)
        const saccadeStep = Math.floor((t + wobble * 0.5) / 1.8);
        let lookOffX = Math.sin(saccadeStep * 7.3) * (drawSize * 0.025);
        let lookOffY = Math.cos(saccadeStep * 4.9) * (drawSize * 0.015);

        // Target tracking bias when engaging
        if (unit.target && battlefield?.isInRange?.(unit, unit.target)) {
          const targetRelX = (unit.target.x - unit.x) * forwardSign;
          lookOffX = Math.sign(targetRelX) * (drawSize * 0.035);
        }

        const inCombat = !!unit.target || unit.attackCooldown > 0;
        const eyeHeight = inCombat ? 1.2 : 2.0;
        const prevAlpha = ctx.globalAlpha;
        ctx.globalAlpha = prevAlpha * (inCombat ? (0.7 + eyePulse * 0.3) : (0.5 + eyePulse * 0.45));
        ctx.fillStyle = inCombat ? '#f472b6' : '#e9d5ff';

        if (spriteKey === 'RejectedLoaf_Drift') {
          // 3/4 single prominent forward eye cavity
          const eyeOffX = forwardSign * (drawSize * 0.08) + lookOffX;
          const eyeOffY = -drawSize * 0.04 + lookOffY;
          ctx.fillRect(eyeOffX - 1, eyeOffY - eyeHeight / 2, 2, eyeHeight);
        } else {
          // Frontal twin eye sockets
          const leftEyeX = -drawSize * 0.12 + lookOffX;
          const rightEyeX = drawSize * 0.12 + lookOffX;
          const eyeOffY = -drawSize * 0.04 + lookOffY;
          ctx.fillRect(leftEyeX - 1, eyeOffY - eyeHeight / 2, 2, eyeHeight);
          ctx.fillRect(rightEyeX - 1, eyeOffY - eyeHeight / 2, 2, eyeHeight);
        }
        ctx.globalAlpha = prevAlpha;

        // Plasma-Miasma smoke wisps drifting from the sockets
        if (this.quality !== 'low') {
          const miasmaProg = (t * 1.8 + wobble) % 1.0;
          const miasmaAlpha = (1 - miasmaProg) * 0.45;
          const miasmaDriftX = -forwardSign * (miasmaProg * drawSize * 0.18);
          const miasmaDriftY = -miasmaProg * drawSize * 0.08 + Math.sin(t * 6 + miasmaProg * 4) * 1.5;
          const prevMiasmaAlpha = ctx.globalAlpha;
          ctx.globalAlpha = prevMiasmaAlpha * miasmaAlpha;
          ctx.fillStyle = '#d8b4fe';
          if (spriteKey === 'RejectedLoaf_Drift') {
            const eyeX = forwardSign * (drawSize * 0.08);
            ctx.fillRect(eyeX + miasmaDriftX - 0.75, -drawSize * 0.04 + miasmaDriftY - 0.75, 1.5, 1.5);
          } else {
            ctx.fillRect(-drawSize * 0.12 + miasmaDriftX - 0.75, -drawSize * 0.04 + miasmaDriftY - 0.75, 1.5, 1.5);
            ctx.fillRect(drawSize * 0.12 + miasmaDriftX - 0.75, -drawSize * 0.04 + miasmaDriftY - 0.75, 1.5, 1.5);
          }
          ctx.globalAlpha = prevMiasmaAlpha;
        }
      }
      ctx.restore();

      // 6. Reactive Chitin Carapace Barrier Pulse & Shard Burst (Panel 4)
      if (unit.hitFlashTimer > 0) {
        const flashProg = unit.hitFlashTimer / 0.12; // 1 -> 0
        const barrierR = drawSize * (0.58 + (1 - flashProg) * 0.22);

        ctx.save();
        ctx.globalAlpha = 0.4 + flashProg * 0.6;
        ctx.strokeStyle = '#f472b6';
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        ctx.arc(skullX, skullY, Math.max(2, barrierR), 0, Math.PI * 2);
        ctx.stroke();

        // Radiating faceted diamond crystal shards returning towards attacker
        const shardCount = 4;
        for (let s = 0; s < shardCount; s++) {
          const sAngle = (s - 1.5) * 0.45 + (forwardSign > 0 ? 0 : Math.PI);
          const sDist = drawSize * 0.45 + (1 - flashProg) * (drawSize * 0.75);
          const sx = skullX + Math.cos(sAngle) * sDist;
          const sy = skullY + Math.sin(sAngle) * sDist;
          ctx.fillStyle = (s % 2 === 0) ? '#f472b6' : '#ffffff';
          ctx.beginPath();
          ctx.moveTo(sx, sy - 2.5);
          ctx.lineTo(sx + 2.5, sy);
          ctx.lineTo(sx, sy + 2.5);
          ctx.lineTo(sx - 2.5, sy);
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      }
    } else if (unitType === 'ApostateLoaf' || unitType === 'Apostate_Loaf' || unitType === 'Apostate Loaf') {
      // 𓏏 BESPOKE SUL APOSTATE LOAF LIVING BIO-RIG:
      // Heavy arachnid strider kinematics, breathing crown squash & stretch,
      // pendulum crystal head with physical momentum, writhing bio-cirri mandibles,
      // bioluminescent hymenium gill aura, ambient spore emission & volcanic mortar burst.
      const isWestern = (unit.team === 'western');
      const forwardSign = isWestern ? 1 : -1;
      const wobble = unit.wobbleOffset || 0;

      const isAttacking = unit.isAttacking || (unit.attackCooldown !== undefined && unit.attackCooldown < 0.22 && unit.attackCooldown > 0);
      const isCharging  = (unit.attackCooldown !== undefined && unit.attackCooldown < 0.45 && unit.attackCooldown >= 0.22);

      // ── 1. STRIDE & GAIT STATE INTERPOLATION (Smooth transition) ──────────────
      if (unit._apostateWalkFactor === undefined) unit._apostateWalkFactor = isWalking ? 1.0 : 0.0;
      const targetWalk = isWalking ? walkPitch : 0.0;
      unit._apostateWalkFactor += (targetWalk - unit._apostateWalkFactor) * 0.15;
      const walkFactor = Math.max(0, Math.min(1, unit._apostateWalkFactor));

      // Stride timing phase (coupled to global walkPhase + individual wobble)
      const stridePhase = walkPhase + wobble;

      // ── 2. ARACHNID BODY KINEMATICS & MASS DYNAMICS ───────────────────────────
      // Heavy arachnid bob: body dips when legs pass/weight transfers, rises on stance push
      const walkBob = walkFactor * (-Math.abs(Math.sin(stridePhase)) * 2.0);
      const idleBob = Math.sin(t * 0.75 + wobble) * 0.9;
      const totalBob = (1 - walkFactor) * idleBob + walkBob;

      // Hit recoil from damage (flinches backward)
      let recoilX = 0;
      let recoilTilt = 0;
      if (unit.hitFlashTimer > 0) {
        const recoilProg = unit.hitFlashTimer / 0.12;
        const kick = Math.sin(recoilProg * Math.PI) * 4.2;
        recoilX = -forwardSign * kick;
        recoilTilt = -forwardSign * (kick * 0.035);
      }

      // Dynamic forward pitch: strider leans into the forward march
      const stridePitch = forwardSign * (walkFactor * 0.06);
      const strideRoll  = Math.sin(stridePhase) * (walkFactor * 0.02);

      // ── 3. SPRITE PANEL SELECTION (Hexapod Alternating Tripod Gait) ───────────
      let spriteKey = 'ApostateLoaf_Front';
      let hexapodBob = 0;
      let hexapodPitch = 0;

      if (isAttacking) {
        spriteKey = 'ApostateLoaf_Eruption'; // Clamshell split & volcanic mortar
      } else if (isCharging) {
        spriteKey = 'ApostateLoaf_Attack';   // Cyan head aura charging
      } else if (walkFactor > 0.15) {
        // Alternating tripod hexapod gait: 4 distinct walkcycle frames cycling smoothly
        const cycle = ((stridePhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
        const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4);
        spriteKey = APOSTATE_LOAF_WALK_FRAMES[phaseIdx % 4] || 'ApostateLoaf_Walk1';
        hexapodBob = (phaseIdx === 1 || phaseIdx === 3) ? -1.2 : 0.4;
        hexapodPitch = forwardSign * ((phaseIdx === 0 || phaseIdx === 1) ? 0.02 : -0.02);
      }

      const totalTilt = stridePitch + strideRoll + recoilTilt + hexapodPitch;

      // Living respiration: subtle crown squash & stretch (±2.6% phasenversetzt)
      const breathPulse = Math.sin(t * 1.35 + wobble);
      const stretchY = 1.0 + breathPulse * 0.026;
      const squashX  = 1.0 - breathPulse * 0.020;

      const bodyX = renderX + recoilX;
      const bodyY = renderY + totalBob + (walkFactor * hexapodBob);

      const loafBitmap = buildSpriteBitmap(spriteKey, renderRace, unit.team);

      // ── 4. BIOLUMINESCENT HYMENIUM GILL GLOW (Under Cap Halo) ─────────────────
      // Radiates soft electric cyan illumination down from beneath the cap rim (focused at gills)
      if (this.quality !== 'low') {
        const gillPulse = 0.60 + 0.30 * Math.sin(t * 2.2 + wobble);
        const gillY = bodyY - drawSize * 0.24;
        const gillTex = getSulApostateGillsCanvas();
        const rx = drawSize * 0.52;
        const ry = drawSize * 0.10;
        ctx.save();
        ctx.globalAlpha = 0.40 * gillPulse;
        ctx.drawImage(gillTex, bodyX - rx, gillY - ry, rx * 2, ry * 2);
        ctx.restore();
      }

      // ── 5. DRAW LIVING SPRITE (With Respiration, Banking & Recoil) ─────────────
      ctx.save();
      ctx.translate(bodyX, bodyY);
      if (forwardSign < 0) ctx.scale(-1, 1);
      ctx.rotate(totalTilt);
      ctx.scale(squashX, stretchY);
      ctx.globalAlpha = spawnAlpha;

      const activeImg = (unit.hitFlashTimer > 0) ? loafBitmap.flash : loafBitmap.normal;
      ctx.drawImage(activeImg, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
      ctx.restore();

      // ── 6. INERTIAL PENDULUM CRYSTAL HEAD & BLAZING OPTIC FLARE ───────────────
      // Head hangs from the flexible bio-spine and swings with momentum/lag
      const headLagX = -forwardSign * walkFactor * (Math.sin(stridePhase * 0.9) * 2.6 + 1.2);
      const headLagY = Math.abs(Math.cos(stridePhase * 0.9)) * 1.2 * walkFactor;
      const idleSwayX = Math.sin(t * 1.4 + wobble) * 0.8;
      const headX = bodyX + (walkFactor > 0.25 ? headLagX : idleSwayX);
      const headY = bodyY + drawSize * 0.065 + headLagY;

      if (this.quality !== 'low') {
        const opticPulse = 0.65 + 0.35 * Math.sin(t * 5.5 + wobble);
        const opticR = drawSize * 0.09;
        const opticTex = getSulApostateOpticCanvas();
        ctx.save();
        ctx.globalAlpha = 0.85 * opticPulse;
        ctx.drawImage(opticTex, headX - opticR, headY - opticR, opticR * 2, opticR * 2);
        ctx.restore();
      }

      // ── 7. AUTONOMIC WRITHING CHIN CIRRI (Living Mandibles) ────────────────────
      // 4 fine bioluminescent cyan filaments beneath the chin that squirm organically (held well above the ground)
      if (this.quality !== 'low') {
        ctx.save();
        ctx.globalAlpha = spawnAlpha;
        ctx.lineCap = 'round';
        const cirriBaseY = headY + drawSize * 0.08;
        for (let c = 0; c < 4; c++) {
          const cOffX = (c - 1.5) * (drawSize * 0.035);
          const cPhase = t * 3.6 + c * 1.4 + wobble;
          const cLen = drawSize * 0.055;
          const c1X = headX + cOffX;
          const c1Y = cirriBaseY;
          const c2X = c1X + Math.sin(cPhase) * (drawSize * 0.035);
          const c2Y = c1Y + cLen * 0.55;
          const c3X = c2X + Math.cos(cPhase * 1.3) * (drawSize * 0.03);
          const c3Y = c1Y + cLen;

          ctx.strokeStyle = (c % 2 === 0) ? '#00f0ff' : '#22d3ee';
          ctx.lineWidth = 1.3;
          ctx.beginPath();
          ctx.moveTo(c1X, c1Y);
          if (typeof ctx.quadraticCurveTo === 'function') {
            ctx.quadraticCurveTo(c2X, c2Y, c3X, c3Y);
          } else {
            ctx.lineTo(c2X, c2Y);
            ctx.lineTo(c3X, c3Y);
          }
          ctx.stroke();
        }
        ctx.restore();
      }

      // ── 8. AMBIENT SPORE EMISSION (Gently drifting into the ether) ─────────────
      // Micro-spores floating from the spore dome, dissolving upward (like Rejected Loaf)
      if (this.quality !== 'low') {
        ctx.save();
        for (let s = 0; s < 4; s++) {
          const sporeTime = ((t * 0.85 + s * 0.28 + wobble * 0.3) % 1.0);
          const sporeAlpha = Math.sin(sporeTime * Math.PI) * 0.60 * spawnAlpha;
          const sporeAngle = s * 1.57 + t * 0.6;
          const sporeRadius = drawSize * (0.28 + Math.sin(sporeAngle) * 0.12);
          const sx = bodyX + Math.cos(sporeAngle) * sporeRadius;
          const sy = (bodyY - drawSize * 0.26) - (sporeTime * drawSize * 0.35);

          ctx.globalAlpha = sporeAlpha;
          ctx.fillStyle = (s % 2 === 0) ? '#c084fc' : '#00f0ff';
          ctx.fillRect(sx - 1.0, sy - 1.0, 2.0, 2.0);
        }
        ctx.restore();
      }

      // ── 9. COMBAT & ERUPTION FX OVERLAYS ──────────────────────────────────────
      // Attack Charge (Large spherical cyan aura gathering power)
      if (isCharging && this.quality !== 'low') {
        const chargePulse = 0.5 + 0.5 * Math.sin(t * 18);
        ctx.save();
        ctx.globalAlpha = 0.40 * chargePulse;
        ctx.fillStyle = '#00f0ff';
        ctx.beginPath();
        ctx.arc(headX, headY, drawSize * 0.22, 0, Math.PI * 2);
        ctx.fill();

        // Spore sparkles converging toward the head
        for (let p = 0; p < 6; p++) {
          const sAngle = t * 4.0 + p * 1.05;
          const sr = drawSize * (0.32 + Math.sin(sAngle) * 0.08);
          const sx = bodyX + Math.cos(sAngle) * sr;
          const sy = bodyY - drawSize * 0.20 + Math.sin(sAngle * 0.8) * (drawSize * 0.1);
          ctx.globalAlpha = 0.75 * chargePulse;
          ctx.fillStyle = (p % 2 === 0) ? '#c084fc' : '#38bdf8';
          ctx.beginPath();
          ctx.arc(sx, sy, 1.6, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      // Volcanic Spore Mortar Eruption (High-velocity particles rising from split crater)
      if (isAttacking && this.quality !== 'low') {
        const particleCount = 10;
        ctx.save();
        ctx.translate(bodyX, bodyY);
        if (forwardSign < 0) ctx.scale(-1, 1);
        for (let p = 0; p < particleCount; p++) {
          const pSeed  = ((t * 11 + p * 1.45) % 1.0);
          const pAngle = Math.sin(p * 2.3 + t * 4.5) * 0.40;
          const px = Math.sin(pAngle) * drawSize * 0.22;
          const py = -drawSize * 0.34 - pSeed * drawSize * 0.48;
          const pRadius = 1.3 + (1.0 - pSeed) * 2.4;
          const pAlpha  = (1.0 - pSeed) * 0.88 * spawnAlpha;
          ctx.fillStyle = APOSTATE_PARTICLE_COLORS[p % APOSTATE_PARTICLE_COLORS.length];
          ctx.globalAlpha = pAlpha;
          ctx.beginPath();
          ctx.arc(px, py, pRadius, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

    } else if (unitType === 'ForgottenLoaf' || unitType === 'Forgotten_Loaf' || unitType === 'Forgotten Loaf' || unit.isForgottenLoaf) {
      // 🕷️ BESPOKE SUL FORGOTTEN LOAF (SPIDER MINE) LIVING RIG:
      // - Articulated TWO-SEGMENT spider legs: high-arched Oberschenkel, amber knee knuckle, plunging Unterschenkel
      // - Predatory GALLOP-STYLE sprint kinematics (chassis heave, pitch-rocking, surge & coordinated gallop gait)
      // - Armored Sul Rumpf (segmented chitin chassis) cradling a compact glowing bio-fluid tank
      // - Physics-based liquid sloshing, inertial surface tilt, micro-bubbles, and hot-air balloon ascent
      const isWestern = (unit.team === 'western');
      const forwardSign = isWestern ? 1 : -1;
      const wobble = unit.wobbleOffset || 0;
      const mineState = unit.mineState || (unit.isBurrowed ? 'burrowed' : 'sprinting');
      const isBurrowing = (mineState === 'burrowing' || unit.isBurrowing);
      const isRising = (mineState === 'rising' || unit.isMineRising);
      const wasBurrowed = !!(unit.wasBurrowedBeforeRising || (unit.isBurrowed && !isRising) || mineState === 'burrowed');
      const isSurfaceCollapse = (isRising && !wasBurrowed);
      const isBurrowed = (!isSurfaceCollapse && (mineState === 'burrowed' || unit.isBurrowed || (isRising && wasBurrowed)));
      const isSprinting = (!isBurrowed && !isRising && !isBurrowing);
      const burrowProg = isBurrowing ? Math.min(1.0, Math.max(0, 1.0 - ((unit.burrowTimer || 0) / 0.75))) : (isBurrowed ? 1.0 : 0.0);
      const riseProg = isRising ? Math.min(1.0, Math.max(0, 1.0 - ((unit.mineRiseTimer !== undefined ? unit.mineRiseTimer : 1.0) / 1.0))) : 0.0;

      // ── 1. GALLOP-STYLE SPRINT KINEMATICS & CHASSIS BASE ────────────────────────
      let bodyBaseX = renderX;
      let bodyBaseY = renderY;
      const groundY = renderY + 8;
      let gallopPitch = 0;
      let gallopPhase = 0;

      // Realistic horse gallop cadence (Muybridge 12-frame reference):
      // Stride frequency calibrated to 1.85 Hz (noble, powerful bounds with distinct flight suspension!)
      const GALLOP_HZ = 1.85;

      if (isSprinting) {
        gallopPhase = (t * GALLOP_HZ * Math.PI * 2) + wobble;
      }

      const TWO_PI = Math.PI * 2;
      const cycleU = ((gallopPhase / TWO_PI) % 1.0 + 1.0) % 1.0;

      if (isSprinting) {
        // Bounding heave:
        // - Deep dual compression during staggered hind strikes (u ~ 0.15) and fore strikes (u ~ 0.55)
        // - Grand upward leap into AIRBORNE SUSPENSION (u ~ 0.85 to 0.02), body lifts high into the air!
        const gallopBounce = Math.sin((cycleU - 0.68) * TWO_PI) * 4.8 + Math.sin(cycleU * TWO_PI * 2.0 - 0.4) * 2.0;
        bodyBaseY = renderY - 3 - gallopBounce;

        // Rocking pitch (Frames 1-12):
        // Hind thrust -> Rears UP (+0.18); Fore landing -> Dives DOWN (-0.16); Mid-air -> Streamlined level
        gallopPitch = Math.sin((cycleU - 0.02) * TWO_PI) * 0.18 * forwardSign;

        // Forward surge impulse: accelerates as hind legs drive and glides forward in mid-air
        const gallopSurge = Math.cos((cycleU - 0.05) * TWO_PI) * 2.8 * forwardSign;
        bodyBaseX = renderX + gallopSurge;
      } else if (isBurrowing) {
        // ── DEDICATED HOP-AND-DRILL BURROWING ANIMATION ──
        // Phase 1 (0.0 .. 0.28): Hop / Spring high into the air ("hochhüpfen")
        // Phase 2 (0.28 .. 1.0): Plunge down, corkscrewing legs like a drill ("wie ein bohrer in den boden graben"),
        // accompanied by flying rock and spark particles ("dabei sollen partikel wegfliegen")
        if (burrowProg < 0.28) {
          const hopT = burrowProg / 0.28;
          const hopHeight = Math.sin(hopT * Math.PI) * 15.0;
          bodyBaseY = renderY - 3 - hopHeight;
          bodyBaseX = renderX;
          gallopPitch = -0.16 * forwardSign * (1.0 - hopT);
        } else {
          const drillT = (burrowProg - 0.28) / 0.72;
          const drillSink = drillT * 11.0;
          const drillVibeX = Math.sin(drillT * 55) * (1.0 - drillT) * 1.5;
          const drillVibeY = Math.cos(drillT * 45) * (1.0 - drillT) * 1.2;
          bodyBaseY = renderY - 3 + drillSink + drillVibeY;
          bodyBaseX = renderX + drillVibeX;
          gallopPitch = 0;
        }
      } else if (isSurfaceCollapse) {
        // ── UNBURROWED SURFACE COLLAPSE ("körper fällt zu boden") ──
        // Chassis slumps down onto the turf as the sphere detaches and launches into the sky
        const collapseProg = Math.min(1.0, riseProg / 0.28);
        const dropT = Math.sin(collapseProg * Math.PI * 0.5);
        bodyBaseY = (renderY - 3) + dropT * 9.0; // Drops from -3 to +6 (resting on turf)
        bodyBaseX = renderX;
        gallopPitch = dropT * (0.18 * forwardSign); // Limp tilt onto the ground
      } else {
        // Buried / Rising base
        bodyBaseY = renderY + 7;
        bodyBaseX = renderX;
      }

      ctx.save();

      // Parametric Horse-Gallop Kinematics based on 12-frame Muybridge cycle:
      // Stance duration is crisp (~25% of cycle), firmly anchored to the turf (lift = 0, no sliding/schlittern!),
      // followed by high-folding flight and full 6-leg airborne suspension (Frames 8-12)
      const getGallopKinematics = (u, uStart, forwardReach, backwardSweep, maxLift) => {
        if (!isSprinting) return { lift: 0, stride: 0 };
        const stanceLen = 0.25;
        const flightLen = 1.0 - stanceLen; // 0.75

        const uRel = ((u - uStart) % 1.0 + 1.0) % 1.0;

        if (uRel < stanceLen) {
          // ── STANCE PHASE: Foot plants firmly on the ground, pushing body forward ──
          // Zero vertical lift/jitter; linear sweep from forwardReach to -backwardSweep
          const tStance = uRel / stanceLen;
          const stride = forwardReach - tStance * (forwardReach + backwardSweep);
          return { lift: 0, stride };
        } else {
          // ── FLIGHT PHASE: Foot snaps off turf, folds knee high under chassis, then reaches out ──
          const tFlight = (uRel - stanceLen) / flightLen; // 0.0 to 1.0
          const liftShape = Math.sin(Math.pow(tFlight, 0.82) * Math.PI);
          const lift = liftShape * maxLift;
          const swingT = (1.0 - Math.cos(tFlight * Math.PI)) * 0.5;
          const stride = -backwardSweep + swingT * (forwardReach + backwardSweep);
          return { lift, stride };
        }
      };

      // ── Horse Gallop Cadence (Muybridge 12-frame gallop cycle) ──
      // Critical: The two front legs and two rear legs MUST execute their steps with a clear TIME OFFSET
      // so their motion is never parallel, perfectly matching natural quadrupedal gallop mechanics!
      //
      // Staggered hind pair (Rear):
      // - Far Rear (background hind): strikes at u = 0.02
      const rearA  = getGallopKinematics(cycleU, 0.02, 4.0, 14.0, 8.5);
      // - Near Rear 1 (outer hind, trailing): strikes at u = 0.06 (stance 0.06 -> 0.31)
      const rearB1 = getGallopKinematics(cycleU, 0.06, 5.0, 16.0, 10.0);
      // - Near Rear 2 (inner hind, leading): strikes at u = 0.20 (stance 0.20 -> 0.45)
      // (Clear 14% phase offset between rearB1 and rearB2: rearB1 pushes while rearB2 lands!)
      const rearB2 = getGallopKinematics(cycleU, 0.20, 6.0, 13.0, 9.0);

      // Staggered fore pair (Front):
      // - Far Front (background fore): strikes at u = 0.42
      const frontA  = getGallopKinematics(cycleU, 0.42, 14.0, 8.0, 9.0);
      // - Near Front 1 (inner fore, trailing): strikes at u = 0.46 (stance 0.46 -> 0.71)
      const frontB1 = getGallopKinematics(cycleU, 0.46, 16.0, 9.0, 10.5);
      // - Near Front 2 (outer fore, leading): strikes at u = 0.60 (stance 0.60 -> 0.85)
      // (Clear 14% phase offset between frontB1 and frontB2: frontB1 vaults while frontB2 plants far forward!)
      const frontB2 = getGallopKinematics(cycleU, 0.60, 19.0, 10.0, 12.0);

      // ── Dedicated Burrowing Auger Drill Kinematics ──
      const isDrilling = isBurrowing && burrowProg >= 0.28;
      const drillT = isDrilling ? (burrowProg - 0.28) / 0.72 : 0;
      const drillSpin = drillT * Math.PI * 26;
      let legSpreadScale = 1.0;
      if (isBurrowing) {
        if (burrowProg < 0.28) {
          const gatherT = Math.sin((burrowProg / 0.28) * Math.PI * 0.5);
          legSpreadScale = 1.0 - gatherT * 0.85;
        } else {
          legSpreadScale = 0.15 * (1.0 - drillT * 0.7);
        }
      }

      // 6-Fluted Spiral Auger Drill calculation during burrow plunge
      const calcDrillLeg = (idx) => {
        if (!isDrilling) return { stride: 0, lift: 0, tamp: 0 };
        const angle = drillSpin + idx * (Math.PI / 3);
        const stride = Math.cos(angle) * (2.8 * (1.0 - drillT * 0.6));
        const tamp = Math.sin(angle) * 3.5;
        return { stride, lift: 0, tamp };
      };

      const drillLeg0 = calcDrillLeg(0);
      const drillLeg1 = calcDrillLeg(1);
      const drillLeg2 = calcDrillLeg(2);
      const drillLeg3 = calcDrillLeg(3);
      const drillLeg4 = calcDrillLeg(4);
      const drillLeg5 = calcDrillLeg(5);

      // Helper to render an articulated TWO-SEGMENT spider leg (Oberschenkel + Unterschenkel)
      // in true 2.5D perspective depth:
      // Oberschenkel is SUBSTANTIALLY THICKER (muscular armored chitin) than the slender Unterschenkel!
      // isRear = true ensures claws point backwards (to the left when facing east), matching user specification!
      const drawSpiderLeg = (coxaOffsetX, coxaYOffset, kneeOffsetX, kneeYOffset, footOffsetX, footBaseY, stride, lift, tamp, isFg, isRear = false) => {
        let finalKneeOffsetX = kneeOffsetX * legSpreadScale;
        let finalKneeYOffset = kneeYOffset;
        let finalFootOffsetX = footOffsetX * legSpreadScale;
        let finalFootBaseY = footBaseY;
        let finalStride = stride;
        let finalLift = lift;
        let finalTamp = tamp;

        if (isSurfaceCollapse) {
          const collapseProg = Math.min(1.0, riseProg / 0.28);
          const dropT = Math.sin(collapseProg * Math.PI * 0.5);
          finalStride = 0;
          finalLift = 0;
          finalTamp = 0;
          // Knee buckles downwards and outwards as the chassis slumps onto the turf
          finalKneeYOffset = kneeYOffset + dropT * 10.5;
          finalKneeOffsetX = kneeOffsetX * (1.0 + dropT * 0.35);
          finalFootOffsetX = footOffsetX * (1.0 + dropT * 0.2);
          finalFootBaseY = footBaseY + dropT * 1.5;
        }

        const coxaX = bodyBaseX + coxaOffsetX;
        const coxaY = bodyBaseY + coxaYOffset; // Far side attaches higher on chassis, near side lower
        // In flight (finalLift > 0), tuck knees slightly toward center of body (Frames 9-11 in Muybridge gallop):
        const flightTuckX = isSprinting ? ((finalLift / 10.0) * (isRear ? (forwardSign * 2.8) : (-forwardSign * 2.4))) : 0;
        const kneeX = bodyBaseX + finalKneeOffsetX + flightTuckX + finalStride * 0.45 * forwardSign;
        const kneeY = bodyBaseY + finalKneeYOffset - finalLift * 0.75 - finalTamp;
        const footX = bodyBaseX + finalFootOffsetX + finalStride * forwardSign;
        const footY = finalFootBaseY - finalLift + finalTamp;
        const clawDir = isRear ? -forwardSign : forwardSign;

        // ── Vector Math for Oberschenkel (Femur) ──
        const fdx = kneeX - coxaX;
        const fdy = kneeY - coxaY;
        const flen = Math.hypot(fdx, fdy) || 1.0;
        const fux = fdx / flen;
        const fuy = fdy / flen;
        // Normal perpendicular pointing dorsal (upward/outward along armor plate)
        let fnx = -fuy;
        let fny = fux;
        if (fny > 0) {
          fnx = -fnx;
          fny = -fny;
        }

        // Biological dorsal arch for Femur
        const femurArch = isFg ? 3.4 : 2.2;
        const fMidX = (coxaX + kneeX) * 0.5 + fnx * femurArch;
        const fMidY = (coxaY + kneeY) * 0.5 + fny * femurArch;

        // ── 1. OBERSCHENKEL (Femur: Heavy Layered Curved Chitin & Hydraulic Sinew) ──
        if (isFg) {
          // Thick muscular chitin body (heavy biological silhouette with dorsal arch)
          ctx.beginPath();
          ctx.moveTo(coxaX, coxaY);
          ctx.quadraticCurveTo(fMidX, fMidY, kneeX, kneeY);
          ctx.strokeStyle = '#220e08';
          ctx.lineWidth = 5.8;
          ctx.lineCap = 'round';
          ctx.stroke();

          // Terracotta muscular core / exposed sinew layer
          ctx.beginPath();
          ctx.moveTo(coxaX, coxaY);
          ctx.quadraticCurveTo(fMidX, fMidY, kneeX, kneeY);
          ctx.strokeStyle = '#6c2514';
          ctx.lineWidth = 4.2;
          ctx.stroke();

          // Dorsal Chitin Carapace Armor Plate (offset towards arched dorsal crest)
          ctx.beginPath();
          ctx.moveTo(coxaX + fnx * 1.5, coxaY + fny * 1.5);
          ctx.quadraticCurveTo(fMidX + fnx * 1.8, fMidY + fny * 1.8, kneeX + fnx * 0.8, kneeY + fny * 0.8);
          ctx.strokeStyle = '#3e150b';
          ctx.lineWidth = 2.4;
          ctx.stroke();

          // Specular highlight edge along dorsal armor ridge
          ctx.beginPath();
          ctx.moveTo(coxaX + fnx * 2.2 + fux * (flen * 0.15), coxaY + fny * 2.2 + fuy * (flen * 0.15));
          ctx.quadraticCurveTo(fMidX + fnx * 2.5, fMidY + fny * 2.5, kneeX + fnx * 1.4 - fux * (flen * 0.1), kneeY + fny * 1.4 - fuy * (flen * 0.1));
          ctx.strokeStyle = '#a8431d';
          ctx.lineWidth = 1.3;
          ctx.stroke();

          // Transverse Armor Overlap Groove (segment dividing proximal & distal plates)
          ctx.beginPath();
          ctx.moveTo(fMidX + fnx * 2.5, fMidY + fny * 2.5);
          ctx.lineTo(fMidX - fnx * 1.8, fMidY - fny * 1.8);
          ctx.strokeStyle = '#1a0905';
          ctx.lineWidth = 1.2;
          ctx.stroke();

          // Inset Mechanical Actuator / Piston Slot
          const pStart = 0.30;
          const pEnd = 0.75;
          ctx.beginPath();
          ctx.moveTo(coxaX + fux * (flen * pStart) - fnx * 0.5, coxaY + fuy * (flen * pStart) - fny * 0.5);
          ctx.quadraticCurveTo(fMidX - fnx * 0.5, fMidY - fny * 0.5, coxaX + fux * (flen * pEnd) - fnx * 0.5, coxaY + fuy * (flen * pEnd) - fny * 0.5);
          ctx.strokeStyle = '#180a06';
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // Golden/amber hydraulic piston rod inside slot
          ctx.beginPath();
          ctx.moveTo(coxaX + fux * (flen * (pStart + 0.08)) - fnx * 0.5, coxaY + fuy * (flen * (pStart + 0.08)) - fny * 0.5);
          ctx.quadraticCurveTo(fMidX - fnx * 0.5, fMidY - fny * 0.5, coxaX + fux * (flen * (pEnd - 0.08)) - fnx * 0.5, coxaY + fuy * (flen * (pEnd - 0.08)) - fny * 0.5);
          ctx.strokeStyle = '#d97706';
          ctx.lineWidth = 0.9;
          ctx.stroke();

          // Ventral Thorn / Secondary Sickle Spur (curving gracefully downward on underside)
          const spurBaseX = coxaX + fux * (flen * 0.42);
          const spurBaseY = coxaY + fuy * (flen * 0.42);
          const spurTipX = spurBaseX - fnx * 4.0 - fux * 1.5;
          const spurTipY = spurBaseY - fny * 4.0 - fuy * 1.5;
          const spurMidX = spurBaseX - fnx * 2.2 - fux * 0.5;
          const spurMidY = spurBaseY - fny * 2.2 - fuy * 0.5;
          ctx.beginPath();
          ctx.moveTo(spurBaseX, spurBaseY);
          ctx.quadraticCurveTo(spurMidX, spurMidY, spurTipX, spurTipY);
          ctx.strokeStyle = '#4a1d12';
          ctx.lineWidth = 1.4;
          ctx.lineCap = 'round';
          ctx.stroke();

          // Base Coxa Ring Collar (chunky attachment sleeve)
          ctx.beginPath();
          ctx.arc(coxaX + fux * 2.0, coxaY + fuy * 2.0, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = '#1c0d08';
          ctx.fill();
          ctx.strokeStyle = '#5a2214';
          ctx.lineWidth = 0.9;
          ctx.stroke();
        } else {
          // Background leg: darker, solid muscular chitin silhouette with dorsal rim
          ctx.beginPath();
          ctx.moveTo(coxaX, coxaY);
          ctx.quadraticCurveTo(fMidX, fMidY, kneeX, kneeY);
          ctx.strokeStyle = '#160905';
          ctx.lineWidth = 4.4;
          ctx.lineCap = 'round';
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(coxaX, coxaY);
          ctx.quadraticCurveTo(fMidX, fMidY, kneeX, kneeY);
          ctx.strokeStyle = '#38160d';
          ctx.lineWidth = 2.8;
          ctx.stroke();

          // Subtle dorsal ridge
          ctx.beginPath();
          ctx.moveTo(coxaX + fnx * 1.0, coxaY + fny * 1.0);
          ctx.quadraticCurveTo(fMidX + fnx * 1.0, fMidY + fny * 1.0, kneeX + fnx * 0.5, kneeY + fny * 0.5);
          ctx.strokeStyle = '#5c2214';
          ctx.lineWidth = 1.2;
          ctx.stroke();
        }

        // ── 2. KNIE-GELENK (Articulated Bio-Mechanical Knee Knuckle) ──
        if (isFg) {
          // Outer casing ring
          ctx.fillStyle = '#1c0d08';
          ctx.beginPath();
          ctx.arc(kneeX, kneeY, 3.2, 0, Math.PI * 2);
          ctx.fill();

          // Amber knuckle core
          ctx.fillStyle = '#f59e0b';
          ctx.beginPath();
          ctx.arc(kneeX, kneeY, 2.2, 0, Math.PI * 2);
          ctx.fill();

          // Bright hot spark specular highlight
          ctx.fillStyle = '#fef08a';
          ctx.beginPath();
          ctx.arc(kneeX - forwardSign * 0.5, kneeY - 0.8, 1.2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = '#220e08';
          ctx.beginPath();
          ctx.arc(kneeX, kneeY, 2.2, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#78351f';
          ctx.beginPath();
          ctx.arc(kneeX, kneeY, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }

        // ── 3. UNTERSCHENKEL (Shin / Tibia: Biologische Formgebung mit organischen Unregelmäßigkeiten) ──
        // Organic outward recurve bowing away from the body before plunging to the ankle
        const sMidX = (kneeX + footX) * 0.5 + clawDir * (isFg ? 3.4 : 2.0);
        const sMidY = (kneeY + footY) * 0.5 - (isFg ? 1.2 : 0.8);

        // Vector and outward-pointing normal for curved chitin carapace
        const stxMid = footX - kneeX;
        const styMid = footY - kneeY;
        const stlenMid = Math.hypot(stxMid, styMid) || 1.0;
        let snxOut = -styMid / stlenMid;
        let snyOut = stxMid / stlenMid;
        if (snxOut * clawDir < 0) {
          snxOut = -snxOut;
          snyOut = -snyOut;
        }

        if (isFg) {
          // Dark muscular chitin base (heavy organic silhouette, matching thigh thickness)
          ctx.beginPath();
          ctx.moveTo(kneeX, kneeY);
          ctx.quadraticCurveTo(sMidX, sMidY, footX, footY);
          ctx.strokeStyle = '#220e08';
          ctx.lineWidth = 4.8; // Substantially thicker for biological mass (previously 2.6)
          ctx.lineCap = 'round';
          ctx.stroke();

          // Terracotta muscular core / exposed sinew layer
          ctx.beginPath();
          ctx.moveTo(kneeX, kneeY);
          ctx.quadraticCurveTo(sMidX, sMidY, footX, footY);
          ctx.strokeStyle = '#6c2514';
          ctx.lineWidth = 3.4;
          ctx.stroke();

          // Outer dorsal chitin carapace armor plate (curved shell on outer crest)
          ctx.beginPath();
          ctx.moveTo(kneeX + snxOut * 1.3, kneeY + snyOut * 1.3);
          ctx.quadraticCurveTo(sMidX + snxOut * 1.6, sMidY + snyOut * 1.6, footX + snxOut * 0.9, footY + snyOut * 0.9);
          ctx.strokeStyle = '#3e150b';
          ctx.lineWidth = 2.0;
          ctx.stroke();

          // Outer biological highlight ridge along curved carapace
          ctx.beginPath();
          ctx.moveTo(kneeX + (sMidX - kneeX) * 0.25 + snxOut * 1.8, kneeY + (sMidY - kneeY) * 0.25 + snyOut * 1.8);
          ctx.quadraticCurveTo(sMidX + snxOut * 2.0, sMidY + snyOut * 2.0, footX - (footX - sMidX) * 0.25 + snxOut * 1.4, footY - (footY - sMidY) * 0.25 + snyOut * 1.4);
          ctx.strokeStyle = '#a8431d';
          ctx.lineWidth = 1.0;
          ctx.stroke();

          // Embedded Biomechanical Actuator & Sinew Fissure (Upper half of shin, directly from reference)
          const p1x = kneeX + (sMidX - kneeX) * 0.28 - snxOut * 0.4;
          const p1y = kneeY + (sMidY - kneeY) * 0.28 - snyOut * 0.4;
          const p2x = sMidX + (footX - sMidX) * 0.14 - snxOut * 0.4;
          const p2y = sMidY + (footY - sMidY) * 0.14 - snyOut * 0.4;

          // Sinew canal
          ctx.beginPath();
          ctx.moveTo(p1x, p1y);
          ctx.lineTo(p2x, p2y);
          ctx.strokeStyle = '#180a06';
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // Amber hydraulic piston core inside sinew canal
          ctx.beginPath();
          ctx.moveTo(p1x + (p2x - p1x) * 0.15, p1y + (p2y - p1y) * 0.15);
          ctx.lineTo(p1x + (p2x - p1x) * 0.85, p1y + (p2y - p1y) * 0.85);
          ctx.strokeStyle = '#d97706';
          ctx.lineWidth = 0.9;
          ctx.stroke();

          // Organic Underside Irregularities (biological chitin nodes & condyle bumps along posterior contour)
          // Replaces artificial vertical section bands with natural anatomical irregularities
          const nodeT = VULTURE_NODE_T;
          const nodeExt = VULTURE_NODE_EXT;
          for (let ni = 0; ni < 3; ni++) {
            const t = nodeT[ni];
            const omt = 1.0 - t;
            const nbx = omt * omt * kneeX + 2.0 * omt * t * sMidX + t * t * footX;
            const nby = omt * omt * kneeY + 2.0 * omt * t * sMidY + t * t * footY;
            const ntx = 2.0 * omt * (sMidX - kneeX) + 2.0 * t * (footX - sMidX);
            const nty = 2.0 * omt * (sMidY - kneeY) + 2.0 * t * (footY - sMidY);
            const ntLen = Math.hypot(ntx, nty) || 1.0;
            const nux = ntx / ntLen;
            const nuy = nty / ntLen;

            // Inner normal pointing toward underside of the leg
            const inx = -snxOut;
            const iny = -snyOut;
            const ext = nodeExt[ni];

            ctx.beginPath();
            ctx.moveTo(nbx + inx * 1.5 + nux * 1.2, nby + iny * 1.5 + nuy * 1.2);
            ctx.lineTo(nbx + inx * (1.8 + ext) - nux * 0.4, nby + iny * (1.8 + ext) - nuy * 0.4);
            ctx.lineTo(nbx + inx * 1.5 - nux * 1.2, nby + iny * 1.5 - nuy * 1.2);
            ctx.fillStyle = '#3e150b';
            ctx.fill();
            ctx.strokeStyle = '#180a06';
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }

          // Organic Ankle Socket Bulb (chitin collar where horn claw anchors into tibia)
          ctx.beginPath();
          ctx.arc(footX, footY - 0.8, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = '#220e08';
          ctx.fill();
          ctx.strokeStyle = '#5a2214';
          ctx.lineWidth = 0.9;
          ctx.stroke();
        } else {
          // Background shin: solid muscular silhouette with organic curvature
          ctx.beginPath();
          ctx.moveTo(kneeX, kneeY);
          ctx.quadraticCurveTo(sMidX, sMidY, footX, footY);
          ctx.strokeStyle = '#180a06';
          ctx.lineWidth = 3.4; // Previously 1.6
          ctx.lineCap = 'round';
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(kneeX, kneeY);
          ctx.quadraticCurveTo(sMidX, sMidY, footX, footY);
          ctx.strokeStyle = '#38160d';
          ctx.lineWidth = 2.2;
          ctx.stroke();

          // Subtle dorsal ridge
          ctx.beginPath();
          ctx.moveTo(kneeX + snxOut * 0.8, kneeY + snyOut * 0.8);
          ctx.quadraticCurveTo(sMidX + snxOut * 1.0, sMidY + snyOut * 1.0, footX + snxOut * 0.5, footY + snyOut * 0.5);
          ctx.strokeStyle = '#5c2214';
          ctx.lineWidth = 1.0;
          ctx.stroke();

          // Background ankle socket
          ctx.beginPath();
          ctx.arc(footX, footY - 0.5, 1.8, 0, Math.PI * 2);
          ctx.fillStyle = '#180a06';
          ctx.fill();
        }

        // ── 4. TALON CLAW (Kurze, spitze biologische Hornkralle mit Fersendorn) ──
        // Direct implementation from reference media_1792594152021_c4006d90.png:
        // Compact, sharp downward-stabbing dagger talon with prominent sharp posterior heel barb
        const isAirborne = finalLift > 1.5;
        const clawPlungeY = isAirborne ? (isFg ? 2.5 : 1.8) : (isFg ? 4.0 : 2.8);
        const clawPlungeX = isAirborne ? (isFg ? 0.8 : 0.5) : (isFg ? 1.4 : 0.9);

        const tipX = footX + clawDir * clawPlungeX;
        const tipY = footY + clawPlungeY;

        const collarFrontX = footX + clawDir * (isFg ? 1.5 : 0.9);
        const collarFrontY = footY - (isFg ? 1.4 : 0.9);
        const collarBackX = footX - clawDir * (isFg ? 1.1 : 0.6);
        const collarBackY = footY - (isFg ? 1.6 : 1.0);

        const barbTipX = footX - clawDir * (isFg ? 3.0 : 1.9);
        const barbTipY = footY - (isFg ? 1.3 : 0.8);
        const notchX = footX - clawDir * (isFg ? 1.2 : 0.7);
        const notchY = footY + (isFg ? 0.6 : 0.3);

        ctx.beginPath();
        ctx.moveTo(collarFrontX, collarFrontY);
        // Anterior convex cutting edge bowing down to the needle tip
        ctx.quadraticCurveTo(collarFrontX + clawDir * (isFg ? 0.8 : 0.4), footY + (isFg ? 1.2 : 0.7), tipX, tipY);
        // Posterior recurve rising from needle tip to notch under the heel barb
        ctx.quadraticCurveTo(notchX, notchY + (isFg ? 1.0 : 0.6), notchX, notchY);
        // Sharp triangular heel barb projecting backward/upward
        ctx.lineTo(barbTipX, barbTipY);
        // Barb top edge returning to ankle socket
        ctx.lineTo(collarBackX, collarBackY);
        ctx.closePath();

        ctx.fillStyle = isFg ? '#c27803' : '#6b2d16'; // Rich organic horn amber / darkened chitin
        ctx.fill();
        ctx.strokeStyle = isFg ? '#1c0d08' : '#140603';
        ctx.lineWidth = isFg ? 0.9 : 0.6;
        ctx.stroke();

        if (isFg) {
          // Anterior sharp cutting edge / specular ivory gleam
          ctx.beginPath();
          ctx.moveTo(collarFrontX, collarFrontY);
          ctx.quadraticCurveTo(collarFrontX + clawDir * 0.7, footY + 1.0, tipX, tipY);
          ctx.strokeStyle = '#fef08a';
          ctx.lineWidth = 0.8;
          ctx.stroke();

          // Needle point flash
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(tipX - 0.4, tipY - 0.4, 0.8, 0.8);
        }
      };

      // ── Layer 1: Background Spider Legs (Far Legs, drawn behind body - EXACTLY 2 LEGS) ─
      // Perspective orientation: legs angle into depth (1 Hinterbein, 1 Vorderbein)
      if (!isBurrowed) {
        // Far Rear Leg (Left Hind: background rear leg, claw points to the left)
        const curRearA = isDrilling ? drillLeg0 : rearA;
        drawSpiderLeg(-forwardSign * 4, -3, -forwardSign * 11, -15, -forwardSign * 9, groundY - 5, curRearA.stride, curRearA.lift, curRearA.tamp || 0, false, true);

        // Far Front Leg (Left Fore: background front leg, claw points forward)
        const curFrontA = isDrilling ? drillLeg1 : frontA;
        drawSpiderLeg(forwardSign * 4, -3, forwardSign * 12, -14, forwardSign * 14, groundY - 5, curFrontA.stride, curFrontA.lift, curFrontA.tamp || 0, false, false);
      }

      // ── Ejected Soil & Sparks Particles during Drilling ("dabei sollen partikel wegfliegen") ──
      if (isDrilling && drillT <= 0.95) {
        ctx.save();
        const pCount = 10;
        for (let pi = 0; pi < pCount; pi++) {
          const pSeed = (pi * 0.19 + drillT * 5.5) % 1.0;
          const pDir = (pi % 2 === 0) ? -1 : 1;
          const px = renderX + pDir * (3 + pSeed * 24);
          const py = (groundY - 2) - Math.sin(pSeed * Math.PI) * (10 + (pi % 3) * 5) + pSeed * pSeed * 10;
          const pAlpha = (1.0 - pSeed) * (1.0 - drillT * 0.4);
          const pSize = 1.1 + (pi % 3) * 0.6;

          ctx.fillStyle = (pi % 3 === 0) ? `rgba(250, 204, 21, ${pAlpha})` : // Golden spark
                          (pi % 3 === 1) ? `rgba(234, 88, 12, ${pAlpha})` :  // Orange friction flash
                                           `rgba(61, 29, 20, ${pAlpha})`;    // Dark bedrock chunk
          ctx.beginPath();
          ctx.arc(px, py, pSize, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      // ── Layer 2: Sul Rumpf (Segmented Chitin Carapace Chassis & Sphere Cradle) ──
      if (!isBurrowed) {
        ctx.save();
        if ((isSprinting || isSurfaceCollapse) && gallopPitch !== 0) {
          ctx.translate(bodyBaseX, bodyBaseY - 1);
          ctx.rotate(gallopPitch);
          ctx.translate(-bodyBaseX, -(bodyBaseY - 1));
        }

        // 1. Thorax / Abdomen Chassis Body (Solid chitin carapace)
        ctx.fillStyle = '#27130d';
        ctx.beginPath();
        ctx.ellipse(bodyBaseX, bodyBaseY - 1, 10.5, 5.0, 0, 0, Math.PI * 2);
        ctx.fill();

        // Segmented dorsal armor plates
        ctx.fillStyle = '#522519';
        ctx.beginPath();
        ctx.ellipse(bodyBaseX, bodyBaseY - 2, 9.0, 4.0, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#78351f';
        ctx.beginPath();
        ctx.ellipse(bodyBaseX, bodyBaseY - 2.5, 7.5, 3.0, 0, 0, Math.PI * 2);
        ctx.fill();

        // Dorsal chitin ridges
        ctx.strokeStyle = '#9a3412';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(bodyBaseX - 6, bodyBaseY - 3);
        ctx.lineTo(bodyBaseX - 6, bodyBaseY);
        ctx.moveTo(bodyBaseX, bodyBaseY - 3.5);
        ctx.lineTo(bodyBaseX, bodyBaseY);
        ctx.moveTo(bodyBaseX + 6, bodyBaseY - 3);
        ctx.lineTo(bodyBaseX + 6, bodyBaseY);
        ctx.stroke();

        // Bioluminescent amber vent fissure slits along chassis flank (dims upon detachment)
        const ventDim = isSurfaceCollapse ? Math.max(0.1, 1.0 - (riseProg / 0.45)) : 1.0;
        const ventPulse = isSprinting ? (0.8 + Math.abs(Math.sin(gallopPhase)) * 0.4) : (0.7 + Math.sin(t * 3.0 + wobble) * 0.3);
        ctx.fillStyle = `rgba(249, 115, 22, ${0.9 * ventPulse * ventDim})`;
        ctx.fillRect(bodyBaseX - 5, bodyBaseY - 1.5, 10, 1.4);
        ctx.fillStyle = `rgba(254, 240, 138, ${0.85 * ventPulse * ventDim})`;
        ctx.fillRect(bodyBaseX - 2.5, bodyBaseY - 1.2, 5, 0.8);

        // 2. Armored Hydraulic Sphere Cradle & Retaining Clamps
        ctx.strokeStyle = '#78351f';
        ctx.lineWidth = 2.0;
        ctx.beginPath();
        ctx.arc(bodyBaseX, bodyBaseY - 6.5, 7.8, 0.15 * Math.PI, 0.85 * Math.PI);
        ctx.stroke();

        // Front and rear retaining claw brackets clasping the sphere
        // When sphere detaches on surface, hydraulic clamps burst open outward!
        const clampSpread = isSurfaceCollapse ? (Math.min(1.0, riseProg / 0.25) * 4.0) : 0;
        ctx.fillStyle = '#9a3412';
        ctx.beginPath();
        ctx.arc(bodyBaseX - 6.5 - clampSpread, bodyBaseY - 5.5 + clampSpread * 0.3, 1.8, 0, Math.PI * 2);
        ctx.arc(bodyBaseX + 6.5 + clampSpread, bodyBaseY - 5.5 + clampSpread * 0.3, 1.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#f59e0b';
        ctx.fillRect(bodyBaseX - 7.0 - clampSpread, bodyBaseY - 6.2 + clampSpread * 0.3, 1.5, 1.5);
        ctx.fillRect(bodyBaseX + 5.5 + clampSpread, bodyBaseY - 6.2 + clampSpread * 0.3, 1.5, 1.5);

        // 3. Coxa Joint Socket Hubs (where legs connect)
        ctx.fillStyle = '#3d1d14';
        ctx.strokeStyle = '#78351f';
        ctx.lineWidth = 1.0;
        const coxaOffsets = VULTURE_COXA_OFFSETS;
        for (let ci = 0; ci < coxaOffsets.length; ci++) {
          const cx = bodyBaseX + coxaOffsets[ci] * forwardSign;
          ctx.beginPath();
          ctx.arc(cx, bodyBaseY + 0.5, 1.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
        ctx.restore();

        // Detachment burst sparks at the opened clamp brackets
        if (isSurfaceCollapse && riseProg <= 0.35) {
          ctx.save();
          const popAlpha = 1.0 - (riseProg / 0.35);
          ctx.fillStyle = `rgba(250, 204, 21, ${popAlpha * 0.9})`;
          for (let si = 0; si < 4; si++) {
            const sAngle = (si * Math.PI / 2) + riseProg * 10;
            const sDist = 3 + riseProg * 22;
            ctx.beginPath();
            ctx.arc(bodyBaseX - 6.5 + Math.cos(sAngle) * sDist, bodyBaseY - 6.5 + Math.sin(sAngle) * sDist, 1.2, 0, Math.PI * 2);
            ctx.arc(bodyBaseX + 6.5 + Math.cos(sAngle) * sDist, bodyBaseY - 6.5 + Math.sin(sAngle) * sDist, 1.2, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }
      }

      // ── Layer 3: Foreground Spider Legs (Near Legs, drawn in front of body - EXACTLY 4 LEGS) ─
      // Perspective orientation: 4 legs with prominent golden knuckles (2 Hinterbeine, 2 Vorderbeine)
      // Both Hinterbeine have feet/claws pointing strictly to the LEFT (isRear = true)
      if (!isBurrowed) {
        // Near Rear Leg 1 (Outer Hind: rear leg arching far left, claw pointing left)
        const curRearB1 = isDrilling ? drillLeg2 : rearB1;
        drawSpiderLeg(-forwardSign * 6, +1, -forwardSign * 18, -13, -forwardSign * 17, groundY + 3, curRearB1.stride, curRearB1.lift, curRearB1.tamp || 0, true, true);

        // Near Rear Leg 2 (Inner Hind: rear leg arching mid left, claw pointing left)
        const curRearB2 = isDrilling ? drillLeg3 : rearB2;
        drawSpiderLeg(-forwardSign * 2, +1, -forwardSign * 10, -12, -forwardSign * 9, groundY + 3, curRearB2.stride, curRearB2.lift, curRearB2.tamp || 0, true, true);

        // Near Front Leg 1 (Inner Fore: front leg lunging mid right, claw pointing forward/right)
        const curFrontB1 = isDrilling ? drillLeg4 : frontB1;
        drawSpiderLeg(forwardSign * 2, +1, forwardSign * 10, -14, forwardSign * 11, groundY + 3, curFrontB1.stride, curFrontB1.lift, curFrontB1.tamp || 0, true, false);

        // Near Front Leg 2 (Outer Fore: lead front leg reaching far right, claw pointing forward/right)
        const curFrontB2 = isDrilling ? drillLeg5 : frontB2;
        drawSpiderLeg(forwardSign * 6, +1, forwardSign * 18, -15, forwardSign * 22, groundY + 3, curFrontB2.stride, curFrontB2.lift, curFrontB2.tamp || 0, true, false);
      }

      // ── Layer 4: Earthen Mound / Crater (Grows when burrowing, covers sunken chassis & legs) ─
      if (isBurrowing || isBurrowed) {
        const moundScale = isBurrowing ? Math.max(0, (burrowProg - 0.28) / 0.72) : 1.0;
        const moundW = 16 * moundScale;
        const moundH = 6.5 * moundScale;

        ctx.fillStyle = '#1c110a';
        ctx.beginPath();
        ctx.ellipse(bodyBaseX, groundY - 2, moundW, moundH, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#2b1b11';
        ctx.beginPath();
        ctx.ellipse(bodyBaseX, groundY - 3, moundW * 0.8, moundH * 0.75, 0, 0, Math.PI * 2);
        ctx.fill();

        // Chitin anchor spurs sticking out of the soil
        if (moundScale > 0.4) {
          ctx.strokeStyle = '#522519';
          ctx.lineWidth = 2.0;
          ctx.beginPath();
          ctx.moveTo(bodyBaseX - 8 * moundScale, groundY - 4);
          ctx.lineTo(bodyBaseX - 14 * moundScale, groundY - 1);
          ctx.moveTo(bodyBaseX + 8 * moundScale, groundY - 4);
          ctx.lineTo(bodyBaseX + 14 * moundScale, groundY - 1);
          ctx.stroke();
        }
      }

      // ── Layer 5: COMPACT SPHERICAL GLASS TANK & REALISTIC LIQUID SIMULATION ────────────
      let sphereX = bodyBaseX;
      let sphereY = bodyBaseY - 7.5;
      const sphereR = 7.2; // Compact, perfectly balanced with the muscular Rumpf

      // Gallop rocking tilt applied to sphere position
      if (isSprinting && gallopPitch !== 0) {
        const sinP = Math.sin(gallopPitch);
        const cosP = Math.cos(gallopPitch);
        const relY = -7.5;
        sphereX = bodyBaseX - sinP * relY;
        sphereY = bodyBaseY + cosP * relY;
      }

      // Hot-Air Balloon Ascent kinematics ("erst eine sekunde langsam nach oben fliegen und dann explodieren")
      let boilAgitation = 1.0;
      if (isRising) {
        // Slow, ominous steady ascent over the full 1.0s duration (~42px lift)
        const liftY = Math.pow(riseProg, 0.95) * 42.0;
        const balloonSway = Math.sin(riseProg * Math.PI * 4) * 1.5;
        if (isSurfaceCollapse) {
          // Detaches immediately from original upright surface position and ascends into sky
          sphereX = renderX + balloonSway;
          sphereY = (renderY - 10.5) - liftY;
        } else {
          sphereY -= liftY;
          sphereX += balloonSway;
        }
        boilAgitation = 2.0 + riseProg * 3.0;

        // Downward bio-steam / propulsion jet escaping socket down to crater or collapsed chassis
        const jetTargetY = isSurfaceCollapse ? (bodyBaseY - 4) : (groundY - 2);
        ctx.save();
        if (this.quality !== 'low') {
          const ventGrad = ctx.createLinearGradient(sphereX, sphereY + sphereR, sphereX, jetTargetY);
          ventGrad.addColorStop(0, 'rgba(56, 189, 248, 0.85)');
          ventGrad.addColorStop(0.5, 'rgba(56, 189, 248, 0.35)');
          ventGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
          ctx.fillStyle = ventGrad;
        } else {
          ctx.fillStyle = 'rgba(56, 189, 248, 0.45)';
        }
        ctx.beginPath();
        ctx.moveTo(sphereX - 2.8, sphereY + sphereR);
        ctx.lineTo(sphereX + 2.8, sphereY + sphereR);
        ctx.lineTo(bodyBaseX + Math.sin(t * 22) * 2.0, jetTargetY);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }

      // Dynamic Sloshing & Inertial Fluid Tilt ("beim laufen sollte die flüssigkeit hin- und herschwappen")
      let fluidTilt = 0;
      let waveAgitation = 1.0;
      if (isSprinting) {
        fluidTilt = -gallopPitch * 1.8 + Math.sin(gallopPhase - 0.4) * 0.28 - forwardSign * 0.16;
        waveAgitation = 1.4;
      } else if (isBurrowing) {
        fluidTilt = Math.sin(t * 32) * 0.14;
        waveAgitation = 1.5;
      } else if (isRising) {
        fluidTilt = Math.sin(t * 24) * 0.16;
        waveAgitation = 3.0;
      } else {
        fluidTilt = Math.sin(t * 2.5 + wobble) * 0.05;
        waveAgitation = 0.8;
      }

      // Outer Glow Halo around glass sphere (quality gated)
      if (this.quality !== 'low') {
        const glowR = sphereR * (isRising ? (1.5 + riseProg * 0.8) : 1.35);
        if (isRising) {
          const glowGrad = ctx.createRadialGradient(sphereX, sphereY, sphereR * 0.5, sphereX, sphereY, glowR);
          glowGrad.addColorStop(0, 'rgba(255, 255, 255, 0.45)');
          glowGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
          ctx.fillStyle = glowGrad;
          ctx.beginPath();
          ctx.arc(sphereX, sphereY, glowR, 0, Math.PI * 2);
          ctx.fill();
        } else {
          if (!_cachedVultureNormalGlowGrad) {
            // Quality-gated via outer this.quality !== 'low'
            _cachedVultureNormalGlowGrad = ctx.createRadialGradient(0, 0, 0.35, 0, 0, 1);
            _cachedVultureNormalGlowGrad.addColorStop(0, 'rgba(56, 189, 248, 0.35)');
            _cachedVultureNormalGlowGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
          }
          ctx.save();
          ctx.translate(sphereX, sphereY);
          ctx.scale(glowR, glowR);
          ctx.fillStyle = _cachedVultureNormalGlowGrad;
          ctx.beginPath();
          ctx.arc(0, 0, 1, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }

      // Clip inside Glass Sphere for Fluid Rendering
      ctx.save();
      ctx.beginPath();
      ctx.arc(sphereX, sphereY, sphereR, 0, Math.PI * 2);
      ctx.clip();

      // Fluid Fill: fills 65% of sphere height
      const fluidBaseY = sphereY + sphereR * 0.12;
      const leftX = sphereX - sphereR - 1.5;
      const rightX = sphereX + sphereR + 1.5;
      const leftY = fluidBaseY - Math.tan(fluidTilt) * (sphereR + 1.5);
      const rightY = fluidBaseY + Math.tan(fluidTilt) * (sphereR + 1.5);

      // Fluid Polygon
      ctx.beginPath();
      ctx.moveTo(leftX, leftY);
      // Sloshing wave surface curve
      const midWaveY = (leftY + rightY) * 0.5 + Math.sin(gallopPhase * 2) * (1.1 * waveAgitation);
      ctx.quadraticCurveTo(sphereX, midWaveY, rightX, rightY);
      ctx.lineTo(rightX, sphereY + sphereR + 2);
      ctx.lineTo(leftX, sphereY + sphereR + 2);
      ctx.closePath();

      // Fluid Radial Core Gradient
      if (this.quality !== 'low') {
        if (isRising) {
          if (!_cachedVultureRisingFluidGrad) {
            _cachedVultureRisingFluidGrad = ctx.createRadialGradient(0, 0, 0.05, 0, 0, 1);
            _cachedVultureRisingFluidGrad.addColorStop(0, '#ffffff');
            _cachedVultureRisingFluidGrad.addColorStop(0.3, '#38bdf8');
            _cachedVultureRisingFluidGrad.addColorStop(0.7, '#0ea5e9');
            _cachedVultureRisingFluidGrad.addColorStop(1, '#0284c7');
          }
        } else {
          if (!_cachedVultureNormalFluidGrad) {
            // Quality-gated via outer this.quality !== 'low'
            _cachedVultureNormalFluidGrad = ctx.createRadialGradient(0, 0, 0.05, 0, 0, 1);
            _cachedVultureNormalFluidGrad.addColorStop(0, '#00e5ff');
            _cachedVultureNormalFluidGrad.addColorStop(0.35, '#38bdf8');
            _cachedVultureNormalFluidGrad.addColorStop(0.75, '#0284c7');
            _cachedVultureNormalFluidGrad.addColorStop(1, '#0369a1');
          }
        }
        ctx.save();
        ctx.translate(sphereX, fluidBaseY + 2);
        ctx.scale(sphereR, sphereR);
        ctx.fillStyle = isRising ? _cachedVultureRisingFluidGrad : _cachedVultureNormalFluidGrad;
        ctx.fill();
        ctx.restore();
      } else {
        ctx.fillStyle = isRising ? '#38bdf8' : '#0284c7';
        ctx.fill();
      }

      // Highlighted Meniscus Surface Line
      ctx.beginPath();
      ctx.moveTo(leftX, leftY);
      ctx.quadraticCurveTo(sphereX, midWaveY, rightX, rightY);
      ctx.strokeStyle = isRising ? '#ffffff' : 'rgba(224, 242, 254, 0.9)';
      ctx.lineWidth = 1.3;
      ctx.stroke();

      // Internal Micro-Bubbles
      const bubbleCount = isRising ? 5 : 3;
      const seeds = VULTURE_BUBBLE_SEEDS;
      for (let bi = 0; bi < bubbleCount; bi++) {
        const s = seeds[bi];
        const bubbleSpeed = 0.65 * boilAgitation;
        const bPhase = (t * bubbleSpeed + s) % 1.0;
        const bx = sphereX + (s - 0.5) * (sphereR * 1.3) + Math.sin(t * 5 + s * 8) * 1.0;
        const by = (sphereY + sphereR * 0.8) - bPhase * (sphereR * 1.2);
        if (by >= midWaveY) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
          ctx.beginPath();
          ctx.arc(bx, by, 0.8 + (bi % 2) * 0.4, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.restore(); // Exit sphere clipping

      // Outer Glass Sphere Contours & Specular Reflections
      ctx.strokeStyle = 'rgba(186, 230, 253, 0.75)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(sphereX, sphereY, sphereR, 0, Math.PI * 2);
      ctx.stroke();

      // Gloss Specular Reflection Arc on Upper-Left Shoulder
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.arc(sphereX, sphereY, sphereR - 1.4, 1.15 * Math.PI, 1.55 * Math.PI);
      ctx.stroke();

      // White Specular Glint Dot
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(sphereX - sphereR * 0.42, sphereY - sphereR * 0.42, 0.9, 0, Math.PI * 2);
      ctx.fill();

      // Lower Refractive Rim Accent
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.45)';
      ctx.lineWidth = 0.9;
      ctx.beginPath();
      ctx.arc(sphereX, sphereY, sphereR - 1.0, 0.15 * Math.PI, 0.45 * Math.PI);
      ctx.stroke();

      ctx.restore();

    } else if (unitType === 'DelvingPillar' || unitType === 'Delving_Pillar' || unitType === 'Delving Pillar') {
      // 𓉐 BESPOKE SUL DELVING PILLAR LIVING BIO-MECHANICAL RIG:
      // Otherworldly Matrix Sentinel airborne swimming & propulsion,
      // 3D layered tentacle bundle (background, core & reaching foreground),
      // searching-finger tip precession, glowing golden searchlight nodes,
      // segmented chitin shell breathing, compound ocular cluster saccades.
      const isWestern = (unit.team === 'western');
      const forwardSign = isWestern ? 1 : -1;
      const faceDir = dir;
      const wobble = unit.wobbleOffset || 0;

      // 30% smaller unit size as requested
      const pillarSize = drawSize * 0.70;

      // Time delta & physical phase integration for continuous organic motion
      if (unit._pillarLastT === undefined) {
        unit._pillarLastT = t;
        unit._pillarPropPhase = wobble;
        unit._pillarFlightFactor = isWalking ? 1.0 : 0.0;
        unit._pillarTargetBias = 0.0;
      }
      const dt = Math.max(0, Math.min(0.1, t - unit._pillarLastT));
      unit._pillarLastT = t;

      // 1. Aerodynamic Cruising State & Flight Factor (framerate-independent smooth blend)
      const targetFlight = isWalking ? 1.0 : 0.0;
      const flightBlendRate = 1 - Math.exp(-7.5 * dt);
      unit._pillarFlightFactor += (targetFlight - unit._pillarFlightFactor) * flightBlendRate;
      const flightFactor = Math.max(0, Math.min(1, unit._pillarFlightFactor));

      // 2. Wide-Wavelength Spatial Ether Hover & Fluid Matrix Propulsion
      // Delving Pillar glides very slowly and smoothly in a long phase (no rapid hopping / footstep bouncing)
      const hoverPhase = t * 0.75 + wobble;
      const hoverBob = Math.sin(hoverPhase) * 3.6 + Math.sin(hoverPhase * 0.5 + 1.1) * 1.2;

      // Matrix Sentinel Propulsion Stroke:
      // Smooth continuous phase integration: integrating propSpeed * dt ensures
      // zero twitching or phase-skipping when accelerating to cruise speed or stopping!
      const propSpeed = 1.4 + flightFactor * 0.8;
      unit._pillarPropPhase = (unit._pillarPropPhase + dt * propSpeed) % (Math.PI * 2);
      const propCycle = unit._pillarPropPhase;

      // Body reaction: subtle forward glide surge and slight pitch
      const bodyThrust = Math.sin(propCycle);
      const thrustKick = Math.max(0, bodyThrust); // 0 to 1 peak push
      const bodySurgeX = faceDir * (thrustKick * flightFactor * 1.2);
      const thrustPitch = faceDir * (thrustKick * 0.03 * flightFactor);

      // Biological drift & organic respiration
      const bioDriftX = Math.cos(t * 0.45 + wobble) * 0.8 + Math.sin(t * 0.9 + wobble * 0.5) * 0.3;
      const breathPulse = Math.sin(t * 1.2 + wobble);
      const stretchY = 1.0 + breathPulse * 0.018 + thrustKick * 0.02;
      const squashX = 1.0 - breathPulse * 0.012 - thrustKick * 0.015;

      // Dynamic tilt & wave slope banking
      const waveSlopeTilt = faceDir * (Math.cos(hoverPhase) * 0.025 * flightFactor);
      const idleTilt = Math.sin(t * 0.5 + wobble) * 0.025;
      const flightTilt = faceDir * (flightFactor * 0.10) + waveSlopeTilt + thrustPitch;
      let recoilTilt = 0;
      let recoilX = 0;
      if (unit.hitFlashTimer > 0) {
        const recoilProg = unit.hitFlashTimer / 0.12;
        const kick = Math.sin(recoilProg * Math.PI) * 3.5;
        recoilX = -faceDir * kick;
        recoilTilt = -faceDir * (kick * 0.03);
      } else if (isAttacking) {
        const launchKick = Math.sin(t * 16) * 2.0;
        recoilX = -faceDir * launchKick;
        recoilTilt = -faceDir * (launchKick * 0.02);
      }
      const totalTilt = (1 - flightFactor) * idleTilt + flightTilt + recoilTilt;

      // Sentinel main position
      const pillarX = renderX + recoilX + bodySurgeX + bioDriftX * (1 - flightFactor * 0.7);
      const pillarY = renderY + hoverBob - pillarSize * 0.12;

      // Target Tracking / Alert Angle (Sentinels raise search tentacles toward quarry)
      let hasTargetAngle = false;
      let targetAngle = 0;
      if (unit.target) {
        const dx = unit.target.x - unit.x;
        const dy = unit.target.y - unit.y;
        targetAngle = Math.atan2(dy, dx);
        hasTargetAngle = true;
      }

      // Smooth target seeking bias factor so foreground search tentacles don't snap abruptly
      const targetBiasGoal = (hasTargetAngle && this.quality !== 'low') ? 1.0 : 0.0;
      const biasBlendRate = 1 - Math.exp(-8.0 * dt);
      unit._pillarTargetBias = (unit._pillarTargetBias || 0) + (targetBiasGoal - (unit._pillarTargetBias || 0)) * biasBlendRate;
      const currentTargetBias = Math.max(0, Math.min(1, unit._pillarTargetBias));

      if (hasTargetAngle) {
        if (unit._pillarTargetAngle === undefined) unit._pillarTargetAngle = targetAngle;
        let diff = (targetAngle - unit._pillarTargetAngle) % (Math.PI * 2);
        if (diff > Math.PI) diff -= Math.PI * 2;
        if (diff < -Math.PI) diff += Math.PI * 2;
        unit._pillarTargetAngle += diff * (1 - Math.exp(-10.0 * dt));
      }

      // 3. Matrix Living Tentacle Kinematic Rig (9 Strands × 9 Segments)
      const cosT = Math.cos(totalTilt);
      const sinT = Math.sin(totalTilt);
      const isLow = (this.quality === 'low') || isSimpleTier;
      const isMed = this.quality === 'medium';
      const strandStep = isLow ? 2 : 1;
      const segStep = isLow ? 2 : 1;

      // Calculate all strand point positions into zero-allocation scratch buffer
      for (let s = 0; s < 9; s += strandStep) {
        const sFreq = DELVING_TENTACLE_FREQ[s];
        const sPhase = DELVING_TENTACLE_PHASE[s];
        const sSpatial = DELVING_TENTACLE_SPATIAL[s];
        const sAmp = DELVING_TENTACLE_AMP[s];
        const sCurlFreq = DELVING_TENTACLE_CURL_FREQ[s];
        const sCurlAmp = DELVING_TENTACLE_CURL_AMP[s];

        // Anchor coordinate in local body space rotated by totalTilt (rear cuffs at -X when facing right)
        const localAnchorX = faceDir * DELVING_TENTACLE_ANCHOR_X[s] * pillarSize * squashX;
        const localAnchorY = DELVING_TENTACLE_ANCHOR_Y[s] * pillarSize * stretchY;
        const anchorX = pillarX + cosT * localAnchorX - sinT * localAnchorY;
        const anchorY = pillarY + sinT * localAnchorX + cosT * localAnchorY;

        const strandLen = pillarSize * DELVING_TENTACLE_LEN[s] * stretchY;
        _delvingTentaclePtsScratch[s][0].x = anchorX;
        _delvingTentaclePtsScratch[s][0].y = anchorY;

        // Is this a foreground seeking tentacle (strands 6, 7, 8)?
        const isForegroundSeeker = (s >= 6);

        for (let k = segStep; k <= 9; k += segStep) {
          const u = k / 9; // 0.11 to 1.0

          // A. Traveling Propulsion Wave (Matrix Sentinel "Schwimmstoß"):
          // Propulsion wave propagates down from base (u=0) to tips (u=1) with a phase delay
          const waveTravelingPhase = propCycle - u * 3.8;
          const strokeWhip = Math.sin(waveTravelingPhase);
          const strokeSpread = Math.cos(waveTravelingPhase);

          // B. Multi-harmonic serpentine wave (Traveling undulating S-curve)
          const wavePhase1 = t * (2.2 * sFreq) - u * sSpatial + sPhase + wobble;
          const wavePhase2 = t * (1.1 * sFreq) - u * 3.4 + sPhase;
          const idleWaveX = Math.sin(wavePhase1) * (u * 4.5 * sAmp) + Math.cos(wavePhase2) * (u * 1.8);
          const idleWaveY = Math.cos(wavePhase1 * 0.8) * (u * 2.2 * sAmp);

          // C. Aerodynamic Backward Flow ("wehen weit nach hinten beim Fliegen")
          // In flight, the tentacles trail significantly further back horizontally against the slipstream
          const baseFlightDragX = -faceDir * (Math.pow(u, 1.05) * (pillarSize * (0.28 + flightFactor * 0.72)));
          // Lifts the tentacles up horizontally into a sleek aerodynamic jet stream instead of hanging down
          const flightLiftY = -flightFactor * (Math.pow(u, 1.28) * (pillarSize * 0.42));

          // D. Active Propulsion Thrust Kick:
          // The stroke creates a strong backward whip surge that travels along the strand
          const thrustSweepX = -faceDir * (0.25 + 0.75 * flightFactor) * Math.max(0, strokeWhip) * (u * pillarSize * 0.52);
          const thrustSweepY = strokeSpread * (u * pillarSize * (0.08 + 0.18 * flightFactor));

          // Rhythmic gathering & flaring (tentacles gather tight on stroke, flare gently on recovery)
          const constrictFactor = 1.0 - (flightFactor * 0.45) * Math.max(0, strokeWhip);
          const lateralSpread = (s - 4) * (0.07 * pillarSize * u) * constrictFactor * (1.0 + strokeSpread * 0.25);

          // High-frequency slipstream wind flutter during flight
          const flutterWave = Math.sin(t * 8.0 + s * 1.1 - u * 4.2) * (flightFactor * u * 3.0);

          // E. Autonomous searching-finger tip precession
          let curlX = 0;
          let curlY = 0;
          if (k >= 5) {
            const curlWeight = (k - 4) / 5;
            const curlSuppression = 1.0 - flightFactor * 0.35;
            curlX = Math.sin(t * sCurlFreq + sPhase * 1.6) * (curlWeight * sCurlAmp * curlSuppression);
            curlY = Math.cos(t * (sCurlFreq * 0.85) + sPhase * 1.6) * (curlWeight * sCurlAmp * 0.6 * curlSuppression);
          }

          // F. Target seeking reach (Foreground tentacles orient smoothly toward enemy target)
          let targetBiasX = 0;
          let targetBiasY = 0;
          if (isForegroundSeeker && currentTargetBias > 0.001) {
            const seekAngle = unit._pillarTargetAngle !== undefined ? unit._pillarTargetAngle : targetAngle;
            const seekWeight = Math.pow(u, 1.5) * 10.0 * currentTargetBias;
            targetBiasX = Math.cos(seekAngle) * seekWeight;
            targetBiasY = Math.sin(seekAngle) * seekWeight;
          }

          // Final coordinate calculation
          const ptX = anchorX
            + idleWaveX * (1 - flightFactor)
            + baseFlightDragX
            + thrustSweepX
            + lateralSpread
            + curlX
            + flutterWave
            + targetBiasX;

          const ptY = anchorY
            + (u * strandLen)
            + idleWaveY * (1 - flightFactor)
            + flightLiftY
            + thrustSweepY
            + curlY
            + targetBiasY;

          _delvingTentaclePtsScratch[s][k].x = ptX;
          _delvingTentaclePtsScratch[s][k].y = ptY;
        }
      }

      // =========================================================================
      // LAYER A: BACKGROUND TENTACLES (Strands 0, 1, 2) — Drawn behind body sprite
      // =========================================================================
      ctx.save();
      ctx.lineCap = 'round';
      for (let k = segStep; k <= 9; k += segStep) {
        const prevIdx = k - segStep;
        ctx.strokeStyle = DELVING_TENTACLE_BG_COLORS[k - 1];
        ctx.lineWidth = Math.max(0.7, DELVING_TENTACLE_SEG_WIDTHS[k - 1] * 0.65);
        ctx.beginPath();
        for (let s = 0; s < 3; s += strandStep) {
          ctx.moveTo(_delvingTentaclePtsScratch[s][prevIdx].x, _delvingTentaclePtsScratch[s][prevIdx].y);
          ctx.lineTo(_delvingTentaclePtsScratch[s][k].x, _delvingTentaclePtsScratch[s][k].y);
        }
        ctx.stroke();
      }
      ctx.restore();

      // =========================================================================
      // LAYER B: BODY SPRITE, CHITIN CARAPACE & COMPOUND SENSORY OCULAR ARRAY
      // =========================================================================
      let spriteKey = 'DelvingPillar_Hover';
      if (isAttacking) {
        spriteKey = 'DelvingPillar_Attack';
      } else if (flightFactor > 0.45) {
        spriteKey = 'DelvingPillar_Drift';
      }

      const pillarBitmap = buildSpriteBitmap(spriteKey, renderRace, unit.team);
      const activeImg = isHitFlashing ? pillarBitmap.flash : pillarBitmap.normal;
      const sW = activeImg.width;
      const sH = activeImg.height;
      const splitFrac = 10 / 24; // Rows 0-9: Dorsal Chitin Carapace; Rows 10-23: Snout, Neck & Root Cuffs
      const splitH = pillarSize * splitFrac;

      ctx.save();
      ctx.translate(pillarX, pillarY);
      ctx.rotate(faceDir * totalTilt);
      ctx.scale(squashX, stretchY);
      ctx.globalAlpha = spawnAlpha;

      // 1. Dorsal Armor Shell (Upper Carapace)
      ctx.drawImage(
        activeImg,
        0, 0, sW, sH * splitFrac,
        -pillarSize / 2, -pillarSize / 2, pillarSize, splitH
      );

      // 2. Visceral Snout, Neck & Root Cuffs with biological delayed inertia
      const neckLagY = Math.sin(hoverPhase - 0.4) * (flightFactor * 0.7) - (breathPulse * 0.3);
      const neckTilt = Math.sin(t * 1.6 + wobble) * 0.02;
      ctx.save();
      ctx.translate(0, -pillarSize / 2 + splitH + neckLagY);
      ctx.rotate(neckTilt);
      ctx.drawImage(
        activeImg,
        0, sH * splitFrac, sW, sH * (1 - splitFrac),
        -pillarSize / 2, 0, pillarSize, pillarSize * (1 - splitFrac)
      );
      ctx.restore();

      // 3. Dynamic Compound Ocular Matrix Glow & Forward Search Beam
      if (!isHitFlashing) {
        const eyeFlicker = 0.70 + 0.30 * Math.sin(t * 9.5 + wobble);
        const inCombat = !!unit.target || unit.attackCooldown > 0;
        const ocularGlowTex = getSulDelvingPillarOcularGlowCanvas();
        const oR = pillarSize * (inCombat ? 0.34 : 0.26);
        ctx.globalAlpha = (inCombat ? 0.85 : 0.55) * eyeFlicker * spawnAlpha;

        // 3/4 Profile: Central compound ocular snout cluster (+X forward in snout area)
        const snoutOffX = pillarSize * 0.22;
        const snoutOffY = -pillarSize * 0.02;
        ctx.drawImage(ocularGlowTex, snoutOffX - oR, snoutOffY - oR, oR * 2, oR * 2);

        // Flank / dorsal ocular sensor dome (-X back on carapace crest)
        const shellOffX = -pillarSize * 0.08;
        const shellOffY = -pillarSize * 0.28;
        const fR = pillarSize * 0.16;
        ctx.drawImage(ocularGlowTex, shellOffX - fR, shellOffY - fR, fR * 2, fR * 2);

        // Forward compound detector search beam projecting forward from the snout
        if (this.quality !== 'low') {
          const beamDist = pillarSize * (inCombat ? 1.4 : 0.95);
          const beamSpread = pillarSize * 0.45;
          const beamTex = getSulDelvingPillarBeamCanvas(inCombat);
          ctx.drawImage(beamTex, snoutOffX, snoutOffY - beamSpread * 0.5, beamDist, beamSpread);
        }
      }

      ctx.restore();

      // =========================================================================
      // LAYER C: MID & FOREGROUND TENTACLES (Strands 3..8) — Drawn in front of body
      // =========================================================================
      ctx.save();
      ctx.lineCap = 'round';
      for (let k = segStep; k <= 9; k += segStep) {
        const prevIdx = k - segStep;
        ctx.strokeStyle = DELVING_TENTACLE_FG_COLORS[k - 1];
        ctx.lineWidth = Math.max(0.7, DELVING_TENTACLE_SEG_WIDTHS[k - 1] * 0.72);
        ctx.beginPath();
        for (let s = 3; s < 9; s += strandStep) {
          ctx.moveTo(_delvingTentaclePtsScratch[s][prevIdx].x, _delvingTentaclePtsScratch[s][prevIdx].y);
          ctx.lineTo(_delvingTentaclePtsScratch[s][k].x, _delvingTentaclePtsScratch[s][k].y);
        }
        ctx.stroke();
      }

      // =========================================================================
      // LAYER D: LUMINOUS SEARCHLIGHT BULBS, LENS FLARES & TELEMETRY BEACONS
      // =========================================================================
      // 1. Tip Bulb Core Spheres
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      for (let s = 0; s < 9; s += strandStep) {
        const tip = _delvingTentaclePtsScratch[s][9];
        const tipRadius = (s === 4 || s === 7) ? 1.8 : 1.3;
        ctx.moveTo(tip.x + tipRadius, tip.y);
        ctx.arc(tip.x, tip.y, tipRadius, 0, Math.PI * 2);
      }
      ctx.fill();

      // 2. Pre-Baked Radiant Searchlight Glows & Directional Cones (Major Strands 1, 3, 4, 6, 7)
      if (!isLow) {
        const tipGlowTex = getSulDelvingPillarTipGlowCanvas();
        const pulse = 0.85 + 0.15 * Math.sin(t * 4.2 + wobble);
        const glowR = 10 * pulse;

        for (let s = 1; s < 9; s += 2) {
          const tip = _delvingTentaclePtsScratch[s][9];
          const prev = _delvingTentaclePtsScratch[s][7];
          // Blit pre-baked soft golden halo
          ctx.drawImage(tipGlowTex, tip.x - glowR, tip.y - glowR, glowR * 2, glowR * 2);

          // Radiant 4-point lens flare cross at tip center
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(tip.x - 2.8, tip.y); ctx.lineTo(tip.x + 2.8, tip.y);
          ctx.moveTo(tip.x, tip.y - 2.8); ctx.lineTo(tip.x, tip.y + 2.8);
          ctx.stroke();

          // High/Ultra: Forward searchlight cone projecting from tentacle tip
          if (!isMed) {
            const dirX = tip.x - prev.x;
            const dirY = tip.y - prev.y;
            const len = Math.hypot(dirX, dirY) || 1;
            const nx = dirX / len;
            const ny = dirY / len;
            const coneDist = pillarSize * 0.42;
            const spread = 5.5;

            ctx.fillStyle = 'rgba(254, 240, 138, 0.12)';
            ctx.beginPath();
            ctx.moveTo(tip.x, tip.y);
            ctx.lineTo(tip.x + nx * coneDist - ny * spread, tip.y + ny * coneDist + nx * spread);
            ctx.lineTo(tip.x + nx * coneDist + ny * spread, tip.y + ny * coneDist - nx * spread);
            if (ctx.closePath) ctx.closePath();
            ctx.fill();
          }
        }

        // Drifting telemetry spark particles shedding into the slipstream
        if (!isMed) {
          for (let s = 2; s < 8; s += 2) {
            const tip = _delvingTentaclePtsScratch[s][9];
            const sparkCycle = (t * 2.2 + s * 0.4) % 1.0;
            const sparkAlpha = (1 - sparkCycle) * 0.65;
            const sparkX = tip.x - faceDir * sparkCycle * (pillarSize * 0.35) + Math.sin(t * 8 + s) * 2;
            const sparkY = tip.y - sparkCycle * 6 + Math.cos(t * 6 + s) * 2;
            ctx.fillStyle = '#fef08a';
            ctx.globalAlpha = sparkAlpha;
            ctx.fillRect(sparkX - 0.6, sparkY - 0.6, 1.2, 1.2);
          }
          ctx.globalAlpha = 1.0;
        }
      }
      ctx.restore();

      // =========================================================================
      // LAYER E: BLINDING MISSILE DISCHARGE
      // =========================================================================
      if (isAttacking && !isLow) {
        ctx.save();
        const blastProg = (t * 8) % 1.0;
        const blastR = pillarSize * (0.3 + blastProg * 0.8);
        ctx.strokeStyle = '#fef08a';
        ctx.globalAlpha = (1 - blastProg) * 0.6;
        ctx.lineWidth = 2.0;
        ctx.beginPath();
        ctx.arc(pillarX, pillarY, blastR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    } else if (unitType === 'HarvestingPillar' || unitType === 'Harvesting_Pillar' || unitType === 'Harvesting Pillar') {
      // 𓉐 BESPOKE SUL HARVESTING PILLAR LIVING ARCHETYPE RIG:
      // - Authentic recreation of 1_harvestingpillar.png:
      //   Part crystalline stone spire, part eldritch apex harvester.
      // - Arachnid stalker gait with chassis-decoupling & lurch-glide pacing
      // - Asynchronous multi-harmonic horn & skeletal finger tentacle physics
      // - Stochastic nervous micro-twitches & independent ocular saccades
      // - Dual-tether peristaltic bio-plasma harvesting channels with life-siphon motes
      const isWestern = (unit.team === 'western');
      const forwardSign = isWestern ? 1 : -1;
      const faceDir = dir;
      const wobble = unit.wobbleOffset || 0;
      const pillarSize = drawSize * 1.012; // Scaled down 12% overall per user request (1.15 * 0.88 = 1.012)

      // 1. Gait State Interpolation & Chassis Decoupling
      if (unit._harvestWalkFactor === undefined) unit._harvestWalkFactor = isWalking ? 1.0 : 0.0;
      const targetWalk = isWalking ? walkPitch : 0.0;
      unit._harvestWalkFactor += (targetWalk - unit._harvestWalkFactor) * 0.14;
      const walkFactor = Math.max(0, Math.min(1, unit._harvestWalkFactor));

      // 2. Incommensurable Multi-Harmonic Wave Oscillators (Anti-Repetition Period > 74s)
      const f1 = t * 0.63 + wobble;          // Arm main sway
      const f2 = t * 0.89 + wobble * 1.3;    // Arm secondary wave
      const f3 = t * 0.41 + wobble * 0.7;    // Visceral brain respiration
      const f4 = t * 1.37 + wobble;          // Base finger undulation
      const f5 = t * 3.11 + wobble * 1.5;    // Nervous claw tremor

      // Levitation / Flying Hover Dynamics (sinusoidal float, no footstep bobbing)
      const hoverWave = (Math.sin(t * 2.6 + wobble) * 3.8 + Math.sin(t * 1.2) * 1.5) * 0.88;
      const totalBob = hoverWave;

      // Respiration & visceral pumping
      const breathPulse = Math.sin(f3);
      const stretchY = 1.0 + breathPulse * 0.025;
      const squashX = 1.0 - breathPulse * 0.018;

      let recoilX = 0;
      let recoilTilt = 0;
      if (unit.hitFlashTimer > 0) {
        const recoilProg = unit.hitFlashTimer / 0.12;
        const kick = Math.sin(recoilProg * Math.PI) * 4.5;
        recoilX = -faceDir * kick;
        recoilTilt = -faceDir * (kick * 0.04);
      }

      // Predatory forward lean during flight
      const forwardPitch = faceDir * (walkFactor * 0.06);
      const totalTilt = forwardPitch + recoilTilt;

      const pillarX = renderX + recoilX;
      const pillarY = renderY + totalBob - 4;

      // Target Tracking & Lock Evaluation
      const isLocked1 = !!unit.dualTetherTarget1Id;
      const isLocked2 = !!unit.dualTetherTarget2Id;
      let tetherTarget1 = null;
      if (isLocked1 && battlefield) {
        tetherTarget1 = battlefield.getUnitOrBaseById?.(unit.dualTetherTarget1Id) || null;
      }

      // Check whether a target candidate is alive and within valid physical combat reach
      const isCandidateInRange = (tgt) => {
        if (!tgt || tgt.isDead || tgt.isGoldenStatue) return false;
        if (battlefield?.isInRange) {
          return battlefield.isInRange(unit, tgt);
        }
        const d = Math.hypot(tgt.x - pillarX, tgt.y - pillarY);
        return d <= (unit.range || 120) * 1.05;
      };

      // Active target must be alive and strictly within reachable combat range (never distant bases or far-off enemies)
      let activeTarget = null;
      if (tetherTarget1 && !tetherTarget1.isDead && isCandidateInRange(tetherTarget1)) {
        activeTarget = tetherTarget1;
      } else if (isCandidateInRange(unit.target)) {
        activeTarget = unit.target;
      }

      // Target Death Recoil: If previous target was killed or lost, immediately reset attack factor to 0
      // so the arms snap back instantly to flight posture instead of reaching toward bases or far targets
      const currentGrabTargetId = (activeTarget && !activeTarget.isDead) ? activeTarget.id : null;
      if (unit._lastHarvestTargetId && !currentGrabTargetId && !isAttacking) {
        unit._harvestAttackFactor = 0.0;
      }
      unit._lastHarvestTargetId = currentGrabTargetId;

      // Attack / Engagement State Interpolation for Arm Pose Whipping
      const isEngaged = !!activeTarget && (isAttacking || isLocked1 || isLocked2 || isCandidateInRange(activeTarget));
      if (unit._harvestAttackFactor === undefined) unit._harvestAttackFactor = isEngaged ? 1.0 : 0.0;
      const targetAttack = isEngaged ? 1.0 : 0.0;
      unit._harvestAttackFactor += (targetAttack - unit._harvestAttackFactor) * 0.14;
      const attackFactor = Math.max(0, Math.min(1, unit._harvestAttackFactor));
      const flightFactor = 1.0 - attackFactor;

      let hasTargetAngle = false;
      let targetAngle = 0;
      let targetX = 0;
      let targetY = 0;
      let hasTarget = false;
      if (activeTarget && !activeTarget.isDead) {
        const enemyCenterY = activeTarget.isAir
          ? (activeTarget.y - 18)
          : (activeTarget.y - Math.max(8, (activeTarget.radius || 18) * 0.45));
        const dx = activeTarget.x - pillarX;
        const dy = enemyCenterY - (pillarY - pillarSize * 0.15);
        targetAngle = Math.atan2(dy, dx);
        hasTargetAngle = true;
        targetX = activeTarget.x;
        targetY = enemyCenterY;
        hasTarget = true;
      } else {
        targetX = pillarX + faceDir * (pillarSize * 1.8);
        targetY = pillarY - 6;
      }

      // Hands grab positions:
      // Coordinated two-handed grip framing: Wrists stop at the approach perimeter of the enemy,
      // so the articulated fingers clasp and wrap AROUND the victim's body instead of poking through it.
      // Hand 1 (Upper Hand) grips from upper-front; Hand 2 (Lower Hand) grips from lower-front.
      let grabX1 = targetX;
      let grabY1 = targetY - 6;
      let grabX2 = targetX;
      let grabY2 = targetY + 6;
      if (hasTarget) {
        const targetRad = Math.max(14, activeTarget.radius || 16);
        const reachDx = targetX - pillarX;
        const reachDir = Math.sign(reachDx) || faceDir;
        const wristOffsetX = reachDir * Math.max(8, targetRad * 0.70);
        const verticalSpan = Math.min(16, Math.max(8, targetRad * 0.55));
        grabX1 = targetX - wristOffsetX;
        grabY1 = targetY - verticalSpan;
        grabX2 = targetX - wristOffsetX;
        grabY2 = targetY + verticalSpan;
      }

      // Stochastic Nervous Micro-Twitches (Periodic rapid muscle shivers)
      const twitchCycle = ((t * 0.45 + wobble * 1.9) % 1.0);
      const isTwitching = twitchCycle < 0.12;
      const twitchAmp = isTwitching ? Math.sin(twitchCycle * 50.0) * 4.2 : 0;

      // Dynamic Sprite Selection
      let spriteKey = 'HarvestingPillar_Idle';
      if (isLocked2) {
        spriteKey = 'HarvestingPillar_Harvest2';
      } else if (isLocked1 || isAttacking) {
        spriteKey = 'HarvestingPillar_Harvest';
      } else if (walkFactor > 0.25) {
        spriteKey = 'HarvestingPillar_Stalk';
      }

      // Proportional Dimensions: Head slightly larger & projecting forward, Arms 10% shorter, Fingers 40% Shorter
      const headSize = pillarSize * 0.74;
      const armLength = pillarSize * 0.97;
      const isLow = (this.quality === 'low') || isSimpleTier;
      const segCount = isLow ? 5 : 8;

      // Forward-projecting head offset (head protrudes forward in flight direction)
      const headForwardOffset = faceDir * (headSize * 0.16);
      const headX = pillarX + headForwardOffset;
      const headY = pillarY;

      // =========================================================================
      // 3. LIVING PROCEDURAL RAINBOW SCALED ARMS (Flight Waves vs Two-Handed Grab)
      // =========================================================================
      const cosT = Math.cos(totalTilt);
      const sinT = Math.sin(totalTilt);

      // Arm Roots on Forward-Jutting Green Brain Head
      const localRoot1X = faceDir * HARVESTING_ARM1_ROOT_X * headSize * squashX;
      const localRoot1Y = HARVESTING_ARM1_ROOT_Y * headSize * stretchY;
      const root1X = headX + cosT * localRoot1X - sinT * localRoot1Y;
      const root1Y = headY + sinT * localRoot1X + cosT * localRoot1Y;

      const localRoot2X = faceDir * HARVESTING_ARM2_ROOT_X * headSize * squashX;
      const localRoot2Y = HARVESTING_ARM2_ROOT_Y * headSize * stretchY;
      const root2X = headX + cosT * localRoot2X - sinT * localRoot2Y;
      const root2Y = headY + sinT * localRoot2X + cosT * localRoot2Y;

      // Physical Reach Clamping:
      // Permits full extension to reach legitimate targets anywhere within combat range (~175px),
      // while preventing absurd elongation across the screen towards distant bases (e.g. 500-1400px).
      const maxCombatReach = Math.max(195, (unit.range || 120) + (unit.radius || 20) + ((activeTarget?.radius || 24) + 20));
      const dX1 = grabX1 - root1X;
      const dY1 = grabY1 - root1Y;
      const dist1 = Math.hypot(dX1, dY1);
      if (dist1 > maxCombatReach && dist1 > 0) {
        grabX1 = root1X + (dX1 / dist1) * maxCombatReach;
        grabY1 = root1Y + (dY1 / dist1) * maxCombatReach;
      }
      const dX2 = grabX2 - root2X;
      const dY2 = grabY2 - root2Y;
      const dist2 = Math.hypot(dX2, dY2);
      if (dist2 > maxCombatReach && dist2 > 0) {
        grabX2 = root2X + (dX2 / dist2) * maxCombatReach;
        grabY2 = root2Y + (dY2 / dist2) * maxCombatReach;
      }

      _harvestingArm1PtsScratch[0].x = root1X;
      _harvestingArm1PtsScratch[0].y = root1Y;
      _harvestingArm2PtsScratch[0].x = root2X;
      _harvestingArm2PtsScratch[0].y = root2Y;

      // Calculate 10 segments along Arm 1 (Background Arm)
      for (let i = 1; i <= 10; i++) {
        const u = i / 10;

        // Flight: U-Shape (Oberarm loops backward, Unterarm reaches forward above head in flight direction)
        const uCurveX1 = (-1.6 * u + 2.0 * u * u) * armLength;
        const flightWaveX1 = faceDir * Math.cos(t * 2.5 - u * 2.0 + wobble) * (u * 3.5);
        const flightX = root1X + faceDir * uCurveX1 + flightWaveX1;

        // Arching over the cranial dome: rising backward then curving forward above the head
        const uCurveY1 = -(headSize * 0.72 * u) - Math.sin(u * Math.PI) * (headSize * 0.32);
        const elbowFlex1 = Math.sin(t * 2.8 + wobble) * (Math.sin(u * Math.PI) * 4.5);
        const forearmWave1 = Math.sin(t * 3.4 - u * 2.4 + wobble) * (u * 4.0);
        const flightY = root1Y + uCurveY1 + elbowFlex1 + forearmWave1;

        // Attack: Arm reaches directly to the victim to GRAB with Hand 1 (upper body clamp)
        // Arches upward/overhead away from Arm 2 so the two arms NEVER cross
        const dist1 = Math.hypot(grabX1 - root1X, grabY1 - root1Y);
        const archY1 = -Math.sin(u * Math.PI) * Math.min(26, dist1 * 0.18);
        const writheY1 = Math.sin(t * 6.5 - u * 3.5 + wobble) * (Math.sin(u * Math.PI) * (isLocked2 ? 4.5 : 3.0));
        const surgeX1 = Math.cos(t * 5.0 - u * 2.8 + wobble) * (Math.sin(u * Math.PI) * 3.0);
        const attackTwitch1 = isTwitching ? (twitchAmp * u * 0.8) : 0;
        const crushTremor1 = hasTarget ? Math.sin(t * 24.0 + u * 4.0) * (u * (isLocked2 ? 1.8 : 1.0)) : 0;

        const attackReachX = root1X + (grabX1 - root1X) * u + surgeX1;
        const attackArchY = root1Y + (grabY1 - root1Y) * u + archY1 + writheY1 + attackTwitch1 + crushTremor1;

        _harvestingArm1PtsScratch[i].x = flightX * flightFactor + attackReachX * attackFactor;
        _harvestingArm1PtsScratch[i].y = flightY * flightFactor + attackArchY * attackFactor;
      }

      // Calculate 10 segments along Arm 2 (Foreground Arm)
      for (let i = 1; i <= 10; i++) {
        const u = i / 10;

        // Flight: U-Shape (Oberarm loops backward, Unterarm reaches forward above head in flight direction)
        const uCurveX2 = (-1.5 * u + 1.9 * u * u) * armLength;
        const flightWaveX2 = faceDir * Math.cos(t * 2.3 - u * 1.8 + wobble + 1.2) * (u * 3.5);
        const flight2X = root2X + faceDir * uCurveX2 + flightWaveX2;

        const uCurveY2 = -(headSize * 0.66 * u) - Math.sin(u * Math.PI * 0.95) * (headSize * 0.28);
        const elbowFlex2 = Math.sin(t * 2.6 + wobble + 1.5) * (Math.sin(u * Math.PI) * 4.2);
        const forearmWave2 = Math.sin(t * 3.1 - u * 2.2 + wobble + 1.8) * (u * 4.5);
        const flight2Y = root2Y + uCurveY2 + elbowFlex2 + forearmWave2;

        // Attack: Arm reaches directly to the victim to GRAB with Hand 2 (lower body clamp)
        // Completely smooth, organic downward-cradling arch with NO piecewise step jumps or kinks!
        const dist2 = Math.hypot(grabX2 - root2X, grabY2 - root2Y);
        const archY2 = Math.sin(u * Math.PI) * Math.min(18, dist2 * 0.14);
        const writheY2 = Math.sin(t * 6.0 - u * 3.2 + wobble + 1.8) * (Math.sin(u * Math.PI) * (isLocked2 ? 4.5 : 3.0));
        const surgeX2 = Math.cos(t * 4.8 - u * 2.5 + wobble + 1.2) * (Math.sin(u * Math.PI) * 3.0);
        const attackTwitch2 = isTwitching ? (-twitchAmp * u * 0.8) : 0;
        const crushTremor2 = hasTarget ? Math.sin(t * 24.0 + u * 4.0 + 1.4) * (u * (isLocked2 ? 1.8 : 1.0)) : 0;

        const attack2ReachX = root2X + (grabX2 - root2X) * u + surgeX2;
        const attack2ArchY = root2Y + (grabY2 - root2Y) * u + archY2 + writheY2 + attackTwitch2 + crushTremor2;

        _harvestingArm2PtsScratch[i].x = flight2X * flightFactor + attack2ReachX * attackFactor;
        _harvestingArm2PtsScratch[i].y = flight2Y * flightFactor + attack2ArchY * attackFactor;
      }

      // Wrists at tips of arms
      const wrist1 = _harvestingArm1PtsScratch[10];
      const wrist2 = _harvestingArm2PtsScratch[10];

      // Tangent angles of arm tips for natural finger alignment
      const arm1Dx = wrist1.x - _harvestingArm1PtsScratch[9].x;
      const arm1Dy = wrist1.y - _harvestingArm1PtsScratch[9].y;
      const tipAngle1 = Math.atan2(arm1Dy, arm1Dx);

      const arm2Dx = wrist2.x - _harvestingArm2PtsScratch[9].x;
      const arm2Dy = wrist2.y - _harvestingArm2PtsScratch[9].y;
      const tipAngle2 = Math.atan2(arm2Dy, arm2Dx);

      // =========================================================================
      // 4. 7 ARTICULATED SPIDER FINGERS PER HAND (3 Phalanges with Knuckle Joints)
      // =========================================================================
      // Coordinated Grasping Dynamics (Attack Greifbewegung):
      // - Synchronized two-handed attack cadence (both hands clamp together, no lagging phase offset!)
      // - When locked (tether active): constant vice-grip with intense pulsing crushing pressure
      // - When lunging: fingers splay wide open, then snap shut into a curved hooked cage
      const isLocked = isLocked1 || isLocked2;
      let clench1 = 0;
      let open1 = 0;
      let clench2 = 0;
      let open2 = 0;

      if (isLocked) {
        const siphonPulse = Math.sin(t * (isLocked2 ? 8.0 : 5.5) + wobble);
        const lockedClench = 0.85 + 0.15 * Math.max(0, siphonPulse);
        clench1 = lockedClench;
        clench2 = lockedClench;
      } else {
        const graspSpeed = isEngaged ? 4.8 : 3.6;
        const graspWave = Math.sin(t * graspSpeed + wobble);
        clench1 = Math.max(0, graspWave);
        clench2 = clench1;
        open1 = Math.max(0, -graspWave);
        open2 = open1;
      }

      // Normal vector across wrist 1 for minimal digit root spacing
      const norm1X = -Math.sin(tipAngle1);
      const norm1Y =  Math.cos(tipAngle1);

      // Arm 1 (Background Hand): 7 Articulated Digits (3 Phalanges each)
      for (let s = 0; s < 7; s++) {
        const spreadAngle = HARVESTING_FINGER_SPREAD_ANGLES[s];
        const fLen = headSize * HARVESTING_FINGER_LEN[s] * 0.88 * stretchY;
        const len1 = fLen * 0.38; // Phalanx 1 (Proximal base)
        const len2 = fLen * 0.34; // Phalanx 2 (Intermediate / Mid bone)
        const len3 = fLen * 0.28; // Phalanx 3 (Distal / Talon claw tip)

        // Minimal Palm Root: reduced to near-zero spacing so digits sprout directly from arm tip
        const palmLateral1 = (s - 3) * 0.32;
        const palmForward1 = (1.0 - Math.pow((s - 3) / 3, 2)) * 0.35;
        const root1FingX = wrist1.x + norm1X * palmLateral1 + Math.cos(tipAngle1) * palmForward1;
        const root1FingY = wrist1.y + norm1Y * palmLateral1 + Math.sin(tipAngle1) * palmForward1;

        _harvestingFingerPtsScratch[s][0].x = root1FingX;
        _harvestingFingerPtsScratch[s][0].y = root1FingY;

        // ---------------------------------------------------------------------
        // HORROR-SCENE INDEPENDENT DIGIT ARTICULATION (Walk / Stalking Motion)
        // Each finger has its own independent life, creeping, snapping, hyperextending,
        // tapping the air, and shivering like spindly arachnid talons in a horror scene.
        // ---------------------------------------------------------------------
        const sFreq1 = (1.8 + HARVESTING_FINGER_FREQ[s] * 2.2);
        const sPhase1 = HARVESTING_FINGER_PHASE[s];
        const digitT1 = t * sFreq1 + sPhase1 + wobble;

        // Non-linear jerky snap (sudden quick knuckle flex/twitch followed by eerie hesitation)
        const rawWave1 = Math.sin(digitT1);
        const horrorSnap1 = Math.sign(rawWave1) * Math.pow(Math.abs(rawWave1), 0.65);
        const eerieHesitation1 = Math.sin(digitT1 * 0.48);

        // Independent erratic micro-spasms on individual digits
        const digitTwitch1 = Math.sin(t * 26.0 + s * 8.3) * 0.09 * (Math.sin(digitT1 * 0.65) > 0.35 ? 1 : 0);

        // Independent splay/spread: digits spread and contract out of sync
        const flightSpread1 = spreadAngle * (0.34 + 0.22 * Math.sin(digitT1 * 0.85 + 0.6));
        const attackSpread1 = spreadAngle * (0.32 * (1.0 - clench1 * 0.45) + open1 * 0.55);
        const baseAngle1 = tipAngle1 + (flightSpread1 * flightFactor + attackSpread1 * attackFactor);

        // --- Posture A: Horror Walk / Stalk (Independent Spindly Digits) ---
        // Joint 1 (Knuckle): alternates between eerie hyperextension (reaching back) and sudden downward claw tap
        const knuckleHorror1 = faceDir * (horrorSnap1 * 0.48 + eerieHesitation1 * 0.22 + (s % 2 === 0 ? 0.24 : -0.18));
        const phi1_flight = baseAngle1 - faceDir * (0.10 + horrorSnap1 * 0.12);
        const phi2_flight = phi1_flight + knuckleHorror1;

        // Joint 2 (Intermediate): curls into tight hook or extends with nervous snap
        const midHorror1 = faceDir * (Math.sin(digitT1 * 1.35 + 1.4) * 0.52 + 0.36 + digitTwitch1);
        const phi3_flight = phi2_flight + midHorror1;

        // --- Posture B: Attack & Grab (Vicious downward clamp into victim) ---
        // Upper hand wraps DOWNWARD around victim's upper torso/head
        const cupping1 = faceDir * ((3 - s) * 0.06) * clench1;
        const openRelief1 = -faceDir * (open1 * 0.35);
        const gripTremor1 = isLocked ? Math.sin(t * 26.0 + s * 1.5) * 0.04 : 0;
        const attackTwitch1 = (isTwitching && (s === 1 || s === 5)) ? (twitchAmp * 0.04) : 0;

        const phi1_attack = baseAngle1 + openRelief1 + faceDir * (clench1 * 0.20) + gripTremor1 + attackTwitch1;
        const knuckleGrip1 = faceDir * (clench1 * 0.70) + cupping1;
        const phi2_attack = phi1_attack + knuckleGrip1;
        const talonGrip1 = faceDir * (clench1 * 0.85);
        const phi3_attack = phi2_attack + talonGrip1;

        // Blend Flight vs Attack states
        const phi1 = phi1_flight * flightFactor + phi1_attack * attackFactor;
        const phi2 = phi2_flight * flightFactor + phi2_attack * attackFactor;
        const phi3 = phi3_flight * flightFactor + phi3_attack * attackFactor;

        // Articulated Phalanx Chain (3 Segments / 2 Discrete Joints)
        let curX = root1FingX;
        let curY = root1FingY;

        curX += Math.cos(phi1) * len1;
        curY += Math.sin(phi1) * len1;
        _harvestingFingerPtsScratch[s][1].x = curX;
        _harvestingFingerPtsScratch[s][1].y = curY;

        curX += Math.cos(phi2) * len2;
        curY += Math.sin(phi2) * len2;
        _harvestingFingerPtsScratch[s][2].x = curX;
        _harvestingFingerPtsScratch[s][2].y = curY;

        curX += Math.cos(phi3) * len3;
        curY += Math.sin(phi3) * len3;
        _harvestingFingerPtsScratch[s][3].x = curX;
        _harvestingFingerPtsScratch[s][3].y = curY;
      }

      // Normal vector across wrist 2 for minimal digit root spacing
      const norm2X = -Math.sin(tipAngle2);
      const norm2Y =  Math.cos(tipAngle2);

      // Arm 2 (Foreground Hand): 7 Articulated Digits (3 Phalanges each)
      for (let s = 0; s < 7; s++) {
        const spreadAngle = HARVESTING_FINGER_SPREAD_ANGLES[s];
        const fLen = headSize * HARVESTING_FINGER_LEN[s] * 0.98 * stretchY;
        const len1 = fLen * 0.38;
        const len2 = fLen * 0.34;
        const len3 = fLen * 0.28;

        // Minimal Palm Root for foreground hand
        const palmLateral2 = (s - 3) * 0.35;
        const palmForward2 = (1.0 - Math.pow((s - 3) / 3, 2)) * 0.35;
        const root2FingX = wrist2.x + norm2X * palmLateral2 + Math.cos(tipAngle2) * palmForward2;
        const root2FingY = wrist2.y + norm2Y * palmLateral2 + Math.sin(tipAngle2) * palmForward2;

        _harvestingFinger2PtsScratch[s][0].x = root2FingX;
        _harvestingFinger2PtsScratch[s][0].y = root2FingY;

        // ---------------------------------------------------------------------
        // HORROR-SCENE INDEPENDENT DIGIT ARTICULATION for Foreground Hand
        // Distinct frequencies and phase permutation so all 14 digits are desynchronized
        // ---------------------------------------------------------------------
        const sFreq2 = (1.9 + HARVESTING_FINGER_FREQ[(s + 3) % 7] * 2.1);
        const sPhase2 = HARVESTING_FINGER_PHASE[(s + 4) % 7] + 1.7;
        const digitT2 = t * sFreq2 + sPhase2 + wobble;

        const rawWave2 = Math.sin(digitT2);
        const horrorSnap2 = Math.sign(rawWave2) * Math.pow(Math.abs(rawWave2), 0.65);
        const eerieHesitation2 = Math.sin(digitT2 * 0.48);
        const digitTwitch2 = Math.sin(t * 27.0 + s * 8.7 + 1.5) * 0.09 * (Math.sin(digitT2 * 0.65) > 0.35 ? 1 : 0);

        const flightSpread2 = spreadAngle * (0.36 + 0.22 * Math.sin(digitT2 * 0.85 + 1.1));
        const attackSpread2 = spreadAngle * (0.34 * (1.0 - clench2 * 0.45) + open2 * 0.55);
        const baseAngle2 = tipAngle2 + (flightSpread2 * flightFactor + attackSpread2 * attackFactor);

        // --- Posture A: Horror Walk / Stalk (Independent Spindly Digits) ---
        const knuckleHorror2 = faceDir * (horrorSnap2 * 0.46 + eerieHesitation2 * 0.20 + (s % 2 === 1 ? 0.24 : -0.18));
        const phi1_flight2 = baseAngle2 - faceDir * (0.08 + horrorSnap2 * 0.12);
        const phi2_flight2 = phi1_flight2 + knuckleHorror2;

        const midHorror2 = faceDir * (Math.sin(digitT2 * 1.35 + 1.6) * 0.50 + 0.34 + digitTwitch2);
        const phi3_flight2 = phi2_flight2 + midHorror2;

        // --- Posture B: Attack & Grab (Vicious upward clamp opposing Hand 1) ---
        // Lower hand wraps UPWARD around victim's underside/belly
        const cupping2 = faceDir * ((3 - s) * 0.065) * clench2;
        const openRelief2 = faceDir * (open2 * 0.35);
        const gripTremor2 = isLocked ? Math.sin(t * 26.0 + s * 1.5 + 1.2) * 0.04 : 0;
        const attackTwitch2 = isTwitching ? (-twitchAmp * 0.04) : 0;

        const phi1_attack2 = baseAngle2 + openRelief2 - faceDir * (clench2 * 0.20) + gripTremor2 + attackTwitch2;
        const knuckleGrip2 = -faceDir * (clench2 * 0.72) - cupping2;
        const phi2_attack2 = phi1_attack2 + knuckleGrip2;
        const talonGrip2 = -faceDir * (clench2 * 0.88);
        const phi3_attack2 = phi2_attack2 + talonGrip2;

        // Blend Flight vs Attack states
        const phi1 = phi1_flight2 * flightFactor + phi1_attack2 * attackFactor;
        const phi2 = phi2_flight2 * flightFactor + phi2_attack2 * attackFactor;
        const phi3 = phi3_flight2 * flightFactor + phi3_attack2 * attackFactor;

        // Articulated Phalanx Chain for Hand 2
        let curX = root2FingX;
        let curY = root2FingY;

        curX += Math.cos(phi1) * len1;
        curY += Math.sin(phi1) * len1;
        _harvestingFinger2PtsScratch[s][1].x = curX;
        _harvestingFinger2PtsScratch[s][1].y = curY;

        curX += Math.cos(phi2) * len2;
        curY += Math.sin(phi2) * len2;
        _harvestingFinger2PtsScratch[s][2].x = curX;
        _harvestingFinger2PtsScratch[s][2].y = curY;

        curX += Math.cos(phi3) * len3;
        curY += Math.sin(phi3) * len3;
        _harvestingFinger2PtsScratch[s][3].x = curX;
        _harvestingFinger2PtsScratch[s][3].y = curY;
      }

      // =========================================================================
      // =========================================================================
      // LAYER A: BACKGROUND SCALED ARM (Arm 1) & 7 SPIDER FINGERS
      // Drawn behind the head so both arms are clearly visible in depth
      // =========================================================================
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // 0. Dark Obsidian Chitin Armature (Heavy ink shadow from artwork)
      for (let k = 0; k < 10; k++) {
        const p0 = _harvestingArm1PtsScratch[k];
        const p1 = _harvestingArm1PtsScratch[k + 1];
        ctx.strokeStyle = '#09090b';
        ctx.lineWidth = Math.max(2.8, (10 - k) * 0.72);
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.stroke();
      }

      // 1. Iridescent Eldritch Scaled Arm 1 Plates
      for (let k = 0; k < 10; k++) {
        const p0 = _harvestingArm1PtsScratch[k];
        const p1 = _harvestingArm1PtsScratch[k + 1];
        ctx.strokeStyle = HARVESTING_ARM_SCALE_COLORS[k];
        ctx.lineWidth = Math.max(2.1, (10 - k) * 0.57);
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.stroke();

        // Overlapping scale highlight rim (metallic jewel sheen)
        ctx.strokeStyle = HARVESTING_ARM_SCALE_HIGHLIGHTS[k];
        ctx.lineWidth = Math.max(0.9, (10 - k) * 0.25);
        ctx.stroke();
      }

      // Forearm bio-tendrils trailing backward (matching artwork PNG)
      const fArm1 = _harvestingArm1PtsScratch[8];
      const fArmW1 = _harvestingArm1PtsScratch[9];
      const tWave1 = Math.sin(t * 3.5 + wobble) * 3.5;
      ctx.strokeStyle = '#18181b';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(fArm1.x, fArm1.y);
      ctx.lineTo(fArm1.x - faceDir * 5, fArm1.y + 4);
      ctx.lineTo(fArm1.x - faceDir * 9, fArm1.y + 7 + tWave1);
      ctx.moveTo(fArmW1.x, fArmW1.y);
      ctx.lineTo(fArmW1.x - faceDir * 6, fArmW1.y - 3);
      ctx.lineTo(fArmW1.x - faceDir * 11, fArmW1.y - 5 - tWave1);
      ctx.stroke();

      // 2. Minimal Wrist Joint (Reduced to a tiny chitin hinge knot so hand is almost purely fingers)
      ctx.fillStyle = '#18181b';
      ctx.beginPath();
      ctx.arc(wrist1.x, wrist1.y, 1.2, 0, Math.PI * 2);
      ctx.fill();

      // 3. 7 Articulated Spider Fingers (Background Hand: 3 Phalanges per finger)
      const phalanxColors1 = isLocked2
        ? HARVESTING_PHALANX_COLORS_1_LOCKED
        : HARVESTING_PHALANX_COLORS_1_IDLE;
      const phalanxWidths1 = HARVESTING_PHALANX_WIDTHS_1;

      if (this.quality === 'low' || isSimpleTier) {
        ctx.strokeStyle = phalanxColors1[1];
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        for (let s = 0; s < 7; s += 2) {
          ctx.moveTo(_harvestingFingerPtsScratch[s][0].x, _harvestingFingerPtsScratch[s][0].y);
          ctx.lineTo(_harvestingFingerPtsScratch[s][3].x, _harvestingFingerPtsScratch[s][3].y);
        }
        ctx.stroke();
      } else {
        for (let k = 1; k <= 3; k++) {
          const prevIdx = k - 1;
          ctx.strokeStyle = phalanxColors1[k - 1];
          ctx.lineWidth = phalanxWidths1[k - 1];
          ctx.beginPath();
          for (let s = 0; s < 7; s++) {
            ctx.moveTo(_harvestingFingerPtsScratch[s][prevIdx].x, _harvestingFingerPtsScratch[s][prevIdx].y);
            ctx.lineTo(_harvestingFingerPtsScratch[s][k].x, _harvestingFingerPtsScratch[s][k].y);
          }
          ctx.stroke();
        }
      }

      // 4. Knuckle Joint Nodules (Articulated Condyles)
      if (this.quality !== 'low') {
        ctx.fillStyle = isLocked2 ? '#9333ea' : '#27272a';
        ctx.beginPath();
        for (let s = 0; s < 7; s++) {
          const j1 = _harvestingFingerPtsScratch[s][1];
          ctx.moveTo(j1.x + 1.2, j1.y);
          ctx.arc(j1.x, j1.y, 1.2, 0, Math.PI * 2);
        }
        ctx.fill();

        ctx.fillStyle = isLocked2 ? '#c084fc' : '#52525b';
        ctx.beginPath();
        for (let s = 0; s < 7; s++) {
          const j2 = _harvestingFingerPtsScratch[s][2];
          ctx.moveTo(j2.x + 0.9, j2.y);
          ctx.arc(j2.x, j2.y, 0.9, 0, Math.PI * 2);
        }
        ctx.fill();
      }
      ctx.restore();

      // =========================================================================
      // LAYER B: BODY SPRITE (Protruding Green Brain Head & Tendrils)
      // =========================================================================
      const pillarBitmap = buildSpriteBitmap(spriteKey, renderRace, unit.team);
      const activeImg = isHitFlashing ? pillarBitmap.flash : pillarBitmap.normal;

      ctx.save();
      ctx.translate(headX, headY);
      ctx.rotate(faceDir * totalTilt);
      ctx.scale(squashX, stretchY);
      ctx.globalAlpha = spawnAlpha;
      ctx.drawImage(activeImg, -headSize / 2, -headSize / 2, headSize, headSize);
      ctx.restore();

      // =========================================================================
      // LAYER C: BIOLUMINESCENT FLEISCHKERN GLOW (Convoluted Brain Mass)
      // =========================================================================
      if (!isHitFlashing && this.quality !== 'low') {
        const coreGlowTex = getSulHarvestingCoreGlowCanvas();
        const corePulse = 0.65 + 0.35 * Math.sin(t * (isLocked2 ? 5.5 : 2.2) + wobble);
        const coreRadius = headSize * (isLocked2 ? 0.38 : 0.30);
        ctx.save();
        ctx.globalAlpha = (isLocked2 ? 0.90 : (isLocked1 ? 0.75 : 0.50)) * corePulse * spawnAlpha;
        ctx.drawImage(coreGlowTex, headX + faceDir * (headSize * 0.02) - coreRadius, headY - headSize * 0.04 - coreRadius, coreRadius * 2, coreRadius * 2);
        ctx.restore();
      }

      // =========================================================================
      // LAYER D: DYNAMIC COMPOUND OCULAR MATRIX (High-Contrast Red Glowing Eyes)
      // =========================================================================
      if (!isHitFlashing) {
        const inCombat = isLocked1 || !!unit.target || unit.attackCooldown > 0;
        const eyeFlicker = 0.85 + 0.15 * Math.sin(t * 11.0 + wobble);
        const eyeRadius = headSize * (inCombat ? 0.22 : 0.18);

        // Ocular saccade offset
        let eyeLookX = 0;
        let eyeLookY = 0;
        if (hasTargetAngle) {
          eyeLookX = Math.cos(targetAngle) * (headSize * 0.05);
          eyeLookY = Math.sin(targetAngle) * (headSize * 0.05);
        } else {
          const saccadeStep = Math.floor((t * 0.4 + wobble) % 4);
          eyeLookX = (saccadeStep === 1 ? 1.5 : (saccadeStep === 3 ? -1.5 : 0));
        }

        const eyeBaseX = headX + faceDir * (headSize * 0.20) + eyeLookX;
        const eyeBaseY = headY - (headSize * 0.03) + eyeLookY;

        ctx.save();
        // 1. Soft atmospheric bloom aura
        if (this.quality !== 'low') {
          const eyeGlowTex = getSulHarvestingEyeGlowCanvas();
          ctx.globalAlpha = (inCombat ? 1.0 : 0.85) * eyeFlicker * spawnAlpha;
          ctx.drawImage(eyeGlowTex, eyeBaseX - eyeRadius, eyeBaseY - eyeRadius, eyeRadius * 2, eyeRadius * 2);
          const fR = eyeRadius * 0.65;
          ctx.drawImage(eyeGlowTex, eyeBaseX - faceDir * (headSize * 0.07) - fR, eyeBaseY - (headSize * 0.05) - fR, fR * 2, fR * 2);
        }

        // 2. High-contrast vivid red glowing eye points (compound ocular cluster)
        // Multi-eye array on frontal lobe: Primary front eye, Upper brow eye, Lower jaw eye, Temple eye
        for (let eIdx = 0; eIdx < HARVESTING_EYE_SPECS.length; eIdx++) {
          const ep = HARVESTING_EYE_SPECS[eIdx];
          const ex = headX + faceDir * (headSize * ep.dxFrac) + eyeLookX * 0.6;
          const ey = headY + (headSize * ep.dyFrac) + eyeLookY * 0.6;

          // Outer intense red halo
          ctx.globalAlpha = (inCombat ? 1.0 : 0.85) * eyeFlicker * spawnAlpha;
          ctx.fillStyle = '#ef4444';
          ctx.beginPath();
          ctx.arc(ex, ey, ep.r * (inCombat ? 1.25 : 1.0), 0, Math.PI * 2);
          ctx.fill();

          // Crimson iris ring
          ctx.fillStyle = '#dc2626';
          ctx.beginPath();
          ctx.arc(ex, ey, ep.r * 0.70, 0, Math.PI * 2);
          ctx.fill();

          // Blazing white/pale-yellow core glint
          ctx.globalAlpha = (inCombat ? 1.0 : 0.95) * spawnAlpha;
          ctx.fillStyle = isLocked2 ? '#fef08a' : '#ffffff';
          ctx.beginPath();
          ctx.arc(ex + eyeLookX * 0.2, ey + eyeLookY * 0.2, ep.coreR * (inCombat ? 1.2 : 1.0), 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      }

      // =========================================================================
      // LAYER E: FOREGROUND SCALED ARM (Arm 2) & 7 SPIDER FINGERS
      // Drawn in front of the head with thick, menacing eldritch scales & grabbing claws
      // =========================================================================
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // 0. Dark Obsidian Chitin Armature (Heavy ink shadow from artwork)
      for (let k = 0; k < 10; k++) {
        const p0 = _harvestingArm2PtsScratch[k];
        const p1 = _harvestingArm2PtsScratch[k + 1];
        ctx.strokeStyle = '#09090b';
        ctx.lineWidth = Math.max(3.4, (10 - k) * 0.92);
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.stroke();
      }

      // 1. Iridescent Eldritch Scaled Arm 2 Body (10 segments with sinister jewel plates)
      for (let k = 0; k < 10; k++) {
        const p0 = _harvestingArm2PtsScratch[k];
        const p1 = _harvestingArm2PtsScratch[k + 1];
        ctx.strokeStyle = HARVESTING_ARM_SCALE_COLORS[k];
        ctx.lineWidth = Math.max(2.6, (10 - k) * 0.75);
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.stroke();

        // Overlapping scale highlight rim
        ctx.strokeStyle = HARVESTING_ARM_SCALE_HIGHLIGHTS[k];
        ctx.lineWidth = Math.max(1.05, (10 - k) * 0.31);
        ctx.stroke();
      }

      // Forearm bio-tendrils trailing backward (matching artwork PNG)
      const fArm2 = _harvestingArm2PtsScratch[8];
      const fArmW2 = _harvestingArm2PtsScratch[9];
      const tWave2 = Math.sin(t * 3.8 + wobble + 1.2) * 3.8;
      ctx.strokeStyle = '#18181b';
      ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.moveTo(fArm2.x, fArm2.y);
      ctx.lineTo(fArm2.x - faceDir * 6, fArm2.y + 5);
      ctx.lineTo(fArm2.x - faceDir * 10, fArm2.y + 8 + tWave2);
      ctx.moveTo(fArmW2.x, fArmW2.y);
      ctx.lineTo(fArmW2.x - faceDir * 7, fArmW2.y - 4);
      ctx.lineTo(fArmW2.x - faceDir * 12, fArmW2.y - 6 - tWave2);
      ctx.stroke();

      // 2. Minimal Wrist Joint (Foreground Hand)
      ctx.fillStyle = '#18181b';
      ctx.beginPath();
      ctx.arc(wrist2.x, wrist2.y, 1.3, 0, Math.PI * 2);
      ctx.fill();

      // 3. 7 Articulated Spider Fingers (Foreground Hand: 3 Phalanges per finger)
      const phalanxColors2 = isLocked2
        ? HARVESTING_PHALANX_COLORS_2_LOCKED
        : HARVESTING_PHALANX_COLORS_2_IDLE;
      const phalanxWidths2 = HARVESTING_PHALANX_WIDTHS_2;

      if (this.quality === 'low' || isSimpleTier) {
        ctx.strokeStyle = phalanxColors2[1];
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        for (let s = 0; s < 7; s += 2) {
          ctx.moveTo(_harvestingFinger2PtsScratch[s][0].x, _harvestingFinger2PtsScratch[s][0].y);
          ctx.lineTo(_harvestingFinger2PtsScratch[s][3].x, _harvestingFinger2PtsScratch[s][3].y);
        }
        ctx.stroke();
      } else {
        for (let k = 1; k <= 3; k++) {
          const prevIdx = k - 1;
          ctx.strokeStyle = phalanxColors2[k - 1];
          ctx.lineWidth = phalanxWidths2[k - 1];
          ctx.beginPath();
          for (let s = 0; s < 7; s++) {
            ctx.moveTo(_harvestingFinger2PtsScratch[s][prevIdx].x, _harvestingFinger2PtsScratch[s][prevIdx].y);
            ctx.lineTo(_harvestingFinger2PtsScratch[s][k].x, _harvestingFinger2PtsScratch[s][k].y);
          }
          ctx.stroke();
        }
      }

      // 4. Knuckle Joint Nodules (Foreground Hand)
      if (this.quality !== 'low') {
        ctx.fillStyle = isLocked2 ? '#c084fc' : '#3f3f46';
        ctx.beginPath();
        for (let s = 0; s < 7; s++) {
          const j1 = _harvestingFinger2PtsScratch[s][1];
          ctx.moveTo(j1.x + 1.4, j1.y);
          ctx.arc(j1.x, j1.y, 1.4, 0, Math.PI * 2);
        }
        ctx.fill();

        ctx.fillStyle = isLocked2 ? '#f0abfc' : '#71717a';
        ctx.beginPath();
        for (let s = 0; s < 7; s++) {
          const j2 = _harvestingFinger2PtsScratch[s][2];
          ctx.moveTo(j2.x + 1.1, j2.y);
          ctx.arc(j2.x, j2.y, 1.1, 0, Math.PI * 2);
        }
        ctx.fill();
      }
      ctx.restore();

      // =========================================================================
      // LAYER F: TWO-HANDED PHYSICAL GRASP DAMAGE, CLAW REND SPARKS & LIFE SIPHON
      // No energy beam in the air — the damage is caused directly by the two hands
      // clutching, tearing, and crushing the victim unit!
      // =========================================================================
      if ((isLocked1 || (isEngaged && hasTarget)) && activeTarget && !activeTarget.isDead) {
        ctx.save();

        const vX = activeTarget.x;
        const vY = activeTarget.isAir ? (activeTarget.y - 18) : (activeTarget.y - Math.max(8, (activeTarget.radius || 18) * 0.45));
        const isClenchTick = clench1 > 0.25 || clench2 > 0.25;

        // 1. Visceral Claw Rend & Crushing Impact Sparks at Both Hand Grip Points
        const sparkPhase = (t * 22) % 1.0;
        if (isClenchTick) {
          // Rend sparks at Hand 1 (Upper back grip)
          const sp1Count = isLocked2 ? 4 : 2;
          for (let sp = 0; sp < sp1Count; sp++) {
            const ang = (sp / sp1Count) * Math.PI * 2 + t * 16;
            const dist = 3 + sparkPhase * (isLocked2 ? 11 : 7);
            ctx.fillStyle = isLocked2 ? '#86efac' : '#f87171';
            ctx.beginPath();
            ctx.arc(wrist1.x + Math.cos(ang) * dist, wrist1.y + Math.sin(ang) * dist, isLocked2 ? 1.8 : 1.3, 0, Math.PI * 2);
            ctx.fill();
          }

          // Rend sparks at Hand 2 (Front chest grip)
          const sp2Count = isLocked2 ? 4 : 2;
          for (let sp = 0; sp < sp2Count; sp++) {
            const ang = (sp / sp2Count) * Math.PI * 2 - t * 15;
            const dist = 3 + sparkPhase * (isLocked2 ? 11 : 7);
            ctx.fillStyle = isLocked2 ? '#fef08a' : '#ef4444';
            ctx.beginPath();
            ctx.arc(wrist2.x + Math.cos(ang) * dist, wrist2.y + Math.sin(ang) * dist, isLocked2 ? 1.8 : 1.3, 0, Math.PI * 2);
            ctx.fill();
          }

          // Claw slash impact lines directly on the victim's body
          ctx.strokeStyle = isLocked2 ? 'rgba(134, 239, 172, 0.85)' : 'rgba(239, 68, 68, 0.85)';
          ctx.lineWidth = isLocked2 ? 1.6 : 1.2;
          ctx.beginPath();
          ctx.moveTo(vX - 6, vY - 4);
          ctx.lineTo(vX + 6, vY + 4);
          ctx.moveTo(vX + 4, vY - 6);
          ctx.lineTo(vX - 4, vY + 6);
          ctx.stroke();
        }

        // 2. Physical Constriction Pressure Field (The victim is trapped in the two-handed vise)
        const crushPulse = 0.75 + 0.25 * Math.sin(t * 12.0 + wobble);
        ctx.strokeStyle = isLocked2 ? 'rgba(74, 222, 128, 0.45)' : 'rgba(220, 38, 38, 0.40)';
        ctx.lineWidth = isLocked2 ? 2.0 : 1.4;
        ctx.beginPath();
        // Constriction ring clamped between wrist 1 and wrist 2
        ctx.ellipse((wrist1.x + wrist2.x) * 0.5, (wrist1.y + wrist2.y) * 0.5, 12 * crushPulse, 8 * crushPulse, 0, 0, Math.PI * 2);
        ctx.stroke();

        // 3. Life Siphon Pulses Traveling UP THE PHYSICAL ARMS (No beam in the air!)
        // Stolen life motes travel along Arm 1 and Arm 2 from the victim back into the pillar
        const moteCount = isLocked2 ? 3 : 2;
        for (let m = 0; m < moteCount; m++) {
          const mProg = (1.0 - ((t * 1.6 + m / moteCount) % 1.0)); // Reverse (1.0 wrist -> 0.0 root)
          const segIdx = Math.min(9, Math.floor(mProg * 10));
          const subU = (mProg * 10) - segIdx;

          // Siphon mote traveling along Arm 1
          const p1A = _harvestingArm1PtsScratch[segIdx];
          const p1B = _harvestingArm1PtsScratch[segIdx + 1];
          const m1X = p1A.x + (p1B.x - p1A.x) * subU;
          const m1Y = p1A.y + (p1B.y - p1A.y) * subU;
          ctx.fillStyle = isLocked2 ? '#86efac' : '#c084fc';
          ctx.beginPath();
          ctx.arc(m1X, m1Y, isLocked2 ? 2.0 : 1.5, 0, Math.PI * 2);
          ctx.fill();

          // Siphon mote traveling along Arm 2
          const p2A = _harvestingArm2PtsScratch[segIdx];
          const p2B = _harvestingArm2PtsScratch[segIdx + 1];
          const m2X = p2A.x + (p2B.x - p2A.x) * subU;
          const m2Y = p2A.y + (p2B.y - p2A.y) * subU;
          ctx.fillStyle = isLocked2 ? '#ffffff' : '#f87171';
          ctx.beginPath();
          ctx.arc(m2X, m2Y, isLocked2 ? 2.2 : 1.6, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      }
    } else if (unitType === 'ProcessingPillar' || unitType === 'Processing_Pillar' || unitType === 'Processing Pillar') {
      // 𓉐 SUL PROCESSING PILLAR 36x36 HD NATIVE DUAL-LAYER RIG
      const faceDir = dir;
      const wobble = unit.wobbleOffset || 0;
      const pillarSize = drawSize * 1.008; // 4% kleiner (1.05 * 0.96 = 1.008)

      // Levitation floating dynamics
      const hoverWave = Math.sin(t * 1.6 + wobble) * 3.4 + Math.sin(t * 0.8) * 1.2;
      const pillarX = renderX;
      const pillarY = renderY + hoverWave;

      const isCharging = (unit.plasmaChargeTimer > 0 && unit.plasmaChargeTimer < 3.0);
      const isAttack = (unit.plasmaChargeTimer >= 3.0 && unit.plasmaChargeTimer < 3.45);
      const isAir = !!unit.plasmaTargetIsAir;
      const chargeProg = isCharging
        ? (unit.plasmaChargeTimer / 3.0)
        : (isAttack ? Math.min(1.0, (unit.plasmaChargeTimer - 3.0) / 0.45) : 0);

      // Organic Combat Pose Interpolation (Smooth transition between idle and strike)
      const isCombatActive = (unit.plasmaChargeTimer > 0 && unit.plasmaChargeTimer < 3.60);
      if (unit._plasmaPoseBlend === undefined) {
        unit._plasmaPoseBlend = isCombatActive ? 1.0 : 0.0;
      }
      const targetBlend = isCombatActive ? 1.0 : 0.0;
      const blendSpeed = targetBlend > unit._plasmaPoseBlend ? 0.14 : 0.07;
      unit._plasmaPoseBlend += (targetBlend - unit._plasmaPoseBlend) * blendSpeed;
      if (Math.abs(unit._plasmaPoseBlend - targetBlend) < 0.005) {
        unit._plasmaPoseBlend = targetBlend;
      }
      const poseBlend = Math.max(0, Math.min(1, unit._plasmaPoseBlend));

      // Call Unified 36x36 HD Dual-Layer Rig
      drawProcessingPillarRig(ctx, {
        x: pillarX,
        y: pillarY,
        size: pillarSize,
        dir: faceDir,
        team: unit.team,
        isHitFlashing: isHitFlashing,
        isCharging: isCharging,
        isAttack: isAttack,
        isAir: isAir,
        chargeProgress: chargeProg,
        blendFactor: poseBlend,
        time: t,
        wobble: wobble,
        quality: this.quality
      });

      // Convergence Plasma Sphere ("Große Kugel") during 3.0s ground charge
      if (isCharging && !isAir) {
        const pixelScale = pillarSize / 36.0;
        const chargeFocusX = pillarX + faceDir * (26 * pixelScale);
        const chargeFocusY = pillarY - (6 * pixelScale);
        const baseRadius = 13.5;
        const currentRad = Math.max(3.0, baseRadius * (0.28 + 0.72 * chargeProg));
        const pulse = 1.0 + Math.sin(t * 16.0 + wobble) * 0.08;
        const sphereR = currentRad * pulse;

        ctx.save();
        if (this.quality !== 'low') {
          ctx.fillStyle = '#c084fc';
          ctx.globalAlpha = 0.28 * (0.4 + 0.6 * chargeProg);
          ctx.beginPath();
          ctx.arc(chargeFocusX, chargeFocusY, sphereR * 1.63, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.fillStyle = '#38bdf8';
        ctx.globalAlpha = 0.85 * (0.5 + 0.5 * chargeProg);
        ctx.beginPath();
        ctx.arc(chargeFocusX, chargeFocusY, sphereR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#bae6fd';
        ctx.globalAlpha = 0.90 * (0.5 + 0.5 * chargeProg);
        ctx.beginPath();
        ctx.arc(chargeFocusX, chargeFocusY, sphereR * 0.58, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = 0.98;
        ctx.beginPath();
        ctx.arc(chargeFocusX, chargeFocusY, sphereR * 0.38, 0, Math.PI * 2);
        ctx.fill();

        if (chargeProg > 0.35) {
          ctx.fillStyle = '#ffffff';
          ctx.globalAlpha = 0.85 * ((chargeProg - 0.35) / 0.65);
          ctx.beginPath();
          ctx.arc(chargeFocusX - sphereR * 0.28, chargeFocusY - sphereR * 0.28, sphereR * 0.18, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.strokeStyle = '#e0f2fe';
        ctx.lineWidth = 1.2;
        ctx.globalAlpha = 0.70 * chargeProg;
        ctx.beginPath();
        ctx.arc(chargeFocusX, chargeFocusY, sphereR, 0, Math.PI * 2);
        ctx.stroke();

        ctx.restore();
      } else if (unit.plasmaChargeTimer >= 3.0 && unit.plasmaChargeTimer < 3.20) {
        const pixelScale = pillarSize / 36.0;
        const chargeFocusX = pillarX + faceDir * (26 * pixelScale);
        const chargeFocusY = pillarY - (6 * pixelScale);
        const dischargeProg = (unit.plasmaChargeTimer - 3.0) / 0.20;
        ctx.save();
        ctx.strokeStyle = '#67e8f9';
        ctx.lineWidth = 2.0;
        ctx.globalAlpha = (1.0 - dischargeProg) * 0.85;
        ctx.beginPath();
        ctx.arc(chargeFocusX, chargeFocusY, 13.5 + dischargeProg * 14, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // Ground feelers responding to agitation (< 70px enemy proximity)
      const groundLevel = renderY + drawSize * 0.52;
      const agitation = unit.whipAgitation || 0;
      if (agitation > 0.05) {
        ctx.save();
        ctx.strokeStyle = isHitFlashing ? '#ffffff' : (agitation > 0.3 ? '#5c3926' : '#3d2518');
        ctx.lineWidth = isHitFlashing ? 1.6 : 1.1;

        for (let f = 0; f < 4; f++) {
          const root = PROCESSING_FEELER_ROOTS[f];
          const rX = pillarX + faceDir * (pillarSize * root.dx);
          const rY = pillarY + (pillarSize * root.dy);
          const feelerHeight = Math.max(10, groundLevel - rY);

          ctx.beginPath();
          ctx.moveTo(rX, rY);

          let curX = rX;
          let curY = rY;
          for (let s = 1; s <= 6; s++) {
            const segProg = s / 6;
            const baseY = rY + feelerHeight * segProg;
            const whipWave = Math.sin(t * 22.0 + f * 1.8 + s * 1.2) * (agitation * 12.0) * segProg;
            curX = rX + faceDir * whipWave;
            curY = baseY;
            ctx.lineTo(curX, curY);
          }
          ctx.stroke();

          if (agitation > 0.4 || isHitFlashing) {
            ctx.fillStyle = isHitFlashing ? '#ffffff' : '#67e8f9';
            ctx.globalAlpha = isHitFlashing ? 1.0 : (agitation * 0.7);
            ctx.fillRect(curX - 1, curY - 1, 2, 2);
            ctx.globalAlpha = 1.0;
          }
        }
        ctx.restore();
      }
    } else if (unitType === 'PreserverLock' || unitType === 'Preserver_Lock' || unitType === 'Preserver Lock') {
      if (unit.isCinematicProp) {
        // Decorative campaign wreckage (docs/singleplayer implementation
        // soft-foraging-flamingo.md, js/data/campaignMissions.js's
        // groundProps) - body layer only, no legs, rotated onto its side so
        // it reads as fallen/discarded hardware lying on the ground rather
        // than a standing unit.
        const bodyBitmap = buildSpriteBitmap('PreserverLock_Body', renderRace, unit.team);
        const bodySrc = isHitFlashing ? bodyBitmap.flash : bodyBitmap.normal;
        ctx.save();
        ctx.translate(renderX, renderY);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(bodySrc, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
        ctx.restore();
        // Returning early here skips the shared HP-bar/status tail below
        // (desired - a decorative prop has neither) but must still balance
        // the two ctx.save() calls still open at this point: the facing-
        // flip save (line ~1070, normally undone at the "matches the
        // facing-flip save() above" restore further down) and this
        // function's own outer save() at its very top.
        ctx.restore();
        ctx.restore();
        return;
      }
      if (this.quality === 'low' || isSimpleTier) {
        // Low Quality Tier: Single pre-baked composite blit (1 draw call, zero rig kinematics)
        let preserverKey = 'PreserverLock';
        if (isWalking) {
          const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4);
          preserverKey = PRESERVER_LOCK_WALK_COMPOSITES[phaseIdx] || 'PreserverLock_Walk1';
        }
        const preserverBmp = buildSpriteBitmap(preserverKey, renderRace, unit.team);
        const preserverSrc = isHitFlashing ? preserverBmp?.flash : preserverBmp?.normal;
        if (preserverSrc) {
          drawLowTierWalkSprite(ctx, preserverSrc, renderX, renderY, drawSize, drawSize, isWalking, walkStride, walkPhase, bounceY, LOW_WALK_STEP);
        }
      } else {
        // Six-legged biomechanical tank: legs and body are separate baked
        // layers (js/engine/render/unitSprites.js) so the undercarriage can
        // cycle through an authentic 6-legged hexapod tripod gait while the torso rides on top,
        // bobbing with whichever tripod is currently planted and rolling a
        // few degrees side to side - a natural predatory hexapod lean/sway
        // rather than the old flat sideways slide.
        let legsKey = 'PreserverLock_Legs_Stand';
        let bodyKey = 'PreserverLock_Body';
        let bodyBob = 0;
        let bodyRoll = 0;

        if (isWalking) {
          const cycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * 4);
          legsKey = PRESERVER_LOCK_WALK_FRAMES[phaseIdx] || 'PreserverLock_Legs_Walk1';
          bodyKey = PRESERVER_LOCK_BODY_FRAMES[phaseIdx] || 'PreserverLock_Body_Walk1';
          bodyBob = (phaseIdx === 0 || phaseIdx === 2) ? 0.8 : 0.2;
          bodyRoll = phaseIdx === 0 ? 0.05 : (phaseIdx === 2 ? -0.05 : 0.0);
        }

        const legsBitmap = buildSpriteBitmap(legsKey, renderRace, unit.team);
        const bodyBitmap = buildSpriteBitmap(bodyKey, renderRace, unit.team);
        const legsSrc = isHitFlashing ? legsBitmap.flash : legsBitmap.normal;
        const bodySrc = isHitFlashing ? bodyBitmap.flash : bodyBitmap.normal;

        // 1. LEGS (Unterteil): tripod stagger-gait undercarriage
        ctx.drawImage(
          legsSrc,
          renderX - drawSize / 2,
          renderY - drawSize / 2 + bodyBob,
          drawSize,
          drawSize
        );

        // 2. BODY (Oberteil): armored torso, horn & orb - rocks a few degrees
        // around a pivot near the hips as weight shifts between tripods.
        ctx.save();
        const bodyPivotX = renderX;
        const bodyPivotY = renderY + drawSize * 0.18;
        ctx.translate(bodyPivotX, bodyPivotY);
        ctx.rotate(bodyRoll);
        ctx.translate(-bodyPivotX, -bodyPivotY);

        ctx.drawImage(
          bodySrc,
          renderX - drawSize / 2,
          renderY - drawSize / 2 + bodyBob,
          drawSize,
          drawSize
        );

        // Head projector corona / lens flare (Vorschlag E):
        // Radiant glow around the twin lenses at the mast peak matching 1_preserverlock.png.
        // Quality-gated per docs/INVARIANTS.md rule 4 (within 8 lines of gradient creation).
        const headX = renderX + drawSize * 0.02;
        const headY = renderY - drawSize * 0.438 + bodyBob;
        const beamTargetCandidate = (unit.beamTargetId && battlefield) ? battlefield.getUnitOrBaseById?.(unit.beamTargetId) : null;
        const isFiring = !!(beamTargetCandidate && !beamTargetCandidate.isDead);
        if (this.quality !== 'low') {
          const flarePulse = isFiring
            ? (0.95 + 0.15 * Math.sin(t * 20 + unit.wobbleOffset))
            : (0.7 + 0.15 * Math.sin(t * 4 + unit.wobbleOffset));
          const flareR = drawSize * (isFiring ? 0.22 : 0.13) * flarePulse;
          const customBullet = (typeof unit.getCustomBulletColorHex === 'function')
            ? unit.getCustomBulletColorHex()
            : (unit.customBulletColor || null);
          const coronaCanvas = getPreserverLockCoronaCanvas(isFiring, customBullet);
          if (coronaCanvas) {
            ctx.drawImage(coronaCanvas, headX - flareR, headY - flareR, flareR * 2, flareR * 2);
          } else {
            ctx.fillStyle = isFiring
              ? (customBullet ? _hexToPreserverRgba(customBullet, 0.6) : 'rgba(125, 211, 252, 0.6)')
              : (customBullet ? _hexToPreserverRgba(customBullet, 0.25) : 'rgba(56, 189, 248, 0.25)');
            ctx.beginPath();
            ctx.arc(headX, headY, flareR, 0, Math.PI * 2);
            ctx.fill();
          }

          if (isFiring && (this.quality === 'high' || this.quality === 'ultra')) {
            // Cross-flare rays when actively firing
            ctx.strokeStyle = customBullet || 'rgba(224, 242, 254, 0.7)';
            ctx.lineWidth = 1.2;
            const rayLen = flareR * 1.5;
            ctx.beginPath();
            ctx.moveTo(headX - rayLen, headY);
            ctx.lineTo(headX + rayLen, headY);
            ctx.moveTo(headX, headY - rayLen);
            ctx.lineTo(headX, headY + rayLen);
            ctx.stroke();
          }
        }
        ctx.restore();
      }
    } else if (unitType === 'BastionLock' || unitType === 'Bastion_Lock' || unitType === 'Bastion Lock' || unit.isBastionLock) {
      // 🛡️ SUL BASTION LOCK:
      // Tier M (36x36) Articulated Bio-Mechanical Shield Barricade
      // - Mobile state: 4-phase tripod/quad crawler gait, back & front shields tucked as armor mantles
      // - Stationary state: Deploys 2 massive organic shield halves forward over 1.0s, locking at ground spires
      // - Centered firing embrasure (22, 18) spits molten metal bullets (10 shots/s) with micro-recoil & muzzle heat
      // - Quality gated: breathing dorsal vent cyan glow, ground spall dust when slamming into ground
      const deployProg = unit.deployProgress !== undefined ? unit.deployProgress : 0.0;
      const isDeployed = deployProg >= 1.0;

      if (this.quality === 'low' || isSimpleTier) {
        let spriteKey = isDeployed ? 'BastionLock_Deployed' : 'BastionLock';
        if (isWalking && deployProg <= 0.0) {
          const walkCycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((walkCycle / (Math.PI * 2)) * 4);
          spriteKey = BASTION_LOCK_WALK_COMPOSITES[phaseIdx] || 'BastionLock_Walk1';
        }
        const bmp = buildSpriteBitmap(spriteKey, renderRace, unit.team);
        const src = isHitFlashing ? bmp?.flash : bmp?.normal;
        if (src) {
          drawLowTierWalkSprite(ctx, src, renderX, renderY, drawSize, drawSize, isWalking && deployProg <= 0.0, walkStride, walkPhase, bounceY, LOW_WALK_STEP);
        }

        // ⚡ Firing Beam for Low & Simple Tier: direct zero-allocation stroke from embrasure to target
        const barrelHeat = unit.barrelHeat || 0;
        const hasRecentBeam = isDeployed && (unit._lastBeamTime !== undefined && unit._lastBeamTime > 0) && ((unit.age - unit._lastBeamTime) >= 0) && ((unit.age - unit._lastBeamTime) < 0.12);
        const isFiringBeam = isDeployed && (barrelHeat > 0 || (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.06)) || hasRecentBeam);
        let bTarget = null;
        if (isFiringBeam) {
          if (unit._lastBeamTarget) {
            bTarget = unit._lastBeamTarget;
          } else if (unit.target) {
            _bastionScratchTarget.x = unit.target.x;
            _bastionScratchTarget.y = unit.target.y;
            bTarget = _bastionScratchTarget;
          }
        }

        if (isFiringBeam && bTarget) {
          const bStartX = renderX + dir * (drawSize * (4.0 / 36));
          const bStartY = renderY - (drawSize * (1.5 / 36));
          const bEndX = bTarget.x;
          const bEndY = bTarget.y;

          ctx.save();
          ctx.lineCap = 'round';
          // 1. Cyan beam body
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 2.4;
          ctx.beginPath();
          ctx.moveTo(bStartX, bStartY);
          ctx.lineTo(bEndX, bEndY);
          ctx.stroke();

          // 2. White core
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.0;
          ctx.beginPath();
          ctx.moveTo(bStartX, bStartY);
          ctx.lineTo(bEndX, bEndY);
          ctx.stroke();

          // 3. Embrasure muzzle flash
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(bStartX - 1.5, bStartY - 1.5, 3, 3);
          ctx.restore();
        }
      } else {
        // High & Ultra Kinematic Rig (3 Decoupled Layers: Shield Back, Body Pod, Shield Front)
        ctx.save();
        ctx.translate(renderX, renderY);
        ctx.scale(dir, 1);
        ctx.translate(-renderX, -renderY);

        let bodyBob = 0;
        let bodyRoll = 0;
        let curBackX = 0, curBackY = 0, curBackRot = 0;
        let curFrontX = 0, curFrontY = 0, curFrontRot = 0;

        const p = Math.max(0.0, Math.min(1.0, deployProg));
        const crouchY = p * 1.5;

        // "Wums!" ground impact slam bounce as shields anchor into dirt
        const slamBounce = (p > 0.85 && p < 1.0) ? (Math.sin((p - 0.85) / 0.15 * Math.PI) * 2.8) : 0;

        let legsKey = 'BastionLock_Legs_Stand';
        if (isWalking && p <= 0.0) {
          // Mobile stance: shields held close at flanks, compact crutch-walking stride
          const walkCycle = ((walkPhase % (Math.PI * 2)) + (Math.PI * 2)) % (Math.PI * 2);
          const phaseIdx = Math.floor((walkCycle / (Math.PI * 2)) * 4);
          legsKey = BASTION_LOCK_WALK_FRAMES[phaseIdx] || 'BastionLock_Legs_Walk1';

          const backStepX = Math.sin(walkCycle) * (drawSize * (5.2 / 36));
          const backStepY = -Math.max(0.0, Math.cos(walkCycle)) * (drawSize * (4.2 / 36));
          const backStepRot = Math.sin(walkCycle) * 0.13;

          const frontStepX = -Math.sin(walkCycle) * (drawSize * (5.2 / 36));
          const frontStepY = -Math.max(0.0, -Math.cos(walkCycle)) * (drawSize * (4.2 / 36));
          const frontStepRot = -Math.sin(walkCycle) * 0.13;

          // Auf- und Ab-Frequenz des Kopfes halbiert (ein satter, schwerer Dip pro Schritt statt schnellem Doppel-Wippen)
          bodyBob = Math.abs(Math.sin(walkCycle)) * (drawSize * (1.4 / 36));
          bodyRoll = Math.sin(walkCycle) * 0.065;

          curBackX = renderX + (drawSize * (0.7 / 36)) + backStepX;
          curBackY = renderY + (-drawSize * (1.0 / 36)) + backStepY;
          curBackRot = -0.07 + backStepRot;

          curFrontX = renderX + (drawSize * (2.0 / 36)) + frontStepX;
          curFrontY = renderY + (drawSize * (1.0 / 36)) + frontStepY;
          curFrontRot = 0.05 + frontStepRot;
        } else {
          legsKey = 'BastionLock_Legs_Stand';
          // Deploy stance: arms swing the shields forward and across IN FRONT of the head!
          // Back shield sweeps forward into locking position (+8.5)
          const backDeployX = (0.7 + p * 7.8) * (drawSize / 36);
          const backDeployY = (-1.0 + p * 2.0) * (drawSize / 36);
          curBackRot = (1.0 - p) * -0.07 + p * 0.02;

          // Front shield sweeps inward into locking position (+1.0)
          const frontDeployX = (2.0 - p * 1.0) * (drawSize / 36);
          const frontDeployY = (1.0 - p * 0.0) * (drawSize / 36);
          curFrontRot = (1.0 - p) * 0.05 - p * 0.02;

          curBackX = renderX + backDeployX;
          curBackY = renderY + crouchY + backDeployY + slamBounce;

          curFrontX = renderX + frontDeployX;
          curFrontY = renderY + crouchY + frontDeployY + slamBounce;
        }

        const backBmp = buildSpriteBitmap('BastionLock_Shield_Back', renderRace, unit.team);
        const bodyBmp = buildSpriteBitmap('BastionLock_Body', renderRace, unit.team);
        const frontBmp = buildSpriteBitmap('BastionLock_Shield_Front', renderRace, unit.team);
        const legsBmp = buildSpriteBitmap(legsKey, renderRace, unit.team);

        const backSrc = isHitFlashing ? backBmp?.flash : backBmp?.normal;
        const bodySrc = isHitFlashing ? bodyBmp?.flash : bodyBmp?.normal;
        const frontSrc = isHitFlashing ? frontBmp?.flash : frontBmp?.normal;
        const legsSrc = isHitFlashing ? legsBmp?.flash : legsBmp?.normal;

        // Firing state and recent beam check (scoped for all layers: recoil, optic flare, beam & shield spill)
        const barrelHeat = unit.barrelHeat || 0;
        const hasRecentBeam = isDeployed && (unit._lastBeamTime !== undefined && unit._lastBeamTime > 0) && ((unit.age - unit._lastBeamTime) >= 0) && ((unit.age - unit._lastBeamTime) < 0.12);
        const isFiringBeam = isDeployed && (barrelHeat > 0 || (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.06)) || hasRecentBeam);
        let bTarget = null;
        if (isFiringBeam) {
          if (unit._lastBeamTarget) {
            bTarget = unit._lastBeamTarget;
          } else if (unit.target) {
            _bastionScratchTarget.x = unit.target.x;
            _bastionScratchTarget.y = unit.target.y;
            bTarget = _bastionScratchTarget;
          }
        }
        const isFiringNow = isFiringBeam && !!bTarget;
        const bodyRecoilX = isFiringNow ? (Math.sin(t * 80) * 0.8 * Math.max(0.4, barrelHeat)) : 0;
        
        // Lowered head pod position (nestled deep between towering shields, matching concept art)
        const podLowerOffsetY = drawSize * (6.0 / 36);
        const bodyCenterX = renderX - bodyRecoilX;
        const bodyCenterY = renderY + crouchY + bodyBob + podLowerOffsetY + slamBounce * 0.4;

        // Ground anchor shockwave ring upon "Wums!" impact slam
        if (this.quality !== 'low' && p >= 0.85 && p < 1.0) {
          const ringProg = (p - 0.85) / 0.15;
          const ringR = drawSize * (0.35 + ringProg * 0.45);
          ctx.save();
          ctx.strokeStyle = `rgba(56, 189, 248, ${0.6 * (1.0 - ringProg)})`;
          ctx.lineWidth = 1.8;
          ctx.beginPath();
          ctx.ellipse(renderX + drawSize * 0.15, renderY + crouchY + drawSize * 0.35, ringR, ringR * 0.35, 0, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }

        // 1. BACK SHIELD LAYER (Far side arm & shield)
        if (backSrc) {
          ctx.save();
          ctx.translate(curBackX, curBackY);
          ctx.rotate(curBackRot);
          ctx.drawImage(backSrc, -drawSize / 2, -drawSize / 2, drawSize, drawSize);

          // ✨ Metallischer Glanz auf dem hinteren Schild (feiner Randreflex)
          if (this.quality !== 'low') {
            const bSheenX = -drawSize * (1.5 / 36);
            const bSheenY = -drawSize * (4.5 / 36);
            ctx.strokeStyle = getBastionBackSheenGrad(ctx, drawSize);
            ctx.lineWidth = Math.max(0.8, drawSize * (0.9 / 36));
            ctx.beginPath();
            ctx.moveTo(bSheenX - drawSize * (3.0 / 36), bSheenY - drawSize * (6.0 / 36));
            ctx.lineTo(bSheenX + drawSize * (1.5 / 36), bSheenY + drawSize * (4.5 / 36));
            ctx.stroke();
          }

          ctx.restore();
        }

        // 2. BACK ARM (Connecting body left shoulder to back shield inner socket)
        const bShX = bodyCenterX - drawSize * (2.5 / 36);
        const bShY = bodyCenterY - drawSize * (1.0 / 36);
        const bSockX = curBackX + drawSize * (2.0 / 36);
        const bSockY = curBackY + drawSize * (3.0 / 36);
        drawBastionArm(ctx, bShX, bShY, bSockX, bSockY, false, drawSize);

        // 3. DECOUPLED HIND LEGS (Extended limbs with ground contact at row 34, animated walkcycle)
        if (legsSrc) {
          ctx.save();
          ctx.translate(renderX, renderY + crouchY + bodyBob * 0.35 + slamBounce * 0.2);
          ctx.drawImage(legsSrc, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
          ctx.restore();
        }

        // 4. LOWERED BODY POD LAYER (Spherical carapace, forward-right looking glowing cyan visor)
        if (bodySrc) {
          ctx.save();
          ctx.translate(bodyCenterX, bodyCenterY);
          ctx.rotate(bodyRoll);
          ctx.drawImage(bodySrc, -drawSize / 2, -drawSize / 2, drawSize, drawSize);

          // Echter dynamischer Leuchtpunkt (recessed optic core nestled behind the right arm front shield)
          if (this.quality !== 'low') {
            // Forward-adjusted anchor aligned with the shield embrasure opening and blue beam axis
            const baseEyeX = drawSize * (3.6 / 36);
            const eyeY = -drawSize * (5.6 / 36);

            const flareHeat = isFiringNow ? Math.max(barrelHeat, 0.85) : 0.0;
            const isWalk = (isWalking && p <= 0.0);

            if (flareHeat > 0.04) {
              // 1. Firing state: strongly pulsating, flickering intense plasma glow!
              const jitter = Math.sin(t * 90.0) * 0.35 + Math.cos(t * 150.0 + unit.wobbleOffset) * 0.25;
              const pulse = Math.sin(t * 30.0) * 0.40;
              const flicker = Math.max(0.55, Math.min(1.50, 1.0 + pulse * 0.55 + jitter * 0.45));

              const fireShiftX = drawSize * ((2.8 * Math.min(1.0, flareHeat / 0.85)) / 36);
              const fireEyeX = baseEyeX + fireShiftX;
              const flareR = drawSize * (((1.8 + 3.2 * flareHeat) * flicker) / 36);

              // Outer high-energy bloom
              ctx.fillStyle = `rgba(14, 165, 233, ${Math.min(0.55, (0.22 + flareHeat * 0.25) * flicker)})`;
              ctx.beginPath();
              ctx.arc(fireEyeX, eyeY, flareR * 2.3, 0, Math.PI * 2);
              ctx.fill();

              // Turbulent plasma corona
              ctx.fillStyle = `rgba(14, 165, 233, ${Math.min(0.75, (0.35 + flareHeat * 0.35) * flicker)})`;
              ctx.beginPath();
              ctx.arc(fireEyeX, eyeY, flareR * 1.5, 0, Math.PI * 2);
              ctx.fill();

              // Radiant pulsing cyan aura
              ctx.fillStyle = `rgba(56, 189, 248, ${Math.min(0.98, (0.65 + flareHeat * 0.35) * flicker)})`;
              ctx.beginPath();
              ctx.arc(fireEyeX, eyeY, flareR, 0, Math.PI * 2);
              ctx.fill();

              // Anamorphic horizontal glare streak pointing forward through shield embrasure (flickering reach)
              ctx.fillStyle = `rgba(186, 230, 253, ${Math.min(0.95, flareHeat * flicker)})`;
              ctx.beginPath();
              ctx.ellipse(
                fireEyeX + flareR * 0.35, 
                eyeY, 
                flareR * (2.2 + 0.6 * pulse), 
                Math.max(1.2, flareR * (0.35 + 0.15 * jitter)), 
                0, 0, Math.PI * 2
              );
              ctx.fill();

              // Blinding white-hot core
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(fireEyeX, eyeY, drawSize * (((0.7 + 0.9 * flareHeat) * (0.85 + 0.3 * flicker)) / 36), 0, Math.PI * 2);
              ctx.fill();

              // Flickering electric discharge arcs
              if (flicker > 1.15) {
                const arcAng = (t * 42.0) % (Math.PI * 0.8) - Math.PI * 0.4;
                const arcLen = flareR * (1.2 + 0.6 * (flicker - 1.15));
                ctx.strokeStyle = `rgba(224, 242, 254, ${0.75 * flareHeat})`;
                ctx.lineWidth = Math.max(1.0, drawSize * (0.8 / 36));
                ctx.beginPath();
                ctx.moveTo(fireEyeX, eyeY);
                ctx.lineTo(fireEyeX + Math.cos(arcAng) * arcLen, eyeY + Math.sin(arcAng) * arcLen);
                ctx.stroke();
              }
            } else if (isWalk) {
              // 2. Walk state: 'mehr glow' with organic 'wabern' (irregular bioluminescent shimmer & drift)
              const wTime = t * 7.5 + unit.wobbleOffset;
              const waber = 0.5 + 0.32 * Math.sin(wTime) 
                                + 0.22 * Math.sin(wTime * 1.63 + 1.4) 
                                + 0.16 * Math.cos(wTime * 0.71 - 0.9);
              const waberFactor = Math.max(0.0, Math.min(1.0, waber));

              // Subtle organic position micro-drift inside the socket during crawler locomotion
              const waberShiftX = Math.sin(wTime * 2.3) * drawSize * (0.28 / 36);
              const waberShiftY = Math.cos(wTime * 1.9) * drawSize * (0.22 / 36);
              const curEyeX = baseEyeX + waberShiftX;
              const curEyeY = eyeY + waberShiftY;

              // Expanded glow radius with fluctuating corona
              const walkGlowR = drawSize * ((2.3 + 0.9 * waberFactor) / 36);
              const walkCoronaR = walkGlowR * 1.7;

              // Outer undulating corona (organic elliptical breathing)
              ctx.fillStyle = `rgba(14, 165, 233, ${0.35 + 0.35 * waberFactor})`;
              ctx.beginPath();
              ctx.ellipse(
                curEyeX, curEyeY, 
                walkCoronaR * (1.0 + 0.12 * Math.sin(wTime * 3.1)), 
                walkCoronaR * (1.0 - 0.08 * Math.cos(wTime * 2.7)), 
                0.15 * Math.sin(wTime), 0, Math.PI * 2
              );
              ctx.fill();

              // Radiant cyan aura
              ctx.fillStyle = `rgba(56, 189, 248, ${0.70 + 0.30 * waberFactor})`;
              ctx.beginPath();
              ctx.arc(curEyeX, curEyeY, walkGlowR, 0, Math.PI * 2);
              ctx.fill();

              // White-hot specular core
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(curEyeX, curEyeY, drawSize * ((0.6 + 0.3 * waberFactor) / 36), 0, Math.PI * 2);
              ctx.fill();
            } else {
              // 3. Idle state: compact gentle breathing pulse nestled in dark carapace socket
              const idlePulse = 0.65 + 0.35 * Math.sin(t * 3.5 + unit.wobbleOffset);
              const idleR = drawSize * (1.4 / 36);
              // Soft outer halo
              ctx.fillStyle = `rgba(14, 165, 233, ${0.30 + 0.25 * idlePulse})`;
              ctx.beginPath();
              ctx.arc(baseEyeX, eyeY, idleR * 1.5, 0, Math.PI * 2);
              ctx.fill();
              // Vibrant cyan core
              ctx.fillStyle = `rgba(56, 189, 248, ${0.70 + 0.25 * idlePulse})`;
              ctx.beginPath();
              ctx.arc(baseEyeX, eyeY, idleR, 0, Math.PI * 2);
              ctx.fill();
              // White specular pinpoint
              ctx.fillStyle = `rgba(255, 255, 255, ${0.85 + 0.15 * idlePulse})`;
              ctx.beginPath();
              ctx.arc(baseEyeX, eyeY, drawSize * (0.45 / 36), 0, Math.PI * 2);
              ctx.fill();
            }
          }
          ctx.restore();
        }

        // 3.5 BASTION BEAM & CORE ENERGY RAY (Rendered between Body and Front Shield!)
        // This ensures the beam originates behind the right front shield, emerging through
        // the embrasure opening, while units in front (helicopters, foreground melee) properly
        // occlude BOTH the shield and the beam without any artificial global z-index hacks.
        if (isFiringBeam && bTarget) {
          const bStartX = bodyCenterX + drawSize * (3.6 / 36);
          const bStartY = bodyCenterY - drawSize * (5.6 / 36);
          const bEndX = renderX + dir * (bTarget.x - renderX);
          const bEndY = bTarget.y;

          // Stärker pulsierend flackernde Strahlmodulation
          const bJitter = Math.sin(t * 90.0) * 0.35 + Math.cos(t * 150.0 + unit.wobbleOffset) * 0.25;
          const bPulse = Math.sin(t * 30.0) * 0.40;
          const bFlicker = Math.max(0.55, Math.min(1.50, 1.0 + bPulse * 0.55 + bJitter * 0.45));

          ctx.save();
          ctx.lineCap = 'round';
          const beamAlpha = Math.min(1.0, (0.45 + (barrelHeat || 0.85) * 0.55) * (0.8 + 0.25 * bFlicker));

          // 1. Äußere weiche Cyan-Atmosphären-Aura (pulsierende Ionisation)
          ctx.strokeStyle = `rgba(14, 165, 233, ${0.30 * beamAlpha * bFlicker})`;
          ctx.lineWidth = Math.max(4.0, 7.5 * bFlicker);
          ctx.beginPath();
          ctx.moveTo(bStartX, bStartY);
          ctx.lineTo(bEndX, bEndY);
          ctx.stroke();

          // 2. Hochenergetischer Cyan-Hauptstrahl
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = Math.max(2.0, 3.6 * bFlicker);
          ctx.globalAlpha = beamAlpha;
          ctx.beginPath();
          ctx.moveTo(bStartX, bStartY);
          ctx.lineTo(bEndX, bEndY);
          ctx.stroke();

          // 3. Blendend weißer Kernstrahl (pulsierende Dicke)
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = Math.max(1.0, 1.4 * bFlicker);
          ctx.globalAlpha = Math.min(1.0, beamAlpha * 1.3);
          ctx.beginPath();
          ctx.moveTo(bStartX, bStartY);
          ctx.lineTo(bEndX, bEndY);
          ctx.stroke();

          ctx.restore();
        }

        // 4. FRONT ARM (Connecting body right shoulder to front shield inner socket)
        const fShX = bodyCenterX + drawSize * (2.0 / 36);
        const fShY = bodyCenterY;
        const fSockX = curFrontX - drawSize * (2.0 / 36);
        const fSockY = curFrontY + drawSize * (3.0 / 36);
        drawBastionArm(ctx, fShX, fShY, fSockX, fSockY, true, drawSize);

        // 5. FRONT SHIELD LAYER (Fore arm & shield locking directly in front of head)
        if (frontSrc) {
          ctx.save();
          ctx.translate(curFrontX, curFrontY);
          ctx.rotate(curFrontRot);
          ctx.drawImage(frontSrc, -drawSize / 2, -drawSize / 2, drawSize, drawSize);

          // ✨ Metallischer Glanz auf dem Frontschild (diagonaler Specular-Bevel-Glint)
          if (this.quality !== 'low') {
            const sheenX = drawSize * (9.2 / 36);
            const sheenY = -drawSize * (2.5 / 36);
            ctx.strokeStyle = getBastionFrontSheenGrad(ctx, drawSize);
            ctx.lineWidth = Math.max(1.0, drawSize * (1.1 / 36));
            ctx.beginPath();
            ctx.moveTo(sheenX - drawSize * (3.8 / 36), sheenY - drawSize * (8.2 / 36));
            ctx.lineTo(sheenX + drawSize * (1.8 / 36), sheenY + drawSize * (5.5 / 36));
            ctx.stroke();

            // Spekularer Glanzpunkt an der oberen Hornkante
            ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
            ctx.beginPath();
            ctx.arc(sheenX - drawSize * (3.8 / 36), sheenY - drawSize * (8.2 / 36), Math.max(0.8, drawSize * (0.7 / 36)), 0, Math.PI * 2);
            ctx.fill();
          }

          // ⚡ Firing Glow Spill am Schild-Ausschnitt (pulsierender Lichtreflex durch den Schusskanal)
          if (this.quality !== 'low' && isFiringBeam && bTarget) {
            const bJitter = Math.sin(t * 90.0) * 0.35 + Math.cos(t * 150.0 + unit.wobbleOffset) * 0.25;
            const bFlicker = Math.max(0.55, Math.min(1.50, 1.0 + Math.sin(t * 30.0) * 0.22 + bJitter * 0.25));
            ctx.fillStyle = `rgba(56, 189, 248, ${0.32 * bFlicker})`;
            ctx.beginPath();
            ctx.arc(-drawSize * (1.8 / 36), -drawSize * (5.5 / 36), drawSize * (3.8 / 36), 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = `rgba(224, 242, 254, ${0.45 * bFlicker})`;
            ctx.beginPath();
            ctx.arc(-drawSize * (1.2 / 36), -drawSize * (5.5 / 36), drawSize * (1.6 / 36), 0, Math.PI * 2);
            ctx.fill();
          }

          // 🛡️ Kinetische Funkensprünge bei Beschuss an der vorderen Kante des Frontschilds (rechter Arm)
          if (this.quality !== 'low') {
            const isUnderFire = (unit.underFireTimer > 0) || (unit.hitFlashTimer > 0) || isHitFlashing;
            if (isUnderFire) {
              const hornX = drawSize * (13.5 / 36);
              const hornY = -drawSize * (3.5 / 36);

              // Stochastische Funkensprünge: unregelmäßige Ausbrüche ab und an
              const sparkSeed = t * 6.8 + unit.wobbleOffset;
              const sparkCycle = (sparkSeed % 1.0);
              if (sparkCycle < 0.38) {
                const spProg = sparkCycle / 0.38; // 0.0 -> 1.0
                const decay = 1.0 - spProg;

                // 1. Spekularer Glint am Schildhorn
                ctx.fillStyle = spProg < 0.3 ? '#ffffff' : '#facc15';
                ctx.beginPath();
                ctx.arc(hornX, hornY, Math.max(1.0, drawSize * ((0.9 * decay) / 36)), 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = `rgba(250, 204, 21, ${0.5 * decay})`;
                ctx.beginPath();
                ctx.arc(hornX, hornY, drawSize * ((2.2 * decay) / 36), 0, Math.PI * 2);
                ctx.fill();

                // 2. Kinetischer Funkensprung 1 (springt nach schräg oben-vorn weg)
                const sparkDist1 = spProg * drawSize * (4.2 / 36);
                const sp1X = hornX + sparkDist1 * 0.9;
                const sp1Y = hornY - sparkDist1 * 0.8 + (spProg * spProg) * drawSize * (2.2 / 36);
                ctx.fillStyle = spProg < 0.4 ? '#ffffff' : (spProg < 0.75 ? '#facc15' : '#f97316');
                ctx.fillRect(sp1X - 0.9, sp1Y - 0.9, 1.8, 1.8);

                // 3. Zweiter kleiner Funke (leicht zeitversetzt)
                if (spProg > 0.12) {
                  const p2 = (spProg - 0.12) / 0.88;
                  const sparkDist2 = p2 * drawSize * (3.0 / 36);
                  const sp2X = hornX + sparkDist2 * 0.45;
                  const sp2Y = hornY - sparkDist2 * 1.15 + (p2 * p2) * drawSize * (2.8 / 36);
                  ctx.fillStyle = p2 < 0.35 ? '#ffffff' : '#38bdf8';
                  ctx.fillRect(sp2X - 0.7, sp2Y - 0.7, 1.4, 1.4);
                }

                // 4. Feiner Abprall-Streak
                ctx.strokeStyle = `rgba(254, 240, 138, ${0.85 * decay})`;
                ctx.lineWidth = 1.0;
                ctx.beginPath();
                ctx.moveTo(hornX, hornY);
                ctx.lineTo(hornX + (1.0 - spProg) * drawSize * (1.8 / 36), hornY - (1.0 - spProg) * drawSize * (1.4 / 36));
                ctx.stroke();
              }
            }
          }

          ctx.restore();
        }
        ctx.restore(); // Rig-level horizontal mirror transform
      }
    } else if (unitType === 'ClaimingPillar' || unitType === 'Claiming_Pillar' || unitType === 'Claiming Pillar' || unit.isClaimingPillar) {
      // 𓉐 SUL CLAIMING PILLAR:
      // Slender volcanic bio-spire rooted into the soil at the territory indicator level.
      // - Emerges vertically out from the dead center of the ground plane / carmine glow (groundAnchorY)
      // - Tall, filigree aspect ratio (slender needle shaft & intricate root buttresses)
      // - Volcanic caldera summit with bubbling molten carmine magma & drifting embers
      // - Glowing arterial carmine channels weaving down the obsidian shaft
      // - Radiant bio-regeneration pulse when unit._hpRegenFlash > 0
      const groundAnchorY = renderY + 6; // Exactly shadowBaseY: ground plane contact point
      const pillarWidth = drawSize * 0.88; // Slender, compact horizontal footprint matching artwork
      const pillarHeight = drawSize * 1.42; // Tall imposing chimney silhouette matching artwork
      const growthProg = unit.isGrowing ? Math.min(1.0, Math.max(0.02, (unit.growthTimer || 0) / 3.0)) : 1.0;
      const emergeRise = growthProg * pillarHeight;
      const drawX = renderX - pillarWidth / 2;
      const drawY = groundAnchorY - emergeRise;

      const pillarBitmap = buildSpriteBitmap('ClaimingPillar', renderRace, unit.team) || buildSpriteBitmap(unitType, renderRace, unit.team);
      const sourceBmp = isHitFlashing ? (pillarBitmap?.flash || pillarBitmap?.normal) : pillarBitmap?.normal;

      const hasClip = unit.isGrowing && growthProg < 1.0 && typeof ctx.clip === 'function';
      if (hasClip) {
        ctx.save();
        ctx.beginPath();
        if (typeof ctx.rect === 'function') {
          ctx.rect(renderX - pillarWidth * 1.5, -5000, pillarWidth * 3.0, (groundAnchorY - (-5000)));
        } else {
          ctx.moveTo(renderX - pillarWidth * 1.5, -5000);
          ctx.lineTo(renderX + pillarWidth * 1.5, -5000);
          ctx.lineTo(renderX + pillarWidth * 1.5, groundAnchorY);
          ctx.lineTo(renderX - pillarWidth * 1.5, groundAnchorY);
          ctx.closePath();
        }
        ctx.clip();
      }

      if (sourceBmp) {
        ctx.drawImage(sourceBmp, drawX, drawY, pillarWidth, pillarHeight);
      }

      // HP Regeneration Vitality Shimmer Pulse
      if (unit._hpRegenFlash > 0) {
        const flashAlpha = Math.min(1.0, unit._hpRegenFlash / 0.35);
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.45 * flashAlpha;
        if (pillarBitmap?.flash) {
          ctx.drawImage(pillarBitmap.flash, drawX, drawY, pillarWidth, pillarHeight);
        }
        ctx.restore();
      }

      if (hasClip) {
        ctx.restore(); // Restores clip rect
      }

      // Emergence Ground Debris while rising out from the glowing ground pool
      if (unit.isGrowing && growthProg < 1.0) {
        ctx.save();
        const crumbCount = 6;
        for (let c = 0; c < crumbCount; c++) {
          const cProg = ((t * 4.0 + c * 0.28) % 1.0);
          const cx = renderX + ((c - 2.5) * 5) + Math.sin(c * 2.1) * 2.5;
          const cy = groundAnchorY - cProg * 14;
          const cAlpha = (1.0 - cProg) * 0.85;
          ctx.fillStyle = (c % 2 === 0) ? '#3e485c' : '#b91c1c';
          ctx.globalAlpha = cAlpha;
          ctx.beginPath();
          ctx.arc(cx, cy, 1.3 + (1.0 - cProg) * 1.1, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      // 🌋 Volcanic Chimney Summit: Billowing Ash Smoke Plume & Thermal Embers (media_1791884915486_3847d277.png)
      const calderaX = renderX;
      const calderaY = drawY + (1.5 / 24) * pillarHeight;
      if (calderaY <= groundAnchorY) {
        ctx.save();

        // 1. Billowing Volcanic Ash Smoke (dark charcoal rolling cumulus plume drifting upwards)
        if (this.quality !== 'low' && !isSimpleTier) {
          const smokePuffCount = (this.quality === 'ultra' || this.quality === 'high') ? 6 : 4;
          const smokeMaxRise = pillarHeight * 0.68;
          for (let s = 0; s < smokePuffCount; s++) {
            const sProg = ((t * 0.62 + s * (1.0 / smokePuffCount)) % 1.0);
            // Non-linear upward convection (accelerates then slows as plume billows)
            const riseDist = Math.pow(sProg, 0.88) * smokeMaxRise;
            // Convective turbulence sway
            const sway = Math.sin(t * 2.0 + s * 1.6 + sProg * 2.8) * (3.5 + sProg * 8.0);
            const sx = calderaX + sway;
            const sy = calderaY - riseDist;
            // Puffs expand into dense rolling cumulus clouds as they ascend
            const sRadius = (pillarWidth * 0.09) + sProg * (pillarWidth * 0.24);
            // Alpha: fades in quickly at chimney mouth, thickens mid-rise, dissolves into the air
            const sAlpha = Math.sin(sProg * Math.PI) * (this.quality === 'ultra' ? 0.46 : 0.36);

            // Staggered dual-tone volcanic ash (charcoal / soot)
            if (s % 2 === 0) {
              ctx.fillStyle = '#0f172a';
              ctx.globalAlpha = sAlpha;
            } else {
              ctx.fillStyle = '#2d3748';
              ctx.globalAlpha = sAlpha * 0.85;
            }

            ctx.beginPath();
            ctx.arc(sx, sy, sRadius, 0, Math.PI * 2);
            ctx.fill();

            // Inner dense soot core on ultra
            if (this.quality === 'ultra' && sProg < 0.6) {
              ctx.fillStyle = '#0a0f1a';
              ctx.globalAlpha = sAlpha * 0.65;
              ctx.beginPath();
              ctx.arc(sx - sway * 0.2, sy + 1, sRadius * 0.52, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        } else {
          // Low quality: lightweight single ash wisp
          const sProg = (t * 0.8) % 1.0;
          ctx.fillStyle = '#1e293b';
          ctx.globalAlpha = (1.0 - sProg) * 0.35;
          ctx.beginPath();
          ctx.arc(calderaX + Math.sin(t * 2.5) * 3, calderaY - sProg * 22, 3 + sProg * 5, 0, Math.PI * 2);
          ctx.fill();
        }

        // 2. Soot-Laden Black Chimney Throat Aperture (media_1791884915486_3847d277.png - kein rotes Glühen)
        ctx.fillStyle = '#05070a';
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        ctx.ellipse(calderaX, calderaY, pillarWidth * 0.15, pillarWidth * 0.07, 0, 0, Math.PI * 2);
        ctx.fill();

        // 3. Incandescent Embers & Sparks Drafted Upward through the Smoke Plume
        const emberCount = (this.quality === 'low' || isSimpleTier) ? 2 : 6;
        for (let e = 0; e < emberCount; e++) {
          const eProg = ((t * 1.6 + e * 0.17) % 1.0);
          const eSway = Math.sin(t * 3.2 + e * 2.1) * (pillarWidth * (0.08 + eProg * 0.12));
          const ex = calderaX + eSway;
          const ey = calderaY - eProg * (pillarHeight * 0.55);
          const eAlpha = Math.sin(eProg * Math.PI) * 0.9;
          const eRadius = 0.8 + (1.0 - eProg) * 1.4;
          ctx.fillStyle = CLAIMING_PILLAR_EMBER_COLORS[e % 3];
          ctx.globalAlpha = eAlpha;
          ctx.beginPath();
          ctx.arc(ex, ey, eRadius, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      }
    } else {
      // ------------------------------------------------------------------------
      // MONOLITHIC & LEGACY FALLBACK RENDERING (Single 1-piece Sprite Bitmap)
      // Rendert monolithische Einheiten und statische Legacy-Referenz-Sprites
      // direkt aus dem vorgebackenen Bitmap mit 1 drawImage-Call.
      // ------------------------------------------------------------------------
      const bitmap = buildSpriteBitmap(unitType, renderRace, unit.team);

      // Cloak shimmer: a faint cyan ghost copy drifting a few px off-phase,
      // composited additively. Cheap stand-in for a real per-pixel distortion
      // shader - reads as a heat-shimmer/predator-cloak effect at a glance.
      if (unit.isCloaked) {
        const shimmerX = Math.sin(t * 5 + unit.wobbleOffset) * 3;
        const shimmerY = Math.cos(t * 4 + unit.wobbleOffset) * 2;
        ctx.save();
        ctx.globalAlpha = (0.16 + 0.08 * Math.sin(t * 7)) * spawnAlpha;
        ctx.globalCompositeOperation = 'lighter';
        ctx.drawImage(
          bitmap.normal,
          renderX - drawSize / 2 + shimmerX, renderY - drawSize / 2 + shimmerY, drawSize, drawSize
        );
        ctx.restore();
      }

      // Atmospheric perspective + moonlight rim light + wounded tint.
      const sourceBitmap = isHitFlashing ? bitmap.flash : bitmap.normal;
      const fogAlpha = unit.isStructure ? 0 : Math.max(0, 1 - depthT) * 0.22;
      const rimAlpha = 0.15;
      const hpRatioForTint = unit.maxHp > 0 ? unit.hp / unit.maxHp : 1;
      const woundAlpha = hpRatioForTint < 0.5 ? Math.min(0.35, (0.5 - hpRatioForTint) * 0.7) : 0;
      const tintSize = Math.ceil(drawSize);

      if (this.quality !== 'low' && (fogAlpha > 0.04 || woundAlpha > 0.04) && tintSize <= 192 && tintSize > 0) {
        if (!this._tintCanvas) {
          this._tintCanvas = document.createElement('canvas');
          this._tintCanvas.width = 192;
          this._tintCanvas.height = 192;
          this._tintCtx = this._tintCanvas.getContext('2d');
        }
        const tctx = this._tintCtx;
        tctx.clearRect(0, 0, tintSize, tintSize);
        tctx.imageSmoothingEnabled = false;
        tctx.drawImage(sourceBitmap, 0, 0, tintSize, tintSize);

        if (fogAlpha > 0.02) {
          tctx.globalCompositeOperation = 'source-atop';
          tctx.globalAlpha = fogAlpha;
          tctx.fillStyle = '#14223a';
          tctx.fillRect(0, 0, tintSize, tintSize);
        }

        if (woundAlpha > 0.02) {
          tctx.globalCompositeOperation = 'source-atop';
          tctx.globalAlpha = woundAlpha;
          tctx.fillStyle = '#780a0a';
          tctx.fillRect(0, 0, tintSize, tintSize);
        }
        tctx.globalAlpha = 1.0;

        tctx.globalCompositeOperation = 'source-atop';
        const rimCanvas = getRimLightGradCanvas();
        if (rimCanvas) {
          tctx.drawImage(rimCanvas, 0, 0, 128, 128, 0, 0, tintSize, tintSize);
        }
        tctx.globalCompositeOperation = 'source-over';

        ctx.drawImage(
          this._tintCanvas, 0, 0, tintSize, tintSize,
          renderX - drawSize / 2, renderY - drawSize / 2, drawSize, drawSize
        );
      } else {
        ctx.drawImage(sourceBitmap, renderX - drawSize / 2, renderY - drawSize / 2, drawSize, drawSize);
      }
    }

    ctx.restore(); // matches the facing-flip save() above

    // 3. FOREGROUND WEAPON & STATUS LIGHT FX (Drawn on top)
    // 🧬 Organic Sul Heartbeat Core Glow at Critical HP (< 35% HP):
    // Diastolic double-pulse ("lub-dub" rhythm every 0.85s) that ONLY illuminates
    // the inner bioluminescent chitin core without any sprite deformation or squash/stretch.
    const isSulUnit = (renderRace === 'Sul' || unit.race === 'Sul' || unit.cardData?.race === 'Sul');
    if (this.quality !== 'low' && isSulUnit && unit.hp < (unit.maxHp || 100) * 0.35 && !unit.isDead) {
      const beatOffset = unit.id ? (getUnitNumericId(unit) * 0.17) : 0;
      const beatCycle = (t + beatOffset) % 0.85;
      let beatAlpha = 0;
      if (beatCycle < 0.12) {
        // Beat 1 (Systole): primary sharp surge
        beatAlpha = Math.sin((beatCycle / 0.12) * Math.PI) * 0.45;
      } else if (beatCycle >= 0.18 && beatCycle < 0.30) {
        // Beat 2 (Diastole): secondary echo
        beatAlpha = Math.sin(((beatCycle - 0.18) / 0.12) * Math.PI) * 0.30;
      }
      if (this.quality !== 'low' && beatAlpha > 0.01) {
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = beatAlpha * (unit.isCloaked ? 0.35 : 1.0);
        const glowR = r * 0.45;
        const hbCanvas = getSulHeartbeatGradCanvas();
        if (hbCanvas) {
          ctx.drawImage(hbCanvas, renderX - glowR, (renderY - r * 0.1) - glowR, glowR * 2, glowR * 2);
        } else {
          ctx.fillStyle = '#bef264';
          ctx.beginPath();
          ctx.arc(renderX, renderY - r * 0.1, glowR, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    }

    // ⚡ Forced Overstimulation: Accelerating Adrenalin-Strobe & Crimson Frenzy Aura
    if (unit.isOverstimulated && (unit.overstimulationTimer || 0) > 0) {
      ctx.save();
      const remTime = unit.overstimulationTimer;
      let strobeAlpha = 0;
      let strobeColor = '#ef4444';
      if (remTime > 5.0) {
        // 10s..5s: Gentle breathing bio-glow
        strobeAlpha = 0.18 + 0.10 * Math.sin(t * 8.0 + unit.wobbleOffset);
      } else if (remTime > 2.0) {
        // 5s..2s: Nervous, accelerating flicker
        strobeAlpha = 0.26 + 0.16 * Math.sin(t * 16.0 + unit.wobbleOffset);
      } else {
        // 2s..0s: Extreme violent strobe / impending rupture
        const strobeTick = Math.sin(t * 32.0 + unit.wobbleOffset);
        strobeAlpha = strobeTick > 0 ? 0.45 : 0.10;
        if (strobeTick > 0.6) strobeColor = '#ffffff';
      }

      const cloakFactor = unit.isCloaked ? 0.4 : 1.0;
      ctx.fillStyle = strobeColor;
      ctx.globalAlpha = strobeAlpha * cloakFactor;
      ctx.beginPath();
      ctx.arc(renderX, renderY, r * 1.08, 0, Math.PI * 2);
      ctx.fill();

      // Quality-gated subtle corona ring
      if (this.quality !== 'low') {
        ctx.strokeStyle = strobeColor;
        ctx.globalAlpha = strobeAlpha * 0.8 * cloakFactor;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      ctx.restore();
    }

    // 🩸 Parasitized Unit: Crimson bio-pulse & attached mini meatball parasite (only after parasite leap lands)
    if (unit.isParasitized && (!unit._overtakeAnimTime || unit._overtakeAnimTime <= 0)) {
      ctx.save();
      const bioPulse = 0.82 + 0.18 * Math.sin(t * 5.0 + unit.wobbleOffset);
      ctx.fillStyle = '#ef4444';
      ctx.globalAlpha = 0.20 * bioPulse;
      ctx.beginPath();
      ctx.arc(renderX, renderY, r * 0.92, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;

      // Attached Mini-Parasite on head / upper chassis
      const paraX = renderX + dir * (r * 0.15);
      const paraY = renderY - r * 0.65;
      const paraBmp = buildSpriteBitmap('ParasiteVulture_Parasite', 'Sul', unit.team);
      const paraSrc = paraBmp?.normal;

      if (paraSrc) {
        const pSize = Math.max(14, Math.round(drawSize * 0.42));
        const pScale = 1.0 + Math.sin(t * 6.0 + unit.wobbleOffset) * 0.08;
        ctx.save();
        ctx.translate(paraX, paraY);
        ctx.scale(pScale, pScale);
        ctx.drawImage(paraSrc, -pSize / 2, -pSize / 2, pSize, pSize);
        ctx.restore();

        // Tendrils burrowing into victim
        ctx.strokeStyle = '#991b1b';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(paraX - 4, paraY + 2);
        ctx.quadraticCurveTo(paraX - 7, paraY + 6, paraX - 5, paraY + 10);
        ctx.moveTo(paraX + 4, paraY + 2);
        ctx.quadraticCurveTo(paraX + 7, paraY + 6, paraX + 5, paraY + 10);
        ctx.stroke();
      }
      ctx.restore();
    }

    // Panzer / HyperPanzer / A9Grizzly Engine Exhaust & Barrel Heat
    if (unitType === 'Panzer' || unitType === 'HyperPanzer' || unitType === 'A9Grizzly') {
      const exhaustX = renderX - dir * (r * 0.65);
      const exhaustY = renderY + r * 0.25;
      const exhaustPuff = (t * 3.5 + unit.x * 0.04) % 1.0;
      ctx.fillStyle = 'rgba(100, 116, 139, 0.45)';
      ctx.fillRect(exhaustX - dir * (exhaustPuff * 8), exhaustY - exhaustPuff * 6, 2.5, 2.5);

      if (unitType !== 'A9Grizzly') {
        const cdVal = (unitType === 'HyperPanzer') ? (unit.cannonCooldown || 0) : unit.attackCooldown;
        const spdVal = unit.attackSpeed || 2.5;
        if (cdVal > 0 && cdVal > (spdVal - 0.7)) {
          const heatAlpha = (cdVal - (spdVal - 0.7)) / 0.7;
          const prevAlpha = ctx.globalAlpha;
          ctx.globalAlpha = prevAlpha * (heatAlpha * 0.75);
          ctx.fillStyle = '#f97316';
          ctx.fillRect(renderX + dir * (r * 0.85), renderY - 3, 3 * dir, 2.5);
          ctx.globalAlpha = prevAlpha;
        }
      }
    }

    // 3. FOREGROUND WEAPON & STATUS LIGHT FX (Drawn on top)
    // Shade / Hyper Shade Tactical Red Sniper Scope
    if (unitType === 'Shade' || unitType === 'HyperShade') {
      const laserLen = Math.min(unit.range, 90);
      ctx.strokeStyle = 'rgba(239, 68, 68, 0.65)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(renderX + dir * (r * 0.8), renderY - 2);
      ctx.lineTo(renderX + dir * (r * 0.8 + laserLen), renderY - 2);
      ctx.stroke();

      // Pulsing red dot
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(renderX + dir * (r * 0.8 + laserLen) - 1.5, renderY - 3.5, 3, 3);
    }

    // Sul Preserver Lock: continuous plasma beam from the suspended orb to
    // its locked ground target. Unconditional on unitType (any future
    // hasBeamTether unit gets this for free) and independent of the big
    // per-unitType body dispatch above, since the beam is a state fact
    // (unit.beamTargetId, synced via WORLD_STATE) rather than a one-off
    // attack event. Straight zero-alloc single-segment stroke (no per-frame
    // point array) - stays a dead-straight line, only width/glow pulses,
    // per the card's design ("bleibt gerade, pulsiert vor Energie/Hitze").
    // Quality-gated per docs/INVARIANTS.md rule 4: full pulsing glow only on
    // high/ultra, a plain static line on low/medium.
    if (unit.beamTargetId && battlefield) {
      const beamTarget = battlefield.getUnitOrBaseById?.(unit.beamTargetId);
      if (beamTarget && !beamTarget.isDead && !beamTarget.isGoldenStatue) {
        const orbX = renderX;
        const orbY = renderY - drawSize * 0.44;
        const prevAlpha = ctx.globalAlpha;
        const customBullet = (typeof unit.getCustomBulletColorHex === 'function')
          ? unit.getCustomBulletColorHex()
          : (unit.customBulletColor || null);
        const beamColor = customBullet || '#7dd3fc';
        ctx.beginPath();
        ctx.moveTo(orbX, orbY);
        ctx.lineTo(beamTarget.x, beamTarget.y);
        if (this.quality === 'high' || this.quality === 'ultra') {
          const pulse = 0.75 + 0.25 * Math.sin(t * 16 + unit.wobbleOffset);
          ctx.strokeStyle = beamColor;
          ctx.lineWidth = 4.5 * pulse;
          ctx.globalAlpha = prevAlpha * 0.35;
          ctx.stroke();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.5 * pulse;
          ctx.globalAlpha = prevAlpha;
          ctx.stroke();
        } else {
          ctx.strokeStyle = customBullet || '#bae6fd';
          ctx.lineWidth = 2;
          ctx.globalAlpha = prevAlpha;
          ctx.stroke();
        }
        ctx.globalAlpha = prevAlpha;
      }
    }

    // 🌐 Human Hacker: World-Space Cable Harpoon & Transcendent XLink Beam
    // Rendered AFTER ctx.restore() of needsSpriteFlip so target world coordinates
    // are never mirrored horizontally, and outside the low-quality sprite gate so
    // the cable and XLink beam remain 100% visible in mobile portrait & all quality tiers.
    if (isHacker) {
      const hackerDrawSize = Math.round(drawSize * 1.21);
      const age = unit.age || 0;
      const numId = getUnitNumericId(unit);
      const hitRecoil = isHitFlashing ? Math.sin(age * 36.0) * 1.5 : 0;
      const hoverBob = unit.isTranscendent
        ? (-13.0 + Math.sin(age * 1.8) * 2.2)
        : (unit.isHacking
            ? (-4.5 + Math.sin(age * 34.0) * 0.35 + hitRecoil)
            : (-5.0 + Math.sin(age * 2.2 + (numId % 7)) * 3.0 + hitRecoil));

      // 4. Sacred Cyber-Mandala & Forcefield Bubble + Wireless Hyper Transcendent XLink
      if (unit.isTranscendent) {
        ctx.save();
        ctx.translate(renderX, renderY + hoverBob);

        // Spinning golden 8-fold mandala halo behind/around hacker
        const mandalaAngle = age * 1.8;
        ctx.save();
        ctx.rotate(mandalaAngle);
        ctx.strokeStyle = '#facc15';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.arc(0, 0, hackerDrawSize * 0.52, 0, Math.PI * 2);
        ctx.stroke();

        // 8 radial geometric spokes & glyph marks
        for (let s = 0; s < 8; s++) {
          const spAng = (s * Math.PI) / 4;
          const sx1 = Math.cos(spAng) * (hackerDrawSize * 0.42);
          const sy1 = Math.sin(spAng) * (hackerDrawSize * 0.42);
          const sx2 = Math.cos(spAng) * (hackerDrawSize * 0.56);
          const sy2 = Math.sin(spAng) * (hackerDrawSize * 0.56);
          ctx.beginPath();
          ctx.moveTo(sx1, sy1);
          ctx.lineTo(sx2, sy2);
          ctx.stroke();
        }
        ctx.restore();

        // Pulsing Spherical Indestructible Forcefield Bubble
        const pulse = Math.sin(age * 5.0) * 0.08 + 1.0;
        const bubbleR = hackerDrawSize * 0.56 * pulse;
        const bubbleCanvas = this.quality !== 'low' ? getHackerBubbleCanvas() : null;
        if (bubbleCanvas) {
          // Baked gradient disc, scaled to the pulsing radius (no per-frame gradient object).
          ctx.drawImage(bubbleCanvas, -bubbleR, -bubbleR, bubbleR * 2, bubbleR * 2);
        } else {
          ctx.fillStyle = 'rgba(56, 189, 248, 0.22)';
          ctx.beginPath();
          ctx.arc(0, 0, bubbleR, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(0, 0, bubbleR, 0, Math.PI * 2);
        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.restore();

        // Wireless Hyper Transcendent XLink (DNA Double-Helix Beam to Opposing Hacker)
        if (battlefield?.units) {
          let oppHacker = null;
          const xlinkUnits = battlefield.units;
          for (let ui = 0; ui < xlinkUnits.length; ui++) {
            const u = xlinkUnits[ui];
            if (u && u !== unit && !u.isDead && u.team !== unit.team && u.isTranscendent &&
                (u.isHacker || u.name === 'Hacker' || u.cardData?.unitType === 'Hacker')) {
              oppHacker = u;
              break;
            }
          }
          if (oppHacker && (unit.team === 'western' || (unit.team === oppHacker.team && unit.id < oppHacker.id))) {
            ctx.save();
            const x1 = renderX;
            const y1 = renderY + hoverBob;
            const oppNumId = getUnitNumericId(oppHacker);
            const oppHover = -13.0 + Math.sin((oppHacker.age || 0) * 1.8 + (oppNumId % 7)) * 2.2;
            const x2 = oppHacker.x;
            const y2 = oppHacker.y + oppHover;

            const dist = Math.hypot(x2 - x1, y2 - y1);
            const steps = Math.max(16, Math.floor(dist / 14));
            const angle = Math.atan2(y2 - y1, x2 - x1);
            const perpX = -Math.sin(angle);
            const perpY = Math.cos(angle);

            // Double helix: Strand A (Cyan), Strand B (Gold) and the cross-rungs,
            // each batched into ONE path / ONE stroke (was ~2.5 strokes per step,
            // i.e. ~285 per frame over a full-lane link - INVARIANTS §7.6).
            const amp = 9.0;
            const phaseBase = -age * 8.0;
            const phaseRate = Math.PI * 8;
            const dxStep = (x2 - x1) / steps;
            const dyStep = (y2 - y1) / steps;

            ctx.lineWidth = 1.8;
            ctx.strokeStyle = '#00f0ff';
            ctx.beginPath();
            for (let step = 0; step <= steps; step++) {
              const sn = Math.sin((step / steps) * phaseRate + phaseBase) * amp;
              const px = x1 + dxStep * step + perpX * sn;
              const py = y1 + dyStep * step + perpY * sn;
              if (step === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
            }
            ctx.stroke();

            ctx.strokeStyle = '#facc15';
            ctx.beginPath();
            for (let step = 0; step <= steps; step++) {
              const sn = Math.sin((step / steps) * phaseRate + phaseBase) * amp;
              const px = x1 + dxStep * step - perpX * sn;
              const py = y1 + dyStep * step - perpY * sn;
              if (step === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
            }
            ctx.stroke();

            // Helix cross-rungs every 2 steps
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
            ctx.lineWidth = 1.0;
            ctx.beginPath();
            for (let step = 0; step < steps; step += 2) {
              const sn = Math.sin((step / steps) * phaseRate + phaseBase) * amp;
              const bx = x1 + dxStep * step;
              const by = y1 + dyStep * step;
              ctx.moveTo(bx + perpX * sn, by + perpY * sn);
              ctx.lineTo(bx - perpX * sn, by - perpY * sn);
            }
            ctx.stroke();
            ctx.restore();
          }
        }
      }

      // 5. Battlefield Cable Harpoon Launch & Penetrator Rig (Active HACKING state)
      if (unit.isHacking && unit.hackerTargetId) {
        const target = battlefield?.getUnitOrBaseById?.(unit.hackerTargetId);
        if (target && !target.isDead) {
          ctx.save();
          const startX = renderX + forwardDir * hackerDrawSize * 0.16;
          const startY = renderY + hoverBob - hackerDrawSize * 0.08;
          const endX = target.x;
          const endY = target.y - (target.isAir ? 18 : 6);

          const dx = endX - startX;
          const dy = endY - startY;
          const dist = Math.hypot(dx, dy);
          const aimAngle = Math.atan2(dy, dx);

          // High-velocity harpoon projectile flight (~1050 px/sec)
          const flightDuration = Math.max(0.12, Math.min(0.40, dist / 1050));
          const lockTimer = unit.hackerLockTimer || 0;
          const shotProgress = Math.min(1.0, lockTimer / flightDuration);
          const isFlying = shotProgress < 1.0;
          const impactAge = Math.max(0, lockTimer - flightDuration);

          // A. Muzzle launch flash at Hacker's arm socket during first 0.08s
          if (lockTimer < 0.08) {
            const launchAlpha = 1.0 - (lockTimer / 0.08);
            ctx.save();
            ctx.translate(startX, startY);
            ctx.rotate(aimAngle);
            ctx.fillStyle = `rgba(0, 240, 255, ${0.9 * launchAlpha})`;
            ctx.beginPath();
            ctx.arc(0, 0, 8 * launchAlpha, 0, Math.PI * 2);
            ctx.fill();
            // Forward muzzle flare spike
            ctx.fillStyle = `rgba(255, 255, 255, ${launchAlpha})`;
            ctx.fillRect(0, -1.5, 14 * launchAlpha, 3);
            ctx.restore();
          }

          // Determine active cable endpoint and spline control point
          let tipX = endX;
          let tipY = endY;
          let midX = (startX + endX) * 0.5;
          let midY = (startY + endY) * 0.5;

          if (isFlying) {
            // ── IN-FLIGHT PHASE: Harpoon Dart pulls unspooling cables at high velocity ──
            const arcY = -Math.sin(shotProgress * Math.PI) * Math.min(16, dist * 0.04);
            tipX = startX + dx * shotProgress;
            tipY = startY + dy * shotProgress + arcY;

            // Cable whips and flutters dynamically in the slipstream
            const flutter = Math.sin(age * 55.0) * (3.0 * (1.0 - shotProgress));
            midX = (startX + tipX) * 0.5;
            midY = (startY + tipY) * 0.5 + arcY * 0.6 + flutter;
          } else {
            // ── IMPACT / LATCHED PHASE: Damped twang vibration & catenary sag ──
            const twangAmp = Math.sin(impactAge * 40.0) * Math.exp(-impactAge * 8.0) * 16.0;
            const perpX = -Math.sin(aimAngle) * twangAmp;
            const perpY = Math.cos(aimAngle) * twangAmp;
            const settle = Math.min(1.0, impactAge / 0.45);
            const catenarySag = (Math.min(55, dist * 0.12) + Math.sin(age * 3.5) * 3.5) * settle;

            midX = (startX + endX) * 0.5 + perpX;
            midY = (startY + endY) * 0.5 + catenarySag + perpY;
          }

          // ── MULTI-STRAND CABLE BUNDLE (Unspooling from Hacker to Harpoon Tip) ──
          // Strand 1: Thick Industrial Carbon Conduit
          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.quadraticCurveTo(midX, midY, tipX, tipY);
          ctx.strokeStyle = '#0f172a';
          ctx.lineWidth = 3.2;
          ctx.stroke();

          // Strand 2: Neon Cyan Data Stream
          ctx.beginPath();
          ctx.moveTo(startX, startY - 1);
          ctx.quadraticCurveTo(midX, midY - 1, tipX, tipY - 1);
          ctx.strokeStyle = '#00f0ff';
          ctx.lineWidth = 1.8;
          ctx.stroke();

          // Strand 3: Radiant Amber Energy Channel
          ctx.beginPath();
          ctx.moveTo(startX, startY + 1.2);
          ctx.quadraticCurveTo(midX, midY + 1.2, tipX, tipY + 1.2);
          ctx.strokeStyle = '#facc15';
          ctx.lineWidth = 1.2;
          ctx.stroke();

          // Strand 4: Crimson System Disruption Wire
          ctx.beginPath();
          ctx.moveTo(startX, startY + 2.2);
          ctx.quadraticCurveTo(midX, midY + 2.2, tipX, tipY + 2.2);
          ctx.strokeStyle = '#ef4444';
          ctx.lineWidth = 0.9;
          ctx.stroke();

          if (isFlying) {
            // ── FLYING HARPOON SPEARHEAD DART ────────────────────────
            const tangentY = dy - Math.cos(shotProgress * Math.PI) * Math.PI * Math.min(16, dist * 0.04);
            const tipAngle = Math.atan2(tangentY, dx);

            ctx.save();
            ctx.translate(tipX, tipY);
            ctx.rotate(tipAngle);

            // Rocket / Thruster Exhaust Flare spitting back into cables
            ctx.fillStyle = '#00f0ff';
            ctx.fillRect(-10, -2, 8, 4);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(-6, -1, 4, 2);

            // Tungsten Harpoon Penetrator Body
            ctx.fillStyle = '#1e293b';
            ctx.beginPath();
            ctx.moveTo(9, 0);       // sharp front penetrator tip
            ctx.lineTo(-4, -4.5);   // upper barb
            ctx.lineTo(-2, -1.5);   // inner notch
            ctx.lineTo(-9, -2.5);   // rear cable collar
            ctx.lineTo(-9, 2.5);    // rear cable collar
            ctx.lineTo(-2, 1.5);    // inner notch
            ctx.lineTo(-4, 4.5);    // lower barb
            ctx.closePath();
            ctx.fill();

            // Glowing Cyan Plasma Cutting Edge
            ctx.strokeStyle = '#00f0ff';
            ctx.lineWidth = 1.4;
            ctx.stroke();

            // White Hot Penetrator Tip
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.moveTo(9, 0);
            ctx.lineTo(2, -1.5);
            ctx.lineTo(2, 1.5);
            ctx.closePath();
            ctx.fill();

            ctx.restore();
          } else {
            // ── LATCHED TARGET CLAMP & DATA DISRUPTION ────────────────
            // Expanding impact shockwave ring on target chassis right after strike
            if (impactAge < 0.22) {
              const ringProgress = impactAge / 0.22;
              const ringR = ringProgress * (target.radius || 20) * 1.5;
              const ringAlpha = 1.0 - ringProgress;
              ctx.strokeStyle = `rgba(0, 240, 255, ${ringAlpha})`;
              ctx.lineWidth = 2.0;
              ctx.beginPath();
              ctx.arc(endX, endY, ringR, 0, Math.PI * 2);
              ctx.stroke();
            }

            // Traveling Data Packets (flowing along spline from Target to Hacker)
            const packetCount = Math.max(3, Math.min(8, Math.floor(dist / 40)));
            for (let p = 0; p < packetCount; p++) {
              const t = (1.0 - ((age * 2.2 + p * (1.0 / packetCount)) % 1.0));
              const oneMinusT = 1.0 - t;
              const px = oneMinusT * oneMinusT * startX + 2 * oneMinusT * t * midX + t * t * endX;
              const py = oneMinusT * oneMinusT * startY + 2 * oneMinusT * t * midY + t * t * endY;

              ctx.fillStyle = (p % 2 === 0) ? '#00f0ff' : '#ffffff';
              ctx.fillRect(px - 1.5, py - 1.5, 3, 3);
            }

            // Deployed 4-Jaw Mechanical Harpoon Clamp on Target
            ctx.save();
            ctx.translate(endX, endY);
            ctx.rotate(aimAngle);

            // Heavy clamp base
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(-6, -4, 10, 8);
            ctx.fillStyle = '#1e293b';
            ctx.fillRect(-4, -3, 8, 6);

            // 4 barbed locking jaws biting into victim hull
            ctx.fillStyle = '#ca8a04';
            ctx.fillRect(2, -6, 4, 3);
            ctx.fillRect(2, 3, 4, 3);
            ctx.fillStyle = '#00f0ff';
            ctx.fillRect(-2, -1.5, 3, 3); // status LED

            ctx.restore();
          }

          // Pulsing electric EMP interference ring around disabled target (visible in both portrait & landscape)
          const ringPulse = Math.sin(age * 8.0) * 0.15 + 1.0;
          ctx.strokeStyle = 'rgba(0, 240, 255, 0.45)';
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(endX, endY, (target.radius || 18) * ringPulse, 0, Math.PI * 2);
          ctx.stroke();

          ctx.restore();
        }
      }
    }

    // Marine Tactical HUD Visor Lamp & Rapid Muzzle Flash Kick
    if (unitType === 'Marine') {
      const isGoldMarine = (unit.cardData?.spriteRecolor === 'gold');
      ctx.fillStyle = isGoldMarine ? '#facc15' : '#38bdf8';
      ctx.fillRect(renderX + dir * (r * 0.4), renderY - r * 0.5, 3, 2);

      // Micro muzzle flash & barrel smoke puff while firing - centered at C-14 Gauss rifle muzzle
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.12)) {
        let gunAimY = 0;
        if (unit.target && !unit.target.isDead) {
          const dTargetY = unit.target.y - unit.y;
          gunAimY = Math.max(-3.5, Math.min(3.5, dTargetY * 0.035));
        }
        const muzzleY = renderY - r * 0.32 + gunAimY;
        ctx.fillStyle = '#ffea00';
        ctx.fillRect(renderX + dir * (r * 0.85), muzzleY - 1.5, 3 * dir, 3);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(renderX + dir * (r * 0.88), muzzleY - 0.75, 2 * dir, 1.5);
      }
    } else if (unitType === 'HyperMarine') {
      ctx.fillStyle = '#fbbf24';
      ctx.fillRect(renderX + dir * (r * 0.4), renderY - r * 0.5, 3, 2);

      // Micro muzzle flash & barrel smoke puff while firing - centered at Hyper C-14 rifle muzzle
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.12)) {
        let gunAimY = 0;
        if (unit.target && !unit.target.isDead) {
          const dTargetY = unit.target.y - unit.y;
          gunAimY = Math.max(-3.5, Math.min(3.5, dTargetY * 0.035));
        }
        const muzzleY = renderY - r * 0.32 + gunAimY;
        ctx.fillStyle = '#ff5a1f';
        ctx.fillRect(renderX + dir * (r * 0.85), muzzleY - 1.5, 3 * dir, 3);
        ctx.fillStyle = '#ffe4b5';
        ctx.fillRect(renderX + dir * (r * 0.88), muzzleY - 0.75, 2 * dir, 1.5);
      }
    } else if (unitType === 'HyperShade') {
      ctx.fillStyle = '#ff0033';
      ctx.fillRect(renderX + dir * (r * 0.35), renderY - r * 0.45, 3, 2);

      // Micro muzzle flash & thermal rail flare while firing
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.14)) {
        ctx.fillStyle = '#ff0033';
        ctx.fillRect(renderX + dir * (r * 0.9), renderY - 3, 4 * dir, 3);
        ctx.fillStyle = '#fef08a';
        ctx.fillRect(renderX + dir * (r * 0.94), renderY - 2, 2 * dir, 1.5);
      }
    } else if (unitType === 'A9Piranha') {
      // Rotary Front Chin Cannon muzzle flash rigidly anchored to the front cannon tip at grid[23][35]
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.08)) {
        const piranhaDrawSize = drawSize * 1.236;
        const pixelSize = piranhaDrawSize / 36;
        const pitchTilt = (unit.speed > 0 ? 0.08 * forwardDir : 0) + Math.sin(t * 4.0 + (unit.wobbleOffset || 0)) * 0.03;
        const localMuzzleX = dir * (piranhaDrawSize * (17.5 / 36));
        const localMuzzleY = piranhaDrawSize * (5.5 / 36);
        const cosTilt = Math.cos(pitchTilt);
        const sinTilt = Math.sin(pitchTilt);
        const mX = renderX + (localMuzzleX * cosTilt - localMuzzleY * sinTilt);
        const mY = renderY + (localMuzzleX * sinTilt + localMuzzleY * cosTilt);
        const flashW = pixelSize * 1.5;
        const flashH = pixelSize * 0.9;

        ctx.save();
        ctx.translate(mX, mY);
        ctx.rotate(pitchTilt);
        ctx.fillStyle = '#ffea00';
        ctx.fillRect(0, -flashH / 2, flashW * dir, flashH);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(dir * (pixelSize * 0.3), -flashH * 0.3, flashW * 0.5 * dir, flashH * 0.6);
        ctx.restore();
      }
    } else if (unitType === 'A9Pigeon') {
      // Chin-mounted 4-barrel Gatling Gun muzzle flash anchored to the pitched nose tip
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.09)) {
        const pigeonDrawSize = drawSize * 1.18;
        const pixelSize = pigeonDrawSize / 36;
        const hoverMicroTilt = Math.sin(t * 4.2 + (unit.wobbleOffset || 0)) * (unit.isHovering || unit.isFlyingBackward ? 0.024 : 0.014);
        const hullRot = ((unit.pigeonPitch || 0) * forwardDir) + hoverMicroTilt;
        const localMuzzleX = dir * (pigeonDrawSize * (17.5 / 36));
        const localMuzzleY = pigeonDrawSize * (4.0 / 36);
        const cosTilt = Math.cos(hullRot);
        const sinTilt = Math.sin(hullRot);
        const mX = renderX + (localMuzzleX * cosTilt - localMuzzleY * sinTilt);
        const mY = renderY + (localMuzzleX * sinTilt + localMuzzleY * cosTilt);
        const flashW = pixelSize * 1.7;
        const flashH = pixelSize * 1.0;

        ctx.save();
        ctx.translate(mX, mY);
        ctx.rotate(hullRot);
        ctx.fillStyle = '#ffea00';
        ctx.fillRect(0, -flashH / 2, flashW * dir, flashH);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(dir * (pixelSize * 0.3), -flashH * 0.3, flashW * 0.55 * dir, flashH * 0.6);
        ctx.restore();
      }
    } else if (unitType === 'A9Grizzly') {
      // Roof-mounted .50 cal pintle MG muzzle flash kick while firing
      if (!unit.isReloading && unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.07)) {
        let gunAimY = 0;
        if (unit.target && !unit.target.isDead) {
          gunAimY = Math.max(-2.5, Math.min(2.5, (unit.target.y - unit.y) * 0.03));
        }
        const mX = renderX + dir * (r * 0.95 * 1.12);
        const mY = renderY - r * 0.62 * 1.12 + gunAimY;
        ctx.fillStyle = '#ffea00';
        ctx.fillRect(mX, mY - 1.5, 3.5 * dir, 3);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(mX + dir * 1, mY - 1, 2 * dir, 2);
      }
    } else if (unitType === 'SentinalLock') {
      // Dripping bio-liquid spit while firing (Dilophosaurus-style glob,
      // not a muzzle spark) - matches the "moisture" salvo, not acid gas.
      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.08)) {
        ctx.fillStyle = '#4d7c0f';
        ctx.fillRect(renderX + dir * (r * 0.85), renderY - 2, 3 * dir, 3);
        ctx.fillStyle = '#a3e635';
        ctx.fillRect(renderX + dir * (r * 0.9), renderY - 1, 2 * dir, 1.5);
      }
    }

    // Devil Patrol Pilot Igniters
    if (unitType === 'DevilPatrol') {
      const flameFlicker = Math.sin(t * 22) > 0 ? '#f97316' : '#facc15';
      ctx.fillStyle = flameFlicker;
      ctx.fillRect(renderX + dir * (r * 0.85), renderY - 3, 4 * dir, 3);
      ctx.fillRect(renderX + dir * (r * 0.85), renderY + 3, 4 * dir, 3);
    }

    // Hyper Devil Patrol Grenade Muzzle Flare & Chest Turbine Core Glow
    if (unitType === 'HyperDevilPatrol' && this.quality !== 'low') {
      const turbinePulse = 0.6 + 0.4 * Math.sin(t * 12);
      const prevAlpha = ctx.globalAlpha;
      ctx.globalAlpha = prevAlpha * turbinePulse;
      ctx.fillStyle = '#38bdf8';
      ctx.fillRect(renderX + dir * (r * 0.2) - 1.5, renderY - 1, 3, 3);
      ctx.globalAlpha = prevAlpha;

      if (unit.attackCooldown > 0 && unit.attackCooldown > (unit.attackSpeed - 0.16)) {
        const mX = renderX + dir * (r * 0.95);
        ctx.fillStyle = '#ffedd5';
        ctx.fillRect(mX, renderY - 2, 4 * dir, 4);
        ctx.fillStyle = '#f97316';
        ctx.fillRect(mX + dir * 1, renderY - 3, 6 * dir, 6);
      }
    }

    // 3.5. Smoking Barrels (1.0 second subtle wisps of rising smoke after firing / during reload)
    if (unit.smokeBarrelTimer > 0 && this.quality !== 'low') {
      let smX = null;
      let smY = null;
      if (unitType === 'Marine' || unitType === 'HyperMarine') {
        smX = renderX + dir * (r * 0.85);
        smY = renderY - r * 0.32;
      } else if (unitType === 'A9Piranha') {
        const piranhaDrawSize = drawSize * 1.236;
        smX = renderX + dir * (piranhaDrawSize * (17.5 / 36));
        smY = renderY + piranhaDrawSize * (5.5 / 36);
      } else if (unitType === 'A9Pigeon') {
        const pigeonDrawSize = drawSize * 1.18;
        smX = renderX + dir * (pigeonDrawSize * (17.5 / 36));
        smY = renderY + pigeonDrawSize * (4.0 / 36);
      } else if (unitType === 'Panzer' || unitType === 'HyperPanzer') {
        smX = renderX + dir * (r * 0.95);
        smY = renderY - r * 0.15;
      } else if (unitType === 'DeployableCannon' || unitType === 'Deployable_Cannon' || unitType === 'Deployable Cannon') {
        smX = renderX + dir * (r * 0.9);
        smY = renderY - r * 0.35;
      }

      if (smX !== null) {
        const smokeProg = unit.smokeBarrelTimer / 1.0;
        const alphaBase = Math.min(1.0, smokeProg * 1.4) * 0.35;
        if (alphaBase > 0.02) {
          ctx.save();
          for (let sp = 0; sp < 3; sp++) {
            const pPhase = ((1.0 - smokeProg) * 2.5 + sp * 0.33) % 1.0;
            const px = smX - dir * (pPhase * 8) + Math.sin(t * 5 + sp * 2) * 1.5;
            const py = smY - pPhase * 15;
            const pr = 1.2 + pPhase * 2.4;
            const pAlpha = alphaBase * (1.0 - pPhase);
            ctx.fillStyle = `rgba(148, 163, 184, ${pAlpha})`;
            ctx.beginPath();
            ctx.arc(px, py, pr, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
        }
      }
    }

    // 4. HEALTH BAR
    // hideHealthBars (docs/singleplayer implementation soft-foraging-
    // flamingo.md): a lightweight campaign encounter that isn't a
    // "vollwertiger Kampf" (full battle, e.g. a boss fight) suppresses the
    // glowing per-unit HP-bar indicator entirely - same opt-in-only pattern
    // as hideBases (js/engine/battlefield.js/renderer.js).
    const barW = Math.max(26, r * 2);
    const barH = 4;
    const barX = renderX - barW / 2;
    const isClaimingPillar = (unit.isClaimingPillar || unitType === 'ClaimingPillar' || unitType === 'Claiming_Pillar' || unitType === 'Claiming Pillar');
    const pillarGrowthProg = unit.isGrowing ? Math.min(1.0, Math.max(0.1, (unit.growthTimer || 0) / 3.0)) : 1.0;
    const isHackerUnit = (unit.isHacker || unitType === 'Hacker' || unitType === 'HackerGold');
    const isA9PigeonUnit = (unit.isA9Pigeon || unitType === 'A9Pigeon' || unit.name === 'A9 Pigeon');
    const barY = isClaimingPillar
      ? (renderY + 6 - (drawSize * 1.42 * pillarGrowthProg) - 10)
      : (isStickman ? (renderY + 24 - drawSize - 10) : (isHackerUnit ? (renderY - r - 15) : (isA9PigeonUnit ? (renderY - r - 18) : (renderY - r - 9))));

    if (!battlefield?.hideHealthBars && !isEnemyInvisible) {
      if (!unit.isHeld) {
        const hpRatio = Math.max(0, unit.hp / unit.maxHp);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.fillRect(barX - 1, barY - 1, barW + 2, barH + 2);

        // Ghost Damage Bar (AAA Staggered Depletion)
        const ghostHp = unit.ghostHp !== undefined ? unit.ghostHp : unit.hp;
        if (ghostHp > unit.hp && unit.maxHp > 0) {
          const ghostRatio = Math.min(1.0, Math.max(0, ghostHp / unit.maxHp));
          ctx.fillStyle = 'rgba(255, 235, 175, 0.92)';
          ctx.fillRect(barX, barY, barW * ghostRatio, barH);
        }

        ctx.fillStyle = hpRatio > 0.5 ? '#00ff66' : hpRatio > 0.25 ? '#ffbb00' : '#ff2200';
        ctx.fillRect(barX, barY, barW * hpRatio, barH);

        // Tactical Hierarchical HP Pip Dividers (League of Legends inspired: 25 Minor / 100 Major blocks)
        if (this.quality !== 'low' && unit.maxHp >= 40 && barW >= 24) {
          const showMinorPips = unit.maxHp <= 300;
          const maxSegments = Math.floor(unit.maxHp / 25);
          for (let pi = 1; pi <= maxSegments; pi++) {
            const hpVal = pi * 25;
            if (hpVal >= unit.maxHp) break;
            const isMajor = hpVal % 100 === 0;
            if (!isMajor && !showMinorPips) continue;

            const pipX = Math.round(barX + (hpVal / unit.maxHp) * barW);
            if (pipX >= barX + 1 && pipX <= barX + barW - 2) {
              if (isMajor) {
                // Major 100 HP block divider: solid 1.5px/2px deep black with subtle relief inset
                ctx.fillStyle = 'rgba(0, 0, 0, 0.95)';
                ctx.fillRect(pipX, barY, 1.5, barH);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
                ctx.fillRect(pipX + 1.5, barY, 0.5, barH);
              } else {
                // Minor 25 HP pip divider: fine 1px dark hairline
                ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
                ctx.fillRect(pipX, barY, 1, barH);
              }
            }
          }
        }

        // Shields segment in health bar
        if (unit.maxShields > 0) {
          const shieldRatio = Math.max(0, unit.shields / unit.maxShields);
          ctx.fillStyle = '#00f0ff';
          ctx.fillRect(barX, barY - 3, barW * shieldRatio, 2);
        }

        // Forced Overstimulation Frenzy Fuse: burning countdown bar draining over 10s
        if (unit.isOverstimulated && (unit.overstimulationTimer || 0) > 0) {
          const fuseRatio = Math.max(0, Math.min(1.0, unit.overstimulationTimer / 10.0));
          const fuseY = barY - (unit.maxShields > 0 ? 6 : 4);
          const isCritical = unit.overstimulationTimer < 2.5;
          ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
          ctx.fillRect(barX, fuseY, barW, 2);
          if (isCritical && Math.sin(t * 22) > 0) {
            ctx.fillStyle = '#ffffff';
          } else {
            ctx.fillStyle = unit.overstimulationTimer < 4.0 ? '#ef4444' : '#f97316';
          }
          ctx.fillRect(barX, fuseY, barW * fuseRatio, 2);
        }
      } else {
        // Dramatic hazard skull indicator when held in execution
        ctx.save();
        ctx.fillStyle = '#ff003c';
        ctx.font = 'bold 9px monospace';
        ctx.textAlign = 'center';
        const skullPulse = Math.sin(t * 18) > 0 ? '☠ HELD' : '⚠ CRITICAL';
        ctx.fillText(skullPulse, renderX, renderY - r - 10);
        ctx.restore();
      }
    }

    // Gold Edition badge: a small persistent diamond next to the health bar
    // (both teams - this is a render fact about the unit, not a
    // notification). Single beginPath/fill, no new heap objects.
    if (isGoldEdition && !isEnemyInvisible) {
      const badgeX = barX + barW + 6;
      const badgeY = barY + barH / 2;
      const badgePulse = this.quality === 'low' ? 0.85 : (0.75 + 0.25 * Math.sin(t * 3 + unit.wobbleOffset));
      const prevAlpha = ctx.globalAlpha;
      ctx.globalAlpha = prevAlpha * badgePulse;
      ctx.fillStyle = '#facc15';
      ctx.beginPath();
      ctx.moveTo(badgeX, badgeY - 3.5);
      ctx.lineTo(badgeX + 3.5, badgeY);
      ctx.lineTo(badgeX, badgeY + 3.5);
      ctx.lineTo(badgeX - 3.5, badgeY);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = prevAlpha;
    }

    // Unit name label - only shown for the currently selected unit
    if (battlefield && battlefield.selectedUnit === unit && !isEnemyInvisible) {
      const nameY = barY - (unit.maxShields > 0 ? 3 : 0) - 4;
      ctx.font = 'bold 11px monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#000';
      ctx.fillText(unit.name, renderX + 1, nameY + 1);
      ctx.fillStyle = '#ffff00';
      ctx.fillText(unit.name, renderX, nameY);
    }

    ctx.restore();
  }


};
